
/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements. See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership. The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License. You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/*jshint evil:true*/

/**
 * The Thrift namespace houses the Apache Thrift JavaScript library
 * elements providing JavaScript bindings for the Apache Thrift RPC
 * system. End users will typically only directly make use of the
 * Transport (TXHRTransport/TWebSocketTransport) and Protocol
 * (TJSONPRotocol/TBinaryProtocol) constructors.
 *
 * Object methods beginning with a __ (e.g. __onOpen()) are internal
 * and should not be called outside of the object's own methods.
 *
 * This library creates one global object: Thrift
 * Code in this library must never create additional global identifiers,
 * all features must be scoped within the Thrift namespace.
 * @namespace
 * @example
 *     var transport = new Thrift.Transport("http://localhost:8585");
 *     var protocol  = new Thrift.Protocol(transport);
 *     var client = new MyThriftSvcClient(protocol);
 *     var result = client.MyMethod();
 */
var Thrift = {
    /**
     * Thrift JavaScript library version.
     * @readonly
     * @const {string} Version
     * @memberof Thrift
     */
    Version: '1.0.0-dev',

    /**
     * Thrift IDL type string to Id mapping.
     * @readonly
     * @property {number}  STOP   - End of a set of fields.
     * @property {number}  VOID   - No value (only legal for return types).
     * @property {number}  BOOL   - True/False integer.
     * @property {number}  BYTE   - Signed 8 bit integer.
     * @property {number}  I08    - Signed 8 bit integer.
     * @property {number}  DOUBLE - 64 bit IEEE 854 floating point.
     * @property {number}  I16    - Signed 16 bit integer.
     * @property {number}  I32    - Signed 32 bit integer.
     * @property {number}  I64    - Signed 64 bit integer.
     * @property {number}  STRING - Array of bytes representing a string of characters.
     * @property {number}  UTF7   - Array of bytes representing a string of UTF7 encoded characters.
     * @property {number}  STRUCT - A multifield type.
     * @property {number}  MAP    - A collection type (map/associative-array/dictionary).
     * @property {number}  SET    - A collection type (unordered and without repeated values).
     * @property {number}  LIST   - A collection type (unordered).
     * @property {number}  UTF8   - Array of bytes representing a string of UTF8 encoded characters.
     * @property {number}  UTF16  - Array of bytes representing a string of UTF16 encoded characters.
     */
    Type: {
        'STOP' : 0,
        'VOID' : 1,
        'BOOL' : 2,
        'BYTE' : 3,
        'I08' : 3,
        'DOUBLE' : 4,
        'I16' : 6,
        'I32' : 8,
        'I64' : 10,
        'STRING' : 11,
        'UTF7' : 11,
        'STRUCT' : 12,
        'MAP' : 13,
        'SET' : 14,
        'LIST' : 15,
        'UTF8' : 16,
        'UTF16' : 17
    },

    /**
     * Thrift RPC message type string to Id mapping.
     * @readonly
     * @property {number}  CALL      - RPC call sent from client to server.
     * @property {number}  REPLY     - RPC call normal response from server to client.
     * @property {number}  EXCEPTION - RPC call exception response from server to client.
     * @property {number}  ONEWAY    - Oneway RPC call from client to server with no response.
     */
    MessageType: {
        'CALL' : 1,
        'REPLY' : 2,
        'EXCEPTION' : 3,
        'ONEWAY' : 4
    },

    /**
     * Utility function returning the count of an object's own properties.
     * @param {object} obj - Object to test.
     * @returns {number} number of object's own properties
     */
    objectLength: function(obj) {
        var length = 0;
        for (var k in obj) {
            if (obj.hasOwnProperty(k)) {
                length++;
            }
        }
        return length;
    },

    /**
     * Utility function to establish prototype inheritance.
     * @see {@link http://javascript.crockford.com/prototypal.html|Prototypal Inheritance}
     * @param {function} constructor - Contstructor function to set as derived.
     * @param {function} superConstructor - Contstructor function to set as base.
     * @param {string} [name] - Type name to set as name property in derived prototype.
     */
    inherits: function(constructor, superConstructor, name) {
      function F() {}
      F.prototype = superConstructor.prototype;
      constructor.prototype = new F();
      constructor.prototype.name = name || "";
    }
};

/**
 * Initializes a Thrift TException instance.
 * @constructor
 * @augments Error
 * @param {string} message - The TException message (distinct from the Error message).
 * @classdesc TException is the base class for all Thrift exceptions types.
 */
Thrift.TException = function(message) {
    this.message = message;
};
Thrift.inherits(Thrift.TException, Error, 'TException');

/**
 * Returns the message set on the exception.
 * @readonly
 * @returns {string} exception message
 */
Thrift.TException.prototype.getMessage = function() {
    return this.message;
};

/**
 * Thrift Application Exception type string to Id mapping.
 * @readonly
 * @property {number}  UNKNOWN                 - Unknown/undefined.
 * @property {number}  UNKNOWN_METHOD          - Client attempted to call a method unknown to the server.
 * @property {number}  INVALID_MESSAGE_TYPE    - Client passed an unknown/unsupported MessageType.
 * @property {number}  WRONG_METHOD_NAME       - Unused.
 * @property {number}  BAD_SEQUENCE_ID         - Unused in Thrift RPC, used to flag proprietary sequence number errors.
 * @property {number}  MISSING_RESULT          - Raised by a server processor if a handler fails to supply the required return result.
 * @property {number}  INTERNAL_ERROR          - Something bad happened.
 * @property {number}  PROTOCOL_ERROR          - The protocol layer failed to serialize or deserialize data.
 * @property {number}  INVALID_TRANSFORM       - Unused.
 * @property {number}  INVALID_PROTOCOL        - The protocol (or version) is not supported.
 * @property {number}  UNSUPPORTED_CLIENT_TYPE - Unused.
 */
Thrift.TApplicationExceptionType = {
    'UNKNOWN' : 0,
    'UNKNOWN_METHOD' : 1,
    'INVALID_MESSAGE_TYPE' : 2,
    'WRONG_METHOD_NAME' : 3,
    'BAD_SEQUENCE_ID' : 4,
    'MISSING_RESULT' : 5,
    'INTERNAL_ERROR' : 6,
    'PROTOCOL_ERROR' : 7,
    'INVALID_TRANSFORM' : 8,
    'INVALID_PROTOCOL' : 9,
    'UNSUPPORTED_CLIENT_TYPE' : 10
};

/**
 * Initializes a Thrift TApplicationException instance.
 * @constructor
 * @augments Thrift.TException
 * @param {string} message - The TApplicationException message (distinct from the Error message).
 * @param {Thrift.TApplicationExceptionType} [code] - The TApplicationExceptionType code.
 * @classdesc TApplicationException is the exception class used to propagate exceptions from an RPC server back to a calling client.
*/
Thrift.TApplicationException = function(message, code) {
    this.message = message;
    this.code = typeof code === "number" ? code : 0;
};
Thrift.inherits(Thrift.TApplicationException, Thrift.TException, 'TApplicationException');

/**
 * Read a TApplicationException from the supplied protocol.
 * @param {object} input - The input protocol to read from.
 */
Thrift.TApplicationException.prototype.read = function(input) {
    while (1) {
        var ret = input.readFieldBegin();

        if (ret.ftype == Thrift.Type.STOP) {
            break;
        }

        var fid = ret.fid;

        switch (fid) {
            case 1:
                if (ret.ftype == Thrift.Type.STRING) {
                    ret = input.readString();
                    this.message = ret.value;
                } else {
                    ret = input.skip(ret.ftype);
                }
                break;
            case 2:
                if (ret.ftype == Thrift.Type.I32) {
                    ret = input.readI32();
                    this.code = ret.value;
                } else {
                    ret = input.skip(ret.ftype);
                }
                break;
           default:
                ret = input.skip(ret.ftype);
                break;
        }

        input.readFieldEnd();
    }

    input.readStructEnd();
};

/**
 * Wite a TApplicationException to the supplied protocol.
 * @param {object} output - The output protocol to write to.
 */
Thrift.TApplicationException.prototype.write = function(output) {
    output.writeStructBegin('TApplicationException');

    if (this.message) {
        output.writeFieldBegin('message', Thrift.Type.STRING, 1);
        output.writeString(this.getMessage());
        output.writeFieldEnd();
    }

    if (this.code) {
        output.writeFieldBegin('type', Thrift.Type.I32, 2);
        output.writeI32(this.code);
        output.writeFieldEnd();
    }

    output.writeFieldStop();
    output.writeStructEnd();
};

/**
 * Returns the application exception code set on the exception.
 * @readonly
 * @returns {Thrift.TApplicationExceptionType} exception code
 */
Thrift.TApplicationException.prototype.getCode = function() {
    return this.code;
};

Thrift.TProtocolExceptionType = {
    UNKNOWN: 0,
    INVALID_DATA: 1,
    NEGATIVE_SIZE: 2,
    SIZE_LIMIT: 3,
    BAD_VERSION: 4,
    NOT_IMPLEMENTED: 5,
    DEPTH_LIMIT: 6
};

Thrift.TProtocolException = function TProtocolException(type, message) {
    Error.call(this);
    Error.captureStackTrace(this, this.constructor);
    this.name = this.constructor.name;
    this.type = type;
    this.message = message;
};
Thrift.inherits(Thrift.TProtocolException, Thrift.TException, 'TProtocolException');

/**
 * Constructor Function for the XHR transport.
 * If you do not specify a url then you must handle XHR operations on
 * your own. This type can also be constructed using the Transport alias
 * for backward compatibility.
 * @constructor
 * @param {string} [url] - The URL to connect to.
 * @classdesc The Apache Thrift Transport layer performs byte level I/O
 * between RPC clients and servers. The JavaScript TXHRTransport object
 * uses Http[s]/XHR. Target servers must implement the http[s] transport
 * (see: node.js example server_http.js).
 * @example
 *     var transport = new Thrift.TXHRTransport("http://localhost:8585");
 */
Thrift.Transport = Thrift.TXHRTransport = function(url, options) {
    this.url = url;
    this.wpos = 0;
    this.rpos = 0;
    this.useCORS = (options && options.useCORS);
    this.customHeaders = options ? (options.customHeaders ? options.customHeaders : {}): {};
    this.send_buf = '';
    this.recv_buf = '';
};

Thrift.TXHRTransport.prototype = {
    /**
     * Gets the browser specific XmlHttpRequest Object.
     * @returns {object} the browser XHR interface object
     */
    getXmlHttpRequestObject: function() {
        try { return new XMLHttpRequest(); } catch (e1) { }
        try { return new ActiveXObject('Msxml2.XMLHTTP'); } catch (e2) { }
        try { return new ActiveXObject('Microsoft.XMLHTTP'); } catch (e3) { }

        throw "Your browser doesn't support XHR.";
    },

    /**
     * Sends the current XRH request if the transport was created with a URL
     * and the async parameter is false. If the transport was not created with
     * a URL, or the async parameter is True and no callback is provided, or
     * the URL is an empty string, the current send buffer is returned.
     * @param {object} async - If true the current send buffer is returned.
     * @param {object} callback - Optional async completion callback
     * @returns {undefined|string} Nothing or the current send buffer.
     * @throws {string} If XHR fails.
     */
    flush: function(async, callback) {
        var self = this;
        if ((async && !callback) || this.url === undefined || this.url === '') {
            return this.send_buf;
        }

        var xreq = this.getXmlHttpRequestObject();

        if (xreq.overrideMimeType) {
            xreq.overrideMimeType('application/vnd.apache.thrift.json; charset=utf-8');
        }

        if (callback) {
            //Ignore XHR callbacks until the data arrives, then call the
            //  client's callback
            xreq.onreadystatechange =
              (function() {
                var clientCallback = callback;
                return function() {
                  if (this.readyState == 4 && this.status == 200) {
                    self.setRecvBuffer(this.responseText);
                    clientCallback();
                  }
                };
              }());

            // detect net::ERR_CONNECTION_REFUSED and call the callback.
            xreq.onerror =
                (function() {
                  var clientCallback = callback;
                  return function() {
                      clientCallback();
                  };
                }());

        }

        xreq.open('POST', this.url, !!async);

        // add custom headers
        Object.keys(self.customHeaders).forEach(function(prop) {
            xreq.setRequestHeader(prop, self.customHeaders[prop]);
        });

        if (xreq.setRequestHeader) {
            xreq.setRequestHeader('Accept', 'application/vnd.apache.thrift.json; charset=utf-8');
            xreq.setRequestHeader('Content-Type', 'application/vnd.apache.thrift.json; charset=utf-8');
        }

        xreq.send(this.send_buf);
        if (async && callback) {
            return;
        }

        if (xreq.readyState != 4) {
            throw 'encountered an unknown ajax ready state: ' + xreq.readyState;
        }

        if (xreq.status != 200) {
            throw 'encountered a unknown request status: ' + xreq.status;
        }

        this.recv_buf = xreq.responseText;
        this.recv_buf_sz = this.recv_buf.length;
        this.wpos = this.recv_buf.length;
        this.rpos = 0;
    },

    /**
     * Creates a jQuery XHR object to be used for a Thrift server call.
     * @param {object} client - The Thrift Service client object generated by the IDL compiler.
     * @param {object} postData - The message to send to the server.
     * @param {function} args - The original call arguments with the success call back at the end.
     * @param {function} recv_method - The Thrift Service Client receive method for the call.
     * @returns {object} A new jQuery XHR object.
     * @throws {string} If the jQuery version is prior to 1.5 or if jQuery is not found.
     */
    jqRequest: function(client, postData, args, recv_method) {
        if (typeof jQuery === 'undefined' ||
            typeof jQuery.Deferred === 'undefined') {
            throw 'Thrift.js requires jQuery 1.5+ to use asynchronous requests';
        }

        var thriftTransport = this;

        var jqXHR = jQuery.ajax({
            url: this.url,
            data: postData,
            type: 'POST',
            cache: false,
            contentType: 'application/vnd.apache.thrift.json; charset=utf-8',
            dataType: 'text thrift',
            converters: {
                'text thrift' : function(responseData) {
                    thriftTransport.setRecvBuffer(responseData);
                    var value = recv_method.call(client);
                    return value;
                }
            },
            context: client,
            success: jQuery.makeArray(args).pop()
        });

        return jqXHR;
    },

    /**
     * Sets the buffer to provide the protocol when deserializing.
     * @param {string} buf - The buffer to supply the protocol.
     */
    setRecvBuffer: function(buf) {
        this.recv_buf = buf;
        this.recv_buf_sz = this.recv_buf.length;
        this.wpos = this.recv_buf.length;
        this.rpos = 0;
    },

    /**
     * Returns true if the transport is open, XHR always returns true.
     * @readonly
     * @returns {boolean} Always True.
     */
    isOpen: function() {
        return true;
    },

    /**
     * Opens the transport connection, with XHR this is a nop.
     */
    open: function() {},

    /**
     * Closes the transport connection, with XHR this is a nop.
     */
    close: function() {},

    /**
     * Returns the specified number of characters from the response
     * buffer.
     * @param {number} len - The number of characters to return.
     * @returns {string} Characters sent by the server.
     */
    read: function(len) {
        var avail = this.wpos - this.rpos;

        if (avail === 0) {
            return '';
        }

        var give = len;

        if (avail < len) {
            give = avail;
        }

        var ret = this.read_buf.substr(this.rpos, give);
        this.rpos += give;

        //clear buf when complete?
        return ret;
    },

    /**
     * Returns the entire response buffer.
     * @returns {string} Characters sent by the server.
     */
    readAll: function() {
        return this.recv_buf;
    },

    /**
     * Sets the send buffer to buf.
     * @param {string} buf - The buffer to send.
     */
    write: function(buf) {
        this.send_buf = buf;
    },

    /**
     * Returns the send buffer.
     * @readonly
     * @returns {string} The send buffer.
     */
    getSendBuffer: function() {
        return this.send_buf;
    }

};


/**
 * Constructor Function for the WebSocket transport.
 * @constructor
 * @param {string} [url] - The URL to connect to.
 * @classdesc The Apache Thrift Transport layer performs byte level I/O
 * between RPC clients and servers. The JavaScript TWebSocketTransport object
 * uses the WebSocket protocol. Target servers must implement WebSocket.
 * (see: node.js example server_http.js).
 * @example
 *   var transport = new Thrift.TWebSocketTransport("http://localhost:8585");
 */
Thrift.TWebSocketTransport = function(url) {
    this.__reset(url);
};

Thrift.TWebSocketTransport.prototype = {
    __reset: function(url) {
      this.url = url;             //Where to connect
      this.socket = null;         //The web socket
      this.callbacks = [];        //Pending callbacks
      this.send_pending = [];     //Buffers/Callback pairs waiting to be sent
      this.send_buf = '';         //Outbound data, immutable until sent
      this.recv_buf = '';         //Inbound data
      this.rb_wpos = 0;           //Network write position in receive buffer
      this.rb_rpos = 0;           //Client read position in receive buffer
    },

    /**
     * Sends the current WS request and registers callback. The async
     * parameter is ignored (WS flush is always async) and the callback
     * function parameter is required.
     * @param {object} async - Ignored.
     * @param {object} callback - The client completion callback.
     * @returns {undefined|string} Nothing (undefined)
     */
    flush: function(async, callback) {
      var self = this;
      if (this.isOpen()) {
        //Send data and register a callback to invoke the client callback
        this.socket.send(this.send_buf);
        this.callbacks.push((function() {
          var clientCallback = callback;
          return function(msg) {
            self.setRecvBuffer(msg);
            clientCallback();
          };
        }()));
      } else {
        //Queue the send to go out __onOpen
        this.send_pending.push({
          buf: this.send_buf,
          cb:  callback
        });
      }
    },

    __onOpen: function() {
       var self = this;
       if (this.send_pending.length > 0) {
          //If the user made calls before the connection was fully
          //open, send them now
          this.send_pending.forEach(function(elem) {
             this.socket.send(elem.buf);
             this.callbacks.push((function() {
               var clientCallback = elem.cb;
               return function(msg) {
                  self.setRecvBuffer(msg);
                  clientCallback();
               };
             }()));
          });
          this.send_pending = [];
       }
    },

    __onClose: function(evt) {
      this.__reset(this.url);
    },

    __onMessage: function(evt) {
      if (this.callbacks.length) {
        this.callbacks.shift()(evt.data);
      }
    },

    __onError: function(evt) {
      console.log("Thrift WebSocket Error: " + evt.toString());
      this.socket.close();
    },

    /**
     * Sets the buffer to use when receiving server responses.
     * @param {string} buf - The buffer to receive server responses.
     */
    setRecvBuffer: function(buf) {
        this.recv_buf = buf;
        this.recv_buf_sz = this.recv_buf.length;
        this.wpos = this.recv_buf.length;
        this.rpos = 0;
    },

    /**
     * Returns true if the transport is open
     * @readonly
     * @returns {boolean}
     */
    isOpen: function() {
        return this.socket && this.socket.readyState == this.socket.OPEN;
    },

    /**
     * Opens the transport connection
     */
    open: function() {
      //If OPEN/CONNECTING/CLOSING ignore additional opens
      if (this.socket && this.socket.readyState != this.socket.CLOSED) {
        return;
      }
      //If there is no socket or the socket is closed:
      this.socket = new WebSocket(this.url);
      this.socket.onopen = this.__onOpen.bind(this);
      this.socket.onmessage = this.__onMessage.bind(this);
      this.socket.onerror = this.__onError.bind(this);
      this.socket.onclose = this.__onClose.bind(this);
    },

    /**
     * Closes the transport connection
     */
    close: function() {
      this.socket.close();
    },

    /**
     * Returns the specified number of characters from the response
     * buffer.
     * @param {number} len - The number of characters to return.
     * @returns {string} Characters sent by the server.
     */
    read: function(len) {
        var avail = this.wpos - this.rpos;

        if (avail === 0) {
            return '';
        }

        var give = len;

        if (avail < len) {
            give = avail;
        }

        var ret = this.read_buf.substr(this.rpos, give);
        this.rpos += give;

        //clear buf when complete?
        return ret;
    },

    /**
     * Returns the entire response buffer.
     * @returns {string} Characters sent by the server.
     */
    readAll: function() {
        return this.recv_buf;
    },

    /**
     * Sets the send buffer to buf.
     * @param {string} buf - The buffer to send.
     */
    write: function(buf) {
        this.send_buf = buf;
    },

    /**
     * Returns the send buffer.
     * @readonly
     * @returns {string} The send buffer.
     */
    getSendBuffer: function() {
        return this.send_buf;
    }

};

/**
 * Initializes a Thrift JSON protocol instance.
 * @constructor
 * @param {Thrift.Transport} transport - The transport to serialize to/from.
 * @classdesc Apache Thrift Protocols perform serialization which enables cross
 * language RPC. The Protocol type is the JavaScript browser implementation
 * of the Apache Thrift TJSONProtocol.
 * @example
 *     var protocol  = new Thrift.Protocol(transport);
 */
Thrift.TJSONProtocol = Thrift.Protocol = function(transport) {
    this.tstack = [];
    this.tpos = [];
    this.transport = transport;
};

/**
 * Thrift IDL type Id to string mapping.
 * @readonly
 * @see {@link Thrift.Type}
 */
Thrift.Protocol.Type = {};
Thrift.Protocol.Type[Thrift.Type.BOOL] = '"tf"';
Thrift.Protocol.Type[Thrift.Type.BYTE] = '"i8"';
Thrift.Protocol.Type[Thrift.Type.I16] = '"i16"';
Thrift.Protocol.Type[Thrift.Type.I32] = '"i32"';
Thrift.Protocol.Type[Thrift.Type.I64] = '"i64"';
Thrift.Protocol.Type[Thrift.Type.DOUBLE] = '"dbl"';
Thrift.Protocol.Type[Thrift.Type.STRUCT] = '"rec"';
Thrift.Protocol.Type[Thrift.Type.STRING] = '"str"';
Thrift.Protocol.Type[Thrift.Type.MAP] = '"map"';
Thrift.Protocol.Type[Thrift.Type.LIST] = '"lst"';
Thrift.Protocol.Type[Thrift.Type.SET] = '"set"';

/**
 * Thrift IDL type string to Id mapping.
 * @readonly
 * @see {@link Thrift.Type}
 */
Thrift.Protocol.RType = {};
Thrift.Protocol.RType.tf = Thrift.Type.BOOL;
Thrift.Protocol.RType.i8 = Thrift.Type.BYTE;
Thrift.Protocol.RType.i16 = Thrift.Type.I16;
Thrift.Protocol.RType.i32 = Thrift.Type.I32;
Thrift.Protocol.RType.i64 = Thrift.Type.I64;
Thrift.Protocol.RType.dbl = Thrift.Type.DOUBLE;
Thrift.Protocol.RType.rec = Thrift.Type.STRUCT;
Thrift.Protocol.RType.str = Thrift.Type.STRING;
Thrift.Protocol.RType.map = Thrift.Type.MAP;
Thrift.Protocol.RType.lst = Thrift.Type.LIST;
Thrift.Protocol.RType.set = Thrift.Type.SET;

/**
 * The TJSONProtocol version number.
 * @readonly
 * @const {number} Version
 * @memberof Thrift.Protocol
 */
 Thrift.Protocol.Version = 1;

Thrift.Protocol.prototype = {
    /**
     * Returns the underlying transport.
     * @readonly
     * @returns {Thrift.Transport} The underlying transport.
     */
    getTransport: function() {
        return this.transport;
    },

    /**
     * Serializes the beginning of a Thrift RPC message.
     * @param {string} name - The service method to call.
     * @param {Thrift.MessageType} messageType - The type of method call.
     * @param {number} seqid - The sequence number of this call (always 0 in Apache Thrift).
     */
    writeMessageBegin: function(name, messageType, seqid) {
        this.tstack = [];
        this.tpos = [];

        this.tstack.push([Thrift.Protocol.Version, '"' +
            name + '"', messageType, seqid]);
    },

    /**
     * Serializes the end of a Thrift RPC message.
     */
    writeMessageEnd: function() {
        var obj = this.tstack.pop();

        this.wobj = this.tstack.pop();
        this.wobj.push(obj);

        this.wbuf = '[' + this.wobj.join(',') + ']';

        this.transport.write(this.wbuf);
     },


    /**
     * Serializes the beginning of a struct.
     * @param {string} name - The name of the struct.
     */
    writeStructBegin: function(name) {
        this.tpos.push(this.tstack.length);
        this.tstack.push({});
    },

    /**
     * Serializes the end of a struct.
     */
    writeStructEnd: function() {

        var p = this.tpos.pop();
        var struct = this.tstack[p];
        var str = '{';
        var first = true;
        for (var key in struct) {
            if (first) {
                first = false;
            } else {
                str += ',';
            }

            str += key + ':' + struct[key];
        }

        str += '}';
        this.tstack[p] = str;
    },

    /**
     * Serializes the beginning of a struct field.
     * @param {string} name - The name of the field.
     * @param {Thrift.Protocol.Type} fieldType - The data type of the field.
     * @param {number} fieldId - The field's unique identifier.
     */
    writeFieldBegin: function(name, fieldType, fieldId) {
        this.tpos.push(this.tstack.length);
        this.tstack.push({ 'fieldId': '"' +
            fieldId + '"', 'fieldType': Thrift.Protocol.Type[fieldType]
        });

    },

    /**
     * Serializes the end of a field.
     */
    writeFieldEnd: function() {
        var value = this.tstack.pop();
        var fieldInfo = this.tstack.pop();

        this.tstack[this.tstack.length - 1][fieldInfo.fieldId] = '{' +
            fieldInfo.fieldType + ':' + value + '}';
        this.tpos.pop();
    },

    /**
     * Serializes the end of the set of fields for a struct.
     */
    writeFieldStop: function() {
        //na
    },

    /**
     * Serializes the beginning of a map collection.
     * @param {Thrift.Type} keyType - The data type of the key.
     * @param {Thrift.Type} valType - The data type of the value.
     * @param {number} [size] - The number of elements in the map (ignored).
     */
    writeMapBegin: function(keyType, valType, size) {
        this.tpos.push(this.tstack.length);
        this.tstack.push([Thrift.Protocol.Type[keyType],
            Thrift.Protocol.Type[valType], 0]);
    },

    /**
     * Serializes the end of a map.
     */
    writeMapEnd: function() {
        var p = this.tpos.pop();

        if (p == this.tstack.length) {
            return;
        }

        if ((this.tstack.length - p - 1) % 2 !== 0) {
            this.tstack.push('');
        }

        var size = (this.tstack.length - p - 1) / 2;

        this.tstack[p][this.tstack[p].length - 1] = size;

        var map = '}';
        var first = true;
        while (this.tstack.length > p + 1) {
            var v = this.tstack.pop();
            var k = this.tstack.pop();
            if (first) {
                first = false;
            } else {
                map = ',' + map;
            }

            if (! isNaN(k)) { k = '"' + k + '"'; } //json "keys" need to be strings
            map = k + ':' + v + map;
        }
        map = '{' + map;

        this.tstack[p].push(map);
        this.tstack[p] = '[' + this.tstack[p].join(',') + ']';
    },

    /**
     * Serializes the beginning of a list collection.
     * @param {Thrift.Type} elemType - The data type of the elements.
     * @param {number} size - The number of elements in the list.
     */
    writeListBegin: function(elemType, size) {
        this.tpos.push(this.tstack.length);
        this.tstack.push([Thrift.Protocol.Type[elemType], size]);
    },

    /**
     * Serializes the end of a list.
     */
    writeListEnd: function() {
        var p = this.tpos.pop();

        while (this.tstack.length > p + 1) {
            var tmpVal = this.tstack[p + 1];
            this.tstack.splice(p + 1, 1);
            this.tstack[p].push(tmpVal);
        }

        this.tstack[p] = '[' + this.tstack[p].join(',') + ']';
    },

    /**
     * Serializes the beginning of a set collection.
     * @param {Thrift.Type} elemType - The data type of the elements.
     * @param {number} size - The number of elements in the list.
     */
    writeSetBegin: function(elemType, size) {
        this.tpos.push(this.tstack.length);
        this.tstack.push([Thrift.Protocol.Type[elemType], size]);
    },

    /**
     * Serializes the end of a set.
     */
    writeSetEnd: function() {
        var p = this.tpos.pop();

        while (this.tstack.length > p + 1) {
            var tmpVal = this.tstack[p + 1];
            this.tstack.splice(p + 1, 1);
            this.tstack[p].push(tmpVal);
        }

        this.tstack[p] = '[' + this.tstack[p].join(',') + ']';
    },

    /** Serializes a boolean */
    writeBool: function(value) {
        this.tstack.push(value ? 1 : 0);
    },

    /** Serializes a number */
    writeByte: function(i8) {
        this.tstack.push(i8);
    },

    /** Serializes a number */
    writeI16: function(i16) {
        this.tstack.push(i16);
    },

    /** Serializes a number */
    writeI32: function(i32) {
        this.tstack.push(i32);
    },

    /** Serializes a number */
    writeI64: function(i64) {
        this.tstack.push(i64);
    },

    /** Serializes a number */
    writeDouble: function(dbl) {
        this.tstack.push(dbl);
    },

    /** Serializes a string */
    writeString: function(str) {
        // We do not encode uri components for wire transfer:
        if (str === null) {
            this.tstack.push(null);
        } else {
            // concat may be slower than building a byte buffer
            var escapedString = '';
            for (var i = 0; i < str.length; i++) {
                var ch = str.charAt(i);      // a single double quote: "
                if (ch === '\"') {
                    escapedString += '\\\"'; // write out as: \"
                } else if (ch === '\\') {    // a single backslash
                    escapedString += '\\\\'; // write out as double backslash
                } else if (ch === '\b') {    // a single backspace: invisible
                    escapedString += '\\b';  // write out as: \b"
                } else if (ch === '\f') {    // a single formfeed: invisible
                    escapedString += '\\f';  // write out as: \f"
                } else if (ch === '\n') {    // a single newline: invisible
                    escapedString += '\\n';  // write out as: \n"
                } else if (ch === '\r') {    // a single return: invisible
                    escapedString += '\\r';  // write out as: \r"
                } else if (ch === '\t') {    // a single tab: invisible
                    escapedString += '\\t';  // write out as: \t"
                } else {
                    escapedString += ch;     // Else it need not be escaped
                }
            }
            this.tstack.push('"' + escapedString + '"');
        }
    },

    /** Serializes a string */
    writeBinary: function(binary) {
        var str = '';
        if (typeof binary == 'string') {
            str = binary;
        } else if (binary instanceof Uint8Array) {
            var arr = binary;
            for (var i = 0; i < arr.length; ++i) {
                str += String.fromCharCode(arr[i]);
            }
        } else {
            throw new TypeError('writeBinary only accepts String or Uint8Array.');
        }
        this.tstack.push('"' + btoa(str) + '"');
    },

    /**
       @class
       @name AnonReadMessageBeginReturn
       @property {string} fname - The name of the service method.
       @property {Thrift.MessageType} mtype - The type of message call.
       @property {number} rseqid - The sequence number of the message (0 in Thrift RPC).
     */
    /**
     * Deserializes the beginning of a message.
     * @returns {AnonReadMessageBeginReturn}
     */
    readMessageBegin: function() {
        this.rstack = [];
        this.rpos = [];

        if (typeof JSON !== 'undefined' && typeof JSON.parse === 'function') {
            this.robj = JSON.parse(this.transport.readAll());
        } else if (typeof jQuery !== 'undefined') {
            this.robj = jQuery.parseJSON(this.transport.readAll());
        } else {
            this.robj = eval(this.transport.readAll());
        }

        var r = {};
        var version = this.robj.shift();

        if (version != Thrift.Protocol.Version) {
            throw 'Wrong thrift protocol version: ' + version;
        }

        r.fname = this.robj.shift();
        r.mtype = this.robj.shift();
        r.rseqid = this.robj.shift();


        //get to the main obj
        this.rstack.push(this.robj.shift());

        return r;
    },

    /** Deserializes the end of a message. */
    readMessageEnd: function() {
    },

    /**
     * Deserializes the beginning of a struct.
     * @param {string} [name] - The name of the struct (ignored)
     * @returns {object} - An object with an empty string fname property
     */
    readStructBegin: function(name) {
        var r = {};
        r.fname = '';

        //incase this is an array of structs
        if (this.rstack[this.rstack.length - 1] instanceof Array) {
            this.rstack.push(this.rstack[this.rstack.length - 1].shift());
        }

        return r;
    },

    /** Deserializes the end of a struct. */
    readStructEnd: function() {
        if (this.rstack[this.rstack.length - 2] instanceof Array) {
            this.rstack.pop();
        }
    },

    /**
       @class
       @name AnonReadFieldBeginReturn
       @property {string} fname - The name of the field (always '').
       @property {Thrift.Type} ftype - The data type of the field.
       @property {number} fid - The unique identifier of the field.
     */
    /**
     * Deserializes the beginning of a field.
     * @returns {AnonReadFieldBeginReturn}
     */
    readFieldBegin: function() {
        var r = {};

        var fid = -1;
        var ftype = Thrift.Type.STOP;

        //get a fieldId
        for (var f in (this.rstack[this.rstack.length - 1])) {
            if (f === null) {
              continue;
            }

            fid = parseInt(f, 10);
            this.rpos.push(this.rstack.length);

            var field = this.rstack[this.rstack.length - 1][fid];

            //remove so we don't see it again
            delete this.rstack[this.rstack.length - 1][fid];

            this.rstack.push(field);

            break;
        }

        if (fid != -1) {

            //should only be 1 of these but this is the only
            //way to match a key
            for (var i in (this.rstack[this.rstack.length - 1])) {
                if (Thrift.Protocol.RType[i] === null) {
                    continue;
                }

                ftype = Thrift.Protocol.RType[i];
                this.rstack[this.rstack.length - 1] =
                    this.rstack[this.rstack.length - 1][i];
            }
        }

        r.fname = '';
        r.ftype = ftype;
        r.fid = fid;

        return r;
    },

    /** Deserializes the end of a field. */
    readFieldEnd: function() {
        var pos = this.rpos.pop();

        //get back to the right place in the stack
        while (this.rstack.length > pos) {
            this.rstack.pop();
        }

    },

    /**
       @class
       @name AnonReadMapBeginReturn
       @property {Thrift.Type} ktype - The data type of the key.
       @property {Thrift.Type} vtype - The data type of the value.
       @property {number} size - The number of elements in the map.
     */
    /**
     * Deserializes the beginning of a map.
     * @returns {AnonReadMapBeginReturn}
     */
    readMapBegin: function() {
        var map = this.rstack.pop();
        var first = map.shift();
        if (first instanceof Array) {
          this.rstack.push(map);
          map = first;
          first = map.shift();
        }

        var r = {};
        r.ktype = Thrift.Protocol.RType[first];
        r.vtype = Thrift.Protocol.RType[map.shift()];
        r.size = map.shift();


        this.rpos.push(this.rstack.length);
        this.rstack.push(map.shift());

        return r;
    },

    /** Deserializes the end of a map. */
    readMapEnd: function() {
        this.readFieldEnd();
    },

    /**
       @class
       @name AnonReadColBeginReturn
       @property {Thrift.Type} etype - The data type of the element.
       @property {number} size - The number of elements in the collection.
     */
    /**
     * Deserializes the beginning of a list.
     * @returns {AnonReadColBeginReturn}
     */
    readListBegin: function() {
        var list = this.rstack[this.rstack.length - 1];

        var r = {};
        r.etype = Thrift.Protocol.RType[list.shift()];
        r.size = list.shift();

        this.rpos.push(this.rstack.length);
        this.rstack.push(list.shift());

        return r;
    },

    /** Deserializes the end of a list. */
    readListEnd: function() {
        this.readFieldEnd();
    },

    /**
     * Deserializes the beginning of a set.
     * @returns {AnonReadColBeginReturn}
     */
    readSetBegin: function(elemType, size) {
        return this.readListBegin(elemType, size);
    },

    /** Deserializes the end of a set. */
    readSetEnd: function() {
        return this.readListEnd();
    },

    /** Returns an object with a value property set to
     *  False unless the next number in the protocol buffer
     *  is 1, in which case the value property is True */
    readBool: function() {
        var r = this.readI32();

        if (r !== null && r.value == '1') {
            r.value = true;
        } else {
            r.value = false;
        }

        return r;
    },

    /** Returns the an object with a value property set to the
        next value found in the protocol buffer */
    readByte: function() {
        return this.readI32();
    },

    /** Returns the an object with a value property set to the
        next value found in the protocol buffer */
    readI16: function() {
        return this.readI32();
    },

    /** Returns the an object with a value property set to the
        next value found in the protocol buffer */
    readI32: function(f) {
        if (f === undefined) {
            f = this.rstack[this.rstack.length - 1];
        }

        var r = {};

        if (f instanceof Array) {
            if (f.length === 0) {
                r.value = undefined;
            } else {
                r.value = f.shift();
            }
        } else if (f instanceof Object) {
           for (var i in f) {
                if (i === null) {
                  continue;
                }
                this.rstack.push(f[i]);
                delete f[i];

                r.value = i;
                break;
           }
        } else {
            r.value = f;
            this.rstack.pop();
        }

        return r;
    },

    /** Returns the an object with a value property set to the
        next value found in the protocol buffer */
    readI64: function() {
        return this.readI32();
    },

    /** Returns the an object with a value property set to the
        next value found in the protocol buffer */
    readDouble: function() {
        return this.readI32();
    },

    /** Returns the an object with a value property set to the
        next value found in the protocol buffer */
    readString: function() {
        var r = this.readI32();
        return r;
    },

    /** Returns the an object with a value property set to the
        next value found in the protocol buffer */
    readBinary: function() {
        var r = this.readI32();
        r.value = atob(r.value);
        return r;
    },

    /**
     * Method to arbitrarily skip over data */
    skip: function(type) {
        var ret, i;
        switch (type) {
            case Thrift.Type.STOP:
                return null;

            case Thrift.Type.BOOL:
                return this.readBool();

            case Thrift.Type.BYTE:
                return this.readByte();

            case Thrift.Type.I16:
                return this.readI16();

            case Thrift.Type.I32:
                return this.readI32();

            case Thrift.Type.I64:
                return this.readI64();

            case Thrift.Type.DOUBLE:
                return this.readDouble();

            case Thrift.Type.STRING:
                return this.readString();

            case Thrift.Type.STRUCT:
                this.readStructBegin();
                while (true) {
                    ret = this.readFieldBegin();
                    if (ret.ftype == Thrift.Type.STOP) {
                        break;
                    }
                    this.skip(ret.ftype);
                    this.readFieldEnd();
                }
                this.readStructEnd();
                return null;

            case Thrift.Type.MAP:
                ret = this.readMapBegin();
                for (i = 0; i < ret.size; i++) {
                    if (i > 0) {
                        if (this.rstack.length > this.rpos[this.rpos.length - 1] + 1) {
                            this.rstack.pop();
                        }
                    }
                    this.skip(ret.ktype);
                    this.skip(ret.vtype);
                }
                this.readMapEnd();
                return null;

            case Thrift.Type.SET:
                ret = this.readSetBegin();
                for (i = 0; i < ret.size; i++) {
                    this.skip(ret.etype);
                }
                this.readSetEnd();
                return null;

            case Thrift.Type.LIST:
                ret = this.readListBegin();
                for (i = 0; i < ret.size; i++) {
                    this.skip(ret.etype);
                }
                this.readListEnd();
                return null;
        }
    }
};


/**
 * Initializes a MutilplexProtocol Implementation as a Wrapper for Thrift.Protocol
 * @constructor
 */
Thrift.MultiplexProtocol = function (srvName, trans, strictRead, strictWrite) {
    Thrift.Protocol.call(this, trans, strictRead, strictWrite);
    this.serviceName = srvName;
};
Thrift.inherits(Thrift.MultiplexProtocol, Thrift.Protocol, 'multiplexProtocol');

/** Override writeMessageBegin method of prototype*/
Thrift.MultiplexProtocol.prototype.writeMessageBegin = function (name, type, seqid) {

    if (type === Thrift.MessageType.CALL || type === Thrift.MessageType.ONEWAY) {
        Thrift.Protocol.prototype.writeMessageBegin.call(this, this.serviceName + ":" + name, type, seqid);
    } else {
        Thrift.Protocol.prototype.writeMessageBegin.call(this, name, type, seqid);
    }
};

Thrift.Multiplexer = function () {
    this.seqid = 0;
};

/** Instantiates a multiplexed client for a specific service
 * @constructor
 * @param {String} serviceName - The transport to serialize to/from.
 * @param {Thrift.ServiceClient} SCl - The Service Client Class
 * @param {Thrift.Transport} transport - Thrift.Transport instance which provides remote host:port
 * @example
 *    var mp = new Thrift.Multiplexer();
 *    var transport = new Thrift.Transport("http://localhost:9090/foo.thrift");
 *    var protocol = new Thrift.Protocol(transport);
 *    var client = mp.createClient('AuthService', AuthServiceClient, transport);
*/
Thrift.Multiplexer.prototype.createClient = function (serviceName, SCl, transport) {
    if (SCl.Client) {
        SCl = SCl.Client;
    }
    var self = this;
    SCl.prototype.new_seqid = function () {
        self.seqid += 1;
        return self.seqid;
    };
    var client = new SCl(new Thrift.MultiplexProtocol(serviceName, transport));

    return client;
};



var copyList, copyMap;

copyList = function(lst, types) {

  if (!lst) {return lst; }

  var type;

  if (types.shift === undefined) {
    type = types;
  }
  else {
    type = types[0];
  }
  var Type = type;

  var len = lst.length, result = [], i, val;
  for (i = 0; i < len; i++) {
    val = lst[i];
    if (type === null) {
      result.push(val);
    }
    else if (type === copyMap || type === copyList) {
      result.push(type(val, types.slice(1)));
    }
    else {
      result.push(new Type(val));
    }
  }
  return result;
};

copyMap = function(obj, types){

  if (!obj) {return obj; }

  var type;

  if (types.shift === undefined) {
    type = types;
  }
  else {
    type = types[0];
  }
  var Type = type;

  var result = {}, val;
  for(var prop in obj) {
    if(obj.hasOwnProperty(prop)) {
      val = obj[prop];
      if (type === null) {
        result[prop] = val;
      }
      else if (type === copyMap || type === copyList) {
        result[prop] = type(val, types.slice(1));
      }
      else {
        result[prop] = new Type(val);
      }
    }
  }
  return result;
};

Thrift.copyMap = copyMap;
Thrift.copyList = copyList;
//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


ExpiredGlance = function(args) {
  this.glanceId = null;
  this.partnerId = null;
  this.region = null;
  this.updatedAtInSecs = null;
  this.maxExpiryAcrossTzInSec = null;
  if (args) {
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.maxExpiryAcrossTzInSec !== undefined && args.maxExpiryAcrossTzInSec !== null) {
      this.maxExpiryAcrossTzInSec = args.maxExpiryAcrossTzInSec;
    }
  }
};
ExpiredGlance.prototype = {};
ExpiredGlance.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.maxExpiryAcrossTzInSec = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ExpiredGlance.prototype.write = function(output) {
  output.writeStructBegin('ExpiredGlance');
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 2);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 3);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.maxExpiryAcrossTzInSec !== null && this.maxExpiryAcrossTzInSec !== undefined) {
    output.writeFieldBegin('maxExpiryAcrossTzInSec', Thrift.Type.I64, 5);
    output.writeI64(this.maxExpiryAcrossTzInSec);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


AdInventoryCurrencyType = {
  '0' : 'USD',
  'USD' : 0,
  '1' : 'INR',
  'INR' : 1
};
AdInventoryDeliveryType = {
  '0' : 'Direct',
  'Direct' : 0
};
AdInventoryTrackerType = {
  '0' : 'Click',
  'Click' : 0,
  '1' : 'Impression',
  'Impression' : 1,
  '2' : 'AppOpen',
  'AppOpen' : 2,
  '3' : 'InstallComplete',
  'InstallComplete' : 3,
  '4' : 'Audience',
  'Audience' : 4,
  '5' : 'Peek',
  'Peek' : 5
};
AdInventoryOem = {
  '0' : 'XIAOMI',
  'XIAOMI' : 0,
  '1' : 'REAL_COLOURS',
  'REAL_COLOURS' : 1,
  '2' : 'SAMSUNG_LSS',
  'SAMSUNG_LSS' : 2,
  '3' : 'SAMSUNG_DLSS',
  'SAMSUNG_DLSS' : 3,
  '4' : 'VIVO',
  'VIVO' : 4,
  '5' : 'MOTOROLA',
  'MOTOROLA' : 5,
  '6' : 'OPPO',
  'OPPO' : 6,
  '7' : 'REALME_AOSP',
  'REALME_AOSP' : 7,
  '8' : 'REALME_GO',
  'REALME_GO' : 8,
  '9' : 'MOTOROLA_AOSP',
  'MOTOROLA_AOSP' : 9,
  '10' : 'MOTOROLA_GO',
  'MOTOROLA_GO' : 10,
  '11' : 'GIONEE',
  'GIONEE' : 11,
  '12' : 'SOFTBANK_MOTO',
  'SOFTBANK_MOTO' : 12,
  '13' : 'SOFTBANK_SHARP',
  'SOFTBANK_SHARP' : 13,
  '14' : 'SOFTBANK_SONY',
  'SOFTBANK_SONY' : 14,
  '15' : 'SOFTBANK_XIAOMI',
  'SOFTBANK_XIAOMI' : 15,
  '16' : 'TRACFONE',
  'TRACFONE' : 16,
  '17' : 'JIO',
  'JIO' : 17,
  '18' : 'TRANSSION',
  'TRANSSION' : 18
};
AdInventoryAdStatus = {
  '0' : 'Live',
  'Live' : 0,
  '1' : 'Created',
  'Created' : 1,
  '2' : 'Paused',
  'Paused' : 2
};
AdInventoryApp = {
  '0' : 'Glance',
  'Glance' : 0,
  '1' : 'Swish',
  'Swish' : 1,
  '2' : 'Roposo',
  'Roposo' : 2,
  '3' : 'Nostra',
  'Nostra' : 3
};
AdInventoryAdFormat = {
  '0' : 'Banner',
  'Banner' : 0,
  '1' : 'Rewarded',
  'Rewarded' : 1,
  '2' : 'Video',
  'Video' : 2,
  '3' : 'Interstitial',
  'Interstitial' : 3,
  '4' : 'NativeVideo',
  'NativeVideo' : 4,
  '5' : 'NativeDisplay',
  'NativeDisplay' : 5,
  '6' : 'NativeLockScreen',
  'NativeLockScreen' : 6
};
AdInventoryCampaignObjectiveCategory = {
  '0' : 'Awareness',
  'Awareness' : 0,
  '1' : 'Consideration',
  'Consideration' : 1,
  '2' : 'Conversion',
  'Conversion' : 2
};
AdInventoryCampaignObjectiveSubCategory = {
  '0' : 'REACH',
  'REACH' : 0,
  '1' : 'BRAND_AWARENESS',
  'BRAND_AWARENESS' : 1,
  '2' : 'TRAFFIC',
  'TRAFFIC' : 2,
  '3' : 'ENGAGEMENT',
  'ENGAGEMENT' : 3,
  '4' : 'APP_INSTALL',
  'APP_INSTALL' : 4,
  '5' : 'CONVERSION',
  'CONVERSION' : 5,
  '6' : 'VIDEO_VIEWS',
  'VIDEO_VIEWS' : 6,
  '7' : 'LEAD_GENERATION',
  'LEAD_GENERATION' : 7,
  '8' : 'CATALOGUE_SALES',
  'CATALOGUE_SALES' : 8,
  '9' : 'STORE_TRAFFIC',
  'STORE_TRAFFIC' : 9,
  '10' : 'REMARKETING',
  'REMARKETING' : 10
};
AdGroupDeliverySetting = function(args) {
  this.isS2SEnabled = null;
  this.adCoefficient = null;
  this.openInBackground = null;
  if (args) {
    if (args.isS2SEnabled !== undefined && args.isS2SEnabled !== null) {
      this.isS2SEnabled = args.isS2SEnabled;
    }
    if (args.adCoefficient !== undefined && args.adCoefficient !== null) {
      this.adCoefficient = args.adCoefficient;
    }
    if (args.openInBackground !== undefined && args.openInBackground !== null) {
      this.openInBackground = args.openInBackground;
    }
  }
};
AdGroupDeliverySetting.prototype = {};
AdGroupDeliverySetting.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.isS2SEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.adCoefficient = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.openInBackground = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdGroupDeliverySetting.prototype.write = function(output) {
  output.writeStructBegin('AdGroupDeliverySetting');
  if (this.isS2SEnabled !== null && this.isS2SEnabled !== undefined) {
    output.writeFieldBegin('isS2SEnabled', Thrift.Type.BOOL, 1);
    output.writeBool(this.isS2SEnabled);
    output.writeFieldEnd();
  }
  if (this.adCoefficient !== null && this.adCoefficient !== undefined) {
    output.writeFieldBegin('adCoefficient', Thrift.Type.I32, 2);
    output.writeI32(this.adCoefficient);
    output.writeFieldEnd();
  }
  if (this.openInBackground !== null && this.openInBackground !== undefined) {
    output.writeFieldBegin('openInBackground', Thrift.Type.BOOL, 3);
    output.writeBool(this.openInBackground);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryImpressionCapping = function(args) {
  this.impressionId = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.dailyImpressionsLimit = null;
  this.totalImpressionsLimit = null;
  this.dailyImpressionsRendered = null;
  this.totalImpressionsRendered = null;
  this.dailyImpressionsRunningDate = null;
  if (args) {
    if (args.impressionId !== undefined && args.impressionId !== null) {
      this.impressionId = args.impressionId;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.dailyImpressionsLimit !== undefined && args.dailyImpressionsLimit !== null) {
      this.dailyImpressionsLimit = args.dailyImpressionsLimit;
    }
    if (args.totalImpressionsLimit !== undefined && args.totalImpressionsLimit !== null) {
      this.totalImpressionsLimit = args.totalImpressionsLimit;
    }
    if (args.dailyImpressionsRendered !== undefined && args.dailyImpressionsRendered !== null) {
      this.dailyImpressionsRendered = args.dailyImpressionsRendered;
    }
    if (args.totalImpressionsRendered !== undefined && args.totalImpressionsRendered !== null) {
      this.totalImpressionsRendered = args.totalImpressionsRendered;
    }
    if (args.dailyImpressionsRunningDate !== undefined && args.dailyImpressionsRunningDate !== null) {
      this.dailyImpressionsRunningDate = args.dailyImpressionsRunningDate;
    }
  }
};
AdInventoryImpressionCapping.prototype = {};
AdInventoryImpressionCapping.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.impressionId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.dailyImpressionsLimit = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.totalImpressionsLimit = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.dailyImpressionsRendered = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I64) {
        this.totalImpressionsRendered = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.dailyImpressionsRunningDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryImpressionCapping.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryImpressionCapping');
  if (this.impressionId !== null && this.impressionId !== undefined) {
    output.writeFieldBegin('impressionId', Thrift.Type.STRING, 1);
    output.writeString(this.impressionId);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 2);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 3);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.dailyImpressionsLimit !== null && this.dailyImpressionsLimit !== undefined) {
    output.writeFieldBegin('dailyImpressionsLimit', Thrift.Type.I64, 4);
    output.writeI64(this.dailyImpressionsLimit);
    output.writeFieldEnd();
  }
  if (this.totalImpressionsLimit !== null && this.totalImpressionsLimit !== undefined) {
    output.writeFieldBegin('totalImpressionsLimit', Thrift.Type.I64, 5);
    output.writeI64(this.totalImpressionsLimit);
    output.writeFieldEnd();
  }
  if (this.dailyImpressionsRendered !== null && this.dailyImpressionsRendered !== undefined) {
    output.writeFieldBegin('dailyImpressionsRendered', Thrift.Type.I64, 6);
    output.writeI64(this.dailyImpressionsRendered);
    output.writeFieldEnd();
  }
  if (this.totalImpressionsRendered !== null && this.totalImpressionsRendered !== undefined) {
    output.writeFieldBegin('totalImpressionsRendered', Thrift.Type.I64, 7);
    output.writeI64(this.totalImpressionsRendered);
    output.writeFieldEnd();
  }
  if (this.dailyImpressionsRunningDate !== null && this.dailyImpressionsRunningDate !== undefined) {
    output.writeFieldBegin('dailyImpressionsRunningDate', Thrift.Type.STRING, 8);
    output.writeString(this.dailyImpressionsRunningDate);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventorySurface = function(args) {
  this.surfaceId = null;
  this.name = null;
  if (args) {
    if (args.surfaceId !== undefined && args.surfaceId !== null) {
      this.surfaceId = args.surfaceId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
  }
};
AdInventorySurface.prototype = {};
AdInventorySurface.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.surfaceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventorySurface.prototype.write = function(output) {
  output.writeStructBegin('AdInventorySurface');
  if (this.surfaceId !== null && this.surfaceId !== undefined) {
    output.writeFieldBegin('surfaceId', Thrift.Type.STRING, 1);
    output.writeString(this.surfaceId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryFrequencyCapping = function(args) {
  this.dailyImpressionLimit = null;
  this.weeklyImpressionLimit = null;
  if (args) {
    if (args.dailyImpressionLimit !== undefined && args.dailyImpressionLimit !== null) {
      this.dailyImpressionLimit = args.dailyImpressionLimit;
    }
    if (args.weeklyImpressionLimit !== undefined && args.weeklyImpressionLimit !== null) {
      this.weeklyImpressionLimit = args.weeklyImpressionLimit;
    }
  }
};
AdInventoryFrequencyCapping.prototype = {};
AdInventoryFrequencyCapping.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.dailyImpressionLimit = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.weeklyImpressionLimit = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryFrequencyCapping.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryFrequencyCapping');
  if (this.dailyImpressionLimit !== null && this.dailyImpressionLimit !== undefined) {
    output.writeFieldBegin('dailyImpressionLimit', Thrift.Type.I32, 1);
    output.writeI32(this.dailyImpressionLimit);
    output.writeFieldEnd();
  }
  if (this.weeklyImpressionLimit !== null && this.weeklyImpressionLimit !== undefined) {
    output.writeFieldBegin('weeklyImpressionLimit', Thrift.Type.I32, 2);
    output.writeI32(this.weeklyImpressionLimit);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryGlanceAdPlacement = function(args) {
  this.adPlacementId = null;
  this.name = null;
  if (args) {
    if (args.adPlacementId !== undefined && args.adPlacementId !== null) {
      this.adPlacementId = args.adPlacementId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
  }
};
AdInventoryGlanceAdPlacement.prototype = {};
AdInventoryGlanceAdPlacement.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adPlacementId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryGlanceAdPlacement.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryGlanceAdPlacement');
  if (this.adPlacementId !== null && this.adPlacementId !== undefined) {
    output.writeFieldBegin('adPlacementId', Thrift.Type.STRING, 1);
    output.writeString(this.adPlacementId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryCategory = function(args) {
  this.categoryId = null;
  this.name = null;
  if (args) {
    if (args.categoryId !== undefined && args.categoryId !== null) {
      this.categoryId = args.categoryId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
  }
};
AdInventoryCategory.prototype = {};
AdInventoryCategory.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.categoryId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryCategory.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryCategory');
  if (this.categoryId !== null && this.categoryId !== undefined) {
    output.writeFieldBegin('categoryId', Thrift.Type.STRING, 1);
    output.writeString(this.categoryId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryCampaignObjective = function(args) {
  this.adCampaignObjectiveId = null;
  this.category = null;
  this.subCategory = null;
  if (args) {
    if (args.adCampaignObjectiveId !== undefined && args.adCampaignObjectiveId !== null) {
      this.adCampaignObjectiveId = args.adCampaignObjectiveId;
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.subCategory !== undefined && args.subCategory !== null) {
      this.subCategory = args.subCategory;
    }
  }
};
AdInventoryCampaignObjective.prototype = {};
AdInventoryCampaignObjective.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adCampaignObjectiveId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.category = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.subCategory = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryCampaignObjective.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryCampaignObjective');
  if (this.adCampaignObjectiveId !== null && this.adCampaignObjectiveId !== undefined) {
    output.writeFieldBegin('adCampaignObjectiveId', Thrift.Type.STRING, 1);
    output.writeString(this.adCampaignObjectiveId);
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.I32, 2);
    output.writeI32(this.category);
    output.writeFieldEnd();
  }
  if (this.subCategory !== null && this.subCategory !== undefined) {
    output.writeFieldBegin('subCategory', Thrift.Type.I32, 3);
    output.writeI32(this.subCategory);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Catalogue = function(args) {
  this.categories = null;
  if (args) {
    if (args.categories !== undefined && args.categories !== null) {
      this.categories = Thrift.copyList(args.categories, [null]);
    }
  }
};
Catalogue.prototype = {};
Catalogue.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size0 = 0;
        var _rtmp34;
        this.categories = [];
        var _etype3 = 0;
        _rtmp34 = input.readListBegin();
        _etype3 = _rtmp34.etype;
        _size0 = _rtmp34.size;
        for (var _i5 = 0; _i5 < _size0; ++_i5)
        {
          var elem6 = null;
          elem6 = input.readString().value;
          this.categories.push(elem6);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Catalogue.prototype.write = function(output) {
  output.writeStructBegin('Catalogue');
  if (this.categories !== null && this.categories !== undefined) {
    output.writeFieldBegin('categories', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.categories.length);
    for (var iter7 in this.categories)
    {
      if (this.categories.hasOwnProperty(iter7))
      {
        iter7 = this.categories[iter7];
        output.writeString(iter7);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Viewability = function(args) {
  this.enabled = null;
  this.url = null;
  if (args) {
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
  }
};
Viewability.prototype = {};
Viewability.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Viewability.prototype.write = function(output) {
  output.writeStructBegin('Viewability');
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 1);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 2);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryAdvertiser = function(args) {
  this.advertiserId = null;
  this.name = null;
  this.advertiserPrimaryCategory = null;
  this.imageUrl = null;
  this.catalogue = null;
  this.viewability = null;
  if (args) {
    if (args.advertiserId !== undefined && args.advertiserId !== null) {
      this.advertiserId = args.advertiserId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.advertiserPrimaryCategory !== undefined && args.advertiserPrimaryCategory !== null) {
      this.advertiserPrimaryCategory = args.advertiserPrimaryCategory;
    }
    if (args.imageUrl !== undefined && args.imageUrl !== null) {
      this.imageUrl = args.imageUrl;
    }
    if (args.catalogue !== undefined && args.catalogue !== null) {
      this.catalogue = new Catalogue(args.catalogue);
    }
    if (args.viewability !== undefined && args.viewability !== null) {
      this.viewability = Thrift.copyMap(args.viewability, [Viewability]);
    }
  }
};
AdInventoryAdvertiser.prototype = {};
AdInventoryAdvertiser.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.advertiserId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.advertiserPrimaryCategory = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.imageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.catalogue = new Catalogue();
        this.catalogue.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.MAP) {
        var _size8 = 0;
        var _rtmp312;
        this.viewability = {};
        var _ktype9 = 0;
        var _vtype10 = 0;
        _rtmp312 = input.readMapBegin();
        _ktype9 = _rtmp312.ktype;
        _vtype10 = _rtmp312.vtype;
        _size8 = _rtmp312.size;
        for (var _i13 = 0; _i13 < _size8; ++_i13)
        {
          if (_i13 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key14 = null;
          var val15 = null;
          key14 = input.readString().value;
          val15 = new Viewability();
          val15.read(input);
          this.viewability[key14] = val15;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryAdvertiser.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryAdvertiser');
  if (this.advertiserId !== null && this.advertiserId !== undefined) {
    output.writeFieldBegin('advertiserId', Thrift.Type.STRING, 1);
    output.writeString(this.advertiserId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.advertiserPrimaryCategory !== null && this.advertiserPrimaryCategory !== undefined) {
    output.writeFieldBegin('advertiserPrimaryCategory', Thrift.Type.STRING, 3);
    output.writeString(this.advertiserPrimaryCategory);
    output.writeFieldEnd();
  }
  if (this.imageUrl !== null && this.imageUrl !== undefined) {
    output.writeFieldBegin('imageUrl', Thrift.Type.STRING, 4);
    output.writeString(this.imageUrl);
    output.writeFieldEnd();
  }
  if (this.catalogue !== null && this.catalogue !== undefined) {
    output.writeFieldBegin('catalogue', Thrift.Type.STRUCT, 5);
    this.catalogue.write(output);
    output.writeFieldEnd();
  }
  if (this.viewability !== null && this.viewability !== undefined) {
    output.writeFieldBegin('viewability', Thrift.Type.MAP, 6);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.STRUCT, Thrift.objectLength(this.viewability));
    for (var kiter16 in this.viewability)
    {
      if (this.viewability.hasOwnProperty(kiter16))
      {
        var viter17 = this.viewability[kiter16];
        output.writeString(kiter16);
        viter17.write(output);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryPageName = function(args) {
  this.pageNameId = null;
  this.name = null;
  if (args) {
    if (args.pageNameId !== undefined && args.pageNameId !== null) {
      this.pageNameId = args.pageNameId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
  }
};
AdInventoryPageName.prototype = {};
AdInventoryPageName.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.pageNameId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryPageName.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryPageName');
  if (this.pageNameId !== null && this.pageNameId !== undefined) {
    output.writeFieldBegin('pageNameId', Thrift.Type.STRING, 1);
    output.writeString(this.pageNameId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryProgrammaticType = function(args) {
  this.id = null;
  this.dealId = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.dealId !== undefined && args.dealId !== null) {
      this.dealId = args.dealId;
    }
  }
};
AdInventoryProgrammaticType.prototype = {};
AdInventoryProgrammaticType.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.dealId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryProgrammaticType.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryProgrammaticType');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.dealId !== null && this.dealId !== undefined) {
    output.writeFieldBegin('dealId', Thrift.Type.STRING, 2);
    output.writeString(this.dealId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryTrackers = function(args) {
  this.ctaText = null;
  this.ctaURL = null;
  if (args) {
    if (args.ctaText !== undefined && args.ctaText !== null) {
      this.ctaText = args.ctaText;
    }
    if (args.ctaURL !== undefined && args.ctaURL !== null) {
      this.ctaURL = args.ctaURL;
    }
  }
};
AdInventoryTrackers.prototype = {};
AdInventoryTrackers.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.ctaText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.ctaURL = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryTrackers.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryTrackers');
  if (this.ctaText !== null && this.ctaText !== undefined) {
    output.writeFieldBegin('ctaText', Thrift.Type.STRING, 1);
    output.writeString(this.ctaText);
    output.writeFieldEnd();
  }
  if (this.ctaURL !== null && this.ctaURL !== undefined) {
    output.writeFieldBegin('ctaURL', Thrift.Type.STRING, 2);
    output.writeString(this.ctaURL);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryAdGroupTrackers = function(args) {
  this.adFill = null;
  this.bidLoss = null;
  this.impression = null;
  this.click = null;
  this.installComplete = null;
  this.appOpen = null;
  this.audience = null;
  this.peek = null;
  if (args) {
    if (args.adFill !== undefined && args.adFill !== null) {
      this.adFill = Thrift.copyList(args.adFill, [null]);
    }
    if (args.bidLoss !== undefined && args.bidLoss !== null) {
      this.bidLoss = Thrift.copyList(args.bidLoss, [null]);
    }
    if (args.impression !== undefined && args.impression !== null) {
      this.impression = Thrift.copyList(args.impression, [null]);
    }
    if (args.click !== undefined && args.click !== null) {
      this.click = Thrift.copyList(args.click, [null]);
    }
    if (args.installComplete !== undefined && args.installComplete !== null) {
      this.installComplete = Thrift.copyList(args.installComplete, [null]);
    }
    if (args.appOpen !== undefined && args.appOpen !== null) {
      this.appOpen = Thrift.copyList(args.appOpen, [null]);
    }
    if (args.audience !== undefined && args.audience !== null) {
      this.audience = Thrift.copyList(args.audience, [null]);
    }
    if (args.peek !== undefined && args.peek !== null) {
      this.peek = Thrift.copyList(args.peek, [null]);
    }
  }
};
AdInventoryAdGroupTrackers.prototype = {};
AdInventoryAdGroupTrackers.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size18 = 0;
        var _rtmp322;
        this.adFill = [];
        var _etype21 = 0;
        _rtmp322 = input.readListBegin();
        _etype21 = _rtmp322.etype;
        _size18 = _rtmp322.size;
        for (var _i23 = 0; _i23 < _size18; ++_i23)
        {
          var elem24 = null;
          elem24 = input.readString().value;
          this.adFill.push(elem24);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size25 = 0;
        var _rtmp329;
        this.bidLoss = [];
        var _etype28 = 0;
        _rtmp329 = input.readListBegin();
        _etype28 = _rtmp329.etype;
        _size25 = _rtmp329.size;
        for (var _i30 = 0; _i30 < _size25; ++_i30)
        {
          var elem31 = null;
          elem31 = input.readString().value;
          this.bidLoss.push(elem31);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size32 = 0;
        var _rtmp336;
        this.impression = [];
        var _etype35 = 0;
        _rtmp336 = input.readListBegin();
        _etype35 = _rtmp336.etype;
        _size32 = _rtmp336.size;
        for (var _i37 = 0; _i37 < _size32; ++_i37)
        {
          var elem38 = null;
          elem38 = input.readString().value;
          this.impression.push(elem38);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size39 = 0;
        var _rtmp343;
        this.click = [];
        var _etype42 = 0;
        _rtmp343 = input.readListBegin();
        _etype42 = _rtmp343.etype;
        _size39 = _rtmp343.size;
        for (var _i44 = 0; _i44 < _size39; ++_i44)
        {
          var elem45 = null;
          elem45 = input.readString().value;
          this.click.push(elem45);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size46 = 0;
        var _rtmp350;
        this.installComplete = [];
        var _etype49 = 0;
        _rtmp350 = input.readListBegin();
        _etype49 = _rtmp350.etype;
        _size46 = _rtmp350.size;
        for (var _i51 = 0; _i51 < _size46; ++_i51)
        {
          var elem52 = null;
          elem52 = input.readString().value;
          this.installComplete.push(elem52);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size53 = 0;
        var _rtmp357;
        this.appOpen = [];
        var _etype56 = 0;
        _rtmp357 = input.readListBegin();
        _etype56 = _rtmp357.etype;
        _size53 = _rtmp357.size;
        for (var _i58 = 0; _i58 < _size53; ++_i58)
        {
          var elem59 = null;
          elem59 = input.readString().value;
          this.appOpen.push(elem59);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size60 = 0;
        var _rtmp364;
        this.audience = [];
        var _etype63 = 0;
        _rtmp364 = input.readListBegin();
        _etype63 = _rtmp364.etype;
        _size60 = _rtmp364.size;
        for (var _i65 = 0; _i65 < _size60; ++_i65)
        {
          var elem66 = null;
          elem66 = input.readString().value;
          this.audience.push(elem66);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size67 = 0;
        var _rtmp371;
        this.peek = [];
        var _etype70 = 0;
        _rtmp371 = input.readListBegin();
        _etype70 = _rtmp371.etype;
        _size67 = _rtmp371.size;
        for (var _i72 = 0; _i72 < _size67; ++_i72)
        {
          var elem73 = null;
          elem73 = input.readString().value;
          this.peek.push(elem73);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryAdGroupTrackers.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryAdGroupTrackers');
  if (this.adFill !== null && this.adFill !== undefined) {
    output.writeFieldBegin('adFill', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.adFill.length);
    for (var iter74 in this.adFill)
    {
      if (this.adFill.hasOwnProperty(iter74))
      {
        iter74 = this.adFill[iter74];
        output.writeString(iter74);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.bidLoss !== null && this.bidLoss !== undefined) {
    output.writeFieldBegin('bidLoss', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.bidLoss.length);
    for (var iter75 in this.bidLoss)
    {
      if (this.bidLoss.hasOwnProperty(iter75))
      {
        iter75 = this.bidLoss[iter75];
        output.writeString(iter75);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.impression !== null && this.impression !== undefined) {
    output.writeFieldBegin('impression', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRING, this.impression.length);
    for (var iter76 in this.impression)
    {
      if (this.impression.hasOwnProperty(iter76))
      {
        iter76 = this.impression[iter76];
        output.writeString(iter76);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.click !== null && this.click !== undefined) {
    output.writeFieldBegin('click', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRING, this.click.length);
    for (var iter77 in this.click)
    {
      if (this.click.hasOwnProperty(iter77))
      {
        iter77 = this.click[iter77];
        output.writeString(iter77);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.installComplete !== null && this.installComplete !== undefined) {
    output.writeFieldBegin('installComplete', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRING, this.installComplete.length);
    for (var iter78 in this.installComplete)
    {
      if (this.installComplete.hasOwnProperty(iter78))
      {
        iter78 = this.installComplete[iter78];
        output.writeString(iter78);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.appOpen !== null && this.appOpen !== undefined) {
    output.writeFieldBegin('appOpen', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.STRING, this.appOpen.length);
    for (var iter79 in this.appOpen)
    {
      if (this.appOpen.hasOwnProperty(iter79))
      {
        iter79 = this.appOpen[iter79];
        output.writeString(iter79);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.audience !== null && this.audience !== undefined) {
    output.writeFieldBegin('audience', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRING, this.audience.length);
    for (var iter80 in this.audience)
    {
      if (this.audience.hasOwnProperty(iter80))
      {
        iter80 = this.audience[iter80];
        output.writeString(iter80);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.peek !== null && this.peek !== undefined) {
    output.writeFieldBegin('peek', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRING, this.peek.length);
    for (var iter81 in this.peek)
    {
      if (this.peek.hasOwnProperty(iter81))
      {
        iter81 = this.peek[iter81];
        output.writeString(iter81);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryProgrammaticSettings = function(args) {
  this.oxBucketPriority = null;
  if (args) {
    if (args.oxBucketPriority !== undefined && args.oxBucketPriority !== null) {
      this.oxBucketPriority = args.oxBucketPriority;
    }
  }
};
AdInventoryProgrammaticSettings.prototype = {};
AdInventoryProgrammaticSettings.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.oxBucketPriority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryProgrammaticSettings.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryProgrammaticSettings');
  if (this.oxBucketPriority !== null && this.oxBucketPriority !== undefined) {
    output.writeFieldBegin('oxBucketPriority', Thrift.Type.I32, 1);
    output.writeI32(this.oxBucketPriority);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryAdUnitLineItem = function(args) {
  this.adUnitLineItemId = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.stackRank = null;
  this.externalAdUnitId = null;
  this.adFormat = null;
  this.client = null;
  this.channelType = null;
  this.programmaticSettings = null;
  if (args) {
    if (args.adUnitLineItemId !== undefined && args.adUnitLineItemId !== null) {
      this.adUnitLineItemId = args.adUnitLineItemId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field adUnitLineItemId is unset!');
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.stackRank !== undefined && args.stackRank !== null) {
      this.stackRank = args.stackRank;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field stackRank is unset!');
    }
    if (args.externalAdUnitId !== undefined && args.externalAdUnitId !== null) {
      this.externalAdUnitId = args.externalAdUnitId;
    }
    if (args.adFormat !== undefined && args.adFormat !== null) {
      this.adFormat = args.adFormat;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field adFormat is unset!');
    }
    if (args.client !== undefined && args.client !== null) {
      this.client = args.client;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field client is unset!');
    }
    if (args.channelType !== undefined && args.channelType !== null) {
      this.channelType = Thrift.copyList(args.channelType, [null]);
    }
    if (args.programmaticSettings !== undefined && args.programmaticSettings !== null) {
      this.programmaticSettings = new AdInventoryProgrammaticSettings(args.programmaticSettings);
    }
  }
};
AdInventoryAdUnitLineItem.prototype = {};
AdInventoryAdUnitLineItem.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adUnitLineItemId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.stackRank = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.externalAdUnitId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.adFormat = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.client = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size82 = 0;
        var _rtmp386;
        this.channelType = [];
        var _etype85 = 0;
        _rtmp386 = input.readListBegin();
        _etype85 = _rtmp386.etype;
        _size82 = _rtmp386.size;
        for (var _i87 = 0; _i87 < _size82; ++_i87)
        {
          var elem88 = null;
          elem88 = input.readString().value;
          this.channelType.push(elem88);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRUCT) {
        this.programmaticSettings = new AdInventoryProgrammaticSettings();
        this.programmaticSettings.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryAdUnitLineItem.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryAdUnitLineItem');
  if (this.adUnitLineItemId !== null && this.adUnitLineItemId !== undefined) {
    output.writeFieldBegin('adUnitLineItemId', Thrift.Type.STRING, 1);
    output.writeString(this.adUnitLineItemId);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 2);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 3);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.stackRank !== null && this.stackRank !== undefined) {
    output.writeFieldBegin('stackRank', Thrift.Type.I32, 4);
    output.writeI32(this.stackRank);
    output.writeFieldEnd();
  }
  if (this.externalAdUnitId !== null && this.externalAdUnitId !== undefined) {
    output.writeFieldBegin('externalAdUnitId', Thrift.Type.STRING, 5);
    output.writeString(this.externalAdUnitId);
    output.writeFieldEnd();
  }
  if (this.adFormat !== null && this.adFormat !== undefined) {
    output.writeFieldBegin('adFormat', Thrift.Type.STRING, 6);
    output.writeString(this.adFormat);
    output.writeFieldEnd();
  }
  if (this.client !== null && this.client !== undefined) {
    output.writeFieldBegin('client', Thrift.Type.STRING, 7);
    output.writeString(this.client);
    output.writeFieldEnd();
  }
  if (this.channelType !== null && this.channelType !== undefined) {
    output.writeFieldBegin('channelType', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRING, this.channelType.length);
    for (var iter89 in this.channelType)
    {
      if (this.channelType.hasOwnProperty(iter89))
      {
        iter89 = this.channelType[iter89];
        output.writeString(iter89);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.programmaticSettings !== null && this.programmaticSettings !== undefined) {
    output.writeFieldBegin('programmaticSettings', Thrift.Type.STRUCT, 9);
    this.programmaticSettings.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryAdUnit = function(args) {
  this.adUnitId = null;
  this.name = null;
  this.refreshRateInSecs = null;
  this.refreshFrequency = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.app = null;
  this.geography = null;
  this.oem = null;
  this.sdkVersion = null;
  this.adunit_line_item = null;
  this.surfaces = null;
  this.category = null;
  this.pageName = null;
  if (args) {
    if (args.adUnitId !== undefined && args.adUnitId !== null) {
      this.adUnitId = args.adUnitId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field adUnitId is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.refreshRateInSecs !== undefined && args.refreshRateInSecs !== null) {
      this.refreshRateInSecs = args.refreshRateInSecs;
    }
    if (args.refreshFrequency !== undefined && args.refreshFrequency !== null) {
      this.refreshFrequency = args.refreshFrequency;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.app !== undefined && args.app !== null) {
      this.app = args.app;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field app is unset!');
    }
    if (args.geography !== undefined && args.geography !== null) {
      this.geography = args.geography;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field geography is unset!');
    }
    if (args.oem !== undefined && args.oem !== null) {
      this.oem = args.oem;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field oem is unset!');
    }
    if (args.sdkVersion !== undefined && args.sdkVersion !== null) {
      this.sdkVersion = args.sdkVersion;
    }
    if (args.adunit_line_item !== undefined && args.adunit_line_item !== null) {
      this.adunit_line_item = Thrift.copyList(args.adunit_line_item, [AdInventoryAdUnitLineItem]);
    }
    if (args.surfaces !== undefined && args.surfaces !== null) {
      this.surfaces = Thrift.copyList(args.surfaces, [AdInventorySurface]);
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = new AdInventoryCategory(args.category);
    }
    if (args.pageName !== undefined && args.pageName !== null) {
      this.pageName = new AdInventoryPageName(args.pageName);
    }
  }
};
AdInventoryAdUnit.prototype = {};
AdInventoryAdUnit.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adUnitId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.refreshRateInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.refreshFrequency = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.app = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.geography = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I32) {
        this.oem = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I32) {
        this.sdkVersion = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size90 = 0;
        var _rtmp394;
        this.adunit_line_item = [];
        var _etype93 = 0;
        _rtmp394 = input.readListBegin();
        _etype93 = _rtmp394.etype;
        _size90 = _rtmp394.size;
        for (var _i95 = 0; _i95 < _size90; ++_i95)
        {
          var elem96 = null;
          elem96 = new AdInventoryAdUnitLineItem();
          elem96.read(input);
          this.adunit_line_item.push(elem96);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.LIST) {
        var _size97 = 0;
        var _rtmp3101;
        this.surfaces = [];
        var _etype100 = 0;
        _rtmp3101 = input.readListBegin();
        _etype100 = _rtmp3101.etype;
        _size97 = _rtmp3101.size;
        for (var _i102 = 0; _i102 < _size97; ++_i102)
        {
          var elem103 = null;
          elem103 = new AdInventorySurface();
          elem103.read(input);
          this.surfaces.push(elem103);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRUCT) {
        this.category = new AdInventoryCategory();
        this.category.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRUCT) {
        this.pageName = new AdInventoryPageName();
        this.pageName.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryAdUnit.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryAdUnit');
  if (this.adUnitId !== null && this.adUnitId !== undefined) {
    output.writeFieldBegin('adUnitId', Thrift.Type.STRING, 1);
    output.writeString(this.adUnitId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.refreshRateInSecs !== null && this.refreshRateInSecs !== undefined) {
    output.writeFieldBegin('refreshRateInSecs', Thrift.Type.I64, 3);
    output.writeI64(this.refreshRateInSecs);
    output.writeFieldEnd();
  }
  if (this.refreshFrequency !== null && this.refreshFrequency !== undefined) {
    output.writeFieldBegin('refreshFrequency', Thrift.Type.I64, 4);
    output.writeI64(this.refreshFrequency);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 5);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 6);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.app !== null && this.app !== undefined) {
    output.writeFieldBegin('app', Thrift.Type.I32, 7);
    output.writeI32(this.app);
    output.writeFieldEnd();
  }
  if (this.geography !== null && this.geography !== undefined) {
    output.writeFieldBegin('geography', Thrift.Type.STRING, 8);
    output.writeString(this.geography);
    output.writeFieldEnd();
  }
  if (this.oem !== null && this.oem !== undefined) {
    output.writeFieldBegin('oem', Thrift.Type.I32, 9);
    output.writeI32(this.oem);
    output.writeFieldEnd();
  }
  if (this.sdkVersion !== null && this.sdkVersion !== undefined) {
    output.writeFieldBegin('sdkVersion', Thrift.Type.I32, 10);
    output.writeI32(this.sdkVersion);
    output.writeFieldEnd();
  }
  if (this.adunit_line_item !== null && this.adunit_line_item !== undefined) {
    output.writeFieldBegin('adunit_line_item', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRUCT, this.adunit_line_item.length);
    for (var iter104 in this.adunit_line_item)
    {
      if (this.adunit_line_item.hasOwnProperty(iter104))
      {
        iter104 = this.adunit_line_item[iter104];
        iter104.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.surfaces !== null && this.surfaces !== undefined) {
    output.writeFieldBegin('surfaces', Thrift.Type.LIST, 12);
    output.writeListBegin(Thrift.Type.STRUCT, this.surfaces.length);
    for (var iter105 in this.surfaces)
    {
      if (this.surfaces.hasOwnProperty(iter105))
      {
        iter105 = this.surfaces[iter105];
        iter105.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRUCT, 13);
    this.category.write(output);
    output.writeFieldEnd();
  }
  if (this.pageName !== null && this.pageName !== undefined) {
    output.writeFieldBegin('pageName', Thrift.Type.STRUCT, 14);
    this.pageName.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryAdForCampaign = function(args) {
  this.adId = null;
  this.name = null;
  this.title = null;
  this.summary = null;
  this.status = null;
  this.creative = null;
  this.widthInPx = null;
  this.heightInPx = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.cta = null;
  this.adgroupId = null;
  this.impressionCapping = null;
  this.externalId = null;
  this.frequencyCapping = null;
  this.glanceDraft = null;
  this.glancePriority = null;
  this.creativeGroupId = null;
  this.glanceImpressionCapping = null;
  this.glanceDrafts = null;
  this.lastPublished = null;
  this.creativeRotationEnabled = null;
  this.impressionCappingSplitPercentage = null;
  if (args) {
    if (args.adId !== undefined && args.adId !== null) {
      this.adId = args.adId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.creative !== undefined && args.creative !== null) {
      this.creative = args.creative;
    }
    if (args.widthInPx !== undefined && args.widthInPx !== null) {
      this.widthInPx = args.widthInPx;
    }
    if (args.heightInPx !== undefined && args.heightInPx !== null) {
      this.heightInPx = args.heightInPx;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.cta !== undefined && args.cta !== null) {
      this.cta = new AdInventoryTrackers(args.cta);
    }
    if (args.adgroupId !== undefined && args.adgroupId !== null) {
      this.adgroupId = args.adgroupId;
    }
    if (args.impressionCapping !== undefined && args.impressionCapping !== null) {
      this.impressionCapping = new AdInventoryImpressionCapping(args.impressionCapping);
    }
    if (args.externalId !== undefined && args.externalId !== null) {
      this.externalId = args.externalId;
    }
    if (args.frequencyCapping !== undefined && args.frequencyCapping !== null) {
      this.frequencyCapping = new AdInventoryFrequencyCapping(args.frequencyCapping);
    }
    if (args.glanceDraft !== undefined && args.glanceDraft !== null) {
      this.glanceDraft = args.glanceDraft;
    }
    if (args.glancePriority !== undefined && args.glancePriority !== null) {
      this.glancePriority = args.glancePriority;
    }
    if (args.creativeGroupId !== undefined && args.creativeGroupId !== null) {
      this.creativeGroupId = args.creativeGroupId;
    }
    if (args.glanceImpressionCapping !== undefined && args.glanceImpressionCapping !== null) {
      this.glanceImpressionCapping = new ImpressionCapping(args.glanceImpressionCapping);
    }
    if (args.glanceDrafts !== undefined && args.glanceDrafts !== null) {
      this.glanceDrafts = Thrift.copyList(args.glanceDrafts, [null]);
    }
    if (args.lastPublished !== undefined && args.lastPublished !== null) {
      this.lastPublished = args.lastPublished;
    }
    if (args.creativeRotationEnabled !== undefined && args.creativeRotationEnabled !== null) {
      this.creativeRotationEnabled = args.creativeRotationEnabled;
    }
    if (args.impressionCappingSplitPercentage !== undefined && args.impressionCappingSplitPercentage !== null) {
      this.impressionCappingSplitPercentage = args.impressionCappingSplitPercentage;
    }
  }
};
AdInventoryAdForCampaign.prototype = {};
AdInventoryAdForCampaign.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.status = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.creative = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.widthInPx = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.heightInPx = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRUCT) {
        this.cta = new AdInventoryTrackers();
        this.cta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.adgroupId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRUCT) {
        this.impressionCapping = new AdInventoryImpressionCapping();
        this.impressionCapping.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.externalId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRUCT) {
        this.frequencyCapping = new AdInventoryFrequencyCapping();
        this.frequencyCapping.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.glanceDraft = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.I32) {
        this.glancePriority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.creativeGroupId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceImpressionCapping = new ImpressionCapping();
        this.glanceImpressionCapping.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.LIST) {
        var _size106 = 0;
        var _rtmp3110;
        this.glanceDrafts = [];
        var _etype109 = 0;
        _rtmp3110 = input.readListBegin();
        _etype109 = _rtmp3110.etype;
        _size106 = _rtmp3110.size;
        for (var _i111 = 0; _i111 < _size106; ++_i111)
        {
          var elem112 = null;
          elem112 = input.readString().value;
          this.glanceDrafts.push(elem112);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRING) {
        this.lastPublished = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.BOOL) {
        this.creativeRotationEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.I32) {
        this.impressionCappingSplitPercentage = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryAdForCampaign.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryAdForCampaign');
  if (this.adId !== null && this.adId !== undefined) {
    output.writeFieldBegin('adId', Thrift.Type.STRING, 1);
    output.writeString(this.adId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 3);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 4);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I32, 5);
    output.writeI32(this.status);
    output.writeFieldEnd();
  }
  if (this.creative !== null && this.creative !== undefined) {
    output.writeFieldBegin('creative', Thrift.Type.STRING, 6);
    output.writeString(this.creative);
    output.writeFieldEnd();
  }
  if (this.widthInPx !== null && this.widthInPx !== undefined) {
    output.writeFieldBegin('widthInPx', Thrift.Type.I32, 7);
    output.writeI32(this.widthInPx);
    output.writeFieldEnd();
  }
  if (this.heightInPx !== null && this.heightInPx !== undefined) {
    output.writeFieldBegin('heightInPx', Thrift.Type.I32, 8);
    output.writeI32(this.heightInPx);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 9);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 10);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.cta !== null && this.cta !== undefined) {
    output.writeFieldBegin('cta', Thrift.Type.STRUCT, 11);
    this.cta.write(output);
    output.writeFieldEnd();
  }
  if (this.adgroupId !== null && this.adgroupId !== undefined) {
    output.writeFieldBegin('adgroupId', Thrift.Type.STRING, 12);
    output.writeString(this.adgroupId);
    output.writeFieldEnd();
  }
  if (this.impressionCapping !== null && this.impressionCapping !== undefined) {
    output.writeFieldBegin('impressionCapping', Thrift.Type.STRUCT, 13);
    this.impressionCapping.write(output);
    output.writeFieldEnd();
  }
  if (this.externalId !== null && this.externalId !== undefined) {
    output.writeFieldBegin('externalId', Thrift.Type.STRING, 14);
    output.writeString(this.externalId);
    output.writeFieldEnd();
  }
  if (this.frequencyCapping !== null && this.frequencyCapping !== undefined) {
    output.writeFieldBegin('frequencyCapping', Thrift.Type.STRUCT, 15);
    this.frequencyCapping.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceDraft !== null && this.glanceDraft !== undefined) {
    output.writeFieldBegin('glanceDraft', Thrift.Type.STRING, 16);
    output.writeString(this.glanceDraft);
    output.writeFieldEnd();
  }
  if (this.glancePriority !== null && this.glancePriority !== undefined) {
    output.writeFieldBegin('glancePriority', Thrift.Type.I32, 17);
    output.writeI32(this.glancePriority);
    output.writeFieldEnd();
  }
  if (this.creativeGroupId !== null && this.creativeGroupId !== undefined) {
    output.writeFieldBegin('creativeGroupId', Thrift.Type.STRING, 18);
    output.writeString(this.creativeGroupId);
    output.writeFieldEnd();
  }
  if (this.glanceImpressionCapping !== null && this.glanceImpressionCapping !== undefined) {
    output.writeFieldBegin('glanceImpressionCapping', Thrift.Type.STRUCT, 19);
    this.glanceImpressionCapping.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceDrafts !== null && this.glanceDrafts !== undefined) {
    output.writeFieldBegin('glanceDrafts', Thrift.Type.LIST, 20);
    output.writeListBegin(Thrift.Type.STRING, this.glanceDrafts.length);
    for (var iter113 in this.glanceDrafts)
    {
      if (this.glanceDrafts.hasOwnProperty(iter113))
      {
        iter113 = this.glanceDrafts[iter113];
        output.writeString(iter113);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.lastPublished !== null && this.lastPublished !== undefined) {
    output.writeFieldBegin('lastPublished', Thrift.Type.STRING, 21);
    output.writeString(this.lastPublished);
    output.writeFieldEnd();
  }
  if (this.creativeRotationEnabled !== null && this.creativeRotationEnabled !== undefined) {
    output.writeFieldBegin('creativeRotationEnabled', Thrift.Type.BOOL, 22);
    output.writeBool(this.creativeRotationEnabled);
    output.writeFieldEnd();
  }
  if (this.impressionCappingSplitPercentage !== null && this.impressionCappingSplitPercentage !== undefined) {
    output.writeFieldBegin('impressionCappingSplitPercentage', Thrift.Type.I32, 23);
    output.writeI32(this.impressionCappingSplitPercentage);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryAdGroup = function(args) {
  this.adgroupId = null;
  this.name = null;
  this.startDate = null;
  this.endDate = null;
  this.ecpm = null;
  this.bucketPriority = null;
  this.budget = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.app = null;
  this.surfaces = null;
  this.adFormat = null;
  this.category = null;
  this.pageName = null;
  this.campaign = null;
  this.ruleIdsList = null;
  this.trackers = null;
  this.oems = null;
  this.campaignId = null;
  this.surfacesIds = null;
  this.categoryIds = null;
  this.adFormatIds = null;
  this.pageNameIds = null;
  this.ads = null;
  this.segmentIds = null;
  this.userNetworkTypes = null;
  this.preferredRegions = null;
  this.languageId = null;
  this.glanceRenderSessionProperties = null;
  this.deliverySetting = null;
  this.impressionCappingId = null;
  this.impressionCapping = null;
  this.frequencyCapping = null;
  this.externalId = null;
  if (args) {
    if (args.adgroupId !== undefined && args.adgroupId !== null) {
      this.adgroupId = args.adgroupId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.startDate !== undefined && args.startDate !== null) {
      this.startDate = args.startDate;
    }
    if (args.endDate !== undefined && args.endDate !== null) {
      this.endDate = args.endDate;
    }
    if (args.ecpm !== undefined && args.ecpm !== null) {
      this.ecpm = args.ecpm;
    }
    if (args.bucketPriority !== undefined && args.bucketPriority !== null) {
      this.bucketPriority = args.bucketPriority;
    }
    if (args.budget !== undefined && args.budget !== null) {
      this.budget = args.budget;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.app !== undefined && args.app !== null) {
      this.app = args.app;
    }
    if (args.surfaces !== undefined && args.surfaces !== null) {
      this.surfaces = Thrift.copyList(args.surfaces, [AdInventorySurface]);
    }
    if (args.adFormat !== undefined && args.adFormat !== null) {
      this.adFormat = Thrift.copyList(args.adFormat, [AdInventoryGlanceAdPlacement]);
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = Thrift.copyList(args.category, [AdInventoryCategory]);
    }
    if (args.pageName !== undefined && args.pageName !== null) {
      this.pageName = Thrift.copyList(args.pageName, [AdInventoryPageName]);
    }
    if (args.campaign !== undefined && args.campaign !== null) {
      this.campaign = args.campaign;
    }
    if (args.ruleIdsList !== undefined && args.ruleIdsList !== null) {
      this.ruleIdsList = Thrift.copyList(args.ruleIdsList, [RuleIds]);
    }
    if (args.trackers !== undefined && args.trackers !== null) {
      this.trackers = new AdInventoryAdGroupTrackers(args.trackers);
    }
    if (args.oems !== undefined && args.oems !== null) {
      this.oems = Thrift.copyList(args.oems, [null]);
    }
    if (args.campaignId !== undefined && args.campaignId !== null) {
      this.campaignId = args.campaignId;
    }
    if (args.surfacesIds !== undefined && args.surfacesIds !== null) {
      this.surfacesIds = Thrift.copyList(args.surfacesIds, [null]);
    }
    if (args.categoryIds !== undefined && args.categoryIds !== null) {
      this.categoryIds = Thrift.copyList(args.categoryIds, [null]);
    }
    if (args.adFormatIds !== undefined && args.adFormatIds !== null) {
      this.adFormatIds = Thrift.copyList(args.adFormatIds, [null]);
    }
    if (args.pageNameIds !== undefined && args.pageNameIds !== null) {
      this.pageNameIds = Thrift.copyList(args.pageNameIds, [null]);
    }
    if (args.ads !== undefined && args.ads !== null) {
      this.ads = Thrift.copyList(args.ads, [AdInventoryAdForCampaign]);
    }
    if (args.segmentIds !== undefined && args.segmentIds !== null) {
      this.segmentIds = Thrift.copyList(args.segmentIds, [null]);
    }
    if (args.userNetworkTypes !== undefined && args.userNetworkTypes !== null) {
      this.userNetworkTypes = Thrift.copyList(args.userNetworkTypes, [null]);
    }
    if (args.preferredRegions !== undefined && args.preferredRegions !== null) {
      this.preferredRegions = Thrift.copyList(args.preferredRegions, [null]);
    }
    if (args.languageId !== undefined && args.languageId !== null) {
      this.languageId = args.languageId;
    }
    if (args.glanceRenderSessionProperties !== undefined && args.glanceRenderSessionProperties !== null) {
      this.glanceRenderSessionProperties = Thrift.copyList(args.glanceRenderSessionProperties, [null]);
    }
    if (args.deliverySetting !== undefined && args.deliverySetting !== null) {
      this.deliverySetting = new AdGroupDeliverySetting(args.deliverySetting);
    }
    if (args.impressionCappingId !== undefined && args.impressionCappingId !== null) {
      this.impressionCappingId = args.impressionCappingId;
    }
    if (args.impressionCapping !== undefined && args.impressionCapping !== null) {
      this.impressionCapping = new AdInventoryImpressionCapping(args.impressionCapping);
    }
    if (args.frequencyCapping !== undefined && args.frequencyCapping !== null) {
      this.frequencyCapping = new AdInventoryFrequencyCapping(args.frequencyCapping);
    }
    if (args.externalId !== undefined && args.externalId !== null) {
      this.externalId = args.externalId;
    }
  }
};
AdInventoryAdGroup.prototype = {};
AdInventoryAdGroup.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adgroupId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.startDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.endDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.DOUBLE) {
        this.ecpm = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.bucketPriority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.budget = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I32) {
        this.app = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size114 = 0;
        var _rtmp3118;
        this.surfaces = [];
        var _etype117 = 0;
        _rtmp3118 = input.readListBegin();
        _etype117 = _rtmp3118.etype;
        _size114 = _rtmp3118.size;
        for (var _i119 = 0; _i119 < _size114; ++_i119)
        {
          var elem120 = null;
          elem120 = new AdInventorySurface();
          elem120.read(input);
          this.surfaces.push(elem120);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.LIST) {
        var _size121 = 0;
        var _rtmp3125;
        this.adFormat = [];
        var _etype124 = 0;
        _rtmp3125 = input.readListBegin();
        _etype124 = _rtmp3125.etype;
        _size121 = _rtmp3125.size;
        for (var _i126 = 0; _i126 < _size121; ++_i126)
        {
          var elem127 = null;
          elem127 = new AdInventoryGlanceAdPlacement();
          elem127.read(input);
          this.adFormat.push(elem127);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size128 = 0;
        var _rtmp3132;
        this.category = [];
        var _etype131 = 0;
        _rtmp3132 = input.readListBegin();
        _etype131 = _rtmp3132.etype;
        _size128 = _rtmp3132.size;
        for (var _i133 = 0; _i133 < _size128; ++_i133)
        {
          var elem134 = null;
          elem134 = new AdInventoryCategory();
          elem134.read(input);
          this.category.push(elem134);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.LIST) {
        var _size135 = 0;
        var _rtmp3139;
        this.pageName = [];
        var _etype138 = 0;
        _rtmp3139 = input.readListBegin();
        _etype138 = _rtmp3139.etype;
        _size135 = _rtmp3139.size;
        for (var _i140 = 0; _i140 < _size135; ++_i140)
        {
          var elem141 = null;
          elem141 = new AdInventoryPageName();
          elem141.read(input);
          this.pageName.push(elem141);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.campaign = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.LIST) {
        var _size142 = 0;
        var _rtmp3146;
        this.ruleIdsList = [];
        var _etype145 = 0;
        _rtmp3146 = input.readListBegin();
        _etype145 = _rtmp3146.etype;
        _size142 = _rtmp3146.size;
        for (var _i147 = 0; _i147 < _size142; ++_i147)
        {
          var elem148 = null;
          elem148 = new RuleIds();
          elem148.read(input);
          this.ruleIdsList.push(elem148);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRUCT) {
        this.trackers = new AdInventoryAdGroupTrackers();
        this.trackers.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.LIST) {
        var _size149 = 0;
        var _rtmp3153;
        this.oems = [];
        var _etype152 = 0;
        _rtmp3153 = input.readListBegin();
        _etype152 = _rtmp3153.etype;
        _size149 = _rtmp3153.size;
        for (var _i154 = 0; _i154 < _size149; ++_i154)
        {
          var elem155 = null;
          elem155 = input.readI32().value;
          this.oems.push(elem155);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.campaignId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.LIST) {
        var _size156 = 0;
        var _rtmp3160;
        this.surfacesIds = [];
        var _etype159 = 0;
        _rtmp3160 = input.readListBegin();
        _etype159 = _rtmp3160.etype;
        _size156 = _rtmp3160.size;
        for (var _i161 = 0; _i161 < _size156; ++_i161)
        {
          var elem162 = null;
          elem162 = input.readString().value;
          this.surfacesIds.push(elem162);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.LIST) {
        var _size163 = 0;
        var _rtmp3167;
        this.categoryIds = [];
        var _etype166 = 0;
        _rtmp3167 = input.readListBegin();
        _etype166 = _rtmp3167.etype;
        _size163 = _rtmp3167.size;
        for (var _i168 = 0; _i168 < _size163; ++_i168)
        {
          var elem169 = null;
          elem169 = input.readString().value;
          this.categoryIds.push(elem169);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.LIST) {
        var _size170 = 0;
        var _rtmp3174;
        this.adFormatIds = [];
        var _etype173 = 0;
        _rtmp3174 = input.readListBegin();
        _etype173 = _rtmp3174.etype;
        _size170 = _rtmp3174.size;
        for (var _i175 = 0; _i175 < _size170; ++_i175)
        {
          var elem176 = null;
          elem176 = input.readString().value;
          this.adFormatIds.push(elem176);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.LIST) {
        var _size177 = 0;
        var _rtmp3181;
        this.pageNameIds = [];
        var _etype180 = 0;
        _rtmp3181 = input.readListBegin();
        _etype180 = _rtmp3181.etype;
        _size177 = _rtmp3181.size;
        for (var _i182 = 0; _i182 < _size177; ++_i182)
        {
          var elem183 = null;
          elem183 = input.readString().value;
          this.pageNameIds.push(elem183);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.LIST) {
        var _size184 = 0;
        var _rtmp3188;
        this.ads = [];
        var _etype187 = 0;
        _rtmp3188 = input.readListBegin();
        _etype187 = _rtmp3188.etype;
        _size184 = _rtmp3188.size;
        for (var _i189 = 0; _i189 < _size184; ++_i189)
        {
          var elem190 = null;
          elem190 = new AdInventoryAdForCampaign();
          elem190.read(input);
          this.ads.push(elem190);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.SET) {
        var _size191 = 0;
        var _rtmp3195;
        this.segmentIds = [];
        var _etype194 = 0;
        _rtmp3195 = input.readSetBegin();
        _etype194 = _rtmp3195.etype;
        _size191 = _rtmp3195.size;
        for (var _i196 = 0; _i196 < _size191; ++_i196)
        {
          var elem197 = null;
          elem197 = input.readI32().value;
          this.segmentIds.push(elem197);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.SET) {
        var _size198 = 0;
        var _rtmp3202;
        this.userNetworkTypes = [];
        var _etype201 = 0;
        _rtmp3202 = input.readSetBegin();
        _etype201 = _rtmp3202.etype;
        _size198 = _rtmp3202.size;
        for (var _i203 = 0; _i203 < _size198; ++_i203)
        {
          var elem204 = null;
          elem204 = input.readI32().value;
          this.userNetworkTypes.push(elem204);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.SET) {
        var _size205 = 0;
        var _rtmp3209;
        this.preferredRegions = [];
        var _etype208 = 0;
        _rtmp3209 = input.readSetBegin();
        _etype208 = _rtmp3209.etype;
        _size205 = _rtmp3209.size;
        for (var _i210 = 0; _i210 < _size205; ++_i210)
        {
          var elem211 = null;
          elem211 = input.readI32().value;
          this.preferredRegions.push(elem211);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.STRING) {
        this.languageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.SET) {
        var _size212 = 0;
        var _rtmp3216;
        this.glanceRenderSessionProperties = [];
        var _etype215 = 0;
        _rtmp3216 = input.readSetBegin();
        _etype215 = _rtmp3216.etype;
        _size212 = _rtmp3216.size;
        for (var _i217 = 0; _i217 < _size212; ++_i217)
        {
          var elem218 = null;
          elem218 = input.readI32().value;
          this.glanceRenderSessionProperties.push(elem218);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.STRUCT) {
        this.deliverySetting = new AdGroupDeliverySetting();
        this.deliverySetting.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.STRING) {
        this.impressionCappingId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRUCT) {
        this.impressionCapping = new AdInventoryImpressionCapping();
        this.impressionCapping.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.STRUCT) {
        this.frequencyCapping = new AdInventoryFrequencyCapping();
        this.frequencyCapping.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.STRING) {
        this.externalId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryAdGroup.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryAdGroup');
  if (this.adgroupId !== null && this.adgroupId !== undefined) {
    output.writeFieldBegin('adgroupId', Thrift.Type.STRING, 1);
    output.writeString(this.adgroupId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.startDate !== null && this.startDate !== undefined) {
    output.writeFieldBegin('startDate', Thrift.Type.STRING, 3);
    output.writeString(this.startDate);
    output.writeFieldEnd();
  }
  if (this.endDate !== null && this.endDate !== undefined) {
    output.writeFieldBegin('endDate', Thrift.Type.STRING, 4);
    output.writeString(this.endDate);
    output.writeFieldEnd();
  }
  if (this.ecpm !== null && this.ecpm !== undefined) {
    output.writeFieldBegin('ecpm', Thrift.Type.DOUBLE, 5);
    output.writeDouble(this.ecpm);
    output.writeFieldEnd();
  }
  if (this.bucketPriority !== null && this.bucketPriority !== undefined) {
    output.writeFieldBegin('bucketPriority', Thrift.Type.I32, 6);
    output.writeI32(this.bucketPriority);
    output.writeFieldEnd();
  }
  if (this.budget !== null && this.budget !== undefined) {
    output.writeFieldBegin('budget', Thrift.Type.I32, 7);
    output.writeI32(this.budget);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 8);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 9);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.app !== null && this.app !== undefined) {
    output.writeFieldBegin('app', Thrift.Type.I32, 10);
    output.writeI32(this.app);
    output.writeFieldEnd();
  }
  if (this.surfaces !== null && this.surfaces !== undefined) {
    output.writeFieldBegin('surfaces', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRUCT, this.surfaces.length);
    for (var iter219 in this.surfaces)
    {
      if (this.surfaces.hasOwnProperty(iter219))
      {
        iter219 = this.surfaces[iter219];
        iter219.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.adFormat !== null && this.adFormat !== undefined) {
    output.writeFieldBegin('adFormat', Thrift.Type.LIST, 12);
    output.writeListBegin(Thrift.Type.STRUCT, this.adFormat.length);
    for (var iter220 in this.adFormat)
    {
      if (this.adFormat.hasOwnProperty(iter220))
      {
        iter220 = this.adFormat[iter220];
        iter220.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRUCT, this.category.length);
    for (var iter221 in this.category)
    {
      if (this.category.hasOwnProperty(iter221))
      {
        iter221 = this.category[iter221];
        iter221.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.pageName !== null && this.pageName !== undefined) {
    output.writeFieldBegin('pageName', Thrift.Type.LIST, 14);
    output.writeListBegin(Thrift.Type.STRUCT, this.pageName.length);
    for (var iter222 in this.pageName)
    {
      if (this.pageName.hasOwnProperty(iter222))
      {
        iter222 = this.pageName[iter222];
        iter222.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.campaign !== null && this.campaign !== undefined) {
    output.writeFieldBegin('campaign', Thrift.Type.STRING, 15);
    output.writeString(this.campaign);
    output.writeFieldEnd();
  }
  if (this.ruleIdsList !== null && this.ruleIdsList !== undefined) {
    output.writeFieldBegin('ruleIdsList', Thrift.Type.LIST, 16);
    output.writeListBegin(Thrift.Type.STRUCT, this.ruleIdsList.length);
    for (var iter223 in this.ruleIdsList)
    {
      if (this.ruleIdsList.hasOwnProperty(iter223))
      {
        iter223 = this.ruleIdsList[iter223];
        iter223.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.trackers !== null && this.trackers !== undefined) {
    output.writeFieldBegin('trackers', Thrift.Type.STRUCT, 17);
    this.trackers.write(output);
    output.writeFieldEnd();
  }
  if (this.oems !== null && this.oems !== undefined) {
    output.writeFieldBegin('oems', Thrift.Type.LIST, 18);
    output.writeListBegin(Thrift.Type.I32, this.oems.length);
    for (var iter224 in this.oems)
    {
      if (this.oems.hasOwnProperty(iter224))
      {
        iter224 = this.oems[iter224];
        output.writeI32(iter224);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.campaignId !== null && this.campaignId !== undefined) {
    output.writeFieldBegin('campaignId', Thrift.Type.STRING, 19);
    output.writeString(this.campaignId);
    output.writeFieldEnd();
  }
  if (this.surfacesIds !== null && this.surfacesIds !== undefined) {
    output.writeFieldBegin('surfacesIds', Thrift.Type.LIST, 20);
    output.writeListBegin(Thrift.Type.STRING, this.surfacesIds.length);
    for (var iter225 in this.surfacesIds)
    {
      if (this.surfacesIds.hasOwnProperty(iter225))
      {
        iter225 = this.surfacesIds[iter225];
        output.writeString(iter225);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.categoryIds !== null && this.categoryIds !== undefined) {
    output.writeFieldBegin('categoryIds', Thrift.Type.LIST, 21);
    output.writeListBegin(Thrift.Type.STRING, this.categoryIds.length);
    for (var iter226 in this.categoryIds)
    {
      if (this.categoryIds.hasOwnProperty(iter226))
      {
        iter226 = this.categoryIds[iter226];
        output.writeString(iter226);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.adFormatIds !== null && this.adFormatIds !== undefined) {
    output.writeFieldBegin('adFormatIds', Thrift.Type.LIST, 22);
    output.writeListBegin(Thrift.Type.STRING, this.adFormatIds.length);
    for (var iter227 in this.adFormatIds)
    {
      if (this.adFormatIds.hasOwnProperty(iter227))
      {
        iter227 = this.adFormatIds[iter227];
        output.writeString(iter227);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.pageNameIds !== null && this.pageNameIds !== undefined) {
    output.writeFieldBegin('pageNameIds', Thrift.Type.LIST, 23);
    output.writeListBegin(Thrift.Type.STRING, this.pageNameIds.length);
    for (var iter228 in this.pageNameIds)
    {
      if (this.pageNameIds.hasOwnProperty(iter228))
      {
        iter228 = this.pageNameIds[iter228];
        output.writeString(iter228);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.ads !== null && this.ads !== undefined) {
    output.writeFieldBegin('ads', Thrift.Type.LIST, 24);
    output.writeListBegin(Thrift.Type.STRUCT, this.ads.length);
    for (var iter229 in this.ads)
    {
      if (this.ads.hasOwnProperty(iter229))
      {
        iter229 = this.ads[iter229];
        iter229.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.segmentIds !== null && this.segmentIds !== undefined) {
    output.writeFieldBegin('segmentIds', Thrift.Type.SET, 25);
    output.writeSetBegin(Thrift.Type.I32, this.segmentIds.length);
    for (var iter230 in this.segmentIds)
    {
      if (this.segmentIds.hasOwnProperty(iter230))
      {
        iter230 = this.segmentIds[iter230];
        output.writeI32(iter230);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.userNetworkTypes !== null && this.userNetworkTypes !== undefined) {
    output.writeFieldBegin('userNetworkTypes', Thrift.Type.SET, 26);
    output.writeSetBegin(Thrift.Type.I32, this.userNetworkTypes.length);
    for (var iter231 in this.userNetworkTypes)
    {
      if (this.userNetworkTypes.hasOwnProperty(iter231))
      {
        iter231 = this.userNetworkTypes[iter231];
        output.writeI32(iter231);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.preferredRegions !== null && this.preferredRegions !== undefined) {
    output.writeFieldBegin('preferredRegions', Thrift.Type.SET, 27);
    output.writeSetBegin(Thrift.Type.I32, this.preferredRegions.length);
    for (var iter232 in this.preferredRegions)
    {
      if (this.preferredRegions.hasOwnProperty(iter232))
      {
        iter232 = this.preferredRegions[iter232];
        output.writeI32(iter232);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.languageId !== null && this.languageId !== undefined) {
    output.writeFieldBegin('languageId', Thrift.Type.STRING, 28);
    output.writeString(this.languageId);
    output.writeFieldEnd();
  }
  if (this.glanceRenderSessionProperties !== null && this.glanceRenderSessionProperties !== undefined) {
    output.writeFieldBegin('glanceRenderSessionProperties', Thrift.Type.SET, 29);
    output.writeSetBegin(Thrift.Type.I32, this.glanceRenderSessionProperties.length);
    for (var iter233 in this.glanceRenderSessionProperties)
    {
      if (this.glanceRenderSessionProperties.hasOwnProperty(iter233))
      {
        iter233 = this.glanceRenderSessionProperties[iter233];
        output.writeI32(iter233);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.deliverySetting !== null && this.deliverySetting !== undefined) {
    output.writeFieldBegin('deliverySetting', Thrift.Type.STRUCT, 30);
    this.deliverySetting.write(output);
    output.writeFieldEnd();
  }
  if (this.impressionCappingId !== null && this.impressionCappingId !== undefined) {
    output.writeFieldBegin('impressionCappingId', Thrift.Type.STRING, 31);
    output.writeString(this.impressionCappingId);
    output.writeFieldEnd();
  }
  if (this.impressionCapping !== null && this.impressionCapping !== undefined) {
    output.writeFieldBegin('impressionCapping', Thrift.Type.STRUCT, 32);
    this.impressionCapping.write(output);
    output.writeFieldEnd();
  }
  if (this.frequencyCapping !== null && this.frequencyCapping !== undefined) {
    output.writeFieldBegin('frequencyCapping', Thrift.Type.STRUCT, 33);
    this.frequencyCapping.write(output);
    output.writeFieldEnd();
  }
  if (this.externalId !== null && this.externalId !== undefined) {
    output.writeFieldBegin('externalId', Thrift.Type.STRING, 34);
    output.writeString(this.externalId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryCampaign = function(args) {
  this.campaignId = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.name = null;
  this.advertiser = null;
  this.adCampaignObjective = null;
  this.advertiserId = null;
  this.adCampaignObjectiveId = null;
  this.currencyType = null;
  this.deliveryType = null;
  this.campaignType = null;
  this.budgetAmount = null;
  this.adgroups = null;
  if (args) {
    if (args.campaignId !== undefined && args.campaignId !== null) {
      this.campaignId = args.campaignId;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.advertiser !== undefined && args.advertiser !== null) {
      this.advertiser = new AdInventoryAdvertiser(args.advertiser);
    }
    if (args.adCampaignObjective !== undefined && args.adCampaignObjective !== null) {
      this.adCampaignObjective = new AdInventoryCampaignObjective(args.adCampaignObjective);
    }
    if (args.advertiserId !== undefined && args.advertiserId !== null) {
      this.advertiserId = args.advertiserId;
    }
    if (args.adCampaignObjectiveId !== undefined && args.adCampaignObjectiveId !== null) {
      this.adCampaignObjectiveId = args.adCampaignObjectiveId;
    }
    if (args.currencyType !== undefined && args.currencyType !== null) {
      this.currencyType = args.currencyType;
    }
    if (args.deliveryType !== undefined && args.deliveryType !== null) {
      this.deliveryType = args.deliveryType;
    }
    if (args.campaignType !== undefined && args.campaignType !== null) {
      this.campaignType = args.campaignType;
    }
    if (args.budgetAmount !== undefined && args.budgetAmount !== null) {
      this.budgetAmount = args.budgetAmount;
    }
    if (args.adgroups !== undefined && args.adgroups !== null) {
      this.adgroups = Thrift.copyList(args.adgroups, [AdInventoryAdGroup]);
    }
  }
};
AdInventoryCampaign.prototype = {};
AdInventoryCampaign.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.campaignId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.advertiser = new AdInventoryAdvertiser();
        this.advertiser.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.adCampaignObjective = new AdInventoryCampaignObjective();
        this.adCampaignObjective.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.advertiserId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.adCampaignObjectiveId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I32) {
        this.currencyType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I32) {
        this.deliveryType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.campaignType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I32) {
        this.budgetAmount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size234 = 0;
        var _rtmp3238;
        this.adgroups = [];
        var _etype237 = 0;
        _rtmp3238 = input.readListBegin();
        _etype237 = _rtmp3238.etype;
        _size234 = _rtmp3238.size;
        for (var _i239 = 0; _i239 < _size234; ++_i239)
        {
          var elem240 = null;
          elem240 = new AdInventoryAdGroup();
          elem240.read(input);
          this.adgroups.push(elem240);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryCampaign.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryCampaign');
  if (this.campaignId !== null && this.campaignId !== undefined) {
    output.writeFieldBegin('campaignId', Thrift.Type.STRING, 1);
    output.writeString(this.campaignId);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 2);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 3);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 4);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.advertiser !== null && this.advertiser !== undefined) {
    output.writeFieldBegin('advertiser', Thrift.Type.STRUCT, 5);
    this.advertiser.write(output);
    output.writeFieldEnd();
  }
  if (this.adCampaignObjective !== null && this.adCampaignObjective !== undefined) {
    output.writeFieldBegin('adCampaignObjective', Thrift.Type.STRUCT, 6);
    this.adCampaignObjective.write(output);
    output.writeFieldEnd();
  }
  if (this.advertiserId !== null && this.advertiserId !== undefined) {
    output.writeFieldBegin('advertiserId', Thrift.Type.STRING, 7);
    output.writeString(this.advertiserId);
    output.writeFieldEnd();
  }
  if (this.adCampaignObjectiveId !== null && this.adCampaignObjectiveId !== undefined) {
    output.writeFieldBegin('adCampaignObjectiveId', Thrift.Type.STRING, 8);
    output.writeString(this.adCampaignObjectiveId);
    output.writeFieldEnd();
  }
  if (this.currencyType !== null && this.currencyType !== undefined) {
    output.writeFieldBegin('currencyType', Thrift.Type.I32, 9);
    output.writeI32(this.currencyType);
    output.writeFieldEnd();
  }
  if (this.deliveryType !== null && this.deliveryType !== undefined) {
    output.writeFieldBegin('deliveryType', Thrift.Type.I32, 10);
    output.writeI32(this.deliveryType);
    output.writeFieldEnd();
  }
  if (this.campaignType !== null && this.campaignType !== undefined) {
    output.writeFieldBegin('campaignType', Thrift.Type.STRING, 11);
    output.writeString(this.campaignType);
    output.writeFieldEnd();
  }
  if (this.budgetAmount !== null && this.budgetAmount !== undefined) {
    output.writeFieldBegin('budgetAmount', Thrift.Type.I32, 12);
    output.writeI32(this.budgetAmount);
    output.writeFieldEnd();
  }
  if (this.adgroups !== null && this.adgroups !== undefined) {
    output.writeFieldBegin('adgroups', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRUCT, this.adgroups.length);
    for (var iter241 in this.adgroups)
    {
      if (this.adgroups.hasOwnProperty(iter241))
      {
        iter241 = this.adgroups[iter241];
        iter241.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryCreativeItem = function(args) {
  this.creativeItemId = null;
  this.url = null;
  this.width = null;
  this.height = null;
  this.adType = null;
  this.name = null;
  this.surface = null;
  this.surfaceId = null;
  this.imageDetails = null;
  this.image = null;
  if (args) {
    if (args.creativeItemId !== undefined && args.creativeItemId !== null) {
      this.creativeItemId = args.creativeItemId;
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.width !== undefined && args.width !== null) {
      this.width = args.width;
    }
    if (args.height !== undefined && args.height !== null) {
      this.height = args.height;
    }
    if (args.adType !== undefined && args.adType !== null) {
      this.adType = args.adType;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.surface !== undefined && args.surface !== null) {
      this.surface = new AdInventorySurface(args.surface);
    }
    if (args.surfaceId !== undefined && args.surfaceId !== null) {
      this.surfaceId = args.surfaceId;
    }
    if (args.imageDetails !== undefined && args.imageDetails !== null) {
      this.imageDetails = new Image(args.imageDetails);
    }
    if (args.image !== undefined && args.image !== null) {
      this.image = args.image;
    }
  }
};
AdInventoryCreativeItem.prototype = {};
AdInventoryCreativeItem.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.creativeItemId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.width = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.height = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.adType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRUCT) {
        this.surface = new AdInventorySurface();
        this.surface.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.surfaceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRUCT) {
        this.imageDetails = new Image();
        this.imageDetails.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I64) {
        this.image = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryCreativeItem.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryCreativeItem');
  if (this.creativeItemId !== null && this.creativeItemId !== undefined) {
    output.writeFieldBegin('creativeItemId', Thrift.Type.STRING, 1);
    output.writeString(this.creativeItemId);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 2);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.width !== null && this.width !== undefined) {
    output.writeFieldBegin('width', Thrift.Type.I32, 3);
    output.writeI32(this.width);
    output.writeFieldEnd();
  }
  if (this.height !== null && this.height !== undefined) {
    output.writeFieldBegin('height', Thrift.Type.I32, 4);
    output.writeI32(this.height);
    output.writeFieldEnd();
  }
  if (this.adType !== null && this.adType !== undefined) {
    output.writeFieldBegin('adType', Thrift.Type.I32, 5);
    output.writeI32(this.adType);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 6);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.surface !== null && this.surface !== undefined) {
    output.writeFieldBegin('surface', Thrift.Type.STRUCT, 7);
    this.surface.write(output);
    output.writeFieldEnd();
  }
  if (this.surfaceId !== null && this.surfaceId !== undefined) {
    output.writeFieldBegin('surfaceId', Thrift.Type.STRING, 8);
    output.writeString(this.surfaceId);
    output.writeFieldEnd();
  }
  if (this.imageDetails !== null && this.imageDetails !== undefined) {
    output.writeFieldBegin('imageDetails', Thrift.Type.STRUCT, 9);
    this.imageDetails.write(output);
    output.writeFieldEnd();
  }
  if (this.image !== null && this.image !== undefined) {
    output.writeFieldBegin('image', Thrift.Type.I64, 10);
    output.writeI64(this.image);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryCreative = function(args) {
  this.creativeId = null;
  this.categoryId = null;
  this.publisher = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.advertiser = null;
  this.campaignObjective = null;
  this.items = null;
  this.advertiserId = null;
  this.campaignObjectiveId = null;
  if (args) {
    if (args.creativeId !== undefined && args.creativeId !== null) {
      this.creativeId = args.creativeId;
    }
    if (args.categoryId !== undefined && args.categoryId !== null) {
      this.categoryId = args.categoryId;
    }
    if (args.publisher !== undefined && args.publisher !== null) {
      this.publisher = args.publisher;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.advertiser !== undefined && args.advertiser !== null) {
      this.advertiser = new AdInventoryAdvertiser(args.advertiser);
    }
    if (args.campaignObjective !== undefined && args.campaignObjective !== null) {
      this.campaignObjective = new AdInventoryCampaignObjective(args.campaignObjective);
    }
    if (args.items !== undefined && args.items !== null) {
      this.items = Thrift.copyList(args.items, [AdInventoryCreativeItem]);
    }
    if (args.advertiserId !== undefined && args.advertiserId !== null) {
      this.advertiserId = args.advertiserId;
    }
    if (args.campaignObjectiveId !== undefined && args.campaignObjectiveId !== null) {
      this.campaignObjectiveId = args.campaignObjectiveId;
    }
  }
};
AdInventoryCreative.prototype = {};
AdInventoryCreative.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.creativeId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.categoryId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.publisher = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.advertiser = new AdInventoryAdvertiser();
        this.advertiser.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRUCT) {
        this.campaignObjective = new AdInventoryCampaignObjective();
        this.campaignObjective.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size242 = 0;
        var _rtmp3246;
        this.items = [];
        var _etype245 = 0;
        _rtmp3246 = input.readListBegin();
        _etype245 = _rtmp3246.etype;
        _size242 = _rtmp3246.size;
        for (var _i247 = 0; _i247 < _size242; ++_i247)
        {
          var elem248 = null;
          elem248 = new AdInventoryCreativeItem();
          elem248.read(input);
          this.items.push(elem248);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.advertiserId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.campaignObjectiveId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryCreative.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryCreative');
  if (this.creativeId !== null && this.creativeId !== undefined) {
    output.writeFieldBegin('creativeId', Thrift.Type.STRING, 1);
    output.writeString(this.creativeId);
    output.writeFieldEnd();
  }
  if (this.categoryId !== null && this.categoryId !== undefined) {
    output.writeFieldBegin('categoryId', Thrift.Type.STRING, 2);
    output.writeString(this.categoryId);
    output.writeFieldEnd();
  }
  if (this.publisher !== null && this.publisher !== undefined) {
    output.writeFieldBegin('publisher', Thrift.Type.STRING, 3);
    output.writeString(this.publisher);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 4);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 5);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.advertiser !== null && this.advertiser !== undefined) {
    output.writeFieldBegin('advertiser', Thrift.Type.STRUCT, 6);
    this.advertiser.write(output);
    output.writeFieldEnd();
  }
  if (this.campaignObjective !== null && this.campaignObjective !== undefined) {
    output.writeFieldBegin('campaignObjective', Thrift.Type.STRUCT, 7);
    this.campaignObjective.write(output);
    output.writeFieldEnd();
  }
  if (this.items !== null && this.items !== undefined) {
    output.writeFieldBegin('items', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRUCT, this.items.length);
    for (var iter249 in this.items)
    {
      if (this.items.hasOwnProperty(iter249))
      {
        iter249 = this.items[iter249];
        iter249.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.advertiserId !== null && this.advertiserId !== undefined) {
    output.writeFieldBegin('advertiserId', Thrift.Type.STRING, 9);
    output.writeString(this.advertiserId);
    output.writeFieldEnd();
  }
  if (this.campaignObjectiveId !== null && this.campaignObjectiveId !== undefined) {
    output.writeFieldBegin('campaignObjectiveId', Thrift.Type.STRING, 10);
    output.writeString(this.campaignObjectiveId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryCreativeGroup = function(args) {
  this.creativeGroupId = null;
  this.title = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.creatives = null;
  this.advertiser = null;
  this.advertiserId = null;
  this.creativeIds = null;
  if (args) {
    if (args.creativeGroupId !== undefined && args.creativeGroupId !== null) {
      this.creativeGroupId = args.creativeGroupId;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.creatives !== undefined && args.creatives !== null) {
      this.creatives = Thrift.copyList(args.creatives, [AdInventoryCreative]);
    }
    if (args.advertiser !== undefined && args.advertiser !== null) {
      this.advertiser = new AdInventoryAdvertiser(args.advertiser);
    }
    if (args.advertiserId !== undefined && args.advertiserId !== null) {
      this.advertiserId = args.advertiserId;
    }
    if (args.creativeIds !== undefined && args.creativeIds !== null) {
      this.creativeIds = Thrift.copyList(args.creativeIds, [null]);
    }
  }
};
AdInventoryCreativeGroup.prototype = {};
AdInventoryCreativeGroup.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.creativeGroupId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size250 = 0;
        var _rtmp3254;
        this.creatives = [];
        var _etype253 = 0;
        _rtmp3254 = input.readListBegin();
        _etype253 = _rtmp3254.etype;
        _size250 = _rtmp3254.size;
        for (var _i255 = 0; _i255 < _size250; ++_i255)
        {
          var elem256 = null;
          elem256 = new AdInventoryCreative();
          elem256.read(input);
          this.creatives.push(elem256);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.advertiser = new AdInventoryAdvertiser();
        this.advertiser.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.advertiserId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size257 = 0;
        var _rtmp3261;
        this.creativeIds = [];
        var _etype260 = 0;
        _rtmp3261 = input.readListBegin();
        _etype260 = _rtmp3261.etype;
        _size257 = _rtmp3261.size;
        for (var _i262 = 0; _i262 < _size257; ++_i262)
        {
          var elem263 = null;
          elem263 = input.readString().value;
          this.creativeIds.push(elem263);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryCreativeGroup.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryCreativeGroup');
  if (this.creativeGroupId !== null && this.creativeGroupId !== undefined) {
    output.writeFieldBegin('creativeGroupId', Thrift.Type.STRING, 1);
    output.writeString(this.creativeGroupId);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 2);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 3);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 4);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.creatives !== null && this.creatives !== undefined) {
    output.writeFieldBegin('creatives', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.creatives.length);
    for (var iter264 in this.creatives)
    {
      if (this.creatives.hasOwnProperty(iter264))
      {
        iter264 = this.creatives[iter264];
        iter264.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.advertiser !== null && this.advertiser !== undefined) {
    output.writeFieldBegin('advertiser', Thrift.Type.STRUCT, 6);
    this.advertiser.write(output);
    output.writeFieldEnd();
  }
  if (this.advertiserId !== null && this.advertiserId !== undefined) {
    output.writeFieldBegin('advertiserId', Thrift.Type.STRING, 7);
    output.writeString(this.advertiserId);
    output.writeFieldEnd();
  }
  if (this.creativeIds !== null && this.creativeIds !== undefined) {
    output.writeFieldBegin('creativeIds', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRING, this.creativeIds.length);
    for (var iter265 in this.creativeIds)
    {
      if (this.creativeIds.hasOwnProperty(iter265))
      {
        iter265 = this.creativeIds[iter265];
        output.writeString(iter265);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryAd = function(args) {
  this.adId = null;
  this.name = null;
  this.title = null;
  this.summary = null;
  this.status = null;
  this.creative = null;
  this.widthInPx = null;
  this.heightInPx = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.adgroup = null;
  this.cta = null;
  this.adgroupId = null;
  this.impressionCappingId = null;
  this.impressionCapping = null;
  this.externalId = null;
  this.frequencyCapping = null;
  this.glanceDraft = null;
  this.glancePriority = null;
  this.creativeGroupId = null;
  this.glanceImpressionCapping = null;
  this.glanceDrafts = null;
  this.lastPublished = null;
  this.creativeRotationEnabled = null;
  this.impressionCappingSplitPercentage = null;
  if (args) {
    if (args.adId !== undefined && args.adId !== null) {
      this.adId = args.adId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.creative !== undefined && args.creative !== null) {
      this.creative = args.creative;
    }
    if (args.widthInPx !== undefined && args.widthInPx !== null) {
      this.widthInPx = args.widthInPx;
    }
    if (args.heightInPx !== undefined && args.heightInPx !== null) {
      this.heightInPx = args.heightInPx;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.adgroup !== undefined && args.adgroup !== null) {
      this.adgroup = new AdInventoryAdGroup(args.adgroup);
    }
    if (args.cta !== undefined && args.cta !== null) {
      this.cta = new AdInventoryTrackers(args.cta);
    }
    if (args.adgroupId !== undefined && args.adgroupId !== null) {
      this.adgroupId = args.adgroupId;
    }
    if (args.impressionCappingId !== undefined && args.impressionCappingId !== null) {
      this.impressionCappingId = args.impressionCappingId;
    }
    if (args.impressionCapping !== undefined && args.impressionCapping !== null) {
      this.impressionCapping = new AdInventoryImpressionCapping(args.impressionCapping);
    }
    if (args.externalId !== undefined && args.externalId !== null) {
      this.externalId = args.externalId;
    }
    if (args.frequencyCapping !== undefined && args.frequencyCapping !== null) {
      this.frequencyCapping = new AdInventoryFrequencyCapping(args.frequencyCapping);
    }
    if (args.glanceDraft !== undefined && args.glanceDraft !== null) {
      this.glanceDraft = args.glanceDraft;
    }
    if (args.glancePriority !== undefined && args.glancePriority !== null) {
      this.glancePriority = args.glancePriority;
    }
    if (args.creativeGroupId !== undefined && args.creativeGroupId !== null) {
      this.creativeGroupId = args.creativeGroupId;
    }
    if (args.glanceImpressionCapping !== undefined && args.glanceImpressionCapping !== null) {
      this.glanceImpressionCapping = new ImpressionCapping(args.glanceImpressionCapping);
    }
    if (args.glanceDrafts !== undefined && args.glanceDrafts !== null) {
      this.glanceDrafts = Thrift.copyList(args.glanceDrafts, [null]);
    }
    if (args.lastPublished !== undefined && args.lastPublished !== null) {
      this.lastPublished = args.lastPublished;
    }
    if (args.creativeRotationEnabled !== undefined && args.creativeRotationEnabled !== null) {
      this.creativeRotationEnabled = args.creativeRotationEnabled;
    }
    if (args.impressionCappingSplitPercentage !== undefined && args.impressionCappingSplitPercentage !== null) {
      this.impressionCappingSplitPercentage = args.impressionCappingSplitPercentage;
    }
  }
};
AdInventoryAd.prototype = {};
AdInventoryAd.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.status = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.creative = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.widthInPx = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.heightInPx = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRUCT) {
        this.adgroup = new AdInventoryAdGroup();
        this.adgroup.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRUCT) {
        this.cta = new AdInventoryTrackers();
        this.cta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.adgroupId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.impressionCappingId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRUCT) {
        this.impressionCapping = new AdInventoryImpressionCapping();
        this.impressionCapping.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.externalId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRUCT) {
        this.frequencyCapping = new AdInventoryFrequencyCapping();
        this.frequencyCapping.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.glanceDraft = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.I32) {
        this.glancePriority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.creativeGroupId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceImpressionCapping = new ImpressionCapping();
        this.glanceImpressionCapping.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.LIST) {
        var _size266 = 0;
        var _rtmp3270;
        this.glanceDrafts = [];
        var _etype269 = 0;
        _rtmp3270 = input.readListBegin();
        _etype269 = _rtmp3270.etype;
        _size266 = _rtmp3270.size;
        for (var _i271 = 0; _i271 < _size266; ++_i271)
        {
          var elem272 = null;
          elem272 = input.readString().value;
          this.glanceDrafts.push(elem272);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.lastPublished = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.BOOL) {
        this.creativeRotationEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.I32) {
        this.impressionCappingSplitPercentage = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryAd.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryAd');
  if (this.adId !== null && this.adId !== undefined) {
    output.writeFieldBegin('adId', Thrift.Type.STRING, 1);
    output.writeString(this.adId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 3);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 4);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I32, 5);
    output.writeI32(this.status);
    output.writeFieldEnd();
  }
  if (this.creative !== null && this.creative !== undefined) {
    output.writeFieldBegin('creative', Thrift.Type.STRING, 6);
    output.writeString(this.creative);
    output.writeFieldEnd();
  }
  if (this.widthInPx !== null && this.widthInPx !== undefined) {
    output.writeFieldBegin('widthInPx', Thrift.Type.I32, 7);
    output.writeI32(this.widthInPx);
    output.writeFieldEnd();
  }
  if (this.heightInPx !== null && this.heightInPx !== undefined) {
    output.writeFieldBegin('heightInPx', Thrift.Type.I32, 8);
    output.writeI32(this.heightInPx);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 9);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 10);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.adgroup !== null && this.adgroup !== undefined) {
    output.writeFieldBegin('adgroup', Thrift.Type.STRUCT, 11);
    this.adgroup.write(output);
    output.writeFieldEnd();
  }
  if (this.cta !== null && this.cta !== undefined) {
    output.writeFieldBegin('cta', Thrift.Type.STRUCT, 12);
    this.cta.write(output);
    output.writeFieldEnd();
  }
  if (this.adgroupId !== null && this.adgroupId !== undefined) {
    output.writeFieldBegin('adgroupId', Thrift.Type.STRING, 13);
    output.writeString(this.adgroupId);
    output.writeFieldEnd();
  }
  if (this.impressionCappingId !== null && this.impressionCappingId !== undefined) {
    output.writeFieldBegin('impressionCappingId', Thrift.Type.STRING, 14);
    output.writeString(this.impressionCappingId);
    output.writeFieldEnd();
  }
  if (this.impressionCapping !== null && this.impressionCapping !== undefined) {
    output.writeFieldBegin('impressionCapping', Thrift.Type.STRUCT, 15);
    this.impressionCapping.write(output);
    output.writeFieldEnd();
  }
  if (this.externalId !== null && this.externalId !== undefined) {
    output.writeFieldBegin('externalId', Thrift.Type.STRING, 16);
    output.writeString(this.externalId);
    output.writeFieldEnd();
  }
  if (this.frequencyCapping !== null && this.frequencyCapping !== undefined) {
    output.writeFieldBegin('frequencyCapping', Thrift.Type.STRUCT, 17);
    this.frequencyCapping.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceDraft !== null && this.glanceDraft !== undefined) {
    output.writeFieldBegin('glanceDraft', Thrift.Type.STRING, 18);
    output.writeString(this.glanceDraft);
    output.writeFieldEnd();
  }
  if (this.glancePriority !== null && this.glancePriority !== undefined) {
    output.writeFieldBegin('glancePriority', Thrift.Type.I32, 19);
    output.writeI32(this.glancePriority);
    output.writeFieldEnd();
  }
  if (this.creativeGroupId !== null && this.creativeGroupId !== undefined) {
    output.writeFieldBegin('creativeGroupId', Thrift.Type.STRING, 20);
    output.writeString(this.creativeGroupId);
    output.writeFieldEnd();
  }
  if (this.glanceImpressionCapping !== null && this.glanceImpressionCapping !== undefined) {
    output.writeFieldBegin('glanceImpressionCapping', Thrift.Type.STRUCT, 21);
    this.glanceImpressionCapping.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceDrafts !== null && this.glanceDrafts !== undefined) {
    output.writeFieldBegin('glanceDrafts', Thrift.Type.LIST, 22);
    output.writeListBegin(Thrift.Type.STRING, this.glanceDrafts.length);
    for (var iter273 in this.glanceDrafts)
    {
      if (this.glanceDrafts.hasOwnProperty(iter273))
      {
        iter273 = this.glanceDrafts[iter273];
        output.writeString(iter273);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.lastPublished !== null && this.lastPublished !== undefined) {
    output.writeFieldBegin('lastPublished', Thrift.Type.STRING, 23);
    output.writeString(this.lastPublished);
    output.writeFieldEnd();
  }
  if (this.creativeRotationEnabled !== null && this.creativeRotationEnabled !== undefined) {
    output.writeFieldBegin('creativeRotationEnabled', Thrift.Type.BOOL, 24);
    output.writeBool(this.creativeRotationEnabled);
    output.writeFieldEnd();
  }
  if (this.impressionCappingSplitPercentage !== null && this.impressionCappingSplitPercentage !== undefined) {
    output.writeFieldBegin('impressionCappingSplitPercentage', Thrift.Type.I32, 25);
    output.writeI32(this.impressionCappingSplitPercentage);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdInventoryGlanceAdPlacementMapping = function(args) {
  this.placementMappingId = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.adFormat = null;
  this.glanceAdPlacement = null;
  if (args) {
    if (args.placementMappingId !== undefined && args.placementMappingId !== null) {
      this.placementMappingId = args.placementMappingId;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.adFormat !== undefined && args.adFormat !== null) {
      this.adFormat = args.adFormat;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field adFormat is unset!');
    }
    if (args.glanceAdPlacement !== undefined && args.glanceAdPlacement !== null) {
      this.glanceAdPlacement = new AdInventoryGlanceAdPlacement(args.glanceAdPlacement);
    }
  }
};
AdInventoryGlanceAdPlacementMapping.prototype = {};
AdInventoryGlanceAdPlacementMapping.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.placementMappingId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.adFormat = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceAdPlacement = new AdInventoryGlanceAdPlacement();
        this.glanceAdPlacement.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdInventoryGlanceAdPlacementMapping.prototype.write = function(output) {
  output.writeStructBegin('AdInventoryGlanceAdPlacementMapping');
  if (this.placementMappingId !== null && this.placementMappingId !== undefined) {
    output.writeFieldBegin('placementMappingId', Thrift.Type.STRING, 1);
    output.writeString(this.placementMappingId);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 2);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 3);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.adFormat !== null && this.adFormat !== undefined) {
    output.writeFieldBegin('adFormat', Thrift.Type.I32, 4);
    output.writeI32(this.adFormat);
    output.writeFieldEnd();
  }
  if (this.glanceAdPlacement !== null && this.glanceAdPlacement !== undefined) {
    output.writeFieldBegin('glanceAdPlacement', Thrift.Type.STRUCT, 5);
    this.glanceAdPlacement.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdImpression = function(args) {
  this.adId = null;
  this.frequency = null;
  if (args) {
    if (args.adId !== undefined && args.adId !== null) {
      this.adId = args.adId;
    }
    if (args.frequency !== undefined && args.frequency !== null) {
      this.frequency = args.frequency;
    }
  }
};
AdImpression.prototype = {};
AdImpression.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.frequency = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdImpression.prototype.write = function(output) {
  output.writeStructBegin('AdImpression');
  if (this.adId !== null && this.adId !== undefined) {
    output.writeFieldBegin('adId', Thrift.Type.STRING, 1);
    output.writeString(this.adId);
    output.writeFieldEnd();
  }
  if (this.frequency !== null && this.frequency !== undefined) {
    output.writeFieldBegin('frequency', Thrift.Type.I32, 2);
    output.writeI32(this.frequency);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

MonetisationUserSummary = function(args) {
  this.userId = null;
  this.date = null;
  this.adImpressions = null;
  if (args) {
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field userId is unset!');
    }
    if (args.date !== undefined && args.date !== null) {
      this.date = args.date;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field date is unset!');
    }
    if (args.adImpressions !== undefined && args.adImpressions !== null) {
      this.adImpressions = Thrift.copyList(args.adImpressions, [AdImpression]);
    }
  }
};
MonetisationUserSummary.prototype = {};
MonetisationUserSummary.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.date = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size274 = 0;
        var _rtmp3278;
        this.adImpressions = [];
        var _etype277 = 0;
        _rtmp3278 = input.readListBegin();
        _etype277 = _rtmp3278.etype;
        _size274 = _rtmp3278.size;
        for (var _i279 = 0; _i279 < _size274; ++_i279)
        {
          var elem280 = null;
          elem280 = new AdImpression();
          elem280.read(input);
          this.adImpressions.push(elem280);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

MonetisationUserSummary.prototype.write = function(output) {
  output.writeStructBegin('MonetisationUserSummary');
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 1);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.date !== null && this.date !== undefined) {
    output.writeFieldBegin('date', Thrift.Type.STRING, 2);
    output.writeString(this.date);
    output.writeFieldEnd();
  }
  if (this.adImpressions !== null && this.adImpressions !== undefined) {
    output.writeFieldBegin('adImpressions', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.adImpressions.length);
    for (var iter281 in this.adImpressions)
    {
      if (this.adImpressions.hasOwnProperty(iter281))
      {
        iter281 = this.adImpressions[iter281];
        iter281.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


StatusType = {
  '1' : 'ACTIVE',
  'ACTIVE' : 1,
  '2' : 'INACTIVE',
  'INACTIVE' : 2,
  '3' : 'PAUSED',
  'PAUSED' : 3
};
ChildPrivacyType = {
  '1' : 'NOT_DIRECTED',
  'NOT_DIRECTED' : 1,
  '2' : 'DIRECTED',
  'DIRECTED' : 2,
  '3' : 'PARTIALLY_DIRECTED',
  'PARTIALLY_DIRECTED' : 3
};
AdUnitType = {
  '1' : 'INTERSTITIAL',
  'INTERSTITIAL' : 1,
  '2' : 'BANNER',
  'BANNER' : 2,
  '3' : 'REWARDED_VIDEO',
  'REWARDED_VIDEO' : 3,
  '4' : 'MREC',
  'MREC' : 4,
  '5' : 'NATIVE',
  'NATIVE' : 5
};
SourceType = {
  '1' : 'MLIB',
  'MLIB' : 1,
  '2' : 'NEBULA',
  'NEBULA' : 2,
  '3' : 'FOLDERS',
  'FOLDERS' : 3,
  '4' : 'GLANCE',
  'GLANCE' : 4
};
ContentType = {
  '1' : 'DISPLAY',
  'DISPLAY' : 1,
  '2' : 'VIDEO',
  'VIDEO' : 2,
  '3' : 'NATIVE',
  'NATIVE' : 3
};
IabCategoryId = function(args) {
  this.iabCategoryIds = null;
  if (args) {
    if (args.iabCategoryIds !== undefined && args.iabCategoryIds !== null) {
      this.iabCategoryIds = Thrift.copyList(args.iabCategoryIds, [null]);
    }
  }
};
IabCategoryId.prototype = {};
IabCategoryId.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size0 = 0;
        var _rtmp34;
        this.iabCategoryIds = [];
        var _etype3 = 0;
        _rtmp34 = input.readListBegin();
        _etype3 = _rtmp34.etype;
        _size0 = _rtmp34.size;
        for (var _i5 = 0; _i5 < _size0; ++_i5)
        {
          var elem6 = null;
          elem6 = input.readI16().value;
          this.iabCategoryIds.push(elem6);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

IabCategoryId.prototype.write = function(output) {
  output.writeStructBegin('IabCategoryId');
  if (this.iabCategoryIds !== null && this.iabCategoryIds !== undefined) {
    output.writeFieldBegin('iabCategoryIds', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.I16, this.iabCategoryIds.length);
    for (var iter7 in this.iabCategoryIds)
    {
      if (this.iabCategoryIds.hasOwnProperty(iter7))
      {
        iter7 = this.iabCategoryIds[iter7];
        output.writeI16(iter7);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Blocks = function(args) {
  this.domain = null;
  this.category = null;
  this.appBlocks = null;
  this.creativeAttributes = null;
  if (args) {
    if (args.domain !== undefined && args.domain !== null) {
      this.domain = Thrift.copyList(args.domain, [null]);
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = new IabCategoryId(args.category);
    }
    if (args.appBlocks !== undefined && args.appBlocks !== null) {
      this.appBlocks = Thrift.copyList(args.appBlocks, [null]);
    }
    if (args.creativeAttributes !== undefined && args.creativeAttributes !== null) {
      this.creativeAttributes = Thrift.copyList(args.creativeAttributes, [null]);
    }
  }
};
Blocks.prototype = {};
Blocks.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size8 = 0;
        var _rtmp312;
        this.domain = [];
        var _etype11 = 0;
        _rtmp312 = input.readListBegin();
        _etype11 = _rtmp312.etype;
        _size8 = _rtmp312.size;
        for (var _i13 = 0; _i13 < _size8; ++_i13)
        {
          var elem14 = null;
          elem14 = input.readString().value;
          this.domain.push(elem14);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.category = new IabCategoryId();
        this.category.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size15 = 0;
        var _rtmp319;
        this.appBlocks = [];
        var _etype18 = 0;
        _rtmp319 = input.readListBegin();
        _etype18 = _rtmp319.etype;
        _size15 = _rtmp319.size;
        for (var _i20 = 0; _i20 < _size15; ++_i20)
        {
          var elem21 = null;
          elem21 = input.readString().value;
          this.appBlocks.push(elem21);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size22 = 0;
        var _rtmp326;
        this.creativeAttributes = [];
        var _etype25 = 0;
        _rtmp326 = input.readListBegin();
        _etype25 = _rtmp326.etype;
        _size22 = _rtmp326.size;
        for (var _i27 = 0; _i27 < _size22; ++_i27)
        {
          var elem28 = null;
          elem28 = input.readI16().value;
          this.creativeAttributes.push(elem28);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Blocks.prototype.write = function(output) {
  output.writeStructBegin('Blocks');
  if (this.domain !== null && this.domain !== undefined) {
    output.writeFieldBegin('domain', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.domain.length);
    for (var iter29 in this.domain)
    {
      if (this.domain.hasOwnProperty(iter29))
      {
        iter29 = this.domain[iter29];
        output.writeString(iter29);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRUCT, 2);
    this.category.write(output);
    output.writeFieldEnd();
  }
  if (this.appBlocks !== null && this.appBlocks !== undefined) {
    output.writeFieldBegin('appBlocks', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRING, this.appBlocks.length);
    for (var iter30 in this.appBlocks)
    {
      if (this.appBlocks.hasOwnProperty(iter30))
      {
        iter30 = this.appBlocks[iter30];
        output.writeString(iter30);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.creativeAttributes !== null && this.creativeAttributes !== undefined) {
    output.writeFieldBegin('creativeAttributes', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.I16, this.creativeAttributes.length);
    for (var iter31 in this.creativeAttributes)
    {
      if (this.creativeAttributes.hasOwnProperty(iter31))
      {
        iter31 = this.creativeAttributes[iter31];
        output.writeI16(iter31);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

BlockSetting = function(args) {
  this.id = null;
  this.created_on = null;
  this.modified_on = null;
  this.deleted = null;
  this.name = null;
  this.blocks = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.deleted !== undefined && args.deleted !== null) {
      this.deleted = args.deleted;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field deleted is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.blocks !== undefined && args.blocks !== null) {
      this.blocks = new Blocks(args.blocks);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field blocks is unset!');
    }
  }
};
BlockSetting.prototype = {};
BlockSetting.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.created_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.modified_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.deleted = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.blocks = new Blocks();
        this.blocks.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

BlockSetting.prototype.write = function(output) {
  output.writeStructBegin('BlockSetting');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.STRING, 2);
    output.writeString(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.STRING, 3);
    output.writeString(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.deleted !== null && this.deleted !== undefined) {
    output.writeFieldBegin('deleted', Thrift.Type.BOOL, 4);
    output.writeBool(this.deleted);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 5);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.blocks !== null && this.blocks !== undefined) {
    output.writeFieldBegin('blocks', Thrift.Type.STRUCT, 6);
    this.blocks.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceApp = function(args) {
  this.created_on = null;
  this.modified_on = null;
  this.id = null;
  this.url = null;
  this.name = null;
  this.iconUrl = null;
  this.bundleId = null;
  this.directedAtMinors = null;
  this.ccpa = null;
  this.block = null;
  this.category = null;
  this.childPrivacy = null;
  this.source = null;
  this.status = null;
  if (args) {
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.iconUrl !== undefined && args.iconUrl !== null) {
      this.iconUrl = args.iconUrl;
    }
    if (args.bundleId !== undefined && args.bundleId !== null) {
      this.bundleId = args.bundleId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field bundleId is unset!');
    }
    if (args.directedAtMinors !== undefined && args.directedAtMinors !== null) {
      this.directedAtMinors = args.directedAtMinors;
    }
    if (args.ccpa !== undefined && args.ccpa !== null) {
      this.ccpa = args.ccpa;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field ccpa is unset!');
    }
    if (args.block !== undefined && args.block !== null) {
      this.block = args.block;
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field category is unset!');
    }
    if (args.childPrivacy !== undefined && args.childPrivacy !== null) {
      this.childPrivacy = args.childPrivacy;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field childPrivacy is unset!');
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
  }
};
GlanceApp.prototype = {};
GlanceApp.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.created_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.modified_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.iconUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.bundleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.directedAtMinors = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.BOOL) {
        this.ccpa = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.block = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.I16) {
        this.category = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I32) {
        this.childPrivacy = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.I32) {
        this.source = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.I16) {
        this.status = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceApp.prototype.write = function(output) {
  output.writeStructBegin('GlanceApp');
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.STRING, 1);
    output.writeString(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.STRING, 2);
    output.writeString(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 3);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 4);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 5);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.iconUrl !== null && this.iconUrl !== undefined) {
    output.writeFieldBegin('iconUrl', Thrift.Type.STRING, 6);
    output.writeString(this.iconUrl);
    output.writeFieldEnd();
  }
  if (this.bundleId !== null && this.bundleId !== undefined) {
    output.writeFieldBegin('bundleId', Thrift.Type.STRING, 7);
    output.writeString(this.bundleId);
    output.writeFieldEnd();
  }
  if (this.directedAtMinors !== null && this.directedAtMinors !== undefined) {
    output.writeFieldBegin('directedAtMinors', Thrift.Type.BOOL, 8);
    output.writeBool(this.directedAtMinors);
    output.writeFieldEnd();
  }
  if (this.ccpa !== null && this.ccpa !== undefined) {
    output.writeFieldBegin('ccpa', Thrift.Type.BOOL, 9);
    output.writeBool(this.ccpa);
    output.writeFieldEnd();
  }
  if (this.block !== null && this.block !== undefined) {
    output.writeFieldBegin('block', Thrift.Type.STRING, 10);
    output.writeString(this.block);
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.I16, 11);
    output.writeI16(this.category);
    output.writeFieldEnd();
  }
  if (this.childPrivacy !== null && this.childPrivacy !== undefined) {
    output.writeFieldBegin('childPrivacy', Thrift.Type.I32, 12);
    output.writeI32(this.childPrivacy);
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.I32, 13);
    output.writeI32(this.source);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I16, 14);
    output.writeI16(this.status);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlasAdUnit = function(args) {
  this.id = null;
  this.created_on = null;
  this.modified_on = null;
  this.name = null;
  this.ad_unit_type = null;
  this.status = null;
  this.app = null;
  this.block = null;
  this.ux_template = null;
  this.sourceType = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.ad_unit_type !== undefined && args.ad_unit_type !== null) {
      this.ad_unit_type = args.ad_unit_type;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field ad_unit_type is unset!');
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field status is unset!');
    }
    if (args.app !== undefined && args.app !== null) {
      this.app = args.app;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field app is unset!');
    }
    if (args.block !== undefined && args.block !== null) {
      this.block = args.block;
    }
    if (args.ux_template !== undefined && args.ux_template !== null) {
      this.ux_template = args.ux_template;
    }
    if (args.sourceType !== undefined && args.sourceType !== null) {
      this.sourceType = args.sourceType;
    }
  }
};
GlasAdUnit.prototype = {};
GlasAdUnit.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.created_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.modified_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I16) {
        this.ad_unit_type = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I16) {
        this.status = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.app = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.block = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.ux_template = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I32) {
        this.sourceType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlasAdUnit.prototype.write = function(output) {
  output.writeStructBegin('GlasAdUnit');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.STRING, 2);
    output.writeString(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.STRING, 3);
    output.writeString(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 4);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.ad_unit_type !== null && this.ad_unit_type !== undefined) {
    output.writeFieldBegin('ad_unit_type', Thrift.Type.I16, 5);
    output.writeI16(this.ad_unit_type);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I16, 6);
    output.writeI16(this.status);
    output.writeFieldEnd();
  }
  if (this.app !== null && this.app !== undefined) {
    output.writeFieldBegin('app', Thrift.Type.STRING, 7);
    output.writeString(this.app);
    output.writeFieldEnd();
  }
  if (this.block !== null && this.block !== undefined) {
    output.writeFieldBegin('block', Thrift.Type.STRING, 8);
    output.writeString(this.block);
    output.writeFieldEnd();
  }
  if (this.ux_template !== null && this.ux_template !== undefined) {
    output.writeFieldBegin('ux_template', Thrift.Type.STRING, 9);
    output.writeString(this.ux_template);
    output.writeFieldEnd();
  }
  if (this.sourceType !== null && this.sourceType !== undefined) {
    output.writeFieldBegin('sourceType', Thrift.Type.I32, 10);
    output.writeI32(this.sourceType);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ConfigObj = function(args) {
  this.compressionEnabled = null;
  if (args) {
    if (args.compressionEnabled !== undefined && args.compressionEnabled !== null) {
      this.compressionEnabled = args.compressionEnabled;
    }
  }
};
ConfigObj.prototype = {};
ConfigObj.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.compressionEnabled = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ConfigObj.prototype.write = function(output) {
  output.writeStructBegin('ConfigObj');
  if (this.compressionEnabled !== null && this.compressionEnabled !== undefined) {
    output.writeFieldBegin('compressionEnabled', Thrift.Type.STRING, 1);
    output.writeString(this.compressionEnabled);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Network = function(args) {
  this.created_on = null;
  this.modified_on = null;
  this.id = null;
  this.name = null;
  this.app_credential_enabled = null;
  this.supported_ad_unit_type = null;
  this.network_logo = null;
  this.network_colorless_logo = null;
  this.endpoint = null;
  this.config = null;
  this.enabled = null;
  this.active_bidder = null;
  this.adapter_name = null;
  this.credential_enabled = null;
  this.timeout = null;
  if (args) {
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.app_credential_enabled !== undefined && args.app_credential_enabled !== null) {
      this.app_credential_enabled = args.app_credential_enabled;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field app_credential_enabled is unset!');
    }
    if (args.supported_ad_unit_type !== undefined && args.supported_ad_unit_type !== null) {
      this.supported_ad_unit_type = Thrift.copyList(args.supported_ad_unit_type, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field supported_ad_unit_type is unset!');
    }
    if (args.network_logo !== undefined && args.network_logo !== null) {
      this.network_logo = args.network_logo;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field network_logo is unset!');
    }
    if (args.network_colorless_logo !== undefined && args.network_colorless_logo !== null) {
      this.network_colorless_logo = args.network_colorless_logo;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field network_colorless_logo is unset!');
    }
    if (args.endpoint !== undefined && args.endpoint !== null) {
      this.endpoint = args.endpoint;
    }
    if (args.config !== undefined && args.config !== null) {
      this.config = new ConfigObj(args.config);
    }
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field enabled is unset!');
    }
    if (args.active_bidder !== undefined && args.active_bidder !== null) {
      this.active_bidder = args.active_bidder;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field active_bidder is unset!');
    }
    if (args.adapter_name !== undefined && args.adapter_name !== null) {
      this.adapter_name = args.adapter_name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field adapter_name is unset!');
    }
    if (args.credential_enabled !== undefined && args.credential_enabled !== null) {
      this.credential_enabled = args.credential_enabled;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field credential_enabled is unset!');
    }
    if (args.timeout !== undefined && args.timeout !== null) {
      this.timeout = args.timeout;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field timeout is unset!');
    }
  }
};
Network.prototype = {};
Network.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.created_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.modified_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.app_credential_enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size32 = 0;
        var _rtmp336;
        this.supported_ad_unit_type = [];
        var _etype35 = 0;
        _rtmp336 = input.readListBegin();
        _etype35 = _rtmp336.etype;
        _size32 = _rtmp336.size;
        for (var _i37 = 0; _i37 < _size32; ++_i37)
        {
          var elem38 = null;
          elem38 = input.readI16().value;
          this.supported_ad_unit_type.push(elem38);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.network_logo = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.network_colorless_logo = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.endpoint = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRUCT) {
        this.config = new ConfigObj();
        this.config.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.BOOL) {
        this.active_bidder = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.adapter_name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.BOOL) {
        this.credential_enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.I32) {
        this.timeout = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Network.prototype.write = function(output) {
  output.writeStructBegin('Network');
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.STRING, 1);
    output.writeString(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.STRING, 2);
    output.writeString(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 3);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 4);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.app_credential_enabled !== null && this.app_credential_enabled !== undefined) {
    output.writeFieldBegin('app_credential_enabled', Thrift.Type.BOOL, 5);
    output.writeBool(this.app_credential_enabled);
    output.writeFieldEnd();
  }
  if (this.supported_ad_unit_type !== null && this.supported_ad_unit_type !== undefined) {
    output.writeFieldBegin('supported_ad_unit_type', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.I16, this.supported_ad_unit_type.length);
    for (var iter39 in this.supported_ad_unit_type)
    {
      if (this.supported_ad_unit_type.hasOwnProperty(iter39))
      {
        iter39 = this.supported_ad_unit_type[iter39];
        output.writeI16(iter39);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.network_logo !== null && this.network_logo !== undefined) {
    output.writeFieldBegin('network_logo', Thrift.Type.STRING, 7);
    output.writeString(this.network_logo);
    output.writeFieldEnd();
  }
  if (this.network_colorless_logo !== null && this.network_colorless_logo !== undefined) {
    output.writeFieldBegin('network_colorless_logo', Thrift.Type.STRING, 8);
    output.writeString(this.network_colorless_logo);
    output.writeFieldEnd();
  }
  if (this.endpoint !== null && this.endpoint !== undefined) {
    output.writeFieldBegin('endpoint', Thrift.Type.STRING, 9);
    output.writeString(this.endpoint);
    output.writeFieldEnd();
  }
  if (this.config !== null && this.config !== undefined) {
    output.writeFieldBegin('config', Thrift.Type.STRUCT, 10);
    this.config.write(output);
    output.writeFieldEnd();
  }
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 11);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  if (this.active_bidder !== null && this.active_bidder !== undefined) {
    output.writeFieldBegin('active_bidder', Thrift.Type.BOOL, 12);
    output.writeBool(this.active_bidder);
    output.writeFieldEnd();
  }
  if (this.adapter_name !== null && this.adapter_name !== undefined) {
    output.writeFieldBegin('adapter_name', Thrift.Type.STRING, 13);
    output.writeString(this.adapter_name);
    output.writeFieldEnd();
  }
  if (this.credential_enabled !== null && this.credential_enabled !== undefined) {
    output.writeFieldBegin('credential_enabled', Thrift.Type.BOOL, 14);
    output.writeBool(this.credential_enabled);
    output.writeFieldEnd();
  }
  if (this.timeout !== null && this.timeout !== undefined) {
    output.writeFieldBegin('timeout', Thrift.Type.I32, 15);
    output.writeI32(this.timeout);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

NetworkAttribute = function(args) {
  this.created_on = null;
  this.modified_on = null;
  this.id = null;
  this.attribute_name = null;
  this.type = null;
  this.is_optional = null;
  this.enabled = null;
  this.value = null;
  this.network = null;
  if (args) {
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.attribute_name !== undefined && args.attribute_name !== null) {
      this.attribute_name = args.attribute_name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field attribute_name is unset!');
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field type is unset!');
    }
    if (args.is_optional !== undefined && args.is_optional !== null) {
      this.is_optional = args.is_optional;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field is_optional is unset!');
    }
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field enabled is unset!');
    }
    if (args.value !== undefined && args.value !== null) {
      this.value = args.value;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field value is unset!');
    }
    if (args.network !== undefined && args.network !== null) {
      this.network = args.network;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field network is unset!');
    }
  }
};
NetworkAttribute.prototype = {};
NetworkAttribute.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.created_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.modified_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.attribute_name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.type = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.is_optional = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.value = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.network = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

NetworkAttribute.prototype.write = function(output) {
  output.writeStructBegin('NetworkAttribute');
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.STRING, 1);
    output.writeString(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.STRING, 2);
    output.writeString(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 3);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.attribute_name !== null && this.attribute_name !== undefined) {
    output.writeFieldBegin('attribute_name', Thrift.Type.STRING, 4);
    output.writeString(this.attribute_name);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.STRING, 5);
    output.writeString(this.type);
    output.writeFieldEnd();
  }
  if (this.is_optional !== null && this.is_optional !== undefined) {
    output.writeFieldBegin('is_optional', Thrift.Type.BOOL, 6);
    output.writeBool(this.is_optional);
    output.writeFieldEnd();
  }
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 7);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  if (this.value !== null && this.value !== undefined) {
    output.writeFieldBegin('value', Thrift.Type.STRING, 8);
    output.writeString(this.value);
    output.writeFieldEnd();
  }
  if (this.network !== null && this.network !== undefined) {
    output.writeFieldBegin('network', Thrift.Type.STRING, 9);
    output.writeString(this.network);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

NetworkAppAttribute = function(args) {
  this.created_on = null;
  this.modified_on = null;
  this.id = null;
  this.attribute_name = null;
  this.type = null;
  this.is_optional = null;
  this.enabled = null;
  this.value = null;
  this.app = null;
  this.network = null;
  if (args) {
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.attribute_name !== undefined && args.attribute_name !== null) {
      this.attribute_name = args.attribute_name;
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    }
    if (args.is_optional !== undefined && args.is_optional !== null) {
      this.is_optional = args.is_optional;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field is_optional is unset!');
    }
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field enabled is unset!');
    }
    if (args.value !== undefined && args.value !== null) {
      this.value = args.value;
    }
    if (args.app !== undefined && args.app !== null) {
      this.app = args.app;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field app is unset!');
    }
    if (args.network !== undefined && args.network !== null) {
      this.network = args.network;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field network is unset!');
    }
  }
};
NetworkAppAttribute.prototype = {};
NetworkAppAttribute.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.created_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.modified_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.attribute_name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.type = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.is_optional = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.value = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.app = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.network = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

NetworkAppAttribute.prototype.write = function(output) {
  output.writeStructBegin('NetworkAppAttribute');
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.STRING, 1);
    output.writeString(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.STRING, 2);
    output.writeString(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 3);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.attribute_name !== null && this.attribute_name !== undefined) {
    output.writeFieldBegin('attribute_name', Thrift.Type.STRING, 4);
    output.writeString(this.attribute_name);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.STRING, 5);
    output.writeString(this.type);
    output.writeFieldEnd();
  }
  if (this.is_optional !== null && this.is_optional !== undefined) {
    output.writeFieldBegin('is_optional', Thrift.Type.BOOL, 6);
    output.writeBool(this.is_optional);
    output.writeFieldEnd();
  }
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 7);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  if (this.value !== null && this.value !== undefined) {
    output.writeFieldBegin('value', Thrift.Type.STRING, 8);
    output.writeString(this.value);
    output.writeFieldEnd();
  }
  if (this.app !== null && this.app !== undefined) {
    output.writeFieldBegin('app', Thrift.Type.STRING, 9);
    output.writeString(this.app);
    output.writeFieldEnd();
  }
  if (this.network !== null && this.network !== undefined) {
    output.writeFieldBegin('network', Thrift.Type.STRING, 11);
    output.writeString(this.network);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdLine = function(args) {
  this.id = null;
  this.created_on = null;
  this.modified_on = null;
  this.deleted = null;
  this.name = null;
  this.bid_floor = null;
  this.countries = null;
  this.placement_id = null;
  this.network = null;
  this.content_type = null;
  this.ad_unit = null;
  this.status = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.deleted !== undefined && args.deleted !== null) {
      this.deleted = args.deleted;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field deleted is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.bid_floor !== undefined && args.bid_floor !== null) {
      this.bid_floor = args.bid_floor;
    }
    if (args.countries !== undefined && args.countries !== null) {
      this.countries = Thrift.copyList(args.countries, [null]);
    }
    if (args.placement_id !== undefined && args.placement_id !== null) {
      this.placement_id = args.placement_id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field placement_id is unset!');
    }
    if (args.network !== undefined && args.network !== null) {
      this.network = args.network;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field network is unset!');
    }
    if (args.content_type !== undefined && args.content_type !== null) {
      this.content_type = args.content_type;
    }
    if (args.ad_unit !== undefined && args.ad_unit !== null) {
      this.ad_unit = args.ad_unit;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field ad_unit is unset!');
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field status is unset!');
    }
  }
};
AdLine.prototype = {};
AdLine.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.created_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.modified_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.deleted = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.DOUBLE) {
        this.bid_floor = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size40 = 0;
        var _rtmp344;
        this.countries = [];
        var _etype43 = 0;
        _rtmp344 = input.readListBegin();
        _etype43 = _rtmp344.etype;
        _size40 = _rtmp344.size;
        for (var _i45 = 0; _i45 < _size40; ++_i45)
        {
          var elem46 = null;
          elem46 = input.readString().value;
          this.countries.push(elem46);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.placement_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.network = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I16) {
        this.content_type = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.ad_unit = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I16) {
        this.status = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdLine.prototype.write = function(output) {
  output.writeStructBegin('AdLine');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.STRING, 2);
    output.writeString(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.STRING, 3);
    output.writeString(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.deleted !== null && this.deleted !== undefined) {
    output.writeFieldBegin('deleted', Thrift.Type.BOOL, 4);
    output.writeBool(this.deleted);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 5);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.bid_floor !== null && this.bid_floor !== undefined) {
    output.writeFieldBegin('bid_floor', Thrift.Type.DOUBLE, 6);
    output.writeDouble(this.bid_floor);
    output.writeFieldEnd();
  }
  if (this.countries !== null && this.countries !== undefined) {
    output.writeFieldBegin('countries', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRING, this.countries.length);
    for (var iter47 in this.countries)
    {
      if (this.countries.hasOwnProperty(iter47))
      {
        iter47 = this.countries[iter47];
        output.writeString(iter47);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.placement_id !== null && this.placement_id !== undefined) {
    output.writeFieldBegin('placement_id', Thrift.Type.STRING, 8);
    output.writeString(this.placement_id);
    output.writeFieldEnd();
  }
  if (this.network !== null && this.network !== undefined) {
    output.writeFieldBegin('network', Thrift.Type.STRING, 9);
    output.writeString(this.network);
    output.writeFieldEnd();
  }
  if (this.content_type !== null && this.content_type !== undefined) {
    output.writeFieldBegin('content_type', Thrift.Type.I16, 10);
    output.writeI16(this.content_type);
    output.writeFieldEnd();
  }
  if (this.ad_unit !== null && this.ad_unit !== undefined) {
    output.writeFieldBegin('ad_unit', Thrift.Type.STRING, 11);
    output.writeString(this.ad_unit);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I16, 12);
    output.writeI16(this.status);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

StringField = function(args) {
  this.value = null;
  this.is_required = null;
  if (args) {
    if (args.value !== undefined && args.value !== null) {
      this.value = args.value;
    }
    if (args.is_required !== undefined && args.is_required !== null) {
      this.is_required = args.is_required;
    }
  }
};
StringField.prototype = {};
StringField.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.value = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.is_required = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

StringField.prototype.write = function(output) {
  output.writeStructBegin('StringField');
  if (this.value !== null && this.value !== undefined) {
    output.writeFieldBegin('value', Thrift.Type.STRING, 1);
    output.writeString(this.value);
    output.writeFieldEnd();
  }
  if (this.is_required !== null && this.is_required !== undefined) {
    output.writeFieldBegin('is_required', Thrift.Type.BOOL, 2);
    output.writeBool(this.is_required);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

IntField = function(args) {
  this.value = null;
  this.is_required = null;
  if (args) {
    if (args.value !== undefined && args.value !== null) {
      this.value = args.value;
    }
    if (args.is_required !== undefined && args.is_required !== null) {
      this.is_required = args.is_required;
    }
  }
};
IntField.prototype = {};
IntField.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.value = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.is_required = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

IntField.prototype.write = function(output) {
  output.writeStructBegin('IntField');
  if (this.value !== null && this.value !== undefined) {
    output.writeFieldBegin('value', Thrift.Type.I32, 1);
    output.writeI32(this.value);
    output.writeFieldEnd();
  }
  if (this.is_required !== null && this.is_required !== undefined) {
    output.writeFieldBegin('is_required', Thrift.Type.BOOL, 2);
    output.writeBool(this.is_required);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

BoolField = function(args) {
  this.value = null;
  this.is_required = null;
  if (args) {
    if (args.value !== undefined && args.value !== null) {
      this.value = args.value;
    }
    if (args.is_required !== undefined && args.is_required !== null) {
      this.is_required = args.is_required;
    }
  }
};
BoolField.prototype = {};
BoolField.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.value = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.is_required = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

BoolField.prototype.write = function(output) {
  output.writeStructBegin('BoolField');
  if (this.value !== null && this.value !== undefined) {
    output.writeFieldBegin('value', Thrift.Type.BOOL, 1);
    output.writeBool(this.value);
    output.writeFieldEnd();
  }
  if (this.is_required !== null && this.is_required !== undefined) {
    output.writeFieldBegin('is_required', Thrift.Type.BOOL, 2);
    output.writeBool(this.is_required);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Value = function(args) {
  this.skippable = null;
  this.baseTemplate = null;
  this.muteByDefault = null;
  this.verticalAlignment = null;
  this.videoMaxDurationInSec = null;
  this.enableEndCardAutoClosure = null;
  this.hmin = null;
  this.wmin = null;
  this.adType = null;
  this.aspectRatio = null;
  this.titleMaxLength = null;
  this.ctaTextMaxLength = null;
  this.descriptionLength = null;
  this.skipDelay = null;
  this.refreshRate = null;
  this.endCardAutoCloseDuration = null;
  this.sponsoredLength = null;
  this.rating = null;
  this.iconWidth = null;
  this.bgColor = null;
  this.ctaText = null;
  this.ctaFontSize = null;
  this.bgTransparency = null;
  if (args) {
    if (args.skippable !== undefined && args.skippable !== null) {
      this.skippable = args.skippable;
    }
    if (args.baseTemplate !== undefined && args.baseTemplate !== null) {
      this.baseTemplate = args.baseTemplate;
    }
    if (args.muteByDefault !== undefined && args.muteByDefault !== null) {
      this.muteByDefault = args.muteByDefault;
    }
    if (args.verticalAlignment !== undefined && args.verticalAlignment !== null) {
      this.verticalAlignment = args.verticalAlignment;
    }
    if (args.videoMaxDurationInSec !== undefined && args.videoMaxDurationInSec !== null) {
      this.videoMaxDurationInSec = args.videoMaxDurationInSec;
    }
    if (args.enableEndCardAutoClosure !== undefined && args.enableEndCardAutoClosure !== null) {
      this.enableEndCardAutoClosure = args.enableEndCardAutoClosure;
    }
    if (args.hmin !== undefined && args.hmin !== null) {
      this.hmin = args.hmin;
    }
    if (args.wmin !== undefined && args.wmin !== null) {
      this.wmin = args.wmin;
    }
    if (args.adType !== undefined && args.adType !== null) {
      this.adType = new StringField(args.adType);
    }
    if (args.aspectRatio !== undefined && args.aspectRatio !== null) {
      this.aspectRatio = args.aspectRatio;
    }
    if (args.titleMaxLength !== undefined && args.titleMaxLength !== null) {
      this.titleMaxLength = new IntField(args.titleMaxLength);
    }
    if (args.ctaTextMaxLength !== undefined && args.ctaTextMaxLength !== null) {
      this.ctaTextMaxLength = new IntField(args.ctaTextMaxLength);
    }
    if (args.descriptionLength !== undefined && args.descriptionLength !== null) {
      this.descriptionLength = new IntField(args.descriptionLength);
    }
    if (args.skipDelay !== undefined && args.skipDelay !== null) {
      this.skipDelay = args.skipDelay;
    }
    if (args.refreshRate !== undefined && args.refreshRate !== null) {
      this.refreshRate = args.refreshRate;
    }
    if (args.endCardAutoCloseDuration !== undefined && args.endCardAutoCloseDuration !== null) {
      this.endCardAutoCloseDuration = args.endCardAutoCloseDuration;
    }
    if (args.sponsoredLength !== undefined && args.sponsoredLength !== null) {
      this.sponsoredLength = new IntField(args.sponsoredLength);
    }
    if (args.rating !== undefined && args.rating !== null) {
      this.rating = new BoolField(args.rating);
    }
    if (args.iconWidth !== undefined && args.iconWidth !== null) {
      this.iconWidth = new IntField(args.iconWidth);
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = args.bgColor;
    }
    if (args.ctaText !== undefined && args.ctaText !== null) {
      this.ctaText = args.ctaText;
    }
    if (args.ctaFontSize !== undefined && args.ctaFontSize !== null) {
      this.ctaFontSize = args.ctaFontSize;
    }
    if (args.bgTransparency !== undefined && args.bgTransparency !== null) {
      this.bgTransparency = args.bgTransparency;
    }
  }
};
Value.prototype = {};
Value.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.skippable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.baseTemplate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.muteByDefault = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.verticalAlignment = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.videoMaxDurationInSec = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.enableEndCardAutoClosure = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.hmin = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.wmin = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRUCT) {
        this.adType = new StringField();
        this.adType.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.aspectRatio = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRUCT) {
        this.titleMaxLength = new IntField();
        this.titleMaxLength.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRUCT) {
        this.ctaTextMaxLength = new IntField();
        this.ctaTextMaxLength.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRUCT) {
        this.descriptionLength = new IntField();
        this.descriptionLength.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.I32) {
        this.skipDelay = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.refreshRate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.I32) {
        this.endCardAutoCloseDuration = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRUCT) {
        this.sponsoredLength = new IntField();
        this.sponsoredLength.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRUCT) {
        this.rating = new BoolField();
        this.rating.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRUCT) {
        this.iconWidth = new IntField();
        this.iconWidth.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.bgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRING) {
        this.ctaText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRING) {
        this.ctaFontSize = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.I32) {
        this.bgTransparency = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Value.prototype.write = function(output) {
  output.writeStructBegin('Value');
  if (this.skippable !== null && this.skippable !== undefined) {
    output.writeFieldBegin('skippable', Thrift.Type.BOOL, 1);
    output.writeBool(this.skippable);
    output.writeFieldEnd();
  }
  if (this.baseTemplate !== null && this.baseTemplate !== undefined) {
    output.writeFieldBegin('baseTemplate', Thrift.Type.STRING, 2);
    output.writeString(this.baseTemplate);
    output.writeFieldEnd();
  }
  if (this.muteByDefault !== null && this.muteByDefault !== undefined) {
    output.writeFieldBegin('muteByDefault', Thrift.Type.BOOL, 3);
    output.writeBool(this.muteByDefault);
    output.writeFieldEnd();
  }
  if (this.verticalAlignment !== null && this.verticalAlignment !== undefined) {
    output.writeFieldBegin('verticalAlignment', Thrift.Type.STRING, 4);
    output.writeString(this.verticalAlignment);
    output.writeFieldEnd();
  }
  if (this.videoMaxDurationInSec !== null && this.videoMaxDurationInSec !== undefined) {
    output.writeFieldBegin('videoMaxDurationInSec', Thrift.Type.STRING, 5);
    output.writeString(this.videoMaxDurationInSec);
    output.writeFieldEnd();
  }
  if (this.enableEndCardAutoClosure !== null && this.enableEndCardAutoClosure !== undefined) {
    output.writeFieldBegin('enableEndCardAutoClosure', Thrift.Type.BOOL, 6);
    output.writeBool(this.enableEndCardAutoClosure);
    output.writeFieldEnd();
  }
  if (this.hmin !== null && this.hmin !== undefined) {
    output.writeFieldBegin('hmin', Thrift.Type.I32, 7);
    output.writeI32(this.hmin);
    output.writeFieldEnd();
  }
  if (this.wmin !== null && this.wmin !== undefined) {
    output.writeFieldBegin('wmin', Thrift.Type.I32, 8);
    output.writeI32(this.wmin);
    output.writeFieldEnd();
  }
  if (this.adType !== null && this.adType !== undefined) {
    output.writeFieldBegin('adType', Thrift.Type.STRUCT, 9);
    this.adType.write(output);
    output.writeFieldEnd();
  }
  if (this.aspectRatio !== null && this.aspectRatio !== undefined) {
    output.writeFieldBegin('aspectRatio', Thrift.Type.STRING, 10);
    output.writeString(this.aspectRatio);
    output.writeFieldEnd();
  }
  if (this.titleMaxLength !== null && this.titleMaxLength !== undefined) {
    output.writeFieldBegin('titleMaxLength', Thrift.Type.STRUCT, 11);
    this.titleMaxLength.write(output);
    output.writeFieldEnd();
  }
  if (this.ctaTextMaxLength !== null && this.ctaTextMaxLength !== undefined) {
    output.writeFieldBegin('ctaTextMaxLength', Thrift.Type.STRUCT, 12);
    this.ctaTextMaxLength.write(output);
    output.writeFieldEnd();
  }
  if (this.descriptionLength !== null && this.descriptionLength !== undefined) {
    output.writeFieldBegin('descriptionLength', Thrift.Type.STRUCT, 13);
    this.descriptionLength.write(output);
    output.writeFieldEnd();
  }
  if (this.skipDelay !== null && this.skipDelay !== undefined) {
    output.writeFieldBegin('skipDelay', Thrift.Type.I32, 14);
    output.writeI32(this.skipDelay);
    output.writeFieldEnd();
  }
  if (this.refreshRate !== null && this.refreshRate !== undefined) {
    output.writeFieldBegin('refreshRate', Thrift.Type.STRING, 15);
    output.writeString(this.refreshRate);
    output.writeFieldEnd();
  }
  if (this.endCardAutoCloseDuration !== null && this.endCardAutoCloseDuration !== undefined) {
    output.writeFieldBegin('endCardAutoCloseDuration', Thrift.Type.I32, 16);
    output.writeI32(this.endCardAutoCloseDuration);
    output.writeFieldEnd();
  }
  if (this.sponsoredLength !== null && this.sponsoredLength !== undefined) {
    output.writeFieldBegin('sponsoredLength', Thrift.Type.STRUCT, 17);
    this.sponsoredLength.write(output);
    output.writeFieldEnd();
  }
  if (this.rating !== null && this.rating !== undefined) {
    output.writeFieldBegin('rating', Thrift.Type.STRUCT, 18);
    this.rating.write(output);
    output.writeFieldEnd();
  }
  if (this.iconWidth !== null && this.iconWidth !== undefined) {
    output.writeFieldBegin('iconWidth', Thrift.Type.STRUCT, 19);
    this.iconWidth.write(output);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.STRING, 20);
    output.writeString(this.bgColor);
    output.writeFieldEnd();
  }
  if (this.ctaText !== null && this.ctaText !== undefined) {
    output.writeFieldBegin('ctaText', Thrift.Type.STRING, 21);
    output.writeString(this.ctaText);
    output.writeFieldEnd();
  }
  if (this.ctaFontSize !== null && this.ctaFontSize !== undefined) {
    output.writeFieldBegin('ctaFontSize', Thrift.Type.STRING, 22);
    output.writeString(this.ctaFontSize);
    output.writeFieldEnd();
  }
  if (this.bgTransparency !== null && this.bgTransparency !== undefined) {
    output.writeFieldBegin('bgTransparency', Thrift.Type.I32, 23);
    output.writeI32(this.bgTransparency);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UxTemplate = function(args) {
  this.created_on = null;
  this.modified_on = null;
  this.id = null;
  this.name = null;
  this.value = null;
  this.default_template = null;
  this.active = null;
  this.ad_unit_type = null;
  if (args) {
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.value !== undefined && args.value !== null) {
      this.value = new Value(args.value);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field value is unset!');
    }
    if (args.default_template !== undefined && args.default_template !== null) {
      this.default_template = args.default_template;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field default_template is unset!');
    }
    if (args.active !== undefined && args.active !== null) {
      this.active = args.active;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field active is unset!');
    }
    if (args.ad_unit_type !== undefined && args.ad_unit_type !== null) {
      this.ad_unit_type = args.ad_unit_type;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field ad_unit_type is unset!');
    }
  }
};
UxTemplate.prototype = {};
UxTemplate.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.created_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.modified_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.value = new Value();
        this.value.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.default_template = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.active = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I16) {
        this.ad_unit_type = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UxTemplate.prototype.write = function(output) {
  output.writeStructBegin('UxTemplate');
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.STRING, 1);
    output.writeString(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.STRING, 2);
    output.writeString(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 3);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 4);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.value !== null && this.value !== undefined) {
    output.writeFieldBegin('value', Thrift.Type.STRUCT, 5);
    this.value.write(output);
    output.writeFieldEnd();
  }
  if (this.default_template !== null && this.default_template !== undefined) {
    output.writeFieldBegin('default_template', Thrift.Type.BOOL, 6);
    output.writeBool(this.default_template);
    output.writeFieldEnd();
  }
  if (this.active !== null && this.active !== undefined) {
    output.writeFieldBegin('active', Thrift.Type.BOOL, 7);
    output.writeBool(this.active);
    output.writeFieldEnd();
  }
  if (this.ad_unit_type !== null && this.ad_unit_type !== undefined) {
    output.writeFieldBegin('ad_unit_type', Thrift.Type.I16, 8);
    output.writeI16(this.ad_unit_type);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UXTemplateMapping = function(args) {
  this.created_on = null;
  this.modified_on = null;
  this.id = null;
  this.glasAdUnit = null;
  this.uxTemplate = null;
  if (args) {
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.glasAdUnit !== undefined && args.glasAdUnit !== null) {
      this.glasAdUnit = new GlasAdUnit(args.glasAdUnit);
    }
    if (args.uxTemplate !== undefined && args.uxTemplate !== null) {
      this.uxTemplate = new UxTemplate(args.uxTemplate);
    }
  }
};
UXTemplateMapping.prototype = {};
UXTemplateMapping.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.created_on = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.modified_on = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.glasAdUnit = new GlasAdUnit();
        this.glasAdUnit.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.uxTemplate = new UxTemplate();
        this.uxTemplate.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UXTemplateMapping.prototype.write = function(output) {
  output.writeStructBegin('UXTemplateMapping');
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.I64, 1);
    output.writeI64(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.I64, 2);
    output.writeI64(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 3);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.glasAdUnit !== null && this.glasAdUnit !== undefined) {
    output.writeFieldBegin('glasAdUnit', Thrift.Type.STRUCT, 4);
    this.glasAdUnit.write(output);
    output.writeFieldEnd();
  }
  if (this.uxTemplate !== null && this.uxTemplate !== undefined) {
    output.writeFieldBegin('uxTemplate', Thrift.Type.STRUCT, 5);
    this.uxTemplate.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

IabCategory = function(args) {
  this.id = null;
  this.created_on = null;
  this.modified_on = null;
  this.name = null;
  this.iab_standard_id = null;
  this.parent_id = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.iab_standard_id !== undefined && args.iab_standard_id !== null) {
      this.iab_standard_id = args.iab_standard_id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field iab_standard_id is unset!');
    }
    if (args.parent_id !== undefined && args.parent_id !== null) {
      this.parent_id = args.parent_id;
    }
  }
};
IabCategory.prototype = {};
IabCategory.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I16) {
        this.id = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.created_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.modified_on = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.iab_standard_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I16) {
        this.parent_id = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

IabCategory.prototype.write = function(output) {
  output.writeStructBegin('IabCategory');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I16, 1);
    output.writeI16(this.id);
    output.writeFieldEnd();
  }
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.STRING, 2);
    output.writeString(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.STRING, 3);
    output.writeString(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 4);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.iab_standard_id !== null && this.iab_standard_id !== undefined) {
    output.writeFieldBegin('iab_standard_id', Thrift.Type.STRING, 5);
    output.writeString(this.iab_standard_id);
    output.writeFieldEnd();
  }
  if (this.parent_id !== null && this.parent_id !== undefined) {
    output.writeFieldBegin('parent_id', Thrift.Type.I16, 6);
    output.writeI16(this.parent_id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CreativeAttribute = function(args) {
  this.created_on = null;
  this.modified_on = null;
  this.id = null;
  this.enabled = null;
  this.name = null;
  this.ortbStandardId = null;
  if (args) {
    if (args.created_on !== undefined && args.created_on !== null) {
      this.created_on = args.created_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field created_on is unset!');
    }
    if (args.modified_on !== undefined && args.modified_on !== null) {
      this.modified_on = args.modified_on;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field modified_on is unset!');
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field enabled is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.ortbStandardId !== undefined && args.ortbStandardId !== null) {
      this.ortbStandardId = args.ortbStandardId;
    }
  }
};
CreativeAttribute.prototype = {};
CreativeAttribute.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.created_on = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.modified_on = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I16) {
        this.id = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I16) {
        this.ortbStandardId = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CreativeAttribute.prototype.write = function(output) {
  output.writeStructBegin('CreativeAttribute');
  if (this.created_on !== null && this.created_on !== undefined) {
    output.writeFieldBegin('created_on', Thrift.Type.I64, 1);
    output.writeI64(this.created_on);
    output.writeFieldEnd();
  }
  if (this.modified_on !== null && this.modified_on !== undefined) {
    output.writeFieldBegin('modified_on', Thrift.Type.I64, 2);
    output.writeI64(this.modified_on);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I16, 3);
    output.writeI16(this.id);
    output.writeFieldEnd();
  }
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 4);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 5);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.ortbStandardId !== null && this.ortbStandardId !== undefined) {
    output.writeFieldBegin('ortbStandardId', Thrift.Type.I16, 6);
    output.writeI16(this.ortbStandardId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


Role = {
  '0' : 'ADMIN',
  'ADMIN' : 0,
  '1' : 'CURATOR',
  'CURATOR' : 1,
  '2' : 'MODERATOR',
  'MODERATOR' : 2,
  '3' : 'DESIGNER',
  'DESIGNER' : 3,
  '4' : 'PARTNER',
  'PARTNER' : 4,
  '5' : 'EXT_ADMIN',
  'EXT_ADMIN' : 5,
  '6' : 'EXT_CURATOR',
  'EXT_CURATOR' : 6
};
Region = {
  '1' : 'US',
  'US' : 1,
  '2' : 'IN',
  'IN' : 2,
  '3' : 'CN',
  'CN' : 3,
  '4' : 'KR',
  'KR' : 4,
  '5' : 'ID',
  'ID' : 5,
  '6' : 'HK',
  'HK' : 6,
  '7' : 'KH',
  'KH' : 7,
  '8' : 'MM',
  'MM' : 8,
  '9' : 'MO',
  'MO' : 9,
  '10' : 'MY',
  'MY' : 10,
  '11' : 'PH',
  'PH' : 11,
  '12' : 'PK',
  'PK' : 12,
  '13' : 'RU',
  'RU' : 13,
  '14' : 'SG',
  'SG' : 14,
  '15' : 'TH',
  'TH' : 15,
  '16' : 'TW',
  'TW' : 16,
  '17' : 'VN',
  'VN' : 17,
  '18' : 'LK',
  'LK' : 18,
  '19' : 'LA',
  'LA' : 19,
  '20' : 'KE',
  'KE' : 20,
  '21' : 'MA',
  'MA' : 21,
  '22' : 'BD',
  'BD' : 22,
  '23' : 'NP',
  'NP' : 23,
  '24' : 'AU',
  'AU' : 24,
  '25' : 'NZ',
  'NZ' : 25,
  '26' : 'MX',
  'MX' : 26,
  '27' : 'BR',
  'BR' : 27,
  '28' : 'CO',
  'CO' : 28,
  '29' : 'JP',
  'JP' : 29,
  '30' : 'AT',
  'AT' : 30,
  '31' : 'DE',
  'DE' : 31,
  '32' : 'NG',
  'NG' : 32,
  '33' : 'GB',
  'GB' : 33,
  '97' : 'ISLAMIC',
  'ISLAMIC' : 97,
  '98' : 'NON_ISLAMIC',
  'NON_ISLAMIC' : 98,
  '99' : 'GLOBAL',
  'GLOBAL' : 99,
  '100' : 'NO_REGION',
  'NO_REGION' : 100,
  '101' : 'ALL_REGION',
  'ALL_REGION' : 101
};
WidgetEnum = {
  '0' : 'BREAKING_NEWS_WIDGET',
  'BREAKING_NEWS_WIDGET' : 0,
  '1' : 'STORIES_YOU_FOLLOW_WIDGET',
  'STORIES_YOU_FOLLOW_WIDGET' : 1,
  '2' : 'TEMPERATURE_WIDGET',
  'TEMPERATURE_WIDGET' : 2,
  '3' : 'AROUND_YOU_NEWS_WIDGET',
  'AROUND_YOU_NEWS_WIDGET' : 3,
  '4' : 'AIR_QUALITY_WIDGET',
  'AIR_QUALITY_WIDGET' : 4,
  '5' : 'MORNING_ROUND_UP_WIDGET',
  'MORNING_ROUND_UP_WIDGET' : 5,
  '6' : 'HEADLINES_WIDGET',
  'HEADLINES_WIDGET' : 6,
  '7' : 'LOCAL_NEWS_WIDGET',
  'LOCAL_NEWS_WIDGET' : 7,
  '8' : 'SPORTS_NEWS_WIDGET',
  'SPORTS_NEWS_WIDGET' : 8,
  '9' : 'SPORTS_HIGHLIGHTS_WIDGET',
  'SPORTS_HIGHLIGHTS_WIDGET' : 9
};
Language = {
  '1' : 'ENG',
  'ENG' : 1,
  '99' : 'NONE',
  'NONE' : 99
};
LanguageLimit = {
  '150' : 'ENG',
  'ENG' : 150,
  '200' : 'HINDI',
  'HINDI' : 200
};
StoryType = {
  '1' : 'NEWS',
  'NEWS' : 1,
  '2' : 'FEATURE',
  'FEATURE' : 2,
  '3' : 'GLANCETV',
  'GLANCETV' : 3,
  '4' : 'TV',
  'TV' : 4
};
PreferredNetworkType = {
  '0' : 'ONLY_WIFI',
  'ONLY_WIFI' : 0,
  '1' : 'ANY',
  'ANY' : 1
};
CategorySubscriptionType = {
  '1' : 'SDK_SUBSCRIBED',
  'SDK_SUBSCRIBED' : 1,
  '2' : 'SDK_UNSUBSCRIBED',
  'SDK_UNSUBSCRIBED' : 2,
  '3' : 'WEB_SUBSCRIBED',
  'WEB_SUBSCRIBED' : 3,
  '4' : 'WEB_UNSUBSCRIBED',
  'WEB_UNSUBSCRIBED' : 4
};
LanguageSubscriptionType = {
  '1' : 'AUTO',
  'AUTO' : 1,
  '2' : 'SDK_SUBSCRIBED',
  'SDK_SUBSCRIBED' : 2,
  '3' : 'SDK_UNSUBSCRIBED',
  'SDK_UNSUBSCRIBED' : 3,
  '4' : 'AUTO_SUBSCRIBED',
  'AUTO_SUBSCRIBED' : 4
};
CtaDisplayType = {
  '1' : 'TEXT',
  'TEXT' : 1,
  '2' : 'IMAGE',
  'IMAGE' : 2
};
PropositionType = {
  '0' : 'UNKNOWN',
  'UNKNOWN' : 0,
  '1' : 'CTA_URL',
  'CTA_URL' : 1,
  '2' : 'APP_INSTALL',
  'APP_INSTALL' : 2
};
TournamentStatus = {
  '1' : 'LIVE',
  'LIVE' : 1,
  '2' : 'UPCOMING',
  'UPCOMING' : 2,
  '3' : 'COMPLETED',
  'COMPLETED' : 3
};
BucketPriority = {
  '4' : 'SPONSORSHIP',
  'SPONSORSHIP' : 4,
  '3' : 'HIGH_PRIORITY',
  'HIGH_PRIORITY' : 3,
  '2' : 'LOW_PRIORITY',
  'LOW_PRIORITY' : 2,
  '1' : 'PRICE_PRIORITY',
  'PRICE_PRIORITY' : 1,
  '0' : 'HOUSE',
  'HOUSE' : 0
};
GlanceFeature = {
  '0' : 'ONE_CLICK_INSTALL',
  'ONE_CLICK_INSTALL' : 0,
  '1' : 'BINGE_OVERLAY',
  'BINGE_OVERLAY' : 1,
  '2' : 'PARTITIONED_BINGE_OVERLAY',
  'PARTITIONED_BINGE_OVERLAY' : 2,
  '3' : 'NATIVE_VIDEO_COUNTERPART',
  'NATIVE_VIDEO_COUNTERPART' : 3,
  '4' : 'CTA_NULLABLE',
  'CTA_NULLABLE' : 4,
  '5' : 'ONLINE_AND_HIGHLIGHTS_ONLY',
  'ONLINE_AND_HIGHLIGHTS_ONLY' : 5,
  '6' : 'SWIPE_VISUAL_CUE',
  'SWIPE_VISUAL_CUE' : 6,
  '7' : 'HIGH_EXPOSURE_TRENDING',
  'HIGH_EXPOSURE_TRENDING' : 7,
  '8' : 'SWIPE_AND_TRENDING_EXPERIMENTS',
  'SWIPE_AND_TRENDING_EXPERIMENTS' : 8,
  '9' : 'SWIPE_SCALE',
  'SWIPE_SCALE' : 9,
  '10' : 'SCALE_GUARANTEE',
  'SCALE_GUARANTEE' : 10,
  '11' : 'APP_OWNERSHIP',
  'APP_OWNERSHIP' : 11,
  '12' : 'LANDING_PAGE_APPEND_UTM',
  'LANDING_PAGE_APPEND_UTM' : 12,
  '13' : 'RECORDED_LIVE',
  'RECORDED_LIVE' : 13,
  '14' : 'FEATURE_BANK',
  'FEATURE_BANK' : 14
};
RewardTransactionType = {
  '0' : 'SWEEPSTAKE',
  'SWEEPSTAKE' : 0,
  '1' : 'GAME_TOURNAMENT',
  'GAME_TOURNAMENT' : 1,
  '2' : 'QUIZ',
  'QUIZ' : 2,
  '3' : 'VOUCHER',
  'VOUCHER' : 3,
  '4' : 'LIVE_SESSION',
  'LIVE_SESSION' : 4
};
OmSdkInfoType = {
  '0' : 'ADVERTISER',
  'ADVERTISER' : 0,
  '1' : 'SPONSORED',
  'SPONSORED' : 1
};
GlanceEUIdentifier = {
  '0' : 'FEED',
  'FEED' : 0,
  '1' : 'LIVE',
  'LIVE' : 1,
  '2' : 'GAMING',
  'GAMING' : 2,
  '3' : 'ANDROID_TV',
  'ANDROID_TV' : 3,
  '4' : 'MONETIZATION',
  'MONETIZATION' : 4,
  '5' : 'FEED_GREENPILOT',
  'FEED_GREENPILOT' : 5,
  '6' : 'FEED_BETA',
  'FEED_BETA' : 6
};
TextSize = {
  '0' : 'SMALL',
  'SMALL' : 0,
  '1' : 'MEDIUM',
  'MEDIUM' : 1,
  '2' : 'LARGE',
  'LARGE' : 2
};
SpaceType = {
  '0' : 'ROUND_UP',
  'ROUND_UP' : 0,
  '1' : 'SYF',
  'SYF' : 1,
  '2' : 'SPORTS_HIGHLIGHTS',
  'SPORTS_HIGHLIGHTS' : 2
};
EventType = {
  '0' : 'INSERT',
  'INSERT' : 0,
  '1' : 'UPDATE',
  'UPDATE' : 1,
  '2' : 'DELETE',
  'DELETE' : 2
};
ContentType = {
  '0' : 'INFO',
  'INFO' : 0,
  '1' : 'ACTION',
  'ACTION' : 1
};
ValueProp = {
  '0' : 'NEWS',
  'NEWS' : 0,
  '1' : 'TOP_STORY',
  'TOP_STORY' : 1,
  '2' : 'POLL',
  'POLL' : 2,
  '3' : 'PREDICT_THE_WINNER',
  'PREDICT_THE_WINNER' : 3,
  '4' : 'CHEER_YOUR_TEAM',
  'CHEER_YOUR_TEAM' : 4,
  '5' : 'WATCH_LIVE',
  'WATCH_LIVE' : 5,
  '6' : 'JC_DOWNLOAD',
  'JC_DOWNLOAD' : 6,
  '7' : 'JC_OPEN',
  'JC_OPEN' : 7,
  '8' : 'T20_TAB',
  'T20_TAB' : 8,
  '9' : 'UNKNOWN',
  'UNKNOWN' : 9
};
LiveEvent = {
  '0' : 'IPL_2023',
  'IPL_2023' : 0,
  '1' : 'IPL',
  'IPL' : 1,
  '2' : 'WORLD_CUP_2024',
  'WORLD_CUP_2024' : 2,
  '3' : 'UNKNOWN',
  'UNKNOWN' : 3
};
SizeFit = {
  '0' : 'FILL',
  'FILL' : 0,
  '1' : 'FIT',
  'FIT' : 1
};
ImageType = {
  '0' : 'LEGACY',
  'LEGACY' : 0,
  '1' : 'THUMBNAIL',
  'THUMBNAIL' : 1,
  '2' : 'BLUR',
  'BLUR' : 2,
  '3' : 'ORIGINAL',
  'ORIGINAL' : 3,
  '4' : 'STUDIO_ORIGINAL',
  'STUDIO_ORIGINAL' : 4,
  '5' : 'UPSCALED',
  'UPSCALED' : 5
};
ImageEncoding = {
  '0' : 'WEBP',
  'WEBP' : 0,
  '1' : 'JPG',
  'JPG' : 1,
  '2' : 'PNG',
  'PNG' : 2
};
WakeupMethod = {
  '1' : 'FRDB',
  'FRDB' : 1,
  '2' : 'FCM',
  'FCM' : 2
};
SessionMode = {
  '0' : 'ANY',
  'ANY' : 0,
  '1' : 'DEFAULT',
  'DEFAULT' : 1,
  '2' : 'BINGE',
  'BINGE' : 2,
  '3' : 'HIGHLIGHTS',
  'HIGHLIGHTS' : 3,
  '4' : 'GAME_REWARDED',
  'GAME_REWARDED' : 4,
  '5' : 'GAME_SPLASH',
  'GAME_SPLASH' : 5,
  '6' : 'SCREENSAVER',
  'SCREENSAVER' : 6,
  '7' : 'PATCHWALL_STORIES',
  'PATCHWALL_STORIES' : 7
};
GlanceType = {
  '1' : 'WALLPAPER',
  'WALLPAPER' : 1,
  '2' : 'STORY',
  'STORY' : 2,
  '3' : 'RESERVED_STORY',
  'RESERVED_STORY' : 3,
  '4' : 'SPONSORED',
  'SPONSORED' : 4
};
Space = {
  '0' : 'NEWS',
  'NEWS' : 0,
  '1' : 'SPORTS',
  'SPORTS' : 1,
  '2' : 'GAMING',
  'GAMING' : 2
};
EU = {
  '0' : 'TRENDS_AND_SPORTS',
  'TRENDS_AND_SPORTS' : 0,
  '1' : 'ROPOSO',
  'ROPOSO' : 1,
  '2' : 'SHOP',
  'SHOP' : 2,
  '3' : 'GAMING',
  'GAMING' : 3
};
SubscriptionType = {
  '0' : 'PRIMARY_REFERRER',
  'PRIMARY_REFERRER' : 0,
  '1' : 'SECONDARY_REFERRER',
  'SECONDARY_REFERRER' : 1,
  '2' : 'USER_SELECTED',
  'USER_SELECTED' : 2
};
GlanceContextType = {
  '0' : 'LIVE',
  'LIVE' : 0,
  '1' : 'FEATURED',
  'FEATURED' : 1,
  '2' : 'LAUNCH',
  'LAUNCH' : 2,
  '3' : 'NEARBY',
  'NEARBY' : 3,
  '4' : 'VIRAL',
  'VIRAL' : 4,
  '5' : 'HOT',
  'HOT' : 5,
  '6' : 'TRENDING',
  'TRENDING' : 6,
  '7' : 'HATKE',
  'HATKE' : 7,
  '8' : 'EXCLUSIVE',
  'EXCLUSIVE' : 8,
  '9' : 'FUN',
  'FUN' : 9,
  '10' : 'POPULAR',
  'POPULAR' : 10,
  '11' : 'BREAKING_NEWS',
  'BREAKING_NEWS' : 11,
  '12' : 'CATEGORY',
  'CATEGORY' : 12,
  '13' : 'SHOPPING',
  'SHOPPING' : 13
};
UserAgeFilter = {
  '0' : 'NEW_USER',
  'NEW_USER' : 0,
  '1' : 'OLD_USER',
  'OLD_USER' : 1,
  '2' : 'ALL_USER',
  'ALL_USER' : 2,
  '3' : 'NEW_USER_0_1',
  'NEW_USER_0_1' : 3,
  '4' : 'NEW_USER_1_2',
  'NEW_USER_1_2' : 4,
  '5' : 'NEW_USER_2_5',
  'NEW_USER_2_5' : 5,
  '6' : 'NEW_USER_5_7',
  'NEW_USER_5_7' : 6
};
SubscriptionStatus = {
  '0' : 'NONE',
  'NONE' : 0,
  '1' : 'ANY',
  'ANY' : 1,
  '2' : 'NO_USER',
  'NO_USER' : 2
};
UserNetworkType = {
  '0' : 'OFFLINE',
  'OFFLINE' : 0,
  '1' : 'WIFI',
  'WIFI' : 1,
  '2' : 'MOBILE_DATA',
  'MOBILE_DATA' : 2
};
ChannelType = {
  '0' : 'DIRECT',
  'DIRECT' : 0,
  '1' : 'PROGRAMMATIC',
  'PROGRAMMATIC' : 1
};
CreativeHosting = {
  '0' : 'PUBLISHER',
  'PUBLISHER' : 0,
  '1' : 'ADVERTISER',
  'ADVERTISER' : 1
};
StreamType = {
  '0' : 'BIDSTREAM',
  'BIDSTREAM' : 0,
  '1' : 'WINSTREAM',
  'WINSTREAM' : 1
};
BubbleType = {
  '0' : 'GLANCE_TV',
  'GLANCE_TV' : 0,
  '1' : 'NEWS',
  'NEWS' : 1,
  '2' : 'ROPOSO_CHANNELS',
  'ROPOSO_CHANNELS' : 2,
  '3' : 'TRENDS',
  'TRENDS' : 3,
  '4' : 'LIVE',
  'LIVE' : 4,
  '5' : 'PERMANENT',
  'PERMANENT' : 5,
  '6' : 'HASHTAG_CHALLENGES',
  'HASHTAG_CHALLENGES' : 6,
  '7' : 'CREATOR_SERIES',
  'CREATOR_SERIES' : 7,
  '8' : 'ANDROID_TV',
  'ANDROID_TV' : 8,
  '9' : 'TWITTER_HASHTAGS',
  'TWITTER_HASHTAGS' : 9,
  '10' : 'PUBLISHER_SERIES',
  'PUBLISHER_SERIES' : 10,
  '11' : 'YT_TIME_SENSITIVE',
  'YT_TIME_SENSITIVE' : 11,
  '12' : 'ONBOARDING',
  'ONBOARDING' : 12,
  '13' : 'TWITTER_CONVERSATIONS',
  'TWITTER_CONVERSATIONS' : 13,
  '14' : 'GPS_PUBLISHER',
  'GPS_PUBLISHER' : 14
};
Identifier = {
  '0' : 'SPACE',
  'SPACE' : 0,
  '1' : 'FEED',
  'FEED' : 1
};
DurationUnit = {
  '0' : 'HOURS',
  'HOURS' : 0,
  '1' : 'DAYS',
  'DAYS' : 1,
  '2' : 'MINUTES',
  'MINUTES' : 2,
  '3' : 'SECONDS',
  'SECONDS' : 3
};
GlanceMetaState = {
  '0' : 'INMOBI_APPROVED',
  'INMOBI_APPROVED' : 0,
  '1' : 'STUDIO_APPROVED',
  'STUDIO_APPROVED' : 1,
  '2' : 'PARTNER_APPROVED',
  'PARTNER_APPROVED' : 2,
  '3' : 'PARTNER_REJECTED',
  'PARTNER_REJECTED' : 3,
  '4' : 'PARTNER_REMOVED',
  'PARTNER_REMOVED' : 4,
  '5' : 'INMOBI_REMOVED',
  'INMOBI_REMOVED' : 5,
  '6' : 'STUDIO_REMOVED',
  'STUDIO_REMOVED' : 6,
  '7' : 'INTERNAL_PENDING',
  'INTERNAL_PENDING' : 7,
  '8' : 'INTERNAL_APPROVED',
  'INTERNAL_APPROVED' : 8,
  '9' : 'INTERNAL_REJECTED',
  'INTERNAL_REJECTED' : 9,
  '10' : 'STUDIO_PAUSED',
  'STUDIO_PAUSED' : 10,
  '11' : 'READY_TO_SERVE',
  'READY_TO_SERVE' : 11
};
Day = {
  '0' : 'MONDAY',
  'MONDAY' : 0,
  '1' : 'TUESDAY',
  'TUESDAY' : 1,
  '2' : 'WEDNESDAY',
  'WEDNESDAY' : 2,
  '3' : 'THURSDAY',
  'THURSDAY' : 3,
  '4' : 'FRIDAY',
  'FRIDAY' : 4,
  '5' : 'SATURDAY',
  'SATURDAY' : 5,
  '6' : 'SUNDAY',
  'SUNDAY' : 6
};
TargetDevice = {
  '0' : 'MOBILE',
  'MOBILE' : 0,
  '1' : 'ANDROID_TV',
  'ANDROID_TV' : 1
};
DraftState = {
  '0' : 'NEW',
  'NEW' : 0,
  '1' : 'CURATOR_CREATED',
  'CURATOR_CREATED' : 1,
  '2' : 'CURATOR_APPROVED',
  'CURATOR_APPROVED' : 2,
  '3' : 'CURATOR_REJECTED',
  'CURATOR_REJECTED' : 3,
  '4' : 'DESIGNER_CREATED',
  'DESIGNER_CREATED' : 4,
  '5' : 'DESIGNER_ASSIGNED',
  'DESIGNER_ASSIGNED' : 5,
  '6' : 'DESIGNER_APPROVED',
  'DESIGNER_APPROVED' : 6,
  '7' : 'APPROVED',
  'APPROVED' : 7,
  '8' : 'PUBLISHED',
  'PUBLISHED' : 8,
  '9' : 'REJECTED',
  'REJECTED' : 9
};
DevNet = {
  '0' : 'UNKNOWN',
  'UNKNOWN' : 0,
  '1' : 'OFFLINE',
  'OFFLINE' : 1,
  '2' : 'WIFI',
  'WIFI' : 2,
  '3' : 'WIFI_METERED',
  'WIFI_METERED' : 3,
  '4' : 'MOBILE',
  'MOBILE' : 4,
  '5' : 'MOBILE_2G',
  'MOBILE_2G' : 5,
  '6' : 'MOBILE_3G',
  'MOBILE_3G' : 6,
  '7' : 'MOBILE_4G',
  'MOBILE_4G' : 7,
  '8' : 'MOBILE_5G',
  'MOBILE_5G' : 8
};
RewardTxnStatus = {
  '0' : 'REFUNDED',
  'REFUNDED' : 0,
  '1' : 'PROCESSED',
  'PROCESSED' : 1
};
TagName = {
  '0' : 'BUMPER_PRIZE',
  'BUMPER_PRIZE' : 0,
  '1' : 'HIGH_CHANCES_OF_WIN',
  'HIGH_CHANCES_OF_WIN' : 1,
  '2' : 'DAILY_WINNERS',
  'DAILY_WINNERS' : 2
};
GlanceTvStatus = {
  '0' : 'ACCEPTED',
  'ACCEPTED' : 0,
  '1' : 'REJECTED',
  'REJECTED' : 1,
  '2' : 'PENDING',
  'PENDING' : 2
};
ImpressionsLimitType = {
  '0' : 'NO_LIMIT',
  'NO_LIMIT' : 0
};
AssetType = {
  '0' : 'CARD',
  'CARD' : 0,
  '1' : 'BUBBLE',
  'BUBBLE' : 1
};
LifetimeEvents = {
  '0' : 'tap_ls',
  'tap_ls' : 0,
  '1' : 'swipe_right_ls',
  'swipe_right_ls' : 1,
  '2' : 'topic_completion',
  'topic_completion' : 2,
  '3' : 'glance_ended',
  'glance_ended' : 3,
  '4' : 'video_ended',
  'video_ended' : 4,
  '5' : 'complete_feed',
  'complete_feed' : 5,
  '6' : 'enter_gc',
  'enter_gc' : 6,
  '7' : 'game_click_gc',
  'game_click_gc' : 7,
  '8' : 'enter_video',
  'enter_video' : 8,
  '9' : 'video_click',
  'video_click' : 9
};
SessionEvents = {
  '0' : 'gc_session_300',
  'gc_session_300' : 0,
  '1' : 'gc_session_600',
  'gc_session_600' : 1,
  '2' : 'gc_session_1200',
  'gc_session_1200' : 2,
  '3' : 'complete_ad_video',
  'complete_ad_video' : 3,
  '4' : 'landing_page_open',
  'landing_page_open' : 4
};
OneTimeEvents = {
  '0' : 'set_category_pref',
  'set_category_pref' : 0,
  '1' : 'set_lang_pref',
  'set_lang_pref' : 1
};
WinningStatus = {
  '0' : 'SCRATCHED',
  'SCRATCHED' : 0,
  '1' : 'UNSCRATCHED',
  'UNSCRATCHED' : 1
};
CardEventType = {
  '0' : 'PUBLISH',
  'PUBLISH' : 0,
  '1' : 'UNPUBLISH',
  'UNPUBLISH' : 1
};
GeoStatus = {
  '0' : 'COARSE',
  'COARSE' : 0,
  '1' : 'FINE',
  'FINE' : 1
};
ExperimentVariant = function(args) {
  this.id = null;
  this.name = null;
  this.startScore = null;
  this.endScore = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.startScore !== undefined && args.startScore !== null) {
      this.startScore = args.startScore;
    }
    if (args.endScore !== undefined && args.endScore !== null) {
      this.endScore = args.endScore;
    }
  }
};
ExperimentVariant.prototype = {};
ExperimentVariant.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.startScore = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.endScore = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ExperimentVariant.prototype.write = function(output) {
  output.writeStructBegin('ExperimentVariant');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.startScore !== null && this.startScore !== undefined) {
    output.writeFieldBegin('startScore', Thrift.Type.I32, 3);
    output.writeI32(this.startScore);
    output.writeFieldEnd();
  }
  if (this.endScore !== null && this.endScore !== undefined) {
    output.writeFieldBegin('endScore', Thrift.Type.I32, 4);
    output.writeI32(this.endScore);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RuleIds = function(args) {
  this.ruleIdlist = null;
  if (args) {
    if (args.ruleIdlist !== undefined && args.ruleIdlist !== null) {
      this.ruleIdlist = Thrift.copyList(args.ruleIdlist, [null]);
    }
  }
};
RuleIds.prototype = {};
RuleIds.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size0 = 0;
        var _rtmp34;
        this.ruleIdlist = [];
        var _etype3 = 0;
        _rtmp34 = input.readListBegin();
        _etype3 = _rtmp34.etype;
        _size0 = _rtmp34.size;
        for (var _i5 = 0; _i5 < _size0; ++_i5)
        {
          var elem6 = null;
          elem6 = input.readString().value;
          this.ruleIdlist.push(elem6);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RuleIds.prototype.write = function(output) {
  output.writeStructBegin('RuleIds');
  if (this.ruleIdlist !== null && this.ruleIdlist !== undefined) {
    output.writeFieldBegin('ruleIdlist', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.ruleIdlist.length);
    for (var iter7 in this.ruleIdlist)
    {
      if (this.ruleIdlist.hasOwnProperty(iter7))
      {
        iter7 = this.ruleIdlist[iter7];
        output.writeString(iter7);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Experiment = function(args) {
  this.id = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.name = null;
  this.expiresAt = null;
  this.control = null;
  this.variants = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field createdAt is unset!');
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.expiresAt !== undefined && args.expiresAt !== null) {
      this.expiresAt = args.expiresAt;
    }
    if (args.control !== undefined && args.control !== null) {
      this.control = new ExperimentVariant(args.control);
    }
    if (args.variants !== undefined && args.variants !== null) {
      this.variants = Thrift.copyList(args.variants, [ExperimentVariant]);
    }
  }
};
Experiment.prototype = {};
Experiment.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.createdAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.updatedAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.expiresAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.control = new ExperimentVariant();
        this.control.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size8 = 0;
        var _rtmp312;
        this.variants = [];
        var _etype11 = 0;
        _rtmp312 = input.readListBegin();
        _etype11 = _rtmp312.etype;
        _size8 = _rtmp312.size;
        for (var _i13 = 0; _i13 < _size8; ++_i13)
        {
          var elem14 = null;
          elem14 = new ExperimentVariant();
          elem14.read(input);
          this.variants.push(elem14);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Experiment.prototype.write = function(output) {
  output.writeStructBegin('Experiment');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.I64, 2);
    output.writeI64(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.I64, 3);
    output.writeI64(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 4);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.expiresAt !== null && this.expiresAt !== undefined) {
    output.writeFieldBegin('expiresAt', Thrift.Type.I64, 5);
    output.writeI64(this.expiresAt);
    output.writeFieldEnd();
  }
  if (this.control !== null && this.control !== undefined) {
    output.writeFieldBegin('control', Thrift.Type.STRUCT, 6);
    this.control.write(output);
    output.writeFieldEnd();
  }
  if (this.variants !== null && this.variants !== undefined) {
    output.writeFieldBegin('variants', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRUCT, this.variants.length);
    for (var iter15 in this.variants)
    {
      if (this.variants.hasOwnProperty(iter15))
      {
        iter15 = this.variants[iter15];
        iter15.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ExperimentMeta = function(args) {
  this.id = null;
  this.variantId = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.variantId !== undefined && args.variantId !== null) {
      this.variantId = args.variantId;
    }
  }
};
ExperimentMeta.prototype = {};
ExperimentMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.variantId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ExperimentMeta.prototype.write = function(output) {
  output.writeStructBegin('ExperimentMeta');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.variantId !== null && this.variantId !== undefined) {
    output.writeFieldBegin('variantId', Thrift.Type.STRING, 2);
    output.writeString(this.variantId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Partner = function(args) {
  this.partnerId = null;
  this.name = null;
  if (args) {
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partnerId is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
  }
};
Partner.prototype = {};
Partner.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Partner.prototype.write = function(output) {
  output.writeStructBegin('Partner');
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 1);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AbConfig = function(args) {
  this.id = null;
  this.name = null;
  this.region = null;
  this.lowerBound = null;
  this.upperBound = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.lowerBound !== undefined && args.lowerBound !== null) {
      this.lowerBound = args.lowerBound;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field lowerBound is unset!');
    }
    if (args.upperBound !== undefined && args.upperBound !== null) {
      this.upperBound = args.upperBound;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field upperBound is unset!');
    }
  }
};
AbConfig.prototype = {};
AbConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.lowerBound = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.upperBound = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AbConfig.prototype.write = function(output) {
  output.writeStructBegin('AbConfig');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 3);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.lowerBound !== null && this.lowerBound !== undefined) {
    output.writeFieldBegin('lowerBound', Thrift.Type.I32, 4);
    output.writeI32(this.lowerBound);
    output.writeFieldEnd();
  }
  if (this.upperBound !== null && this.upperBound !== undefined) {
    output.writeFieldBegin('upperBound', Thrift.Type.I32, 5);
    output.writeI32(this.upperBound);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerMeta = function(args) {
  this.partnerId = null;
  this.name = null;
  this.supportedRegions = null;
  this.autoApprove = null;
  this.approveEmails = null;
  this.abConfigs = null;
  this.isSponsoredEnabled = null;
  this.cdnPrefix = null;
  this.s3CdnPrefix = null;
  if (args) {
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partnerId is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.supportedRegions !== undefined && args.supportedRegions !== null) {
      this.supportedRegions = Thrift.copyList(args.supportedRegions, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field supportedRegions is unset!');
    }
    if (args.autoApprove !== undefined && args.autoApprove !== null) {
      this.autoApprove = args.autoApprove;
    }
    if (args.approveEmails !== undefined && args.approveEmails !== null) {
      this.approveEmails = Thrift.copyList(args.approveEmails, [null]);
    }
    if (args.abConfigs !== undefined && args.abConfigs !== null) {
      this.abConfigs = Thrift.copyList(args.abConfigs, [AbConfig]);
    }
    if (args.isSponsoredEnabled !== undefined && args.isSponsoredEnabled !== null) {
      this.isSponsoredEnabled = args.isSponsoredEnabled;
    }
    if (args.cdnPrefix !== undefined && args.cdnPrefix !== null) {
      this.cdnPrefix = args.cdnPrefix;
    }
    if (args.s3CdnPrefix !== undefined && args.s3CdnPrefix !== null) {
      this.s3CdnPrefix = args.s3CdnPrefix;
    }
  }
};
PartnerMeta.prototype = {};
PartnerMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size16 = 0;
        var _rtmp320;
        this.supportedRegions = [];
        var _etype19 = 0;
        _rtmp320 = input.readListBegin();
        _etype19 = _rtmp320.etype;
        _size16 = _rtmp320.size;
        for (var _i21 = 0; _i21 < _size16; ++_i21)
        {
          var elem22 = null;
          elem22 = input.readI32().value;
          this.supportedRegions.push(elem22);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.autoApprove = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size23 = 0;
        var _rtmp327;
        this.approveEmails = [];
        var _etype26 = 0;
        _rtmp327 = input.readListBegin();
        _etype26 = _rtmp327.etype;
        _size23 = _rtmp327.size;
        for (var _i28 = 0; _i28 < _size23; ++_i28)
        {
          var elem29 = null;
          elem29 = input.readString().value;
          this.approveEmails.push(elem29);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size30 = 0;
        var _rtmp334;
        this.abConfigs = [];
        var _etype33 = 0;
        _rtmp334 = input.readListBegin();
        _etype33 = _rtmp334.etype;
        _size30 = _rtmp334.size;
        for (var _i35 = 0; _i35 < _size30; ++_i35)
        {
          var elem36 = null;
          elem36 = new AbConfig();
          elem36.read(input);
          this.abConfigs.push(elem36);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.isSponsoredEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.cdnPrefix = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.s3CdnPrefix = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerMeta.prototype.write = function(output) {
  output.writeStructBegin('PartnerMeta');
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 1);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.supportedRegions !== null && this.supportedRegions !== undefined) {
    output.writeFieldBegin('supportedRegions', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.I32, this.supportedRegions.length);
    for (var iter37 in this.supportedRegions)
    {
      if (this.supportedRegions.hasOwnProperty(iter37))
      {
        iter37 = this.supportedRegions[iter37];
        output.writeI32(iter37);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.autoApprove !== null && this.autoApprove !== undefined) {
    output.writeFieldBegin('autoApprove', Thrift.Type.BOOL, 4);
    output.writeBool(this.autoApprove);
    output.writeFieldEnd();
  }
  if (this.approveEmails !== null && this.approveEmails !== undefined) {
    output.writeFieldBegin('approveEmails', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRING, this.approveEmails.length);
    for (var iter38 in this.approveEmails)
    {
      if (this.approveEmails.hasOwnProperty(iter38))
      {
        iter38 = this.approveEmails[iter38];
        output.writeString(iter38);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.abConfigs !== null && this.abConfigs !== undefined) {
    output.writeFieldBegin('abConfigs', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.STRUCT, this.abConfigs.length);
    for (var iter39 in this.abConfigs)
    {
      if (this.abConfigs.hasOwnProperty(iter39))
      {
        iter39 = this.abConfigs[iter39];
        iter39.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.isSponsoredEnabled !== null && this.isSponsoredEnabled !== undefined) {
    output.writeFieldBegin('isSponsoredEnabled', Thrift.Type.BOOL, 7);
    output.writeBool(this.isSponsoredEnabled);
    output.writeFieldEnd();
  }
  if (this.cdnPrefix !== null && this.cdnPrefix !== undefined) {
    output.writeFieldBegin('cdnPrefix', Thrift.Type.STRING, 8);
    output.writeString(this.cdnPrefix);
    output.writeFieldEnd();
  }
  if (this.s3CdnPrefix !== null && this.s3CdnPrefix !== undefined) {
    output.writeFieldBegin('s3CdnPrefix', Thrift.Type.STRING, 9);
    output.writeString(this.s3CdnPrefix);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ApiKeyMeta = function(args) {
  this.apiKey = null;
  this.partnerId = null;
  this.name = null;
  this.fcmServerKey = null;
  this.blurRequired = null;
  if (args) {
    if (args.apiKey !== undefined && args.apiKey !== null) {
      this.apiKey = args.apiKey;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field apiKey is unset!');
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partnerId is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.fcmServerKey !== undefined && args.fcmServerKey !== null) {
      this.fcmServerKey = args.fcmServerKey;
    }
    if (args.blurRequired !== undefined && args.blurRequired !== null) {
      this.blurRequired = args.blurRequired;
    }
  }
};
ApiKeyMeta.prototype = {};
ApiKeyMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.apiKey = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.fcmServerKey = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.blurRequired = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ApiKeyMeta.prototype.write = function(output) {
  output.writeStructBegin('ApiKeyMeta');
  if (this.apiKey !== null && this.apiKey !== undefined) {
    output.writeFieldBegin('apiKey', Thrift.Type.STRING, 1);
    output.writeString(this.apiKey);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 2);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 4);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.fcmServerKey !== null && this.fcmServerKey !== undefined) {
    output.writeFieldBegin('fcmServerKey', Thrift.Type.STRING, 5);
    output.writeString(this.fcmServerKey);
    output.writeFieldEnd();
  }
  if (this.blurRequired !== null && this.blurRequired !== undefined) {
    output.writeFieldBegin('blurRequired', Thrift.Type.BOOL, 6);
    output.writeBool(this.blurRequired);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppOptionalDetails = function(args) {
  this.extendedDescription = null;
  if (args) {
    if (args.extendedDescription !== undefined && args.extendedDescription !== null) {
      this.extendedDescription = args.extendedDescription;
    }
  }
};
AppOptionalDetails.prototype = {};
AppOptionalDetails.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.extendedDescription = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppOptionalDetails.prototype.write = function(output) {
  output.writeStructBegin('AppOptionalDetails');
  if (this.extendedDescription !== null && this.extendedDescription !== undefined) {
    output.writeFieldBegin('extendedDescription', Thrift.Type.STRING, 1);
    output.writeString(this.extendedDescription);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdjustPreload = function(args) {
  this.tracker = null;
  this.clickId = null;
  if (args) {
    if (args.tracker !== undefined && args.tracker !== null) {
      this.tracker = args.tracker;
    }
    if (args.clickId !== undefined && args.clickId !== null) {
      this.clickId = args.clickId;
    }
  }
};
AdjustPreload.prototype = {};
AdjustPreload.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.tracker = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.clickId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdjustPreload.prototype.write = function(output) {
  output.writeStructBegin('AdjustPreload');
  if (this.tracker !== null && this.tracker !== undefined) {
    output.writeFieldBegin('tracker', Thrift.Type.STRING, 1);
    output.writeString(this.tracker);
    output.writeFieldEnd();
  }
  if (this.clickId !== null && this.clickId !== undefined) {
    output.writeFieldBegin('clickId', Thrift.Type.STRING, 2);
    output.writeString(this.clickId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppsFlyerPreload = function(args) {
  this.contentProviderUrl = null;
  this.enabled = null;
  if (args) {
    if (args.contentProviderUrl !== undefined && args.contentProviderUrl !== null) {
      this.contentProviderUrl = args.contentProviderUrl;
    }
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    }
  }
};
AppsFlyerPreload.prototype = {};
AppsFlyerPreload.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.contentProviderUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppsFlyerPreload.prototype.write = function(output) {
  output.writeStructBegin('AppsFlyerPreload');
  if (this.contentProviderUrl !== null && this.contentProviderUrl !== undefined) {
    output.writeFieldBegin('contentProviderUrl', Thrift.Type.STRING, 1);
    output.writeString(this.contentProviderUrl);
    output.writeFieldEnd();
  }
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 2);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PreloadAttirbution = function(args) {
  this.adjust = null;
  this.appsflyr = null;
  this.beaconTimestampSupported = null;
  this.videoBeaconTimestampSupported = null;
  this.playstoreSupported = null;
  if (args) {
    if (args.adjust !== undefined && args.adjust !== null) {
      this.adjust = new AdjustPreload(args.adjust);
    }
    if (args.appsflyr !== undefined && args.appsflyr !== null) {
      this.appsflyr = new AppsFlyerPreload(args.appsflyr);
    }
    if (args.beaconTimestampSupported !== undefined && args.beaconTimestampSupported !== null) {
      this.beaconTimestampSupported = args.beaconTimestampSupported;
    }
    if (args.videoBeaconTimestampSupported !== undefined && args.videoBeaconTimestampSupported !== null) {
      this.videoBeaconTimestampSupported = args.videoBeaconTimestampSupported;
    }
    if (args.playstoreSupported !== undefined && args.playstoreSupported !== null) {
      this.playstoreSupported = args.playstoreSupported;
    }
  }
};
PreloadAttirbution.prototype = {};
PreloadAttirbution.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.adjust = new AdjustPreload();
        this.adjust.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.appsflyr = new AppsFlyerPreload();
        this.appsflyr.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.beaconTimestampSupported = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.videoBeaconTimestampSupported = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.playstoreSupported = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PreloadAttirbution.prototype.write = function(output) {
  output.writeStructBegin('PreloadAttirbution');
  if (this.adjust !== null && this.adjust !== undefined) {
    output.writeFieldBegin('adjust', Thrift.Type.STRUCT, 1);
    this.adjust.write(output);
    output.writeFieldEnd();
  }
  if (this.appsflyr !== null && this.appsflyr !== undefined) {
    output.writeFieldBegin('appsflyr', Thrift.Type.STRUCT, 2);
    this.appsflyr.write(output);
    output.writeFieldEnd();
  }
  if (this.beaconTimestampSupported !== null && this.beaconTimestampSupported !== undefined) {
    output.writeFieldBegin('beaconTimestampSupported', Thrift.Type.BOOL, 3);
    output.writeBool(this.beaconTimestampSupported);
    output.writeFieldEnd();
  }
  if (this.videoBeaconTimestampSupported !== null && this.videoBeaconTimestampSupported !== undefined) {
    output.writeFieldBegin('videoBeaconTimestampSupported', Thrift.Type.BOOL, 4);
    output.writeBool(this.videoBeaconTimestampSupported);
    output.writeFieldEnd();
  }
  if (this.playstoreSupported !== null && this.playstoreSupported !== undefined) {
    output.writeFieldBegin('playstoreSupported', Thrift.Type.BOOL, 5);
    output.writeBool(this.playstoreSupported);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Tag = function(args) {
  this.iconUrl = null;
  this.text = null;
  this.bgColor = null;
  if (args) {
    if (args.iconUrl !== undefined && args.iconUrl !== null) {
      this.iconUrl = args.iconUrl;
    }
    if (args.text !== undefined && args.text !== null) {
      this.text = args.text;
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = args.bgColor;
    }
  }
};
Tag.prototype = {};
Tag.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.iconUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.bgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Tag.prototype.write = function(output) {
  output.writeStructBegin('Tag');
  if (this.iconUrl !== null && this.iconUrl !== undefined) {
    output.writeFieldBegin('iconUrl', Thrift.Type.STRING, 1);
    output.writeString(this.iconUrl);
    output.writeFieldEnd();
  }
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRING, 2);
    output.writeString(this.text);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.STRING, 3);
    output.writeString(this.bgColor);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CategoryInfo = function(args) {
  this.category = null;
  this.rank = null;
  if (args) {
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.rank !== undefined && args.rank !== null) {
      this.rank = args.rank;
    }
  }
};
CategoryInfo.prototype = {};
CategoryInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.rank = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CategoryInfo.prototype.write = function(output) {
  output.writeStructBegin('CategoryInfo');
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 1);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.rank !== null && this.rank !== undefined) {
    output.writeFieldBegin('rank', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.rank);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

InstallCtaMeta = function(args) {
  this.text = null;
  this.textSize = null;
  this.bgColor = null;
  this.textColor = null;
  this.visibleOnLs = null;
  this.visibleOnUls = null;
  if (args) {
    if (args.text !== undefined && args.text !== null) {
      this.text = args.text;
    }
    if (args.textSize !== undefined && args.textSize !== null) {
      this.textSize = args.textSize;
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = args.bgColor;
    }
    if (args.textColor !== undefined && args.textColor !== null) {
      this.textColor = args.textColor;
    }
    if (args.visibleOnLs !== undefined && args.visibleOnLs !== null) {
      this.visibleOnLs = args.visibleOnLs;
    }
    if (args.visibleOnUls !== undefined && args.visibleOnUls !== null) {
      this.visibleOnUls = args.visibleOnUls;
    }
  }
};
InstallCtaMeta.prototype = {};
InstallCtaMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.textSize = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.bgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.textColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.visibleOnLs = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.visibleOnUls = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

InstallCtaMeta.prototype.write = function(output) {
  output.writeStructBegin('InstallCtaMeta');
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRING, 1);
    output.writeString(this.text);
    output.writeFieldEnd();
  }
  if (this.textSize !== null && this.textSize !== undefined) {
    output.writeFieldBegin('textSize', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.textSize);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.STRING, 3);
    output.writeString(this.bgColor);
    output.writeFieldEnd();
  }
  if (this.textColor !== null && this.textColor !== undefined) {
    output.writeFieldBegin('textColor', Thrift.Type.STRING, 4);
    output.writeString(this.textColor);
    output.writeFieldEnd();
  }
  if (this.visibleOnLs !== null && this.visibleOnLs !== undefined) {
    output.writeFieldBegin('visibleOnLs', Thrift.Type.BOOL, 5);
    output.writeBool(this.visibleOnLs);
    output.writeFieldEnd();
  }
  if (this.visibleOnUls !== null && this.visibleOnUls !== undefined) {
    output.writeFieldBegin('visibleOnUls', Thrift.Type.BOOL, 6);
    output.writeBool(this.visibleOnUls);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppInstallMeta = function(args) {
  this.packageName = null;
  this.appBundleUrl = null;
  this.appName = null;
  this.logoUrl = null;
  this.category = null;
  this.size = null;
  this.rating = null;
  this.downloadCount = null;
  this.title = null;
  this.description = null;
  this.screenshotUrls = null;
  this.notifTemplateId = null;
  this.brandColor = null;
  this.brandBgImage = null;
  this.d1NotifTemplateId = null;
  this.ltvNotifTemplateId = null;
  this.appId = null;
  this.secondaryBrandColor = null;
  this.appOptionalDetails = null;
  this.preloadEnable = null;
  this.contentProviderUrl = null;
  this.prldAttr = null;
  this.templateId = null;
  this.tag = null;
  this.reviewCount = null;
  this.sizeInMb = null;
  this.categoryInfo = null;
  this.textColor = null;
  this.bgColor = null;
  this.carouselImages = null;
  this.installNowCta = null;
  this.installLaterCta = null;
  this.fbu = null;
  this.sourceName = null;
  this.dspAdvertiserGuid = null;
  if (args) {
    if (args.packageName !== undefined && args.packageName !== null) {
      this.packageName = args.packageName;
    }
    if (args.appBundleUrl !== undefined && args.appBundleUrl !== null) {
      this.appBundleUrl = args.appBundleUrl;
    }
    if (args.appName !== undefined && args.appName !== null) {
      this.appName = args.appName;
    }
    if (args.logoUrl !== undefined && args.logoUrl !== null) {
      this.logoUrl = args.logoUrl;
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.size !== undefined && args.size !== null) {
      this.size = args.size;
    }
    if (args.rating !== undefined && args.rating !== null) {
      this.rating = args.rating;
    }
    if (args.downloadCount !== undefined && args.downloadCount !== null) {
      this.downloadCount = args.downloadCount;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.screenshotUrls !== undefined && args.screenshotUrls !== null) {
      this.screenshotUrls = Thrift.copyList(args.screenshotUrls, [null]);
    }
    if (args.notifTemplateId !== undefined && args.notifTemplateId !== null) {
      this.notifTemplateId = args.notifTemplateId;
    }
    if (args.brandColor !== undefined && args.brandColor !== null) {
      this.brandColor = args.brandColor;
    }
    if (args.brandBgImage !== undefined && args.brandBgImage !== null) {
      this.brandBgImage = args.brandBgImage;
    }
    if (args.d1NotifTemplateId !== undefined && args.d1NotifTemplateId !== null) {
      this.d1NotifTemplateId = args.d1NotifTemplateId;
    }
    if (args.ltvNotifTemplateId !== undefined && args.ltvNotifTemplateId !== null) {
      this.ltvNotifTemplateId = args.ltvNotifTemplateId;
    }
    if (args.appId !== undefined && args.appId !== null) {
      this.appId = args.appId;
    }
    if (args.secondaryBrandColor !== undefined && args.secondaryBrandColor !== null) {
      this.secondaryBrandColor = args.secondaryBrandColor;
    }
    if (args.appOptionalDetails !== undefined && args.appOptionalDetails !== null) {
      this.appOptionalDetails = new AppOptionalDetails(args.appOptionalDetails);
    }
    if (args.preloadEnable !== undefined && args.preloadEnable !== null) {
      this.preloadEnable = args.preloadEnable;
    }
    if (args.contentProviderUrl !== undefined && args.contentProviderUrl !== null) {
      this.contentProviderUrl = args.contentProviderUrl;
    }
    if (args.prldAttr !== undefined && args.prldAttr !== null) {
      this.prldAttr = new PreloadAttirbution(args.prldAttr);
    }
    if (args.templateId !== undefined && args.templateId !== null) {
      this.templateId = args.templateId;
    }
    if (args.tag !== undefined && args.tag !== null) {
      this.tag = new Tag(args.tag);
    }
    if (args.reviewCount !== undefined && args.reviewCount !== null) {
      this.reviewCount = args.reviewCount;
    }
    if (args.sizeInMb !== undefined && args.sizeInMb !== null) {
      this.sizeInMb = args.sizeInMb;
    }
    if (args.categoryInfo !== undefined && args.categoryInfo !== null) {
      this.categoryInfo = new CategoryInfo(args.categoryInfo);
    }
    if (args.textColor !== undefined && args.textColor !== null) {
      this.textColor = args.textColor;
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = Thrift.copyList(args.bgColor, [null]);
    }
    if (args.carouselImages !== undefined && args.carouselImages !== null) {
      this.carouselImages = Thrift.copyList(args.carouselImages, [null]);
    }
    if (args.installNowCta !== undefined && args.installNowCta !== null) {
      this.installNowCta = new InstallCtaMeta(args.installNowCta);
    }
    if (args.installLaterCta !== undefined && args.installLaterCta !== null) {
      this.installLaterCta = new InstallCtaMeta(args.installLaterCta);
    }
    if (args.fbu !== undefined && args.fbu !== null) {
      this.fbu = args.fbu;
    }
    if (args.sourceName !== undefined && args.sourceName !== null) {
      this.sourceName = Thrift.copyList(args.sourceName, [null]);
    }
    if (args.dspAdvertiserGuid !== undefined && args.dspAdvertiserGuid !== null) {
      this.dspAdvertiserGuid = args.dspAdvertiserGuid;
    }
  }
};
AppInstallMeta.prototype = {};
AppInstallMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.packageName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.appBundleUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.appName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.logoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.size = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.DOUBLE) {
        this.rating = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.downloadCount = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size40 = 0;
        var _rtmp344;
        this.screenshotUrls = [];
        var _etype43 = 0;
        _rtmp344 = input.readListBegin();
        _etype43 = _rtmp344.etype;
        _size40 = _rtmp344.size;
        for (var _i45 = 0; _i45 < _size40; ++_i45)
        {
          var elem46 = null;
          elem46 = input.readString().value;
          this.screenshotUrls.push(elem46);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.notifTemplateId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.brandColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.brandBgImage = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.d1NotifTemplateId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.ltvNotifTemplateId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.appId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.secondaryBrandColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRUCT) {
        this.appOptionalDetails = new AppOptionalDetails();
        this.appOptionalDetails.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.BOOL) {
        this.preloadEnable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRING) {
        this.contentProviderUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRUCT) {
        this.prldAttr = new PreloadAttirbution();
        this.prldAttr.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.templateId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.STRUCT) {
        this.tag = new Tag();
        this.tag.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.STRING) {
        this.reviewCount = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.STRING) {
        this.sizeInMb = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.STRUCT) {
        this.categoryInfo = new CategoryInfo();
        this.categoryInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.STRING) {
        this.textColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.LIST) {
        var _size47 = 0;
        var _rtmp351;
        this.bgColor = [];
        var _etype50 = 0;
        _rtmp351 = input.readListBegin();
        _etype50 = _rtmp351.etype;
        _size47 = _rtmp351.size;
        for (var _i52 = 0; _i52 < _size47; ++_i52)
        {
          var elem53 = null;
          elem53 = input.readString().value;
          this.bgColor.push(elem53);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.LIST) {
        var _size54 = 0;
        var _rtmp358;
        this.carouselImages = [];
        var _etype57 = 0;
        _rtmp358 = input.readListBegin();
        _etype57 = _rtmp358.etype;
        _size54 = _rtmp358.size;
        for (var _i59 = 0; _i59 < _size54; ++_i59)
        {
          var elem60 = null;
          elem60 = input.readString().value;
          this.carouselImages.push(elem60);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.STRUCT) {
        this.installNowCta = new InstallCtaMeta();
        this.installNowCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRUCT) {
        this.installLaterCta = new InstallCtaMeta();
        this.installLaterCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.STRING) {
        this.fbu = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.LIST) {
        var _size61 = 0;
        var _rtmp365;
        this.sourceName = [];
        var _etype64 = 0;
        _rtmp365 = input.readListBegin();
        _etype64 = _rtmp365.etype;
        _size61 = _rtmp365.size;
        for (var _i66 = 0; _i66 < _size61; ++_i66)
        {
          var elem67 = null;
          elem67 = input.readString().value;
          this.sourceName.push(elem67);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.STRING) {
        this.dspAdvertiserGuid = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppInstallMeta.prototype.write = function(output) {
  output.writeStructBegin('AppInstallMeta');
  if (this.packageName !== null && this.packageName !== undefined) {
    output.writeFieldBegin('packageName', Thrift.Type.STRING, 1);
    output.writeString(this.packageName);
    output.writeFieldEnd();
  }
  if (this.appBundleUrl !== null && this.appBundleUrl !== undefined) {
    output.writeFieldBegin('appBundleUrl', Thrift.Type.STRING, 2);
    output.writeString(this.appBundleUrl);
    output.writeFieldEnd();
  }
  if (this.appName !== null && this.appName !== undefined) {
    output.writeFieldBegin('appName', Thrift.Type.STRING, 3);
    output.writeString(this.appName);
    output.writeFieldEnd();
  }
  if (this.logoUrl !== null && this.logoUrl !== undefined) {
    output.writeFieldBegin('logoUrl', Thrift.Type.STRING, 4);
    output.writeString(this.logoUrl);
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 5);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.size !== null && this.size !== undefined) {
    output.writeFieldBegin('size', Thrift.Type.STRING, 6);
    output.writeString(this.size);
    output.writeFieldEnd();
  }
  if (this.rating !== null && this.rating !== undefined) {
    output.writeFieldBegin('rating', Thrift.Type.DOUBLE, 7);
    output.writeDouble(this.rating);
    output.writeFieldEnd();
  }
  if (this.downloadCount !== null && this.downloadCount !== undefined) {
    output.writeFieldBegin('downloadCount', Thrift.Type.STRING, 8);
    output.writeString(this.downloadCount);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 9);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 10);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.screenshotUrls !== null && this.screenshotUrls !== undefined) {
    output.writeFieldBegin('screenshotUrls', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRING, this.screenshotUrls.length);
    for (var iter68 in this.screenshotUrls)
    {
      if (this.screenshotUrls.hasOwnProperty(iter68))
      {
        iter68 = this.screenshotUrls[iter68];
        output.writeString(iter68);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.notifTemplateId !== null && this.notifTemplateId !== undefined) {
    output.writeFieldBegin('notifTemplateId', Thrift.Type.STRING, 12);
    output.writeString(this.notifTemplateId);
    output.writeFieldEnd();
  }
  if (this.brandColor !== null && this.brandColor !== undefined) {
    output.writeFieldBegin('brandColor', Thrift.Type.STRING, 13);
    output.writeString(this.brandColor);
    output.writeFieldEnd();
  }
  if (this.brandBgImage !== null && this.brandBgImage !== undefined) {
    output.writeFieldBegin('brandBgImage', Thrift.Type.STRING, 14);
    output.writeString(this.brandBgImage);
    output.writeFieldEnd();
  }
  if (this.d1NotifTemplateId !== null && this.d1NotifTemplateId !== undefined) {
    output.writeFieldBegin('d1NotifTemplateId', Thrift.Type.STRING, 15);
    output.writeString(this.d1NotifTemplateId);
    output.writeFieldEnd();
  }
  if (this.ltvNotifTemplateId !== null && this.ltvNotifTemplateId !== undefined) {
    output.writeFieldBegin('ltvNotifTemplateId', Thrift.Type.STRING, 16);
    output.writeString(this.ltvNotifTemplateId);
    output.writeFieldEnd();
  }
  if (this.appId !== null && this.appId !== undefined) {
    output.writeFieldBegin('appId', Thrift.Type.STRING, 17);
    output.writeString(this.appId);
    output.writeFieldEnd();
  }
  if (this.secondaryBrandColor !== null && this.secondaryBrandColor !== undefined) {
    output.writeFieldBegin('secondaryBrandColor', Thrift.Type.STRING, 18);
    output.writeString(this.secondaryBrandColor);
    output.writeFieldEnd();
  }
  if (this.appOptionalDetails !== null && this.appOptionalDetails !== undefined) {
    output.writeFieldBegin('appOptionalDetails', Thrift.Type.STRUCT, 19);
    this.appOptionalDetails.write(output);
    output.writeFieldEnd();
  }
  if (this.preloadEnable !== null && this.preloadEnable !== undefined) {
    output.writeFieldBegin('preloadEnable', Thrift.Type.BOOL, 20);
    output.writeBool(this.preloadEnable);
    output.writeFieldEnd();
  }
  if (this.contentProviderUrl !== null && this.contentProviderUrl !== undefined) {
    output.writeFieldBegin('contentProviderUrl', Thrift.Type.STRING, 21);
    output.writeString(this.contentProviderUrl);
    output.writeFieldEnd();
  }
  if (this.prldAttr !== null && this.prldAttr !== undefined) {
    output.writeFieldBegin('prldAttr', Thrift.Type.STRUCT, 22);
    this.prldAttr.write(output);
    output.writeFieldEnd();
  }
  if (this.templateId !== null && this.templateId !== undefined) {
    output.writeFieldBegin('templateId', Thrift.Type.STRING, 23);
    output.writeString(this.templateId);
    output.writeFieldEnd();
  }
  if (this.tag !== null && this.tag !== undefined) {
    output.writeFieldBegin('tag', Thrift.Type.STRUCT, 24);
    this.tag.write(output);
    output.writeFieldEnd();
  }
  if (this.reviewCount !== null && this.reviewCount !== undefined) {
    output.writeFieldBegin('reviewCount', Thrift.Type.STRING, 25);
    output.writeString(this.reviewCount);
    output.writeFieldEnd();
  }
  if (this.sizeInMb !== null && this.sizeInMb !== undefined) {
    output.writeFieldBegin('sizeInMb', Thrift.Type.STRING, 26);
    output.writeString(this.sizeInMb);
    output.writeFieldEnd();
  }
  if (this.categoryInfo !== null && this.categoryInfo !== undefined) {
    output.writeFieldBegin('categoryInfo', Thrift.Type.STRUCT, 27);
    this.categoryInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.textColor !== null && this.textColor !== undefined) {
    output.writeFieldBegin('textColor', Thrift.Type.STRING, 28);
    output.writeString(this.textColor);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.LIST, 29);
    output.writeListBegin(Thrift.Type.STRING, this.bgColor.length);
    for (var iter69 in this.bgColor)
    {
      if (this.bgColor.hasOwnProperty(iter69))
      {
        iter69 = this.bgColor[iter69];
        output.writeString(iter69);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.carouselImages !== null && this.carouselImages !== undefined) {
    output.writeFieldBegin('carouselImages', Thrift.Type.LIST, 30);
    output.writeListBegin(Thrift.Type.STRING, this.carouselImages.length);
    for (var iter70 in this.carouselImages)
    {
      if (this.carouselImages.hasOwnProperty(iter70))
      {
        iter70 = this.carouselImages[iter70];
        output.writeString(iter70);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.installNowCta !== null && this.installNowCta !== undefined) {
    output.writeFieldBegin('installNowCta', Thrift.Type.STRUCT, 31);
    this.installNowCta.write(output);
    output.writeFieldEnd();
  }
  if (this.installLaterCta !== null && this.installLaterCta !== undefined) {
    output.writeFieldBegin('installLaterCta', Thrift.Type.STRUCT, 32);
    this.installLaterCta.write(output);
    output.writeFieldEnd();
  }
  if (this.fbu !== null && this.fbu !== undefined) {
    output.writeFieldBegin('fbu', Thrift.Type.STRING, 33);
    output.writeString(this.fbu);
    output.writeFieldEnd();
  }
  if (this.sourceName !== null && this.sourceName !== undefined) {
    output.writeFieldBegin('sourceName', Thrift.Type.LIST, 34);
    output.writeListBegin(Thrift.Type.STRING, this.sourceName.length);
    for (var iter71 in this.sourceName)
    {
      if (this.sourceName.hasOwnProperty(iter71))
      {
        iter71 = this.sourceName[iter71];
        output.writeString(iter71);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.dspAdvertiserGuid !== null && this.dspAdvertiserGuid !== undefined) {
    output.writeFieldBegin('dspAdvertiserGuid', Thrift.Type.STRING, 35);
    output.writeString(this.dspAdvertiserGuid);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CtaDisplayText = function(args) {
  this.text = null;
  if (args) {
    if (args.text !== undefined && args.text !== null) {
      this.text = args.text;
    }
  }
};
CtaDisplayText.prototype = {};
CtaDisplayText.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CtaDisplayText.prototype.write = function(output) {
  output.writeStructBegin('CtaDisplayText');
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRING, 1);
    output.writeString(this.text);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CtaDisplayImage = function(args) {
  this.downloadImageUrl = null;
  this.imageUrl = null;
  this.altText = null;
  this.imageWidth = null;
  this.imageHeight = null;
  if (args) {
    if (args.downloadImageUrl !== undefined && args.downloadImageUrl !== null) {
      this.downloadImageUrl = args.downloadImageUrl;
    }
    if (args.imageUrl !== undefined && args.imageUrl !== null) {
      this.imageUrl = args.imageUrl;
    }
    if (args.altText !== undefined && args.altText !== null) {
      this.altText = args.altText;
    }
    if (args.imageWidth !== undefined && args.imageWidth !== null) {
      this.imageWidth = args.imageWidth;
    }
    if (args.imageHeight !== undefined && args.imageHeight !== null) {
      this.imageHeight = args.imageHeight;
    }
  }
};
CtaDisplayImage.prototype = {};
CtaDisplayImage.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.downloadImageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.imageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.altText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.imageWidth = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.imageHeight = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CtaDisplayImage.prototype.write = function(output) {
  output.writeStructBegin('CtaDisplayImage');
  if (this.downloadImageUrl !== null && this.downloadImageUrl !== undefined) {
    output.writeFieldBegin('downloadImageUrl', Thrift.Type.STRING, 1);
    output.writeString(this.downloadImageUrl);
    output.writeFieldEnd();
  }
  if (this.imageUrl !== null && this.imageUrl !== undefined) {
    output.writeFieldBegin('imageUrl', Thrift.Type.STRING, 2);
    output.writeString(this.imageUrl);
    output.writeFieldEnd();
  }
  if (this.altText !== null && this.altText !== undefined) {
    output.writeFieldBegin('altText', Thrift.Type.STRING, 3);
    output.writeString(this.altText);
    output.writeFieldEnd();
  }
  if (this.imageWidth !== null && this.imageWidth !== undefined) {
    output.writeFieldBegin('imageWidth', Thrift.Type.I32, 4);
    output.writeI32(this.imageWidth);
    output.writeFieldEnd();
  }
  if (this.imageHeight !== null && this.imageHeight !== undefined) {
    output.writeFieldBegin('imageHeight', Thrift.Type.I32, 5);
    output.writeI32(this.imageHeight);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CtaBgClr = function(args) {
  this.backGroundcolour = null;
  if (args) {
    if (args.backGroundcolour !== undefined && args.backGroundcolour !== null) {
      this.backGroundcolour = Thrift.copyList(args.backGroundcolour, [null]);
    }
  }
};
CtaBgClr.prototype = {};
CtaBgClr.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size72 = 0;
        var _rtmp376;
        this.backGroundcolour = [];
        var _etype75 = 0;
        _rtmp376 = input.readListBegin();
        _etype75 = _rtmp376.etype;
        _size72 = _rtmp376.size;
        for (var _i77 = 0; _i77 < _size72; ++_i77)
        {
          var elem78 = null;
          elem78 = input.readString().value;
          this.backGroundcolour.push(elem78);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CtaBgClr.prototype.write = function(output) {
  output.writeStructBegin('CtaBgClr');
  if (this.backGroundcolour !== null && this.backGroundcolour !== undefined) {
    output.writeFieldBegin('backGroundcolour', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.backGroundcolour.length);
    for (var iter79 in this.backGroundcolour)
    {
      if (this.backGroundcolour.hasOwnProperty(iter79))
      {
        iter79 = this.backGroundcolour[iter79];
        output.writeString(iter79);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CtaDisplay = function(args) {
  this.ctaDisplayImage = null;
  this.ctaDisplayType = null;
  this.ctaDisplayText = null;
  this.bgClr = null;
  this.brdrClr = null;
  this.txtClr = null;
  this.txtSize = null;
  this.lftIcnUrl = null;
  this.rgtIcnUrl = null;
  this.bold = null;
  this.italic = null;
  this.underlined = null;
  if (args) {
    if (args.ctaDisplayImage !== undefined && args.ctaDisplayImage !== null) {
      this.ctaDisplayImage = new CtaDisplayImage(args.ctaDisplayImage);
    }
    if (args.ctaDisplayType !== undefined && args.ctaDisplayType !== null) {
      this.ctaDisplayType = args.ctaDisplayType;
    }
    if (args.ctaDisplayText !== undefined && args.ctaDisplayText !== null) {
      this.ctaDisplayText = new CtaDisplayText(args.ctaDisplayText);
    }
    if (args.bgClr !== undefined && args.bgClr !== null) {
      this.bgClr = new CtaBgClr(args.bgClr);
    }
    if (args.brdrClr !== undefined && args.brdrClr !== null) {
      this.brdrClr = args.brdrClr;
    }
    if (args.txtClr !== undefined && args.txtClr !== null) {
      this.txtClr = args.txtClr;
    }
    if (args.txtSize !== undefined && args.txtSize !== null) {
      this.txtSize = args.txtSize;
    }
    if (args.lftIcnUrl !== undefined && args.lftIcnUrl !== null) {
      this.lftIcnUrl = args.lftIcnUrl;
    }
    if (args.rgtIcnUrl !== undefined && args.rgtIcnUrl !== null) {
      this.rgtIcnUrl = args.rgtIcnUrl;
    }
    if (args.bold !== undefined && args.bold !== null) {
      this.bold = args.bold;
    }
    if (args.italic !== undefined && args.italic !== null) {
      this.italic = args.italic;
    }
    if (args.underlined !== undefined && args.underlined !== null) {
      this.underlined = args.underlined;
    }
  }
};
CtaDisplay.prototype = {};
CtaDisplay.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.ctaDisplayImage = new CtaDisplayImage();
        this.ctaDisplayImage.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.ctaDisplayType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.ctaDisplayText = new CtaDisplayText();
        this.ctaDisplayText.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.bgClr = new CtaBgClr();
        this.bgClr.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.brdrClr = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.txtClr = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.txtSize = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.lftIcnUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.rgtIcnUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.BOOL) {
        this.bold = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.BOOL) {
        this.italic = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.BOOL) {
        this.underlined = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CtaDisplay.prototype.write = function(output) {
  output.writeStructBegin('CtaDisplay');
  if (this.ctaDisplayImage !== null && this.ctaDisplayImage !== undefined) {
    output.writeFieldBegin('ctaDisplayImage', Thrift.Type.STRUCT, 1);
    this.ctaDisplayImage.write(output);
    output.writeFieldEnd();
  }
  if (this.ctaDisplayType !== null && this.ctaDisplayType !== undefined) {
    output.writeFieldBegin('ctaDisplayType', Thrift.Type.I32, 2);
    output.writeI32(this.ctaDisplayType);
    output.writeFieldEnd();
  }
  if (this.ctaDisplayText !== null && this.ctaDisplayText !== undefined) {
    output.writeFieldBegin('ctaDisplayText', Thrift.Type.STRUCT, 3);
    this.ctaDisplayText.write(output);
    output.writeFieldEnd();
  }
  if (this.bgClr !== null && this.bgClr !== undefined) {
    output.writeFieldBegin('bgClr', Thrift.Type.STRUCT, 4);
    this.bgClr.write(output);
    output.writeFieldEnd();
  }
  if (this.brdrClr !== null && this.brdrClr !== undefined) {
    output.writeFieldBegin('brdrClr', Thrift.Type.STRING, 5);
    output.writeString(this.brdrClr);
    output.writeFieldEnd();
  }
  if (this.txtClr !== null && this.txtClr !== undefined) {
    output.writeFieldBegin('txtClr', Thrift.Type.STRING, 6);
    output.writeString(this.txtClr);
    output.writeFieldEnd();
  }
  if (this.txtSize !== null && this.txtSize !== undefined) {
    output.writeFieldBegin('txtSize', Thrift.Type.I32, 7);
    output.writeI32(this.txtSize);
    output.writeFieldEnd();
  }
  if (this.lftIcnUrl !== null && this.lftIcnUrl !== undefined) {
    output.writeFieldBegin('lftIcnUrl', Thrift.Type.STRING, 8);
    output.writeString(this.lftIcnUrl);
    output.writeFieldEnd();
  }
  if (this.rgtIcnUrl !== null && this.rgtIcnUrl !== undefined) {
    output.writeFieldBegin('rgtIcnUrl', Thrift.Type.STRING, 9);
    output.writeString(this.rgtIcnUrl);
    output.writeFieldEnd();
  }
  if (this.bold !== null && this.bold !== undefined) {
    output.writeFieldBegin('bold', Thrift.Type.BOOL, 10);
    output.writeBool(this.bold);
    output.writeFieldEnd();
  }
  if (this.italic !== null && this.italic !== undefined) {
    output.writeFieldBegin('italic', Thrift.Type.BOOL, 11);
    output.writeBool(this.italic);
    output.writeFieldEnd();
  }
  if (this.underlined !== null && this.underlined !== undefined) {
    output.writeFieldBegin('underlined', Thrift.Type.BOOL, 12);
    output.writeBool(this.underlined);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Product = function(args) {
  this.id = null;
  this.name = null;
  this.descriptionUrl = null;
  this.coverImageUrl = null;
  this.logoUrl = null;
  this.originalPriceDeprecated = null;
  this.sellPriceDeprecated = null;
  this.currencySymbol = null;
  this.originalPrice = null;
  this.sellPrice = null;
  this.pUrl = null;
  this.parentProductId = null;
  this.outOfStock = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.descriptionUrl !== undefined && args.descriptionUrl !== null) {
      this.descriptionUrl = args.descriptionUrl;
    }
    if (args.coverImageUrl !== undefined && args.coverImageUrl !== null) {
      this.coverImageUrl = args.coverImageUrl;
    }
    if (args.logoUrl !== undefined && args.logoUrl !== null) {
      this.logoUrl = args.logoUrl;
    }
    if (args.originalPriceDeprecated !== undefined && args.originalPriceDeprecated !== null) {
      this.originalPriceDeprecated = args.originalPriceDeprecated;
    }
    if (args.sellPriceDeprecated !== undefined && args.sellPriceDeprecated !== null) {
      this.sellPriceDeprecated = args.sellPriceDeprecated;
    }
    if (args.currencySymbol !== undefined && args.currencySymbol !== null) {
      this.currencySymbol = args.currencySymbol;
    }
    if (args.originalPrice !== undefined && args.originalPrice !== null) {
      this.originalPrice = args.originalPrice;
    }
    if (args.sellPrice !== undefined && args.sellPrice !== null) {
      this.sellPrice = args.sellPrice;
    }
    if (args.pUrl !== undefined && args.pUrl !== null) {
      this.pUrl = args.pUrl;
    }
    if (args.parentProductId !== undefined && args.parentProductId !== null) {
      this.parentProductId = args.parentProductId;
    }
    if (args.outOfStock !== undefined && args.outOfStock !== null) {
      this.outOfStock = args.outOfStock;
    }
  }
};
Product.prototype = {};
Product.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.descriptionUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.coverImageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.logoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.DOUBLE) {
        this.originalPriceDeprecated = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.DOUBLE) {
        this.sellPriceDeprecated = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.currencySymbol = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.originalPrice = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.sellPrice = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.pUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.parentProductId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.BOOL) {
        this.outOfStock = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Product.prototype.write = function(output) {
  output.writeStructBegin('Product');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.descriptionUrl !== null && this.descriptionUrl !== undefined) {
    output.writeFieldBegin('descriptionUrl', Thrift.Type.STRING, 3);
    output.writeString(this.descriptionUrl);
    output.writeFieldEnd();
  }
  if (this.coverImageUrl !== null && this.coverImageUrl !== undefined) {
    output.writeFieldBegin('coverImageUrl', Thrift.Type.STRING, 4);
    output.writeString(this.coverImageUrl);
    output.writeFieldEnd();
  }
  if (this.logoUrl !== null && this.logoUrl !== undefined) {
    output.writeFieldBegin('logoUrl', Thrift.Type.STRING, 5);
    output.writeString(this.logoUrl);
    output.writeFieldEnd();
  }
  if (this.originalPriceDeprecated !== null && this.originalPriceDeprecated !== undefined) {
    output.writeFieldBegin('originalPriceDeprecated', Thrift.Type.DOUBLE, 6);
    output.writeDouble(this.originalPriceDeprecated);
    output.writeFieldEnd();
  }
  if (this.sellPriceDeprecated !== null && this.sellPriceDeprecated !== undefined) {
    output.writeFieldBegin('sellPriceDeprecated', Thrift.Type.DOUBLE, 7);
    output.writeDouble(this.sellPriceDeprecated);
    output.writeFieldEnd();
  }
  if (this.currencySymbol !== null && this.currencySymbol !== undefined) {
    output.writeFieldBegin('currencySymbol', Thrift.Type.STRING, 8);
    output.writeString(this.currencySymbol);
    output.writeFieldEnd();
  }
  if (this.originalPrice !== null && this.originalPrice !== undefined) {
    output.writeFieldBegin('originalPrice', Thrift.Type.STRING, 9);
    output.writeString(this.originalPrice);
    output.writeFieldEnd();
  }
  if (this.sellPrice !== null && this.sellPrice !== undefined) {
    output.writeFieldBegin('sellPrice', Thrift.Type.STRING, 10);
    output.writeString(this.sellPrice);
    output.writeFieldEnd();
  }
  if (this.pUrl !== null && this.pUrl !== undefined) {
    output.writeFieldBegin('pUrl', Thrift.Type.STRING, 11);
    output.writeString(this.pUrl);
    output.writeFieldEnd();
  }
  if (this.parentProductId !== null && this.parentProductId !== undefined) {
    output.writeFieldBegin('parentProductId', Thrift.Type.STRING, 12);
    output.writeString(this.parentProductId);
    output.writeFieldEnd();
  }
  if (this.outOfStock !== null && this.outOfStock !== undefined) {
    output.writeFieldBegin('outOfStock', Thrift.Type.BOOL, 13);
    output.writeBool(this.outOfStock);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ProductTiles = function(args) {
  this.products = null;
  if (args) {
    if (args.products !== undefined && args.products !== null) {
      this.products = Thrift.copyList(args.products, [Product]);
    }
  }
};
ProductTiles.prototype = {};
ProductTiles.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size80 = 0;
        var _rtmp384;
        this.products = [];
        var _etype83 = 0;
        _rtmp384 = input.readListBegin();
        _etype83 = _rtmp384.etype;
        _size80 = _rtmp384.size;
        for (var _i85 = 0; _i85 < _size80; ++_i85)
        {
          var elem86 = null;
          elem86 = new Product();
          elem86.read(input);
          this.products.push(elem86);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ProductTiles.prototype.write = function(output) {
  output.writeStructBegin('ProductTiles');
  if (this.products !== null && this.products !== undefined) {
    output.writeFieldBegin('products', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.products.length);
    for (var iter87 in this.products)
    {
      if (this.products.hasOwnProperty(iter87))
      {
        iter87 = this.products[iter87];
        iter87.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

WidgetTagDetails = function(args) {
  this.bgColor = null;
  this.iconPath = null;
  if (args) {
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = args.bgColor;
    }
    if (args.iconPath !== undefined && args.iconPath !== null) {
      this.iconPath = args.iconPath;
    }
  }
};
WidgetTagDetails.prototype = {};
WidgetTagDetails.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.bgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.iconPath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

WidgetTagDetails.prototype.write = function(output) {
  output.writeStructBegin('WidgetTagDetails');
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.STRING, 1);
    output.writeString(this.bgColor);
    output.writeFieldEnd();
  }
  if (this.iconPath !== null && this.iconPath !== undefined) {
    output.writeFieldBegin('iconPath', Thrift.Type.STRING, 2);
    output.writeString(this.iconPath);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ImageMap = function(args) {
  this.baseImage = null;
  this.l0Medium = null;
  this.l0Large = null;
  this.lPlus = null;
  this.viewMore = null;
  this.l0Small = null;
  this.destinationLarge = null;
  this.destinationSmall = null;
  this.vertical = null;
  this.small1X1 = null;
  this.large16X9 = null;
  this.baseImageBlur = null;
  this.l0LargeBlur = null;
  this.lPlusBlur = null;
  this.viewMoreBlur = null;
  this.l0SmallBlur = null;
  this.destinationLargeBlur = null;
  this.destinationSmallBlur = null;
  this.verticalBlur = null;
  this.small1X1Blur = null;
  this.large16X9Blur = null;
  this.l0MediumBlur = null;
  this.l1Plus = null;
  this.baseDominantColour = null;
  if (args) {
    if (args.baseImage !== undefined && args.baseImage !== null) {
      this.baseImage = args.baseImage;
    }
    if (args.l0Medium !== undefined && args.l0Medium !== null) {
      this.l0Medium = args.l0Medium;
    }
    if (args.l0Large !== undefined && args.l0Large !== null) {
      this.l0Large = args.l0Large;
    }
    if (args.lPlus !== undefined && args.lPlus !== null) {
      this.lPlus = args.lPlus;
    }
    if (args.viewMore !== undefined && args.viewMore !== null) {
      this.viewMore = args.viewMore;
    }
    if (args.l0Small !== undefined && args.l0Small !== null) {
      this.l0Small = args.l0Small;
    }
    if (args.destinationLarge !== undefined && args.destinationLarge !== null) {
      this.destinationLarge = args.destinationLarge;
    }
    if (args.destinationSmall !== undefined && args.destinationSmall !== null) {
      this.destinationSmall = args.destinationSmall;
    }
    if (args.vertical !== undefined && args.vertical !== null) {
      this.vertical = args.vertical;
    }
    if (args.small1X1 !== undefined && args.small1X1 !== null) {
      this.small1X1 = args.small1X1;
    }
    if (args.large16X9 !== undefined && args.large16X9 !== null) {
      this.large16X9 = args.large16X9;
    }
    if (args.baseImageBlur !== undefined && args.baseImageBlur !== null) {
      this.baseImageBlur = args.baseImageBlur;
    }
    if (args.l0LargeBlur !== undefined && args.l0LargeBlur !== null) {
      this.l0LargeBlur = args.l0LargeBlur;
    }
    if (args.lPlusBlur !== undefined && args.lPlusBlur !== null) {
      this.lPlusBlur = args.lPlusBlur;
    }
    if (args.viewMoreBlur !== undefined && args.viewMoreBlur !== null) {
      this.viewMoreBlur = args.viewMoreBlur;
    }
    if (args.l0SmallBlur !== undefined && args.l0SmallBlur !== null) {
      this.l0SmallBlur = args.l0SmallBlur;
    }
    if (args.destinationLargeBlur !== undefined && args.destinationLargeBlur !== null) {
      this.destinationLargeBlur = args.destinationLargeBlur;
    }
    if (args.destinationSmallBlur !== undefined && args.destinationSmallBlur !== null) {
      this.destinationSmallBlur = args.destinationSmallBlur;
    }
    if (args.verticalBlur !== undefined && args.verticalBlur !== null) {
      this.verticalBlur = args.verticalBlur;
    }
    if (args.small1X1Blur !== undefined && args.small1X1Blur !== null) {
      this.small1X1Blur = args.small1X1Blur;
    }
    if (args.large16X9Blur !== undefined && args.large16X9Blur !== null) {
      this.large16X9Blur = args.large16X9Blur;
    }
    if (args.l0MediumBlur !== undefined && args.l0MediumBlur !== null) {
      this.l0MediumBlur = args.l0MediumBlur;
    }
    if (args.l1Plus !== undefined && args.l1Plus !== null) {
      this.l1Plus = args.l1Plus;
    }
    if (args.baseDominantColour !== undefined && args.baseDominantColour !== null) {
      this.baseDominantColour = args.baseDominantColour;
    }
  }
};
ImageMap.prototype = {};
ImageMap.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.baseImage = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.l0Medium = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.l0Large = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.lPlus = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.viewMore = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.l0Small = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.destinationLarge = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.destinationSmall = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.vertical = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.small1X1 = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.large16X9 = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.baseImageBlur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.l0LargeBlur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.lPlusBlur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.viewMoreBlur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.l0SmallBlur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.destinationLargeBlur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.destinationSmallBlur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.verticalBlur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.small1X1Blur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRING) {
        this.large16X9Blur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRING) {
        this.l0MediumBlur = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.l1Plus = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.STRING) {
        this.baseDominantColour = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ImageMap.prototype.write = function(output) {
  output.writeStructBegin('ImageMap');
  if (this.baseImage !== null && this.baseImage !== undefined) {
    output.writeFieldBegin('baseImage', Thrift.Type.STRING, 1);
    output.writeString(this.baseImage);
    output.writeFieldEnd();
  }
  if (this.l0Medium !== null && this.l0Medium !== undefined) {
    output.writeFieldBegin('l0Medium', Thrift.Type.STRING, 2);
    output.writeString(this.l0Medium);
    output.writeFieldEnd();
  }
  if (this.l0Large !== null && this.l0Large !== undefined) {
    output.writeFieldBegin('l0Large', Thrift.Type.STRING, 3);
    output.writeString(this.l0Large);
    output.writeFieldEnd();
  }
  if (this.lPlus !== null && this.lPlus !== undefined) {
    output.writeFieldBegin('lPlus', Thrift.Type.STRING, 4);
    output.writeString(this.lPlus);
    output.writeFieldEnd();
  }
  if (this.viewMore !== null && this.viewMore !== undefined) {
    output.writeFieldBegin('viewMore', Thrift.Type.STRING, 5);
    output.writeString(this.viewMore);
    output.writeFieldEnd();
  }
  if (this.l0Small !== null && this.l0Small !== undefined) {
    output.writeFieldBegin('l0Small', Thrift.Type.STRING, 6);
    output.writeString(this.l0Small);
    output.writeFieldEnd();
  }
  if (this.destinationLarge !== null && this.destinationLarge !== undefined) {
    output.writeFieldBegin('destinationLarge', Thrift.Type.STRING, 7);
    output.writeString(this.destinationLarge);
    output.writeFieldEnd();
  }
  if (this.destinationSmall !== null && this.destinationSmall !== undefined) {
    output.writeFieldBegin('destinationSmall', Thrift.Type.STRING, 8);
    output.writeString(this.destinationSmall);
    output.writeFieldEnd();
  }
  if (this.vertical !== null && this.vertical !== undefined) {
    output.writeFieldBegin('vertical', Thrift.Type.STRING, 9);
    output.writeString(this.vertical);
    output.writeFieldEnd();
  }
  if (this.small1X1 !== null && this.small1X1 !== undefined) {
    output.writeFieldBegin('small1X1', Thrift.Type.STRING, 10);
    output.writeString(this.small1X1);
    output.writeFieldEnd();
  }
  if (this.large16X9 !== null && this.large16X9 !== undefined) {
    output.writeFieldBegin('large16X9', Thrift.Type.STRING, 11);
    output.writeString(this.large16X9);
    output.writeFieldEnd();
  }
  if (this.baseImageBlur !== null && this.baseImageBlur !== undefined) {
    output.writeFieldBegin('baseImageBlur', Thrift.Type.STRING, 12);
    output.writeString(this.baseImageBlur);
    output.writeFieldEnd();
  }
  if (this.l0LargeBlur !== null && this.l0LargeBlur !== undefined) {
    output.writeFieldBegin('l0LargeBlur', Thrift.Type.STRING, 13);
    output.writeString(this.l0LargeBlur);
    output.writeFieldEnd();
  }
  if (this.lPlusBlur !== null && this.lPlusBlur !== undefined) {
    output.writeFieldBegin('lPlusBlur', Thrift.Type.STRING, 14);
    output.writeString(this.lPlusBlur);
    output.writeFieldEnd();
  }
  if (this.viewMoreBlur !== null && this.viewMoreBlur !== undefined) {
    output.writeFieldBegin('viewMoreBlur', Thrift.Type.STRING, 15);
    output.writeString(this.viewMoreBlur);
    output.writeFieldEnd();
  }
  if (this.l0SmallBlur !== null && this.l0SmallBlur !== undefined) {
    output.writeFieldBegin('l0SmallBlur', Thrift.Type.STRING, 16);
    output.writeString(this.l0SmallBlur);
    output.writeFieldEnd();
  }
  if (this.destinationLargeBlur !== null && this.destinationLargeBlur !== undefined) {
    output.writeFieldBegin('destinationLargeBlur', Thrift.Type.STRING, 17);
    output.writeString(this.destinationLargeBlur);
    output.writeFieldEnd();
  }
  if (this.destinationSmallBlur !== null && this.destinationSmallBlur !== undefined) {
    output.writeFieldBegin('destinationSmallBlur', Thrift.Type.STRING, 18);
    output.writeString(this.destinationSmallBlur);
    output.writeFieldEnd();
  }
  if (this.verticalBlur !== null && this.verticalBlur !== undefined) {
    output.writeFieldBegin('verticalBlur', Thrift.Type.STRING, 19);
    output.writeString(this.verticalBlur);
    output.writeFieldEnd();
  }
  if (this.small1X1Blur !== null && this.small1X1Blur !== undefined) {
    output.writeFieldBegin('small1X1Blur', Thrift.Type.STRING, 20);
    output.writeString(this.small1X1Blur);
    output.writeFieldEnd();
  }
  if (this.large16X9Blur !== null && this.large16X9Blur !== undefined) {
    output.writeFieldBegin('large16X9Blur', Thrift.Type.STRING, 21);
    output.writeString(this.large16X9Blur);
    output.writeFieldEnd();
  }
  if (this.l0MediumBlur !== null && this.l0MediumBlur !== undefined) {
    output.writeFieldBegin('l0MediumBlur', Thrift.Type.STRING, 22);
    output.writeString(this.l0MediumBlur);
    output.writeFieldEnd();
  }
  if (this.l1Plus !== null && this.l1Plus !== undefined) {
    output.writeFieldBegin('l1Plus', Thrift.Type.STRING, 23);
    output.writeString(this.l1Plus);
    output.writeFieldEnd();
  }
  if (this.baseDominantColour !== null && this.baseDominantColour !== undefined) {
    output.writeFieldBegin('baseDominantColour', Thrift.Type.STRING, 24);
    output.writeString(this.baseDominantColour);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SpaceServingCategory = function(args) {
  this.id = null;
  this.defaultDisplayName = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.defaultDisplayName !== undefined && args.defaultDisplayName !== null) {
      this.defaultDisplayName = args.defaultDisplayName;
    }
  }
};
SpaceServingCategory.prototype = {};
SpaceServingCategory.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.defaultDisplayName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SpaceServingCategory.prototype.write = function(output) {
  output.writeStructBegin('SpaceServingCategory');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.defaultDisplayName !== null && this.defaultDisplayName !== undefined) {
    output.writeFieldBegin('defaultDisplayName', Thrift.Type.STRING, 2);
    output.writeString(this.defaultDisplayName);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

City = function(args) {
  this.id = null;
  this.name = null;
  this.countryId = null;
  this.stateId = null;
  this.lattitude = null;
  this.longitude = null;
  this.latitude = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.countryId !== undefined && args.countryId !== null) {
      this.countryId = args.countryId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field countryId is unset!');
    }
    if (args.stateId !== undefined && args.stateId !== null) {
      this.stateId = args.stateId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field stateId is unset!');
    }
    if (args.lattitude !== undefined && args.lattitude !== null) {
      this.lattitude = args.lattitude;
    }
    if (args.longitude !== undefined && args.longitude !== null) {
      this.longitude = args.longitude;
    }
    if (args.latitude !== undefined && args.latitude !== null) {
      this.latitude = args.latitude;
    }
  }
};
City.prototype = {};
City.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.id = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.countryId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.stateId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.DOUBLE) {
        this.lattitude = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.DOUBLE) {
        this.longitude = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.DOUBLE) {
        this.latitude = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

City.prototype.write = function(output) {
  output.writeStructBegin('City');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I64, 1);
    output.writeI64(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.countryId !== null && this.countryId !== undefined) {
    output.writeFieldBegin('countryId', Thrift.Type.I64, 3);
    output.writeI64(this.countryId);
    output.writeFieldEnd();
  }
  if (this.stateId !== null && this.stateId !== undefined) {
    output.writeFieldBegin('stateId', Thrift.Type.I64, 4);
    output.writeI64(this.stateId);
    output.writeFieldEnd();
  }
  if (this.lattitude !== null && this.lattitude !== undefined) {
    output.writeFieldBegin('lattitude', Thrift.Type.DOUBLE, 5);
    output.writeDouble(this.lattitude);
    output.writeFieldEnd();
  }
  if (this.longitude !== null && this.longitude !== undefined) {
    output.writeFieldBegin('longitude', Thrift.Type.DOUBLE, 6);
    output.writeDouble(this.longitude);
    output.writeFieldEnd();
  }
  if (this.latitude !== null && this.latitude !== undefined) {
    output.writeFieldBegin('latitude', Thrift.Type.DOUBLE, 7);
    output.writeDouble(this.latitude);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

State = function(args) {
  this.id = null;
  this.name = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
  }
};
State.prototype = {};
State.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.id = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

State.prototype.write = function(output) {
  output.writeStructBegin('State');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I64, 1);
    output.writeI64(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Country = function(args) {
  this.id = null;
  this.name = null;
  this.region = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
  }
};
Country.prototype = {};
Country.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.id = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.region = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Country.prototype.write = function(output) {
  output.writeStructBegin('Country');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I64, 1);
    output.writeI64(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.STRING, 3);
    output.writeString(this.region);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CityInfo = function(args) {
  this.cityId = null;
  this.cityName = null;
  this.stateId = null;
  this.stateName = null;
  this.countryId = null;
  this.region = null;
  this.country = null;
  this.state = null;
  this.city = null;
  if (args) {
    if (args.cityId !== undefined && args.cityId !== null) {
      this.cityId = args.cityId;
    }
    if (args.cityName !== undefined && args.cityName !== null) {
      this.cityName = args.cityName;
    }
    if (args.stateId !== undefined && args.stateId !== null) {
      this.stateId = args.stateId;
    }
    if (args.stateName !== undefined && args.stateName !== null) {
      this.stateName = args.stateName;
    }
    if (args.countryId !== undefined && args.countryId !== null) {
      this.countryId = args.countryId;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.country !== undefined && args.country !== null) {
      this.country = new Country(args.country);
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = new State(args.state);
    }
    if (args.city !== undefined && args.city !== null) {
      this.city = new City(args.city);
    }
  }
};
CityInfo.prototype = {};
CityInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.cityId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.cityName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.stateId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.stateName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.countryId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.region = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRUCT) {
        this.country = new Country();
        this.country.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRUCT) {
        this.state = new State();
        this.state.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRUCT) {
        this.city = new City();
        this.city.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CityInfo.prototype.write = function(output) {
  output.writeStructBegin('CityInfo');
  if (this.cityId !== null && this.cityId !== undefined) {
    output.writeFieldBegin('cityId', Thrift.Type.I64, 1);
    output.writeI64(this.cityId);
    output.writeFieldEnd();
  }
  if (this.cityName !== null && this.cityName !== undefined) {
    output.writeFieldBegin('cityName', Thrift.Type.STRING, 2);
    output.writeString(this.cityName);
    output.writeFieldEnd();
  }
  if (this.stateId !== null && this.stateId !== undefined) {
    output.writeFieldBegin('stateId', Thrift.Type.I64, 3);
    output.writeI64(this.stateId);
    output.writeFieldEnd();
  }
  if (this.stateName !== null && this.stateName !== undefined) {
    output.writeFieldBegin('stateName', Thrift.Type.STRING, 4);
    output.writeString(this.stateName);
    output.writeFieldEnd();
  }
  if (this.countryId !== null && this.countryId !== undefined) {
    output.writeFieldBegin('countryId', Thrift.Type.I64, 5);
    output.writeI64(this.countryId);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.STRING, 6);
    output.writeString(this.region);
    output.writeFieldEnd();
  }
  if (this.country !== null && this.country !== undefined) {
    output.writeFieldBegin('country', Thrift.Type.STRUCT, 7);
    this.country.write(output);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.STRUCT, 8);
    this.state.write(output);
    output.writeFieldEnd();
  }
  if (this.city !== null && this.city !== undefined) {
    output.writeFieldBegin('city', Thrift.Type.STRUCT, 9);
    this.city.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

StateInfo = function(args) {
  this.country = null;
  this.state = null;
  if (args) {
    if (args.country !== undefined && args.country !== null) {
      this.country = new Country(args.country);
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = new State(args.state);
    }
  }
};
StateInfo.prototype = {};
StateInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.country = new Country();
        this.country.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.state = new State();
        this.state.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

StateInfo.prototype.write = function(output) {
  output.writeStructBegin('StateInfo');
  if (this.country !== null && this.country !== undefined) {
    output.writeFieldBegin('country', Thrift.Type.STRUCT, 1);
    this.country.write(output);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.STRUCT, 2);
    this.state.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Location = function(args) {
  this.id = null;
  this.cities = null;
  this.states = null;
  this.cityInfo = null;
  this.stateInfo = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.cities !== undefined && args.cities !== null) {
      this.cities = Thrift.copyList(args.cities, [null]);
    }
    if (args.states !== undefined && args.states !== null) {
      this.states = Thrift.copyList(args.states, [null]);
    }
    if (args.cityInfo !== undefined && args.cityInfo !== null) {
      this.cityInfo = Thrift.copyList(args.cityInfo, [CityInfo]);
    }
    if (args.stateInfo !== undefined && args.stateInfo !== null) {
      this.stateInfo = Thrift.copyList(args.stateInfo, [StateInfo]);
    }
  }
};
Location.prototype = {};
Location.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size88 = 0;
        var _rtmp392;
        this.cities = [];
        var _etype91 = 0;
        _rtmp392 = input.readListBegin();
        _etype91 = _rtmp392.etype;
        _size88 = _rtmp392.size;
        for (var _i93 = 0; _i93 < _size88; ++_i93)
        {
          var elem94 = null;
          elem94 = input.readI32().value;
          this.cities.push(elem94);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size95 = 0;
        var _rtmp399;
        this.states = [];
        var _etype98 = 0;
        _rtmp399 = input.readListBegin();
        _etype98 = _rtmp399.etype;
        _size95 = _rtmp399.size;
        for (var _i100 = 0; _i100 < _size95; ++_i100)
        {
          var elem101 = null;
          elem101 = input.readI32().value;
          this.states.push(elem101);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size102 = 0;
        var _rtmp3106;
        this.cityInfo = [];
        var _etype105 = 0;
        _rtmp3106 = input.readListBegin();
        _etype105 = _rtmp3106.etype;
        _size102 = _rtmp3106.size;
        for (var _i107 = 0; _i107 < _size102; ++_i107)
        {
          var elem108 = null;
          elem108 = new CityInfo();
          elem108.read(input);
          this.cityInfo.push(elem108);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size109 = 0;
        var _rtmp3113;
        this.stateInfo = [];
        var _etype112 = 0;
        _rtmp3113 = input.readListBegin();
        _etype112 = _rtmp3113.etype;
        _size109 = _rtmp3113.size;
        for (var _i114 = 0; _i114 < _size109; ++_i114)
        {
          var elem115 = null;
          elem115 = new StateInfo();
          elem115.read(input);
          this.stateInfo.push(elem115);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Location.prototype.write = function(output) {
  output.writeStructBegin('Location');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.cities !== null && this.cities !== undefined) {
    output.writeFieldBegin('cities', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.I32, this.cities.length);
    for (var iter116 in this.cities)
    {
      if (this.cities.hasOwnProperty(iter116))
      {
        iter116 = this.cities[iter116];
        output.writeI32(iter116);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.states !== null && this.states !== undefined) {
    output.writeFieldBegin('states', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.I32, this.states.length);
    for (var iter117 in this.states)
    {
      if (this.states.hasOwnProperty(iter117))
      {
        iter117 = this.states[iter117];
        output.writeI32(iter117);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.cityInfo !== null && this.cityInfo !== undefined) {
    output.writeFieldBegin('cityInfo', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRUCT, this.cityInfo.length);
    for (var iter118 in this.cityInfo)
    {
      if (this.cityInfo.hasOwnProperty(iter118))
      {
        iter118 = this.cityInfo[iter118];
        iter118.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.stateInfo !== null && this.stateInfo !== undefined) {
    output.writeFieldBegin('stateInfo', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.stateInfo.length);
    for (var iter119 in this.stateInfo)
    {
      if (this.stateInfo.hasOwnProperty(iter119))
      {
        iter119 = this.stateInfo[iter119];
        iter119.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

LinearGradient = function(args) {
  this.startColor = null;
  this.endColor = null;
  if (args) {
    if (args.startColor !== undefined && args.startColor !== null) {
      this.startColor = args.startColor;
    }
    if (args.endColor !== undefined && args.endColor !== null) {
      this.endColor = args.endColor;
    }
  }
};
LinearGradient.prototype = {};
LinearGradient.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.startColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.endColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

LinearGradient.prototype.write = function(output) {
  output.writeStructBegin('LinearGradient');
  if (this.startColor !== null && this.startColor !== undefined) {
    output.writeFieldBegin('startColor', Thrift.Type.STRING, 1);
    output.writeString(this.startColor);
    output.writeFieldEnd();
  }
  if (this.endColor !== null && this.endColor !== undefined) {
    output.writeFieldBegin('endColor', Thrift.Type.STRING, 2);
    output.writeString(this.endColor);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TagLine = function(args) {
  this.value = null;
  this.imageLeft = null;
  this.linearGradient = null;
  this.validUptoEpoch = null;
  if (args) {
    if (args.value !== undefined && args.value !== null) {
      this.value = args.value;
    }
    if (args.imageLeft !== undefined && args.imageLeft !== null) {
      this.imageLeft = args.imageLeft;
    }
    if (args.linearGradient !== undefined && args.linearGradient !== null) {
      this.linearGradient = new LinearGradient(args.linearGradient);
    }
    if (args.validUptoEpoch !== undefined && args.validUptoEpoch !== null) {
      this.validUptoEpoch = args.validUptoEpoch;
    }
  }
};
TagLine.prototype = {};
TagLine.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.value = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.imageLeft = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.linearGradient = new LinearGradient();
        this.linearGradient.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.validUptoEpoch = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TagLine.prototype.write = function(output) {
  output.writeStructBegin('TagLine');
  if (this.value !== null && this.value !== undefined) {
    output.writeFieldBegin('value', Thrift.Type.STRING, 1);
    output.writeString(this.value);
    output.writeFieldEnd();
  }
  if (this.imageLeft !== null && this.imageLeft !== undefined) {
    output.writeFieldBegin('imageLeft', Thrift.Type.STRING, 2);
    output.writeString(this.imageLeft);
    output.writeFieldEnd();
  }
  if (this.linearGradient !== null && this.linearGradient !== undefined) {
    output.writeFieldBegin('linearGradient', Thrift.Type.STRUCT, 3);
    this.linearGradient.write(output);
    output.writeFieldEnd();
  }
  if (this.validUptoEpoch !== null && this.validUptoEpoch !== undefined) {
    output.writeFieldBegin('validUptoEpoch', Thrift.Type.STRING, 4);
    output.writeString(this.validUptoEpoch);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TagLineInfo = function(args) {
  this.breakingNewsTagLine = null;
  this.topNewsTagLine = null;
  this.sportsTagLine = null;
  this.tags = null;
  if (args) {
    if (args.breakingNewsTagLine !== undefined && args.breakingNewsTagLine !== null) {
      this.breakingNewsTagLine = new TagLine(args.breakingNewsTagLine);
    }
    if (args.topNewsTagLine !== undefined && args.topNewsTagLine !== null) {
      this.topNewsTagLine = new TagLine(args.topNewsTagLine);
    }
    if (args.sportsTagLine !== undefined && args.sportsTagLine !== null) {
      this.sportsTagLine = new TagLine(args.sportsTagLine);
    }
    if (args.tags !== undefined && args.tags !== null) {
      this.tags = Thrift.copyList(args.tags, [TagLine]);
    }
  }
};
TagLineInfo.prototype = {};
TagLineInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.breakingNewsTagLine = new TagLine();
        this.breakingNewsTagLine.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.topNewsTagLine = new TagLine();
        this.topNewsTagLine.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.sportsTagLine = new TagLine();
        this.sportsTagLine.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size120 = 0;
        var _rtmp3124;
        this.tags = [];
        var _etype123 = 0;
        _rtmp3124 = input.readListBegin();
        _etype123 = _rtmp3124.etype;
        _size120 = _rtmp3124.size;
        for (var _i125 = 0; _i125 < _size120; ++_i125)
        {
          var elem126 = null;
          elem126 = new TagLine();
          elem126.read(input);
          this.tags.push(elem126);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TagLineInfo.prototype.write = function(output) {
  output.writeStructBegin('TagLineInfo');
  if (this.breakingNewsTagLine !== null && this.breakingNewsTagLine !== undefined) {
    output.writeFieldBegin('breakingNewsTagLine', Thrift.Type.STRUCT, 1);
    this.breakingNewsTagLine.write(output);
    output.writeFieldEnd();
  }
  if (this.topNewsTagLine !== null && this.topNewsTagLine !== undefined) {
    output.writeFieldBegin('topNewsTagLine', Thrift.Type.STRUCT, 2);
    this.topNewsTagLine.write(output);
    output.writeFieldEnd();
  }
  if (this.sportsTagLine !== null && this.sportsTagLine !== undefined) {
    output.writeFieldBegin('sportsTagLine', Thrift.Type.STRUCT, 3);
    this.sportsTagLine.write(output);
    output.writeFieldEnd();
  }
  if (this.tags !== null && this.tags !== undefined) {
    output.writeFieldBegin('tags', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRUCT, this.tags.length);
    for (var iter127 in this.tags)
    {
      if (this.tags.hasOwnProperty(iter127))
      {
        iter127 = this.tags[iter127];
        iter127.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SpaceData = function(args) {
  this.spaceWidgets = null;
  this.lockScreenImagePath = null;
  this.publisherName = null;
  this.publisherLogo = null;
  this.breakingNewsEndTime = null;
  this.bgColor = null;
  this.iconPath = null;
  this.bgLockscreenImage = null;
  this.imageMap = null;
  this.breakingNewsTag = null;
  this.topNewsTag = null;
  this.spaceServingCategories = null;
  this.destinationUrl = null;
  this.locations = null;
  this.publisherId = null;
  this.zappWidgetIds = null;
  this.taggedCategories = null;
  this.eventType = null;
  this.contentTypes = null;
  this.breakingNewsStartTime = null;
  this.l0ZappWidgetIds = null;
  this.tagLineInfo = null;
  this.l0Worthy = null;
  if (args) {
    if (args.spaceWidgets !== undefined && args.spaceWidgets !== null) {
      this.spaceWidgets = Thrift.copyList(args.spaceWidgets, [null]);
    }
    if (args.lockScreenImagePath !== undefined && args.lockScreenImagePath !== null) {
      this.lockScreenImagePath = args.lockScreenImagePath;
    }
    if (args.publisherName !== undefined && args.publisherName !== null) {
      this.publisherName = args.publisherName;
    }
    if (args.publisherLogo !== undefined && args.publisherLogo !== null) {
      this.publisherLogo = args.publisherLogo;
    }
    if (args.breakingNewsEndTime !== undefined && args.breakingNewsEndTime !== null) {
      this.breakingNewsEndTime = args.breakingNewsEndTime;
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = args.bgColor;
    }
    if (args.iconPath !== undefined && args.iconPath !== null) {
      this.iconPath = args.iconPath;
    }
    if (args.bgLockscreenImage !== undefined && args.bgLockscreenImage !== null) {
      this.bgLockscreenImage = args.bgLockscreenImage;
    }
    if (args.imageMap !== undefined && args.imageMap !== null) {
      this.imageMap = new ImageMap(args.imageMap);
    }
    if (args.breakingNewsTag !== undefined && args.breakingNewsTag !== null) {
      this.breakingNewsTag = new WidgetTagDetails(args.breakingNewsTag);
    }
    if (args.topNewsTag !== undefined && args.topNewsTag !== null) {
      this.topNewsTag = new WidgetTagDetails(args.topNewsTag);
    }
    if (args.spaceServingCategories !== undefined && args.spaceServingCategories !== null) {
      this.spaceServingCategories = Thrift.copyList(args.spaceServingCategories, [SpaceServingCategory]);
    }
    if (args.destinationUrl !== undefined && args.destinationUrl !== null) {
      this.destinationUrl = args.destinationUrl;
    }
    if (args.locations !== undefined && args.locations !== null) {
      this.locations = Thrift.copyList(args.locations, [Location]);
    }
    if (args.publisherId !== undefined && args.publisherId !== null) {
      this.publisherId = args.publisherId;
    }
    if (args.zappWidgetIds !== undefined && args.zappWidgetIds !== null) {
      this.zappWidgetIds = Thrift.copyList(args.zappWidgetIds, [null]);
    }
    if (args.taggedCategories !== undefined && args.taggedCategories !== null) {
      this.taggedCategories = Thrift.copyMap(args.taggedCategories, [Thrift.copyList, null]);
    }
    if (args.eventType !== undefined && args.eventType !== null) {
      this.eventType = args.eventType;
    }
    if (args.contentTypes !== undefined && args.contentTypes !== null) {
      this.contentTypes = Thrift.copyList(args.contentTypes, [null]);
    }
    if (args.breakingNewsStartTime !== undefined && args.breakingNewsStartTime !== null) {
      this.breakingNewsStartTime = args.breakingNewsStartTime;
    }
    if (args.l0ZappWidgetIds !== undefined && args.l0ZappWidgetIds !== null) {
      this.l0ZappWidgetIds = Thrift.copyList(args.l0ZappWidgetIds, [null]);
    }
    if (args.tagLineInfo !== undefined && args.tagLineInfo !== null) {
      this.tagLineInfo = new TagLineInfo(args.tagLineInfo);
    }
    if (args.l0Worthy !== undefined && args.l0Worthy !== null) {
      this.l0Worthy = args.l0Worthy;
    }
  }
};
SpaceData.prototype = {};
SpaceData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size128 = 0;
        var _rtmp3132;
        this.spaceWidgets = [];
        var _etype131 = 0;
        _rtmp3132 = input.readListBegin();
        _etype131 = _rtmp3132.etype;
        _size128 = _rtmp3132.size;
        for (var _i133 = 0; _i133 < _size128; ++_i133)
        {
          var elem134 = null;
          elem134 = input.readI32().value;
          this.spaceWidgets.push(elem134);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.lockScreenImagePath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.publisherName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.publisherLogo = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.breakingNewsEndTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.bgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.iconPath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.bgLockscreenImage = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRUCT) {
        this.imageMap = new ImageMap();
        this.imageMap.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRUCT) {
        this.breakingNewsTag = new WidgetTagDetails();
        this.breakingNewsTag.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRUCT) {
        this.topNewsTag = new WidgetTagDetails();
        this.topNewsTag.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size135 = 0;
        var _rtmp3139;
        this.spaceServingCategories = [];
        var _etype138 = 0;
        _rtmp3139 = input.readListBegin();
        _etype138 = _rtmp3139.etype;
        _size135 = _rtmp3139.size;
        for (var _i140 = 0; _i140 < _size135; ++_i140)
        {
          var elem141 = null;
          elem141 = new SpaceServingCategory();
          elem141.read(input);
          this.spaceServingCategories.push(elem141);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.destinationUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.LIST) {
        var _size142 = 0;
        var _rtmp3146;
        this.locations = [];
        var _etype145 = 0;
        _rtmp3146 = input.readListBegin();
        _etype145 = _rtmp3146.etype;
        _size142 = _rtmp3146.size;
        for (var _i147 = 0; _i147 < _size142; ++_i147)
        {
          var elem148 = null;
          elem148 = new Location();
          elem148.read(input);
          this.locations.push(elem148);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.publisherId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.LIST) {
        var _size149 = 0;
        var _rtmp3153;
        this.zappWidgetIds = [];
        var _etype152 = 0;
        _rtmp3153 = input.readListBegin();
        _etype152 = _rtmp3153.etype;
        _size149 = _rtmp3153.size;
        for (var _i154 = 0; _i154 < _size149; ++_i154)
        {
          var elem155 = null;
          elem155 = input.readString().value;
          this.zappWidgetIds.push(elem155);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.MAP) {
        var _size156 = 0;
        var _rtmp3160;
        this.taggedCategories = {};
        var _ktype157 = 0;
        var _vtype158 = 0;
        _rtmp3160 = input.readMapBegin();
        _ktype157 = _rtmp3160.ktype;
        _vtype158 = _rtmp3160.vtype;
        _size156 = _rtmp3160.size;
        for (var _i161 = 0; _i161 < _size156; ++_i161)
        {
          if (_i161 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key162 = null;
          var val163 = null;
          key162 = input.readString().value;
          var _size164 = 0;
          var _rtmp3168;
          val163 = [];
          var _etype167 = 0;
          _rtmp3168 = input.readListBegin();
          _etype167 = _rtmp3168.etype;
          _size164 = _rtmp3168.size;
          for (var _i169 = 0; _i169 < _size164; ++_i169)
          {
            var elem170 = null;
            elem170 = input.readString().value;
            val163.push(elem170);
          }
          input.readListEnd();
          this.taggedCategories[key162] = val163;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.I32) {
        this.eventType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.LIST) {
        var _size171 = 0;
        var _rtmp3175;
        this.contentTypes = [];
        var _etype174 = 0;
        _rtmp3175 = input.readListBegin();
        _etype174 = _rtmp3175.etype;
        _size171 = _rtmp3175.size;
        for (var _i176 = 0; _i176 < _size171; ++_i176)
        {
          var elem177 = null;
          elem177 = input.readI32().value;
          this.contentTypes.push(elem177);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.I64) {
        this.breakingNewsStartTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.LIST) {
        var _size178 = 0;
        var _rtmp3182;
        this.l0ZappWidgetIds = [];
        var _etype181 = 0;
        _rtmp3182 = input.readListBegin();
        _etype181 = _rtmp3182.etype;
        _size178 = _rtmp3182.size;
        for (var _i183 = 0; _i183 < _size178; ++_i183)
        {
          var elem184 = null;
          elem184 = input.readString().value;
          this.l0ZappWidgetIds.push(elem184);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRUCT) {
        this.tagLineInfo = new TagLineInfo();
        this.tagLineInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.BOOL) {
        this.l0Worthy = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SpaceData.prototype.write = function(output) {
  output.writeStructBegin('SpaceData');
  if (this.spaceWidgets !== null && this.spaceWidgets !== undefined) {
    output.writeFieldBegin('spaceWidgets', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.I32, this.spaceWidgets.length);
    for (var iter185 in this.spaceWidgets)
    {
      if (this.spaceWidgets.hasOwnProperty(iter185))
      {
        iter185 = this.spaceWidgets[iter185];
        output.writeI32(iter185);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.lockScreenImagePath !== null && this.lockScreenImagePath !== undefined) {
    output.writeFieldBegin('lockScreenImagePath', Thrift.Type.STRING, 3);
    output.writeString(this.lockScreenImagePath);
    output.writeFieldEnd();
  }
  if (this.publisherName !== null && this.publisherName !== undefined) {
    output.writeFieldBegin('publisherName', Thrift.Type.STRING, 4);
    output.writeString(this.publisherName);
    output.writeFieldEnd();
  }
  if (this.publisherLogo !== null && this.publisherLogo !== undefined) {
    output.writeFieldBegin('publisherLogo', Thrift.Type.STRING, 5);
    output.writeString(this.publisherLogo);
    output.writeFieldEnd();
  }
  if (this.breakingNewsEndTime !== null && this.breakingNewsEndTime !== undefined) {
    output.writeFieldBegin('breakingNewsEndTime', Thrift.Type.I64, 6);
    output.writeI64(this.breakingNewsEndTime);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.STRING, 7);
    output.writeString(this.bgColor);
    output.writeFieldEnd();
  }
  if (this.iconPath !== null && this.iconPath !== undefined) {
    output.writeFieldBegin('iconPath', Thrift.Type.STRING, 8);
    output.writeString(this.iconPath);
    output.writeFieldEnd();
  }
  if (this.bgLockscreenImage !== null && this.bgLockscreenImage !== undefined) {
    output.writeFieldBegin('bgLockscreenImage', Thrift.Type.STRING, 9);
    output.writeString(this.bgLockscreenImage);
    output.writeFieldEnd();
  }
  if (this.imageMap !== null && this.imageMap !== undefined) {
    output.writeFieldBegin('imageMap', Thrift.Type.STRUCT, 10);
    this.imageMap.write(output);
    output.writeFieldEnd();
  }
  if (this.breakingNewsTag !== null && this.breakingNewsTag !== undefined) {
    output.writeFieldBegin('breakingNewsTag', Thrift.Type.STRUCT, 11);
    this.breakingNewsTag.write(output);
    output.writeFieldEnd();
  }
  if (this.topNewsTag !== null && this.topNewsTag !== undefined) {
    output.writeFieldBegin('topNewsTag', Thrift.Type.STRUCT, 12);
    this.topNewsTag.write(output);
    output.writeFieldEnd();
  }
  if (this.spaceServingCategories !== null && this.spaceServingCategories !== undefined) {
    output.writeFieldBegin('spaceServingCategories', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRUCT, this.spaceServingCategories.length);
    for (var iter186 in this.spaceServingCategories)
    {
      if (this.spaceServingCategories.hasOwnProperty(iter186))
      {
        iter186 = this.spaceServingCategories[iter186];
        iter186.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.destinationUrl !== null && this.destinationUrl !== undefined) {
    output.writeFieldBegin('destinationUrl', Thrift.Type.STRING, 14);
    output.writeString(this.destinationUrl);
    output.writeFieldEnd();
  }
  if (this.locations !== null && this.locations !== undefined) {
    output.writeFieldBegin('locations', Thrift.Type.LIST, 15);
    output.writeListBegin(Thrift.Type.STRUCT, this.locations.length);
    for (var iter187 in this.locations)
    {
      if (this.locations.hasOwnProperty(iter187))
      {
        iter187 = this.locations[iter187];
        iter187.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.publisherId !== null && this.publisherId !== undefined) {
    output.writeFieldBegin('publisherId', Thrift.Type.STRING, 16);
    output.writeString(this.publisherId);
    output.writeFieldEnd();
  }
  if (this.zappWidgetIds !== null && this.zappWidgetIds !== undefined) {
    output.writeFieldBegin('zappWidgetIds', Thrift.Type.LIST, 17);
    output.writeListBegin(Thrift.Type.STRING, this.zappWidgetIds.length);
    for (var iter188 in this.zappWidgetIds)
    {
      if (this.zappWidgetIds.hasOwnProperty(iter188))
      {
        iter188 = this.zappWidgetIds[iter188];
        output.writeString(iter188);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.taggedCategories !== null && this.taggedCategories !== undefined) {
    output.writeFieldBegin('taggedCategories', Thrift.Type.MAP, 18);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.LIST, Thrift.objectLength(this.taggedCategories));
    for (var kiter189 in this.taggedCategories)
    {
      if (this.taggedCategories.hasOwnProperty(kiter189))
      {
        var viter190 = this.taggedCategories[kiter189];
        output.writeString(kiter189);
        output.writeListBegin(Thrift.Type.STRING, viter190.length);
        for (var iter191 in viter190)
        {
          if (viter190.hasOwnProperty(iter191))
          {
            iter191 = viter190[iter191];
            output.writeString(iter191);
          }
        }
        output.writeListEnd();
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.eventType !== null && this.eventType !== undefined) {
    output.writeFieldBegin('eventType', Thrift.Type.I32, 19);
    output.writeI32(this.eventType);
    output.writeFieldEnd();
  }
  if (this.contentTypes !== null && this.contentTypes !== undefined) {
    output.writeFieldBegin('contentTypes', Thrift.Type.LIST, 20);
    output.writeListBegin(Thrift.Type.I32, this.contentTypes.length);
    for (var iter192 in this.contentTypes)
    {
      if (this.contentTypes.hasOwnProperty(iter192))
      {
        iter192 = this.contentTypes[iter192];
        output.writeI32(iter192);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.breakingNewsStartTime !== null && this.breakingNewsStartTime !== undefined) {
    output.writeFieldBegin('breakingNewsStartTime', Thrift.Type.I64, 21);
    output.writeI64(this.breakingNewsStartTime);
    output.writeFieldEnd();
  }
  if (this.l0ZappWidgetIds !== null && this.l0ZappWidgetIds !== undefined) {
    output.writeFieldBegin('l0ZappWidgetIds', Thrift.Type.LIST, 22);
    output.writeListBegin(Thrift.Type.STRING, this.l0ZappWidgetIds.length);
    for (var iter193 in this.l0ZappWidgetIds)
    {
      if (this.l0ZappWidgetIds.hasOwnProperty(iter193))
      {
        iter193 = this.l0ZappWidgetIds[iter193];
        output.writeString(iter193);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.tagLineInfo !== null && this.tagLineInfo !== undefined) {
    output.writeFieldBegin('tagLineInfo', Thrift.Type.STRUCT, 23);
    this.tagLineInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.l0Worthy !== null && this.l0Worthy !== undefined) {
    output.writeFieldBegin('l0Worthy', Thrift.Type.BOOL, 24);
    output.writeBool(this.l0Worthy);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

LiveEventData = function(args) {
  this.proposition = null;
  this.teams = null;
  this.imageMap = null;
  this.liveEventCollection = null;
  if (args) {
    if (args.proposition !== undefined && args.proposition !== null) {
      this.proposition = args.proposition;
    }
    if (args.teams !== undefined && args.teams !== null) {
      this.teams = Thrift.copyList(args.teams, [null]);
    }
    if (args.imageMap !== undefined && args.imageMap !== null) {
      this.imageMap = new ImageMap(args.imageMap);
    }
    if (args.liveEventCollection !== undefined && args.liveEventCollection !== null) {
      this.liveEventCollection = args.liveEventCollection;
    }
  }
};
LiveEventData.prototype = {};
LiveEventData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.proposition = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size194 = 0;
        var _rtmp3198;
        this.teams = [];
        var _etype197 = 0;
        _rtmp3198 = input.readListBegin();
        _etype197 = _rtmp3198.etype;
        _size194 = _rtmp3198.size;
        for (var _i199 = 0; _i199 < _size194; ++_i199)
        {
          var elem200 = null;
          elem200 = input.readString().value;
          this.teams.push(elem200);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.imageMap = new ImageMap();
        this.imageMap.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.liveEventCollection = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

LiveEventData.prototype.write = function(output) {
  output.writeStructBegin('LiveEventData');
  if (this.proposition !== null && this.proposition !== undefined) {
    output.writeFieldBegin('proposition', Thrift.Type.I32, 1);
    output.writeI32(this.proposition);
    output.writeFieldEnd();
  }
  if (this.teams !== null && this.teams !== undefined) {
    output.writeFieldBegin('teams', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.teams.length);
    for (var iter201 in this.teams)
    {
      if (this.teams.hasOwnProperty(iter201))
      {
        iter201 = this.teams[iter201];
        output.writeString(iter201);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.imageMap !== null && this.imageMap !== undefined) {
    output.writeFieldBegin('imageMap', Thrift.Type.STRUCT, 3);
    this.imageMap.write(output);
    output.writeFieldEnd();
  }
  if (this.liveEventCollection !== null && this.liveEventCollection !== undefined) {
    output.writeFieldBegin('liveEventCollection', Thrift.Type.STRING, 4);
    output.writeString(this.liveEventCollection);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CtaMeta = function(args) {
  this.url = null;
  this.ctaDisplay = null;
  this.shouldUnlock = null;
  this.originalUrl = null;
  this.openInBg = null;
  this.customCtaLoader = null;
  this.productTiles = null;
  if (args) {
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.ctaDisplay !== undefined && args.ctaDisplay !== null) {
      this.ctaDisplay = new CtaDisplay(args.ctaDisplay);
    }
    if (args.shouldUnlock !== undefined && args.shouldUnlock !== null) {
      this.shouldUnlock = args.shouldUnlock;
    }
    if (args.originalUrl !== undefined && args.originalUrl !== null) {
      this.originalUrl = args.originalUrl;
    }
    if (args.openInBg !== undefined && args.openInBg !== null) {
      this.openInBg = args.openInBg;
    }
    if (args.customCtaLoader !== undefined && args.customCtaLoader !== null) {
      this.customCtaLoader = args.customCtaLoader;
    }
    if (args.productTiles !== undefined && args.productTiles !== null) {
      this.productTiles = new ProductTiles(args.productTiles);
    }
  }
};
CtaMeta.prototype = {};
CtaMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.ctaDisplay = new CtaDisplay();
        this.ctaDisplay.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldUnlock = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.originalUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.openInBg = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.customCtaLoader = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRUCT) {
        this.productTiles = new ProductTiles();
        this.productTiles.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CtaMeta.prototype.write = function(output) {
  output.writeStructBegin('CtaMeta');
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 1);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.ctaDisplay !== null && this.ctaDisplay !== undefined) {
    output.writeFieldBegin('ctaDisplay', Thrift.Type.STRUCT, 2);
    this.ctaDisplay.write(output);
    output.writeFieldEnd();
  }
  if (this.shouldUnlock !== null && this.shouldUnlock !== undefined) {
    output.writeFieldBegin('shouldUnlock', Thrift.Type.BOOL, 3);
    output.writeBool(this.shouldUnlock);
    output.writeFieldEnd();
  }
  if (this.originalUrl !== null && this.originalUrl !== undefined) {
    output.writeFieldBegin('originalUrl', Thrift.Type.STRING, 4);
    output.writeString(this.originalUrl);
    output.writeFieldEnd();
  }
  if (this.openInBg !== null && this.openInBg !== undefined) {
    output.writeFieldBegin('openInBg', Thrift.Type.BOOL, 5);
    output.writeBool(this.openInBg);
    output.writeFieldEnd();
  }
  if (this.customCtaLoader !== null && this.customCtaLoader !== undefined) {
    output.writeFieldBegin('customCtaLoader', Thrift.Type.STRING, 6);
    output.writeString(this.customCtaLoader);
    output.writeFieldEnd();
  }
  if (this.productTiles !== null && this.productTiles !== undefined) {
    output.writeFieldBegin('productTiles', Thrift.Type.STRUCT, 7);
    this.productTiles.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppCta = function(args) {
  this.appInstallMeta = null;
  this.appInstallCta = null;
  this.appInstalledCta = null;
  this.appInstallingCta = null;
  if (args) {
    if (args.appInstallMeta !== undefined && args.appInstallMeta !== null) {
      this.appInstallMeta = new AppInstallMeta(args.appInstallMeta);
    }
    if (args.appInstallCta !== undefined && args.appInstallCta !== null) {
      this.appInstallCta = new CtaMeta(args.appInstallCta);
    }
    if (args.appInstalledCta !== undefined && args.appInstalledCta !== null) {
      this.appInstalledCta = new CtaMeta(args.appInstalledCta);
    }
    if (args.appInstallingCta !== undefined && args.appInstallingCta !== null) {
      this.appInstallingCta = new CtaMeta(args.appInstallingCta);
    }
  }
};
AppCta.prototype = {};
AppCta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.appInstallMeta = new AppInstallMeta();
        this.appInstallMeta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.appInstallCta = new CtaMeta();
        this.appInstallCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.appInstalledCta = new CtaMeta();
        this.appInstalledCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.appInstallingCta = new CtaMeta();
        this.appInstallingCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppCta.prototype.write = function(output) {
  output.writeStructBegin('AppCta');
  if (this.appInstallMeta !== null && this.appInstallMeta !== undefined) {
    output.writeFieldBegin('appInstallMeta', Thrift.Type.STRUCT, 1);
    this.appInstallMeta.write(output);
    output.writeFieldEnd();
  }
  if (this.appInstallCta !== null && this.appInstallCta !== undefined) {
    output.writeFieldBegin('appInstallCta', Thrift.Type.STRUCT, 2);
    this.appInstallCta.write(output);
    output.writeFieldEnd();
  }
  if (this.appInstalledCta !== null && this.appInstalledCta !== undefined) {
    output.writeFieldBegin('appInstalledCta', Thrift.Type.STRUCT, 3);
    this.appInstalledCta.write(output);
    output.writeFieldEnd();
  }
  if (this.appInstallingCta !== null && this.appInstallingCta !== undefined) {
    output.writeFieldBegin('appInstallingCta', Thrift.Type.STRUCT, 4);
    this.appInstallingCta.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Cta = function(args) {
  this.url = null;
  this.text = null;
  this.shouldUnlock = null;
  this.originalUrl = null;
  this.appCta = null;
  this.openUrlCta = null;
  this.ctaType = null;
  this.autoOpenCtaDurationInSecs = null;
  if (args) {
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field url is unset!');
    }
    if (args.text !== undefined && args.text !== null) {
      this.text = args.text;
    }
    if (args.shouldUnlock !== undefined && args.shouldUnlock !== null) {
      this.shouldUnlock = args.shouldUnlock;
    }
    if (args.originalUrl !== undefined && args.originalUrl !== null) {
      this.originalUrl = args.originalUrl;
    }
    if (args.appCta !== undefined && args.appCta !== null) {
      this.appCta = new AppCta(args.appCta);
    }
    if (args.openUrlCta !== undefined && args.openUrlCta !== null) {
      this.openUrlCta = new CtaMeta(args.openUrlCta);
    }
    if (args.ctaType !== undefined && args.ctaType !== null) {
      this.ctaType = args.ctaType;
    }
    if (args.autoOpenCtaDurationInSecs !== undefined && args.autoOpenCtaDurationInSecs !== null) {
      this.autoOpenCtaDurationInSecs = args.autoOpenCtaDurationInSecs;
    }
  }
};
Cta.prototype = {};
Cta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldUnlock = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.originalUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.appCta = new AppCta();
        this.appCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.openUrlCta = new CtaMeta();
        this.openUrlCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.ctaType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.autoOpenCtaDurationInSecs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Cta.prototype.write = function(output) {
  output.writeStructBegin('Cta');
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 1);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRING, 2);
    output.writeString(this.text);
    output.writeFieldEnd();
  }
  if (this.shouldUnlock !== null && this.shouldUnlock !== undefined) {
    output.writeFieldBegin('shouldUnlock', Thrift.Type.BOOL, 3);
    output.writeBool(this.shouldUnlock);
    output.writeFieldEnd();
  }
  if (this.originalUrl !== null && this.originalUrl !== undefined) {
    output.writeFieldBegin('originalUrl', Thrift.Type.STRING, 4);
    output.writeString(this.originalUrl);
    output.writeFieldEnd();
  }
  if (this.appCta !== null && this.appCta !== undefined) {
    output.writeFieldBegin('appCta', Thrift.Type.STRUCT, 5);
    this.appCta.write(output);
    output.writeFieldEnd();
  }
  if (this.openUrlCta !== null && this.openUrlCta !== undefined) {
    output.writeFieldBegin('openUrlCta', Thrift.Type.STRUCT, 6);
    this.openUrlCta.write(output);
    output.writeFieldEnd();
  }
  if (this.ctaType !== null && this.ctaType !== undefined) {
    output.writeFieldBegin('ctaType', Thrift.Type.I32, 7);
    output.writeI32(this.ctaType);
    output.writeFieldEnd();
  }
  if (this.autoOpenCtaDurationInSecs !== null && this.autoOpenCtaDurationInSecs !== undefined) {
    output.writeFieldBegin('autoOpenCtaDurationInSecs', Thrift.Type.I32, 8);
    output.writeI32(this.autoOpenCtaDurationInSecs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ArticlePeek = function(args) {
  this.cta = null;
  this.summary = null;
  this.cheesySummary = null;
  this.canSkipSummary = null;
  this.loadAndroidJs = null;
  this.canSwipeSkipSummary = null;
  if (args) {
    if (args.cta !== undefined && args.cta !== null) {
      this.cta = new Cta(args.cta);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field cta is unset!');
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.cheesySummary !== undefined && args.cheesySummary !== null) {
      this.cheesySummary = args.cheesySummary;
    }
    if (args.canSkipSummary !== undefined && args.canSkipSummary !== null) {
      this.canSkipSummary = args.canSkipSummary;
    }
    if (args.loadAndroidJs !== undefined && args.loadAndroidJs !== null) {
      this.loadAndroidJs = args.loadAndroidJs;
    }
    if (args.canSwipeSkipSummary !== undefined && args.canSwipeSkipSummary !== null) {
      this.canSwipeSkipSummary = args.canSwipeSkipSummary;
    }
  }
};
ArticlePeek.prototype = {};
ArticlePeek.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.cta = new Cta();
        this.cta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.cheesySummary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.canSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.loadAndroidJs = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.canSwipeSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ArticlePeek.prototype.write = function(output) {
  output.writeStructBegin('ArticlePeek');
  if (this.cta !== null && this.cta !== undefined) {
    output.writeFieldBegin('cta', Thrift.Type.STRUCT, 1);
    this.cta.write(output);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 2);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.cheesySummary !== null && this.cheesySummary !== undefined) {
    output.writeFieldBegin('cheesySummary', Thrift.Type.STRING, 3);
    output.writeString(this.cheesySummary);
    output.writeFieldEnd();
  }
  if (this.canSkipSummary !== null && this.canSkipSummary !== undefined) {
    output.writeFieldBegin('canSkipSummary', Thrift.Type.BOOL, 4);
    output.writeBool(this.canSkipSummary);
    output.writeFieldEnd();
  }
  if (this.loadAndroidJs !== null && this.loadAndroidJs !== undefined) {
    output.writeFieldBegin('loadAndroidJs', Thrift.Type.BOOL, 5);
    output.writeBool(this.loadAndroidJs);
    output.writeFieldEnd();
  }
  if (this.canSwipeSkipSummary !== null && this.canSwipeSkipSummary !== undefined) {
    output.writeFieldBegin('canSwipeSkipSummary', Thrift.Type.BOOL, 6);
    output.writeBool(this.canSwipeSkipSummary);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

WebPeek = function(args) {
  this.url = null;
  this.hardwareAccelerated = null;
  this.cta = null;
  this.summary = null;
  this.cheesySummary = null;
  this.canSkipSummary = null;
  this.loadAndroidJs = null;
  this.notifTemplateAppId = null;
  if (args) {
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field url is unset!');
    }
    if (args.hardwareAccelerated !== undefined && args.hardwareAccelerated !== null) {
      this.hardwareAccelerated = args.hardwareAccelerated;
    }
    if (args.cta !== undefined && args.cta !== null) {
      this.cta = new Cta(args.cta);
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.cheesySummary !== undefined && args.cheesySummary !== null) {
      this.cheesySummary = args.cheesySummary;
    }
    if (args.canSkipSummary !== undefined && args.canSkipSummary !== null) {
      this.canSkipSummary = args.canSkipSummary;
    }
    if (args.loadAndroidJs !== undefined && args.loadAndroidJs !== null) {
      this.loadAndroidJs = args.loadAndroidJs;
    }
    if (args.notifTemplateAppId !== undefined && args.notifTemplateAppId !== null) {
      this.notifTemplateAppId = args.notifTemplateAppId;
    }
  }
};
WebPeek.prototype = {};
WebPeek.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.hardwareAccelerated = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.cta = new Cta();
        this.cta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.cheesySummary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.canSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.loadAndroidJs = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.notifTemplateAppId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

WebPeek.prototype.write = function(output) {
  output.writeStructBegin('WebPeek');
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 1);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.hardwareAccelerated !== null && this.hardwareAccelerated !== undefined) {
    output.writeFieldBegin('hardwareAccelerated', Thrift.Type.BOOL, 2);
    output.writeBool(this.hardwareAccelerated);
    output.writeFieldEnd();
  }
  if (this.cta !== null && this.cta !== undefined) {
    output.writeFieldBegin('cta', Thrift.Type.STRUCT, 3);
    this.cta.write(output);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 4);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.cheesySummary !== null && this.cheesySummary !== undefined) {
    output.writeFieldBegin('cheesySummary', Thrift.Type.STRING, 5);
    output.writeString(this.cheesySummary);
    output.writeFieldEnd();
  }
  if (this.canSkipSummary !== null && this.canSkipSummary !== undefined) {
    output.writeFieldBegin('canSkipSummary', Thrift.Type.BOOL, 6);
    output.writeBool(this.canSkipSummary);
    output.writeFieldEnd();
  }
  if (this.loadAndroidJs !== null && this.loadAndroidJs !== undefined) {
    output.writeFieldBegin('loadAndroidJs', Thrift.Type.BOOL, 7);
    output.writeBool(this.loadAndroidJs);
    output.writeFieldEnd();
  }
  if (this.notifTemplateAppId !== null && this.notifTemplateAppId !== undefined) {
    output.writeFieldBegin('notifTemplateAppId', Thrift.Type.STRING, 8);
    output.writeString(this.notifTemplateAppId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

NativeVideoPeek = function(args) {
  this.videoUrl = null;
  this.shouldOverflow = null;
  this.cta = null;
  this.summary = null;
  this.cheesySummary = null;
  this.canSkipSummary = null;
  this.loadAndroidJs = null;
  this.dashVideoUrl = null;
  this.adaptiveStreamVideoUrls = null;
  this.durationInMillis = null;
  this.isAdEligible = null;
  this.noAds = null;
  this.canSwipeSkipSummary = null;
  this.hlsVideoUrl = null;
  if (args) {
    if (args.videoUrl !== undefined && args.videoUrl !== null) {
      this.videoUrl = args.videoUrl;
    }
    if (args.shouldOverflow !== undefined && args.shouldOverflow !== null) {
      this.shouldOverflow = args.shouldOverflow;
    }
    if (args.cta !== undefined && args.cta !== null) {
      this.cta = new Cta(args.cta);
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.cheesySummary !== undefined && args.cheesySummary !== null) {
      this.cheesySummary = args.cheesySummary;
    }
    if (args.canSkipSummary !== undefined && args.canSkipSummary !== null) {
      this.canSkipSummary = args.canSkipSummary;
    }
    if (args.loadAndroidJs !== undefined && args.loadAndroidJs !== null) {
      this.loadAndroidJs = args.loadAndroidJs;
    }
    if (args.dashVideoUrl !== undefined && args.dashVideoUrl !== null) {
      this.dashVideoUrl = args.dashVideoUrl;
    }
    if (args.adaptiveStreamVideoUrls !== undefined && args.adaptiveStreamVideoUrls !== null) {
      this.adaptiveStreamVideoUrls = Thrift.copyList(args.adaptiveStreamVideoUrls, [null]);
    }
    if (args.durationInMillis !== undefined && args.durationInMillis !== null) {
      this.durationInMillis = args.durationInMillis;
    }
    if (args.isAdEligible !== undefined && args.isAdEligible !== null) {
      this.isAdEligible = args.isAdEligible;
    }
    if (args.noAds !== undefined && args.noAds !== null) {
      this.noAds = args.noAds;
    }
    if (args.canSwipeSkipSummary !== undefined && args.canSwipeSkipSummary !== null) {
      this.canSwipeSkipSummary = args.canSwipeSkipSummary;
    }
    if (args.hlsVideoUrl !== undefined && args.hlsVideoUrl !== null) {
      this.hlsVideoUrl = args.hlsVideoUrl;
    }
  }
};
NativeVideoPeek.prototype = {};
NativeVideoPeek.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.videoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldOverflow = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.cta = new Cta();
        this.cta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.cheesySummary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.canSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.loadAndroidJs = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.dashVideoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size202 = 0;
        var _rtmp3206;
        this.adaptiveStreamVideoUrls = [];
        var _etype205 = 0;
        _rtmp3206 = input.readListBegin();
        _etype205 = _rtmp3206.etype;
        _size202 = _rtmp3206.size;
        for (var _i207 = 0; _i207 < _size202; ++_i207)
        {
          var elem208 = null;
          elem208 = input.readString().value;
          this.adaptiveStreamVideoUrls.push(elem208);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I64) {
        this.durationInMillis = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.BOOL) {
        this.isAdEligible = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.BOOL) {
        this.noAds = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.BOOL) {
        this.canSwipeSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.hlsVideoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

NativeVideoPeek.prototype.write = function(output) {
  output.writeStructBegin('NativeVideoPeek');
  if (this.videoUrl !== null && this.videoUrl !== undefined) {
    output.writeFieldBegin('videoUrl', Thrift.Type.STRING, 1);
    output.writeString(this.videoUrl);
    output.writeFieldEnd();
  }
  if (this.shouldOverflow !== null && this.shouldOverflow !== undefined) {
    output.writeFieldBegin('shouldOverflow', Thrift.Type.BOOL, 2);
    output.writeBool(this.shouldOverflow);
    output.writeFieldEnd();
  }
  if (this.cta !== null && this.cta !== undefined) {
    output.writeFieldBegin('cta', Thrift.Type.STRUCT, 3);
    this.cta.write(output);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 4);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.cheesySummary !== null && this.cheesySummary !== undefined) {
    output.writeFieldBegin('cheesySummary', Thrift.Type.STRING, 5);
    output.writeString(this.cheesySummary);
    output.writeFieldEnd();
  }
  if (this.canSkipSummary !== null && this.canSkipSummary !== undefined) {
    output.writeFieldBegin('canSkipSummary', Thrift.Type.BOOL, 6);
    output.writeBool(this.canSkipSummary);
    output.writeFieldEnd();
  }
  if (this.loadAndroidJs !== null && this.loadAndroidJs !== undefined) {
    output.writeFieldBegin('loadAndroidJs', Thrift.Type.BOOL, 7);
    output.writeBool(this.loadAndroidJs);
    output.writeFieldEnd();
  }
  if (this.dashVideoUrl !== null && this.dashVideoUrl !== undefined) {
    output.writeFieldBegin('dashVideoUrl', Thrift.Type.STRING, 8);
    output.writeString(this.dashVideoUrl);
    output.writeFieldEnd();
  }
  if (this.adaptiveStreamVideoUrls !== null && this.adaptiveStreamVideoUrls !== undefined) {
    output.writeFieldBegin('adaptiveStreamVideoUrls', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRING, this.adaptiveStreamVideoUrls.length);
    for (var iter209 in this.adaptiveStreamVideoUrls)
    {
      if (this.adaptiveStreamVideoUrls.hasOwnProperty(iter209))
      {
        iter209 = this.adaptiveStreamVideoUrls[iter209];
        output.writeString(iter209);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.durationInMillis !== null && this.durationInMillis !== undefined) {
    output.writeFieldBegin('durationInMillis', Thrift.Type.I64, 10);
    output.writeI64(this.durationInMillis);
    output.writeFieldEnd();
  }
  if (this.isAdEligible !== null && this.isAdEligible !== undefined) {
    output.writeFieldBegin('isAdEligible', Thrift.Type.BOOL, 11);
    output.writeBool(this.isAdEligible);
    output.writeFieldEnd();
  }
  if (this.noAds !== null && this.noAds !== undefined) {
    output.writeFieldBegin('noAds', Thrift.Type.BOOL, 12);
    output.writeBool(this.noAds);
    output.writeFieldEnd();
  }
  if (this.canSwipeSkipSummary !== null && this.canSwipeSkipSummary !== undefined) {
    output.writeFieldBegin('canSwipeSkipSummary', Thrift.Type.BOOL, 13);
    output.writeBool(this.canSwipeSkipSummary);
    output.writeFieldEnd();
  }
  if (this.hlsVideoUrl !== null && this.hlsVideoUrl !== undefined) {
    output.writeFieldBegin('hlsVideoUrl', Thrift.Type.STRING, 14);
    output.writeString(this.hlsVideoUrl);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

NativeLiveVideoPeek = function(args) {
  this.channelName = null;
  this.showStartTime = null;
  this.showEndTime = null;
  this.overlayZipUrl = null;
  this.zipUrlSuffix = null;
  this.sizeFit = null;
  this.backgroundImageUrl = null;
  this.defaultMuteState = null;
  this.guid = null;
  this.slugId = null;
  this.introVideoUrl = null;
  this.shouldRepeatIntroVideo = true;
  this.stopIntroOnHostJoin = true;
  this.showUrl = null;
  this.showUrlSuffix = null;
  if (args) {
    if (args.channelName !== undefined && args.channelName !== null) {
      this.channelName = args.channelName;
    }
    if (args.showStartTime !== undefined && args.showStartTime !== null) {
      this.showStartTime = args.showStartTime;
    }
    if (args.showEndTime !== undefined && args.showEndTime !== null) {
      this.showEndTime = args.showEndTime;
    }
    if (args.overlayZipUrl !== undefined && args.overlayZipUrl !== null) {
      this.overlayZipUrl = args.overlayZipUrl;
    }
    if (args.zipUrlSuffix !== undefined && args.zipUrlSuffix !== null) {
      this.zipUrlSuffix = args.zipUrlSuffix;
    }
    if (args.sizeFit !== undefined && args.sizeFit !== null) {
      this.sizeFit = args.sizeFit;
    }
    if (args.backgroundImageUrl !== undefined && args.backgroundImageUrl !== null) {
      this.backgroundImageUrl = args.backgroundImageUrl;
    }
    if (args.defaultMuteState !== undefined && args.defaultMuteState !== null) {
      this.defaultMuteState = args.defaultMuteState;
    }
    if (args.guid !== undefined && args.guid !== null) {
      this.guid = args.guid;
    }
    if (args.slugId !== undefined && args.slugId !== null) {
      this.slugId = args.slugId;
    }
    if (args.introVideoUrl !== undefined && args.introVideoUrl !== null) {
      this.introVideoUrl = args.introVideoUrl;
    }
    if (args.shouldRepeatIntroVideo !== undefined && args.shouldRepeatIntroVideo !== null) {
      this.shouldRepeatIntroVideo = args.shouldRepeatIntroVideo;
    }
    if (args.stopIntroOnHostJoin !== undefined && args.stopIntroOnHostJoin !== null) {
      this.stopIntroOnHostJoin = args.stopIntroOnHostJoin;
    }
    if (args.showUrl !== undefined && args.showUrl !== null) {
      this.showUrl = args.showUrl;
    }
    if (args.showUrlSuffix !== undefined && args.showUrlSuffix !== null) {
      this.showUrlSuffix = args.showUrlSuffix;
    }
  }
};
NativeLiveVideoPeek.prototype = {};
NativeLiveVideoPeek.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.channelName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.showStartTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.showEndTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.overlayZipUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.zipUrlSuffix = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.sizeFit = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.backgroundImageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.defaultMuteState = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.guid = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.slugId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.introVideoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldRepeatIntroVideo = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.BOOL) {
        this.stopIntroOnHostJoin = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.showUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.showUrlSuffix = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

NativeLiveVideoPeek.prototype.write = function(output) {
  output.writeStructBegin('NativeLiveVideoPeek');
  if (this.channelName !== null && this.channelName !== undefined) {
    output.writeFieldBegin('channelName', Thrift.Type.STRING, 1);
    output.writeString(this.channelName);
    output.writeFieldEnd();
  }
  if (this.showStartTime !== null && this.showStartTime !== undefined) {
    output.writeFieldBegin('showStartTime', Thrift.Type.I64, 2);
    output.writeI64(this.showStartTime);
    output.writeFieldEnd();
  }
  if (this.showEndTime !== null && this.showEndTime !== undefined) {
    output.writeFieldBegin('showEndTime', Thrift.Type.I64, 3);
    output.writeI64(this.showEndTime);
    output.writeFieldEnd();
  }
  if (this.overlayZipUrl !== null && this.overlayZipUrl !== undefined) {
    output.writeFieldBegin('overlayZipUrl', Thrift.Type.STRING, 4);
    output.writeString(this.overlayZipUrl);
    output.writeFieldEnd();
  }
  if (this.zipUrlSuffix !== null && this.zipUrlSuffix !== undefined) {
    output.writeFieldBegin('zipUrlSuffix', Thrift.Type.STRING, 5);
    output.writeString(this.zipUrlSuffix);
    output.writeFieldEnd();
  }
  if (this.sizeFit !== null && this.sizeFit !== undefined) {
    output.writeFieldBegin('sizeFit', Thrift.Type.I32, 6);
    output.writeI32(this.sizeFit);
    output.writeFieldEnd();
  }
  if (this.backgroundImageUrl !== null && this.backgroundImageUrl !== undefined) {
    output.writeFieldBegin('backgroundImageUrl', Thrift.Type.STRING, 7);
    output.writeString(this.backgroundImageUrl);
    output.writeFieldEnd();
  }
  if (this.defaultMuteState !== null && this.defaultMuteState !== undefined) {
    output.writeFieldBegin('defaultMuteState', Thrift.Type.BOOL, 8);
    output.writeBool(this.defaultMuteState);
    output.writeFieldEnd();
  }
  if (this.guid !== null && this.guid !== undefined) {
    output.writeFieldBegin('guid', Thrift.Type.STRING, 9);
    output.writeString(this.guid);
    output.writeFieldEnd();
  }
  if (this.slugId !== null && this.slugId !== undefined) {
    output.writeFieldBegin('slugId', Thrift.Type.STRING, 10);
    output.writeString(this.slugId);
    output.writeFieldEnd();
  }
  if (this.introVideoUrl !== null && this.introVideoUrl !== undefined) {
    output.writeFieldBegin('introVideoUrl', Thrift.Type.STRING, 11);
    output.writeString(this.introVideoUrl);
    output.writeFieldEnd();
  }
  if (this.shouldRepeatIntroVideo !== null && this.shouldRepeatIntroVideo !== undefined) {
    output.writeFieldBegin('shouldRepeatIntroVideo', Thrift.Type.BOOL, 12);
    output.writeBool(this.shouldRepeatIntroVideo);
    output.writeFieldEnd();
  }
  if (this.stopIntroOnHostJoin !== null && this.stopIntroOnHostJoin !== undefined) {
    output.writeFieldBegin('stopIntroOnHostJoin', Thrift.Type.BOOL, 13);
    output.writeBool(this.stopIntroOnHostJoin);
    output.writeFieldEnd();
  }
  if (this.showUrl !== null && this.showUrl !== undefined) {
    output.writeFieldBegin('showUrl', Thrift.Type.STRING, 14);
    output.writeString(this.showUrl);
    output.writeFieldEnd();
  }
  if (this.showUrlSuffix !== null && this.showUrlSuffix !== undefined) {
    output.writeFieldBegin('showUrlSuffix', Thrift.Type.STRING, 15);
    output.writeString(this.showUrlSuffix);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

FusionVideoPeek = function(args) {
  this.channelName = null;
  this.showStartTime = null;
  this.showEndTime = null;
  this.showOverlayUrl = null;
  this.liveStreamUrl = null;
  this.showOverlayUrlSuffix = null;
  this.slugId = null;
  this.stopIntroOnHostJoin = true;
  this.isLive = true;
  this.introVideoUrl = null;
  this.shouldRepeatIntroVideo = true;
  if (args) {
    if (args.channelName !== undefined && args.channelName !== null) {
      this.channelName = args.channelName;
    }
    if (args.showStartTime !== undefined && args.showStartTime !== null) {
      this.showStartTime = args.showStartTime;
    }
    if (args.showEndTime !== undefined && args.showEndTime !== null) {
      this.showEndTime = args.showEndTime;
    }
    if (args.showOverlayUrl !== undefined && args.showOverlayUrl !== null) {
      this.showOverlayUrl = args.showOverlayUrl;
    }
    if (args.liveStreamUrl !== undefined && args.liveStreamUrl !== null) {
      this.liveStreamUrl = args.liveStreamUrl;
    }
    if (args.showOverlayUrlSuffix !== undefined && args.showOverlayUrlSuffix !== null) {
      this.showOverlayUrlSuffix = args.showOverlayUrlSuffix;
    }
    if (args.slugId !== undefined && args.slugId !== null) {
      this.slugId = args.slugId;
    }
    if (args.stopIntroOnHostJoin !== undefined && args.stopIntroOnHostJoin !== null) {
      this.stopIntroOnHostJoin = args.stopIntroOnHostJoin;
    }
    if (args.isLive !== undefined && args.isLive !== null) {
      this.isLive = args.isLive;
    }
    if (args.introVideoUrl !== undefined && args.introVideoUrl !== null) {
      this.introVideoUrl = args.introVideoUrl;
    }
    if (args.shouldRepeatIntroVideo !== undefined && args.shouldRepeatIntroVideo !== null) {
      this.shouldRepeatIntroVideo = args.shouldRepeatIntroVideo;
    }
  }
};
FusionVideoPeek.prototype = {};
FusionVideoPeek.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.channelName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.showStartTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.showEndTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.showOverlayUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.liveStreamUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.showOverlayUrlSuffix = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.slugId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.stopIntroOnHostJoin = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.BOOL) {
        this.isLive = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.introVideoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldRepeatIntroVideo = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

FusionVideoPeek.prototype.write = function(output) {
  output.writeStructBegin('FusionVideoPeek');
  if (this.channelName !== null && this.channelName !== undefined) {
    output.writeFieldBegin('channelName', Thrift.Type.STRING, 1);
    output.writeString(this.channelName);
    output.writeFieldEnd();
  }
  if (this.showStartTime !== null && this.showStartTime !== undefined) {
    output.writeFieldBegin('showStartTime', Thrift.Type.I64, 2);
    output.writeI64(this.showStartTime);
    output.writeFieldEnd();
  }
  if (this.showEndTime !== null && this.showEndTime !== undefined) {
    output.writeFieldBegin('showEndTime', Thrift.Type.I64, 3);
    output.writeI64(this.showEndTime);
    output.writeFieldEnd();
  }
  if (this.showOverlayUrl !== null && this.showOverlayUrl !== undefined) {
    output.writeFieldBegin('showOverlayUrl', Thrift.Type.STRING, 4);
    output.writeString(this.showOverlayUrl);
    output.writeFieldEnd();
  }
  if (this.liveStreamUrl !== null && this.liveStreamUrl !== undefined) {
    output.writeFieldBegin('liveStreamUrl', Thrift.Type.STRING, 5);
    output.writeString(this.liveStreamUrl);
    output.writeFieldEnd();
  }
  if (this.showOverlayUrlSuffix !== null && this.showOverlayUrlSuffix !== undefined) {
    output.writeFieldBegin('showOverlayUrlSuffix', Thrift.Type.STRING, 6);
    output.writeString(this.showOverlayUrlSuffix);
    output.writeFieldEnd();
  }
  if (this.slugId !== null && this.slugId !== undefined) {
    output.writeFieldBegin('slugId', Thrift.Type.STRING, 7);
    output.writeString(this.slugId);
    output.writeFieldEnd();
  }
  if (this.stopIntroOnHostJoin !== null && this.stopIntroOnHostJoin !== undefined) {
    output.writeFieldBegin('stopIntroOnHostJoin', Thrift.Type.BOOL, 8);
    output.writeBool(this.stopIntroOnHostJoin);
    output.writeFieldEnd();
  }
  if (this.isLive !== null && this.isLive !== undefined) {
    output.writeFieldBegin('isLive', Thrift.Type.BOOL, 9);
    output.writeBool(this.isLive);
    output.writeFieldEnd();
  }
  if (this.introVideoUrl !== null && this.introVideoUrl !== undefined) {
    output.writeFieldBegin('introVideoUrl', Thrift.Type.STRING, 10);
    output.writeString(this.introVideoUrl);
    output.writeFieldEnd();
  }
  if (this.shouldRepeatIntroVideo !== null && this.shouldRepeatIntroVideo !== undefined) {
    output.writeFieldBegin('shouldRepeatIntroVideo', Thrift.Type.BOOL, 11);
    output.writeBool(this.shouldRepeatIntroVideo);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TvVideoPeek = function(args) {
  this.videoUrl = null;
  this.summary = null;
  this.screenSaverCta = null;
  this.videoCta = null;
  this.canSkipSummary = null;
  this.overflow = null;
  this.loadAndroidJs = null;
  if (args) {
    if (args.videoUrl !== undefined && args.videoUrl !== null) {
      this.videoUrl = args.videoUrl;
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.screenSaverCta !== undefined && args.screenSaverCta !== null) {
      this.screenSaverCta = new Cta(args.screenSaverCta);
    }
    if (args.videoCta !== undefined && args.videoCta !== null) {
      this.videoCta = new Cta(args.videoCta);
    }
    if (args.canSkipSummary !== undefined && args.canSkipSummary !== null) {
      this.canSkipSummary = args.canSkipSummary;
    }
    if (args.overflow !== undefined && args.overflow !== null) {
      this.overflow = args.overflow;
    }
    if (args.loadAndroidJs !== undefined && args.loadAndroidJs !== null) {
      this.loadAndroidJs = args.loadAndroidJs;
    }
  }
};
TvVideoPeek.prototype = {};
TvVideoPeek.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.videoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.screenSaverCta = new Cta();
        this.screenSaverCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.videoCta = new Cta();
        this.videoCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.canSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.overflow = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.loadAndroidJs = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TvVideoPeek.prototype.write = function(output) {
  output.writeStructBegin('TvVideoPeek');
  if (this.videoUrl !== null && this.videoUrl !== undefined) {
    output.writeFieldBegin('videoUrl', Thrift.Type.STRING, 1);
    output.writeString(this.videoUrl);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 2);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.screenSaverCta !== null && this.screenSaverCta !== undefined) {
    output.writeFieldBegin('screenSaverCta', Thrift.Type.STRUCT, 3);
    this.screenSaverCta.write(output);
    output.writeFieldEnd();
  }
  if (this.videoCta !== null && this.videoCta !== undefined) {
    output.writeFieldBegin('videoCta', Thrift.Type.STRUCT, 4);
    this.videoCta.write(output);
    output.writeFieldEnd();
  }
  if (this.canSkipSummary !== null && this.canSkipSummary !== undefined) {
    output.writeFieldBegin('canSkipSummary', Thrift.Type.BOOL, 5);
    output.writeBool(this.canSkipSummary);
    output.writeFieldEnd();
  }
  if (this.overflow !== null && this.overflow !== undefined) {
    output.writeFieldBegin('overflow', Thrift.Type.BOOL, 6);
    output.writeBool(this.overflow);
    output.writeFieldEnd();
  }
  if (this.loadAndroidJs !== null && this.loadAndroidJs !== undefined) {
    output.writeFieldBegin('loadAndroidJs', Thrift.Type.BOOL, 7);
    output.writeBool(this.loadAndroidJs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

LiveVideoPeek = function(args) {
  this.videoUrl = null;
  this.startTime = null;
  this.duration = null;
  this.shouldOverflow = null;
  this.cta = null;
  this.summary = null;
  this.canSkipSummary = null;
  if (args) {
    if (args.videoUrl !== undefined && args.videoUrl !== null) {
      this.videoUrl = args.videoUrl;
    }
    if (args.startTime !== undefined && args.startTime !== null) {
      this.startTime = args.startTime;
    }
    if (args.duration !== undefined && args.duration !== null) {
      this.duration = args.duration;
    }
    if (args.shouldOverflow !== undefined && args.shouldOverflow !== null) {
      this.shouldOverflow = args.shouldOverflow;
    }
    if (args.cta !== undefined && args.cta !== null) {
      this.cta = new Cta(args.cta);
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.canSkipSummary !== undefined && args.canSkipSummary !== null) {
      this.canSkipSummary = args.canSkipSummary;
    }
  }
};
LiveVideoPeek.prototype = {};
LiveVideoPeek.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.videoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.startTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.duration = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldOverflow = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.cta = new Cta();
        this.cta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.canSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

LiveVideoPeek.prototype.write = function(output) {
  output.writeStructBegin('LiveVideoPeek');
  if (this.videoUrl !== null && this.videoUrl !== undefined) {
    output.writeFieldBegin('videoUrl', Thrift.Type.STRING, 1);
    output.writeString(this.videoUrl);
    output.writeFieldEnd();
  }
  if (this.startTime !== null && this.startTime !== undefined) {
    output.writeFieldBegin('startTime', Thrift.Type.I64, 2);
    output.writeI64(this.startTime);
    output.writeFieldEnd();
  }
  if (this.duration !== null && this.duration !== undefined) {
    output.writeFieldBegin('duration', Thrift.Type.I64, 3);
    output.writeI64(this.duration);
    output.writeFieldEnd();
  }
  if (this.shouldOverflow !== null && this.shouldOverflow !== undefined) {
    output.writeFieldBegin('shouldOverflow', Thrift.Type.BOOL, 4);
    output.writeBool(this.shouldOverflow);
    output.writeFieldEnd();
  }
  if (this.cta !== null && this.cta !== undefined) {
    output.writeFieldBegin('cta', Thrift.Type.STRUCT, 5);
    this.cta.write(output);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 6);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.canSkipSummary !== null && this.canSkipSummary !== undefined) {
    output.writeFieldBegin('canSkipSummary', Thrift.Type.BOOL, 7);
    output.writeBool(this.canSkipSummary);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

FacebookVideoMeta = function(args) {
  this.id = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
  }
};
FacebookVideoMeta.prototype = {};
FacebookVideoMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

FacebookVideoMeta.prototype.write = function(output) {
  output.writeStructBegin('FacebookVideoMeta');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

YoutubeVideoMeta = function(args) {
  this.id = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
  }
};
YoutubeVideoMeta.prototype = {};
YoutubeVideoMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

YoutubeVideoMeta.prototype.write = function(output) {
  output.writeStructBegin('YoutubeVideoMeta');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

InstagramVideoMeta = function(args) {
  this.id = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
  }
};
InstagramVideoMeta.prototype = {};
InstagramVideoMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

InstagramVideoMeta.prototype.write = function(output) {
  output.writeStructBegin('InstagramVideoMeta');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

YoukuVideoMeta = function(args) {
  this.id = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
  }
};
YoukuVideoMeta.prototype = {};
YoukuVideoMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

YoukuVideoMeta.prototype.write = function(output) {
  output.writeStructBegin('YoukuVideoMeta');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

VideoMeta = function(args) {
  this.facebook = null;
  this.youtube = null;
  this.instagram = null;
  this.youku = null;
  if (args) {
    if (args.facebook !== undefined && args.facebook !== null) {
      this.facebook = new FacebookVideoMeta(args.facebook);
    }
    if (args.youtube !== undefined && args.youtube !== null) {
      this.youtube = new YoutubeVideoMeta(args.youtube);
    }
    if (args.instagram !== undefined && args.instagram !== null) {
      this.instagram = new InstagramVideoMeta(args.instagram);
    }
    if (args.youku !== undefined && args.youku !== null) {
      this.youku = new YoukuVideoMeta(args.youku);
    }
  }
};
VideoMeta.prototype = {};
VideoMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.facebook = new FacebookVideoMeta();
        this.facebook.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.youtube = new YoutubeVideoMeta();
        this.youtube.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.instagram = new InstagramVideoMeta();
        this.instagram.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.youku = new YoukuVideoMeta();
        this.youku.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

VideoMeta.prototype.write = function(output) {
  output.writeStructBegin('VideoMeta');
  if (this.facebook !== null && this.facebook !== undefined) {
    output.writeFieldBegin('facebook', Thrift.Type.STRUCT, 1);
    this.facebook.write(output);
    output.writeFieldEnd();
  }
  if (this.youtube !== null && this.youtube !== undefined) {
    output.writeFieldBegin('youtube', Thrift.Type.STRUCT, 2);
    this.youtube.write(output);
    output.writeFieldEnd();
  }
  if (this.instagram !== null && this.instagram !== undefined) {
    output.writeFieldBegin('instagram', Thrift.Type.STRUCT, 3);
    this.instagram.write(output);
    output.writeFieldEnd();
  }
  if (this.youku !== null && this.youku !== undefined) {
    output.writeFieldBegin('youku', Thrift.Type.STRUCT, 4);
    this.youku.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AspectRatio = function(args) {
  this.width = null;
  this.height = null;
  if (args) {
    if (args.width !== undefined && args.width !== null) {
      this.width = args.width;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field width is unset!');
    }
    if (args.height !== undefined && args.height !== null) {
      this.height = args.height;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field height is unset!');
    }
  }
};
AspectRatio.prototype = {};
AspectRatio.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.width = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.height = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AspectRatio.prototype.write = function(output) {
  output.writeStructBegin('AspectRatio');
  if (this.width !== null && this.width !== undefined) {
    output.writeFieldBegin('width', Thrift.Type.I32, 1);
    output.writeI32(this.width);
    output.writeFieldEnd();
  }
  if (this.height !== null && this.height !== undefined) {
    output.writeFieldBegin('height', Thrift.Type.I32, 2);
    output.writeI32(this.height);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

VideoData = function(args) {
  this.meta = null;
  this.thumbnailUrl = null;
  this.aspectRatio = null;
  this.originalUrl = null;
  this.durationInMillis = null;
  if (args) {
    if (args.meta !== undefined && args.meta !== null) {
      this.meta = new VideoMeta(args.meta);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field meta is unset!');
    }
    if (args.thumbnailUrl !== undefined && args.thumbnailUrl !== null) {
      this.thumbnailUrl = args.thumbnailUrl;
    }
    if (args.aspectRatio !== undefined && args.aspectRatio !== null) {
      this.aspectRatio = new AspectRatio(args.aspectRatio);
    }
    if (args.originalUrl !== undefined && args.originalUrl !== null) {
      this.originalUrl = args.originalUrl;
    }
    if (args.durationInMillis !== undefined && args.durationInMillis !== null) {
      this.durationInMillis = args.durationInMillis;
    }
  }
};
VideoData.prototype = {};
VideoData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.meta = new VideoMeta();
        this.meta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.thumbnailUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.aspectRatio = new AspectRatio();
        this.aspectRatio.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.originalUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.durationInMillis = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

VideoData.prototype.write = function(output) {
  output.writeStructBegin('VideoData');
  if (this.meta !== null && this.meta !== undefined) {
    output.writeFieldBegin('meta', Thrift.Type.STRUCT, 1);
    this.meta.write(output);
    output.writeFieldEnd();
  }
  if (this.thumbnailUrl !== null && this.thumbnailUrl !== undefined) {
    output.writeFieldBegin('thumbnailUrl', Thrift.Type.STRING, 2);
    output.writeString(this.thumbnailUrl);
    output.writeFieldEnd();
  }
  if (this.aspectRatio !== null && this.aspectRatio !== undefined) {
    output.writeFieldBegin('aspectRatio', Thrift.Type.STRUCT, 3);
    this.aspectRatio.write(output);
    output.writeFieldEnd();
  }
  if (this.originalUrl !== null && this.originalUrl !== undefined) {
    output.writeFieldBegin('originalUrl', Thrift.Type.STRING, 4);
    output.writeString(this.originalUrl);
    output.writeFieldEnd();
  }
  if (this.durationInMillis !== null && this.durationInMillis !== undefined) {
    output.writeFieldBegin('durationInMillis', Thrift.Type.I64, 5);
    output.writeI64(this.durationInMillis);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

VideoPeek = function(args) {
  this.video = null;
  this.cta = null;
  this.summary = null;
  this.cheesySummary = null;
  this.canSkipSummary = null;
  this.loadAndroidJs = null;
  this.canSwipeSkipSummary = null;
  if (args) {
    if (args.video !== undefined && args.video !== null) {
      this.video = new VideoData(args.video);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field video is unset!');
    }
    if (args.cta !== undefined && args.cta !== null) {
      this.cta = new Cta(args.cta);
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.cheesySummary !== undefined && args.cheesySummary !== null) {
      this.cheesySummary = args.cheesySummary;
    }
    if (args.canSkipSummary !== undefined && args.canSkipSummary !== null) {
      this.canSkipSummary = args.canSkipSummary;
    }
    if (args.loadAndroidJs !== undefined && args.loadAndroidJs !== null) {
      this.loadAndroidJs = args.loadAndroidJs;
    }
    if (args.canSwipeSkipSummary !== undefined && args.canSwipeSkipSummary !== null) {
      this.canSwipeSkipSummary = args.canSwipeSkipSummary;
    }
  }
};
VideoPeek.prototype = {};
VideoPeek.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.video = new VideoData();
        this.video.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.cta = new Cta();
        this.cta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.cheesySummary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.canSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.loadAndroidJs = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.canSwipeSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

VideoPeek.prototype.write = function(output) {
  output.writeStructBegin('VideoPeek');
  if (this.video !== null && this.video !== undefined) {
    output.writeFieldBegin('video', Thrift.Type.STRUCT, 1);
    this.video.write(output);
    output.writeFieldEnd();
  }
  if (this.cta !== null && this.cta !== undefined) {
    output.writeFieldBegin('cta', Thrift.Type.STRUCT, 2);
    this.cta.write(output);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 3);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.cheesySummary !== null && this.cheesySummary !== undefined) {
    output.writeFieldBegin('cheesySummary', Thrift.Type.STRING, 4);
    output.writeString(this.cheesySummary);
    output.writeFieldEnd();
  }
  if (this.canSkipSummary !== null && this.canSkipSummary !== undefined) {
    output.writeFieldBegin('canSkipSummary', Thrift.Type.BOOL, 5);
    output.writeBool(this.canSkipSummary);
    output.writeFieldEnd();
  }
  if (this.loadAndroidJs !== null && this.loadAndroidJs !== undefined) {
    output.writeFieldBegin('loadAndroidJs', Thrift.Type.BOOL, 6);
    output.writeBool(this.loadAndroidJs);
    output.writeFieldEnd();
  }
  if (this.canSwipeSkipSummary !== null && this.canSwipeSkipSummary !== undefined) {
    output.writeFieldBegin('canSwipeSkipSummary', Thrift.Type.BOOL, 7);
    output.writeBool(this.canSwipeSkipSummary);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AttributionDetails = function(args) {
  this.caption = null;
  this.author = null;
  this.source = null;
  this.link = null;
  if (args) {
    if (args.caption !== undefined && args.caption !== null) {
      this.caption = args.caption;
    }
    if (args.author !== undefined && args.author !== null) {
      this.author = args.author;
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    }
    if (args.link !== undefined && args.link !== null) {
      this.link = args.link;
    }
  }
};
AttributionDetails.prototype = {};
AttributionDetails.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.caption = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.author = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.link = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AttributionDetails.prototype.write = function(output) {
  output.writeStructBegin('AttributionDetails');
  if (this.caption !== null && this.caption !== undefined) {
    output.writeFieldBegin('caption', Thrift.Type.STRING, 1);
    output.writeString(this.caption);
    output.writeFieldEnd();
  }
  if (this.author !== null && this.author !== undefined) {
    output.writeFieldBegin('author', Thrift.Type.STRING, 2);
    output.writeString(this.author);
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.STRING, 3);
    output.writeString(this.source);
    output.writeFieldEnd();
  }
  if (this.link !== null && this.link !== undefined) {
    output.writeFieldBegin('link', Thrift.Type.STRING, 4);
    output.writeString(this.link);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TargetDeviceProperties = function(args) {
  this.targetDevice = null;
  if (args) {
    if (args.targetDevice !== undefined && args.targetDevice !== null) {
      this.targetDevice = args.targetDevice;
    }
  }
};
TargetDeviceProperties.prototype = {};
TargetDeviceProperties.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.targetDevice = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TargetDeviceProperties.prototype.write = function(output) {
  output.writeStructBegin('TargetDeviceProperties');
  if (this.targetDevice !== null && this.targetDevice !== undefined) {
    output.writeFieldBegin('targetDevice', Thrift.Type.I32, 1);
    output.writeI32(this.targetDevice);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ShareDetails = function(args) {
  this.subText = null;
  this.fallbackUrl = null;
  this.fallbackTitle = null;
  this.imageAspectRatio = null;
  if (args) {
    if (args.subText !== undefined && args.subText !== null) {
      this.subText = args.subText;
    }
    if (args.fallbackUrl !== undefined && args.fallbackUrl !== null) {
      this.fallbackUrl = args.fallbackUrl;
    }
    if (args.fallbackTitle !== undefined && args.fallbackTitle !== null) {
      this.fallbackTitle = args.fallbackTitle;
    }
    if (args.imageAspectRatio !== undefined && args.imageAspectRatio !== null) {
      this.imageAspectRatio = new AspectRatio(args.imageAspectRatio);
    }
  }
};
ShareDetails.prototype = {};
ShareDetails.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.subText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.fallbackUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.fallbackTitle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.imageAspectRatio = new AspectRatio();
        this.imageAspectRatio.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ShareDetails.prototype.write = function(output) {
  output.writeStructBegin('ShareDetails');
  if (this.subText !== null && this.subText !== undefined) {
    output.writeFieldBegin('subText', Thrift.Type.STRING, 1);
    output.writeString(this.subText);
    output.writeFieldEnd();
  }
  if (this.fallbackUrl !== null && this.fallbackUrl !== undefined) {
    output.writeFieldBegin('fallbackUrl', Thrift.Type.STRING, 2);
    output.writeString(this.fallbackUrl);
    output.writeFieldEnd();
  }
  if (this.fallbackTitle !== null && this.fallbackTitle !== undefined) {
    output.writeFieldBegin('fallbackTitle', Thrift.Type.STRING, 3);
    output.writeString(this.fallbackTitle);
    output.writeFieldEnd();
  }
  if (this.imageAspectRatio !== null && this.imageAspectRatio !== undefined) {
    output.writeFieldBegin('imageAspectRatio', Thrift.Type.STRUCT, 4);
    this.imageAspectRatio.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceSlots = function(args) {
  this.totalSlots = null;
  this.sponsoredSlots = null;
  if (args) {
    if (args.totalSlots !== undefined && args.totalSlots !== null) {
      this.totalSlots = args.totalSlots;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field totalSlots is unset!');
    }
    if (args.sponsoredSlots !== undefined && args.sponsoredSlots !== null) {
      this.sponsoredSlots = Thrift.copyList(args.sponsoredSlots, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field sponsoredSlots is unset!');
    }
  }
};
GlanceSlots.prototype = {};
GlanceSlots.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.totalSlots = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size210 = 0;
        var _rtmp3214;
        this.sponsoredSlots = [];
        var _etype213 = 0;
        _rtmp3214 = input.readListBegin();
        _etype213 = _rtmp3214.etype;
        _size210 = _rtmp3214.size;
        for (var _i215 = 0; _i215 < _size210; ++_i215)
        {
          var elem216 = null;
          elem216 = input.readI32().value;
          this.sponsoredSlots.push(elem216);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceSlots.prototype.write = function(output) {
  output.writeStructBegin('GlanceSlots');
  if (this.totalSlots !== null && this.totalSlots !== undefined) {
    output.writeFieldBegin('totalSlots', Thrift.Type.I32, 1);
    output.writeI32(this.totalSlots);
    output.writeFieldEnd();
  }
  if (this.sponsoredSlots !== null && this.sponsoredSlots !== undefined) {
    output.writeFieldBegin('sponsoredSlots', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.I32, this.sponsoredSlots.length);
    for (var iter217 in this.sponsoredSlots)
    {
      if (this.sponsoredSlots.hasOwnProperty(iter217))
      {
        iter217 = this.sponsoredSlots[iter217];
        output.writeI32(iter217);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GamPgConfig = function(args) {
  this.timeoutPeriodInMinutes = null;
  this.shouldFireTrackersAfterTimeout = null;
  if (args) {
    if (args.timeoutPeriodInMinutes !== undefined && args.timeoutPeriodInMinutes !== null) {
      this.timeoutPeriodInMinutes = args.timeoutPeriodInMinutes;
    }
    if (args.shouldFireTrackersAfterTimeout !== undefined && args.shouldFireTrackersAfterTimeout !== null) {
      this.shouldFireTrackersAfterTimeout = args.shouldFireTrackersAfterTimeout;
    }
  }
};
GamPgConfig.prototype = {};
GamPgConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.timeoutPeriodInMinutes = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldFireTrackersAfterTimeout = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GamPgConfig.prototype.write = function(output) {
  output.writeStructBegin('GamPgConfig');
  if (this.timeoutPeriodInMinutes !== null && this.timeoutPeriodInMinutes !== undefined) {
    output.writeFieldBegin('timeoutPeriodInMinutes', Thrift.Type.I32, 1);
    output.writeI32(this.timeoutPeriodInMinutes);
    output.writeFieldEnd();
  }
  if (this.shouldFireTrackersAfterTimeout !== null && this.shouldFireTrackersAfterTimeout !== undefined) {
    output.writeFieldBegin('shouldFireTrackersAfterTimeout', Thrift.Type.BOOL, 2);
    output.writeBool(this.shouldFireTrackersAfterTimeout);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceConfig = function(args) {
  this.refreshIntervalInHrs = null;
  this.wakeupMethod = null;
  this.configUpdateWindowInHrs = null;
  this.configUpdateWindowMaxCount = null;
  this.partnerConfig = null;
  this.apiInitialDelayLimitInSecs = null;
  this.preferredNetworkTypeForAnalytics = null;
  this.appAnalyticsAllowed = null;
  this.userDataSyncRequestedAt = null;
  this.detectGpay = null;
  this.safetyNetEnabled = null;
  if (args) {
    if (args.refreshIntervalInHrs !== undefined && args.refreshIntervalInHrs !== null) {
      this.refreshIntervalInHrs = args.refreshIntervalInHrs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field refreshIntervalInHrs is unset!');
    }
    if (args.wakeupMethod !== undefined && args.wakeupMethod !== null) {
      this.wakeupMethod = args.wakeupMethod;
    }
    if (args.configUpdateWindowInHrs !== undefined && args.configUpdateWindowInHrs !== null) {
      this.configUpdateWindowInHrs = args.configUpdateWindowInHrs;
    }
    if (args.configUpdateWindowMaxCount !== undefined && args.configUpdateWindowMaxCount !== null) {
      this.configUpdateWindowMaxCount = args.configUpdateWindowMaxCount;
    }
    if (args.partnerConfig !== undefined && args.partnerConfig !== null) {
      this.partnerConfig = args.partnerConfig;
    }
    if (args.apiInitialDelayLimitInSecs !== undefined && args.apiInitialDelayLimitInSecs !== null) {
      this.apiInitialDelayLimitInSecs = args.apiInitialDelayLimitInSecs;
    }
    if (args.preferredNetworkTypeForAnalytics !== undefined && args.preferredNetworkTypeForAnalytics !== null) {
      this.preferredNetworkTypeForAnalytics = args.preferredNetworkTypeForAnalytics;
    }
    if (args.appAnalyticsAllowed !== undefined && args.appAnalyticsAllowed !== null) {
      this.appAnalyticsAllowed = args.appAnalyticsAllowed;
    }
    if (args.userDataSyncRequestedAt !== undefined && args.userDataSyncRequestedAt !== null) {
      this.userDataSyncRequestedAt = args.userDataSyncRequestedAt;
    }
    if (args.detectGpay !== undefined && args.detectGpay !== null) {
      this.detectGpay = args.detectGpay;
    }
    if (args.safetyNetEnabled !== undefined && args.safetyNetEnabled !== null) {
      this.safetyNetEnabled = args.safetyNetEnabled;
    }
  }
};
GlanceConfig.prototype = {};
GlanceConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.refreshIntervalInHrs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.wakeupMethod = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.configUpdateWindowInHrs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.configUpdateWindowMaxCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.partnerConfig = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.apiInitialDelayLimitInSecs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.preferredNetworkTypeForAnalytics = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.appAnalyticsAllowed = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I64) {
        this.userDataSyncRequestedAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.BOOL) {
        this.detectGpay = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.BOOL) {
        this.safetyNetEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceConfig.prototype.write = function(output) {
  output.writeStructBegin('GlanceConfig');
  if (this.refreshIntervalInHrs !== null && this.refreshIntervalInHrs !== undefined) {
    output.writeFieldBegin('refreshIntervalInHrs', Thrift.Type.I32, 1);
    output.writeI32(this.refreshIntervalInHrs);
    output.writeFieldEnd();
  }
  if (this.wakeupMethod !== null && this.wakeupMethod !== undefined) {
    output.writeFieldBegin('wakeupMethod', Thrift.Type.I32, 2);
    output.writeI32(this.wakeupMethod);
    output.writeFieldEnd();
  }
  if (this.configUpdateWindowInHrs !== null && this.configUpdateWindowInHrs !== undefined) {
    output.writeFieldBegin('configUpdateWindowInHrs', Thrift.Type.I32, 3);
    output.writeI32(this.configUpdateWindowInHrs);
    output.writeFieldEnd();
  }
  if (this.configUpdateWindowMaxCount !== null && this.configUpdateWindowMaxCount !== undefined) {
    output.writeFieldBegin('configUpdateWindowMaxCount', Thrift.Type.I32, 4);
    output.writeI32(this.configUpdateWindowMaxCount);
    output.writeFieldEnd();
  }
  if (this.partnerConfig !== null && this.partnerConfig !== undefined) {
    output.writeFieldBegin('partnerConfig', Thrift.Type.STRING, 5);
    output.writeString(this.partnerConfig);
    output.writeFieldEnd();
  }
  if (this.apiInitialDelayLimitInSecs !== null && this.apiInitialDelayLimitInSecs !== undefined) {
    output.writeFieldBegin('apiInitialDelayLimitInSecs', Thrift.Type.I32, 6);
    output.writeI32(this.apiInitialDelayLimitInSecs);
    output.writeFieldEnd();
  }
  if (this.preferredNetworkTypeForAnalytics !== null && this.preferredNetworkTypeForAnalytics !== undefined) {
    output.writeFieldBegin('preferredNetworkTypeForAnalytics', Thrift.Type.I32, 7);
    output.writeI32(this.preferredNetworkTypeForAnalytics);
    output.writeFieldEnd();
  }
  if (this.appAnalyticsAllowed !== null && this.appAnalyticsAllowed !== undefined) {
    output.writeFieldBegin('appAnalyticsAllowed', Thrift.Type.BOOL, 8);
    output.writeBool(this.appAnalyticsAllowed);
    output.writeFieldEnd();
  }
  if (this.userDataSyncRequestedAt !== null && this.userDataSyncRequestedAt !== undefined) {
    output.writeFieldBegin('userDataSyncRequestedAt', Thrift.Type.I64, 9);
    output.writeI64(this.userDataSyncRequestedAt);
    output.writeFieldEnd();
  }
  if (this.detectGpay !== null && this.detectGpay !== undefined) {
    output.writeFieldBegin('detectGpay', Thrift.Type.BOOL, 10);
    output.writeBool(this.detectGpay);
    output.writeFieldEnd();
  }
  if (this.safetyNetEnabled !== null && this.safetyNetEnabled !== undefined) {
    output.writeFieldBegin('safetyNetEnabled', Thrift.Type.BOOL, 11);
    output.writeBool(this.safetyNetEnabled);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceContentConfig = function(args) {
  this.repeatCount = null;
  this.wallpaperStoreSize = null;
  this.maxStoriesPerDay = null;
  this.maxWallpapersPerDay = null;
  this.refreshOnDataInitialDelayInHrs = null;
  this.refreshOnDataSubsequentDelayInHrs = null;
  this.refreshOnDataMaxStoriesCount = null;
  this.refreshOnDataMaxWallpapersCount = null;
  this.contentUpdateWindowInHrs = null;
  this.contentUpdateWindowMaxCount = null;
  this.maxParallelDownloads = null;
  this.glanceCategories = null;
  this.mainSlots = null;
  this.bingeSlots = null;
  this.sponsoredGlancesEnabled = null;
  this.shouldShowWallpapers = null;
  this.shareDetails = null;
  this.glanceLanguages = null;
  this.featureBankQuota = null;
  this.liveContentThreshold = null;
  this.useRewardedRender = null;
  this.useGameSplashRender = null;
  this.assetCleanupSize = null;
  this.allSponsoredSlots = null;
  this.backupFetchGlancePeriodInHrs = null;
  this.gamPgConfig = null;
  this.timeTargetedCampaignEnabled = null;
  if (args) {
    if (args.repeatCount !== undefined && args.repeatCount !== null) {
      this.repeatCount = args.repeatCount;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field repeatCount is unset!');
    }
    if (args.wallpaperStoreSize !== undefined && args.wallpaperStoreSize !== null) {
      this.wallpaperStoreSize = args.wallpaperStoreSize;
    }
    if (args.maxStoriesPerDay !== undefined && args.maxStoriesPerDay !== null) {
      this.maxStoriesPerDay = args.maxStoriesPerDay;
    }
    if (args.maxWallpapersPerDay !== undefined && args.maxWallpapersPerDay !== null) {
      this.maxWallpapersPerDay = args.maxWallpapersPerDay;
    }
    if (args.refreshOnDataInitialDelayInHrs !== undefined && args.refreshOnDataInitialDelayInHrs !== null) {
      this.refreshOnDataInitialDelayInHrs = args.refreshOnDataInitialDelayInHrs;
    }
    if (args.refreshOnDataSubsequentDelayInHrs !== undefined && args.refreshOnDataSubsequentDelayInHrs !== null) {
      this.refreshOnDataSubsequentDelayInHrs = args.refreshOnDataSubsequentDelayInHrs;
    }
    if (args.refreshOnDataMaxStoriesCount !== undefined && args.refreshOnDataMaxStoriesCount !== null) {
      this.refreshOnDataMaxStoriesCount = args.refreshOnDataMaxStoriesCount;
    }
    if (args.refreshOnDataMaxWallpapersCount !== undefined && args.refreshOnDataMaxWallpapersCount !== null) {
      this.refreshOnDataMaxWallpapersCount = args.refreshOnDataMaxWallpapersCount;
    }
    if (args.contentUpdateWindowInHrs !== undefined && args.contentUpdateWindowInHrs !== null) {
      this.contentUpdateWindowInHrs = args.contentUpdateWindowInHrs;
    }
    if (args.contentUpdateWindowMaxCount !== undefined && args.contentUpdateWindowMaxCount !== null) {
      this.contentUpdateWindowMaxCount = args.contentUpdateWindowMaxCount;
    }
    if (args.maxParallelDownloads !== undefined && args.maxParallelDownloads !== null) {
      this.maxParallelDownloads = args.maxParallelDownloads;
    }
    if (args.glanceCategories !== undefined && args.glanceCategories !== null) {
      this.glanceCategories = Thrift.copyList(args.glanceCategories, [null]);
    }
    if (args.mainSlots !== undefined && args.mainSlots !== null) {
      this.mainSlots = new GlanceSlots(args.mainSlots);
    }
    if (args.bingeSlots !== undefined && args.bingeSlots !== null) {
      this.bingeSlots = new GlanceSlots(args.bingeSlots);
    }
    if (args.sponsoredGlancesEnabled !== undefined && args.sponsoredGlancesEnabled !== null) {
      this.sponsoredGlancesEnabled = args.sponsoredGlancesEnabled;
    }
    if (args.shouldShowWallpapers !== undefined && args.shouldShowWallpapers !== null) {
      this.shouldShowWallpapers = args.shouldShowWallpapers;
    }
    if (args.shareDetails !== undefined && args.shareDetails !== null) {
      this.shareDetails = new ShareDetails(args.shareDetails);
    }
    if (args.glanceLanguages !== undefined && args.glanceLanguages !== null) {
      this.glanceLanguages = Thrift.copyList(args.glanceLanguages, [null]);
    }
    if (args.featureBankQuota !== undefined && args.featureBankQuota !== null) {
      this.featureBankQuota = args.featureBankQuota;
    }
    if (args.liveContentThreshold !== undefined && args.liveContentThreshold !== null) {
      this.liveContentThreshold = args.liveContentThreshold;
    }
    if (args.useRewardedRender !== undefined && args.useRewardedRender !== null) {
      this.useRewardedRender = args.useRewardedRender;
    }
    if (args.useGameSplashRender !== undefined && args.useGameSplashRender !== null) {
      this.useGameSplashRender = args.useGameSplashRender;
    }
    if (args.assetCleanupSize !== undefined && args.assetCleanupSize !== null) {
      this.assetCleanupSize = args.assetCleanupSize;
    }
    if (args.allSponsoredSlots !== undefined && args.allSponsoredSlots !== null) {
      this.allSponsoredSlots = new GlanceSlots(args.allSponsoredSlots);
    }
    if (args.backupFetchGlancePeriodInHrs !== undefined && args.backupFetchGlancePeriodInHrs !== null) {
      this.backupFetchGlancePeriodInHrs = args.backupFetchGlancePeriodInHrs;
    }
    if (args.gamPgConfig !== undefined && args.gamPgConfig !== null) {
      this.gamPgConfig = new GamPgConfig(args.gamPgConfig);
    }
    if (args.timeTargetedCampaignEnabled !== undefined && args.timeTargetedCampaignEnabled !== null) {
      this.timeTargetedCampaignEnabled = args.timeTargetedCampaignEnabled;
    }
  }
};
GlanceContentConfig.prototype = {};
GlanceContentConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.repeatCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.wallpaperStoreSize = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.maxStoriesPerDay = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.maxWallpapersPerDay = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.refreshOnDataInitialDelayInHrs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.refreshOnDataSubsequentDelayInHrs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.refreshOnDataMaxStoriesCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.refreshOnDataMaxWallpapersCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I32) {
        this.contentUpdateWindowInHrs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I32) {
        this.contentUpdateWindowMaxCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.I32) {
        this.maxParallelDownloads = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.LIST) {
        var _size218 = 0;
        var _rtmp3222;
        this.glanceCategories = [];
        var _etype221 = 0;
        _rtmp3222 = input.readListBegin();
        _etype221 = _rtmp3222.etype;
        _size218 = _rtmp3222.size;
        for (var _i223 = 0; _i223 < _size218; ++_i223)
        {
          var elem224 = null;
          elem224 = new GlanceCategory();
          elem224.read(input);
          this.glanceCategories.push(elem224);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRUCT) {
        this.mainSlots = new GlanceSlots();
        this.mainSlots.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRUCT) {
        this.bingeSlots = new GlanceSlots();
        this.bingeSlots.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.BOOL) {
        this.sponsoredGlancesEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldShowWallpapers = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRUCT) {
        this.shareDetails = new ShareDetails();
        this.shareDetails.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.LIST) {
        var _size225 = 0;
        var _rtmp3229;
        this.glanceLanguages = [];
        var _etype228 = 0;
        _rtmp3229 = input.readListBegin();
        _etype228 = _rtmp3229.etype;
        _size225 = _rtmp3229.size;
        for (var _i230 = 0; _i230 < _size225; ++_i230)
        {
          var elem231 = null;
          elem231 = new GlanceLanguage();
          elem231.read(input);
          this.glanceLanguages.push(elem231);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.I32) {
        this.featureBankQuota = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.I32) {
        this.liveContentThreshold = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.BOOL) {
        this.useRewardedRender = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.BOOL) {
        this.useGameSplashRender = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.I32) {
        this.assetCleanupSize = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.STRUCT) {
        this.allSponsoredSlots = new GlanceSlots();
        this.allSponsoredSlots.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.I32) {
        this.backupFetchGlancePeriodInHrs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.STRUCT) {
        this.gamPgConfig = new GamPgConfig();
        this.gamPgConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.BOOL) {
        this.timeTargetedCampaignEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceContentConfig.prototype.write = function(output) {
  output.writeStructBegin('GlanceContentConfig');
  if (this.repeatCount !== null && this.repeatCount !== undefined) {
    output.writeFieldBegin('repeatCount', Thrift.Type.I32, 1);
    output.writeI32(this.repeatCount);
    output.writeFieldEnd();
  }
  if (this.wallpaperStoreSize !== null && this.wallpaperStoreSize !== undefined) {
    output.writeFieldBegin('wallpaperStoreSize', Thrift.Type.I32, 2);
    output.writeI32(this.wallpaperStoreSize);
    output.writeFieldEnd();
  }
  if (this.maxStoriesPerDay !== null && this.maxStoriesPerDay !== undefined) {
    output.writeFieldBegin('maxStoriesPerDay', Thrift.Type.I32, 3);
    output.writeI32(this.maxStoriesPerDay);
    output.writeFieldEnd();
  }
  if (this.maxWallpapersPerDay !== null && this.maxWallpapersPerDay !== undefined) {
    output.writeFieldBegin('maxWallpapersPerDay', Thrift.Type.I32, 4);
    output.writeI32(this.maxWallpapersPerDay);
    output.writeFieldEnd();
  }
  if (this.refreshOnDataInitialDelayInHrs !== null && this.refreshOnDataInitialDelayInHrs !== undefined) {
    output.writeFieldBegin('refreshOnDataInitialDelayInHrs', Thrift.Type.I32, 5);
    output.writeI32(this.refreshOnDataInitialDelayInHrs);
    output.writeFieldEnd();
  }
  if (this.refreshOnDataSubsequentDelayInHrs !== null && this.refreshOnDataSubsequentDelayInHrs !== undefined) {
    output.writeFieldBegin('refreshOnDataSubsequentDelayInHrs', Thrift.Type.I32, 6);
    output.writeI32(this.refreshOnDataSubsequentDelayInHrs);
    output.writeFieldEnd();
  }
  if (this.refreshOnDataMaxStoriesCount !== null && this.refreshOnDataMaxStoriesCount !== undefined) {
    output.writeFieldBegin('refreshOnDataMaxStoriesCount', Thrift.Type.I32, 7);
    output.writeI32(this.refreshOnDataMaxStoriesCount);
    output.writeFieldEnd();
  }
  if (this.refreshOnDataMaxWallpapersCount !== null && this.refreshOnDataMaxWallpapersCount !== undefined) {
    output.writeFieldBegin('refreshOnDataMaxWallpapersCount', Thrift.Type.I32, 8);
    output.writeI32(this.refreshOnDataMaxWallpapersCount);
    output.writeFieldEnd();
  }
  if (this.contentUpdateWindowInHrs !== null && this.contentUpdateWindowInHrs !== undefined) {
    output.writeFieldBegin('contentUpdateWindowInHrs', Thrift.Type.I32, 9);
    output.writeI32(this.contentUpdateWindowInHrs);
    output.writeFieldEnd();
  }
  if (this.contentUpdateWindowMaxCount !== null && this.contentUpdateWindowMaxCount !== undefined) {
    output.writeFieldBegin('contentUpdateWindowMaxCount', Thrift.Type.I32, 10);
    output.writeI32(this.contentUpdateWindowMaxCount);
    output.writeFieldEnd();
  }
  if (this.maxParallelDownloads !== null && this.maxParallelDownloads !== undefined) {
    output.writeFieldBegin('maxParallelDownloads', Thrift.Type.I32, 11);
    output.writeI32(this.maxParallelDownloads);
    output.writeFieldEnd();
  }
  if (this.glanceCategories !== null && this.glanceCategories !== undefined) {
    output.writeFieldBegin('glanceCategories', Thrift.Type.LIST, 12);
    output.writeListBegin(Thrift.Type.STRUCT, this.glanceCategories.length);
    for (var iter232 in this.glanceCategories)
    {
      if (this.glanceCategories.hasOwnProperty(iter232))
      {
        iter232 = this.glanceCategories[iter232];
        iter232.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.mainSlots !== null && this.mainSlots !== undefined) {
    output.writeFieldBegin('mainSlots', Thrift.Type.STRUCT, 13);
    this.mainSlots.write(output);
    output.writeFieldEnd();
  }
  if (this.bingeSlots !== null && this.bingeSlots !== undefined) {
    output.writeFieldBegin('bingeSlots', Thrift.Type.STRUCT, 14);
    this.bingeSlots.write(output);
    output.writeFieldEnd();
  }
  if (this.sponsoredGlancesEnabled !== null && this.sponsoredGlancesEnabled !== undefined) {
    output.writeFieldBegin('sponsoredGlancesEnabled', Thrift.Type.BOOL, 15);
    output.writeBool(this.sponsoredGlancesEnabled);
    output.writeFieldEnd();
  }
  if (this.shouldShowWallpapers !== null && this.shouldShowWallpapers !== undefined) {
    output.writeFieldBegin('shouldShowWallpapers', Thrift.Type.BOOL, 16);
    output.writeBool(this.shouldShowWallpapers);
    output.writeFieldEnd();
  }
  if (this.shareDetails !== null && this.shareDetails !== undefined) {
    output.writeFieldBegin('shareDetails', Thrift.Type.STRUCT, 17);
    this.shareDetails.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceLanguages !== null && this.glanceLanguages !== undefined) {
    output.writeFieldBegin('glanceLanguages', Thrift.Type.LIST, 18);
    output.writeListBegin(Thrift.Type.STRUCT, this.glanceLanguages.length);
    for (var iter233 in this.glanceLanguages)
    {
      if (this.glanceLanguages.hasOwnProperty(iter233))
      {
        iter233 = this.glanceLanguages[iter233];
        iter233.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.featureBankQuota !== null && this.featureBankQuota !== undefined) {
    output.writeFieldBegin('featureBankQuota', Thrift.Type.I32, 19);
    output.writeI32(this.featureBankQuota);
    output.writeFieldEnd();
  }
  if (this.liveContentThreshold !== null && this.liveContentThreshold !== undefined) {
    output.writeFieldBegin('liveContentThreshold', Thrift.Type.I32, 20);
    output.writeI32(this.liveContentThreshold);
    output.writeFieldEnd();
  }
  if (this.useRewardedRender !== null && this.useRewardedRender !== undefined) {
    output.writeFieldBegin('useRewardedRender', Thrift.Type.BOOL, 21);
    output.writeBool(this.useRewardedRender);
    output.writeFieldEnd();
  }
  if (this.useGameSplashRender !== null && this.useGameSplashRender !== undefined) {
    output.writeFieldBegin('useGameSplashRender', Thrift.Type.BOOL, 22);
    output.writeBool(this.useGameSplashRender);
    output.writeFieldEnd();
  }
  if (this.assetCleanupSize !== null && this.assetCleanupSize !== undefined) {
    output.writeFieldBegin('assetCleanupSize', Thrift.Type.I32, 23);
    output.writeI32(this.assetCleanupSize);
    output.writeFieldEnd();
  }
  if (this.allSponsoredSlots !== null && this.allSponsoredSlots !== undefined) {
    output.writeFieldBegin('allSponsoredSlots', Thrift.Type.STRUCT, 24);
    this.allSponsoredSlots.write(output);
    output.writeFieldEnd();
  }
  if (this.backupFetchGlancePeriodInHrs !== null && this.backupFetchGlancePeriodInHrs !== undefined) {
    output.writeFieldBegin('backupFetchGlancePeriodInHrs', Thrift.Type.I32, 25);
    output.writeI32(this.backupFetchGlancePeriodInHrs);
    output.writeFieldEnd();
  }
  if (this.gamPgConfig !== null && this.gamPgConfig !== undefined) {
    output.writeFieldBegin('gamPgConfig', Thrift.Type.STRUCT, 26);
    this.gamPgConfig.write(output);
    output.writeFieldEnd();
  }
  if (this.timeTargetedCampaignEnabled !== null && this.timeTargetedCampaignEnabled !== undefined) {
    output.writeFieldBegin('timeTargetedCampaignEnabled', Thrift.Type.BOOL, 27);
    output.writeBool(this.timeTargetedCampaignEnabled);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceLanguage = function(args) {
  this.id = null;
  this.displayName = null;
  this.defaultSubscription = null;
  this.isSubscriptionModifiable = null;
  this.imageUrl = null;
  this.isNew = null;
  this.appLanguageImageUrl = null;
  this.isSupportedAppLanguage = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.displayName !== undefined && args.displayName !== null) {
      this.displayName = args.displayName;
    }
    if (args.defaultSubscription !== undefined && args.defaultSubscription !== null) {
      this.defaultSubscription = args.defaultSubscription;
    }
    if (args.isSubscriptionModifiable !== undefined && args.isSubscriptionModifiable !== null) {
      this.isSubscriptionModifiable = args.isSubscriptionModifiable;
    }
    if (args.imageUrl !== undefined && args.imageUrl !== null) {
      this.imageUrl = args.imageUrl;
    }
    if (args.isNew !== undefined && args.isNew !== null) {
      this.isNew = args.isNew;
    }
    if (args.appLanguageImageUrl !== undefined && args.appLanguageImageUrl !== null) {
      this.appLanguageImageUrl = args.appLanguageImageUrl;
    }
    if (args.isSupportedAppLanguage !== undefined && args.isSupportedAppLanguage !== null) {
      this.isSupportedAppLanguage = args.isSupportedAppLanguage;
    }
  }
};
GlanceLanguage.prototype = {};
GlanceLanguage.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.displayName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.defaultSubscription = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.isSubscriptionModifiable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.imageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.isNew = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.appLanguageImageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.isSupportedAppLanguage = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceLanguage.prototype.write = function(output) {
  output.writeStructBegin('GlanceLanguage');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.displayName !== null && this.displayName !== undefined) {
    output.writeFieldBegin('displayName', Thrift.Type.STRING, 2);
    output.writeString(this.displayName);
    output.writeFieldEnd();
  }
  if (this.defaultSubscription !== null && this.defaultSubscription !== undefined) {
    output.writeFieldBegin('defaultSubscription', Thrift.Type.BOOL, 3);
    output.writeBool(this.defaultSubscription);
    output.writeFieldEnd();
  }
  if (this.isSubscriptionModifiable !== null && this.isSubscriptionModifiable !== undefined) {
    output.writeFieldBegin('isSubscriptionModifiable', Thrift.Type.BOOL, 4);
    output.writeBool(this.isSubscriptionModifiable);
    output.writeFieldEnd();
  }
  if (this.imageUrl !== null && this.imageUrl !== undefined) {
    output.writeFieldBegin('imageUrl', Thrift.Type.STRING, 5);
    output.writeString(this.imageUrl);
    output.writeFieldEnd();
  }
  if (this.isNew !== null && this.isNew !== undefined) {
    output.writeFieldBegin('isNew', Thrift.Type.BOOL, 6);
    output.writeBool(this.isNew);
    output.writeFieldEnd();
  }
  if (this.appLanguageImageUrl !== null && this.appLanguageImageUrl !== undefined) {
    output.writeFieldBegin('appLanguageImageUrl', Thrift.Type.STRING, 7);
    output.writeString(this.appLanguageImageUrl);
    output.writeFieldEnd();
  }
  if (this.isSupportedAppLanguage !== null && this.isSupportedAppLanguage !== undefined) {
    output.writeFieldBegin('isSupportedAppLanguage', Thrift.Type.BOOL, 8);
    output.writeBool(this.isSupportedAppLanguage);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceAdConfig = function(args) {
  this.adsEnabled = null;
  this.callsOnData = null;
  this.assetsOnData = null;
  this.maxCallsOnData = null;
  this.maxAssetsOnData = null;
  this.useStrictModeOnData = null;
  this.loadPercentage = null;
  this.callTimeoutInSecs = null;
  this.accountId = null;
  this.placementId = null;
  this.bulkFetchCount = null;
  this.enableAfterInHrs = null;
  this.maxShowOnData = null;
  if (args) {
    if (args.adsEnabled !== undefined && args.adsEnabled !== null) {
      this.adsEnabled = args.adsEnabled;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field adsEnabled is unset!');
    }
    if (args.callsOnData !== undefined && args.callsOnData !== null) {
      this.callsOnData = args.callsOnData;
    }
    if (args.assetsOnData !== undefined && args.assetsOnData !== null) {
      this.assetsOnData = args.assetsOnData;
    }
    if (args.maxCallsOnData !== undefined && args.maxCallsOnData !== null) {
      this.maxCallsOnData = args.maxCallsOnData;
    }
    if (args.maxAssetsOnData !== undefined && args.maxAssetsOnData !== null) {
      this.maxAssetsOnData = args.maxAssetsOnData;
    }
    if (args.useStrictModeOnData !== undefined && args.useStrictModeOnData !== null) {
      this.useStrictModeOnData = args.useStrictModeOnData;
    }
    if (args.loadPercentage !== undefined && args.loadPercentage !== null) {
      this.loadPercentage = args.loadPercentage;
    }
    if (args.callTimeoutInSecs !== undefined && args.callTimeoutInSecs !== null) {
      this.callTimeoutInSecs = args.callTimeoutInSecs;
    }
    if (args.accountId !== undefined && args.accountId !== null) {
      this.accountId = args.accountId;
    }
    if (args.placementId !== undefined && args.placementId !== null) {
      this.placementId = args.placementId;
    }
    if (args.bulkFetchCount !== undefined && args.bulkFetchCount !== null) {
      this.bulkFetchCount = args.bulkFetchCount;
    }
    if (args.enableAfterInHrs !== undefined && args.enableAfterInHrs !== null) {
      this.enableAfterInHrs = args.enableAfterInHrs;
    }
    if (args.maxShowOnData !== undefined && args.maxShowOnData !== null) {
      this.maxShowOnData = args.maxShowOnData;
    }
  }
};
GlanceAdConfig.prototype = {};
GlanceAdConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.adsEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.callsOnData = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.assetsOnData = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.maxCallsOnData = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.maxAssetsOnData = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.useStrictModeOnData = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.loadPercentage = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.callTimeoutInSecs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.accountId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I64) {
        this.placementId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.I32) {
        this.bulkFetchCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I32) {
        this.enableAfterInHrs = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.I32) {
        this.maxShowOnData = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceAdConfig.prototype.write = function(output) {
  output.writeStructBegin('GlanceAdConfig');
  if (this.adsEnabled !== null && this.adsEnabled !== undefined) {
    output.writeFieldBegin('adsEnabled', Thrift.Type.BOOL, 1);
    output.writeBool(this.adsEnabled);
    output.writeFieldEnd();
  }
  if (this.callsOnData !== null && this.callsOnData !== undefined) {
    output.writeFieldBegin('callsOnData', Thrift.Type.BOOL, 2);
    output.writeBool(this.callsOnData);
    output.writeFieldEnd();
  }
  if (this.assetsOnData !== null && this.assetsOnData !== undefined) {
    output.writeFieldBegin('assetsOnData', Thrift.Type.BOOL, 3);
    output.writeBool(this.assetsOnData);
    output.writeFieldEnd();
  }
  if (this.maxCallsOnData !== null && this.maxCallsOnData !== undefined) {
    output.writeFieldBegin('maxCallsOnData', Thrift.Type.I32, 4);
    output.writeI32(this.maxCallsOnData);
    output.writeFieldEnd();
  }
  if (this.maxAssetsOnData !== null && this.maxAssetsOnData !== undefined) {
    output.writeFieldBegin('maxAssetsOnData', Thrift.Type.I32, 5);
    output.writeI32(this.maxAssetsOnData);
    output.writeFieldEnd();
  }
  if (this.useStrictModeOnData !== null && this.useStrictModeOnData !== undefined) {
    output.writeFieldBegin('useStrictModeOnData', Thrift.Type.BOOL, 6);
    output.writeBool(this.useStrictModeOnData);
    output.writeFieldEnd();
  }
  if (this.loadPercentage !== null && this.loadPercentage !== undefined) {
    output.writeFieldBegin('loadPercentage', Thrift.Type.I32, 7);
    output.writeI32(this.loadPercentage);
    output.writeFieldEnd();
  }
  if (this.callTimeoutInSecs !== null && this.callTimeoutInSecs !== undefined) {
    output.writeFieldBegin('callTimeoutInSecs', Thrift.Type.I32, 8);
    output.writeI32(this.callTimeoutInSecs);
    output.writeFieldEnd();
  }
  if (this.accountId !== null && this.accountId !== undefined) {
    output.writeFieldBegin('accountId', Thrift.Type.STRING, 9);
    output.writeString(this.accountId);
    output.writeFieldEnd();
  }
  if (this.placementId !== null && this.placementId !== undefined) {
    output.writeFieldBegin('placementId', Thrift.Type.I64, 10);
    output.writeI64(this.placementId);
    output.writeFieldEnd();
  }
  if (this.bulkFetchCount !== null && this.bulkFetchCount !== undefined) {
    output.writeFieldBegin('bulkFetchCount', Thrift.Type.I32, 11);
    output.writeI32(this.bulkFetchCount);
    output.writeFieldEnd();
  }
  if (this.enableAfterInHrs !== null && this.enableAfterInHrs !== undefined) {
    output.writeFieldBegin('enableAfterInHrs', Thrift.Type.I32, 12);
    output.writeI32(this.enableAfterInHrs);
    output.writeFieldEnd();
  }
  if (this.maxShowOnData !== null && this.maxShowOnData !== undefined) {
    output.writeFieldBegin('maxShowOnData', Thrift.Type.I32, 13);
    output.writeI32(this.maxShowOnData);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceGlobalConfig = function(args) {
  this.contentConfig = null;
  this.adConfig = null;
  if (args) {
    if (args.contentConfig !== undefined && args.contentConfig !== null) {
      this.contentConfig = new GlanceContentConfig(args.contentConfig);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field contentConfig is unset!');
    }
    if (args.adConfig !== undefined && args.adConfig !== null) {
      this.adConfig = new GlanceAdConfig(args.adConfig);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field adConfig is unset!');
    }
  }
};
GlanceGlobalConfig.prototype = {};
GlanceGlobalConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.contentConfig = new GlanceContentConfig();
        this.contentConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.adConfig = new GlanceAdConfig();
        this.adConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceGlobalConfig.prototype.write = function(output) {
  output.writeStructBegin('GlanceGlobalConfig');
  if (this.contentConfig !== null && this.contentConfig !== undefined) {
    output.writeFieldBegin('contentConfig', Thrift.Type.STRUCT, 1);
    this.contentConfig.write(output);
    output.writeFieldEnd();
  }
  if (this.adConfig !== null && this.adConfig !== undefined) {
    output.writeFieldBegin('adConfig', Thrift.Type.STRUCT, 2);
    this.adConfig.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ImageMeta = function(args) {
  this.type = null;
  this.width = null;
  this.height = null;
  this.filesize = null;
  this.md5Hash = null;
  this.encoding = null;
  this.checksum = null;
  if (args) {
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field type is unset!');
    }
    if (args.width !== undefined && args.width !== null) {
      this.width = args.width;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field width is unset!');
    }
    if (args.height !== undefined && args.height !== null) {
      this.height = args.height;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field height is unset!');
    }
    if (args.filesize !== undefined && args.filesize !== null) {
      this.filesize = args.filesize;
    }
    if (args.md5Hash !== undefined && args.md5Hash !== null) {
      this.md5Hash = args.md5Hash;
    }
    if (args.encoding !== undefined && args.encoding !== null) {
      this.encoding = args.encoding;
    }
    if (args.checksum !== undefined && args.checksum !== null) {
      this.checksum = Thrift.copyMap(args.checksum, [null]);
    }
  }
};
ImageMeta.prototype = {};
ImageMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.width = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.height = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.filesize = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.md5Hash = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.encoding = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.MAP) {
        var _size234 = 0;
        var _rtmp3238;
        this.checksum = {};
        var _ktype235 = 0;
        var _vtype236 = 0;
        _rtmp3238 = input.readMapBegin();
        _ktype235 = _rtmp3238.ktype;
        _vtype236 = _rtmp3238.vtype;
        _size234 = _rtmp3238.size;
        for (var _i239 = 0; _i239 < _size234; ++_i239)
        {
          if (_i239 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key240 = null;
          var val241 = null;
          key240 = input.readString().value;
          val241 = input.readString().value;
          this.checksum[key240] = val241;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ImageMeta.prototype.write = function(output) {
  output.writeStructBegin('ImageMeta');
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.I32, 1);
    output.writeI32(this.type);
    output.writeFieldEnd();
  }
  if (this.width !== null && this.width !== undefined) {
    output.writeFieldBegin('width', Thrift.Type.I32, 2);
    output.writeI32(this.width);
    output.writeFieldEnd();
  }
  if (this.height !== null && this.height !== undefined) {
    output.writeFieldBegin('height', Thrift.Type.I32, 3);
    output.writeI32(this.height);
    output.writeFieldEnd();
  }
  if (this.filesize !== null && this.filesize !== undefined) {
    output.writeFieldBegin('filesize', Thrift.Type.I64, 4);
    output.writeI64(this.filesize);
    output.writeFieldEnd();
  }
  if (this.md5Hash !== null && this.md5Hash !== undefined) {
    output.writeFieldBegin('md5Hash', Thrift.Type.STRING, 5);
    output.writeString(this.md5Hash);
    output.writeFieldEnd();
  }
  if (this.encoding !== null && this.encoding !== undefined) {
    output.writeFieldBegin('encoding', Thrift.Type.I32, 6);
    output.writeI32(this.encoding);
    output.writeFieldEnd();
  }
  if (this.checksum !== null && this.checksum !== undefined) {
    output.writeFieldBegin('checksum', Thrift.Type.MAP, 7);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.STRING, Thrift.objectLength(this.checksum));
    for (var kiter242 in this.checksum)
    {
      if (this.checksum.hasOwnProperty(kiter242))
      {
        var viter243 = this.checksum[kiter242];
        output.writeString(kiter242);
        output.writeString(viter243);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RoposoGlance = function(args) {
  this.gpidToGlaneIds = null;
  this.lastUpdatedAtInSec = null;
  if (args) {
    if (args.gpidToGlaneIds !== undefined && args.gpidToGlaneIds !== null) {
      this.gpidToGlaneIds = Thrift.copyMap(args.gpidToGlaneIds, [Thrift.copyList, null]);
    }
    if (args.lastUpdatedAtInSec !== undefined && args.lastUpdatedAtInSec !== null) {
      this.lastUpdatedAtInSec = args.lastUpdatedAtInSec;
    }
  }
};
RoposoGlance.prototype = {};
RoposoGlance.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.MAP) {
        var _size244 = 0;
        var _rtmp3248;
        this.gpidToGlaneIds = {};
        var _ktype245 = 0;
        var _vtype246 = 0;
        _rtmp3248 = input.readMapBegin();
        _ktype245 = _rtmp3248.ktype;
        _vtype246 = _rtmp3248.vtype;
        _size244 = _rtmp3248.size;
        for (var _i249 = 0; _i249 < _size244; ++_i249)
        {
          if (_i249 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key250 = null;
          var val251 = null;
          key250 = input.readString().value;
          var _size252 = 0;
          var _rtmp3256;
          val251 = [];
          var _etype255 = 0;
          _rtmp3256 = input.readListBegin();
          _etype255 = _rtmp3256.etype;
          _size252 = _rtmp3256.size;
          for (var _i257 = 0; _i257 < _size252; ++_i257)
          {
            var elem258 = null;
            elem258 = input.readString().value;
            val251.push(elem258);
          }
          input.readListEnd();
          this.gpidToGlaneIds[key250] = val251;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.lastUpdatedAtInSec = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RoposoGlance.prototype.write = function(output) {
  output.writeStructBegin('RoposoGlance');
  if (this.gpidToGlaneIds !== null && this.gpidToGlaneIds !== undefined) {
    output.writeFieldBegin('gpidToGlaneIds', Thrift.Type.MAP, 1);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.LIST, Thrift.objectLength(this.gpidToGlaneIds));
    for (var kiter259 in this.gpidToGlaneIds)
    {
      if (this.gpidToGlaneIds.hasOwnProperty(kiter259))
      {
        var viter260 = this.gpidToGlaneIds[kiter259];
        output.writeString(kiter259);
        output.writeListBegin(Thrift.Type.STRING, viter260.length);
        for (var iter261 in viter260)
        {
          if (viter260.hasOwnProperty(iter261))
          {
            iter261 = viter260[iter261];
            output.writeString(iter261);
          }
        }
        output.writeListEnd();
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.lastUpdatedAtInSec !== null && this.lastUpdatedAtInSec !== undefined) {
    output.writeFieldBegin('lastUpdatedAtInSec', Thrift.Type.I64, 2);
    output.writeI64(this.lastUpdatedAtInSec);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Image = function(args) {
  this.id = null;
  this.createdAtInSecs = null;
  this.imageMetas = null;
  this.attribution = null;
  this.blur = null;
  this.version = null;
  this.dominantColor = null;
  this.palette = null;
  this.isBeautifulWallpaper = null;
  this.isBeautifulWallpaperActive = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field createdAtInSecs is unset!');
    }
    if (args.imageMetas !== undefined && args.imageMetas !== null) {
      this.imageMetas = Thrift.copyList(args.imageMetas, [ImageMeta]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field imageMetas is unset!');
    }
    if (args.attribution !== undefined && args.attribution !== null) {
      this.attribution = new AttributionDetails(args.attribution);
    }
    if (args.blur !== undefined && args.blur !== null) {
      this.blur = args.blur;
    }
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
    if (args.dominantColor !== undefined && args.dominantColor !== null) {
      this.dominantColor = args.dominantColor;
    }
    if (args.palette !== undefined && args.palette !== null) {
      this.palette = Thrift.copyList(args.palette, [null]);
    }
    if (args.isBeautifulWallpaper !== undefined && args.isBeautifulWallpaper !== null) {
      this.isBeautifulWallpaper = args.isBeautifulWallpaper;
    }
    if (args.isBeautifulWallpaperActive !== undefined && args.isBeautifulWallpaperActive !== null) {
      this.isBeautifulWallpaperActive = args.isBeautifulWallpaperActive;
    }
  }
};
Image.prototype = {};
Image.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size262 = 0;
        var _rtmp3266;
        this.imageMetas = [];
        var _etype265 = 0;
        _rtmp3266 = input.readListBegin();
        _etype265 = _rtmp3266.etype;
        _size262 = _rtmp3266.size;
        for (var _i267 = 0; _i267 < _size262; ++_i267)
        {
          var elem268 = null;
          elem268 = new ImageMeta();
          elem268.read(input);
          this.imageMetas.push(elem268);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.attribution = new AttributionDetails();
        this.attribution.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.blur = input.readBinary().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.version = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.dominantColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size269 = 0;
        var _rtmp3273;
        this.palette = [];
        var _etype272 = 0;
        _rtmp3273 = input.readListBegin();
        _etype272 = _rtmp3273.etype;
        _size269 = _rtmp3273.size;
        for (var _i274 = 0; _i274 < _size269; ++_i274)
        {
          var elem275 = null;
          elem275 = input.readString().value;
          this.palette.push(elem275);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.BOOL) {
        this.isBeautifulWallpaper = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.BOOL) {
        this.isBeautifulWallpaperActive = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Image.prototype.write = function(output) {
  output.writeStructBegin('Image');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.imageMetas !== null && this.imageMetas !== undefined) {
    output.writeFieldBegin('imageMetas', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.imageMetas.length);
    for (var iter276 in this.imageMetas)
    {
      if (this.imageMetas.hasOwnProperty(iter276))
      {
        iter276 = this.imageMetas[iter276];
        iter276.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.attribution !== null && this.attribution !== undefined) {
    output.writeFieldBegin('attribution', Thrift.Type.STRUCT, 4);
    this.attribution.write(output);
    output.writeFieldEnd();
  }
  if (this.blur !== null && this.blur !== undefined) {
    output.writeFieldBegin('blur', Thrift.Type.STRING, 5);
    output.writeBinary(this.blur);
    output.writeFieldEnd();
  }
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.I64, 6);
    output.writeI64(this.version);
    output.writeFieldEnd();
  }
  if (this.dominantColor !== null && this.dominantColor !== undefined) {
    output.writeFieldBegin('dominantColor', Thrift.Type.STRING, 7);
    output.writeString(this.dominantColor);
    output.writeFieldEnd();
  }
  if (this.palette !== null && this.palette !== undefined) {
    output.writeFieldBegin('palette', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRING, this.palette.length);
    for (var iter277 in this.palette)
    {
      if (this.palette.hasOwnProperty(iter277))
      {
        iter277 = this.palette[iter277];
        output.writeString(iter277);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.isBeautifulWallpaper !== null && this.isBeautifulWallpaper !== undefined) {
    output.writeFieldBegin('isBeautifulWallpaper', Thrift.Type.BOOL, 9);
    output.writeBool(this.isBeautifulWallpaper);
    output.writeFieldEnd();
  }
  if (this.isBeautifulWallpaperActive !== null && this.isBeautifulWallpaperActive !== undefined) {
    output.writeFieldBegin('isBeautifulWallpaperActive', Thrift.Type.BOOL, 10);
    output.writeBool(this.isBeautifulWallpaperActive);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

InternalImage = function(args) {
  this.id = null;
  this.imageId = null;
  this.createdAtInSecs = null;
  this.imageMetas = null;
  this.attribution = null;
  this.blur = null;
  this.version = null;
  this.updatedAt = null;
  this.isBeautifulWallpaper = null;
  this.isBeautifulWallpaperActive = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.imageId !== undefined && args.imageId !== null) {
      this.imageId = args.imageId;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.imageMetas !== undefined && args.imageMetas !== null) {
      this.imageMetas = Thrift.copyList(args.imageMetas, [ImageMeta]);
    }
    if (args.attribution !== undefined && args.attribution !== null) {
      this.attribution = new AttributionDetails(args.attribution);
    }
    if (args.blur !== undefined && args.blur !== null) {
      this.blur = args.blur;
    }
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.isBeautifulWallpaper !== undefined && args.isBeautifulWallpaper !== null) {
      this.isBeautifulWallpaper = args.isBeautifulWallpaper;
    }
    if (args.isBeautifulWallpaperActive !== undefined && args.isBeautifulWallpaperActive !== null) {
      this.isBeautifulWallpaperActive = args.isBeautifulWallpaperActive;
    }
  }
};
InternalImage.prototype = {};
InternalImage.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.id = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.imageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size278 = 0;
        var _rtmp3282;
        this.imageMetas = [];
        var _etype281 = 0;
        _rtmp3282 = input.readListBegin();
        _etype281 = _rtmp3282.etype;
        _size278 = _rtmp3282.size;
        for (var _i283 = 0; _i283 < _size278; ++_i283)
        {
          var elem284 = null;
          elem284 = new ImageMeta();
          elem284.read(input);
          this.imageMetas.push(elem284);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.attribution = new AttributionDetails();
        this.attribution.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.blur = input.readBinary().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I64) {
        this.version = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.BOOL) {
        this.isBeautifulWallpaper = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.BOOL) {
        this.isBeautifulWallpaperActive = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

InternalImage.prototype.write = function(output) {
  output.writeStructBegin('InternalImage');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I64, 1);
    output.writeI64(this.id);
    output.writeFieldEnd();
  }
  if (this.imageId !== null && this.imageId !== undefined) {
    output.writeFieldBegin('imageId', Thrift.Type.STRING, 2);
    output.writeString(this.imageId);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 3);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.imageMetas !== null && this.imageMetas !== undefined) {
    output.writeFieldBegin('imageMetas', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRUCT, this.imageMetas.length);
    for (var iter285 in this.imageMetas)
    {
      if (this.imageMetas.hasOwnProperty(iter285))
      {
        iter285 = this.imageMetas[iter285];
        iter285.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.attribution !== null && this.attribution !== undefined) {
    output.writeFieldBegin('attribution', Thrift.Type.STRUCT, 5);
    this.attribution.write(output);
    output.writeFieldEnd();
  }
  if (this.blur !== null && this.blur !== undefined) {
    output.writeFieldBegin('blur', Thrift.Type.STRING, 6);
    output.writeBinary(this.blur);
    output.writeFieldEnd();
  }
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.I64, 7);
    output.writeI64(this.version);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 8);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.isBeautifulWallpaper !== null && this.isBeautifulWallpaper !== undefined) {
    output.writeFieldBegin('isBeautifulWallpaper', Thrift.Type.BOOL, 9);
    output.writeBool(this.isBeautifulWallpaper);
    output.writeFieldEnd();
  }
  if (this.isBeautifulWallpaperActive !== null && this.isBeautifulWallpaperActive !== undefined) {
    output.writeFieldBegin('isBeautifulWallpaperActive', Thrift.Type.BOOL, 10);
    output.writeBool(this.isBeautifulWallpaperActive);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UserLifeTimeFilter = function(args) {
  this.startDay = null;
  this.endDay = null;
  if (args) {
    if (args.startDay !== undefined && args.startDay !== null) {
      this.startDay = args.startDay;
    }
    if (args.endDay !== undefined && args.endDay !== null) {
      this.endDay = args.endDay;
    }
  }
};
UserLifeTimeFilter.prototype = {};
UserLifeTimeFilter.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.startDay = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.endDay = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserLifeTimeFilter.prototype.write = function(output) {
  output.writeStructBegin('UserLifeTimeFilter');
  if (this.startDay !== null && this.startDay !== undefined) {
    output.writeFieldBegin('startDay', Thrift.Type.I64, 1);
    output.writeI64(this.startDay);
    output.writeFieldEnd();
  }
  if (this.endDay !== null && this.endDay !== undefined) {
    output.writeFieldBegin('endDay', Thrift.Type.I64, 2);
    output.writeI64(this.endDay);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

StudioCategory = function(args) {
  this.id = null;
  this.displayName = null;
  this.languageId = null;
  this.regions = null;
  this.excludedRegions = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.displayName !== undefined && args.displayName !== null) {
      this.displayName = args.displayName;
    }
    if (args.languageId !== undefined && args.languageId !== null) {
      this.languageId = args.languageId;
    }
    if (args.regions !== undefined && args.regions !== null) {
      this.regions = Thrift.copyList(args.regions, [null]);
    }
    if (args.excludedRegions !== undefined && args.excludedRegions !== null) {
      this.excludedRegions = Thrift.copyList(args.excludedRegions, [null]);
    }
  }
};
StudioCategory.prototype = {};
StudioCategory.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.displayName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.languageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size286 = 0;
        var _rtmp3290;
        this.regions = [];
        var _etype289 = 0;
        _rtmp3290 = input.readListBegin();
        _etype289 = _rtmp3290.etype;
        _size286 = _rtmp3290.size;
        for (var _i291 = 0; _i291 < _size286; ++_i291)
        {
          var elem292 = null;
          elem292 = input.readI32().value;
          this.regions.push(elem292);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size293 = 0;
        var _rtmp3297;
        this.excludedRegions = [];
        var _etype296 = 0;
        _rtmp3297 = input.readListBegin();
        _etype296 = _rtmp3297.etype;
        _size293 = _rtmp3297.size;
        for (var _i298 = 0; _i298 < _size293; ++_i298)
        {
          var elem299 = null;
          elem299 = input.readI32().value;
          this.excludedRegions.push(elem299);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

StudioCategory.prototype.write = function(output) {
  output.writeStructBegin('StudioCategory');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.displayName !== null && this.displayName !== undefined) {
    output.writeFieldBegin('displayName', Thrift.Type.STRING, 2);
    output.writeString(this.displayName);
    output.writeFieldEnd();
  }
  if (this.languageId !== null && this.languageId !== undefined) {
    output.writeFieldBegin('languageId', Thrift.Type.STRING, 3);
    output.writeString(this.languageId);
    output.writeFieldEnd();
  }
  if (this.regions !== null && this.regions !== undefined) {
    output.writeFieldBegin('regions', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.I32, this.regions.length);
    for (var iter300 in this.regions)
    {
      if (this.regions.hasOwnProperty(iter300))
      {
        iter300 = this.regions[iter300];
        output.writeI32(iter300);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.excludedRegions !== null && this.excludedRegions !== undefined) {
    output.writeFieldBegin('excludedRegions', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.I32, this.excludedRegions.length);
    for (var iter301 in this.excludedRegions)
    {
      if (this.excludedRegions.hasOwnProperty(iter301))
      {
        iter301 = this.excludedRegions[iter301];
        output.writeI32(iter301);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceCategory = function(args) {
  this.id = null;
  this.displayName = null;
  this.defaultSubscription = null;
  this.isSubscriptionModifiable = null;
  this.imageUrl = null;
  this.categoryLanguageId = null;
  this.vernacularNameMap = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.displayName !== undefined && args.displayName !== null) {
      this.displayName = args.displayName;
    }
    if (args.defaultSubscription !== undefined && args.defaultSubscription !== null) {
      this.defaultSubscription = args.defaultSubscription;
    }
    if (args.isSubscriptionModifiable !== undefined && args.isSubscriptionModifiable !== null) {
      this.isSubscriptionModifiable = args.isSubscriptionModifiable;
    }
    if (args.imageUrl !== undefined && args.imageUrl !== null) {
      this.imageUrl = args.imageUrl;
    }
    if (args.categoryLanguageId !== undefined && args.categoryLanguageId !== null) {
      this.categoryLanguageId = args.categoryLanguageId;
    }
    if (args.vernacularNameMap !== undefined && args.vernacularNameMap !== null) {
      this.vernacularNameMap = Thrift.copyMap(args.vernacularNameMap, [null]);
    }
  }
};
GlanceCategory.prototype = {};
GlanceCategory.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.displayName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.defaultSubscription = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.isSubscriptionModifiable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.imageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.categoryLanguageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.MAP) {
        var _size302 = 0;
        var _rtmp3306;
        this.vernacularNameMap = {};
        var _ktype303 = 0;
        var _vtype304 = 0;
        _rtmp3306 = input.readMapBegin();
        _ktype303 = _rtmp3306.ktype;
        _vtype304 = _rtmp3306.vtype;
        _size302 = _rtmp3306.size;
        for (var _i307 = 0; _i307 < _size302; ++_i307)
        {
          if (_i307 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key308 = null;
          var val309 = null;
          key308 = input.readString().value;
          val309 = input.readString().value;
          this.vernacularNameMap[key308] = val309;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceCategory.prototype.write = function(output) {
  output.writeStructBegin('GlanceCategory');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.displayName !== null && this.displayName !== undefined) {
    output.writeFieldBegin('displayName', Thrift.Type.STRING, 2);
    output.writeString(this.displayName);
    output.writeFieldEnd();
  }
  if (this.defaultSubscription !== null && this.defaultSubscription !== undefined) {
    output.writeFieldBegin('defaultSubscription', Thrift.Type.BOOL, 3);
    output.writeBool(this.defaultSubscription);
    output.writeFieldEnd();
  }
  if (this.isSubscriptionModifiable !== null && this.isSubscriptionModifiable !== undefined) {
    output.writeFieldBegin('isSubscriptionModifiable', Thrift.Type.BOOL, 4);
    output.writeBool(this.isSubscriptionModifiable);
    output.writeFieldEnd();
  }
  if (this.imageUrl !== null && this.imageUrl !== undefined) {
    output.writeFieldBegin('imageUrl', Thrift.Type.STRING, 5);
    output.writeString(this.imageUrl);
    output.writeFieldEnd();
  }
  if (this.categoryLanguageId !== null && this.categoryLanguageId !== undefined) {
    output.writeFieldBegin('categoryLanguageId', Thrift.Type.STRING, 6);
    output.writeString(this.categoryLanguageId);
    output.writeFieldEnd();
  }
  if (this.vernacularNameMap !== null && this.vernacularNameMap !== undefined) {
    output.writeFieldBegin('vernacularNameMap', Thrift.Type.MAP, 7);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.STRING, Thrift.objectLength(this.vernacularNameMap));
    for (var kiter310 in this.vernacularNameMap)
    {
      if (this.vernacularNameMap.hasOwnProperty(kiter310))
      {
        var viter311 = this.vernacularNameMap[kiter310];
        output.writeString(kiter310);
        output.writeString(viter311);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RichText = function(args) {
  this.text = null;
  this.color = null;
  if (args) {
    if (args.text !== undefined && args.text !== null) {
      this.text = args.text;
    }
    if (args.color !== undefined && args.color !== null) {
      this.color = args.color;
    }
  }
};
RichText.prototype = {};
RichText.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.color = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RichText.prototype.write = function(output) {
  output.writeStructBegin('RichText');
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRING, 1);
    output.writeString(this.text);
    output.writeFieldEnd();
  }
  if (this.color !== null && this.color !== undefined) {
    output.writeFieldBegin('color', Thrift.Type.STRING, 2);
    output.writeString(this.color);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceContext = function(args) {
  this.richText = null;
  this.icon = null;
  this.bgColor = null;
  if (args) {
    if (args.richText !== undefined && args.richText !== null) {
      this.richText = new RichText(args.richText);
    }
    if (args.icon !== undefined && args.icon !== null) {
      this.icon = args.icon;
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = args.bgColor;
    }
  }
};
GlanceContext.prototype = {};
GlanceContext.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.richText = new RichText();
        this.richText.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.icon = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.bgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceContext.prototype.write = function(output) {
  output.writeStructBegin('GlanceContext');
  if (this.richText !== null && this.richText !== undefined) {
    output.writeFieldBegin('richText', Thrift.Type.STRUCT, 1);
    this.richText.write(output);
    output.writeFieldEnd();
  }
  if (this.icon !== null && this.icon !== undefined) {
    output.writeFieldBegin('icon', Thrift.Type.STRING, 2);
    output.writeString(this.icon);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.STRING, 3);
    output.writeString(this.bgColor);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PeekData = function(args) {
  this.title = null;
  this.sourceName = null;
  this.heartCount = null;
  this.shortUrl = null;
  this.shortTitle = null;
  this.glanceContext = null;
  this.ctaText = null;
  this.bgColor = null;
  this.ctaBgColor = null;
  this.iconUrl = null;
  this.showAnim = null;
  this.subText = null;
  this.useBubbleImageAsRibbonIcon = null;
  this.playStorePreloadSupported = null;
  if (args) {
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field title is unset!');
    }
    if (args.sourceName !== undefined && args.sourceName !== null) {
      this.sourceName = args.sourceName;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field sourceName is unset!');
    }
    if (args.heartCount !== undefined && args.heartCount !== null) {
      this.heartCount = args.heartCount;
    }
    if (args.shortUrl !== undefined && args.shortUrl !== null) {
      this.shortUrl = args.shortUrl;
    }
    if (args.shortTitle !== undefined && args.shortTitle !== null) {
      this.shortTitle = args.shortTitle;
    }
    if (args.glanceContext !== undefined && args.glanceContext !== null) {
      this.glanceContext = new GlanceContext(args.glanceContext);
    }
    if (args.ctaText !== undefined && args.ctaText !== null) {
      this.ctaText = args.ctaText;
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = args.bgColor;
    }
    if (args.ctaBgColor !== undefined && args.ctaBgColor !== null) {
      this.ctaBgColor = args.ctaBgColor;
    }
    if (args.iconUrl !== undefined && args.iconUrl !== null) {
      this.iconUrl = args.iconUrl;
    }
    if (args.showAnim !== undefined && args.showAnim !== null) {
      this.showAnim = args.showAnim;
    }
    if (args.subText !== undefined && args.subText !== null) {
      this.subText = args.subText;
    }
    if (args.useBubbleImageAsRibbonIcon !== undefined && args.useBubbleImageAsRibbonIcon !== null) {
      this.useBubbleImageAsRibbonIcon = args.useBubbleImageAsRibbonIcon;
    }
    if (args.playStorePreloadSupported !== undefined && args.playStorePreloadSupported !== null) {
      this.playStorePreloadSupported = args.playStorePreloadSupported;
    }
  }
};
PeekData.prototype = {};
PeekData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.sourceName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.heartCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.shortUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.shortTitle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceContext = new GlanceContext();
        this.glanceContext.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.ctaText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.bgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.ctaBgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.iconUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.BOOL) {
        this.showAnim = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.subText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.BOOL) {
        this.useBubbleImageAsRibbonIcon = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.BOOL) {
        this.playStorePreloadSupported = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PeekData.prototype.write = function(output) {
  output.writeStructBegin('PeekData');
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 1);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.sourceName !== null && this.sourceName !== undefined) {
    output.writeFieldBegin('sourceName', Thrift.Type.STRING, 2);
    output.writeString(this.sourceName);
    output.writeFieldEnd();
  }
  if (this.heartCount !== null && this.heartCount !== undefined) {
    output.writeFieldBegin('heartCount', Thrift.Type.I64, 3);
    output.writeI64(this.heartCount);
    output.writeFieldEnd();
  }
  if (this.shortUrl !== null && this.shortUrl !== undefined) {
    output.writeFieldBegin('shortUrl', Thrift.Type.STRING, 4);
    output.writeString(this.shortUrl);
    output.writeFieldEnd();
  }
  if (this.shortTitle !== null && this.shortTitle !== undefined) {
    output.writeFieldBegin('shortTitle', Thrift.Type.STRING, 5);
    output.writeString(this.shortTitle);
    output.writeFieldEnd();
  }
  if (this.glanceContext !== null && this.glanceContext !== undefined) {
    output.writeFieldBegin('glanceContext', Thrift.Type.STRUCT, 6);
    this.glanceContext.write(output);
    output.writeFieldEnd();
  }
  if (this.ctaText !== null && this.ctaText !== undefined) {
    output.writeFieldBegin('ctaText', Thrift.Type.STRING, 7);
    output.writeString(this.ctaText);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.STRING, 8);
    output.writeString(this.bgColor);
    output.writeFieldEnd();
  }
  if (this.ctaBgColor !== null && this.ctaBgColor !== undefined) {
    output.writeFieldBegin('ctaBgColor', Thrift.Type.STRING, 9);
    output.writeString(this.ctaBgColor);
    output.writeFieldEnd();
  }
  if (this.iconUrl !== null && this.iconUrl !== undefined) {
    output.writeFieldBegin('iconUrl', Thrift.Type.STRING, 10);
    output.writeString(this.iconUrl);
    output.writeFieldEnd();
  }
  if (this.showAnim !== null && this.showAnim !== undefined) {
    output.writeFieldBegin('showAnim', Thrift.Type.BOOL, 11);
    output.writeBool(this.showAnim);
    output.writeFieldEnd();
  }
  if (this.subText !== null && this.subText !== undefined) {
    output.writeFieldBegin('subText', Thrift.Type.STRING, 12);
    output.writeString(this.subText);
    output.writeFieldEnd();
  }
  if (this.useBubbleImageAsRibbonIcon !== null && this.useBubbleImageAsRibbonIcon !== undefined) {
    output.writeFieldBegin('useBubbleImageAsRibbonIcon', Thrift.Type.BOOL, 13);
    output.writeBool(this.useBubbleImageAsRibbonIcon);
    output.writeFieldEnd();
  }
  if (this.playStorePreloadSupported !== null && this.playStorePreloadSupported !== undefined) {
    output.writeFieldBegin('playStorePreloadSupported', Thrift.Type.BOOL, 14);
    output.writeBool(this.playStorePreloadSupported);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Peek = function(args) {
  this.article = null;
  this.video = null;
  this.webpeek = null;
  this.nativeVideoPeek = null;
  this.liveVideoPeek = null;
  this.tvVideoPeek = null;
  this.nativeLiveVideoPeek = null;
  this.fusionVideoPeek = null;
  if (args) {
    if (args.article !== undefined && args.article !== null) {
      this.article = new ArticlePeek(args.article);
    }
    if (args.video !== undefined && args.video !== null) {
      this.video = new VideoPeek(args.video);
    }
    if (args.webpeek !== undefined && args.webpeek !== null) {
      this.webpeek = new WebPeek(args.webpeek);
    }
    if (args.nativeVideoPeek !== undefined && args.nativeVideoPeek !== null) {
      this.nativeVideoPeek = new NativeVideoPeek(args.nativeVideoPeek);
    }
    if (args.liveVideoPeek !== undefined && args.liveVideoPeek !== null) {
      this.liveVideoPeek = new LiveVideoPeek(args.liveVideoPeek);
    }
    if (args.tvVideoPeek !== undefined && args.tvVideoPeek !== null) {
      this.tvVideoPeek = new TvVideoPeek(args.tvVideoPeek);
    }
    if (args.nativeLiveVideoPeek !== undefined && args.nativeLiveVideoPeek !== null) {
      this.nativeLiveVideoPeek = new NativeLiveVideoPeek(args.nativeLiveVideoPeek);
    }
    if (args.fusionVideoPeek !== undefined && args.fusionVideoPeek !== null) {
      this.fusionVideoPeek = new FusionVideoPeek(args.fusionVideoPeek);
    }
  }
};
Peek.prototype = {};
Peek.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.article = new ArticlePeek();
        this.article.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.video = new VideoPeek();
        this.video.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.webpeek = new WebPeek();
        this.webpeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.nativeVideoPeek = new NativeVideoPeek();
        this.nativeVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.liveVideoPeek = new LiveVideoPeek();
        this.liveVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.tvVideoPeek = new TvVideoPeek();
        this.tvVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRUCT) {
        this.nativeLiveVideoPeek = new NativeLiveVideoPeek();
        this.nativeLiveVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRUCT) {
        this.fusionVideoPeek = new FusionVideoPeek();
        this.fusionVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Peek.prototype.write = function(output) {
  output.writeStructBegin('Peek');
  if (this.article !== null && this.article !== undefined) {
    output.writeFieldBegin('article', Thrift.Type.STRUCT, 1);
    this.article.write(output);
    output.writeFieldEnd();
  }
  if (this.video !== null && this.video !== undefined) {
    output.writeFieldBegin('video', Thrift.Type.STRUCT, 2);
    this.video.write(output);
    output.writeFieldEnd();
  }
  if (this.webpeek !== null && this.webpeek !== undefined) {
    output.writeFieldBegin('webpeek', Thrift.Type.STRUCT, 3);
    this.webpeek.write(output);
    output.writeFieldEnd();
  }
  if (this.nativeVideoPeek !== null && this.nativeVideoPeek !== undefined) {
    output.writeFieldBegin('nativeVideoPeek', Thrift.Type.STRUCT, 4);
    this.nativeVideoPeek.write(output);
    output.writeFieldEnd();
  }
  if (this.liveVideoPeek !== null && this.liveVideoPeek !== undefined) {
    output.writeFieldBegin('liveVideoPeek', Thrift.Type.STRUCT, 5);
    this.liveVideoPeek.write(output);
    output.writeFieldEnd();
  }
  if (this.tvVideoPeek !== null && this.tvVideoPeek !== undefined) {
    output.writeFieldBegin('tvVideoPeek', Thrift.Type.STRUCT, 6);
    this.tvVideoPeek.write(output);
    output.writeFieldEnd();
  }
  if (this.nativeLiveVideoPeek !== null && this.nativeLiveVideoPeek !== undefined) {
    output.writeFieldBegin('nativeLiveVideoPeek', Thrift.Type.STRUCT, 7);
    this.nativeLiveVideoPeek.write(output);
    output.writeFieldEnd();
  }
  if (this.fusionVideoPeek !== null && this.fusionVideoPeek !== undefined) {
    output.writeFieldBegin('fusionVideoPeek', Thrift.Type.STRUCT, 8);
    this.fusionVideoPeek.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Beacon = function(args) {
  this.url = null;
  this.minDurationInSecs = null;
  this.delayDurationInMillis = null;
  this.beaconPartner = null;
  if (args) {
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field url is unset!');
    }
    if (args.minDurationInSecs !== undefined && args.minDurationInSecs !== null) {
      this.minDurationInSecs = args.minDurationInSecs;
    }
    if (args.delayDurationInMillis !== undefined && args.delayDurationInMillis !== null) {
      this.delayDurationInMillis = args.delayDurationInMillis;
    }
    if (args.beaconPartner !== undefined && args.beaconPartner !== null) {
      this.beaconPartner = args.beaconPartner;
    }
  }
};
Beacon.prototype = {};
Beacon.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.minDurationInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.delayDurationInMillis = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.beaconPartner = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Beacon.prototype.write = function(output) {
  output.writeStructBegin('Beacon');
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 1);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.minDurationInSecs !== null && this.minDurationInSecs !== undefined) {
    output.writeFieldBegin('minDurationInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.minDurationInSecs);
    output.writeFieldEnd();
  }
  if (this.delayDurationInMillis !== null && this.delayDurationInMillis !== undefined) {
    output.writeFieldBegin('delayDurationInMillis', Thrift.Type.I64, 3);
    output.writeI64(this.delayDurationInMillis);
    output.writeFieldEnd();
  }
  if (this.beaconPartner !== null && this.beaconPartner !== undefined) {
    output.writeFieldBegin('beaconPartner', Thrift.Type.STRING, 4);
    output.writeString(this.beaconPartner);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GamingEuSpaceData = function(args) {
  this.publishedAt = null;
  this.tournamentId = null;
  this.tournamentName = null;
  this.gameId = null;
  this.gameName = null;
  this.isLeaderboardEnabled = null;
  this.category = null;
  this.gameIcon = null;
  this.gameBanner = null;
  this.gameStatus = null;
  this.tourneyStartTime = null;
  this.tourneyEndTime = null;
  this.gameCta = null;
  if (args) {
    if (args.publishedAt !== undefined && args.publishedAt !== null) {
      this.publishedAt = args.publishedAt;
    }
    if (args.tournamentId !== undefined && args.tournamentId !== null) {
      this.tournamentId = args.tournamentId;
    }
    if (args.tournamentName !== undefined && args.tournamentName !== null) {
      this.tournamentName = args.tournamentName;
    }
    if (args.gameId !== undefined && args.gameId !== null) {
      this.gameId = args.gameId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field gameId is unset!');
    }
    if (args.gameName !== undefined && args.gameName !== null) {
      this.gameName = args.gameName;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field gameName is unset!');
    }
    if (args.isLeaderboardEnabled !== undefined && args.isLeaderboardEnabled !== null) {
      this.isLeaderboardEnabled = args.isLeaderboardEnabled;
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.gameIcon !== undefined && args.gameIcon !== null) {
      this.gameIcon = args.gameIcon;
    }
    if (args.gameBanner !== undefined && args.gameBanner !== null) {
      this.gameBanner = args.gameBanner;
    }
    if (args.gameStatus !== undefined && args.gameStatus !== null) {
      this.gameStatus = args.gameStatus;
    }
    if (args.tourneyStartTime !== undefined && args.tourneyStartTime !== null) {
      this.tourneyStartTime = args.tourneyStartTime;
    }
    if (args.tourneyEndTime !== undefined && args.tourneyEndTime !== null) {
      this.tourneyEndTime = args.tourneyEndTime;
    }
    if (args.gameCta !== undefined && args.gameCta !== null) {
      this.gameCta = args.gameCta;
    }
  }
};
GamingEuSpaceData.prototype = {};
GamingEuSpaceData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.publishedAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.tournamentId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.tournamentName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.gameId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.gameName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.isLeaderboardEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.gameIcon = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.gameBanner = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.gameStatus = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.I64) {
        this.tourneyStartTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I64) {
        this.tourneyEndTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.gameCta = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GamingEuSpaceData.prototype.write = function(output) {
  output.writeStructBegin('GamingEuSpaceData');
  if (this.publishedAt !== null && this.publishedAt !== undefined) {
    output.writeFieldBegin('publishedAt', Thrift.Type.I64, 1);
    output.writeI64(this.publishedAt);
    output.writeFieldEnd();
  }
  if (this.tournamentId !== null && this.tournamentId !== undefined) {
    output.writeFieldBegin('tournamentId', Thrift.Type.STRING, 2);
    output.writeString(this.tournamentId);
    output.writeFieldEnd();
  }
  if (this.tournamentName !== null && this.tournamentName !== undefined) {
    output.writeFieldBegin('tournamentName', Thrift.Type.STRING, 3);
    output.writeString(this.tournamentName);
    output.writeFieldEnd();
  }
  if (this.gameId !== null && this.gameId !== undefined) {
    output.writeFieldBegin('gameId', Thrift.Type.STRING, 4);
    output.writeString(this.gameId);
    output.writeFieldEnd();
  }
  if (this.gameName !== null && this.gameName !== undefined) {
    output.writeFieldBegin('gameName', Thrift.Type.STRING, 5);
    output.writeString(this.gameName);
    output.writeFieldEnd();
  }
  if (this.isLeaderboardEnabled !== null && this.isLeaderboardEnabled !== undefined) {
    output.writeFieldBegin('isLeaderboardEnabled', Thrift.Type.BOOL, 6);
    output.writeBool(this.isLeaderboardEnabled);
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 7);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.gameIcon !== null && this.gameIcon !== undefined) {
    output.writeFieldBegin('gameIcon', Thrift.Type.STRING, 8);
    output.writeString(this.gameIcon);
    output.writeFieldEnd();
  }
  if (this.gameBanner !== null && this.gameBanner !== undefined) {
    output.writeFieldBegin('gameBanner', Thrift.Type.STRING, 9);
    output.writeString(this.gameBanner);
    output.writeFieldEnd();
  }
  if (this.gameStatus !== null && this.gameStatus !== undefined) {
    output.writeFieldBegin('gameStatus', Thrift.Type.STRING, 10);
    output.writeString(this.gameStatus);
    output.writeFieldEnd();
  }
  if (this.tourneyStartTime !== null && this.tourneyStartTime !== undefined) {
    output.writeFieldBegin('tourneyStartTime', Thrift.Type.I64, 11);
    output.writeI64(this.tourneyStartTime);
    output.writeFieldEnd();
  }
  if (this.tourneyEndTime !== null && this.tourneyEndTime !== undefined) {
    output.writeFieldBegin('tourneyEndTime', Thrift.Type.I64, 12);
    output.writeI64(this.tourneyEndTime);
    output.writeFieldEnd();
  }
  if (this.gameCta !== null && this.gameCta !== undefined) {
    output.writeFieldBegin('gameCta', Thrift.Type.STRING, 13);
    output.writeString(this.gameCta);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GameMetaData = function(args) {
  this.gameId = null;
  this.gameName = null;
  this.gameCategory = null;
  this.rating = null;
  this.ctaUrl = null;
  this.bannerUrl = null;
  if (args) {
    if (args.gameId !== undefined && args.gameId !== null) {
      this.gameId = args.gameId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field gameId is unset!');
    }
    if (args.gameName !== undefined && args.gameName !== null) {
      this.gameName = args.gameName;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field gameName is unset!');
    }
    if (args.gameCategory !== undefined && args.gameCategory !== null) {
      this.gameCategory = args.gameCategory;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field gameCategory is unset!');
    }
    if (args.rating !== undefined && args.rating !== null) {
      this.rating = args.rating;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field rating is unset!');
    }
    if (args.ctaUrl !== undefined && args.ctaUrl !== null) {
      this.ctaUrl = args.ctaUrl;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field ctaUrl is unset!');
    }
    if (args.bannerUrl !== undefined && args.bannerUrl !== null) {
      this.bannerUrl = args.bannerUrl;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field bannerUrl is unset!');
    }
  }
};
GameMetaData.prototype = {};
GameMetaData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.gameId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.gameName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.gameCategory = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.DOUBLE) {
        this.rating = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.ctaUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.bannerUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GameMetaData.prototype.write = function(output) {
  output.writeStructBegin('GameMetaData');
  if (this.gameId !== null && this.gameId !== undefined) {
    output.writeFieldBegin('gameId', Thrift.Type.STRING, 1);
    output.writeString(this.gameId);
    output.writeFieldEnd();
  }
  if (this.gameName !== null && this.gameName !== undefined) {
    output.writeFieldBegin('gameName', Thrift.Type.STRING, 2);
    output.writeString(this.gameName);
    output.writeFieldEnd();
  }
  if (this.gameCategory !== null && this.gameCategory !== undefined) {
    output.writeFieldBegin('gameCategory', Thrift.Type.STRING, 3);
    output.writeString(this.gameCategory);
    output.writeFieldEnd();
  }
  if (this.rating !== null && this.rating !== undefined) {
    output.writeFieldBegin('rating', Thrift.Type.DOUBLE, 4);
    output.writeDouble(this.rating);
    output.writeFieldEnd();
  }
  if (this.ctaUrl !== null && this.ctaUrl !== undefined) {
    output.writeFieldBegin('ctaUrl', Thrift.Type.STRING, 5);
    output.writeString(this.ctaUrl);
    output.writeFieldEnd();
  }
  if (this.bannerUrl !== null && this.bannerUrl !== undefined) {
    output.writeFieldBegin('bannerUrl', Thrift.Type.STRING, 6);
    output.writeString(this.bannerUrl);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SportEuSpaceData = function(args) {
  this.leagueId = null;
  this.teamIds = null;
  if (args) {
    if (args.leagueId !== undefined && args.leagueId !== null) {
      this.leagueId = args.leagueId;
    }
    if (args.teamIds !== undefined && args.teamIds !== null) {
      this.teamIds = Thrift.copyList(args.teamIds, [null]);
    }
  }
};
SportEuSpaceData.prototype = {};
SportEuSpaceData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.leagueId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size312 = 0;
        var _rtmp3316;
        this.teamIds = [];
        var _etype315 = 0;
        _rtmp3316 = input.readListBegin();
        _etype315 = _rtmp3316.etype;
        _size312 = _rtmp3316.size;
        for (var _i317 = 0; _i317 < _size312; ++_i317)
        {
          var elem318 = null;
          elem318 = input.readString().value;
          this.teamIds.push(elem318);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SportEuSpaceData.prototype.write = function(output) {
  output.writeStructBegin('SportEuSpaceData');
  if (this.leagueId !== null && this.leagueId !== undefined) {
    output.writeFieldBegin('leagueId', Thrift.Type.STRING, 1);
    output.writeString(this.leagueId);
    output.writeFieldEnd();
  }
  if (this.teamIds !== null && this.teamIds !== undefined) {
    output.writeFieldBegin('teamIds', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.teamIds.length);
    for (var iter319 in this.teamIds)
    {
      if (this.teamIds.hasOwnProperty(iter319))
      {
        iter319 = this.teamIds[iter319];
        output.writeString(iter319);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Beacons = function(args) {
  this.renderBeacons = null;
  this.peekBeacons = null;
  this.ctaBeacons = null;
  this.videoBeacons = null;
  this.likeBeacons = null;
  this.shareBeacons = null;
  this.holdBeacons = null;
  this.appSubmitBeacons = null;
  this.installCompleteBeacons = null;
  this.appOpenBeacons = null;
  this.videoStartedBeacons = null;
  this.glanceStartedBeacons = null;
  this.installNowBeacons = null;
  this.installLaterBeacons = null;
  this.previewTapBeacons = null;
  this.videoViewedBeacons = null;
  this.glanceViewedBeacons = null;
  this.bidLossBeacons = null;
  if (args) {
    if (args.renderBeacons !== undefined && args.renderBeacons !== null) {
      this.renderBeacons = Thrift.copyList(args.renderBeacons, [Beacon]);
    }
    if (args.peekBeacons !== undefined && args.peekBeacons !== null) {
      this.peekBeacons = Thrift.copyList(args.peekBeacons, [Beacon]);
    }
    if (args.ctaBeacons !== undefined && args.ctaBeacons !== null) {
      this.ctaBeacons = Thrift.copyList(args.ctaBeacons, [Beacon]);
    }
    if (args.videoBeacons !== undefined && args.videoBeacons !== null) {
      this.videoBeacons = Thrift.copyList(args.videoBeacons, [Beacon]);
    }
    if (args.likeBeacons !== undefined && args.likeBeacons !== null) {
      this.likeBeacons = Thrift.copyList(args.likeBeacons, [Beacon]);
    }
    if (args.shareBeacons !== undefined && args.shareBeacons !== null) {
      this.shareBeacons = Thrift.copyList(args.shareBeacons, [Beacon]);
    }
    if (args.holdBeacons !== undefined && args.holdBeacons !== null) {
      this.holdBeacons = Thrift.copyList(args.holdBeacons, [Beacon]);
    }
    if (args.appSubmitBeacons !== undefined && args.appSubmitBeacons !== null) {
      this.appSubmitBeacons = Thrift.copyList(args.appSubmitBeacons, [Beacon]);
    }
    if (args.installCompleteBeacons !== undefined && args.installCompleteBeacons !== null) {
      this.installCompleteBeacons = Thrift.copyList(args.installCompleteBeacons, [Beacon]);
    }
    if (args.appOpenBeacons !== undefined && args.appOpenBeacons !== null) {
      this.appOpenBeacons = Thrift.copyList(args.appOpenBeacons, [Beacon]);
    }
    if (args.videoStartedBeacons !== undefined && args.videoStartedBeacons !== null) {
      this.videoStartedBeacons = Thrift.copyList(args.videoStartedBeacons, [Beacon]);
    }
    if (args.glanceStartedBeacons !== undefined && args.glanceStartedBeacons !== null) {
      this.glanceStartedBeacons = Thrift.copyList(args.glanceStartedBeacons, [Beacon]);
    }
    if (args.installNowBeacons !== undefined && args.installNowBeacons !== null) {
      this.installNowBeacons = Thrift.copyList(args.installNowBeacons, [Beacon]);
    }
    if (args.installLaterBeacons !== undefined && args.installLaterBeacons !== null) {
      this.installLaterBeacons = Thrift.copyList(args.installLaterBeacons, [Beacon]);
    }
    if (args.previewTapBeacons !== undefined && args.previewTapBeacons !== null) {
      this.previewTapBeacons = Thrift.copyList(args.previewTapBeacons, [Beacon]);
    }
    if (args.videoViewedBeacons !== undefined && args.videoViewedBeacons !== null) {
      this.videoViewedBeacons = Thrift.copyList(args.videoViewedBeacons, [Beacon]);
    }
    if (args.glanceViewedBeacons !== undefined && args.glanceViewedBeacons !== null) {
      this.glanceViewedBeacons = Thrift.copyList(args.glanceViewedBeacons, [Beacon]);
    }
    if (args.bidLossBeacons !== undefined && args.bidLossBeacons !== null) {
      this.bidLossBeacons = Thrift.copyList(args.bidLossBeacons, [Beacon]);
    }
  }
};
Beacons.prototype = {};
Beacons.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size320 = 0;
        var _rtmp3324;
        this.renderBeacons = [];
        var _etype323 = 0;
        _rtmp3324 = input.readListBegin();
        _etype323 = _rtmp3324.etype;
        _size320 = _rtmp3324.size;
        for (var _i325 = 0; _i325 < _size320; ++_i325)
        {
          var elem326 = null;
          elem326 = new Beacon();
          elem326.read(input);
          this.renderBeacons.push(elem326);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size327 = 0;
        var _rtmp3331;
        this.peekBeacons = [];
        var _etype330 = 0;
        _rtmp3331 = input.readListBegin();
        _etype330 = _rtmp3331.etype;
        _size327 = _rtmp3331.size;
        for (var _i332 = 0; _i332 < _size327; ++_i332)
        {
          var elem333 = null;
          elem333 = new Beacon();
          elem333.read(input);
          this.peekBeacons.push(elem333);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size334 = 0;
        var _rtmp3338;
        this.ctaBeacons = [];
        var _etype337 = 0;
        _rtmp3338 = input.readListBegin();
        _etype337 = _rtmp3338.etype;
        _size334 = _rtmp3338.size;
        for (var _i339 = 0; _i339 < _size334; ++_i339)
        {
          var elem340 = null;
          elem340 = new Beacon();
          elem340.read(input);
          this.ctaBeacons.push(elem340);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size341 = 0;
        var _rtmp3345;
        this.videoBeacons = [];
        var _etype344 = 0;
        _rtmp3345 = input.readListBegin();
        _etype344 = _rtmp3345.etype;
        _size341 = _rtmp3345.size;
        for (var _i346 = 0; _i346 < _size341; ++_i346)
        {
          var elem347 = null;
          elem347 = new Beacon();
          elem347.read(input);
          this.videoBeacons.push(elem347);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size348 = 0;
        var _rtmp3352;
        this.likeBeacons = [];
        var _etype351 = 0;
        _rtmp3352 = input.readListBegin();
        _etype351 = _rtmp3352.etype;
        _size348 = _rtmp3352.size;
        for (var _i353 = 0; _i353 < _size348; ++_i353)
        {
          var elem354 = null;
          elem354 = new Beacon();
          elem354.read(input);
          this.likeBeacons.push(elem354);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size355 = 0;
        var _rtmp3359;
        this.shareBeacons = [];
        var _etype358 = 0;
        _rtmp3359 = input.readListBegin();
        _etype358 = _rtmp3359.etype;
        _size355 = _rtmp3359.size;
        for (var _i360 = 0; _i360 < _size355; ++_i360)
        {
          var elem361 = null;
          elem361 = new Beacon();
          elem361.read(input);
          this.shareBeacons.push(elem361);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size362 = 0;
        var _rtmp3366;
        this.holdBeacons = [];
        var _etype365 = 0;
        _rtmp3366 = input.readListBegin();
        _etype365 = _rtmp3366.etype;
        _size362 = _rtmp3366.size;
        for (var _i367 = 0; _i367 < _size362; ++_i367)
        {
          var elem368 = null;
          elem368 = new Beacon();
          elem368.read(input);
          this.holdBeacons.push(elem368);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size369 = 0;
        var _rtmp3373;
        this.appSubmitBeacons = [];
        var _etype372 = 0;
        _rtmp3373 = input.readListBegin();
        _etype372 = _rtmp3373.etype;
        _size369 = _rtmp3373.size;
        for (var _i374 = 0; _i374 < _size369; ++_i374)
        {
          var elem375 = null;
          elem375 = new Beacon();
          elem375.read(input);
          this.appSubmitBeacons.push(elem375);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size376 = 0;
        var _rtmp3380;
        this.installCompleteBeacons = [];
        var _etype379 = 0;
        _rtmp3380 = input.readListBegin();
        _etype379 = _rtmp3380.etype;
        _size376 = _rtmp3380.size;
        for (var _i381 = 0; _i381 < _size376; ++_i381)
        {
          var elem382 = null;
          elem382 = new Beacon();
          elem382.read(input);
          this.installCompleteBeacons.push(elem382);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.LIST) {
        var _size383 = 0;
        var _rtmp3387;
        this.appOpenBeacons = [];
        var _etype386 = 0;
        _rtmp3387 = input.readListBegin();
        _etype386 = _rtmp3387.etype;
        _size383 = _rtmp3387.size;
        for (var _i388 = 0; _i388 < _size383; ++_i388)
        {
          var elem389 = null;
          elem389 = new Beacon();
          elem389.read(input);
          this.appOpenBeacons.push(elem389);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size390 = 0;
        var _rtmp3394;
        this.videoStartedBeacons = [];
        var _etype393 = 0;
        _rtmp3394 = input.readListBegin();
        _etype393 = _rtmp3394.etype;
        _size390 = _rtmp3394.size;
        for (var _i395 = 0; _i395 < _size390; ++_i395)
        {
          var elem396 = null;
          elem396 = new Beacon();
          elem396.read(input);
          this.videoStartedBeacons.push(elem396);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.LIST) {
        var _size397 = 0;
        var _rtmp3401;
        this.glanceStartedBeacons = [];
        var _etype400 = 0;
        _rtmp3401 = input.readListBegin();
        _etype400 = _rtmp3401.etype;
        _size397 = _rtmp3401.size;
        for (var _i402 = 0; _i402 < _size397; ++_i402)
        {
          var elem403 = null;
          elem403 = new Beacon();
          elem403.read(input);
          this.glanceStartedBeacons.push(elem403);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size404 = 0;
        var _rtmp3408;
        this.installNowBeacons = [];
        var _etype407 = 0;
        _rtmp3408 = input.readListBegin();
        _etype407 = _rtmp3408.etype;
        _size404 = _rtmp3408.size;
        for (var _i409 = 0; _i409 < _size404; ++_i409)
        {
          var elem410 = null;
          elem410 = new Beacon();
          elem410.read(input);
          this.installNowBeacons.push(elem410);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.LIST) {
        var _size411 = 0;
        var _rtmp3415;
        this.installLaterBeacons = [];
        var _etype414 = 0;
        _rtmp3415 = input.readListBegin();
        _etype414 = _rtmp3415.etype;
        _size411 = _rtmp3415.size;
        for (var _i416 = 0; _i416 < _size411; ++_i416)
        {
          var elem417 = null;
          elem417 = new Beacon();
          elem417.read(input);
          this.installLaterBeacons.push(elem417);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.LIST) {
        var _size418 = 0;
        var _rtmp3422;
        this.previewTapBeacons = [];
        var _etype421 = 0;
        _rtmp3422 = input.readListBegin();
        _etype421 = _rtmp3422.etype;
        _size418 = _rtmp3422.size;
        for (var _i423 = 0; _i423 < _size418; ++_i423)
        {
          var elem424 = null;
          elem424 = new Beacon();
          elem424.read(input);
          this.previewTapBeacons.push(elem424);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.LIST) {
        var _size425 = 0;
        var _rtmp3429;
        this.videoViewedBeacons = [];
        var _etype428 = 0;
        _rtmp3429 = input.readListBegin();
        _etype428 = _rtmp3429.etype;
        _size425 = _rtmp3429.size;
        for (var _i430 = 0; _i430 < _size425; ++_i430)
        {
          var elem431 = null;
          elem431 = new Beacon();
          elem431.read(input);
          this.videoViewedBeacons.push(elem431);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.LIST) {
        var _size432 = 0;
        var _rtmp3436;
        this.glanceViewedBeacons = [];
        var _etype435 = 0;
        _rtmp3436 = input.readListBegin();
        _etype435 = _rtmp3436.etype;
        _size432 = _rtmp3436.size;
        for (var _i437 = 0; _i437 < _size432; ++_i437)
        {
          var elem438 = null;
          elem438 = new Beacon();
          elem438.read(input);
          this.glanceViewedBeacons.push(elem438);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.LIST) {
        var _size439 = 0;
        var _rtmp3443;
        this.bidLossBeacons = [];
        var _etype442 = 0;
        _rtmp3443 = input.readListBegin();
        _etype442 = _rtmp3443.etype;
        _size439 = _rtmp3443.size;
        for (var _i444 = 0; _i444 < _size439; ++_i444)
        {
          var elem445 = null;
          elem445 = new Beacon();
          elem445.read(input);
          this.bidLossBeacons.push(elem445);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Beacons.prototype.write = function(output) {
  output.writeStructBegin('Beacons');
  if (this.renderBeacons !== null && this.renderBeacons !== undefined) {
    output.writeFieldBegin('renderBeacons', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.renderBeacons.length);
    for (var iter446 in this.renderBeacons)
    {
      if (this.renderBeacons.hasOwnProperty(iter446))
      {
        iter446 = this.renderBeacons[iter446];
        iter446.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.peekBeacons !== null && this.peekBeacons !== undefined) {
    output.writeFieldBegin('peekBeacons', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.peekBeacons.length);
    for (var iter447 in this.peekBeacons)
    {
      if (this.peekBeacons.hasOwnProperty(iter447))
      {
        iter447 = this.peekBeacons[iter447];
        iter447.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.ctaBeacons !== null && this.ctaBeacons !== undefined) {
    output.writeFieldBegin('ctaBeacons', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.ctaBeacons.length);
    for (var iter448 in this.ctaBeacons)
    {
      if (this.ctaBeacons.hasOwnProperty(iter448))
      {
        iter448 = this.ctaBeacons[iter448];
        iter448.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.videoBeacons !== null && this.videoBeacons !== undefined) {
    output.writeFieldBegin('videoBeacons', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRUCT, this.videoBeacons.length);
    for (var iter449 in this.videoBeacons)
    {
      if (this.videoBeacons.hasOwnProperty(iter449))
      {
        iter449 = this.videoBeacons[iter449];
        iter449.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.likeBeacons !== null && this.likeBeacons !== undefined) {
    output.writeFieldBegin('likeBeacons', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.likeBeacons.length);
    for (var iter450 in this.likeBeacons)
    {
      if (this.likeBeacons.hasOwnProperty(iter450))
      {
        iter450 = this.likeBeacons[iter450];
        iter450.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.shareBeacons !== null && this.shareBeacons !== undefined) {
    output.writeFieldBegin('shareBeacons', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.STRUCT, this.shareBeacons.length);
    for (var iter451 in this.shareBeacons)
    {
      if (this.shareBeacons.hasOwnProperty(iter451))
      {
        iter451 = this.shareBeacons[iter451];
        iter451.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.holdBeacons !== null && this.holdBeacons !== undefined) {
    output.writeFieldBegin('holdBeacons', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRUCT, this.holdBeacons.length);
    for (var iter452 in this.holdBeacons)
    {
      if (this.holdBeacons.hasOwnProperty(iter452))
      {
        iter452 = this.holdBeacons[iter452];
        iter452.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.appSubmitBeacons !== null && this.appSubmitBeacons !== undefined) {
    output.writeFieldBegin('appSubmitBeacons', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRUCT, this.appSubmitBeacons.length);
    for (var iter453 in this.appSubmitBeacons)
    {
      if (this.appSubmitBeacons.hasOwnProperty(iter453))
      {
        iter453 = this.appSubmitBeacons[iter453];
        iter453.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.installCompleteBeacons !== null && this.installCompleteBeacons !== undefined) {
    output.writeFieldBegin('installCompleteBeacons', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRUCT, this.installCompleteBeacons.length);
    for (var iter454 in this.installCompleteBeacons)
    {
      if (this.installCompleteBeacons.hasOwnProperty(iter454))
      {
        iter454 = this.installCompleteBeacons[iter454];
        iter454.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.appOpenBeacons !== null && this.appOpenBeacons !== undefined) {
    output.writeFieldBegin('appOpenBeacons', Thrift.Type.LIST, 10);
    output.writeListBegin(Thrift.Type.STRUCT, this.appOpenBeacons.length);
    for (var iter455 in this.appOpenBeacons)
    {
      if (this.appOpenBeacons.hasOwnProperty(iter455))
      {
        iter455 = this.appOpenBeacons[iter455];
        iter455.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.videoStartedBeacons !== null && this.videoStartedBeacons !== undefined) {
    output.writeFieldBegin('videoStartedBeacons', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRUCT, this.videoStartedBeacons.length);
    for (var iter456 in this.videoStartedBeacons)
    {
      if (this.videoStartedBeacons.hasOwnProperty(iter456))
      {
        iter456 = this.videoStartedBeacons[iter456];
        iter456.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.glanceStartedBeacons !== null && this.glanceStartedBeacons !== undefined) {
    output.writeFieldBegin('glanceStartedBeacons', Thrift.Type.LIST, 12);
    output.writeListBegin(Thrift.Type.STRUCT, this.glanceStartedBeacons.length);
    for (var iter457 in this.glanceStartedBeacons)
    {
      if (this.glanceStartedBeacons.hasOwnProperty(iter457))
      {
        iter457 = this.glanceStartedBeacons[iter457];
        iter457.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.installNowBeacons !== null && this.installNowBeacons !== undefined) {
    output.writeFieldBegin('installNowBeacons', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRUCT, this.installNowBeacons.length);
    for (var iter458 in this.installNowBeacons)
    {
      if (this.installNowBeacons.hasOwnProperty(iter458))
      {
        iter458 = this.installNowBeacons[iter458];
        iter458.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.installLaterBeacons !== null && this.installLaterBeacons !== undefined) {
    output.writeFieldBegin('installLaterBeacons', Thrift.Type.LIST, 14);
    output.writeListBegin(Thrift.Type.STRUCT, this.installLaterBeacons.length);
    for (var iter459 in this.installLaterBeacons)
    {
      if (this.installLaterBeacons.hasOwnProperty(iter459))
      {
        iter459 = this.installLaterBeacons[iter459];
        iter459.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.previewTapBeacons !== null && this.previewTapBeacons !== undefined) {
    output.writeFieldBegin('previewTapBeacons', Thrift.Type.LIST, 15);
    output.writeListBegin(Thrift.Type.STRUCT, this.previewTapBeacons.length);
    for (var iter460 in this.previewTapBeacons)
    {
      if (this.previewTapBeacons.hasOwnProperty(iter460))
      {
        iter460 = this.previewTapBeacons[iter460];
        iter460.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.videoViewedBeacons !== null && this.videoViewedBeacons !== undefined) {
    output.writeFieldBegin('videoViewedBeacons', Thrift.Type.LIST, 16);
    output.writeListBegin(Thrift.Type.STRUCT, this.videoViewedBeacons.length);
    for (var iter461 in this.videoViewedBeacons)
    {
      if (this.videoViewedBeacons.hasOwnProperty(iter461))
      {
        iter461 = this.videoViewedBeacons[iter461];
        iter461.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.glanceViewedBeacons !== null && this.glanceViewedBeacons !== undefined) {
    output.writeFieldBegin('glanceViewedBeacons', Thrift.Type.LIST, 17);
    output.writeListBegin(Thrift.Type.STRUCT, this.glanceViewedBeacons.length);
    for (var iter462 in this.glanceViewedBeacons)
    {
      if (this.glanceViewedBeacons.hasOwnProperty(iter462))
      {
        iter462 = this.glanceViewedBeacons[iter462];
        iter462.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.bidLossBeacons !== null && this.bidLossBeacons !== undefined) {
    output.writeFieldBegin('bidLossBeacons', Thrift.Type.LIST, 18);
    output.writeListBegin(Thrift.Type.STRUCT, this.bidLossBeacons.length);
    for (var iter463 in this.bidLossBeacons)
    {
      if (this.bidLossBeacons.hasOwnProperty(iter463))
      {
        iter463 = this.bidLossBeacons[iter463];
        iter463.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

BeaconsInfo = function(args) {
  this.s2sIntegration = null;
  if (args) {
    if (args.s2sIntegration !== undefined && args.s2sIntegration !== null) {
      this.s2sIntegration = args.s2sIntegration;
    }
  }
};
BeaconsInfo.prototype = {};
BeaconsInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.s2sIntegration = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

BeaconsInfo.prototype.write = function(output) {
  output.writeStructBegin('BeaconsInfo');
  if (this.s2sIntegration !== null && this.s2sIntegration !== undefined) {
    output.writeFieldBegin('s2sIntegration', Thrift.Type.BOOL, 1);
    output.writeBool(this.s2sIntegration);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SubscriptionMeta = function(args) {
  this.id = null;
  this.type = null;
  this.startTimeInSecs = null;
  this.active = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field type is unset!');
    }
    if (args.startTimeInSecs !== undefined && args.startTimeInSecs !== null) {
      this.startTimeInSecs = args.startTimeInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field startTimeInSecs is unset!');
    }
    if (args.active !== undefined && args.active !== null) {
      this.active = args.active;
    }
  }
};
SubscriptionMeta.prototype = {};
SubscriptionMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.startTimeInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.active = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SubscriptionMeta.prototype.write = function(output) {
  output.writeStructBegin('SubscriptionMeta');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.I32, 2);
    output.writeI32(this.type);
    output.writeFieldEnd();
  }
  if (this.startTimeInSecs !== null && this.startTimeInSecs !== undefined) {
    output.writeFieldBegin('startTimeInSecs', Thrift.Type.I64, 3);
    output.writeI64(this.startTimeInSecs);
    output.writeFieldEnd();
  }
  if (this.active !== null && this.active !== undefined) {
    output.writeFieldBegin('active', Thrift.Type.BOOL, 4);
    output.writeBool(this.active);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Proposition = function(args) {
  this.type = null;
  this.ctaUrl = null;
  this.appInstallMeta = null;
  if (args) {
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field type is unset!');
    }
    if (args.ctaUrl !== undefined && args.ctaUrl !== null) {
      this.ctaUrl = args.ctaUrl;
    }
    if (args.appInstallMeta !== undefined && args.appInstallMeta !== null) {
      this.appInstallMeta = new AppInstallMeta(args.appInstallMeta);
    }
  }
};
Proposition.prototype = {};
Proposition.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.ctaUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.appInstallMeta = new AppInstallMeta();
        this.appInstallMeta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Proposition.prototype.write = function(output) {
  output.writeStructBegin('Proposition');
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.I32, 1);
    output.writeI32(this.type);
    output.writeFieldEnd();
  }
  if (this.ctaUrl !== null && this.ctaUrl !== undefined) {
    output.writeFieldBegin('ctaUrl', Thrift.Type.STRING, 2);
    output.writeString(this.ctaUrl);
    output.writeFieldEnd();
  }
  if (this.appInstallMeta !== null && this.appInstallMeta !== undefined) {
    output.writeFieldBegin('appInstallMeta', Thrift.Type.STRUCT, 3);
    this.appInstallMeta.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Publisher = function(args) {
  this.id = null;
  this.name = null;
  this.displayName = null;
  this.active = null;
  this.primaryReserveCount = null;
  this.primaryReserveExpiryInDays = null;
  this.secondaryReserveCount = null;
  this.secondaryReserveExpiryInDays = null;
  this.propositions = null;
  this.logoUrl = null;
  this.advertiserId = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.displayName !== undefined && args.displayName !== null) {
      this.displayName = args.displayName;
    }
    if (args.active !== undefined && args.active !== null) {
      this.active = args.active;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field active is unset!');
    }
    if (args.primaryReserveCount !== undefined && args.primaryReserveCount !== null) {
      this.primaryReserveCount = args.primaryReserveCount;
    }
    if (args.primaryReserveExpiryInDays !== undefined && args.primaryReserveExpiryInDays !== null) {
      this.primaryReserveExpiryInDays = args.primaryReserveExpiryInDays;
    }
    if (args.secondaryReserveCount !== undefined && args.secondaryReserveCount !== null) {
      this.secondaryReserveCount = args.secondaryReserveCount;
    }
    if (args.secondaryReserveExpiryInDays !== undefined && args.secondaryReserveExpiryInDays !== null) {
      this.secondaryReserveExpiryInDays = args.secondaryReserveExpiryInDays;
    }
    if (args.propositions !== undefined && args.propositions !== null) {
      this.propositions = Thrift.copyList(args.propositions, [Proposition]);
    }
    if (args.logoUrl !== undefined && args.logoUrl !== null) {
      this.logoUrl = args.logoUrl;
    }
    if (args.advertiserId !== undefined && args.advertiserId !== null) {
      this.advertiserId = args.advertiserId;
    }
  }
};
Publisher.prototype = {};
Publisher.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.displayName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.active = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.primaryReserveCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.primaryReserveExpiryInDays = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.secondaryReserveCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.secondaryReserveExpiryInDays = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size464 = 0;
        var _rtmp3468;
        this.propositions = [];
        var _etype467 = 0;
        _rtmp3468 = input.readListBegin();
        _etype467 = _rtmp3468.etype;
        _size464 = _rtmp3468.size;
        for (var _i469 = 0; _i469 < _size464; ++_i469)
        {
          var elem470 = null;
          elem470 = new Proposition();
          elem470.read(input);
          this.propositions.push(elem470);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.logoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.advertiserId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Publisher.prototype.write = function(output) {
  output.writeStructBegin('Publisher');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.displayName !== null && this.displayName !== undefined) {
    output.writeFieldBegin('displayName', Thrift.Type.STRING, 3);
    output.writeString(this.displayName);
    output.writeFieldEnd();
  }
  if (this.active !== null && this.active !== undefined) {
    output.writeFieldBegin('active', Thrift.Type.BOOL, 4);
    output.writeBool(this.active);
    output.writeFieldEnd();
  }
  if (this.primaryReserveCount !== null && this.primaryReserveCount !== undefined) {
    output.writeFieldBegin('primaryReserveCount', Thrift.Type.I32, 5);
    output.writeI32(this.primaryReserveCount);
    output.writeFieldEnd();
  }
  if (this.primaryReserveExpiryInDays !== null && this.primaryReserveExpiryInDays !== undefined) {
    output.writeFieldBegin('primaryReserveExpiryInDays', Thrift.Type.I32, 6);
    output.writeI32(this.primaryReserveExpiryInDays);
    output.writeFieldEnd();
  }
  if (this.secondaryReserveCount !== null && this.secondaryReserveCount !== undefined) {
    output.writeFieldBegin('secondaryReserveCount', Thrift.Type.I32, 7);
    output.writeI32(this.secondaryReserveCount);
    output.writeFieldEnd();
  }
  if (this.secondaryReserveExpiryInDays !== null && this.secondaryReserveExpiryInDays !== undefined) {
    output.writeFieldBegin('secondaryReserveExpiryInDays', Thrift.Type.I32, 8);
    output.writeI32(this.secondaryReserveExpiryInDays);
    output.writeFieldEnd();
  }
  if (this.propositions !== null && this.propositions !== undefined) {
    output.writeFieldBegin('propositions', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRUCT, this.propositions.length);
    for (var iter471 in this.propositions)
    {
      if (this.propositions.hasOwnProperty(iter471))
      {
        iter471 = this.propositions[iter471];
        iter471.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.logoUrl !== null && this.logoUrl !== undefined) {
    output.writeFieldBegin('logoUrl', Thrift.Type.STRING, 10);
    output.writeString(this.logoUrl);
    output.writeFieldEnd();
  }
  if (this.advertiserId !== null && this.advertiserId !== undefined) {
    output.writeFieldBegin('advertiserId', Thrift.Type.STRING, 11);
    output.writeString(this.advertiserId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PublisherData = function(args) {
  this.id = null;
  this.displayName = null;
  this.logoUrl = null;
  this.advertiserId = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.displayName !== undefined && args.displayName !== null) {
      this.displayName = args.displayName;
    }
    if (args.logoUrl !== undefined && args.logoUrl !== null) {
      this.logoUrl = args.logoUrl;
    }
    if (args.advertiserId !== undefined && args.advertiserId !== null) {
      this.advertiserId = args.advertiserId;
    }
  }
};
PublisherData.prototype = {};
PublisherData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.displayName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.logoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.advertiserId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PublisherData.prototype.write = function(output) {
  output.writeStructBegin('PublisherData');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.displayName !== null && this.displayName !== undefined) {
    output.writeFieldBegin('displayName', Thrift.Type.STRING, 2);
    output.writeString(this.displayName);
    output.writeFieldEnd();
  }
  if (this.logoUrl !== null && this.logoUrl !== undefined) {
    output.writeFieldBegin('logoUrl', Thrift.Type.STRING, 3);
    output.writeString(this.logoUrl);
    output.writeFieldEnd();
  }
  if (this.advertiserId !== null && this.advertiserId !== undefined) {
    output.writeFieldBegin('advertiserId', Thrift.Type.STRING, 4);
    output.writeString(this.advertiserId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ShoppableData = function(args) {
  this.iconToolTip = null;
  this.iconImageUrl = null;
  this.shoppingUrl = null;
  this.offlineShoppingZipUrl = null;
  if (args) {
    if (args.iconToolTip !== undefined && args.iconToolTip !== null) {
      this.iconToolTip = args.iconToolTip;
    }
    if (args.iconImageUrl !== undefined && args.iconImageUrl !== null) {
      this.iconImageUrl = args.iconImageUrl;
    }
    if (args.shoppingUrl !== undefined && args.shoppingUrl !== null) {
      this.shoppingUrl = args.shoppingUrl;
    }
    if (args.offlineShoppingZipUrl !== undefined && args.offlineShoppingZipUrl !== null) {
      this.offlineShoppingZipUrl = args.offlineShoppingZipUrl;
    }
  }
};
ShoppableData.prototype = {};
ShoppableData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.iconToolTip = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.iconImageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.shoppingUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.offlineShoppingZipUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ShoppableData.prototype.write = function(output) {
  output.writeStructBegin('ShoppableData');
  if (this.iconToolTip !== null && this.iconToolTip !== undefined) {
    output.writeFieldBegin('iconToolTip', Thrift.Type.STRING, 1);
    output.writeString(this.iconToolTip);
    output.writeFieldEnd();
  }
  if (this.iconImageUrl !== null && this.iconImageUrl !== undefined) {
    output.writeFieldBegin('iconImageUrl', Thrift.Type.STRING, 2);
    output.writeString(this.iconImageUrl);
    output.writeFieldEnd();
  }
  if (this.shoppingUrl !== null && this.shoppingUrl !== undefined) {
    output.writeFieldBegin('shoppingUrl', Thrift.Type.STRING, 3);
    output.writeString(this.shoppingUrl);
    output.writeFieldEnd();
  }
  if (this.offlineShoppingZipUrl !== null && this.offlineShoppingZipUrl !== undefined) {
    output.writeFieldBegin('offlineShoppingZipUrl', Thrift.Type.STRING, 4);
    output.writeString(this.offlineShoppingZipUrl);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Programmatic = function(args) {
  this.advertiserId = null;
  this.creativeId = null;
  this.channelType = null;
  this.creativeHosting = null;
  this.cappingPacingCheck = null;
  this.streamType = null;
  this.sendDeviceId = null;
  this.sendSegmentId = null;
  this.id = null;
  if (args) {
    if (args.advertiserId !== undefined && args.advertiserId !== null) {
      this.advertiserId = args.advertiserId;
    }
    if (args.creativeId !== undefined && args.creativeId !== null) {
      this.creativeId = args.creativeId;
    }
    if (args.channelType !== undefined && args.channelType !== null) {
      this.channelType = args.channelType;
    }
    if (args.creativeHosting !== undefined && args.creativeHosting !== null) {
      this.creativeHosting = args.creativeHosting;
    }
    if (args.cappingPacingCheck !== undefined && args.cappingPacingCheck !== null) {
      this.cappingPacingCheck = args.cappingPacingCheck;
    }
    if (args.streamType !== undefined && args.streamType !== null) {
      this.streamType = args.streamType;
    }
    if (args.sendDeviceId !== undefined && args.sendDeviceId !== null) {
      this.sendDeviceId = args.sendDeviceId;
    }
    if (args.sendSegmentId !== undefined && args.sendSegmentId !== null) {
      this.sendSegmentId = args.sendSegmentId;
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
  }
};
Programmatic.prototype = {};
Programmatic.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.advertiserId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.creativeId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.channelType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.creativeHosting = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.cappingPacingCheck = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.streamType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.sendDeviceId = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.sendSegmentId = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Programmatic.prototype.write = function(output) {
  output.writeStructBegin('Programmatic');
  if (this.advertiserId !== null && this.advertiserId !== undefined) {
    output.writeFieldBegin('advertiserId', Thrift.Type.STRING, 1);
    output.writeString(this.advertiserId);
    output.writeFieldEnd();
  }
  if (this.creativeId !== null && this.creativeId !== undefined) {
    output.writeFieldBegin('creativeId', Thrift.Type.STRING, 2);
    output.writeString(this.creativeId);
    output.writeFieldEnd();
  }
  if (this.channelType !== null && this.channelType !== undefined) {
    output.writeFieldBegin('channelType', Thrift.Type.I32, 3);
    output.writeI32(this.channelType);
    output.writeFieldEnd();
  }
  if (this.creativeHosting !== null && this.creativeHosting !== undefined) {
    output.writeFieldBegin('creativeHosting', Thrift.Type.I32, 4);
    output.writeI32(this.creativeHosting);
    output.writeFieldEnd();
  }
  if (this.cappingPacingCheck !== null && this.cappingPacingCheck !== undefined) {
    output.writeFieldBegin('cappingPacingCheck', Thrift.Type.BOOL, 5);
    output.writeBool(this.cappingPacingCheck);
    output.writeFieldEnd();
  }
  if (this.streamType !== null && this.streamType !== undefined) {
    output.writeFieldBegin('streamType', Thrift.Type.I32, 6);
    output.writeI32(this.streamType);
    output.writeFieldEnd();
  }
  if (this.sendDeviceId !== null && this.sendDeviceId !== undefined) {
    output.writeFieldBegin('sendDeviceId', Thrift.Type.BOOL, 7);
    output.writeBool(this.sendDeviceId);
    output.writeFieldEnd();
  }
  if (this.sendSegmentId !== null && this.sendSegmentId !== undefined) {
    output.writeFieldBegin('sendSegmentId', Thrift.Type.BOOL, 8);
    output.writeBool(this.sendSegmentId);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 9);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TrelloCardInfo = function(args) {
  this.id = null;
  this.boardId = null;
  this.members = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.boardId !== undefined && args.boardId !== null) {
      this.boardId = args.boardId;
    }
    if (args.members !== undefined && args.members !== null) {
      this.members = Thrift.copyList(args.members, [null]);
    }
  }
};
TrelloCardInfo.prototype = {};
TrelloCardInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.boardId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size472 = 0;
        var _rtmp3476;
        this.members = [];
        var _etype475 = 0;
        _rtmp3476 = input.readListBegin();
        _etype475 = _rtmp3476.etype;
        _size472 = _rtmp3476.size;
        for (var _i477 = 0; _i477 < _size472; ++_i477)
        {
          var elem478 = null;
          elem478 = input.readString().value;
          this.members.push(elem478);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TrelloCardInfo.prototype.write = function(output) {
  output.writeStructBegin('TrelloCardInfo');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.boardId !== null && this.boardId !== undefined) {
    output.writeFieldBegin('boardId', Thrift.Type.STRING, 2);
    output.writeString(this.boardId);
    output.writeFieldEnd();
  }
  if (this.members !== null && this.members !== undefined) {
    output.writeFieldBegin('members', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRING, this.members.length);
    for (var iter479 in this.members)
    {
      if (this.members.hasOwnProperty(iter479))
      {
        iter479 = this.members[iter479];
        output.writeString(iter479);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

FrequencyCaps = function(args) {
  this.daily = null;
  this.weekly = null;
  if (args) {
    if (args.daily !== undefined && args.daily !== null) {
      this.daily = args.daily;
    }
    if (args.weekly !== undefined && args.weekly !== null) {
      this.weekly = args.weekly;
    }
  }
};
FrequencyCaps.prototype = {};
FrequencyCaps.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.daily = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.weekly = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

FrequencyCaps.prototype.write = function(output) {
  output.writeStructBegin('FrequencyCaps');
  if (this.daily !== null && this.daily !== undefined) {
    output.writeFieldBegin('daily', Thrift.Type.I32, 1);
    output.writeI32(this.daily);
    output.writeFieldEnd();
  }
  if (this.weekly !== null && this.weekly !== undefined) {
    output.writeFieldBegin('weekly', Thrift.Type.I32, 2);
    output.writeI32(this.weekly);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppTargeting = function(args) {
  this.present = null;
  this.absent = null;
  this.anyPresent = null;
  this.anyAbsent = null;
  if (args) {
    if (args.present !== undefined && args.present !== null) {
      this.present = Thrift.copyList(args.present, [null]);
    }
    if (args.absent !== undefined && args.absent !== null) {
      this.absent = Thrift.copyList(args.absent, [null]);
    }
    if (args.anyPresent !== undefined && args.anyPresent !== null) {
      this.anyPresent = Thrift.copyList(args.anyPresent, [null]);
    }
    if (args.anyAbsent !== undefined && args.anyAbsent !== null) {
      this.anyAbsent = Thrift.copyList(args.anyAbsent, [null]);
    }
  }
};
AppTargeting.prototype = {};
AppTargeting.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size480 = 0;
        var _rtmp3484;
        this.present = [];
        var _etype483 = 0;
        _rtmp3484 = input.readListBegin();
        _etype483 = _rtmp3484.etype;
        _size480 = _rtmp3484.size;
        for (var _i485 = 0; _i485 < _size480; ++_i485)
        {
          var elem486 = null;
          elem486 = input.readString().value;
          this.present.push(elem486);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size487 = 0;
        var _rtmp3491;
        this.absent = [];
        var _etype490 = 0;
        _rtmp3491 = input.readListBegin();
        _etype490 = _rtmp3491.etype;
        _size487 = _rtmp3491.size;
        for (var _i492 = 0; _i492 < _size487; ++_i492)
        {
          var elem493 = null;
          elem493 = input.readString().value;
          this.absent.push(elem493);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size494 = 0;
        var _rtmp3498;
        this.anyPresent = [];
        var _etype497 = 0;
        _rtmp3498 = input.readListBegin();
        _etype497 = _rtmp3498.etype;
        _size494 = _rtmp3498.size;
        for (var _i499 = 0; _i499 < _size494; ++_i499)
        {
          var elem500 = null;
          elem500 = input.readString().value;
          this.anyPresent.push(elem500);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size501 = 0;
        var _rtmp3505;
        this.anyAbsent = [];
        var _etype504 = 0;
        _rtmp3505 = input.readListBegin();
        _etype504 = _rtmp3505.etype;
        _size501 = _rtmp3505.size;
        for (var _i506 = 0; _i506 < _size501; ++_i506)
        {
          var elem507 = null;
          elem507 = input.readString().value;
          this.anyAbsent.push(elem507);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppTargeting.prototype.write = function(output) {
  output.writeStructBegin('AppTargeting');
  if (this.present !== null && this.present !== undefined) {
    output.writeFieldBegin('present', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.present.length);
    for (var iter508 in this.present)
    {
      if (this.present.hasOwnProperty(iter508))
      {
        iter508 = this.present[iter508];
        output.writeString(iter508);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.absent !== null && this.absent !== undefined) {
    output.writeFieldBegin('absent', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.absent.length);
    for (var iter509 in this.absent)
    {
      if (this.absent.hasOwnProperty(iter509))
      {
        iter509 = this.absent[iter509];
        output.writeString(iter509);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.anyPresent !== null && this.anyPresent !== undefined) {
    output.writeFieldBegin('anyPresent', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRING, this.anyPresent.length);
    for (var iter510 in this.anyPresent)
    {
      if (this.anyPresent.hasOwnProperty(iter510))
      {
        iter510 = this.anyPresent[iter510];
        output.writeString(iter510);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.anyAbsent !== null && this.anyAbsent !== undefined) {
    output.writeFieldBegin('anyAbsent', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRING, this.anyAbsent.length);
    for (var iter511 in this.anyAbsent)
    {
      if (this.anyAbsent.hasOwnProperty(iter511))
      {
        iter511 = this.anyAbsent[iter511];
        output.writeString(iter511);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Targeting = function(args) {
  this.apps = null;
  this.requiresGPayReadiness = null;
  this.enableDSPRetargeting = null;
  this.childLockFlag = null;
  if (args) {
    if (args.apps !== undefined && args.apps !== null) {
      this.apps = new AppTargeting(args.apps);
    }
    if (args.requiresGPayReadiness !== undefined && args.requiresGPayReadiness !== null) {
      this.requiresGPayReadiness = args.requiresGPayReadiness;
    }
    if (args.enableDSPRetargeting !== undefined && args.enableDSPRetargeting !== null) {
      this.enableDSPRetargeting = args.enableDSPRetargeting;
    }
    if (args.childLockFlag !== undefined && args.childLockFlag !== null) {
      this.childLockFlag = args.childLockFlag;
    }
  }
};
Targeting.prototype = {};
Targeting.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.apps = new AppTargeting();
        this.apps.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.requiresGPayReadiness = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.enableDSPRetargeting = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.childLockFlag = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Targeting.prototype.write = function(output) {
  output.writeStructBegin('Targeting');
  if (this.apps !== null && this.apps !== undefined) {
    output.writeFieldBegin('apps', Thrift.Type.STRUCT, 1);
    this.apps.write(output);
    output.writeFieldEnd();
  }
  if (this.requiresGPayReadiness !== null && this.requiresGPayReadiness !== undefined) {
    output.writeFieldBegin('requiresGPayReadiness', Thrift.Type.BOOL, 2);
    output.writeBool(this.requiresGPayReadiness);
    output.writeFieldEnd();
  }
  if (this.enableDSPRetargeting !== null && this.enableDSPRetargeting !== undefined) {
    output.writeFieldBegin('enableDSPRetargeting', Thrift.Type.BOOL, 3);
    output.writeBool(this.enableDSPRetargeting);
    output.writeFieldEnd();
  }
  if (this.childLockFlag !== null && this.childLockFlag !== undefined) {
    output.writeFieldBegin('childLockFlag', Thrift.Type.BOOL, 4);
    output.writeBool(this.childLockFlag);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceProperty = function(args) {
  this.categories = null;
  if (args) {
    if (args.categories !== undefined && args.categories !== null) {
      this.categories = Thrift.copyList(args.categories, [null]);
    }
  }
};
GlanceProperty.prototype = {};
GlanceProperty.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size512 = 0;
        var _rtmp3516;
        this.categories = [];
        var _etype515 = 0;
        _rtmp3516 = input.readListBegin();
        _etype515 = _rtmp3516.etype;
        _size512 = _rtmp3516.size;
        for (var _i517 = 0; _i517 < _size512; ++_i517)
        {
          var elem518 = null;
          elem518 = input.readString().value;
          this.categories.push(elem518);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceProperty.prototype.write = function(output) {
  output.writeStructBegin('GlanceProperty');
  if (this.categories !== null && this.categories !== undefined) {
    output.writeFieldBegin('categories', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.categories.length);
    for (var iter519 in this.categories)
    {
      if (this.categories.hasOwnProperty(iter519))
      {
        iter519 = this.categories[iter519];
        output.writeString(iter519);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GameCategory = function(args) {
  this.name = null;
  if (args) {
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
  }
};
GameCategory.prototype = {};
GameCategory.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GameCategory.prototype.write = function(output) {
  output.writeStructBegin('GameCategory');
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 1);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Interaction = function(args) {
  this.glanceId = null;
  this.likeCount = null;
  this.shareCount = null;
  this.updatedAt = null;
  this.likeSeedCount = null;
  this.shareSeedCount = null;
  this.viewCount = null;
  this.liveViewCount = null;
  this.liveViewSeedCount = null;
  if (args) {
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    }
    if (args.likeCount !== undefined && args.likeCount !== null) {
      this.likeCount = args.likeCount;
    }
    if (args.shareCount !== undefined && args.shareCount !== null) {
      this.shareCount = args.shareCount;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.likeSeedCount !== undefined && args.likeSeedCount !== null) {
      this.likeSeedCount = args.likeSeedCount;
    }
    if (args.shareSeedCount !== undefined && args.shareSeedCount !== null) {
      this.shareSeedCount = args.shareSeedCount;
    }
    if (args.viewCount !== undefined && args.viewCount !== null) {
      this.viewCount = args.viewCount;
    }
    if (args.liveViewCount !== undefined && args.liveViewCount !== null) {
      this.liveViewCount = args.liveViewCount;
    }
    if (args.liveViewSeedCount !== undefined && args.liveViewSeedCount !== null) {
      this.liveViewSeedCount = args.liveViewSeedCount;
    }
  }
};
Interaction.prototype = {};
Interaction.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.likeCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.shareCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.likeSeedCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.shareSeedCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I64) {
        this.viewCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I64) {
        this.liveViewCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I64) {
        this.liveViewSeedCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Interaction.prototype.write = function(output) {
  output.writeStructBegin('Interaction');
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.likeCount !== null && this.likeCount !== undefined) {
    output.writeFieldBegin('likeCount', Thrift.Type.I64, 2);
    output.writeI64(this.likeCount);
    output.writeFieldEnd();
  }
  if (this.shareCount !== null && this.shareCount !== undefined) {
    output.writeFieldBegin('shareCount', Thrift.Type.I64, 3);
    output.writeI64(this.shareCount);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 4);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.likeSeedCount !== null && this.likeSeedCount !== undefined) {
    output.writeFieldBegin('likeSeedCount', Thrift.Type.I64, 5);
    output.writeI64(this.likeSeedCount);
    output.writeFieldEnd();
  }
  if (this.shareSeedCount !== null && this.shareSeedCount !== undefined) {
    output.writeFieldBegin('shareSeedCount', Thrift.Type.I64, 6);
    output.writeI64(this.shareSeedCount);
    output.writeFieldEnd();
  }
  if (this.viewCount !== null && this.viewCount !== undefined) {
    output.writeFieldBegin('viewCount', Thrift.Type.I64, 7);
    output.writeI64(this.viewCount);
    output.writeFieldEnd();
  }
  if (this.liveViewCount !== null && this.liveViewCount !== undefined) {
    output.writeFieldBegin('liveViewCount', Thrift.Type.I64, 8);
    output.writeI64(this.liveViewCount);
    output.writeFieldEnd();
  }
  if (this.liveViewSeedCount !== null && this.liveViewSeedCount !== undefined) {
    output.writeFieldBegin('liveViewSeedCount', Thrift.Type.I64, 9);
    output.writeI64(this.liveViewSeedCount);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RoposoInteraction = function(args) {
  this.glanceId = null;
  this.likeCount = null;
  this.shareCount = null;
  this.viewCount = null;
  this.roposoStoryId = null;
  if (args) {
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    }
    if (args.likeCount !== undefined && args.likeCount !== null) {
      this.likeCount = args.likeCount;
    }
    if (args.shareCount !== undefined && args.shareCount !== null) {
      this.shareCount = args.shareCount;
    }
    if (args.viewCount !== undefined && args.viewCount !== null) {
      this.viewCount = args.viewCount;
    }
    if (args.roposoStoryId !== undefined && args.roposoStoryId !== null) {
      this.roposoStoryId = args.roposoStoryId;
    }
  }
};
RoposoInteraction.prototype = {};
RoposoInteraction.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.likeCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.shareCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.viewCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.roposoStoryId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RoposoInteraction.prototype.write = function(output) {
  output.writeStructBegin('RoposoInteraction');
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.likeCount !== null && this.likeCount !== undefined) {
    output.writeFieldBegin('likeCount', Thrift.Type.I64, 2);
    output.writeI64(this.likeCount);
    output.writeFieldEnd();
  }
  if (this.shareCount !== null && this.shareCount !== undefined) {
    output.writeFieldBegin('shareCount', Thrift.Type.I64, 3);
    output.writeI64(this.shareCount);
    output.writeFieldEnd();
  }
  if (this.viewCount !== null && this.viewCount !== undefined) {
    output.writeFieldBegin('viewCount', Thrift.Type.I64, 4);
    output.writeI64(this.viewCount);
    output.writeFieldEnd();
  }
  if (this.roposoStoryId !== null && this.roposoStoryId !== undefined) {
    output.writeFieldBegin('roposoStoryId', Thrift.Type.STRING, 5);
    output.writeString(this.roposoStoryId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Bubble = function(args) {
  this.bubbleId = null;
  this.name = null;
  this.imagePath = null;
  this.autoNext = null;
  this.overlayImagePath = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.glanceType = null;
  this.qualifier = null;
  this.lastPublishedAtInSecs = null;
  this.glanceProperty = null;
  this.glanceIds = null;
  this.language = null;
  this.status = null;
  this.bubbleType = null;
  this.storyCount = null;
  this.ecpm = null;
  this.bucketPriority = null;
  this.adCoefficient = null;
  this.publishingPartnerId = null;
  this.glancePublishTimeCheckpoint = null;
  this.publishingPartner = null;
  this.country = null;
  this.parentBubble = null;
  if (args) {
    if (args.bubbleId !== undefined && args.bubbleId !== null) {
      this.bubbleId = args.bubbleId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field bubbleId is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.imagePath !== undefined && args.imagePath !== null) {
      this.imagePath = args.imagePath;
    }
    if (args.autoNext !== undefined && args.autoNext !== null) {
      this.autoNext = args.autoNext;
    }
    if (args.overlayImagePath !== undefined && args.overlayImagePath !== null) {
      this.overlayImagePath = args.overlayImagePath;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.glanceType !== undefined && args.glanceType !== null) {
      this.glanceType = args.glanceType;
    }
    if (args.qualifier !== undefined && args.qualifier !== null) {
      this.qualifier = args.qualifier;
    }
    if (args.lastPublishedAtInSecs !== undefined && args.lastPublishedAtInSecs !== null) {
      this.lastPublishedAtInSecs = args.lastPublishedAtInSecs;
    }
    if (args.glanceProperty !== undefined && args.glanceProperty !== null) {
      this.glanceProperty = new GlanceProperty(args.glanceProperty);
    }
    if (args.glanceIds !== undefined && args.glanceIds !== null) {
      this.glanceIds = Thrift.copyList(args.glanceIds, [null]);
    }
    if (args.language !== undefined && args.language !== null) {
      this.language = args.language;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.bubbleType !== undefined && args.bubbleType !== null) {
      this.bubbleType = args.bubbleType;
    }
    if (args.storyCount !== undefined && args.storyCount !== null) {
      this.storyCount = args.storyCount;
    }
    if (args.ecpm !== undefined && args.ecpm !== null) {
      this.ecpm = args.ecpm;
    }
    if (args.bucketPriority !== undefined && args.bucketPriority !== null) {
      this.bucketPriority = args.bucketPriority;
    }
    if (args.adCoefficient !== undefined && args.adCoefficient !== null) {
      this.adCoefficient = args.adCoefficient;
    }
    if (args.publishingPartnerId !== undefined && args.publishingPartnerId !== null) {
      this.publishingPartnerId = args.publishingPartnerId;
    }
    if (args.glancePublishTimeCheckpoint !== undefined && args.glancePublishTimeCheckpoint !== null) {
      this.glancePublishTimeCheckpoint = args.glancePublishTimeCheckpoint;
    }
    if (args.publishingPartner !== undefined && args.publishingPartner !== null) {
      this.publishingPartner = args.publishingPartner;
    }
    if (args.country !== undefined && args.country !== null) {
      this.country = args.country;
    }
    if (args.parentBubble !== undefined && args.parentBubble !== null) {
      this.parentBubble = args.parentBubble;
    }
  }
};
Bubble.prototype = {};
Bubble.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.bubbleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.imagePath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.autoNext = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.overlayImagePath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.glanceType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.qualifier = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I64) {
        this.lastPublishedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceProperty = new GlanceProperty();
        this.glanceProperty.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.LIST) {
        var _size520 = 0;
        var _rtmp3524;
        this.glanceIds = [];
        var _etype523 = 0;
        _rtmp3524 = input.readListBegin();
        _etype523 = _rtmp3524.etype;
        _size520 = _rtmp3524.size;
        for (var _i525 = 0; _i525 < _size520; ++_i525)
        {
          var elem526 = null;
          elem526 = input.readString().value;
          this.glanceIds.push(elem526);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.language = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.I32) {
        this.bubbleType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.I32) {
        this.storyCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.DOUBLE) {
        this.ecpm = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.I32) {
        this.bucketPriority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.I32) {
        this.adCoefficient = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.publishingPartnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.I64) {
        this.glancePublishTimeCheckpoint = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRING) {
        this.publishingPartner = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.country = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.STRING) {
        this.parentBubble = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Bubble.prototype.write = function(output) {
  output.writeStructBegin('Bubble');
  if (this.bubbleId !== null && this.bubbleId !== undefined) {
    output.writeFieldBegin('bubbleId', Thrift.Type.STRING, 1);
    output.writeString(this.bubbleId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.imagePath !== null && this.imagePath !== undefined) {
    output.writeFieldBegin('imagePath', Thrift.Type.STRING, 3);
    output.writeString(this.imagePath);
    output.writeFieldEnd();
  }
  if (this.autoNext !== null && this.autoNext !== undefined) {
    output.writeFieldBegin('autoNext', Thrift.Type.BOOL, 4);
    output.writeBool(this.autoNext);
    output.writeFieldEnd();
  }
  if (this.overlayImagePath !== null && this.overlayImagePath !== undefined) {
    output.writeFieldBegin('overlayImagePath', Thrift.Type.STRING, 5);
    output.writeString(this.overlayImagePath);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 6);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 7);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.glanceType !== null && this.glanceType !== undefined) {
    output.writeFieldBegin('glanceType', Thrift.Type.I32, 8);
    output.writeI32(this.glanceType);
    output.writeFieldEnd();
  }
  if (this.qualifier !== null && this.qualifier !== undefined) {
    output.writeFieldBegin('qualifier', Thrift.Type.STRING, 9);
    output.writeString(this.qualifier);
    output.writeFieldEnd();
  }
  if (this.lastPublishedAtInSecs !== null && this.lastPublishedAtInSecs !== undefined) {
    output.writeFieldBegin('lastPublishedAtInSecs', Thrift.Type.I64, 10);
    output.writeI64(this.lastPublishedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.glanceProperty !== null && this.glanceProperty !== undefined) {
    output.writeFieldBegin('glanceProperty', Thrift.Type.STRUCT, 11);
    this.glanceProperty.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceIds !== null && this.glanceIds !== undefined) {
    output.writeFieldBegin('glanceIds', Thrift.Type.LIST, 12);
    output.writeListBegin(Thrift.Type.STRING, this.glanceIds.length);
    for (var iter527 in this.glanceIds)
    {
      if (this.glanceIds.hasOwnProperty(iter527))
      {
        iter527 = this.glanceIds[iter527];
        output.writeString(iter527);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.language !== null && this.language !== undefined) {
    output.writeFieldBegin('language', Thrift.Type.STRING, 13);
    output.writeString(this.language);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 14);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  if (this.bubbleType !== null && this.bubbleType !== undefined) {
    output.writeFieldBegin('bubbleType', Thrift.Type.I32, 15);
    output.writeI32(this.bubbleType);
    output.writeFieldEnd();
  }
  if (this.storyCount !== null && this.storyCount !== undefined) {
    output.writeFieldBegin('storyCount', Thrift.Type.I32, 16);
    output.writeI32(this.storyCount);
    output.writeFieldEnd();
  }
  if (this.ecpm !== null && this.ecpm !== undefined) {
    output.writeFieldBegin('ecpm', Thrift.Type.DOUBLE, 17);
    output.writeDouble(this.ecpm);
    output.writeFieldEnd();
  }
  if (this.bucketPriority !== null && this.bucketPriority !== undefined) {
    output.writeFieldBegin('bucketPriority', Thrift.Type.I32, 18);
    output.writeI32(this.bucketPriority);
    output.writeFieldEnd();
  }
  if (this.adCoefficient !== null && this.adCoefficient !== undefined) {
    output.writeFieldBegin('adCoefficient', Thrift.Type.I32, 19);
    output.writeI32(this.adCoefficient);
    output.writeFieldEnd();
  }
  if (this.publishingPartnerId !== null && this.publishingPartnerId !== undefined) {
    output.writeFieldBegin('publishingPartnerId', Thrift.Type.STRING, 20);
    output.writeString(this.publishingPartnerId);
    output.writeFieldEnd();
  }
  if (this.glancePublishTimeCheckpoint !== null && this.glancePublishTimeCheckpoint !== undefined) {
    output.writeFieldBegin('glancePublishTimeCheckpoint', Thrift.Type.I64, 21);
    output.writeI64(this.glancePublishTimeCheckpoint);
    output.writeFieldEnd();
  }
  if (this.publishingPartner !== null && this.publishingPartner !== undefined) {
    output.writeFieldBegin('publishingPartner', Thrift.Type.STRING, 22);
    output.writeString(this.publishingPartner);
    output.writeFieldEnd();
  }
  if (this.country !== null && this.country !== undefined) {
    output.writeFieldBegin('country', Thrift.Type.STRING, 23);
    output.writeString(this.country);
    output.writeFieldEnd();
  }
  if (this.parentBubble !== null && this.parentBubble !== undefined) {
    output.writeFieldBegin('parentBubble', Thrift.Type.STRING, 24);
    output.writeString(this.parentBubble);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceBubbleDetails = function(args) {
  this.glanceTimer = null;
  if (args) {
    if (args.glanceTimer !== undefined && args.glanceTimer !== null) {
      this.glanceTimer = args.glanceTimer;
    }
  }
};
GlanceBubbleDetails.prototype = {};
GlanceBubbleDetails.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.glanceTimer = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceBubbleDetails.prototype.write = function(output) {
  output.writeStructBegin('GlanceBubbleDetails');
  if (this.glanceTimer !== null && this.glanceTimer !== undefined) {
    output.writeFieldBegin('glanceTimer', Thrift.Type.I64, 1);
    output.writeI64(this.glanceTimer);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceCampaignMeta = function(args) {
  this.objective = null;
  this.eCpm = null;
  this.glroId = null;
  if (args) {
    if (args.objective !== undefined && args.objective !== null) {
      this.objective = args.objective;
    }
    if (args.eCpm !== undefined && args.eCpm !== null) {
      this.eCpm = args.eCpm;
    }
    if (args.glroId !== undefined && args.glroId !== null) {
      this.glroId = args.glroId;
    }
  }
};
GlanceCampaignMeta.prototype = {};
GlanceCampaignMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.objective = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.eCpm = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.glroId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceCampaignMeta.prototype.write = function(output) {
  output.writeStructBegin('GlanceCampaignMeta');
  if (this.objective !== null && this.objective !== undefined) {
    output.writeFieldBegin('objective', Thrift.Type.STRING, 1);
    output.writeString(this.objective);
    output.writeFieldEnd();
  }
  if (this.eCpm !== null && this.eCpm !== undefined) {
    output.writeFieldBegin('eCpm', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.eCpm);
    output.writeFieldEnd();
  }
  if (this.glroId !== null && this.glroId !== undefined) {
    output.writeFieldBegin('glroId', Thrift.Type.STRING, 3);
    output.writeString(this.glroId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OmSdkInfo = function(args) {
  this.validationUrl = null;
  this.verificationParam = null;
  this.vendorKey = null;
  this.type = null;
  if (args) {
    if (args.validationUrl !== undefined && args.validationUrl !== null) {
      this.validationUrl = args.validationUrl;
    }
    if (args.verificationParam !== undefined && args.verificationParam !== null) {
      this.verificationParam = args.verificationParam;
    }
    if (args.vendorKey !== undefined && args.vendorKey !== null) {
      this.vendorKey = args.vendorKey;
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    }
  }
};
OmSdkInfo.prototype = {};
OmSdkInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.validationUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.verificationParam = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.vendorKey = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OmSdkInfo.prototype.write = function(output) {
  output.writeStructBegin('OmSdkInfo');
  if (this.validationUrl !== null && this.validationUrl !== undefined) {
    output.writeFieldBegin('validationUrl', Thrift.Type.STRING, 1);
    output.writeString(this.validationUrl);
    output.writeFieldEnd();
  }
  if (this.verificationParam !== null && this.verificationParam !== undefined) {
    output.writeFieldBegin('verificationParam', Thrift.Type.STRING, 2);
    output.writeString(this.verificationParam);
    output.writeFieldEnd();
  }
  if (this.vendorKey !== null && this.vendorKey !== undefined) {
    output.writeFieldBegin('vendorKey', Thrift.Type.STRING, 3);
    output.writeString(this.vendorKey);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.I32, 4);
    output.writeI32(this.type);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CtaViewConfig = function(args) {
  this.height = null;
  this.removeTopPadding = null;
  this.hideCrossIcon = null;
  this.dragable = null;
  if (args) {
    if (args.height !== undefined && args.height !== null) {
      this.height = args.height;
    }
    if (args.removeTopPadding !== undefined && args.removeTopPadding !== null) {
      this.removeTopPadding = args.removeTopPadding;
    }
    if (args.hideCrossIcon !== undefined && args.hideCrossIcon !== null) {
      this.hideCrossIcon = args.hideCrossIcon;
    }
    if (args.dragable !== undefined && args.dragable !== null) {
      this.dragable = args.dragable;
    }
  }
};
CtaViewConfig.prototype = {};
CtaViewConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.height = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.removeTopPadding = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.hideCrossIcon = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.dragable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CtaViewConfig.prototype.write = function(output) {
  output.writeStructBegin('CtaViewConfig');
  if (this.height !== null && this.height !== undefined) {
    output.writeFieldBegin('height', Thrift.Type.I32, 1);
    output.writeI32(this.height);
    output.writeFieldEnd();
  }
  if (this.removeTopPadding !== null && this.removeTopPadding !== undefined) {
    output.writeFieldBegin('removeTopPadding', Thrift.Type.BOOL, 2);
    output.writeBool(this.removeTopPadding);
    output.writeFieldEnd();
  }
  if (this.hideCrossIcon !== null && this.hideCrossIcon !== undefined) {
    output.writeFieldBegin('hideCrossIcon', Thrift.Type.BOOL, 3);
    output.writeBool(this.hideCrossIcon);
    output.writeFieldEnd();
  }
  if (this.dragable !== null && this.dragable !== undefined) {
    output.writeFieldBegin('dragable', Thrift.Type.BOOL, 4);
    output.writeBool(this.dragable);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OciConfigs = function(args) {
  this.appOpenNudgeConfig = null;
  this.appOpenNotificationConfig = null;
  if (args) {
    if (args.appOpenNudgeConfig !== undefined && args.appOpenNudgeConfig !== null) {
      this.appOpenNudgeConfig = new AppOpenNudgeConfig(args.appOpenNudgeConfig);
    }
    if (args.appOpenNotificationConfig !== undefined && args.appOpenNotificationConfig !== null) {
      this.appOpenNotificationConfig = new AppOpenNotificationConfig(args.appOpenNotificationConfig);
    }
  }
};
OciConfigs.prototype = {};
OciConfigs.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.appOpenNudgeConfig = new AppOpenNudgeConfig();
        this.appOpenNudgeConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.appOpenNotificationConfig = new AppOpenNotificationConfig();
        this.appOpenNotificationConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OciConfigs.prototype.write = function(output) {
  output.writeStructBegin('OciConfigs');
  if (this.appOpenNudgeConfig !== null && this.appOpenNudgeConfig !== undefined) {
    output.writeFieldBegin('appOpenNudgeConfig', Thrift.Type.STRUCT, 1);
    this.appOpenNudgeConfig.write(output);
    output.writeFieldEnd();
  }
  if (this.appOpenNotificationConfig !== null && this.appOpenNotificationConfig !== undefined) {
    output.writeFieldBegin('appOpenNotificationConfig', Thrift.Type.STRUCT, 2);
    this.appOpenNotificationConfig.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

FastCardData = function(args) {
  this.enable_fc_deeplink = null;
  this.fc_deeplink = null;
  if (args) {
    if (args.enable_fc_deeplink !== undefined && args.enable_fc_deeplink !== null) {
      this.enable_fc_deeplink = args.enable_fc_deeplink;
    }
    if (args.fc_deeplink !== undefined && args.fc_deeplink !== null) {
      this.fc_deeplink = args.fc_deeplink;
    }
  }
};
FastCardData.prototype = {};
FastCardData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.enable_fc_deeplink = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.fc_deeplink = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

FastCardData.prototype.write = function(output) {
  output.writeStructBegin('FastCardData');
  if (this.enable_fc_deeplink !== null && this.enable_fc_deeplink !== undefined) {
    output.writeFieldBegin('enable_fc_deeplink', Thrift.Type.BOOL, 1);
    output.writeBool(this.enable_fc_deeplink);
    output.writeFieldEnd();
  }
  if (this.fc_deeplink !== null && this.fc_deeplink !== undefined) {
    output.writeFieldBegin('fc_deeplink', Thrift.Type.STRING, 2);
    output.writeString(this.fc_deeplink);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlamCampaignData = function(args) {
  this.campaignId = null;
  this.adSetId = null;
  this.adId = null;
  this.creativeGroupId = null;
  this.campaignObjectiveId = null;
  this.creativeId = null;
  if (args) {
    if (args.campaignId !== undefined && args.campaignId !== null) {
      this.campaignId = args.campaignId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field campaignId is unset!');
    }
    if (args.adSetId !== undefined && args.adSetId !== null) {
      this.adSetId = args.adSetId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field adSetId is unset!');
    }
    if (args.adId !== undefined && args.adId !== null) {
      this.adId = args.adId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field adId is unset!');
    }
    if (args.creativeGroupId !== undefined && args.creativeGroupId !== null) {
      this.creativeGroupId = args.creativeGroupId;
    }
    if (args.campaignObjectiveId !== undefined && args.campaignObjectiveId !== null) {
      this.campaignObjectiveId = args.campaignObjectiveId;
    }
    if (args.creativeId !== undefined && args.creativeId !== null) {
      this.creativeId = args.creativeId;
    }
  }
};
GlamCampaignData.prototype = {};
GlamCampaignData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.campaignId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.adSetId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.adId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.creativeGroupId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.campaignObjectiveId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.creativeId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlamCampaignData.prototype.write = function(output) {
  output.writeStructBegin('GlamCampaignData');
  if (this.campaignId !== null && this.campaignId !== undefined) {
    output.writeFieldBegin('campaignId', Thrift.Type.STRING, 1);
    output.writeString(this.campaignId);
    output.writeFieldEnd();
  }
  if (this.adSetId !== null && this.adSetId !== undefined) {
    output.writeFieldBegin('adSetId', Thrift.Type.STRING, 2);
    output.writeString(this.adSetId);
    output.writeFieldEnd();
  }
  if (this.adId !== null && this.adId !== undefined) {
    output.writeFieldBegin('adId', Thrift.Type.STRING, 3);
    output.writeString(this.adId);
    output.writeFieldEnd();
  }
  if (this.creativeGroupId !== null && this.creativeGroupId !== undefined) {
    output.writeFieldBegin('creativeGroupId', Thrift.Type.STRING, 4);
    output.writeString(this.creativeGroupId);
    output.writeFieldEnd();
  }
  if (this.campaignObjectiveId !== null && this.campaignObjectiveId !== undefined) {
    output.writeFieldBegin('campaignObjectiveId', Thrift.Type.STRING, 5);
    output.writeString(this.campaignObjectiveId);
    output.writeFieldEnd();
  }
  if (this.creativeId !== null && this.creativeId !== undefined) {
    output.writeFieldBegin('creativeId', Thrift.Type.STRING, 6);
    output.writeString(this.creativeId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ValuePropInfoThrift = function(args) {
  this.region = null;
  this.vpId = null;
  if (args) {
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.vpId !== undefined && args.vpId !== null) {
      this.vpId = args.vpId;
    }
  }
};
ValuePropInfoThrift.prototype = {};
ValuePropInfoThrift.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.vpId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ValuePropInfoThrift.prototype.write = function(output) {
  output.writeStructBegin('ValuePropInfoThrift');
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 1);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.vpId !== null && this.vpId !== undefined) {
    output.writeFieldBegin('vpId', Thrift.Type.STRING, 2);
    output.writeString(this.vpId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

EuInfoThrift = function(args) {
  this.region = null;
  this.euId = null;
  if (args) {
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.euId !== undefined && args.euId !== null) {
      this.euId = args.euId;
    }
  }
};
EuInfoThrift.prototype = {};
EuInfoThrift.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.euId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

EuInfoThrift.prototype.write = function(output) {
  output.writeStructBegin('EuInfoThrift');
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 1);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.euId !== null && this.euId !== undefined) {
    output.writeFieldBegin('euId', Thrift.Type.STRING, 2);
    output.writeString(this.euId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DspCampaignData = function(args) {
  this.isDspCampaign = null;
  this.dspAdvertiserGuid = null;
  if (args) {
    if (args.isDspCampaign !== undefined && args.isDspCampaign !== null) {
      this.isDspCampaign = args.isDspCampaign;
    }
    if (args.dspAdvertiserGuid !== undefined && args.dspAdvertiserGuid !== null) {
      this.dspAdvertiserGuid = args.dspAdvertiserGuid;
    }
  }
};
DspCampaignData.prototype = {};
DspCampaignData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.isDspCampaign = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.dspAdvertiserGuid = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DspCampaignData.prototype.write = function(output) {
  output.writeStructBegin('DspCampaignData');
  if (this.isDspCampaign !== null && this.isDspCampaign !== undefined) {
    output.writeFieldBegin('isDspCampaign', Thrift.Type.BOOL, 1);
    output.writeBool(this.isDspCampaign);
    output.writeFieldEnd();
  }
  if (this.dspAdvertiserGuid !== null && this.dspAdvertiserGuid !== undefined) {
    output.writeFieldBegin('dspAdvertiserGuid', Thrift.Type.STRING, 2);
    output.writeString(this.dspAdvertiserGuid);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceData = function(args) {
  this.id = null;
  this.glanceId = null;
  this.image = null;
  this.type = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.peekData = null;
  this.peek = null;
  this.priority = 0;
  this.publisherData = null;
  this.categoryIds = null;
  this.destinationUrlFailedAt = null;
  this.version = null;
  this.frequencyCaps = null;
  this.beacons = null;
  this.renderSessionMode = null;
  this.offlinePeek = null;
  this.stickinessCount = null;
  this.storyType = null;
  this.cityIds = null;
  this.segmentIds = null;
  this.userAgeType = null;
  this.categorySubscriptionStatus = null;
  this.targetForCategory = null;
  this.isShareable = null;
  this.shareUrl = null;
  this.overlayImage = null;
  this.glanceFeatures = null;
  this.bingeGlanceId = null;
  this.overlayGlanceId = null;
  this.targetForCategories = null;
  this.glanceLanguageId = null;
  this.vernacularGlances = null;
  this.shoppableData = null;
  this.antiSegmentIds = null;
  this.imageDbId = null;
  this.overlayImageDbId = null;
  this.updatedAt = null;
  this.userLifeTimeFilter = null;
  this.targeting = null;
  this.tinyShareUrl = null;
  this.topicName = null;
  this.homescreenWorthy = null;
  this.bubbleId = null;
  this.glanceBubbleDetails = null;
  this.cardPressId = null;
  this.carriers = null;
  this.eId = null;
  this.renderSessionProperties = null;
  this.userNetworkTypes = null;
  this.allowKeyboard = null;
  this.campaignMeta = null;
  this.beaconsInfo = null;
  this.omSdkInfo = null;
  this.sourceCreatorId = null;
  this.source = null;
  this.draftId = null;
  this.rankInStory = null;
  this.dynamicOverlay = null;
  this.impressionCappingId = null;
  this.ctaViewConfig = null;
  this.rankInBubble = null;
  this.adCoefficient = null;
  this.bucketPriority = null;
  this.euIdentifier = null;
  this.subCategories = null;
  this.suggestedSubCategory = null;
  this.allowNativeKeyboard = null;
  this.otherTopics = null;
  this.l3Tags = null;
  this.programmaticDetail = null;
  this.programmatic = null;
  this.ociConfigs = null;
  this.isBeautifulWallpaperEnabled = null;
  this.assetFileSizeInBytes = null;
  this.spaceData = null;
  this.publishingDestination = null;
  this.destinationIdentifier = null;
  this.event = null;
  this.liveEventData = null;
  this.tags = null;
  this.updatedBy = null;
  this.gamingEuSpaceData = null;
  this.gameMetaData = null;
  this.viewMoreCta = null;
  this.sportEuSpaceData = null;
  this.spaces = null;
  this.eu = null;
  this.fastCardData = null;
  this.glamCampaignData = null;
  this.valueProp = null;
  this.eus = null;
  this.valueProps = null;
  this.gamPgAdUnitId = null;
  this.images = null;
  this.useUnlockNudge = null;
  this.dspCampaignData = null;
  this.unknownEnumFlag = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
    if (args.image !== undefined && args.image !== null) {
      this.image = new Image(args.image);
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field type is unset!');
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field createdAtInSecs is unset!');
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field updatedAtInSecs is unset!');
    }
    if (args.peekData !== undefined && args.peekData !== null) {
      this.peekData = new PeekData(args.peekData);
    }
    if (args.peek !== undefined && args.peek !== null) {
      this.peek = new Peek(args.peek);
    }
    if (args.priority !== undefined && args.priority !== null) {
      this.priority = args.priority;
    }
    if (args.publisherData !== undefined && args.publisherData !== null) {
      this.publisherData = new PublisherData(args.publisherData);
    }
    if (args.categoryIds !== undefined && args.categoryIds !== null) {
      this.categoryIds = Thrift.copyList(args.categoryIds, [null]);
    }
    if (args.destinationUrlFailedAt !== undefined && args.destinationUrlFailedAt !== null) {
      this.destinationUrlFailedAt = args.destinationUrlFailedAt;
    }
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
    if (args.frequencyCaps !== undefined && args.frequencyCaps !== null) {
      this.frequencyCaps = new FrequencyCaps(args.frequencyCaps);
    }
    if (args.beacons !== undefined && args.beacons !== null) {
      this.beacons = new Beacons(args.beacons);
    }
    if (args.renderSessionMode !== undefined && args.renderSessionMode !== null) {
      this.renderSessionMode = args.renderSessionMode;
    }
    if (args.offlinePeek !== undefined && args.offlinePeek !== null) {
      this.offlinePeek = new Peek(args.offlinePeek);
    }
    if (args.stickinessCount !== undefined && args.stickinessCount !== null) {
      this.stickinessCount = args.stickinessCount;
    }
    if (args.storyType !== undefined && args.storyType !== null) {
      this.storyType = args.storyType;
    }
    if (args.cityIds !== undefined && args.cityIds !== null) {
      this.cityIds = Thrift.copyList(args.cityIds, [null]);
    }
    if (args.segmentIds !== undefined && args.segmentIds !== null) {
      this.segmentIds = Thrift.copyList(args.segmentIds, [null]);
    }
    if (args.userAgeType !== undefined && args.userAgeType !== null) {
      this.userAgeType = args.userAgeType;
    }
    if (args.categorySubscriptionStatus !== undefined && args.categorySubscriptionStatus !== null) {
      this.categorySubscriptionStatus = args.categorySubscriptionStatus;
    }
    if (args.targetForCategory !== undefined && args.targetForCategory !== null) {
      this.targetForCategory = args.targetForCategory;
    }
    if (args.isShareable !== undefined && args.isShareable !== null) {
      this.isShareable = args.isShareable;
    }
    if (args.shareUrl !== undefined && args.shareUrl !== null) {
      this.shareUrl = args.shareUrl;
    }
    if (args.overlayImage !== undefined && args.overlayImage !== null) {
      this.overlayImage = new Image(args.overlayImage);
    }
    if (args.glanceFeatures !== undefined && args.glanceFeatures !== null) {
      this.glanceFeatures = Thrift.copyList(args.glanceFeatures, [null]);
    }
    if (args.bingeGlanceId !== undefined && args.bingeGlanceId !== null) {
      this.bingeGlanceId = args.bingeGlanceId;
    }
    if (args.overlayGlanceId !== undefined && args.overlayGlanceId !== null) {
      this.overlayGlanceId = args.overlayGlanceId;
    }
    if (args.targetForCategories !== undefined && args.targetForCategories !== null) {
      this.targetForCategories = Thrift.copyList(args.targetForCategories, [null]);
    }
    if (args.glanceLanguageId !== undefined && args.glanceLanguageId !== null) {
      this.glanceLanguageId = args.glanceLanguageId;
    }
    if (args.vernacularGlances !== undefined && args.vernacularGlances !== null) {
      this.vernacularGlances = Thrift.copyList(args.vernacularGlances, [null]);
    }
    if (args.shoppableData !== undefined && args.shoppableData !== null) {
      this.shoppableData = new ShoppableData(args.shoppableData);
    }
    if (args.antiSegmentIds !== undefined && args.antiSegmentIds !== null) {
      this.antiSegmentIds = Thrift.copyList(args.antiSegmentIds, [null]);
    }
    if (args.imageDbId !== undefined && args.imageDbId !== null) {
      this.imageDbId = args.imageDbId;
    }
    if (args.overlayImageDbId !== undefined && args.overlayImageDbId !== null) {
      this.overlayImageDbId = args.overlayImageDbId;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.userLifeTimeFilter !== undefined && args.userLifeTimeFilter !== null) {
      this.userLifeTimeFilter = new UserLifeTimeFilter(args.userLifeTimeFilter);
    }
    if (args.targeting !== undefined && args.targeting !== null) {
      this.targeting = new Targeting(args.targeting);
    }
    if (args.tinyShareUrl !== undefined && args.tinyShareUrl !== null) {
      this.tinyShareUrl = args.tinyShareUrl;
    }
    if (args.topicName !== undefined && args.topicName !== null) {
      this.topicName = args.topicName;
    }
    if (args.homescreenWorthy !== undefined && args.homescreenWorthy !== null) {
      this.homescreenWorthy = args.homescreenWorthy;
    }
    if (args.bubbleId !== undefined && args.bubbleId !== null) {
      this.bubbleId = args.bubbleId;
    }
    if (args.glanceBubbleDetails !== undefined && args.glanceBubbleDetails !== null) {
      this.glanceBubbleDetails = new GlanceBubbleDetails(args.glanceBubbleDetails);
    }
    if (args.cardPressId !== undefined && args.cardPressId !== null) {
      this.cardPressId = args.cardPressId;
    }
    if (args.carriers !== undefined && args.carriers !== null) {
      this.carriers = Thrift.copyList(args.carriers, [null]);
    }
    if (args.eId !== undefined && args.eId !== null) {
      this.eId = args.eId;
    }
    if (args.renderSessionProperties !== undefined && args.renderSessionProperties !== null) {
      this.renderSessionProperties = Thrift.copyList(args.renderSessionProperties, [null]);
    }
    if (args.userNetworkTypes !== undefined && args.userNetworkTypes !== null) {
      this.userNetworkTypes = Thrift.copyList(args.userNetworkTypes, [null]);
    }
    if (args.allowKeyboard !== undefined && args.allowKeyboard !== null) {
      this.allowKeyboard = args.allowKeyboard;
    }
    if (args.campaignMeta !== undefined && args.campaignMeta !== null) {
      this.campaignMeta = new GlanceCampaignMeta(args.campaignMeta);
    }
    if (args.beaconsInfo !== undefined && args.beaconsInfo !== null) {
      this.beaconsInfo = new BeaconsInfo(args.beaconsInfo);
    }
    if (args.omSdkInfo !== undefined && args.omSdkInfo !== null) {
      this.omSdkInfo = new OmSdkInfo(args.omSdkInfo);
    }
    if (args.sourceCreatorId !== undefined && args.sourceCreatorId !== null) {
      this.sourceCreatorId = args.sourceCreatorId;
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    }
    if (args.draftId !== undefined && args.draftId !== null) {
      this.draftId = args.draftId;
    }
    if (args.rankInStory !== undefined && args.rankInStory !== null) {
      this.rankInStory = args.rankInStory;
    }
    if (args.dynamicOverlay !== undefined && args.dynamicOverlay !== null) {
      this.dynamicOverlay = args.dynamicOverlay;
    }
    if (args.impressionCappingId !== undefined && args.impressionCappingId !== null) {
      this.impressionCappingId = args.impressionCappingId;
    }
    if (args.ctaViewConfig !== undefined && args.ctaViewConfig !== null) {
      this.ctaViewConfig = new CtaViewConfig(args.ctaViewConfig);
    }
    if (args.rankInBubble !== undefined && args.rankInBubble !== null) {
      this.rankInBubble = args.rankInBubble;
    }
    if (args.adCoefficient !== undefined && args.adCoefficient !== null) {
      this.adCoefficient = args.adCoefficient;
    }
    if (args.bucketPriority !== undefined && args.bucketPriority !== null) {
      this.bucketPriority = args.bucketPriority;
    }
    if (args.euIdentifier !== undefined && args.euIdentifier !== null) {
      this.euIdentifier = args.euIdentifier;
    }
    if (args.subCategories !== undefined && args.subCategories !== null) {
      this.subCategories = Thrift.copyList(args.subCategories, [null]);
    }
    if (args.suggestedSubCategory !== undefined && args.suggestedSubCategory !== null) {
      this.suggestedSubCategory = args.suggestedSubCategory;
    }
    if (args.allowNativeKeyboard !== undefined && args.allowNativeKeyboard !== null) {
      this.allowNativeKeyboard = args.allowNativeKeyboard;
    }
    if (args.otherTopics !== undefined && args.otherTopics !== null) {
      this.otherTopics = Thrift.copyList(args.otherTopics, [null]);
    }
    if (args.l3Tags !== undefined && args.l3Tags !== null) {
      this.l3Tags = Thrift.copyList(args.l3Tags, [null]);
    }
    if (args.programmaticDetail !== undefined && args.programmaticDetail !== null) {
      this.programmaticDetail = new Programmatic(args.programmaticDetail);
    }
    if (args.programmatic !== undefined && args.programmatic !== null) {
      this.programmatic = args.programmatic;
    }
    if (args.ociConfigs !== undefined && args.ociConfigs !== null) {
      this.ociConfigs = new OciConfigs(args.ociConfigs);
    }
    if (args.isBeautifulWallpaperEnabled !== undefined && args.isBeautifulWallpaperEnabled !== null) {
      this.isBeautifulWallpaperEnabled = args.isBeautifulWallpaperEnabled;
    }
    if (args.assetFileSizeInBytes !== undefined && args.assetFileSizeInBytes !== null) {
      this.assetFileSizeInBytes = args.assetFileSizeInBytes;
    }
    if (args.spaceData !== undefined && args.spaceData !== null) {
      this.spaceData = new SpaceData(args.spaceData);
    }
    if (args.publishingDestination !== undefined && args.publishingDestination !== null) {
      this.publishingDestination = args.publishingDestination;
    }
    if (args.destinationIdentifier !== undefined && args.destinationIdentifier !== null) {
      this.destinationIdentifier = args.destinationIdentifier;
    }
    if (args.event !== undefined && args.event !== null) {
      this.event = args.event;
    }
    if (args.liveEventData !== undefined && args.liveEventData !== null) {
      this.liveEventData = new LiveEventData(args.liveEventData);
    }
    if (args.tags !== undefined && args.tags !== null) {
      this.tags = Thrift.copyList(args.tags, [null]);
    }
    if (args.updatedBy !== undefined && args.updatedBy !== null) {
      this.updatedBy = args.updatedBy;
    }
    if (args.gamingEuSpaceData !== undefined && args.gamingEuSpaceData !== null) {
      this.gamingEuSpaceData = new GamingEuSpaceData(args.gamingEuSpaceData);
    }
    if (args.gameMetaData !== undefined && args.gameMetaData !== null) {
      this.gameMetaData = new GameMetaData(args.gameMetaData);
    }
    if (args.viewMoreCta !== undefined && args.viewMoreCta !== null) {
      this.viewMoreCta = args.viewMoreCta;
    }
    if (args.sportEuSpaceData !== undefined && args.sportEuSpaceData !== null) {
      this.sportEuSpaceData = new SportEuSpaceData(args.sportEuSpaceData);
    }
    if (args.spaces !== undefined && args.spaces !== null) {
      this.spaces = Thrift.copyList(args.spaces, [null]);
    }
    if (args.eu !== undefined && args.eu !== null) {
      this.eu = args.eu;
    }
    if (args.fastCardData !== undefined && args.fastCardData !== null) {
      this.fastCardData = new FastCardData(args.fastCardData);
    }
    if (args.glamCampaignData !== undefined && args.glamCampaignData !== null) {
      this.glamCampaignData = new GlamCampaignData(args.glamCampaignData);
    }
    if (args.valueProp !== undefined && args.valueProp !== null) {
      this.valueProp = args.valueProp;
    }
    if (args.eus !== undefined && args.eus !== null) {
      this.eus = Thrift.copyList(args.eus, [EuInfoThrift]);
    }
    if (args.valueProps !== undefined && args.valueProps !== null) {
      this.valueProps = Thrift.copyList(args.valueProps, [ValuePropInfoThrift]);
    }
    if (args.gamPgAdUnitId !== undefined && args.gamPgAdUnitId !== null) {
      this.gamPgAdUnitId = args.gamPgAdUnitId;
    }
    if (args.images !== undefined && args.images !== null) {
      this.images = Thrift.copyList(args.images, [Image]);
    }
    if (args.useUnlockNudge !== undefined && args.useUnlockNudge !== null) {
      this.useUnlockNudge = args.useUnlockNudge;
    }
    if (args.dspCampaignData !== undefined && args.dspCampaignData !== null) {
      this.dspCampaignData = new DspCampaignData(args.dspCampaignData);
    }
    if (args.unknownEnumFlag !== undefined && args.unknownEnumFlag !== null) {
      this.unknownEnumFlag = args.unknownEnumFlag;
    }
  }
};
GlanceData.prototype = {};
GlanceData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.image = new Image();
        this.image.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRUCT) {
        this.peekData = new PeekData();
        this.peekData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRUCT) {
        this.peek = new Peek();
        this.peek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I32) {
        this.priority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRUCT) {
        this.publisherData = new PublisherData();
        this.publisherData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size528 = 0;
        var _rtmp3532;
        this.categoryIds = [];
        var _etype531 = 0;
        _rtmp3532 = input.readListBegin();
        _etype531 = _rtmp3532.etype;
        _size528 = _rtmp3532.size;
        for (var _i533 = 0; _i533 < _size528; ++_i533)
        {
          var elem534 = null;
          elem534 = input.readString().value;
          this.categoryIds.push(elem534);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I64) {
        this.destinationUrlFailedAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.I64) {
        this.version = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRUCT) {
        this.frequencyCaps = new FrequencyCaps();
        this.frequencyCaps.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRUCT) {
        this.beacons = new Beacons();
        this.beacons.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.I32) {
        this.renderSessionMode = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRUCT) {
        this.offlinePeek = new Peek();
        this.offlinePeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.I32) {
        this.stickinessCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.I32) {
        this.storyType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.SET) {
        var _size535 = 0;
        var _rtmp3539;
        this.cityIds = [];
        var _etype538 = 0;
        _rtmp3539 = input.readSetBegin();
        _etype538 = _rtmp3539.etype;
        _size535 = _rtmp3539.size;
        for (var _i540 = 0; _i540 < _size535; ++_i540)
        {
          var elem541 = null;
          elem541 = input.readI32().value;
          this.cityIds.push(elem541);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.SET) {
        var _size542 = 0;
        var _rtmp3546;
        this.segmentIds = [];
        var _etype545 = 0;
        _rtmp3546 = input.readSetBegin();
        _etype545 = _rtmp3546.etype;
        _size542 = _rtmp3546.size;
        for (var _i547 = 0; _i547 < _size542; ++_i547)
        {
          var elem548 = null;
          elem548 = input.readI32().value;
          this.segmentIds.push(elem548);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.I32) {
        this.userAgeType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.I32) {
        this.categorySubscriptionStatus = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.STRING) {
        this.targetForCategory = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.BOOL) {
        this.isShareable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.STRING) {
        this.shareUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.STRUCT) {
        this.overlayImage = new Image();
        this.overlayImage.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.LIST) {
        var _size549 = 0;
        var _rtmp3553;
        this.glanceFeatures = [];
        var _etype552 = 0;
        _rtmp3553 = input.readListBegin();
        _etype552 = _rtmp3553.etype;
        _size549 = _rtmp3553.size;
        for (var _i554 = 0; _i554 < _size549; ++_i554)
        {
          var elem555 = null;
          elem555 = input.readI32().value;
          this.glanceFeatures.push(elem555);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.STRING) {
        this.bingeGlanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.STRING) {
        this.overlayGlanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.LIST) {
        var _size556 = 0;
        var _rtmp3560;
        this.targetForCategories = [];
        var _etype559 = 0;
        _rtmp3560 = input.readListBegin();
        _etype559 = _rtmp3560.etype;
        _size556 = _rtmp3560.size;
        for (var _i561 = 0; _i561 < _size556; ++_i561)
        {
          var elem562 = null;
          elem562 = input.readString().value;
          this.targetForCategories.push(elem562);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRING) {
        this.glanceLanguageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.LIST) {
        var _size563 = 0;
        var _rtmp3567;
        this.vernacularGlances = [];
        var _etype566 = 0;
        _rtmp3567 = input.readListBegin();
        _etype566 = _rtmp3567.etype;
        _size563 = _rtmp3567.size;
        for (var _i568 = 0; _i568 < _size563; ++_i568)
        {
          var elem569 = null;
          elem569 = input.readString().value;
          this.vernacularGlances.push(elem569);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.STRUCT) {
        this.shoppableData = new ShoppableData();
        this.shoppableData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.SET) {
        var _size570 = 0;
        var _rtmp3574;
        this.antiSegmentIds = [];
        var _etype573 = 0;
        _rtmp3574 = input.readSetBegin();
        _etype573 = _rtmp3574.etype;
        _size570 = _rtmp3574.size;
        for (var _i575 = 0; _i575 < _size570; ++_i575)
        {
          var elem576 = null;
          elem576 = input.readI32().value;
          this.antiSegmentIds.push(elem576);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 36:
      if (ftype == Thrift.Type.I64) {
        this.imageDbId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 37:
      if (ftype == Thrift.Type.I64) {
        this.overlayImageDbId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 38:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 39:
      if (ftype == Thrift.Type.STRUCT) {
        this.userLifeTimeFilter = new UserLifeTimeFilter();
        this.userLifeTimeFilter.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 40:
      if (ftype == Thrift.Type.STRUCT) {
        this.targeting = new Targeting();
        this.targeting.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 41:
      if (ftype == Thrift.Type.STRING) {
        this.tinyShareUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 42:
      if (ftype == Thrift.Type.STRING) {
        this.topicName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 43:
      if (ftype == Thrift.Type.BOOL) {
        this.homescreenWorthy = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 44:
      if (ftype == Thrift.Type.STRING) {
        this.bubbleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 45:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceBubbleDetails = new GlanceBubbleDetails();
        this.glanceBubbleDetails.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 46:
      if (ftype == Thrift.Type.STRING) {
        this.cardPressId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 47:
      if (ftype == Thrift.Type.SET) {
        var _size577 = 0;
        var _rtmp3581;
        this.carriers = [];
        var _etype580 = 0;
        _rtmp3581 = input.readSetBegin();
        _etype580 = _rtmp3581.etype;
        _size577 = _rtmp3581.size;
        for (var _i582 = 0; _i582 < _size577; ++_i582)
        {
          var elem583 = null;
          elem583 = input.readI32().value;
          this.carriers.push(elem583);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 48:
      if (ftype == Thrift.Type.STRING) {
        this.eId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 49:
      if (ftype == Thrift.Type.SET) {
        var _size584 = 0;
        var _rtmp3588;
        this.renderSessionProperties = [];
        var _etype587 = 0;
        _rtmp3588 = input.readSetBegin();
        _etype587 = _rtmp3588.etype;
        _size584 = _rtmp3588.size;
        for (var _i589 = 0; _i589 < _size584; ++_i589)
        {
          var elem590 = null;
          elem590 = input.readI32().value;
          this.renderSessionProperties.push(elem590);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 50:
      if (ftype == Thrift.Type.SET) {
        var _size591 = 0;
        var _rtmp3595;
        this.userNetworkTypes = [];
        var _etype594 = 0;
        _rtmp3595 = input.readSetBegin();
        _etype594 = _rtmp3595.etype;
        _size591 = _rtmp3595.size;
        for (var _i596 = 0; _i596 < _size591; ++_i596)
        {
          var elem597 = null;
          elem597 = input.readI32().value;
          this.userNetworkTypes.push(elem597);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 51:
      if (ftype == Thrift.Type.BOOL) {
        this.allowKeyboard = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 52:
      if (ftype == Thrift.Type.STRUCT) {
        this.campaignMeta = new GlanceCampaignMeta();
        this.campaignMeta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 53:
      if (ftype == Thrift.Type.STRUCT) {
        this.beaconsInfo = new BeaconsInfo();
        this.beaconsInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 54:
      if (ftype == Thrift.Type.STRUCT) {
        this.omSdkInfo = new OmSdkInfo();
        this.omSdkInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 55:
      if (ftype == Thrift.Type.STRING) {
        this.sourceCreatorId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 56:
      if (ftype == Thrift.Type.STRING) {
        this.source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 57:
      if (ftype == Thrift.Type.STRING) {
        this.draftId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 58:
      if (ftype == Thrift.Type.I32) {
        this.rankInStory = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 59:
      if (ftype == Thrift.Type.BOOL) {
        this.dynamicOverlay = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 60:
      if (ftype == Thrift.Type.STRING) {
        this.impressionCappingId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 61:
      if (ftype == Thrift.Type.STRUCT) {
        this.ctaViewConfig = new CtaViewConfig();
        this.ctaViewConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 62:
      if (ftype == Thrift.Type.I32) {
        this.rankInBubble = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 63:
      if (ftype == Thrift.Type.I32) {
        this.adCoefficient = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 64:
      if (ftype == Thrift.Type.I32) {
        this.bucketPriority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 65:
      if (ftype == Thrift.Type.I32) {
        this.euIdentifier = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 66:
      if (ftype == Thrift.Type.LIST) {
        var _size598 = 0;
        var _rtmp3602;
        this.subCategories = [];
        var _etype601 = 0;
        _rtmp3602 = input.readListBegin();
        _etype601 = _rtmp3602.etype;
        _size598 = _rtmp3602.size;
        for (var _i603 = 0; _i603 < _size598; ++_i603)
        {
          var elem604 = null;
          elem604 = input.readString().value;
          this.subCategories.push(elem604);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 67:
      if (ftype == Thrift.Type.STRING) {
        this.suggestedSubCategory = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 68:
      if (ftype == Thrift.Type.BOOL) {
        this.allowNativeKeyboard = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 69:
      if (ftype == Thrift.Type.LIST) {
        var _size605 = 0;
        var _rtmp3609;
        this.otherTopics = [];
        var _etype608 = 0;
        _rtmp3609 = input.readListBegin();
        _etype608 = _rtmp3609.etype;
        _size605 = _rtmp3609.size;
        for (var _i610 = 0; _i610 < _size605; ++_i610)
        {
          var elem611 = null;
          elem611 = input.readString().value;
          this.otherTopics.push(elem611);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 70:
      if (ftype == Thrift.Type.LIST) {
        var _size612 = 0;
        var _rtmp3616;
        this.l3Tags = [];
        var _etype615 = 0;
        _rtmp3616 = input.readListBegin();
        _etype615 = _rtmp3616.etype;
        _size612 = _rtmp3616.size;
        for (var _i617 = 0; _i617 < _size612; ++_i617)
        {
          var elem618 = null;
          elem618 = input.readString().value;
          this.l3Tags.push(elem618);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 71:
      if (ftype == Thrift.Type.STRUCT) {
        this.programmaticDetail = new Programmatic();
        this.programmaticDetail.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 72:
      if (ftype == Thrift.Type.STRING) {
        this.programmatic = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 73:
      if (ftype == Thrift.Type.STRUCT) {
        this.ociConfigs = new OciConfigs();
        this.ociConfigs.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 75:
      if (ftype == Thrift.Type.BOOL) {
        this.isBeautifulWallpaperEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 76:
      if (ftype == Thrift.Type.I64) {
        this.assetFileSizeInBytes = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 77:
      if (ftype == Thrift.Type.STRUCT) {
        this.spaceData = new SpaceData();
        this.spaceData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 78:
      if (ftype == Thrift.Type.I32) {
        this.publishingDestination = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 79:
      if (ftype == Thrift.Type.I32) {
        this.destinationIdentifier = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 80:
      if (ftype == Thrift.Type.I32) {
        this.event = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 81:
      if (ftype == Thrift.Type.STRUCT) {
        this.liveEventData = new LiveEventData();
        this.liveEventData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 82:
      if (ftype == Thrift.Type.SET) {
        var _size619 = 0;
        var _rtmp3623;
        this.tags = [];
        var _etype622 = 0;
        _rtmp3623 = input.readSetBegin();
        _etype622 = _rtmp3623.etype;
        _size619 = _rtmp3623.size;
        for (var _i624 = 0; _i624 < _size619; ++_i624)
        {
          var elem625 = null;
          elem625 = input.readString().value;
          this.tags.push(elem625);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 83:
      if (ftype == Thrift.Type.STRING) {
        this.updatedBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 84:
      if (ftype == Thrift.Type.STRUCT) {
        this.gamingEuSpaceData = new GamingEuSpaceData();
        this.gamingEuSpaceData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 85:
      if (ftype == Thrift.Type.STRUCT) {
        this.gameMetaData = new GameMetaData();
        this.gameMetaData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 86:
      if (ftype == Thrift.Type.STRING) {
        this.viewMoreCta = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 87:
      if (ftype == Thrift.Type.STRUCT) {
        this.sportEuSpaceData = new SportEuSpaceData();
        this.sportEuSpaceData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 88:
      if (ftype == Thrift.Type.LIST) {
        var _size626 = 0;
        var _rtmp3630;
        this.spaces = [];
        var _etype629 = 0;
        _rtmp3630 = input.readListBegin();
        _etype629 = _rtmp3630.etype;
        _size626 = _rtmp3630.size;
        for (var _i631 = 0; _i631 < _size626; ++_i631)
        {
          var elem632 = null;
          elem632 = input.readI32().value;
          this.spaces.push(elem632);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 89:
      if (ftype == Thrift.Type.I32) {
        this.eu = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 90:
      if (ftype == Thrift.Type.STRUCT) {
        this.fastCardData = new FastCardData();
        this.fastCardData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 91:
      if (ftype == Thrift.Type.STRUCT) {
        this.glamCampaignData = new GlamCampaignData();
        this.glamCampaignData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 92:
      if (ftype == Thrift.Type.STRING) {
        this.valueProp = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 93:
      if (ftype == Thrift.Type.LIST) {
        var _size633 = 0;
        var _rtmp3637;
        this.eus = [];
        var _etype636 = 0;
        _rtmp3637 = input.readListBegin();
        _etype636 = _rtmp3637.etype;
        _size633 = _rtmp3637.size;
        for (var _i638 = 0; _i638 < _size633; ++_i638)
        {
          var elem639 = null;
          elem639 = new EuInfoThrift();
          elem639.read(input);
          this.eus.push(elem639);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 94:
      if (ftype == Thrift.Type.LIST) {
        var _size640 = 0;
        var _rtmp3644;
        this.valueProps = [];
        var _etype643 = 0;
        _rtmp3644 = input.readListBegin();
        _etype643 = _rtmp3644.etype;
        _size640 = _rtmp3644.size;
        for (var _i645 = 0; _i645 < _size640; ++_i645)
        {
          var elem646 = null;
          elem646 = new ValuePropInfoThrift();
          elem646.read(input);
          this.valueProps.push(elem646);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 95:
      if (ftype == Thrift.Type.STRING) {
        this.gamPgAdUnitId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 96:
      if (ftype == Thrift.Type.LIST) {
        var _size647 = 0;
        var _rtmp3651;
        this.images = [];
        var _etype650 = 0;
        _rtmp3651 = input.readListBegin();
        _etype650 = _rtmp3651.etype;
        _size647 = _rtmp3651.size;
        for (var _i652 = 0; _i652 < _size647; ++_i652)
        {
          var elem653 = null;
          elem653 = new Image();
          elem653.read(input);
          this.images.push(elem653);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 97:
      if (ftype == Thrift.Type.BOOL) {
        this.useUnlockNudge = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 98:
      if (ftype == Thrift.Type.STRUCT) {
        this.dspCampaignData = new DspCampaignData();
        this.dspCampaignData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 99:
      if (ftype == Thrift.Type.BOOL) {
        this.unknownEnumFlag = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceData.prototype.write = function(output) {
  output.writeStructBegin('GlanceData');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 2);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.image !== null && this.image !== undefined) {
    output.writeFieldBegin('image', Thrift.Type.STRUCT, 3);
    this.image.write(output);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.I32, 4);
    output.writeI32(this.type);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 6);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.peekData !== null && this.peekData !== undefined) {
    output.writeFieldBegin('peekData', Thrift.Type.STRUCT, 7);
    this.peekData.write(output);
    output.writeFieldEnd();
  }
  if (this.peek !== null && this.peek !== undefined) {
    output.writeFieldBegin('peek', Thrift.Type.STRUCT, 8);
    this.peek.write(output);
    output.writeFieldEnd();
  }
  if (this.priority !== null && this.priority !== undefined) {
    output.writeFieldBegin('priority', Thrift.Type.I32, 9);
    output.writeI32(this.priority);
    output.writeFieldEnd();
  }
  if (this.publisherData !== null && this.publisherData !== undefined) {
    output.writeFieldBegin('publisherData', Thrift.Type.STRUCT, 10);
    this.publisherData.write(output);
    output.writeFieldEnd();
  }
  if (this.categoryIds !== null && this.categoryIds !== undefined) {
    output.writeFieldBegin('categoryIds', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRING, this.categoryIds.length);
    for (var iter654 in this.categoryIds)
    {
      if (this.categoryIds.hasOwnProperty(iter654))
      {
        iter654 = this.categoryIds[iter654];
        output.writeString(iter654);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.destinationUrlFailedAt !== null && this.destinationUrlFailedAt !== undefined) {
    output.writeFieldBegin('destinationUrlFailedAt', Thrift.Type.I64, 12);
    output.writeI64(this.destinationUrlFailedAt);
    output.writeFieldEnd();
  }
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.I64, 13);
    output.writeI64(this.version);
    output.writeFieldEnd();
  }
  if (this.frequencyCaps !== null && this.frequencyCaps !== undefined) {
    output.writeFieldBegin('frequencyCaps', Thrift.Type.STRUCT, 14);
    this.frequencyCaps.write(output);
    output.writeFieldEnd();
  }
  if (this.beacons !== null && this.beacons !== undefined) {
    output.writeFieldBegin('beacons', Thrift.Type.STRUCT, 15);
    this.beacons.write(output);
    output.writeFieldEnd();
  }
  if (this.renderSessionMode !== null && this.renderSessionMode !== undefined) {
    output.writeFieldBegin('renderSessionMode', Thrift.Type.I32, 16);
    output.writeI32(this.renderSessionMode);
    output.writeFieldEnd();
  }
  if (this.offlinePeek !== null && this.offlinePeek !== undefined) {
    output.writeFieldBegin('offlinePeek', Thrift.Type.STRUCT, 17);
    this.offlinePeek.write(output);
    output.writeFieldEnd();
  }
  if (this.stickinessCount !== null && this.stickinessCount !== undefined) {
    output.writeFieldBegin('stickinessCount', Thrift.Type.I32, 18);
    output.writeI32(this.stickinessCount);
    output.writeFieldEnd();
  }
  if (this.storyType !== null && this.storyType !== undefined) {
    output.writeFieldBegin('storyType', Thrift.Type.I32, 19);
    output.writeI32(this.storyType);
    output.writeFieldEnd();
  }
  if (this.cityIds !== null && this.cityIds !== undefined) {
    output.writeFieldBegin('cityIds', Thrift.Type.SET, 20);
    output.writeSetBegin(Thrift.Type.I32, this.cityIds.length);
    for (var iter655 in this.cityIds)
    {
      if (this.cityIds.hasOwnProperty(iter655))
      {
        iter655 = this.cityIds[iter655];
        output.writeI32(iter655);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.segmentIds !== null && this.segmentIds !== undefined) {
    output.writeFieldBegin('segmentIds', Thrift.Type.SET, 21);
    output.writeSetBegin(Thrift.Type.I32, this.segmentIds.length);
    for (var iter656 in this.segmentIds)
    {
      if (this.segmentIds.hasOwnProperty(iter656))
      {
        iter656 = this.segmentIds[iter656];
        output.writeI32(iter656);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.userAgeType !== null && this.userAgeType !== undefined) {
    output.writeFieldBegin('userAgeType', Thrift.Type.I32, 22);
    output.writeI32(this.userAgeType);
    output.writeFieldEnd();
  }
  if (this.categorySubscriptionStatus !== null && this.categorySubscriptionStatus !== undefined) {
    output.writeFieldBegin('categorySubscriptionStatus', Thrift.Type.I32, 23);
    output.writeI32(this.categorySubscriptionStatus);
    output.writeFieldEnd();
  }
  if (this.targetForCategory !== null && this.targetForCategory !== undefined) {
    output.writeFieldBegin('targetForCategory', Thrift.Type.STRING, 24);
    output.writeString(this.targetForCategory);
    output.writeFieldEnd();
  }
  if (this.isShareable !== null && this.isShareable !== undefined) {
    output.writeFieldBegin('isShareable', Thrift.Type.BOOL, 25);
    output.writeBool(this.isShareable);
    output.writeFieldEnd();
  }
  if (this.shareUrl !== null && this.shareUrl !== undefined) {
    output.writeFieldBegin('shareUrl', Thrift.Type.STRING, 26);
    output.writeString(this.shareUrl);
    output.writeFieldEnd();
  }
  if (this.overlayImage !== null && this.overlayImage !== undefined) {
    output.writeFieldBegin('overlayImage', Thrift.Type.STRUCT, 27);
    this.overlayImage.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceFeatures !== null && this.glanceFeatures !== undefined) {
    output.writeFieldBegin('glanceFeatures', Thrift.Type.LIST, 28);
    output.writeListBegin(Thrift.Type.I32, this.glanceFeatures.length);
    for (var iter657 in this.glanceFeatures)
    {
      if (this.glanceFeatures.hasOwnProperty(iter657))
      {
        iter657 = this.glanceFeatures[iter657];
        output.writeI32(iter657);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.bingeGlanceId !== null && this.bingeGlanceId !== undefined) {
    output.writeFieldBegin('bingeGlanceId', Thrift.Type.STRING, 29);
    output.writeString(this.bingeGlanceId);
    output.writeFieldEnd();
  }
  if (this.overlayGlanceId !== null && this.overlayGlanceId !== undefined) {
    output.writeFieldBegin('overlayGlanceId', Thrift.Type.STRING, 30);
    output.writeString(this.overlayGlanceId);
    output.writeFieldEnd();
  }
  if (this.targetForCategories !== null && this.targetForCategories !== undefined) {
    output.writeFieldBegin('targetForCategories', Thrift.Type.LIST, 31);
    output.writeListBegin(Thrift.Type.STRING, this.targetForCategories.length);
    for (var iter658 in this.targetForCategories)
    {
      if (this.targetForCategories.hasOwnProperty(iter658))
      {
        iter658 = this.targetForCategories[iter658];
        output.writeString(iter658);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.glanceLanguageId !== null && this.glanceLanguageId !== undefined) {
    output.writeFieldBegin('glanceLanguageId', Thrift.Type.STRING, 32);
    output.writeString(this.glanceLanguageId);
    output.writeFieldEnd();
  }
  if (this.vernacularGlances !== null && this.vernacularGlances !== undefined) {
    output.writeFieldBegin('vernacularGlances', Thrift.Type.LIST, 33);
    output.writeListBegin(Thrift.Type.STRING, this.vernacularGlances.length);
    for (var iter659 in this.vernacularGlances)
    {
      if (this.vernacularGlances.hasOwnProperty(iter659))
      {
        iter659 = this.vernacularGlances[iter659];
        output.writeString(iter659);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.shoppableData !== null && this.shoppableData !== undefined) {
    output.writeFieldBegin('shoppableData', Thrift.Type.STRUCT, 34);
    this.shoppableData.write(output);
    output.writeFieldEnd();
  }
  if (this.antiSegmentIds !== null && this.antiSegmentIds !== undefined) {
    output.writeFieldBegin('antiSegmentIds', Thrift.Type.SET, 35);
    output.writeSetBegin(Thrift.Type.I32, this.antiSegmentIds.length);
    for (var iter660 in this.antiSegmentIds)
    {
      if (this.antiSegmentIds.hasOwnProperty(iter660))
      {
        iter660 = this.antiSegmentIds[iter660];
        output.writeI32(iter660);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.imageDbId !== null && this.imageDbId !== undefined) {
    output.writeFieldBegin('imageDbId', Thrift.Type.I64, 36);
    output.writeI64(this.imageDbId);
    output.writeFieldEnd();
  }
  if (this.overlayImageDbId !== null && this.overlayImageDbId !== undefined) {
    output.writeFieldBegin('overlayImageDbId', Thrift.Type.I64, 37);
    output.writeI64(this.overlayImageDbId);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 38);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.userLifeTimeFilter !== null && this.userLifeTimeFilter !== undefined) {
    output.writeFieldBegin('userLifeTimeFilter', Thrift.Type.STRUCT, 39);
    this.userLifeTimeFilter.write(output);
    output.writeFieldEnd();
  }
  if (this.targeting !== null && this.targeting !== undefined) {
    output.writeFieldBegin('targeting', Thrift.Type.STRUCT, 40);
    this.targeting.write(output);
    output.writeFieldEnd();
  }
  if (this.tinyShareUrl !== null && this.tinyShareUrl !== undefined) {
    output.writeFieldBegin('tinyShareUrl', Thrift.Type.STRING, 41);
    output.writeString(this.tinyShareUrl);
    output.writeFieldEnd();
  }
  if (this.topicName !== null && this.topicName !== undefined) {
    output.writeFieldBegin('topicName', Thrift.Type.STRING, 42);
    output.writeString(this.topicName);
    output.writeFieldEnd();
  }
  if (this.homescreenWorthy !== null && this.homescreenWorthy !== undefined) {
    output.writeFieldBegin('homescreenWorthy', Thrift.Type.BOOL, 43);
    output.writeBool(this.homescreenWorthy);
    output.writeFieldEnd();
  }
  if (this.bubbleId !== null && this.bubbleId !== undefined) {
    output.writeFieldBegin('bubbleId', Thrift.Type.STRING, 44);
    output.writeString(this.bubbleId);
    output.writeFieldEnd();
  }
  if (this.glanceBubbleDetails !== null && this.glanceBubbleDetails !== undefined) {
    output.writeFieldBegin('glanceBubbleDetails', Thrift.Type.STRUCT, 45);
    this.glanceBubbleDetails.write(output);
    output.writeFieldEnd();
  }
  if (this.cardPressId !== null && this.cardPressId !== undefined) {
    output.writeFieldBegin('cardPressId', Thrift.Type.STRING, 46);
    output.writeString(this.cardPressId);
    output.writeFieldEnd();
  }
  if (this.carriers !== null && this.carriers !== undefined) {
    output.writeFieldBegin('carriers', Thrift.Type.SET, 47);
    output.writeSetBegin(Thrift.Type.I32, this.carriers.length);
    for (var iter661 in this.carriers)
    {
      if (this.carriers.hasOwnProperty(iter661))
      {
        iter661 = this.carriers[iter661];
        output.writeI32(iter661);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.eId !== null && this.eId !== undefined) {
    output.writeFieldBegin('eId', Thrift.Type.STRING, 48);
    output.writeString(this.eId);
    output.writeFieldEnd();
  }
  if (this.renderSessionProperties !== null && this.renderSessionProperties !== undefined) {
    output.writeFieldBegin('renderSessionProperties', Thrift.Type.SET, 49);
    output.writeSetBegin(Thrift.Type.I32, this.renderSessionProperties.length);
    for (var iter662 in this.renderSessionProperties)
    {
      if (this.renderSessionProperties.hasOwnProperty(iter662))
      {
        iter662 = this.renderSessionProperties[iter662];
        output.writeI32(iter662);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.userNetworkTypes !== null && this.userNetworkTypes !== undefined) {
    output.writeFieldBegin('userNetworkTypes', Thrift.Type.SET, 50);
    output.writeSetBegin(Thrift.Type.I32, this.userNetworkTypes.length);
    for (var iter663 in this.userNetworkTypes)
    {
      if (this.userNetworkTypes.hasOwnProperty(iter663))
      {
        iter663 = this.userNetworkTypes[iter663];
        output.writeI32(iter663);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.allowKeyboard !== null && this.allowKeyboard !== undefined) {
    output.writeFieldBegin('allowKeyboard', Thrift.Type.BOOL, 51);
    output.writeBool(this.allowKeyboard);
    output.writeFieldEnd();
  }
  if (this.campaignMeta !== null && this.campaignMeta !== undefined) {
    output.writeFieldBegin('campaignMeta', Thrift.Type.STRUCT, 52);
    this.campaignMeta.write(output);
    output.writeFieldEnd();
  }
  if (this.beaconsInfo !== null && this.beaconsInfo !== undefined) {
    output.writeFieldBegin('beaconsInfo', Thrift.Type.STRUCT, 53);
    this.beaconsInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.omSdkInfo !== null && this.omSdkInfo !== undefined) {
    output.writeFieldBegin('omSdkInfo', Thrift.Type.STRUCT, 54);
    this.omSdkInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.sourceCreatorId !== null && this.sourceCreatorId !== undefined) {
    output.writeFieldBegin('sourceCreatorId', Thrift.Type.STRING, 55);
    output.writeString(this.sourceCreatorId);
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.STRING, 56);
    output.writeString(this.source);
    output.writeFieldEnd();
  }
  if (this.draftId !== null && this.draftId !== undefined) {
    output.writeFieldBegin('draftId', Thrift.Type.STRING, 57);
    output.writeString(this.draftId);
    output.writeFieldEnd();
  }
  if (this.rankInStory !== null && this.rankInStory !== undefined) {
    output.writeFieldBegin('rankInStory', Thrift.Type.I32, 58);
    output.writeI32(this.rankInStory);
    output.writeFieldEnd();
  }
  if (this.dynamicOverlay !== null && this.dynamicOverlay !== undefined) {
    output.writeFieldBegin('dynamicOverlay', Thrift.Type.BOOL, 59);
    output.writeBool(this.dynamicOverlay);
    output.writeFieldEnd();
  }
  if (this.impressionCappingId !== null && this.impressionCappingId !== undefined) {
    output.writeFieldBegin('impressionCappingId', Thrift.Type.STRING, 60);
    output.writeString(this.impressionCappingId);
    output.writeFieldEnd();
  }
  if (this.ctaViewConfig !== null && this.ctaViewConfig !== undefined) {
    output.writeFieldBegin('ctaViewConfig', Thrift.Type.STRUCT, 61);
    this.ctaViewConfig.write(output);
    output.writeFieldEnd();
  }
  if (this.rankInBubble !== null && this.rankInBubble !== undefined) {
    output.writeFieldBegin('rankInBubble', Thrift.Type.I32, 62);
    output.writeI32(this.rankInBubble);
    output.writeFieldEnd();
  }
  if (this.adCoefficient !== null && this.adCoefficient !== undefined) {
    output.writeFieldBegin('adCoefficient', Thrift.Type.I32, 63);
    output.writeI32(this.adCoefficient);
    output.writeFieldEnd();
  }
  if (this.bucketPriority !== null && this.bucketPriority !== undefined) {
    output.writeFieldBegin('bucketPriority', Thrift.Type.I32, 64);
    output.writeI32(this.bucketPriority);
    output.writeFieldEnd();
  }
  if (this.euIdentifier !== null && this.euIdentifier !== undefined) {
    output.writeFieldBegin('euIdentifier', Thrift.Type.I32, 65);
    output.writeI32(this.euIdentifier);
    output.writeFieldEnd();
  }
  if (this.subCategories !== null && this.subCategories !== undefined) {
    output.writeFieldBegin('subCategories', Thrift.Type.LIST, 66);
    output.writeListBegin(Thrift.Type.STRING, this.subCategories.length);
    for (var iter664 in this.subCategories)
    {
      if (this.subCategories.hasOwnProperty(iter664))
      {
        iter664 = this.subCategories[iter664];
        output.writeString(iter664);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.suggestedSubCategory !== null && this.suggestedSubCategory !== undefined) {
    output.writeFieldBegin('suggestedSubCategory', Thrift.Type.STRING, 67);
    output.writeString(this.suggestedSubCategory);
    output.writeFieldEnd();
  }
  if (this.allowNativeKeyboard !== null && this.allowNativeKeyboard !== undefined) {
    output.writeFieldBegin('allowNativeKeyboard', Thrift.Type.BOOL, 68);
    output.writeBool(this.allowNativeKeyboard);
    output.writeFieldEnd();
  }
  if (this.otherTopics !== null && this.otherTopics !== undefined) {
    output.writeFieldBegin('otherTopics', Thrift.Type.LIST, 69);
    output.writeListBegin(Thrift.Type.STRING, this.otherTopics.length);
    for (var iter665 in this.otherTopics)
    {
      if (this.otherTopics.hasOwnProperty(iter665))
      {
        iter665 = this.otherTopics[iter665];
        output.writeString(iter665);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.l3Tags !== null && this.l3Tags !== undefined) {
    output.writeFieldBegin('l3Tags', Thrift.Type.LIST, 70);
    output.writeListBegin(Thrift.Type.STRING, this.l3Tags.length);
    for (var iter666 in this.l3Tags)
    {
      if (this.l3Tags.hasOwnProperty(iter666))
      {
        iter666 = this.l3Tags[iter666];
        output.writeString(iter666);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.programmaticDetail !== null && this.programmaticDetail !== undefined) {
    output.writeFieldBegin('programmaticDetail', Thrift.Type.STRUCT, 71);
    this.programmaticDetail.write(output);
    output.writeFieldEnd();
  }
  if (this.programmatic !== null && this.programmatic !== undefined) {
    output.writeFieldBegin('programmatic', Thrift.Type.STRING, 72);
    output.writeString(this.programmatic);
    output.writeFieldEnd();
  }
  if (this.ociConfigs !== null && this.ociConfigs !== undefined) {
    output.writeFieldBegin('ociConfigs', Thrift.Type.STRUCT, 73);
    this.ociConfigs.write(output);
    output.writeFieldEnd();
  }
  if (this.isBeautifulWallpaperEnabled !== null && this.isBeautifulWallpaperEnabled !== undefined) {
    output.writeFieldBegin('isBeautifulWallpaperEnabled', Thrift.Type.BOOL, 75);
    output.writeBool(this.isBeautifulWallpaperEnabled);
    output.writeFieldEnd();
  }
  if (this.assetFileSizeInBytes !== null && this.assetFileSizeInBytes !== undefined) {
    output.writeFieldBegin('assetFileSizeInBytes', Thrift.Type.I64, 76);
    output.writeI64(this.assetFileSizeInBytes);
    output.writeFieldEnd();
  }
  if (this.spaceData !== null && this.spaceData !== undefined) {
    output.writeFieldBegin('spaceData', Thrift.Type.STRUCT, 77);
    this.spaceData.write(output);
    output.writeFieldEnd();
  }
  if (this.publishingDestination !== null && this.publishingDestination !== undefined) {
    output.writeFieldBegin('publishingDestination', Thrift.Type.I32, 78);
    output.writeI32(this.publishingDestination);
    output.writeFieldEnd();
  }
  if (this.destinationIdentifier !== null && this.destinationIdentifier !== undefined) {
    output.writeFieldBegin('destinationIdentifier', Thrift.Type.I32, 79);
    output.writeI32(this.destinationIdentifier);
    output.writeFieldEnd();
  }
  if (this.event !== null && this.event !== undefined) {
    output.writeFieldBegin('event', Thrift.Type.I32, 80);
    output.writeI32(this.event);
    output.writeFieldEnd();
  }
  if (this.liveEventData !== null && this.liveEventData !== undefined) {
    output.writeFieldBegin('liveEventData', Thrift.Type.STRUCT, 81);
    this.liveEventData.write(output);
    output.writeFieldEnd();
  }
  if (this.tags !== null && this.tags !== undefined) {
    output.writeFieldBegin('tags', Thrift.Type.SET, 82);
    output.writeSetBegin(Thrift.Type.STRING, this.tags.length);
    for (var iter667 in this.tags)
    {
      if (this.tags.hasOwnProperty(iter667))
      {
        iter667 = this.tags[iter667];
        output.writeString(iter667);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.updatedBy !== null && this.updatedBy !== undefined) {
    output.writeFieldBegin('updatedBy', Thrift.Type.STRING, 83);
    output.writeString(this.updatedBy);
    output.writeFieldEnd();
  }
  if (this.gamingEuSpaceData !== null && this.gamingEuSpaceData !== undefined) {
    output.writeFieldBegin('gamingEuSpaceData', Thrift.Type.STRUCT, 84);
    this.gamingEuSpaceData.write(output);
    output.writeFieldEnd();
  }
  if (this.gameMetaData !== null && this.gameMetaData !== undefined) {
    output.writeFieldBegin('gameMetaData', Thrift.Type.STRUCT, 85);
    this.gameMetaData.write(output);
    output.writeFieldEnd();
  }
  if (this.viewMoreCta !== null && this.viewMoreCta !== undefined) {
    output.writeFieldBegin('viewMoreCta', Thrift.Type.STRING, 86);
    output.writeString(this.viewMoreCta);
    output.writeFieldEnd();
  }
  if (this.sportEuSpaceData !== null && this.sportEuSpaceData !== undefined) {
    output.writeFieldBegin('sportEuSpaceData', Thrift.Type.STRUCT, 87);
    this.sportEuSpaceData.write(output);
    output.writeFieldEnd();
  }
  if (this.spaces !== null && this.spaces !== undefined) {
    output.writeFieldBegin('spaces', Thrift.Type.LIST, 88);
    output.writeListBegin(Thrift.Type.I32, this.spaces.length);
    for (var iter668 in this.spaces)
    {
      if (this.spaces.hasOwnProperty(iter668))
      {
        iter668 = this.spaces[iter668];
        output.writeI32(iter668);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.eu !== null && this.eu !== undefined) {
    output.writeFieldBegin('eu', Thrift.Type.I32, 89);
    output.writeI32(this.eu);
    output.writeFieldEnd();
  }
  if (this.fastCardData !== null && this.fastCardData !== undefined) {
    output.writeFieldBegin('fastCardData', Thrift.Type.STRUCT, 90);
    this.fastCardData.write(output);
    output.writeFieldEnd();
  }
  if (this.glamCampaignData !== null && this.glamCampaignData !== undefined) {
    output.writeFieldBegin('glamCampaignData', Thrift.Type.STRUCT, 91);
    this.glamCampaignData.write(output);
    output.writeFieldEnd();
  }
  if (this.valueProp !== null && this.valueProp !== undefined) {
    output.writeFieldBegin('valueProp', Thrift.Type.STRING, 92);
    output.writeString(this.valueProp);
    output.writeFieldEnd();
  }
  if (this.eus !== null && this.eus !== undefined) {
    output.writeFieldBegin('eus', Thrift.Type.LIST, 93);
    output.writeListBegin(Thrift.Type.STRUCT, this.eus.length);
    for (var iter669 in this.eus)
    {
      if (this.eus.hasOwnProperty(iter669))
      {
        iter669 = this.eus[iter669];
        iter669.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.valueProps !== null && this.valueProps !== undefined) {
    output.writeFieldBegin('valueProps', Thrift.Type.LIST, 94);
    output.writeListBegin(Thrift.Type.STRUCT, this.valueProps.length);
    for (var iter670 in this.valueProps)
    {
      if (this.valueProps.hasOwnProperty(iter670))
      {
        iter670 = this.valueProps[iter670];
        iter670.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.gamPgAdUnitId !== null && this.gamPgAdUnitId !== undefined) {
    output.writeFieldBegin('gamPgAdUnitId', Thrift.Type.STRING, 95);
    output.writeString(this.gamPgAdUnitId);
    output.writeFieldEnd();
  }
  if (this.images !== null && this.images !== undefined) {
    output.writeFieldBegin('images', Thrift.Type.LIST, 96);
    output.writeListBegin(Thrift.Type.STRUCT, this.images.length);
    for (var iter671 in this.images)
    {
      if (this.images.hasOwnProperty(iter671))
      {
        iter671 = this.images[iter671];
        iter671.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.useUnlockNudge !== null && this.useUnlockNudge !== undefined) {
    output.writeFieldBegin('useUnlockNudge', Thrift.Type.BOOL, 97);
    output.writeBool(this.useUnlockNudge);
    output.writeFieldEnd();
  }
  if (this.dspCampaignData !== null && this.dspCampaignData !== undefined) {
    output.writeFieldBegin('dspCampaignData', Thrift.Type.STRUCT, 98);
    this.dspCampaignData.write(output);
    output.writeFieldEnd();
  }
  if (this.unknownEnumFlag !== null && this.unknownEnumFlag !== undefined) {
    output.writeFieldBegin('unknownEnumFlag', Thrift.Type.BOOL, 99);
    output.writeBool(this.unknownEnumFlag);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AbsoluteTime = function(args) {
  this.timeInSecs = null;
  if (args) {
    if (args.timeInSecs !== undefined && args.timeInSecs !== null) {
      this.timeInSecs = args.timeInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field timeInSecs is unset!');
    }
  }
};
AbsoluteTime.prototype = {};
AbsoluteTime.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.timeInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AbsoluteTime.prototype.write = function(output) {
  output.writeStructBegin('AbsoluteTime');
  if (this.timeInSecs !== null && this.timeInSecs !== undefined) {
    output.writeFieldBegin('timeInSecs', Thrift.Type.I64, 1);
    output.writeI64(this.timeInSecs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RelativeTime = function(args) {
  this.year = null;
  this.month = null;
  this.date = null;
  this.hour = null;
  this.minute = null;
  if (args) {
    if (args.year !== undefined && args.year !== null) {
      this.year = args.year;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field year is unset!');
    }
    if (args.month !== undefined && args.month !== null) {
      this.month = args.month;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field month is unset!');
    }
    if (args.date !== undefined && args.date !== null) {
      this.date = args.date;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field date is unset!');
    }
    if (args.hour !== undefined && args.hour !== null) {
      this.hour = args.hour;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field hour is unset!');
    }
    if (args.minute !== undefined && args.minute !== null) {
      this.minute = args.minute;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field minute is unset!');
    }
  }
};
RelativeTime.prototype = {};
RelativeTime.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.year = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.month = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.date = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.hour = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.minute = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RelativeTime.prototype.write = function(output) {
  output.writeStructBegin('RelativeTime');
  if (this.year !== null && this.year !== undefined) {
    output.writeFieldBegin('year', Thrift.Type.I32, 1);
    output.writeI32(this.year);
    output.writeFieldEnd();
  }
  if (this.month !== null && this.month !== undefined) {
    output.writeFieldBegin('month', Thrift.Type.I32, 2);
    output.writeI32(this.month);
    output.writeFieldEnd();
  }
  if (this.date !== null && this.date !== undefined) {
    output.writeFieldBegin('date', Thrift.Type.I32, 3);
    output.writeI32(this.date);
    output.writeFieldEnd();
  }
  if (this.hour !== null && this.hour !== undefined) {
    output.writeFieldBegin('hour', Thrift.Type.I32, 4);
    output.writeI32(this.hour);
    output.writeFieldEnd();
  }
  if (this.minute !== null && this.minute !== undefined) {
    output.writeFieldBegin('minute', Thrift.Type.I32, 5);
    output.writeI32(this.minute);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Duration = function(args) {
  this.unit = null;
  this.length = null;
  if (args) {
    if (args.unit !== undefined && args.unit !== null) {
      this.unit = args.unit;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field unit is unset!');
    }
    if (args.length !== undefined && args.length !== null) {
      this.length = args.length;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field length is unset!');
    }
  }
};
Duration.prototype = {};
Duration.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.unit = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.length = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Duration.prototype.write = function(output) {
  output.writeStructBegin('Duration');
  if (this.unit !== null && this.unit !== undefined) {
    output.writeFieldBegin('unit', Thrift.Type.I32, 1);
    output.writeI32(this.unit);
    output.writeFieldEnd();
  }
  if (this.length !== null && this.length !== undefined) {
    output.writeFieldBegin('length', Thrift.Type.I32, 2);
    output.writeI32(this.length);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

StartTime = function(args) {
  this.absoluteTime = null;
  this.relativeTime = null;
  if (args) {
    if (args.absoluteTime !== undefined && args.absoluteTime !== null) {
      this.absoluteTime = new AbsoluteTime(args.absoluteTime);
    }
    if (args.relativeTime !== undefined && args.relativeTime !== null) {
      this.relativeTime = new RelativeTime(args.relativeTime);
    }
  }
};
StartTime.prototype = {};
StartTime.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.absoluteTime = new AbsoluteTime();
        this.absoluteTime.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.relativeTime = new RelativeTime();
        this.relativeTime.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

StartTime.prototype.write = function(output) {
  output.writeStructBegin('StartTime');
  if (this.absoluteTime !== null && this.absoluteTime !== undefined) {
    output.writeFieldBegin('absoluteTime', Thrift.Type.STRUCT, 1);
    this.absoluteTime.write(output);
    output.writeFieldEnd();
  }
  if (this.relativeTime !== null && this.relativeTime !== undefined) {
    output.writeFieldBegin('relativeTime', Thrift.Type.STRUCT, 2);
    this.relativeTime.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceTime = function(args) {
  this.duration = null;
  this.startTime = null;
  this.userLevelDuration = null;
  this.absoluteStartTime = null;
  this.relativeStartTime = null;
  if (args) {
    if (args.duration !== undefined && args.duration !== null) {
      this.duration = new Duration(args.duration);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field duration is unset!');
    }
    if (args.startTime !== undefined && args.startTime !== null) {
      this.startTime = new StartTime(args.startTime);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field startTime is unset!');
    }
    if (args.userLevelDuration !== undefined && args.userLevelDuration !== null) {
      this.userLevelDuration = new Duration(args.userLevelDuration);
    }
    if (args.absoluteStartTime !== undefined && args.absoluteStartTime !== null) {
      this.absoluteStartTime = new AbsoluteTime(args.absoluteStartTime);
    }
    if (args.relativeStartTime !== undefined && args.relativeStartTime !== null) {
      this.relativeStartTime = new RelativeTime(args.relativeStartTime);
    }
  }
};
GlanceTime.prototype = {};
GlanceTime.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.duration = new Duration();
        this.duration.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.startTime = new StartTime();
        this.startTime.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.userLevelDuration = new Duration();
        this.userLevelDuration.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.absoluteStartTime = new AbsoluteTime();
        this.absoluteStartTime.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.relativeStartTime = new RelativeTime();
        this.relativeStartTime.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceTime.prototype.write = function(output) {
  output.writeStructBegin('GlanceTime');
  if (this.duration !== null && this.duration !== undefined) {
    output.writeFieldBegin('duration', Thrift.Type.STRUCT, 1);
    this.duration.write(output);
    output.writeFieldEnd();
  }
  if (this.startTime !== null && this.startTime !== undefined) {
    output.writeFieldBegin('startTime', Thrift.Type.STRUCT, 2);
    this.startTime.write(output);
    output.writeFieldEnd();
  }
  if (this.userLevelDuration !== null && this.userLevelDuration !== undefined) {
    output.writeFieldBegin('userLevelDuration', Thrift.Type.STRUCT, 3);
    this.userLevelDuration.write(output);
    output.writeFieldEnd();
  }
  if (this.absoluteStartTime !== null && this.absoluteStartTime !== undefined) {
    output.writeFieldBegin('absoluteStartTime', Thrift.Type.STRUCT, 4);
    this.absoluteStartTime.write(output);
    output.writeFieldEnd();
  }
  if (this.relativeStartTime !== null && this.relativeStartTime !== undefined) {
    output.writeFieldBegin('relativeStartTime', Thrift.Type.STRUCT, 5);
    this.relativeStartTime.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartingTime = function(args) {
  this.startTime = null;
  this.endTime = null;
  if (args) {
    if (args.startTime !== undefined && args.startTime !== null) {
      this.startTime = args.startTime;
    }
    if (args.endTime !== undefined && args.endTime !== null) {
      this.endTime = args.endTime;
    }
  }
};
PartingTime.prototype = {};
PartingTime.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I16) {
        this.startTime = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I16) {
        this.endTime = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartingTime.prototype.write = function(output) {
  output.writeStructBegin('PartingTime');
  if (this.startTime !== null && this.startTime !== undefined) {
    output.writeFieldBegin('startTime', Thrift.Type.I16, 1);
    output.writeI16(this.startTime);
    output.writeFieldEnd();
  }
  if (this.endTime !== null && this.endTime !== undefined) {
    output.writeFieldBegin('endTime', Thrift.Type.I16, 2);
    output.writeI16(this.endTime);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ResyncDetails = function(args) {
  this.resyncedBy = null;
  this.resyncedAtInSecs = null;
  if (args) {
    if (args.resyncedBy !== undefined && args.resyncedBy !== null) {
      this.resyncedBy = args.resyncedBy;
    }
    if (args.resyncedAtInSecs !== undefined && args.resyncedAtInSecs !== null) {
      this.resyncedAtInSecs = args.resyncedAtInSecs;
    }
  }
};
ResyncDetails.prototype = {};
ResyncDetails.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.resyncedBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.resyncedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ResyncDetails.prototype.write = function(output) {
  output.writeStructBegin('ResyncDetails');
  if (this.resyncedBy !== null && this.resyncedBy !== undefined) {
    output.writeFieldBegin('resyncedBy', Thrift.Type.STRING, 1);
    output.writeString(this.resyncedBy);
    output.writeFieldEnd();
  }
  if (this.resyncedAtInSecs !== null && this.resyncedAtInSecs !== undefined) {
    output.writeFieldBegin('resyncedAtInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.resyncedAtInSecs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceMeta = function(args) {
  this.metaId = null;
  this.glanceId = null;
  this.partnerId = null;
  this.region = null;
  this.updatedAtInSecs = null;
  this.glanceTime = null;
  this.removed = false;
  this.state = null;
  this.createdAtInSecs = null;
  this.maxExpiryAcrossTzInSec = null;
  this.rebrandlyUrl = null;
  this.version = null;
  this.approvedAtInSecs = null;
  this.abTag = null;
  this.abTags = null;
  this.absoluteStartTimeInSec = null;
  this.experiments = null;
  this.internalRejectionReason = null;
  this.updatedAt = null;
  this.expiryAcrossRegionInSec = null;
  this.ruleIds = null;
  this.pausedAtInSecs = null;
  this.isPartedGlance = false;
  this.partingSchedule = null;
  this.euIdentifier = null;
  this.ruleIdsList = null;
  this.isProgrammatic = false;
  this.appTargeting = null;
  if (args) {
    if (args.metaId !== undefined && args.metaId !== null) {
      this.metaId = args.metaId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field metaId is unset!');
    }
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partnerId is unset!');
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field region is unset!');
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field updatedAtInSecs is unset!');
    }
    if (args.glanceTime !== undefined && args.glanceTime !== null) {
      this.glanceTime = new GlanceTime(args.glanceTime);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceTime is unset!');
    }
    if (args.removed !== undefined && args.removed !== null) {
      this.removed = args.removed;
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.maxExpiryAcrossTzInSec !== undefined && args.maxExpiryAcrossTzInSec !== null) {
      this.maxExpiryAcrossTzInSec = args.maxExpiryAcrossTzInSec;
    }
    if (args.rebrandlyUrl !== undefined && args.rebrandlyUrl !== null) {
      this.rebrandlyUrl = args.rebrandlyUrl;
    }
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
    if (args.approvedAtInSecs !== undefined && args.approvedAtInSecs !== null) {
      this.approvedAtInSecs = args.approvedAtInSecs;
    }
    if (args.abTag !== undefined && args.abTag !== null) {
      this.abTag = args.abTag;
    }
    if (args.abTags !== undefined && args.abTags !== null) {
      this.abTags = Thrift.copyList(args.abTags, [null]);
    }
    if (args.absoluteStartTimeInSec !== undefined && args.absoluteStartTimeInSec !== null) {
      this.absoluteStartTimeInSec = args.absoluteStartTimeInSec;
    }
    if (args.experiments !== undefined && args.experiments !== null) {
      this.experiments = Thrift.copyList(args.experiments, [ExperimentMeta]);
    }
    if (args.internalRejectionReason !== undefined && args.internalRejectionReason !== null) {
      this.internalRejectionReason = args.internalRejectionReason;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.expiryAcrossRegionInSec !== undefined && args.expiryAcrossRegionInSec !== null) {
      this.expiryAcrossRegionInSec = args.expiryAcrossRegionInSec;
    }
    if (args.ruleIds !== undefined && args.ruleIds !== null) {
      this.ruleIds = Thrift.copyList(args.ruleIds, [null]);
    }
    if (args.pausedAtInSecs !== undefined && args.pausedAtInSecs !== null) {
      this.pausedAtInSecs = args.pausedAtInSecs;
    }
    if (args.isPartedGlance !== undefined && args.isPartedGlance !== null) {
      this.isPartedGlance = args.isPartedGlance;
    }
    if (args.partingSchedule !== undefined && args.partingSchedule !== null) {
      this.partingSchedule = Thrift.copyMap(args.partingSchedule, [Thrift.copyList, PartingTime]);
    }
    if (args.euIdentifier !== undefined && args.euIdentifier !== null) {
      this.euIdentifier = args.euIdentifier;
    }
    if (args.ruleIdsList !== undefined && args.ruleIdsList !== null) {
      this.ruleIdsList = Thrift.copyList(args.ruleIdsList, [RuleIds]);
    }
    if (args.isProgrammatic !== undefined && args.isProgrammatic !== null) {
      this.isProgrammatic = args.isProgrammatic;
    }
    if (args.appTargeting !== undefined && args.appTargeting !== null) {
      this.appTargeting = new AppTargeting(args.appTargeting);
    }
  }
};
GlanceMeta.prototype = {};
GlanceMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.metaId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceTime = new GlanceTime();
        this.glanceTime.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.removed = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.state = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I64) {
        this.maxExpiryAcrossTzInSec = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.rebrandlyUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I64) {
        this.version = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.I64) {
        this.approvedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.abTag = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.LIST) {
        var _size672 = 0;
        var _rtmp3676;
        this.abTags = [];
        var _etype675 = 0;
        _rtmp3676 = input.readListBegin();
        _etype675 = _rtmp3676.etype;
        _size672 = _rtmp3676.size;
        for (var _i677 = 0; _i677 < _size672; ++_i677)
        {
          var elem678 = null;
          elem678 = input.readString().value;
          this.abTags.push(elem678);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.I64) {
        this.absoluteStartTimeInSec = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.LIST) {
        var _size679 = 0;
        var _rtmp3683;
        this.experiments = [];
        var _etype682 = 0;
        _rtmp3683 = input.readListBegin();
        _etype682 = _rtmp3683.etype;
        _size679 = _rtmp3683.size;
        for (var _i684 = 0; _i684 < _size679; ++_i684)
        {
          var elem685 = null;
          elem685 = new ExperimentMeta();
          elem685.read(input);
          this.experiments.push(elem685);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.internalRejectionReason = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.I64) {
        this.expiryAcrossRegionInSec = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.LIST) {
        var _size686 = 0;
        var _rtmp3690;
        this.ruleIds = [];
        var _etype689 = 0;
        _rtmp3690 = input.readListBegin();
        _etype689 = _rtmp3690.etype;
        _size686 = _rtmp3690.size;
        for (var _i691 = 0; _i691 < _size686; ++_i691)
        {
          var elem692 = null;
          elem692 = input.readString().value;
          this.ruleIds.push(elem692);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.I64) {
        this.pausedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.BOOL) {
        this.isPartedGlance = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.MAP) {
        var _size693 = 0;
        var _rtmp3697;
        this.partingSchedule = {};
        var _ktype694 = 0;
        var _vtype695 = 0;
        _rtmp3697 = input.readMapBegin();
        _ktype694 = _rtmp3697.ktype;
        _vtype695 = _rtmp3697.vtype;
        _size693 = _rtmp3697.size;
        for (var _i698 = 0; _i698 < _size693; ++_i698)
        {
          if (_i698 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key699 = null;
          var val700 = null;
          key699 = input.readString().value;
          var _size701 = 0;
          var _rtmp3705;
          val700 = [];
          var _etype704 = 0;
          _rtmp3705 = input.readListBegin();
          _etype704 = _rtmp3705.etype;
          _size701 = _rtmp3705.size;
          for (var _i706 = 0; _i706 < _size701; ++_i706)
          {
            var elem707 = null;
            elem707 = new PartingTime();
            elem707.read(input);
            val700.push(elem707);
          }
          input.readListEnd();
          this.partingSchedule[key699] = val700;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.I32) {
        this.euIdentifier = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.LIST) {
        var _size708 = 0;
        var _rtmp3712;
        this.ruleIdsList = [];
        var _etype711 = 0;
        _rtmp3712 = input.readListBegin();
        _etype711 = _rtmp3712.etype;
        _size708 = _rtmp3712.size;
        for (var _i713 = 0; _i713 < _size708; ++_i713)
        {
          var elem714 = null;
          elem714 = new RuleIds();
          elem714.read(input);
          this.ruleIdsList.push(elem714);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.BOOL) {
        this.isProgrammatic = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.STRUCT) {
        this.appTargeting = new AppTargeting();
        this.appTargeting.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceMeta.prototype.write = function(output) {
  output.writeStructBegin('GlanceMeta');
  if (this.metaId !== null && this.metaId !== undefined) {
    output.writeFieldBegin('metaId', Thrift.Type.STRING, 1);
    output.writeString(this.metaId);
    output.writeFieldEnd();
  }
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 2);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 3);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 4);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.glanceTime !== null && this.glanceTime !== undefined) {
    output.writeFieldBegin('glanceTime', Thrift.Type.STRUCT, 6);
    this.glanceTime.write(output);
    output.writeFieldEnd();
  }
  if (this.removed !== null && this.removed !== undefined) {
    output.writeFieldBegin('removed', Thrift.Type.BOOL, 7);
    output.writeBool(this.removed);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.I32, 8);
    output.writeI32(this.state);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 9);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.maxExpiryAcrossTzInSec !== null && this.maxExpiryAcrossTzInSec !== undefined) {
    output.writeFieldBegin('maxExpiryAcrossTzInSec', Thrift.Type.I64, 10);
    output.writeI64(this.maxExpiryAcrossTzInSec);
    output.writeFieldEnd();
  }
  if (this.rebrandlyUrl !== null && this.rebrandlyUrl !== undefined) {
    output.writeFieldBegin('rebrandlyUrl', Thrift.Type.STRING, 11);
    output.writeString(this.rebrandlyUrl);
    output.writeFieldEnd();
  }
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.I64, 12);
    output.writeI64(this.version);
    output.writeFieldEnd();
  }
  if (this.approvedAtInSecs !== null && this.approvedAtInSecs !== undefined) {
    output.writeFieldBegin('approvedAtInSecs', Thrift.Type.I64, 13);
    output.writeI64(this.approvedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.abTag !== null && this.abTag !== undefined) {
    output.writeFieldBegin('abTag', Thrift.Type.STRING, 14);
    output.writeString(this.abTag);
    output.writeFieldEnd();
  }
  if (this.abTags !== null && this.abTags !== undefined) {
    output.writeFieldBegin('abTags', Thrift.Type.LIST, 15);
    output.writeListBegin(Thrift.Type.STRING, this.abTags.length);
    for (var iter715 in this.abTags)
    {
      if (this.abTags.hasOwnProperty(iter715))
      {
        iter715 = this.abTags[iter715];
        output.writeString(iter715);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.absoluteStartTimeInSec !== null && this.absoluteStartTimeInSec !== undefined) {
    output.writeFieldBegin('absoluteStartTimeInSec', Thrift.Type.I64, 16);
    output.writeI64(this.absoluteStartTimeInSec);
    output.writeFieldEnd();
  }
  if (this.experiments !== null && this.experiments !== undefined) {
    output.writeFieldBegin('experiments', Thrift.Type.LIST, 17);
    output.writeListBegin(Thrift.Type.STRUCT, this.experiments.length);
    for (var iter716 in this.experiments)
    {
      if (this.experiments.hasOwnProperty(iter716))
      {
        iter716 = this.experiments[iter716];
        iter716.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.internalRejectionReason !== null && this.internalRejectionReason !== undefined) {
    output.writeFieldBegin('internalRejectionReason', Thrift.Type.STRING, 18);
    output.writeString(this.internalRejectionReason);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 19);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.expiryAcrossRegionInSec !== null && this.expiryAcrossRegionInSec !== undefined) {
    output.writeFieldBegin('expiryAcrossRegionInSec', Thrift.Type.I64, 20);
    output.writeI64(this.expiryAcrossRegionInSec);
    output.writeFieldEnd();
  }
  if (this.ruleIds !== null && this.ruleIds !== undefined) {
    output.writeFieldBegin('ruleIds', Thrift.Type.LIST, 21);
    output.writeListBegin(Thrift.Type.STRING, this.ruleIds.length);
    for (var iter717 in this.ruleIds)
    {
      if (this.ruleIds.hasOwnProperty(iter717))
      {
        iter717 = this.ruleIds[iter717];
        output.writeString(iter717);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.pausedAtInSecs !== null && this.pausedAtInSecs !== undefined) {
    output.writeFieldBegin('pausedAtInSecs', Thrift.Type.I64, 22);
    output.writeI64(this.pausedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.isPartedGlance !== null && this.isPartedGlance !== undefined) {
    output.writeFieldBegin('isPartedGlance', Thrift.Type.BOOL, 23);
    output.writeBool(this.isPartedGlance);
    output.writeFieldEnd();
  }
  if (this.partingSchedule !== null && this.partingSchedule !== undefined) {
    output.writeFieldBegin('partingSchedule', Thrift.Type.MAP, 24);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.LIST, Thrift.objectLength(this.partingSchedule));
    for (var kiter718 in this.partingSchedule)
    {
      if (this.partingSchedule.hasOwnProperty(kiter718))
      {
        var viter719 = this.partingSchedule[kiter718];
        output.writeString(kiter718);
        output.writeListBegin(Thrift.Type.STRUCT, viter719.length);
        for (var iter720 in viter719)
        {
          if (viter719.hasOwnProperty(iter720))
          {
            iter720 = viter719[iter720];
            iter720.write(output);
          }
        }
        output.writeListEnd();
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.euIdentifier !== null && this.euIdentifier !== undefined) {
    output.writeFieldBegin('euIdentifier', Thrift.Type.I32, 25);
    output.writeI32(this.euIdentifier);
    output.writeFieldEnd();
  }
  if (this.ruleIdsList !== null && this.ruleIdsList !== undefined) {
    output.writeFieldBegin('ruleIdsList', Thrift.Type.LIST, 26);
    output.writeListBegin(Thrift.Type.STRUCT, this.ruleIdsList.length);
    for (var iter721 in this.ruleIdsList)
    {
      if (this.ruleIdsList.hasOwnProperty(iter721))
      {
        iter721 = this.ruleIdsList[iter721];
        iter721.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.isProgrammatic !== null && this.isProgrammatic !== undefined) {
    output.writeFieldBegin('isProgrammatic', Thrift.Type.BOOL, 27);
    output.writeBool(this.isProgrammatic);
    output.writeFieldEnd();
  }
  if (this.appTargeting !== null && this.appTargeting !== undefined) {
    output.writeFieldBegin('appTargeting', Thrift.Type.STRUCT, 28);
    this.appTargeting.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DraftUsers = function(args) {
  this.curatedBy = null;
  this.designedBy = null;
  this.publishedBy = null;
  this.resyncDetails = null;
  if (args) {
    if (args.curatedBy !== undefined && args.curatedBy !== null) {
      this.curatedBy = args.curatedBy;
    }
    if (args.designedBy !== undefined && args.designedBy !== null) {
      this.designedBy = args.designedBy;
    }
    if (args.publishedBy !== undefined && args.publishedBy !== null) {
      this.publishedBy = args.publishedBy;
    }
    if (args.resyncDetails !== undefined && args.resyncDetails !== null) {
      this.resyncDetails = Thrift.copyList(args.resyncDetails, [ResyncDetails]);
    }
  }
};
DraftUsers.prototype = {};
DraftUsers.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.curatedBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.designedBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.publishedBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size722 = 0;
        var _rtmp3726;
        this.resyncDetails = [];
        var _etype725 = 0;
        _rtmp3726 = input.readListBegin();
        _etype725 = _rtmp3726.etype;
        _size722 = _rtmp3726.size;
        for (var _i727 = 0; _i727 < _size722; ++_i727)
        {
          var elem728 = null;
          elem728 = new ResyncDetails();
          elem728.read(input);
          this.resyncDetails.push(elem728);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DraftUsers.prototype.write = function(output) {
  output.writeStructBegin('DraftUsers');
  if (this.curatedBy !== null && this.curatedBy !== undefined) {
    output.writeFieldBegin('curatedBy', Thrift.Type.STRING, 1);
    output.writeString(this.curatedBy);
    output.writeFieldEnd();
  }
  if (this.designedBy !== null && this.designedBy !== undefined) {
    output.writeFieldBegin('designedBy', Thrift.Type.STRING, 2);
    output.writeString(this.designedBy);
    output.writeFieldEnd();
  }
  if (this.publishedBy !== null && this.publishedBy !== undefined) {
    output.writeFieldBegin('publishedBy', Thrift.Type.STRING, 3);
    output.writeString(this.publishedBy);
    output.writeFieldEnd();
  }
  if (this.resyncDetails !== null && this.resyncDetails !== undefined) {
    output.writeFieldBegin('resyncDetails', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRUCT, this.resyncDetails.length);
    for (var iter729 in this.resyncDetails)
    {
      if (this.resyncDetails.hasOwnProperty(iter729))
      {
        iter729 = this.resyncDetails[iter729];
        iter729.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DraftEditTimes = function(args) {
  this.curatedAtInSecs = null;
  this.designedAtInSecs = null;
  this.publishedAtInSecs = null;
  if (args) {
    if (args.curatedAtInSecs !== undefined && args.curatedAtInSecs !== null) {
      this.curatedAtInSecs = args.curatedAtInSecs;
    }
    if (args.designedAtInSecs !== undefined && args.designedAtInSecs !== null) {
      this.designedAtInSecs = args.designedAtInSecs;
    }
    if (args.publishedAtInSecs !== undefined && args.publishedAtInSecs !== null) {
      this.publishedAtInSecs = args.publishedAtInSecs;
    }
  }
};
DraftEditTimes.prototype = {};
DraftEditTimes.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.curatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.designedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.publishedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DraftEditTimes.prototype.write = function(output) {
  output.writeStructBegin('DraftEditTimes');
  if (this.curatedAtInSecs !== null && this.curatedAtInSecs !== undefined) {
    output.writeFieldBegin('curatedAtInSecs', Thrift.Type.I64, 1);
    output.writeI64(this.curatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.designedAtInSecs !== null && this.designedAtInSecs !== undefined) {
    output.writeFieldBegin('designedAtInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.designedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.publishedAtInSecs !== null && this.publishedAtInSecs !== undefined) {
    output.writeFieldBegin('publishedAtInSecs', Thrift.Type.I64, 3);
    output.writeI64(this.publishedAtInSecs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DraftEditMeta = function(args) {
  this.state = null;
  this.users = null;
  this.editTimes = null;
  this.curationReason = null;
  this.rejectionReason = null;
  if (args) {
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field state is unset!');
    }
    if (args.users !== undefined && args.users !== null) {
      this.users = new DraftUsers(args.users);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field users is unset!');
    }
    if (args.editTimes !== undefined && args.editTimes !== null) {
      this.editTimes = new DraftEditTimes(args.editTimes);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field editTimes is unset!');
    }
    if (args.curationReason !== undefined && args.curationReason !== null) {
      this.curationReason = args.curationReason;
    }
    if (args.rejectionReason !== undefined && args.rejectionReason !== null) {
      this.rejectionReason = args.rejectionReason;
    }
  }
};
DraftEditMeta.prototype = {};
DraftEditMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.state = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.users = new DraftUsers();
        this.users.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.editTimes = new DraftEditTimes();
        this.editTimes.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.curationReason = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.rejectionReason = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DraftEditMeta.prototype.write = function(output) {
  output.writeStructBegin('DraftEditMeta');
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.I32, 1);
    output.writeI32(this.state);
    output.writeFieldEnd();
  }
  if (this.users !== null && this.users !== undefined) {
    output.writeFieldBegin('users', Thrift.Type.STRUCT, 2);
    this.users.write(output);
    output.writeFieldEnd();
  }
  if (this.editTimes !== null && this.editTimes !== undefined) {
    output.writeFieldBegin('editTimes', Thrift.Type.STRUCT, 3);
    this.editTimes.write(output);
    output.writeFieldEnd();
  }
  if (this.curationReason !== null && this.curationReason !== undefined) {
    output.writeFieldBegin('curationReason', Thrift.Type.STRING, 4);
    output.writeString(this.curationReason);
    output.writeFieldEnd();
  }
  if (this.rejectionReason !== null && this.rejectionReason !== undefined) {
    output.writeFieldBegin('rejectionReason', Thrift.Type.STRING, 5);
    output.writeString(this.rejectionReason);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PeekDraft = function(args) {
  this.title = null;
  this.sourceName = null;
  this.heartCount = null;
  this.summary = null;
  this.shortUrl = null;
  this.ctaUrl = null;
  this.cta = null;
  this.videoUrl = null;
  this.video = null;
  this.shouldUnlock = null;
  this.shortTitle = null;
  this.summaryLimit = null;
  this.cheesySummary = null;
  this.canSkipSummary = null;
  this.glanceContext = null;
  this.offlinePeekUrl = null;
  this.videoCtaUrl = null;
  this.webPeekUrl = null;
  this.appCta = null;
  this.ctaType = null;
  this.hardwareAccelerated = false;
  this.loadAndroidJs = false;
  this.nativeVideoUrl = null;
  this.nativeVideoOverflow = null;
  this.lockscreenCtaText = null;
  this.liveVideoPeek = null;
  this.tvVideoPeek = null;
  this.bgColor = null;
  this.ctaBgColor = null;
  this.iconUrl = null;
  this.showAnim = null;
  this.subText = null;
  this.useBubbleImageAsRibbonIcon = null;
  this.notifTemplateAppId = null;
  this.nativeVideoDurationInMillis = null;
  this.nativeLiveVideoPeek = null;
  this.fusionVideoPeek = null;
  this.noAds = null;
  this.articleCtaUrl = null;
  if (args) {
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.sourceName !== undefined && args.sourceName !== null) {
      this.sourceName = args.sourceName;
    }
    if (args.heartCount !== undefined && args.heartCount !== null) {
      this.heartCount = args.heartCount;
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.shortUrl !== undefined && args.shortUrl !== null) {
      this.shortUrl = args.shortUrl;
    }
    if (args.ctaUrl !== undefined && args.ctaUrl !== null) {
      this.ctaUrl = args.ctaUrl;
    }
    if (args.cta !== undefined && args.cta !== null) {
      this.cta = new Cta(args.cta);
    }
    if (args.videoUrl !== undefined && args.videoUrl !== null) {
      this.videoUrl = args.videoUrl;
    }
    if (args.video !== undefined && args.video !== null) {
      this.video = new VideoData(args.video);
    }
    if (args.shouldUnlock !== undefined && args.shouldUnlock !== null) {
      this.shouldUnlock = args.shouldUnlock;
    }
    if (args.shortTitle !== undefined && args.shortTitle !== null) {
      this.shortTitle = args.shortTitle;
    }
    if (args.summaryLimit !== undefined && args.summaryLimit !== null) {
      this.summaryLimit = args.summaryLimit;
    }
    if (args.cheesySummary !== undefined && args.cheesySummary !== null) {
      this.cheesySummary = args.cheesySummary;
    }
    if (args.canSkipSummary !== undefined && args.canSkipSummary !== null) {
      this.canSkipSummary = args.canSkipSummary;
    }
    if (args.glanceContext !== undefined && args.glanceContext !== null) {
      this.glanceContext = args.glanceContext;
    }
    if (args.offlinePeekUrl !== undefined && args.offlinePeekUrl !== null) {
      this.offlinePeekUrl = args.offlinePeekUrl;
    }
    if (args.videoCtaUrl !== undefined && args.videoCtaUrl !== null) {
      this.videoCtaUrl = args.videoCtaUrl;
    }
    if (args.webPeekUrl !== undefined && args.webPeekUrl !== null) {
      this.webPeekUrl = args.webPeekUrl;
    }
    if (args.appCta !== undefined && args.appCta !== null) {
      this.appCta = new AppCta(args.appCta);
    }
    if (args.ctaType !== undefined && args.ctaType !== null) {
      this.ctaType = args.ctaType;
    }
    if (args.hardwareAccelerated !== undefined && args.hardwareAccelerated !== null) {
      this.hardwareAccelerated = args.hardwareAccelerated;
    }
    if (args.loadAndroidJs !== undefined && args.loadAndroidJs !== null) {
      this.loadAndroidJs = args.loadAndroidJs;
    }
    if (args.nativeVideoUrl !== undefined && args.nativeVideoUrl !== null) {
      this.nativeVideoUrl = args.nativeVideoUrl;
    }
    if (args.nativeVideoOverflow !== undefined && args.nativeVideoOverflow !== null) {
      this.nativeVideoOverflow = args.nativeVideoOverflow;
    }
    if (args.lockscreenCtaText !== undefined && args.lockscreenCtaText !== null) {
      this.lockscreenCtaText = args.lockscreenCtaText;
    }
    if (args.liveVideoPeek !== undefined && args.liveVideoPeek !== null) {
      this.liveVideoPeek = new LiveVideoPeek(args.liveVideoPeek);
    }
    if (args.tvVideoPeek !== undefined && args.tvVideoPeek !== null) {
      this.tvVideoPeek = new TvVideoPeek(args.tvVideoPeek);
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = args.bgColor;
    }
    if (args.ctaBgColor !== undefined && args.ctaBgColor !== null) {
      this.ctaBgColor = args.ctaBgColor;
    }
    if (args.iconUrl !== undefined && args.iconUrl !== null) {
      this.iconUrl = args.iconUrl;
    }
    if (args.showAnim !== undefined && args.showAnim !== null) {
      this.showAnim = args.showAnim;
    }
    if (args.subText !== undefined && args.subText !== null) {
      this.subText = args.subText;
    }
    if (args.useBubbleImageAsRibbonIcon !== undefined && args.useBubbleImageAsRibbonIcon !== null) {
      this.useBubbleImageAsRibbonIcon = args.useBubbleImageAsRibbonIcon;
    }
    if (args.notifTemplateAppId !== undefined && args.notifTemplateAppId !== null) {
      this.notifTemplateAppId = args.notifTemplateAppId;
    }
    if (args.nativeVideoDurationInMillis !== undefined && args.nativeVideoDurationInMillis !== null) {
      this.nativeVideoDurationInMillis = args.nativeVideoDurationInMillis;
    }
    if (args.nativeLiveVideoPeek !== undefined && args.nativeLiveVideoPeek !== null) {
      this.nativeLiveVideoPeek = new NativeLiveVideoPeek(args.nativeLiveVideoPeek);
    }
    if (args.fusionVideoPeek !== undefined && args.fusionVideoPeek !== null) {
      this.fusionVideoPeek = new FusionVideoPeek(args.fusionVideoPeek);
    }
    if (args.noAds !== undefined && args.noAds !== null) {
      this.noAds = args.noAds;
    }
    if (args.articleCtaUrl !== undefined && args.articleCtaUrl !== null) {
      this.articleCtaUrl = args.articleCtaUrl;
    }
  }
};
PeekDraft.prototype = {};
PeekDraft.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.sourceName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.heartCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.shortUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.ctaUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRUCT) {
        this.cta = new Cta();
        this.cta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.videoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRUCT) {
        this.video = new VideoData();
        this.video.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldUnlock = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.shortTitle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I32) {
        this.summaryLimit = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.cheesySummary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.BOOL) {
        this.canSkipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.I32) {
        this.glanceContext = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.offlinePeekUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.videoCtaUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.webPeekUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRUCT) {
        this.appCta = new AppCta();
        this.appCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.I32) {
        this.ctaType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.BOOL) {
        this.hardwareAccelerated = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.BOOL) {
        this.loadAndroidJs = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.nativeVideoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.BOOL) {
        this.nativeVideoOverflow = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.STRING) {
        this.lockscreenCtaText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.STRUCT) {
        this.liveVideoPeek = new LiveVideoPeek();
        this.liveVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.STRUCT) {
        this.tvVideoPeek = new TvVideoPeek();
        this.tvVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.STRING) {
        this.bgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.STRING) {
        this.ctaBgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.STRING) {
        this.iconUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.BOOL) {
        this.showAnim = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRING) {
        this.subText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.BOOL) {
        this.useBubbleImageAsRibbonIcon = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.STRING) {
        this.notifTemplateAppId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.I64) {
        this.nativeVideoDurationInMillis = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 36:
      if (ftype == Thrift.Type.STRUCT) {
        this.nativeLiveVideoPeek = new NativeLiveVideoPeek();
        this.nativeLiveVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 37:
      if (ftype == Thrift.Type.STRUCT) {
        this.fusionVideoPeek = new FusionVideoPeek();
        this.fusionVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 38:
      if (ftype == Thrift.Type.BOOL) {
        this.noAds = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 39:
      if (ftype == Thrift.Type.STRING) {
        this.articleCtaUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PeekDraft.prototype.write = function(output) {
  output.writeStructBegin('PeekDraft');
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 1);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.sourceName !== null && this.sourceName !== undefined) {
    output.writeFieldBegin('sourceName', Thrift.Type.STRING, 2);
    output.writeString(this.sourceName);
    output.writeFieldEnd();
  }
  if (this.heartCount !== null && this.heartCount !== undefined) {
    output.writeFieldBegin('heartCount', Thrift.Type.I64, 3);
    output.writeI64(this.heartCount);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 4);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.shortUrl !== null && this.shortUrl !== undefined) {
    output.writeFieldBegin('shortUrl', Thrift.Type.STRING, 5);
    output.writeString(this.shortUrl);
    output.writeFieldEnd();
  }
  if (this.ctaUrl !== null && this.ctaUrl !== undefined) {
    output.writeFieldBegin('ctaUrl', Thrift.Type.STRING, 6);
    output.writeString(this.ctaUrl);
    output.writeFieldEnd();
  }
  if (this.cta !== null && this.cta !== undefined) {
    output.writeFieldBegin('cta', Thrift.Type.STRUCT, 7);
    this.cta.write(output);
    output.writeFieldEnd();
  }
  if (this.videoUrl !== null && this.videoUrl !== undefined) {
    output.writeFieldBegin('videoUrl', Thrift.Type.STRING, 8);
    output.writeString(this.videoUrl);
    output.writeFieldEnd();
  }
  if (this.video !== null && this.video !== undefined) {
    output.writeFieldBegin('video', Thrift.Type.STRUCT, 9);
    this.video.write(output);
    output.writeFieldEnd();
  }
  if (this.shouldUnlock !== null && this.shouldUnlock !== undefined) {
    output.writeFieldBegin('shouldUnlock', Thrift.Type.BOOL, 10);
    output.writeBool(this.shouldUnlock);
    output.writeFieldEnd();
  }
  if (this.shortTitle !== null && this.shortTitle !== undefined) {
    output.writeFieldBegin('shortTitle', Thrift.Type.STRING, 11);
    output.writeString(this.shortTitle);
    output.writeFieldEnd();
  }
  if (this.summaryLimit !== null && this.summaryLimit !== undefined) {
    output.writeFieldBegin('summaryLimit', Thrift.Type.I32, 12);
    output.writeI32(this.summaryLimit);
    output.writeFieldEnd();
  }
  if (this.cheesySummary !== null && this.cheesySummary !== undefined) {
    output.writeFieldBegin('cheesySummary', Thrift.Type.STRING, 13);
    output.writeString(this.cheesySummary);
    output.writeFieldEnd();
  }
  if (this.canSkipSummary !== null && this.canSkipSummary !== undefined) {
    output.writeFieldBegin('canSkipSummary', Thrift.Type.BOOL, 14);
    output.writeBool(this.canSkipSummary);
    output.writeFieldEnd();
  }
  if (this.glanceContext !== null && this.glanceContext !== undefined) {
    output.writeFieldBegin('glanceContext', Thrift.Type.I32, 15);
    output.writeI32(this.glanceContext);
    output.writeFieldEnd();
  }
  if (this.offlinePeekUrl !== null && this.offlinePeekUrl !== undefined) {
    output.writeFieldBegin('offlinePeekUrl', Thrift.Type.STRING, 16);
    output.writeString(this.offlinePeekUrl);
    output.writeFieldEnd();
  }
  if (this.videoCtaUrl !== null && this.videoCtaUrl !== undefined) {
    output.writeFieldBegin('videoCtaUrl', Thrift.Type.STRING, 17);
    output.writeString(this.videoCtaUrl);
    output.writeFieldEnd();
  }
  if (this.webPeekUrl !== null && this.webPeekUrl !== undefined) {
    output.writeFieldBegin('webPeekUrl', Thrift.Type.STRING, 18);
    output.writeString(this.webPeekUrl);
    output.writeFieldEnd();
  }
  if (this.appCta !== null && this.appCta !== undefined) {
    output.writeFieldBegin('appCta', Thrift.Type.STRUCT, 19);
    this.appCta.write(output);
    output.writeFieldEnd();
  }
  if (this.ctaType !== null && this.ctaType !== undefined) {
    output.writeFieldBegin('ctaType', Thrift.Type.I32, 20);
    output.writeI32(this.ctaType);
    output.writeFieldEnd();
  }
  if (this.hardwareAccelerated !== null && this.hardwareAccelerated !== undefined) {
    output.writeFieldBegin('hardwareAccelerated', Thrift.Type.BOOL, 21);
    output.writeBool(this.hardwareAccelerated);
    output.writeFieldEnd();
  }
  if (this.loadAndroidJs !== null && this.loadAndroidJs !== undefined) {
    output.writeFieldBegin('loadAndroidJs', Thrift.Type.BOOL, 22);
    output.writeBool(this.loadAndroidJs);
    output.writeFieldEnd();
  }
  if (this.nativeVideoUrl !== null && this.nativeVideoUrl !== undefined) {
    output.writeFieldBegin('nativeVideoUrl', Thrift.Type.STRING, 23);
    output.writeString(this.nativeVideoUrl);
    output.writeFieldEnd();
  }
  if (this.nativeVideoOverflow !== null && this.nativeVideoOverflow !== undefined) {
    output.writeFieldBegin('nativeVideoOverflow', Thrift.Type.BOOL, 24);
    output.writeBool(this.nativeVideoOverflow);
    output.writeFieldEnd();
  }
  if (this.lockscreenCtaText !== null && this.lockscreenCtaText !== undefined) {
    output.writeFieldBegin('lockscreenCtaText', Thrift.Type.STRING, 25);
    output.writeString(this.lockscreenCtaText);
    output.writeFieldEnd();
  }
  if (this.liveVideoPeek !== null && this.liveVideoPeek !== undefined) {
    output.writeFieldBegin('liveVideoPeek', Thrift.Type.STRUCT, 26);
    this.liveVideoPeek.write(output);
    output.writeFieldEnd();
  }
  if (this.tvVideoPeek !== null && this.tvVideoPeek !== undefined) {
    output.writeFieldBegin('tvVideoPeek', Thrift.Type.STRUCT, 27);
    this.tvVideoPeek.write(output);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.STRING, 28);
    output.writeString(this.bgColor);
    output.writeFieldEnd();
  }
  if (this.ctaBgColor !== null && this.ctaBgColor !== undefined) {
    output.writeFieldBegin('ctaBgColor', Thrift.Type.STRING, 29);
    output.writeString(this.ctaBgColor);
    output.writeFieldEnd();
  }
  if (this.iconUrl !== null && this.iconUrl !== undefined) {
    output.writeFieldBegin('iconUrl', Thrift.Type.STRING, 30);
    output.writeString(this.iconUrl);
    output.writeFieldEnd();
  }
  if (this.showAnim !== null && this.showAnim !== undefined) {
    output.writeFieldBegin('showAnim', Thrift.Type.BOOL, 31);
    output.writeBool(this.showAnim);
    output.writeFieldEnd();
  }
  if (this.subText !== null && this.subText !== undefined) {
    output.writeFieldBegin('subText', Thrift.Type.STRING, 32);
    output.writeString(this.subText);
    output.writeFieldEnd();
  }
  if (this.useBubbleImageAsRibbonIcon !== null && this.useBubbleImageAsRibbonIcon !== undefined) {
    output.writeFieldBegin('useBubbleImageAsRibbonIcon', Thrift.Type.BOOL, 33);
    output.writeBool(this.useBubbleImageAsRibbonIcon);
    output.writeFieldEnd();
  }
  if (this.notifTemplateAppId !== null && this.notifTemplateAppId !== undefined) {
    output.writeFieldBegin('notifTemplateAppId', Thrift.Type.STRING, 34);
    output.writeString(this.notifTemplateAppId);
    output.writeFieldEnd();
  }
  if (this.nativeVideoDurationInMillis !== null && this.nativeVideoDurationInMillis !== undefined) {
    output.writeFieldBegin('nativeVideoDurationInMillis', Thrift.Type.I64, 35);
    output.writeI64(this.nativeVideoDurationInMillis);
    output.writeFieldEnd();
  }
  if (this.nativeLiveVideoPeek !== null && this.nativeLiveVideoPeek !== undefined) {
    output.writeFieldBegin('nativeLiveVideoPeek', Thrift.Type.STRUCT, 36);
    this.nativeLiveVideoPeek.write(output);
    output.writeFieldEnd();
  }
  if (this.fusionVideoPeek !== null && this.fusionVideoPeek !== undefined) {
    output.writeFieldBegin('fusionVideoPeek', Thrift.Type.STRUCT, 37);
    this.fusionVideoPeek.write(output);
    output.writeFieldEnd();
  }
  if (this.noAds !== null && this.noAds !== undefined) {
    output.writeFieldBegin('noAds', Thrift.Type.BOOL, 38);
    output.writeBool(this.noAds);
    output.writeFieldEnd();
  }
  if (this.articleCtaUrl !== null && this.articleCtaUrl !== undefined) {
    output.writeFieldBegin('articleCtaUrl', Thrift.Type.STRING, 39);
    output.writeString(this.articleCtaUrl);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ImpressionCappingData = function(args) {
  this.totalImpressionRequired = null;
  this.triggerPercentage = null;
  this.servingCappingEnabled = null;
  if (args) {
    if (args.totalImpressionRequired !== undefined && args.totalImpressionRequired !== null) {
      this.totalImpressionRequired = args.totalImpressionRequired;
    }
    if (args.triggerPercentage !== undefined && args.triggerPercentage !== null) {
      this.triggerPercentage = args.triggerPercentage;
    }
    if (args.servingCappingEnabled !== undefined && args.servingCappingEnabled !== null) {
      this.servingCappingEnabled = args.servingCappingEnabled;
    }
  }
};
ImpressionCappingData.prototype = {};
ImpressionCappingData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.totalImpressionRequired = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.triggerPercentage = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.servingCappingEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ImpressionCappingData.prototype.write = function(output) {
  output.writeStructBegin('ImpressionCappingData');
  if (this.totalImpressionRequired !== null && this.totalImpressionRequired !== undefined) {
    output.writeFieldBegin('totalImpressionRequired', Thrift.Type.I64, 1);
    output.writeI64(this.totalImpressionRequired);
    output.writeFieldEnd();
  }
  if (this.triggerPercentage !== null && this.triggerPercentage !== undefined) {
    output.writeFieldBegin('triggerPercentage', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.triggerPercentage);
    output.writeFieldEnd();
  }
  if (this.servingCappingEnabled !== null && this.servingCappingEnabled !== undefined) {
    output.writeFieldBegin('servingCappingEnabled', Thrift.Type.BOOL, 3);
    output.writeBool(this.servingCappingEnabled);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ImpressionCapping = function(args) {
  this.totalImpression = null;
  this.trigger = null;
  if (args) {
    if (args.totalImpression !== undefined && args.totalImpression !== null) {
      this.totalImpression = args.totalImpression;
    }
    if (args.trigger !== undefined && args.trigger !== null) {
      this.trigger = args.trigger;
    }
  }
};
ImpressionCapping.prototype = {};
ImpressionCapping.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.totalImpression = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.trigger = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ImpressionCapping.prototype.write = function(output) {
  output.writeStructBegin('ImpressionCapping');
  if (this.totalImpression !== null && this.totalImpression !== undefined) {
    output.writeFieldBegin('totalImpression', Thrift.Type.I64, 1);
    output.writeI64(this.totalImpression);
    output.writeFieldEnd();
  }
  if (this.trigger !== null && this.trigger !== undefined) {
    output.writeFieldBegin('trigger', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.trigger);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraft = function(args) {
  this.editMeta = null;
  this.draftId = null;
  this.imageId = null;
  this.image = null;
  this.type = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.peek = null;
  this.priority = 0;
  this.preferedRegions = null;
  this.tags = null;
  this.curationReason = null;
  this.rejectionReason = null;
  this.publisherData = null;
  this.categoryIds = null;
  this.trelloCardInfo = null;
  this.version = null;
  this.storyType = null;
  this.frequencyCaps = null;
  this.renderSessionMode = null;
  this.beacons = null;
  this.stickinessCount = null;
  this.cityIds = null;
  this.segmentIds = null;
  this.userAgeType = null;
  this.categorySubscriptionStatus = null;
  this.targetForCategory = null;
  this.isShareable = null;
  this.shareUrl = null;
  this.overlayImage = null;
  this.bingeImage = null;
  this.bingeImageId = null;
  this.overlayImageId = null;
  this.targetForCategories = null;
  this.glanceLanguageId = null;
  this.isShoppable = false;
  this.shoppableData = null;
  this.antiSegmentIds = null;
  this.userLifeTimeFilter = null;
  this.targeting = null;
  this.topicName = null;
  this.homescreenWorthy = true;
  this.bubbleId = null;
  this.glanceBubbleDetails = null;
  this.cardPressId = null;
  this.carriers = null;
  this.glanceFeatures = null;
  this.eId = null;
  this.renderSessionProperties = null;
  this.userNetworkTypes = null;
  this.campaignMeta = null;
  this.allowKeyboard = null;
  this.impressionCappingData = null;
  this.beaconsInfo = null;
  this.omSdkInfo = null;
  this.sourceCreatorId = null;
  this.source = null;
  this.rankInStory = null;
  this.dynamicOverlay = null;
  this.ctaViewConfig = null;
  this.paused = null;
  this.ruleIds = null;
  this.rankInBubble = null;
  this.adCoefficient = null;
  this.bucketPriority = null;
  this.sequence = null;
  this.subCategories = null;
  this.suggestedSubCategory = null;
  this.otherTopics = null;
  this.l3Tags = null;
  this.allowNativeKeyboard = true;
  this.programmaticDetail = null;
  this.programmatic = null;
  this.ociConfigs = null;
  this.isBeautifulWallpaperEnabled = null;
  this.assetFileSizeInBytes = null;
  this.spaceData = null;
  this.publishingDestination = null;
  this.destinationIdentifier = null;
  this.event = null;
  this.liveEventData = null;
  this.sportEuSpaceData = null;
  this.spaces = null;
  this.eu = null;
  this.fastCardData = null;
  this.glamCampaignData = null;
  this.valueProp = null;
  this.eus = null;
  this.valueProps = null;
  this.gamPgAdUnitId = null;
  this.useUnlockNudge = null;
  this.dspCampaignData = null;
  if (args) {
    if (args.editMeta !== undefined && args.editMeta !== null) {
      this.editMeta = new DraftEditMeta(args.editMeta);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field editMeta is unset!');
    }
    if (args.draftId !== undefined && args.draftId !== null) {
      this.draftId = args.draftId;
    }
    if (args.imageId !== undefined && args.imageId !== null) {
      this.imageId = args.imageId;
    }
    if (args.image !== undefined && args.image !== null) {
      this.image = new Image(args.image);
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.peek !== undefined && args.peek !== null) {
      this.peek = new PeekDraft(args.peek);
    }
    if (args.priority !== undefined && args.priority !== null) {
      this.priority = args.priority;
    }
    if (args.preferedRegions !== undefined && args.preferedRegions !== null) {
      this.preferedRegions = Thrift.copyList(args.preferedRegions, [null]);
    }
    if (args.tags !== undefined && args.tags !== null) {
      this.tags = Thrift.copyList(args.tags, [null]);
    }
    if (args.curationReason !== undefined && args.curationReason !== null) {
      this.curationReason = args.curationReason;
    }
    if (args.rejectionReason !== undefined && args.rejectionReason !== null) {
      this.rejectionReason = args.rejectionReason;
    }
    if (args.publisherData !== undefined && args.publisherData !== null) {
      this.publisherData = new PublisherData(args.publisherData);
    }
    if (args.categoryIds !== undefined && args.categoryIds !== null) {
      this.categoryIds = Thrift.copyList(args.categoryIds, [null]);
    }
    if (args.trelloCardInfo !== undefined && args.trelloCardInfo !== null) {
      this.trelloCardInfo = new TrelloCardInfo(args.trelloCardInfo);
    }
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
    if (args.storyType !== undefined && args.storyType !== null) {
      this.storyType = args.storyType;
    }
    if (args.frequencyCaps !== undefined && args.frequencyCaps !== null) {
      this.frequencyCaps = new FrequencyCaps(args.frequencyCaps);
    }
    if (args.renderSessionMode !== undefined && args.renderSessionMode !== null) {
      this.renderSessionMode = args.renderSessionMode;
    }
    if (args.beacons !== undefined && args.beacons !== null) {
      this.beacons = new Beacons(args.beacons);
    }
    if (args.stickinessCount !== undefined && args.stickinessCount !== null) {
      this.stickinessCount = args.stickinessCount;
    }
    if (args.cityIds !== undefined && args.cityIds !== null) {
      this.cityIds = Thrift.copyList(args.cityIds, [null]);
    }
    if (args.segmentIds !== undefined && args.segmentIds !== null) {
      this.segmentIds = Thrift.copyList(args.segmentIds, [null]);
    }
    if (args.userAgeType !== undefined && args.userAgeType !== null) {
      this.userAgeType = args.userAgeType;
    }
    if (args.categorySubscriptionStatus !== undefined && args.categorySubscriptionStatus !== null) {
      this.categorySubscriptionStatus = args.categorySubscriptionStatus;
    }
    if (args.targetForCategory !== undefined && args.targetForCategory !== null) {
      this.targetForCategory = args.targetForCategory;
    }
    if (args.isShareable !== undefined && args.isShareable !== null) {
      this.isShareable = args.isShareable;
    }
    if (args.shareUrl !== undefined && args.shareUrl !== null) {
      this.shareUrl = args.shareUrl;
    }
    if (args.overlayImage !== undefined && args.overlayImage !== null) {
      this.overlayImage = new Image(args.overlayImage);
    }
    if (args.bingeImage !== undefined && args.bingeImage !== null) {
      this.bingeImage = new Image(args.bingeImage);
    }
    if (args.bingeImageId !== undefined && args.bingeImageId !== null) {
      this.bingeImageId = args.bingeImageId;
    }
    if (args.overlayImageId !== undefined && args.overlayImageId !== null) {
      this.overlayImageId = args.overlayImageId;
    }
    if (args.targetForCategories !== undefined && args.targetForCategories !== null) {
      this.targetForCategories = Thrift.copyList(args.targetForCategories, [null]);
    }
    if (args.glanceLanguageId !== undefined && args.glanceLanguageId !== null) {
      this.glanceLanguageId = args.glanceLanguageId;
    }
    if (args.isShoppable !== undefined && args.isShoppable !== null) {
      this.isShoppable = args.isShoppable;
    }
    if (args.shoppableData !== undefined && args.shoppableData !== null) {
      this.shoppableData = new ShoppableData(args.shoppableData);
    }
    if (args.antiSegmentIds !== undefined && args.antiSegmentIds !== null) {
      this.antiSegmentIds = Thrift.copyList(args.antiSegmentIds, [null]);
    }
    if (args.userLifeTimeFilter !== undefined && args.userLifeTimeFilter !== null) {
      this.userLifeTimeFilter = new UserLifeTimeFilter(args.userLifeTimeFilter);
    }
    if (args.targeting !== undefined && args.targeting !== null) {
      this.targeting = new Targeting(args.targeting);
    }
    if (args.topicName !== undefined && args.topicName !== null) {
      this.topicName = args.topicName;
    }
    if (args.homescreenWorthy !== undefined && args.homescreenWorthy !== null) {
      this.homescreenWorthy = args.homescreenWorthy;
    }
    if (args.bubbleId !== undefined && args.bubbleId !== null) {
      this.bubbleId = args.bubbleId;
    }
    if (args.glanceBubbleDetails !== undefined && args.glanceBubbleDetails !== null) {
      this.glanceBubbleDetails = new GlanceBubbleDetails(args.glanceBubbleDetails);
    }
    if (args.cardPressId !== undefined && args.cardPressId !== null) {
      this.cardPressId = args.cardPressId;
    }
    if (args.carriers !== undefined && args.carriers !== null) {
      this.carriers = Thrift.copyList(args.carriers, [null]);
    }
    if (args.glanceFeatures !== undefined && args.glanceFeatures !== null) {
      this.glanceFeatures = Thrift.copyList(args.glanceFeatures, [null]);
    }
    if (args.eId !== undefined && args.eId !== null) {
      this.eId = args.eId;
    }
    if (args.renderSessionProperties !== undefined && args.renderSessionProperties !== null) {
      this.renderSessionProperties = Thrift.copyList(args.renderSessionProperties, [null]);
    }
    if (args.userNetworkTypes !== undefined && args.userNetworkTypes !== null) {
      this.userNetworkTypes = Thrift.copyList(args.userNetworkTypes, [null]);
    }
    if (args.campaignMeta !== undefined && args.campaignMeta !== null) {
      this.campaignMeta = new GlanceCampaignMeta(args.campaignMeta);
    }
    if (args.allowKeyboard !== undefined && args.allowKeyboard !== null) {
      this.allowKeyboard = args.allowKeyboard;
    }
    if (args.impressionCappingData !== undefined && args.impressionCappingData !== null) {
      this.impressionCappingData = new ImpressionCappingData(args.impressionCappingData);
    }
    if (args.beaconsInfo !== undefined && args.beaconsInfo !== null) {
      this.beaconsInfo = new BeaconsInfo(args.beaconsInfo);
    }
    if (args.omSdkInfo !== undefined && args.omSdkInfo !== null) {
      this.omSdkInfo = new OmSdkInfo(args.omSdkInfo);
    }
    if (args.sourceCreatorId !== undefined && args.sourceCreatorId !== null) {
      this.sourceCreatorId = args.sourceCreatorId;
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    }
    if (args.rankInStory !== undefined && args.rankInStory !== null) {
      this.rankInStory = args.rankInStory;
    }
    if (args.dynamicOverlay !== undefined && args.dynamicOverlay !== null) {
      this.dynamicOverlay = args.dynamicOverlay;
    }
    if (args.ctaViewConfig !== undefined && args.ctaViewConfig !== null) {
      this.ctaViewConfig = new CtaViewConfig(args.ctaViewConfig);
    }
    if (args.paused !== undefined && args.paused !== null) {
      this.paused = args.paused;
    }
    if (args.ruleIds !== undefined && args.ruleIds !== null) {
      this.ruleIds = Thrift.copyList(args.ruleIds, [null]);
    }
    if (args.rankInBubble !== undefined && args.rankInBubble !== null) {
      this.rankInBubble = args.rankInBubble;
    }
    if (args.adCoefficient !== undefined && args.adCoefficient !== null) {
      this.adCoefficient = args.adCoefficient;
    }
    if (args.bucketPriority !== undefined && args.bucketPriority !== null) {
      this.bucketPriority = args.bucketPriority;
    }
    if (args.sequence !== undefined && args.sequence !== null) {
      this.sequence = args.sequence;
    }
    if (args.subCategories !== undefined && args.subCategories !== null) {
      this.subCategories = Thrift.copyList(args.subCategories, [null]);
    }
    if (args.suggestedSubCategory !== undefined && args.suggestedSubCategory !== null) {
      this.suggestedSubCategory = args.suggestedSubCategory;
    }
    if (args.otherTopics !== undefined && args.otherTopics !== null) {
      this.otherTopics = Thrift.copyList(args.otherTopics, [null]);
    }
    if (args.l3Tags !== undefined && args.l3Tags !== null) {
      this.l3Tags = Thrift.copyList(args.l3Tags, [null]);
    }
    if (args.allowNativeKeyboard !== undefined && args.allowNativeKeyboard !== null) {
      this.allowNativeKeyboard = args.allowNativeKeyboard;
    }
    if (args.programmaticDetail !== undefined && args.programmaticDetail !== null) {
      this.programmaticDetail = new Programmatic(args.programmaticDetail);
    }
    if (args.programmatic !== undefined && args.programmatic !== null) {
      this.programmatic = args.programmatic;
    }
    if (args.ociConfigs !== undefined && args.ociConfigs !== null) {
      this.ociConfigs = new OciConfigs(args.ociConfigs);
    }
    if (args.isBeautifulWallpaperEnabled !== undefined && args.isBeautifulWallpaperEnabled !== null) {
      this.isBeautifulWallpaperEnabled = args.isBeautifulWallpaperEnabled;
    }
    if (args.assetFileSizeInBytes !== undefined && args.assetFileSizeInBytes !== null) {
      this.assetFileSizeInBytes = args.assetFileSizeInBytes;
    }
    if (args.spaceData !== undefined && args.spaceData !== null) {
      this.spaceData = new SpaceData(args.spaceData);
    }
    if (args.publishingDestination !== undefined && args.publishingDestination !== null) {
      this.publishingDestination = args.publishingDestination;
    }
    if (args.destinationIdentifier !== undefined && args.destinationIdentifier !== null) {
      this.destinationIdentifier = args.destinationIdentifier;
    }
    if (args.event !== undefined && args.event !== null) {
      this.event = args.event;
    }
    if (args.liveEventData !== undefined && args.liveEventData !== null) {
      this.liveEventData = new LiveEventData(args.liveEventData);
    }
    if (args.sportEuSpaceData !== undefined && args.sportEuSpaceData !== null) {
      this.sportEuSpaceData = new SportEuSpaceData(args.sportEuSpaceData);
    }
    if (args.spaces !== undefined && args.spaces !== null) {
      this.spaces = Thrift.copyList(args.spaces, [null]);
    }
    if (args.eu !== undefined && args.eu !== null) {
      this.eu = args.eu;
    }
    if (args.fastCardData !== undefined && args.fastCardData !== null) {
      this.fastCardData = new FastCardData(args.fastCardData);
    }
    if (args.glamCampaignData !== undefined && args.glamCampaignData !== null) {
      this.glamCampaignData = new GlamCampaignData(args.glamCampaignData);
    }
    if (args.valueProp !== undefined && args.valueProp !== null) {
      this.valueProp = args.valueProp;
    }
    if (args.eus !== undefined && args.eus !== null) {
      this.eus = Thrift.copyList(args.eus, [EuInfoThrift]);
    }
    if (args.valueProps !== undefined && args.valueProps !== null) {
      this.valueProps = Thrift.copyList(args.valueProps, [ValuePropInfoThrift]);
    }
    if (args.gamPgAdUnitId !== undefined && args.gamPgAdUnitId !== null) {
      this.gamPgAdUnitId = args.gamPgAdUnitId;
    }
    if (args.useUnlockNudge !== undefined && args.useUnlockNudge !== null) {
      this.useUnlockNudge = args.useUnlockNudge;
    }
    if (args.dspCampaignData !== undefined && args.dspCampaignData !== null) {
      this.dspCampaignData = new DspCampaignData(args.dspCampaignData);
    }
  }
};
GlanceDraft.prototype = {};
GlanceDraft.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.editMeta = new DraftEditMeta();
        this.editMeta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.draftId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.imageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.image = new Image();
        this.image.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRUCT) {
        this.peek = new PeekDraft();
        this.peek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I32) {
        this.priority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.SET) {
        var _size730 = 0;
        var _rtmp3734;
        this.preferedRegions = [];
        var _etype733 = 0;
        _rtmp3734 = input.readSetBegin();
        _etype733 = _rtmp3734.etype;
        _size730 = _rtmp3734.size;
        for (var _i735 = 0; _i735 < _size730; ++_i735)
        {
          var elem736 = null;
          elem736 = input.readI32().value;
          this.preferedRegions.push(elem736);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.SET) {
        var _size737 = 0;
        var _rtmp3741;
        this.tags = [];
        var _etype740 = 0;
        _rtmp3741 = input.readSetBegin();
        _etype740 = _rtmp3741.etype;
        _size737 = _rtmp3741.size;
        for (var _i742 = 0; _i742 < _size737; ++_i742)
        {
          var elem743 = null;
          elem743 = input.readString().value;
          this.tags.push(elem743);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.curationReason = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.rejectionReason = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRUCT) {
        this.publisherData = new PublisherData();
        this.publisherData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.SET) {
        var _size744 = 0;
        var _rtmp3748;
        this.categoryIds = [];
        var _etype747 = 0;
        _rtmp3748 = input.readSetBegin();
        _etype747 = _rtmp3748.etype;
        _size744 = _rtmp3748.size;
        for (var _i749 = 0; _i749 < _size744; ++_i749)
        {
          var elem750 = null;
          elem750 = input.readString().value;
          this.categoryIds.push(elem750);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRUCT) {
        this.trelloCardInfo = new TrelloCardInfo();
        this.trelloCardInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.I64) {
        this.version = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.I32) {
        this.storyType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRUCT) {
        this.frequencyCaps = new FrequencyCaps();
        this.frequencyCaps.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.I32) {
        this.renderSessionMode = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRUCT) {
        this.beacons = new Beacons();
        this.beacons.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.I32) {
        this.stickinessCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.SET) {
        var _size751 = 0;
        var _rtmp3755;
        this.cityIds = [];
        var _etype754 = 0;
        _rtmp3755 = input.readSetBegin();
        _etype754 = _rtmp3755.etype;
        _size751 = _rtmp3755.size;
        for (var _i756 = 0; _i756 < _size751; ++_i756)
        {
          var elem757 = null;
          elem757 = input.readI32().value;
          this.cityIds.push(elem757);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.SET) {
        var _size758 = 0;
        var _rtmp3762;
        this.segmentIds = [];
        var _etype761 = 0;
        _rtmp3762 = input.readSetBegin();
        _etype761 = _rtmp3762.etype;
        _size758 = _rtmp3762.size;
        for (var _i763 = 0; _i763 < _size758; ++_i763)
        {
          var elem764 = null;
          elem764 = input.readI32().value;
          this.segmentIds.push(elem764);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.I32) {
        this.userAgeType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.I32) {
        this.categorySubscriptionStatus = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.STRING) {
        this.targetForCategory = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.BOOL) {
        this.isShareable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.STRING) {
        this.shareUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.STRUCT) {
        this.overlayImage = new Image();
        this.overlayImage.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.STRUCT) {
        this.bingeImage = new Image();
        this.bingeImage.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRING) {
        this.bingeImageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.STRING) {
        this.overlayImageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.SET) {
        var _size765 = 0;
        var _rtmp3769;
        this.targetForCategories = [];
        var _etype768 = 0;
        _rtmp3769 = input.readSetBegin();
        _etype768 = _rtmp3769.etype;
        _size765 = _rtmp3769.size;
        for (var _i770 = 0; _i770 < _size765; ++_i770)
        {
          var elem771 = null;
          elem771 = input.readString().value;
          this.targetForCategories.push(elem771);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.STRING) {
        this.glanceLanguageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 36:
      if (ftype == Thrift.Type.BOOL) {
        this.isShoppable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 37:
      if (ftype == Thrift.Type.STRUCT) {
        this.shoppableData = new ShoppableData();
        this.shoppableData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 38:
      if (ftype == Thrift.Type.SET) {
        var _size772 = 0;
        var _rtmp3776;
        this.antiSegmentIds = [];
        var _etype775 = 0;
        _rtmp3776 = input.readSetBegin();
        _etype775 = _rtmp3776.etype;
        _size772 = _rtmp3776.size;
        for (var _i777 = 0; _i777 < _size772; ++_i777)
        {
          var elem778 = null;
          elem778 = input.readI32().value;
          this.antiSegmentIds.push(elem778);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 39:
      if (ftype == Thrift.Type.STRUCT) {
        this.userLifeTimeFilter = new UserLifeTimeFilter();
        this.userLifeTimeFilter.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 40:
      if (ftype == Thrift.Type.STRUCT) {
        this.targeting = new Targeting();
        this.targeting.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 41:
      if (ftype == Thrift.Type.STRING) {
        this.topicName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 42:
      if (ftype == Thrift.Type.BOOL) {
        this.homescreenWorthy = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 43:
      if (ftype == Thrift.Type.STRING) {
        this.bubbleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 44:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceBubbleDetails = new GlanceBubbleDetails();
        this.glanceBubbleDetails.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 45:
      if (ftype == Thrift.Type.STRING) {
        this.cardPressId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 46:
      if (ftype == Thrift.Type.SET) {
        var _size779 = 0;
        var _rtmp3783;
        this.carriers = [];
        var _etype782 = 0;
        _rtmp3783 = input.readSetBegin();
        _etype782 = _rtmp3783.etype;
        _size779 = _rtmp3783.size;
        for (var _i784 = 0; _i784 < _size779; ++_i784)
        {
          var elem785 = null;
          elem785 = input.readI32().value;
          this.carriers.push(elem785);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 47:
      if (ftype == Thrift.Type.LIST) {
        var _size786 = 0;
        var _rtmp3790;
        this.glanceFeatures = [];
        var _etype789 = 0;
        _rtmp3790 = input.readListBegin();
        _etype789 = _rtmp3790.etype;
        _size786 = _rtmp3790.size;
        for (var _i791 = 0; _i791 < _size786; ++_i791)
        {
          var elem792 = null;
          elem792 = input.readI32().value;
          this.glanceFeatures.push(elem792);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 48:
      if (ftype == Thrift.Type.STRING) {
        this.eId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 49:
      if (ftype == Thrift.Type.SET) {
        var _size793 = 0;
        var _rtmp3797;
        this.renderSessionProperties = [];
        var _etype796 = 0;
        _rtmp3797 = input.readSetBegin();
        _etype796 = _rtmp3797.etype;
        _size793 = _rtmp3797.size;
        for (var _i798 = 0; _i798 < _size793; ++_i798)
        {
          var elem799 = null;
          elem799 = input.readI32().value;
          this.renderSessionProperties.push(elem799);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 50:
      if (ftype == Thrift.Type.SET) {
        var _size800 = 0;
        var _rtmp3804;
        this.userNetworkTypes = [];
        var _etype803 = 0;
        _rtmp3804 = input.readSetBegin();
        _etype803 = _rtmp3804.etype;
        _size800 = _rtmp3804.size;
        for (var _i805 = 0; _i805 < _size800; ++_i805)
        {
          var elem806 = null;
          elem806 = input.readI32().value;
          this.userNetworkTypes.push(elem806);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 51:
      if (ftype == Thrift.Type.STRUCT) {
        this.campaignMeta = new GlanceCampaignMeta();
        this.campaignMeta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 52:
      if (ftype == Thrift.Type.BOOL) {
        this.allowKeyboard = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 53:
      if (ftype == Thrift.Type.STRUCT) {
        this.impressionCappingData = new ImpressionCappingData();
        this.impressionCappingData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 54:
      if (ftype == Thrift.Type.STRUCT) {
        this.beaconsInfo = new BeaconsInfo();
        this.beaconsInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 55:
      if (ftype == Thrift.Type.STRUCT) {
        this.omSdkInfo = new OmSdkInfo();
        this.omSdkInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 56:
      if (ftype == Thrift.Type.STRING) {
        this.sourceCreatorId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 57:
      if (ftype == Thrift.Type.STRING) {
        this.source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 58:
      if (ftype == Thrift.Type.I32) {
        this.rankInStory = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 59:
      if (ftype == Thrift.Type.BOOL) {
        this.dynamicOverlay = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 60:
      if (ftype == Thrift.Type.STRUCT) {
        this.ctaViewConfig = new CtaViewConfig();
        this.ctaViewConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 61:
      if (ftype == Thrift.Type.BOOL) {
        this.paused = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 62:
      if (ftype == Thrift.Type.LIST) {
        var _size807 = 0;
        var _rtmp3811;
        this.ruleIds = [];
        var _etype810 = 0;
        _rtmp3811 = input.readListBegin();
        _etype810 = _rtmp3811.etype;
        _size807 = _rtmp3811.size;
        for (var _i812 = 0; _i812 < _size807; ++_i812)
        {
          var elem813 = null;
          elem813 = input.readString().value;
          this.ruleIds.push(elem813);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 63:
      if (ftype == Thrift.Type.I32) {
        this.rankInBubble = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 64:
      if (ftype == Thrift.Type.I32) {
        this.adCoefficient = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 65:
      if (ftype == Thrift.Type.I32) {
        this.bucketPriority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 66:
      if (ftype == Thrift.Type.I32) {
        this.sequence = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 67:
      if (ftype == Thrift.Type.LIST) {
        var _size814 = 0;
        var _rtmp3818;
        this.subCategories = [];
        var _etype817 = 0;
        _rtmp3818 = input.readListBegin();
        _etype817 = _rtmp3818.etype;
        _size814 = _rtmp3818.size;
        for (var _i819 = 0; _i819 < _size814; ++_i819)
        {
          var elem820 = null;
          elem820 = input.readString().value;
          this.subCategories.push(elem820);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 68:
      if (ftype == Thrift.Type.STRING) {
        this.suggestedSubCategory = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 69:
      if (ftype == Thrift.Type.LIST) {
        var _size821 = 0;
        var _rtmp3825;
        this.otherTopics = [];
        var _etype824 = 0;
        _rtmp3825 = input.readListBegin();
        _etype824 = _rtmp3825.etype;
        _size821 = _rtmp3825.size;
        for (var _i826 = 0; _i826 < _size821; ++_i826)
        {
          var elem827 = null;
          elem827 = input.readString().value;
          this.otherTopics.push(elem827);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 70:
      if (ftype == Thrift.Type.LIST) {
        var _size828 = 0;
        var _rtmp3832;
        this.l3Tags = [];
        var _etype831 = 0;
        _rtmp3832 = input.readListBegin();
        _etype831 = _rtmp3832.etype;
        _size828 = _rtmp3832.size;
        for (var _i833 = 0; _i833 < _size828; ++_i833)
        {
          var elem834 = null;
          elem834 = input.readString().value;
          this.l3Tags.push(elem834);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 71:
      if (ftype == Thrift.Type.BOOL) {
        this.allowNativeKeyboard = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 72:
      if (ftype == Thrift.Type.STRUCT) {
        this.programmaticDetail = new Programmatic();
        this.programmaticDetail.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 73:
      if (ftype == Thrift.Type.STRING) {
        this.programmatic = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 74:
      if (ftype == Thrift.Type.STRUCT) {
        this.ociConfigs = new OciConfigs();
        this.ociConfigs.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 75:
      if (ftype == Thrift.Type.BOOL) {
        this.isBeautifulWallpaperEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 76:
      if (ftype == Thrift.Type.I64) {
        this.assetFileSizeInBytes = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 77:
      if (ftype == Thrift.Type.STRUCT) {
        this.spaceData = new SpaceData();
        this.spaceData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 78:
      if (ftype == Thrift.Type.I32) {
        this.publishingDestination = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 79:
      if (ftype == Thrift.Type.I32) {
        this.destinationIdentifier = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 80:
      if (ftype == Thrift.Type.I32) {
        this.event = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 81:
      if (ftype == Thrift.Type.STRUCT) {
        this.liveEventData = new LiveEventData();
        this.liveEventData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 82:
      if (ftype == Thrift.Type.STRUCT) {
        this.sportEuSpaceData = new SportEuSpaceData();
        this.sportEuSpaceData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 83:
      if (ftype == Thrift.Type.LIST) {
        var _size835 = 0;
        var _rtmp3839;
        this.spaces = [];
        var _etype838 = 0;
        _rtmp3839 = input.readListBegin();
        _etype838 = _rtmp3839.etype;
        _size835 = _rtmp3839.size;
        for (var _i840 = 0; _i840 < _size835; ++_i840)
        {
          var elem841 = null;
          elem841 = input.readI32().value;
          this.spaces.push(elem841);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 84:
      if (ftype == Thrift.Type.I32) {
        this.eu = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 85:
      if (ftype == Thrift.Type.STRUCT) {
        this.fastCardData = new FastCardData();
        this.fastCardData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 86:
      if (ftype == Thrift.Type.STRUCT) {
        this.glamCampaignData = new GlamCampaignData();
        this.glamCampaignData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 87:
      if (ftype == Thrift.Type.STRING) {
        this.valueProp = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 88:
      if (ftype == Thrift.Type.LIST) {
        var _size842 = 0;
        var _rtmp3846;
        this.eus = [];
        var _etype845 = 0;
        _rtmp3846 = input.readListBegin();
        _etype845 = _rtmp3846.etype;
        _size842 = _rtmp3846.size;
        for (var _i847 = 0; _i847 < _size842; ++_i847)
        {
          var elem848 = null;
          elem848 = new EuInfoThrift();
          elem848.read(input);
          this.eus.push(elem848);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 89:
      if (ftype == Thrift.Type.LIST) {
        var _size849 = 0;
        var _rtmp3853;
        this.valueProps = [];
        var _etype852 = 0;
        _rtmp3853 = input.readListBegin();
        _etype852 = _rtmp3853.etype;
        _size849 = _rtmp3853.size;
        for (var _i854 = 0; _i854 < _size849; ++_i854)
        {
          var elem855 = null;
          elem855 = new ValuePropInfoThrift();
          elem855.read(input);
          this.valueProps.push(elem855);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 90:
      if (ftype == Thrift.Type.STRING) {
        this.gamPgAdUnitId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 91:
      if (ftype == Thrift.Type.BOOL) {
        this.useUnlockNudge = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 92:
      if (ftype == Thrift.Type.STRUCT) {
        this.dspCampaignData = new DspCampaignData();
        this.dspCampaignData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraft.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraft');
  if (this.editMeta !== null && this.editMeta !== undefined) {
    output.writeFieldBegin('editMeta', Thrift.Type.STRUCT, 1);
    this.editMeta.write(output);
    output.writeFieldEnd();
  }
  if (this.draftId !== null && this.draftId !== undefined) {
    output.writeFieldBegin('draftId', Thrift.Type.STRING, 2);
    output.writeString(this.draftId);
    output.writeFieldEnd();
  }
  if (this.imageId !== null && this.imageId !== undefined) {
    output.writeFieldBegin('imageId', Thrift.Type.STRING, 3);
    output.writeString(this.imageId);
    output.writeFieldEnd();
  }
  if (this.image !== null && this.image !== undefined) {
    output.writeFieldBegin('image', Thrift.Type.STRUCT, 4);
    this.image.write(output);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.I32, 5);
    output.writeI32(this.type);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 6);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 7);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.peek !== null && this.peek !== undefined) {
    output.writeFieldBegin('peek', Thrift.Type.STRUCT, 8);
    this.peek.write(output);
    output.writeFieldEnd();
  }
  if (this.priority !== null && this.priority !== undefined) {
    output.writeFieldBegin('priority', Thrift.Type.I32, 9);
    output.writeI32(this.priority);
    output.writeFieldEnd();
  }
  if (this.preferedRegions !== null && this.preferedRegions !== undefined) {
    output.writeFieldBegin('preferedRegions', Thrift.Type.SET, 10);
    output.writeSetBegin(Thrift.Type.I32, this.preferedRegions.length);
    for (var iter856 in this.preferedRegions)
    {
      if (this.preferedRegions.hasOwnProperty(iter856))
      {
        iter856 = this.preferedRegions[iter856];
        output.writeI32(iter856);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.tags !== null && this.tags !== undefined) {
    output.writeFieldBegin('tags', Thrift.Type.SET, 11);
    output.writeSetBegin(Thrift.Type.STRING, this.tags.length);
    for (var iter857 in this.tags)
    {
      if (this.tags.hasOwnProperty(iter857))
      {
        iter857 = this.tags[iter857];
        output.writeString(iter857);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.curationReason !== null && this.curationReason !== undefined) {
    output.writeFieldBegin('curationReason', Thrift.Type.STRING, 12);
    output.writeString(this.curationReason);
    output.writeFieldEnd();
  }
  if (this.rejectionReason !== null && this.rejectionReason !== undefined) {
    output.writeFieldBegin('rejectionReason', Thrift.Type.STRING, 13);
    output.writeString(this.rejectionReason);
    output.writeFieldEnd();
  }
  if (this.publisherData !== null && this.publisherData !== undefined) {
    output.writeFieldBegin('publisherData', Thrift.Type.STRUCT, 14);
    this.publisherData.write(output);
    output.writeFieldEnd();
  }
  if (this.categoryIds !== null && this.categoryIds !== undefined) {
    output.writeFieldBegin('categoryIds', Thrift.Type.SET, 15);
    output.writeSetBegin(Thrift.Type.STRING, this.categoryIds.length);
    for (var iter858 in this.categoryIds)
    {
      if (this.categoryIds.hasOwnProperty(iter858))
      {
        iter858 = this.categoryIds[iter858];
        output.writeString(iter858);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.trelloCardInfo !== null && this.trelloCardInfo !== undefined) {
    output.writeFieldBegin('trelloCardInfo', Thrift.Type.STRUCT, 16);
    this.trelloCardInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.I64, 17);
    output.writeI64(this.version);
    output.writeFieldEnd();
  }
  if (this.storyType !== null && this.storyType !== undefined) {
    output.writeFieldBegin('storyType', Thrift.Type.I32, 18);
    output.writeI32(this.storyType);
    output.writeFieldEnd();
  }
  if (this.frequencyCaps !== null && this.frequencyCaps !== undefined) {
    output.writeFieldBegin('frequencyCaps', Thrift.Type.STRUCT, 19);
    this.frequencyCaps.write(output);
    output.writeFieldEnd();
  }
  if (this.renderSessionMode !== null && this.renderSessionMode !== undefined) {
    output.writeFieldBegin('renderSessionMode', Thrift.Type.I32, 20);
    output.writeI32(this.renderSessionMode);
    output.writeFieldEnd();
  }
  if (this.beacons !== null && this.beacons !== undefined) {
    output.writeFieldBegin('beacons', Thrift.Type.STRUCT, 21);
    this.beacons.write(output);
    output.writeFieldEnd();
  }
  if (this.stickinessCount !== null && this.stickinessCount !== undefined) {
    output.writeFieldBegin('stickinessCount', Thrift.Type.I32, 22);
    output.writeI32(this.stickinessCount);
    output.writeFieldEnd();
  }
  if (this.cityIds !== null && this.cityIds !== undefined) {
    output.writeFieldBegin('cityIds', Thrift.Type.SET, 23);
    output.writeSetBegin(Thrift.Type.I32, this.cityIds.length);
    for (var iter859 in this.cityIds)
    {
      if (this.cityIds.hasOwnProperty(iter859))
      {
        iter859 = this.cityIds[iter859];
        output.writeI32(iter859);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.segmentIds !== null && this.segmentIds !== undefined) {
    output.writeFieldBegin('segmentIds', Thrift.Type.SET, 24);
    output.writeSetBegin(Thrift.Type.I32, this.segmentIds.length);
    for (var iter860 in this.segmentIds)
    {
      if (this.segmentIds.hasOwnProperty(iter860))
      {
        iter860 = this.segmentIds[iter860];
        output.writeI32(iter860);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.userAgeType !== null && this.userAgeType !== undefined) {
    output.writeFieldBegin('userAgeType', Thrift.Type.I32, 25);
    output.writeI32(this.userAgeType);
    output.writeFieldEnd();
  }
  if (this.categorySubscriptionStatus !== null && this.categorySubscriptionStatus !== undefined) {
    output.writeFieldBegin('categorySubscriptionStatus', Thrift.Type.I32, 26);
    output.writeI32(this.categorySubscriptionStatus);
    output.writeFieldEnd();
  }
  if (this.targetForCategory !== null && this.targetForCategory !== undefined) {
    output.writeFieldBegin('targetForCategory', Thrift.Type.STRING, 27);
    output.writeString(this.targetForCategory);
    output.writeFieldEnd();
  }
  if (this.isShareable !== null && this.isShareable !== undefined) {
    output.writeFieldBegin('isShareable', Thrift.Type.BOOL, 28);
    output.writeBool(this.isShareable);
    output.writeFieldEnd();
  }
  if (this.shareUrl !== null && this.shareUrl !== undefined) {
    output.writeFieldBegin('shareUrl', Thrift.Type.STRING, 29);
    output.writeString(this.shareUrl);
    output.writeFieldEnd();
  }
  if (this.overlayImage !== null && this.overlayImage !== undefined) {
    output.writeFieldBegin('overlayImage', Thrift.Type.STRUCT, 30);
    this.overlayImage.write(output);
    output.writeFieldEnd();
  }
  if (this.bingeImage !== null && this.bingeImage !== undefined) {
    output.writeFieldBegin('bingeImage', Thrift.Type.STRUCT, 31);
    this.bingeImage.write(output);
    output.writeFieldEnd();
  }
  if (this.bingeImageId !== null && this.bingeImageId !== undefined) {
    output.writeFieldBegin('bingeImageId', Thrift.Type.STRING, 32);
    output.writeString(this.bingeImageId);
    output.writeFieldEnd();
  }
  if (this.overlayImageId !== null && this.overlayImageId !== undefined) {
    output.writeFieldBegin('overlayImageId', Thrift.Type.STRING, 33);
    output.writeString(this.overlayImageId);
    output.writeFieldEnd();
  }
  if (this.targetForCategories !== null && this.targetForCategories !== undefined) {
    output.writeFieldBegin('targetForCategories', Thrift.Type.SET, 34);
    output.writeSetBegin(Thrift.Type.STRING, this.targetForCategories.length);
    for (var iter861 in this.targetForCategories)
    {
      if (this.targetForCategories.hasOwnProperty(iter861))
      {
        iter861 = this.targetForCategories[iter861];
        output.writeString(iter861);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.glanceLanguageId !== null && this.glanceLanguageId !== undefined) {
    output.writeFieldBegin('glanceLanguageId', Thrift.Type.STRING, 35);
    output.writeString(this.glanceLanguageId);
    output.writeFieldEnd();
  }
  if (this.isShoppable !== null && this.isShoppable !== undefined) {
    output.writeFieldBegin('isShoppable', Thrift.Type.BOOL, 36);
    output.writeBool(this.isShoppable);
    output.writeFieldEnd();
  }
  if (this.shoppableData !== null && this.shoppableData !== undefined) {
    output.writeFieldBegin('shoppableData', Thrift.Type.STRUCT, 37);
    this.shoppableData.write(output);
    output.writeFieldEnd();
  }
  if (this.antiSegmentIds !== null && this.antiSegmentIds !== undefined) {
    output.writeFieldBegin('antiSegmentIds', Thrift.Type.SET, 38);
    output.writeSetBegin(Thrift.Type.I32, this.antiSegmentIds.length);
    for (var iter862 in this.antiSegmentIds)
    {
      if (this.antiSegmentIds.hasOwnProperty(iter862))
      {
        iter862 = this.antiSegmentIds[iter862];
        output.writeI32(iter862);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.userLifeTimeFilter !== null && this.userLifeTimeFilter !== undefined) {
    output.writeFieldBegin('userLifeTimeFilter', Thrift.Type.STRUCT, 39);
    this.userLifeTimeFilter.write(output);
    output.writeFieldEnd();
  }
  if (this.targeting !== null && this.targeting !== undefined) {
    output.writeFieldBegin('targeting', Thrift.Type.STRUCT, 40);
    this.targeting.write(output);
    output.writeFieldEnd();
  }
  if (this.topicName !== null && this.topicName !== undefined) {
    output.writeFieldBegin('topicName', Thrift.Type.STRING, 41);
    output.writeString(this.topicName);
    output.writeFieldEnd();
  }
  if (this.homescreenWorthy !== null && this.homescreenWorthy !== undefined) {
    output.writeFieldBegin('homescreenWorthy', Thrift.Type.BOOL, 42);
    output.writeBool(this.homescreenWorthy);
    output.writeFieldEnd();
  }
  if (this.bubbleId !== null && this.bubbleId !== undefined) {
    output.writeFieldBegin('bubbleId', Thrift.Type.STRING, 43);
    output.writeString(this.bubbleId);
    output.writeFieldEnd();
  }
  if (this.glanceBubbleDetails !== null && this.glanceBubbleDetails !== undefined) {
    output.writeFieldBegin('glanceBubbleDetails', Thrift.Type.STRUCT, 44);
    this.glanceBubbleDetails.write(output);
    output.writeFieldEnd();
  }
  if (this.cardPressId !== null && this.cardPressId !== undefined) {
    output.writeFieldBegin('cardPressId', Thrift.Type.STRING, 45);
    output.writeString(this.cardPressId);
    output.writeFieldEnd();
  }
  if (this.carriers !== null && this.carriers !== undefined) {
    output.writeFieldBegin('carriers', Thrift.Type.SET, 46);
    output.writeSetBegin(Thrift.Type.I32, this.carriers.length);
    for (var iter863 in this.carriers)
    {
      if (this.carriers.hasOwnProperty(iter863))
      {
        iter863 = this.carriers[iter863];
        output.writeI32(iter863);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.glanceFeatures !== null && this.glanceFeatures !== undefined) {
    output.writeFieldBegin('glanceFeatures', Thrift.Type.LIST, 47);
    output.writeListBegin(Thrift.Type.I32, this.glanceFeatures.length);
    for (var iter864 in this.glanceFeatures)
    {
      if (this.glanceFeatures.hasOwnProperty(iter864))
      {
        iter864 = this.glanceFeatures[iter864];
        output.writeI32(iter864);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.eId !== null && this.eId !== undefined) {
    output.writeFieldBegin('eId', Thrift.Type.STRING, 48);
    output.writeString(this.eId);
    output.writeFieldEnd();
  }
  if (this.renderSessionProperties !== null && this.renderSessionProperties !== undefined) {
    output.writeFieldBegin('renderSessionProperties', Thrift.Type.SET, 49);
    output.writeSetBegin(Thrift.Type.I32, this.renderSessionProperties.length);
    for (var iter865 in this.renderSessionProperties)
    {
      if (this.renderSessionProperties.hasOwnProperty(iter865))
      {
        iter865 = this.renderSessionProperties[iter865];
        output.writeI32(iter865);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.userNetworkTypes !== null && this.userNetworkTypes !== undefined) {
    output.writeFieldBegin('userNetworkTypes', Thrift.Type.SET, 50);
    output.writeSetBegin(Thrift.Type.I32, this.userNetworkTypes.length);
    for (var iter866 in this.userNetworkTypes)
    {
      if (this.userNetworkTypes.hasOwnProperty(iter866))
      {
        iter866 = this.userNetworkTypes[iter866];
        output.writeI32(iter866);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.campaignMeta !== null && this.campaignMeta !== undefined) {
    output.writeFieldBegin('campaignMeta', Thrift.Type.STRUCT, 51);
    this.campaignMeta.write(output);
    output.writeFieldEnd();
  }
  if (this.allowKeyboard !== null && this.allowKeyboard !== undefined) {
    output.writeFieldBegin('allowKeyboard', Thrift.Type.BOOL, 52);
    output.writeBool(this.allowKeyboard);
    output.writeFieldEnd();
  }
  if (this.impressionCappingData !== null && this.impressionCappingData !== undefined) {
    output.writeFieldBegin('impressionCappingData', Thrift.Type.STRUCT, 53);
    this.impressionCappingData.write(output);
    output.writeFieldEnd();
  }
  if (this.beaconsInfo !== null && this.beaconsInfo !== undefined) {
    output.writeFieldBegin('beaconsInfo', Thrift.Type.STRUCT, 54);
    this.beaconsInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.omSdkInfo !== null && this.omSdkInfo !== undefined) {
    output.writeFieldBegin('omSdkInfo', Thrift.Type.STRUCT, 55);
    this.omSdkInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.sourceCreatorId !== null && this.sourceCreatorId !== undefined) {
    output.writeFieldBegin('sourceCreatorId', Thrift.Type.STRING, 56);
    output.writeString(this.sourceCreatorId);
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.STRING, 57);
    output.writeString(this.source);
    output.writeFieldEnd();
  }
  if (this.rankInStory !== null && this.rankInStory !== undefined) {
    output.writeFieldBegin('rankInStory', Thrift.Type.I32, 58);
    output.writeI32(this.rankInStory);
    output.writeFieldEnd();
  }
  if (this.dynamicOverlay !== null && this.dynamicOverlay !== undefined) {
    output.writeFieldBegin('dynamicOverlay', Thrift.Type.BOOL, 59);
    output.writeBool(this.dynamicOverlay);
    output.writeFieldEnd();
  }
  if (this.ctaViewConfig !== null && this.ctaViewConfig !== undefined) {
    output.writeFieldBegin('ctaViewConfig', Thrift.Type.STRUCT, 60);
    this.ctaViewConfig.write(output);
    output.writeFieldEnd();
  }
  if (this.paused !== null && this.paused !== undefined) {
    output.writeFieldBegin('paused', Thrift.Type.BOOL, 61);
    output.writeBool(this.paused);
    output.writeFieldEnd();
  }
  if (this.ruleIds !== null && this.ruleIds !== undefined) {
    output.writeFieldBegin('ruleIds', Thrift.Type.LIST, 62);
    output.writeListBegin(Thrift.Type.STRING, this.ruleIds.length);
    for (var iter867 in this.ruleIds)
    {
      if (this.ruleIds.hasOwnProperty(iter867))
      {
        iter867 = this.ruleIds[iter867];
        output.writeString(iter867);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.rankInBubble !== null && this.rankInBubble !== undefined) {
    output.writeFieldBegin('rankInBubble', Thrift.Type.I32, 63);
    output.writeI32(this.rankInBubble);
    output.writeFieldEnd();
  }
  if (this.adCoefficient !== null && this.adCoefficient !== undefined) {
    output.writeFieldBegin('adCoefficient', Thrift.Type.I32, 64);
    output.writeI32(this.adCoefficient);
    output.writeFieldEnd();
  }
  if (this.bucketPriority !== null && this.bucketPriority !== undefined) {
    output.writeFieldBegin('bucketPriority', Thrift.Type.I32, 65);
    output.writeI32(this.bucketPriority);
    output.writeFieldEnd();
  }
  if (this.sequence !== null && this.sequence !== undefined) {
    output.writeFieldBegin('sequence', Thrift.Type.I32, 66);
    output.writeI32(this.sequence);
    output.writeFieldEnd();
  }
  if (this.subCategories !== null && this.subCategories !== undefined) {
    output.writeFieldBegin('subCategories', Thrift.Type.LIST, 67);
    output.writeListBegin(Thrift.Type.STRING, this.subCategories.length);
    for (var iter868 in this.subCategories)
    {
      if (this.subCategories.hasOwnProperty(iter868))
      {
        iter868 = this.subCategories[iter868];
        output.writeString(iter868);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.suggestedSubCategory !== null && this.suggestedSubCategory !== undefined) {
    output.writeFieldBegin('suggestedSubCategory', Thrift.Type.STRING, 68);
    output.writeString(this.suggestedSubCategory);
    output.writeFieldEnd();
  }
  if (this.otherTopics !== null && this.otherTopics !== undefined) {
    output.writeFieldBegin('otherTopics', Thrift.Type.LIST, 69);
    output.writeListBegin(Thrift.Type.STRING, this.otherTopics.length);
    for (var iter869 in this.otherTopics)
    {
      if (this.otherTopics.hasOwnProperty(iter869))
      {
        iter869 = this.otherTopics[iter869];
        output.writeString(iter869);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.l3Tags !== null && this.l3Tags !== undefined) {
    output.writeFieldBegin('l3Tags', Thrift.Type.LIST, 70);
    output.writeListBegin(Thrift.Type.STRING, this.l3Tags.length);
    for (var iter870 in this.l3Tags)
    {
      if (this.l3Tags.hasOwnProperty(iter870))
      {
        iter870 = this.l3Tags[iter870];
        output.writeString(iter870);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.allowNativeKeyboard !== null && this.allowNativeKeyboard !== undefined) {
    output.writeFieldBegin('allowNativeKeyboard', Thrift.Type.BOOL, 71);
    output.writeBool(this.allowNativeKeyboard);
    output.writeFieldEnd();
  }
  if (this.programmaticDetail !== null && this.programmaticDetail !== undefined) {
    output.writeFieldBegin('programmaticDetail', Thrift.Type.STRUCT, 72);
    this.programmaticDetail.write(output);
    output.writeFieldEnd();
  }
  if (this.programmatic !== null && this.programmatic !== undefined) {
    output.writeFieldBegin('programmatic', Thrift.Type.STRING, 73);
    output.writeString(this.programmatic);
    output.writeFieldEnd();
  }
  if (this.ociConfigs !== null && this.ociConfigs !== undefined) {
    output.writeFieldBegin('ociConfigs', Thrift.Type.STRUCT, 74);
    this.ociConfigs.write(output);
    output.writeFieldEnd();
  }
  if (this.isBeautifulWallpaperEnabled !== null && this.isBeautifulWallpaperEnabled !== undefined) {
    output.writeFieldBegin('isBeautifulWallpaperEnabled', Thrift.Type.BOOL, 75);
    output.writeBool(this.isBeautifulWallpaperEnabled);
    output.writeFieldEnd();
  }
  if (this.assetFileSizeInBytes !== null && this.assetFileSizeInBytes !== undefined) {
    output.writeFieldBegin('assetFileSizeInBytes', Thrift.Type.I64, 76);
    output.writeI64(this.assetFileSizeInBytes);
    output.writeFieldEnd();
  }
  if (this.spaceData !== null && this.spaceData !== undefined) {
    output.writeFieldBegin('spaceData', Thrift.Type.STRUCT, 77);
    this.spaceData.write(output);
    output.writeFieldEnd();
  }
  if (this.publishingDestination !== null && this.publishingDestination !== undefined) {
    output.writeFieldBegin('publishingDestination', Thrift.Type.I32, 78);
    output.writeI32(this.publishingDestination);
    output.writeFieldEnd();
  }
  if (this.destinationIdentifier !== null && this.destinationIdentifier !== undefined) {
    output.writeFieldBegin('destinationIdentifier', Thrift.Type.I32, 79);
    output.writeI32(this.destinationIdentifier);
    output.writeFieldEnd();
  }
  if (this.event !== null && this.event !== undefined) {
    output.writeFieldBegin('event', Thrift.Type.I32, 80);
    output.writeI32(this.event);
    output.writeFieldEnd();
  }
  if (this.liveEventData !== null && this.liveEventData !== undefined) {
    output.writeFieldBegin('liveEventData', Thrift.Type.STRUCT, 81);
    this.liveEventData.write(output);
    output.writeFieldEnd();
  }
  if (this.sportEuSpaceData !== null && this.sportEuSpaceData !== undefined) {
    output.writeFieldBegin('sportEuSpaceData', Thrift.Type.STRUCT, 82);
    this.sportEuSpaceData.write(output);
    output.writeFieldEnd();
  }
  if (this.spaces !== null && this.spaces !== undefined) {
    output.writeFieldBegin('spaces', Thrift.Type.LIST, 83);
    output.writeListBegin(Thrift.Type.I32, this.spaces.length);
    for (var iter871 in this.spaces)
    {
      if (this.spaces.hasOwnProperty(iter871))
      {
        iter871 = this.spaces[iter871];
        output.writeI32(iter871);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.eu !== null && this.eu !== undefined) {
    output.writeFieldBegin('eu', Thrift.Type.I32, 84);
    output.writeI32(this.eu);
    output.writeFieldEnd();
  }
  if (this.fastCardData !== null && this.fastCardData !== undefined) {
    output.writeFieldBegin('fastCardData', Thrift.Type.STRUCT, 85);
    this.fastCardData.write(output);
    output.writeFieldEnd();
  }
  if (this.glamCampaignData !== null && this.glamCampaignData !== undefined) {
    output.writeFieldBegin('glamCampaignData', Thrift.Type.STRUCT, 86);
    this.glamCampaignData.write(output);
    output.writeFieldEnd();
  }
  if (this.valueProp !== null && this.valueProp !== undefined) {
    output.writeFieldBegin('valueProp', Thrift.Type.STRING, 87);
    output.writeString(this.valueProp);
    output.writeFieldEnd();
  }
  if (this.eus !== null && this.eus !== undefined) {
    output.writeFieldBegin('eus', Thrift.Type.LIST, 88);
    output.writeListBegin(Thrift.Type.STRUCT, this.eus.length);
    for (var iter872 in this.eus)
    {
      if (this.eus.hasOwnProperty(iter872))
      {
        iter872 = this.eus[iter872];
        iter872.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.valueProps !== null && this.valueProps !== undefined) {
    output.writeFieldBegin('valueProps', Thrift.Type.LIST, 89);
    output.writeListBegin(Thrift.Type.STRUCT, this.valueProps.length);
    for (var iter873 in this.valueProps)
    {
      if (this.valueProps.hasOwnProperty(iter873))
      {
        iter873 = this.valueProps[iter873];
        iter873.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.gamPgAdUnitId !== null && this.gamPgAdUnitId !== undefined) {
    output.writeFieldBegin('gamPgAdUnitId', Thrift.Type.STRING, 90);
    output.writeString(this.gamPgAdUnitId);
    output.writeFieldEnd();
  }
  if (this.useUnlockNudge !== null && this.useUnlockNudge !== undefined) {
    output.writeFieldBegin('useUnlockNudge', Thrift.Type.BOOL, 91);
    output.writeBool(this.useUnlockNudge);
    output.writeFieldEnd();
  }
  if (this.dspCampaignData !== null && this.dspCampaignData !== undefined) {
    output.writeFieldBegin('dspCampaignData', Thrift.Type.STRUCT, 92);
    this.dspCampaignData.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancePair = function(args) {
  this.meta = null;
  this.data = null;
  if (args) {
    if (args.meta !== undefined && args.meta !== null) {
      this.meta = new GlanceMeta(args.meta);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field meta is unset!');
    }
    if (args.data !== undefined && args.data !== null) {
      this.data = new GlanceData(args.data);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field data is unset!');
    }
  }
};
GlancePair.prototype = {};
GlancePair.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.meta = new GlanceMeta();
        this.meta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.data = new GlanceData();
        this.data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancePair.prototype.write = function(output) {
  output.writeStructBegin('GlancePair');
  if (this.meta !== null && this.meta !== undefined) {
    output.writeFieldBegin('meta', Thrift.Type.STRUCT, 1);
    this.meta.write(output);
    output.writeFieldEnd();
  }
  if (this.data !== null && this.data !== undefined) {
    output.writeFieldBegin('data', Thrift.Type.STRUCT, 2);
    this.data.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancePairs = function(args) {
  this.pairs = null;
  if (args) {
    if (args.pairs !== undefined && args.pairs !== null) {
      this.pairs = Thrift.copyList(args.pairs, [GlancePair]);
    }
  }
};
GlancePairs.prototype = {};
GlancePairs.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size874 = 0;
        var _rtmp3878;
        this.pairs = [];
        var _etype877 = 0;
        _rtmp3878 = input.readListBegin();
        _etype877 = _rtmp3878.etype;
        _size874 = _rtmp3878.size;
        for (var _i879 = 0; _i879 < _size874; ++_i879)
        {
          var elem880 = null;
          elem880 = new GlancePair();
          elem880.read(input);
          this.pairs.push(elem880);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancePairs.prototype.write = function(output) {
  output.writeStructBegin('GlancePairs');
  if (this.pairs !== null && this.pairs !== undefined) {
    output.writeFieldBegin('pairs', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.pairs.length);
    for (var iter881 in this.pairs)
    {
      if (this.pairs.hasOwnProperty(iter881))
      {
        iter881 = this.pairs[iter881];
        iter881.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RegionRequest = function(args) {
  this.partner = null;
  this.region = null;
  this.glanceTime = null;
  this.abTags = null;
  this.experiments = null;
  this.ruleIds = null;
  this.isPartedGlance = false;
  this.partingSchedule = null;
  this.ruleIdsList = null;
  this.appTargeting = null;
  if (args) {
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field region is unset!');
    }
    if (args.glanceTime !== undefined && args.glanceTime !== null) {
      this.glanceTime = new GlanceTime(args.glanceTime);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceTime is unset!');
    }
    if (args.abTags !== undefined && args.abTags !== null) {
      this.abTags = Thrift.copyList(args.abTags, [null]);
    }
    if (args.experiments !== undefined && args.experiments !== null) {
      this.experiments = Thrift.copyList(args.experiments, [ExperimentMeta]);
    }
    if (args.ruleIds !== undefined && args.ruleIds !== null) {
      this.ruleIds = Thrift.copyList(args.ruleIds, [null]);
    }
    if (args.isPartedGlance !== undefined && args.isPartedGlance !== null) {
      this.isPartedGlance = args.isPartedGlance;
    }
    if (args.partingSchedule !== undefined && args.partingSchedule !== null) {
      this.partingSchedule = Thrift.copyMap(args.partingSchedule, [Thrift.copyList, PartingTime]);
    }
    if (args.ruleIdsList !== undefined && args.ruleIdsList !== null) {
      this.ruleIdsList = Thrift.copyList(args.ruleIdsList, [RuleIds]);
    }
    if (args.appTargeting !== undefined && args.appTargeting !== null) {
      this.appTargeting = new AppTargeting(args.appTargeting);
    }
  }
};
RegionRequest.prototype = {};
RegionRequest.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceTime = new GlanceTime();
        this.glanceTime.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size882 = 0;
        var _rtmp3886;
        this.abTags = [];
        var _etype885 = 0;
        _rtmp3886 = input.readListBegin();
        _etype885 = _rtmp3886.etype;
        _size882 = _rtmp3886.size;
        for (var _i887 = 0; _i887 < _size882; ++_i887)
        {
          var elem888 = null;
          elem888 = input.readString().value;
          this.abTags.push(elem888);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size889 = 0;
        var _rtmp3893;
        this.experiments = [];
        var _etype892 = 0;
        _rtmp3893 = input.readListBegin();
        _etype892 = _rtmp3893.etype;
        _size889 = _rtmp3893.size;
        for (var _i894 = 0; _i894 < _size889; ++_i894)
        {
          var elem895 = null;
          elem895 = new ExperimentMeta();
          elem895.read(input);
          this.experiments.push(elem895);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size896 = 0;
        var _rtmp3900;
        this.ruleIds = [];
        var _etype899 = 0;
        _rtmp3900 = input.readListBegin();
        _etype899 = _rtmp3900.etype;
        _size896 = _rtmp3900.size;
        for (var _i901 = 0; _i901 < _size896; ++_i901)
        {
          var elem902 = null;
          elem902 = input.readString().value;
          this.ruleIds.push(elem902);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.isPartedGlance = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.MAP) {
        var _size903 = 0;
        var _rtmp3907;
        this.partingSchedule = {};
        var _ktype904 = 0;
        var _vtype905 = 0;
        _rtmp3907 = input.readMapBegin();
        _ktype904 = _rtmp3907.ktype;
        _vtype905 = _rtmp3907.vtype;
        _size903 = _rtmp3907.size;
        for (var _i908 = 0; _i908 < _size903; ++_i908)
        {
          if (_i908 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key909 = null;
          var val910 = null;
          key909 = input.readString().value;
          var _size911 = 0;
          var _rtmp3915;
          val910 = [];
          var _etype914 = 0;
          _rtmp3915 = input.readListBegin();
          _etype914 = _rtmp3915.etype;
          _size911 = _rtmp3915.size;
          for (var _i916 = 0; _i916 < _size911; ++_i916)
          {
            var elem917 = null;
            elem917 = new PartingTime();
            elem917.read(input);
            val910.push(elem917);
          }
          input.readListEnd();
          this.partingSchedule[key909] = val910;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size918 = 0;
        var _rtmp3922;
        this.ruleIdsList = [];
        var _etype921 = 0;
        _rtmp3922 = input.readListBegin();
        _etype921 = _rtmp3922.etype;
        _size918 = _rtmp3922.size;
        for (var _i923 = 0; _i923 < _size918; ++_i923)
        {
          var elem924 = null;
          elem924 = new RuleIds();
          elem924.read(input);
          this.ruleIdsList.push(elem924);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRUCT) {
        this.appTargeting = new AppTargeting();
        this.appTargeting.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RegionRequest.prototype.write = function(output) {
  output.writeStructBegin('RegionRequest');
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 1);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 2);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.glanceTime !== null && this.glanceTime !== undefined) {
    output.writeFieldBegin('glanceTime', Thrift.Type.STRUCT, 3);
    this.glanceTime.write(output);
    output.writeFieldEnd();
  }
  if (this.abTags !== null && this.abTags !== undefined) {
    output.writeFieldBegin('abTags', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRING, this.abTags.length);
    for (var iter925 in this.abTags)
    {
      if (this.abTags.hasOwnProperty(iter925))
      {
        iter925 = this.abTags[iter925];
        output.writeString(iter925);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.experiments !== null && this.experiments !== undefined) {
    output.writeFieldBegin('experiments', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.experiments.length);
    for (var iter926 in this.experiments)
    {
      if (this.experiments.hasOwnProperty(iter926))
      {
        iter926 = this.experiments[iter926];
        iter926.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.ruleIds !== null && this.ruleIds !== undefined) {
    output.writeFieldBegin('ruleIds', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.STRING, this.ruleIds.length);
    for (var iter927 in this.ruleIds)
    {
      if (this.ruleIds.hasOwnProperty(iter927))
      {
        iter927 = this.ruleIds[iter927];
        output.writeString(iter927);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.isPartedGlance !== null && this.isPartedGlance !== undefined) {
    output.writeFieldBegin('isPartedGlance', Thrift.Type.BOOL, 7);
    output.writeBool(this.isPartedGlance);
    output.writeFieldEnd();
  }
  if (this.partingSchedule !== null && this.partingSchedule !== undefined) {
    output.writeFieldBegin('partingSchedule', Thrift.Type.MAP, 8);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.LIST, Thrift.objectLength(this.partingSchedule));
    for (var kiter928 in this.partingSchedule)
    {
      if (this.partingSchedule.hasOwnProperty(kiter928))
      {
        var viter929 = this.partingSchedule[kiter928];
        output.writeString(kiter928);
        output.writeListBegin(Thrift.Type.STRUCT, viter929.length);
        for (var iter930 in viter929)
        {
          if (viter929.hasOwnProperty(iter930))
          {
            iter930 = viter929[iter930];
            iter930.write(output);
          }
        }
        output.writeListEnd();
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.ruleIdsList !== null && this.ruleIdsList !== undefined) {
    output.writeFieldBegin('ruleIdsList', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRUCT, this.ruleIdsList.length);
    for (var iter931 in this.ruleIdsList)
    {
      if (this.ruleIdsList.hasOwnProperty(iter931))
      {
        iter931 = this.ruleIdsList[iter931];
        iter931.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.appTargeting !== null && this.appTargeting !== undefined) {
    output.writeFieldBegin('appTargeting', Thrift.Type.STRUCT, 10);
    this.appTargeting.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PublishRequest = function(args) {
  this.draftId = null;
  this.regions = null;
  this.publishAsArticle = null;
  if (args) {
    if (args.draftId !== undefined && args.draftId !== null) {
      this.draftId = args.draftId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field draftId is unset!');
    }
    if (args.regions !== undefined && args.regions !== null) {
      this.regions = Thrift.copyList(args.regions, [RegionRequest]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field regions is unset!');
    }
    if (args.publishAsArticle !== undefined && args.publishAsArticle !== null) {
      this.publishAsArticle = args.publishAsArticle;
    }
  }
};
PublishRequest.prototype = {};
PublishRequest.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.draftId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size932 = 0;
        var _rtmp3936;
        this.regions = [];
        var _etype935 = 0;
        _rtmp3936 = input.readListBegin();
        _etype935 = _rtmp3936.etype;
        _size932 = _rtmp3936.size;
        for (var _i937 = 0; _i937 < _size932; ++_i937)
        {
          var elem938 = null;
          elem938 = new RegionRequest();
          elem938.read(input);
          this.regions.push(elem938);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.publishAsArticle = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PublishRequest.prototype.write = function(output) {
  output.writeStructBegin('PublishRequest');
  if (this.draftId !== null && this.draftId !== undefined) {
    output.writeFieldBegin('draftId', Thrift.Type.STRING, 1);
    output.writeString(this.draftId);
    output.writeFieldEnd();
  }
  if (this.regions !== null && this.regions !== undefined) {
    output.writeFieldBegin('regions', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.regions.length);
    for (var iter939 in this.regions)
    {
      if (this.regions.hasOwnProperty(iter939))
      {
        iter939 = this.regions[iter939];
        iter939.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.publishAsArticle !== null && this.publishAsArticle !== undefined) {
    output.writeFieldBegin('publishAsArticle', Thrift.Type.BOOL, 3);
    output.writeBool(this.publishAsArticle);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SegmentTargetingRule = function(args) {
  this.ruleId = null;
  this.name = null;
  this.description = null;
  this.status = null;
  this.startDate = null;
  this.endDate = null;
  this.segmentQueries = null;
  this.genders = null;
  this.handsetInfos = null;
  this.cities = null;
  this.states = null;
  if (args) {
    if (args.ruleId !== undefined && args.ruleId !== null) {
      this.ruleId = args.ruleId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.startDate !== undefined && args.startDate !== null) {
      this.startDate = args.startDate;
    }
    if (args.endDate !== undefined && args.endDate !== null) {
      this.endDate = args.endDate;
    }
    if (args.segmentQueries !== undefined && args.segmentQueries !== null) {
      this.segmentQueries = Thrift.copyList(args.segmentQueries, [null]);
    }
    if (args.genders !== undefined && args.genders !== null) {
      this.genders = Thrift.copyList(args.genders, [null]);
    }
    if (args.handsetInfos !== undefined && args.handsetInfos !== null) {
      this.handsetInfos = Thrift.copyList(args.handsetInfos, [null]);
    }
    if (args.cities !== undefined && args.cities !== null) {
      this.cities = Thrift.copyList(args.cities, [null]);
    }
    if (args.states !== undefined && args.states !== null) {
      this.states = Thrift.copyList(args.states, [null]);
    }
  }
};
SegmentTargetingRule.prototype = {};
SegmentTargetingRule.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.ruleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.startDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.endDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size940 = 0;
        var _rtmp3944;
        this.segmentQueries = [];
        var _etype943 = 0;
        _rtmp3944 = input.readListBegin();
        _etype943 = _rtmp3944.etype;
        _size940 = _rtmp3944.size;
        for (var _i945 = 0; _i945 < _size940; ++_i945)
        {
          var elem946 = null;
          elem946 = input.readString().value;
          this.segmentQueries.push(elem946);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size947 = 0;
        var _rtmp3951;
        this.genders = [];
        var _etype950 = 0;
        _rtmp3951 = input.readListBegin();
        _etype950 = _rtmp3951.etype;
        _size947 = _rtmp3951.size;
        for (var _i952 = 0; _i952 < _size947; ++_i952)
        {
          var elem953 = null;
          elem953 = input.readString().value;
          this.genders.push(elem953);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size954 = 0;
        var _rtmp3958;
        this.handsetInfos = [];
        var _etype957 = 0;
        _rtmp3958 = input.readListBegin();
        _etype957 = _rtmp3958.etype;
        _size954 = _rtmp3958.size;
        for (var _i959 = 0; _i959 < _size954; ++_i959)
        {
          var elem960 = null;
          elem960 = input.readString().value;
          this.handsetInfos.push(elem960);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.LIST) {
        var _size961 = 0;
        var _rtmp3965;
        this.cities = [];
        var _etype964 = 0;
        _rtmp3965 = input.readListBegin();
        _etype964 = _rtmp3965.etype;
        _size961 = _rtmp3965.size;
        for (var _i966 = 0; _i966 < _size961; ++_i966)
        {
          var elem967 = null;
          elem967 = input.readI64().value;
          this.cities.push(elem967);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size968 = 0;
        var _rtmp3972;
        this.states = [];
        var _etype971 = 0;
        _rtmp3972 = input.readListBegin();
        _etype971 = _rtmp3972.etype;
        _size968 = _rtmp3972.size;
        for (var _i973 = 0; _i973 < _size968; ++_i973)
        {
          var elem974 = null;
          elem974 = input.readI64().value;
          this.states.push(elem974);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SegmentTargetingRule.prototype.write = function(output) {
  output.writeStructBegin('SegmentTargetingRule');
  if (this.ruleId !== null && this.ruleId !== undefined) {
    output.writeFieldBegin('ruleId', Thrift.Type.STRING, 1);
    output.writeString(this.ruleId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 3);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 4);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  if (this.startDate !== null && this.startDate !== undefined) {
    output.writeFieldBegin('startDate', Thrift.Type.STRING, 5);
    output.writeString(this.startDate);
    output.writeFieldEnd();
  }
  if (this.endDate !== null && this.endDate !== undefined) {
    output.writeFieldBegin('endDate', Thrift.Type.STRING, 6);
    output.writeString(this.endDate);
    output.writeFieldEnd();
  }
  if (this.segmentQueries !== null && this.segmentQueries !== undefined) {
    output.writeFieldBegin('segmentQueries', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRING, this.segmentQueries.length);
    for (var iter975 in this.segmentQueries)
    {
      if (this.segmentQueries.hasOwnProperty(iter975))
      {
        iter975 = this.segmentQueries[iter975];
        output.writeString(iter975);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.genders !== null && this.genders !== undefined) {
    output.writeFieldBegin('genders', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRING, this.genders.length);
    for (var iter976 in this.genders)
    {
      if (this.genders.hasOwnProperty(iter976))
      {
        iter976 = this.genders[iter976];
        output.writeString(iter976);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.handsetInfos !== null && this.handsetInfos !== undefined) {
    output.writeFieldBegin('handsetInfos', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRING, this.handsetInfos.length);
    for (var iter977 in this.handsetInfos)
    {
      if (this.handsetInfos.hasOwnProperty(iter977))
      {
        iter977 = this.handsetInfos[iter977];
        output.writeString(iter977);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.cities !== null && this.cities !== undefined) {
    output.writeFieldBegin('cities', Thrift.Type.LIST, 10);
    output.writeListBegin(Thrift.Type.I64, this.cities.length);
    for (var iter978 in this.cities)
    {
      if (this.cities.hasOwnProperty(iter978))
      {
        iter978 = this.cities[iter978];
        output.writeI64(iter978);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.states !== null && this.states !== undefined) {
    output.writeFieldBegin('states', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.I64, this.states.length);
    for (var iter979 in this.states)
    {
      if (this.states.hasOwnProperty(iter979))
      {
        iter979 = this.states[iter979];
        output.writeI64(iter979);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

App = function(args) {
  this.appId = null;
  this.name = null;
  this.packageId = null;
  this.logoUrl = null;
  this.category = null;
  this.size = null;
  this.rating = null;
  this.downloadCount = null;
  this.title = null;
  this.description = null;
  this.screenshotUrls = null;
  this.ociTargetable = null;
  this.clickBeaconUrl = null;
  this.brandColor = null;
  this.brandBgImage = null;
  this.appOpenTargetingRuleId = null;
  this.renderBeaconUrl = null;
  this.advertiserApiKey = null;
  this.secondaryBrandColor = null;
  this.appOptionalDetails = null;
  this.preloadEnable = null;
  this.contentProviderUrl = null;
  this.prldAttr = null;
  this.templateId = null;
  this.tag = null;
  this.reviewCount = null;
  this.sizeInMb = null;
  this.categoryInfo = null;
  this.textColor = null;
  this.bgColor = null;
  this.carouselImages = null;
  this.installNowCta = null;
  this.installLaterCta = null;
  this.fbu = null;
  this.sourceName = null;
  this.dspAdvertiserGuid = null;
  if (args) {
    if (args.appId !== undefined && args.appId !== null) {
      this.appId = args.appId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.packageId !== undefined && args.packageId !== null) {
      this.packageId = args.packageId;
    }
    if (args.logoUrl !== undefined && args.logoUrl !== null) {
      this.logoUrl = args.logoUrl;
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.size !== undefined && args.size !== null) {
      this.size = args.size;
    }
    if (args.rating !== undefined && args.rating !== null) {
      this.rating = args.rating;
    }
    if (args.downloadCount !== undefined && args.downloadCount !== null) {
      this.downloadCount = args.downloadCount;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.screenshotUrls !== undefined && args.screenshotUrls !== null) {
      this.screenshotUrls = Thrift.copyList(args.screenshotUrls, [null]);
    }
    if (args.ociTargetable !== undefined && args.ociTargetable !== null) {
      this.ociTargetable = args.ociTargetable;
    }
    if (args.clickBeaconUrl !== undefined && args.clickBeaconUrl !== null) {
      this.clickBeaconUrl = args.clickBeaconUrl;
    }
    if (args.brandColor !== undefined && args.brandColor !== null) {
      this.brandColor = args.brandColor;
    }
    if (args.brandBgImage !== undefined && args.brandBgImage !== null) {
      this.brandBgImage = args.brandBgImage;
    }
    if (args.appOpenTargetingRuleId !== undefined && args.appOpenTargetingRuleId !== null) {
      this.appOpenTargetingRuleId = args.appOpenTargetingRuleId;
    }
    if (args.renderBeaconUrl !== undefined && args.renderBeaconUrl !== null) {
      this.renderBeaconUrl = args.renderBeaconUrl;
    }
    if (args.advertiserApiKey !== undefined && args.advertiserApiKey !== null) {
      this.advertiserApiKey = args.advertiserApiKey;
    }
    if (args.secondaryBrandColor !== undefined && args.secondaryBrandColor !== null) {
      this.secondaryBrandColor = args.secondaryBrandColor;
    }
    if (args.appOptionalDetails !== undefined && args.appOptionalDetails !== null) {
      this.appOptionalDetails = new AppOptionalDetails(args.appOptionalDetails);
    }
    if (args.preloadEnable !== undefined && args.preloadEnable !== null) {
      this.preloadEnable = args.preloadEnable;
    }
    if (args.contentProviderUrl !== undefined && args.contentProviderUrl !== null) {
      this.contentProviderUrl = args.contentProviderUrl;
    }
    if (args.prldAttr !== undefined && args.prldAttr !== null) {
      this.prldAttr = new PreloadAttirbution(args.prldAttr);
    }
    if (args.templateId !== undefined && args.templateId !== null) {
      this.templateId = args.templateId;
    }
    if (args.tag !== undefined && args.tag !== null) {
      this.tag = new Tag(args.tag);
    }
    if (args.reviewCount !== undefined && args.reviewCount !== null) {
      this.reviewCount = args.reviewCount;
    }
    if (args.sizeInMb !== undefined && args.sizeInMb !== null) {
      this.sizeInMb = args.sizeInMb;
    }
    if (args.categoryInfo !== undefined && args.categoryInfo !== null) {
      this.categoryInfo = new CategoryInfo(args.categoryInfo);
    }
    if (args.textColor !== undefined && args.textColor !== null) {
      this.textColor = args.textColor;
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = Thrift.copyList(args.bgColor, [null]);
    }
    if (args.carouselImages !== undefined && args.carouselImages !== null) {
      this.carouselImages = Thrift.copyList(args.carouselImages, [null]);
    }
    if (args.installNowCta !== undefined && args.installNowCta !== null) {
      this.installNowCta = new InstallCtaMeta(args.installNowCta);
    }
    if (args.installLaterCta !== undefined && args.installLaterCta !== null) {
      this.installLaterCta = new InstallCtaMeta(args.installLaterCta);
    }
    if (args.fbu !== undefined && args.fbu !== null) {
      this.fbu = args.fbu;
    }
    if (args.sourceName !== undefined && args.sourceName !== null) {
      this.sourceName = Thrift.copyList(args.sourceName, [null]);
    }
    if (args.dspAdvertiserGuid !== undefined && args.dspAdvertiserGuid !== null) {
      this.dspAdvertiserGuid = args.dspAdvertiserGuid;
    }
  }
};
App.prototype = {};
App.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.appId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.packageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.logoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.size = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.DOUBLE) {
        this.rating = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.downloadCount = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size980 = 0;
        var _rtmp3984;
        this.screenshotUrls = [];
        var _etype983 = 0;
        _rtmp3984 = input.readListBegin();
        _etype983 = _rtmp3984.etype;
        _size980 = _rtmp3984.size;
        for (var _i985 = 0; _i985 < _size980; ++_i985)
        {
          var elem986 = null;
          elem986 = input.readString().value;
          this.screenshotUrls.push(elem986);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.BOOL) {
        this.ociTargetable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.clickBeaconUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.brandColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.brandBgImage = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.appOpenTargetingRuleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.renderBeaconUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.advertiserApiKey = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.secondaryBrandColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRUCT) {
        this.appOptionalDetails = new AppOptionalDetails();
        this.appOptionalDetails.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.BOOL) {
        this.preloadEnable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRING) {
        this.contentProviderUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRUCT) {
        this.prldAttr = new PreloadAttirbution();
        this.prldAttr.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.STRING) {
        this.templateId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.STRUCT) {
        this.tag = new Tag();
        this.tag.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.STRING) {
        this.reviewCount = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.STRING) {
        this.sizeInMb = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.STRUCT) {
        this.categoryInfo = new CategoryInfo();
        this.categoryInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.STRING) {
        this.textColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.LIST) {
        var _size987 = 0;
        var _rtmp3991;
        this.bgColor = [];
        var _etype990 = 0;
        _rtmp3991 = input.readListBegin();
        _etype990 = _rtmp3991.etype;
        _size987 = _rtmp3991.size;
        for (var _i992 = 0; _i992 < _size987; ++_i992)
        {
          var elem993 = null;
          elem993 = input.readString().value;
          this.bgColor.push(elem993);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.LIST) {
        var _size994 = 0;
        var _rtmp3998;
        this.carouselImages = [];
        var _etype997 = 0;
        _rtmp3998 = input.readListBegin();
        _etype997 = _rtmp3998.etype;
        _size994 = _rtmp3998.size;
        for (var _i999 = 0; _i999 < _size994; ++_i999)
        {
          var elem1000 = null;
          elem1000 = input.readString().value;
          this.carouselImages.push(elem1000);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRUCT) {
        this.installNowCta = new InstallCtaMeta();
        this.installNowCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.STRUCT) {
        this.installLaterCta = new InstallCtaMeta();
        this.installLaterCta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.STRING) {
        this.fbu = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.LIST) {
        var _size1001 = 0;
        var _rtmp31005;
        this.sourceName = [];
        var _etype1004 = 0;
        _rtmp31005 = input.readListBegin();
        _etype1004 = _rtmp31005.etype;
        _size1001 = _rtmp31005.size;
        for (var _i1006 = 0; _i1006 < _size1001; ++_i1006)
        {
          var elem1007 = null;
          elem1007 = input.readString().value;
          this.sourceName.push(elem1007);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 36:
      if (ftype == Thrift.Type.STRING) {
        this.dspAdvertiserGuid = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

App.prototype.write = function(output) {
  output.writeStructBegin('App');
  if (this.appId !== null && this.appId !== undefined) {
    output.writeFieldBegin('appId', Thrift.Type.STRING, 1);
    output.writeString(this.appId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.packageId !== null && this.packageId !== undefined) {
    output.writeFieldBegin('packageId', Thrift.Type.STRING, 3);
    output.writeString(this.packageId);
    output.writeFieldEnd();
  }
  if (this.logoUrl !== null && this.logoUrl !== undefined) {
    output.writeFieldBegin('logoUrl', Thrift.Type.STRING, 4);
    output.writeString(this.logoUrl);
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 5);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.size !== null && this.size !== undefined) {
    output.writeFieldBegin('size', Thrift.Type.STRING, 6);
    output.writeString(this.size);
    output.writeFieldEnd();
  }
  if (this.rating !== null && this.rating !== undefined) {
    output.writeFieldBegin('rating', Thrift.Type.DOUBLE, 7);
    output.writeDouble(this.rating);
    output.writeFieldEnd();
  }
  if (this.downloadCount !== null && this.downloadCount !== undefined) {
    output.writeFieldBegin('downloadCount', Thrift.Type.STRING, 8);
    output.writeString(this.downloadCount);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 9);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 10);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.screenshotUrls !== null && this.screenshotUrls !== undefined) {
    output.writeFieldBegin('screenshotUrls', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRING, this.screenshotUrls.length);
    for (var iter1008 in this.screenshotUrls)
    {
      if (this.screenshotUrls.hasOwnProperty(iter1008))
      {
        iter1008 = this.screenshotUrls[iter1008];
        output.writeString(iter1008);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.ociTargetable !== null && this.ociTargetable !== undefined) {
    output.writeFieldBegin('ociTargetable', Thrift.Type.BOOL, 12);
    output.writeBool(this.ociTargetable);
    output.writeFieldEnd();
  }
  if (this.clickBeaconUrl !== null && this.clickBeaconUrl !== undefined) {
    output.writeFieldBegin('clickBeaconUrl', Thrift.Type.STRING, 13);
    output.writeString(this.clickBeaconUrl);
    output.writeFieldEnd();
  }
  if (this.brandColor !== null && this.brandColor !== undefined) {
    output.writeFieldBegin('brandColor', Thrift.Type.STRING, 14);
    output.writeString(this.brandColor);
    output.writeFieldEnd();
  }
  if (this.brandBgImage !== null && this.brandBgImage !== undefined) {
    output.writeFieldBegin('brandBgImage', Thrift.Type.STRING, 15);
    output.writeString(this.brandBgImage);
    output.writeFieldEnd();
  }
  if (this.appOpenTargetingRuleId !== null && this.appOpenTargetingRuleId !== undefined) {
    output.writeFieldBegin('appOpenTargetingRuleId', Thrift.Type.STRING, 16);
    output.writeString(this.appOpenTargetingRuleId);
    output.writeFieldEnd();
  }
  if (this.renderBeaconUrl !== null && this.renderBeaconUrl !== undefined) {
    output.writeFieldBegin('renderBeaconUrl', Thrift.Type.STRING, 17);
    output.writeString(this.renderBeaconUrl);
    output.writeFieldEnd();
  }
  if (this.advertiserApiKey !== null && this.advertiserApiKey !== undefined) {
    output.writeFieldBegin('advertiserApiKey', Thrift.Type.STRING, 18);
    output.writeString(this.advertiserApiKey);
    output.writeFieldEnd();
  }
  if (this.secondaryBrandColor !== null && this.secondaryBrandColor !== undefined) {
    output.writeFieldBegin('secondaryBrandColor', Thrift.Type.STRING, 19);
    output.writeString(this.secondaryBrandColor);
    output.writeFieldEnd();
  }
  if (this.appOptionalDetails !== null && this.appOptionalDetails !== undefined) {
    output.writeFieldBegin('appOptionalDetails', Thrift.Type.STRUCT, 20);
    this.appOptionalDetails.write(output);
    output.writeFieldEnd();
  }
  if (this.preloadEnable !== null && this.preloadEnable !== undefined) {
    output.writeFieldBegin('preloadEnable', Thrift.Type.BOOL, 21);
    output.writeBool(this.preloadEnable);
    output.writeFieldEnd();
  }
  if (this.contentProviderUrl !== null && this.contentProviderUrl !== undefined) {
    output.writeFieldBegin('contentProviderUrl', Thrift.Type.STRING, 22);
    output.writeString(this.contentProviderUrl);
    output.writeFieldEnd();
  }
  if (this.prldAttr !== null && this.prldAttr !== undefined) {
    output.writeFieldBegin('prldAttr', Thrift.Type.STRUCT, 23);
    this.prldAttr.write(output);
    output.writeFieldEnd();
  }
  if (this.templateId !== null && this.templateId !== undefined) {
    output.writeFieldBegin('templateId', Thrift.Type.STRING, 24);
    output.writeString(this.templateId);
    output.writeFieldEnd();
  }
  if (this.tag !== null && this.tag !== undefined) {
    output.writeFieldBegin('tag', Thrift.Type.STRUCT, 25);
    this.tag.write(output);
    output.writeFieldEnd();
  }
  if (this.reviewCount !== null && this.reviewCount !== undefined) {
    output.writeFieldBegin('reviewCount', Thrift.Type.STRING, 26);
    output.writeString(this.reviewCount);
    output.writeFieldEnd();
  }
  if (this.sizeInMb !== null && this.sizeInMb !== undefined) {
    output.writeFieldBegin('sizeInMb', Thrift.Type.STRING, 27);
    output.writeString(this.sizeInMb);
    output.writeFieldEnd();
  }
  if (this.categoryInfo !== null && this.categoryInfo !== undefined) {
    output.writeFieldBegin('categoryInfo', Thrift.Type.STRUCT, 28);
    this.categoryInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.textColor !== null && this.textColor !== undefined) {
    output.writeFieldBegin('textColor', Thrift.Type.STRING, 29);
    output.writeString(this.textColor);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.LIST, 30);
    output.writeListBegin(Thrift.Type.STRING, this.bgColor.length);
    for (var iter1009 in this.bgColor)
    {
      if (this.bgColor.hasOwnProperty(iter1009))
      {
        iter1009 = this.bgColor[iter1009];
        output.writeString(iter1009);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.carouselImages !== null && this.carouselImages !== undefined) {
    output.writeFieldBegin('carouselImages', Thrift.Type.LIST, 31);
    output.writeListBegin(Thrift.Type.STRING, this.carouselImages.length);
    for (var iter1010 in this.carouselImages)
    {
      if (this.carouselImages.hasOwnProperty(iter1010))
      {
        iter1010 = this.carouselImages[iter1010];
        output.writeString(iter1010);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.installNowCta !== null && this.installNowCta !== undefined) {
    output.writeFieldBegin('installNowCta', Thrift.Type.STRUCT, 32);
    this.installNowCta.write(output);
    output.writeFieldEnd();
  }
  if (this.installLaterCta !== null && this.installLaterCta !== undefined) {
    output.writeFieldBegin('installLaterCta', Thrift.Type.STRUCT, 33);
    this.installLaterCta.write(output);
    output.writeFieldEnd();
  }
  if (this.fbu !== null && this.fbu !== undefined) {
    output.writeFieldBegin('fbu', Thrift.Type.STRING, 34);
    output.writeString(this.fbu);
    output.writeFieldEnd();
  }
  if (this.sourceName !== null && this.sourceName !== undefined) {
    output.writeFieldBegin('sourceName', Thrift.Type.LIST, 35);
    output.writeListBegin(Thrift.Type.STRING, this.sourceName.length);
    for (var iter1011 in this.sourceName)
    {
      if (this.sourceName.hasOwnProperty(iter1011))
      {
        iter1011 = this.sourceName[iter1011];
        output.writeString(iter1011);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.dspAdvertiserGuid !== null && this.dspAdvertiserGuid !== undefined) {
    output.writeFieldBegin('dspAdvertiserGuid', Thrift.Type.STRING, 36);
    output.writeString(this.dspAdvertiserGuid);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SpaceEvent = function(args) {
  this.eventType = null;
  this.owner = null;
  this.timestampInMillis = null;
  this.parentContentId = null;
  this.meta = null;
  this.data = null;
  this.contentType = null;
  if (args) {
    if (args.eventType !== undefined && args.eventType !== null) {
      this.eventType = args.eventType;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field eventType is unset!');
    }
    if (args.owner !== undefined && args.owner !== null) {
      this.owner = args.owner;
    }
    if (args.timestampInMillis !== undefined && args.timestampInMillis !== null) {
      this.timestampInMillis = args.timestampInMillis;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field timestampInMillis is unset!');
    }
    if (args.parentContentId !== undefined && args.parentContentId !== null) {
      this.parentContentId = args.parentContentId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field parentContentId is unset!');
    }
    if (args.meta !== undefined && args.meta !== null) {
      this.meta = new GlanceMeta(args.meta);
    }
    if (args.data !== undefined && args.data !== null) {
      this.data = new GlanceData(args.data);
    }
    if (args.contentType !== undefined && args.contentType !== null) {
      this.contentType = Thrift.copyList(args.contentType, [null]);
    }
  }
};
SpaceEvent.prototype = {};
SpaceEvent.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.eventType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.owner = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.timestampInMillis = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.parentContentId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.meta = new GlanceMeta();
        this.meta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.data = new GlanceData();
        this.data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size1012 = 0;
        var _rtmp31016;
        this.contentType = [];
        var _etype1015 = 0;
        _rtmp31016 = input.readListBegin();
        _etype1015 = _rtmp31016.etype;
        _size1012 = _rtmp31016.size;
        for (var _i1017 = 0; _i1017 < _size1012; ++_i1017)
        {
          var elem1018 = null;
          elem1018 = input.readI32().value;
          this.contentType.push(elem1018);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SpaceEvent.prototype.write = function(output) {
  output.writeStructBegin('SpaceEvent');
  if (this.eventType !== null && this.eventType !== undefined) {
    output.writeFieldBegin('eventType', Thrift.Type.I32, 1);
    output.writeI32(this.eventType);
    output.writeFieldEnd();
  }
  if (this.owner !== null && this.owner !== undefined) {
    output.writeFieldBegin('owner', Thrift.Type.STRING, 2);
    output.writeString(this.owner);
    output.writeFieldEnd();
  }
  if (this.timestampInMillis !== null && this.timestampInMillis !== undefined) {
    output.writeFieldBegin('timestampInMillis', Thrift.Type.I64, 3);
    output.writeI64(this.timestampInMillis);
    output.writeFieldEnd();
  }
  if (this.parentContentId !== null && this.parentContentId !== undefined) {
    output.writeFieldBegin('parentContentId', Thrift.Type.STRING, 4);
    output.writeString(this.parentContentId);
    output.writeFieldEnd();
  }
  if (this.meta !== null && this.meta !== undefined) {
    output.writeFieldBegin('meta', Thrift.Type.STRUCT, 5);
    this.meta.write(output);
    output.writeFieldEnd();
  }
  if (this.data !== null && this.data !== undefined) {
    output.writeFieldBegin('data', Thrift.Type.STRUCT, 6);
    this.data.write(output);
    output.writeFieldEnd();
  }
  if (this.contentType !== null && this.contentType !== undefined) {
    output.writeFieldBegin('contentType', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.I32, this.contentType.length);
    for (var iter1019 in this.contentType)
    {
      if (this.contentType.hasOwnProperty(iter1019))
      {
        iter1019 = this.contentType[iter1019];
        output.writeI32(iter1019);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GtvProvider = function(args) {
  this.glanceProviderId = null;
  this.name = null;
  this.roposoProviderId = null;
  this.trelloCardListId = null;
  this.providerType = null;
  this.storageAccountName = null;
  if (args) {
    if (args.glanceProviderId !== undefined && args.glanceProviderId !== null) {
      this.glanceProviderId = args.glanceProviderId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.roposoProviderId !== undefined && args.roposoProviderId !== null) {
      this.roposoProviderId = args.roposoProviderId;
    }
    if (args.trelloCardListId !== undefined && args.trelloCardListId !== null) {
      this.trelloCardListId = args.trelloCardListId;
    }
    if (args.providerType !== undefined && args.providerType !== null) {
      this.providerType = args.providerType;
    }
    if (args.storageAccountName !== undefined && args.storageAccountName !== null) {
      this.storageAccountName = args.storageAccountName;
    }
  }
};
GtvProvider.prototype = {};
GtvProvider.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceProviderId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.roposoProviderId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.trelloCardListId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.providerType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.storageAccountName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GtvProvider.prototype.write = function(output) {
  output.writeStructBegin('GtvProvider');
  if (this.glanceProviderId !== null && this.glanceProviderId !== undefined) {
    output.writeFieldBegin('glanceProviderId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceProviderId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.roposoProviderId !== null && this.roposoProviderId !== undefined) {
    output.writeFieldBegin('roposoProviderId', Thrift.Type.STRING, 3);
    output.writeString(this.roposoProviderId);
    output.writeFieldEnd();
  }
  if (this.trelloCardListId !== null && this.trelloCardListId !== undefined) {
    output.writeFieldBegin('trelloCardListId', Thrift.Type.STRING, 4);
    output.writeString(this.trelloCardListId);
    output.writeFieldEnd();
  }
  if (this.providerType !== null && this.providerType !== undefined) {
    output.writeFieldBegin('providerType', Thrift.Type.STRING, 5);
    output.writeString(this.providerType);
    output.writeFieldEnd();
  }
  if (this.storageAccountName !== null && this.storageAccountName !== undefined) {
    output.writeFieldBegin('storageAccountName', Thrift.Type.STRING, 6);
    output.writeString(this.storageAccountName);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

StudioSessionToken = function(args) {
  this.createdAtInSecs = null;
  this.user = null;
  this.role = null;
  this.partner = null;
  this.partners = null;
  if (args) {
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field createdAtInSecs is unset!');
    }
    if (args.user !== undefined && args.user !== null) {
      this.user = new StudioUser(args.user);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field user is unset!');
    }
    if (args.role !== undefined && args.role !== null) {
      this.role = args.role;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field role is unset!');
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    }
    if (args.partners !== undefined && args.partners !== null) {
      this.partners = Thrift.copyList(args.partners, [Partner]);
    }
  }
};
StudioSessionToken.prototype = {};
StudioSessionToken.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.user = new StudioUser();
        this.user.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.role = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size1020 = 0;
        var _rtmp31024;
        this.partners = [];
        var _etype1023 = 0;
        _rtmp31024 = input.readListBegin();
        _etype1023 = _rtmp31024.etype;
        _size1020 = _rtmp31024.size;
        for (var _i1025 = 0; _i1025 < _size1020; ++_i1025)
        {
          var elem1026 = null;
          elem1026 = new Partner();
          elem1026.read(input);
          this.partners.push(elem1026);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

StudioSessionToken.prototype.write = function(output) {
  output.writeStructBegin('StudioSessionToken');
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 1);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.user !== null && this.user !== undefined) {
    output.writeFieldBegin('user', Thrift.Type.STRUCT, 2);
    this.user.write(output);
    output.writeFieldEnd();
  }
  if (this.role !== null && this.role !== undefined) {
    output.writeFieldBegin('role', Thrift.Type.I32, 3);
    output.writeI32(this.role);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 4);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.partners !== null && this.partners !== undefined) {
    output.writeFieldBegin('partners', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.partners.length);
    for (var iter1027 in this.partners)
    {
      if (this.partners.hasOwnProperty(iter1027))
      {
        iter1027 = this.partners[iter1027];
        iter1027.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SDKVersion = function(args) {
  this.sdkVersion = null;
  if (args) {
    if (args.sdkVersion !== undefined && args.sdkVersion !== null) {
      this.sdkVersion = args.sdkVersion;
    }
  }
};
SDKVersion.prototype = {};
SDKVersion.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.sdkVersion = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SDKVersion.prototype.write = function(output) {
  output.writeStructBegin('SDKVersion');
  if (this.sdkVersion !== null && this.sdkVersion !== undefined) {
    output.writeFieldBegin('sdkVersion', Thrift.Type.STRING, 1);
    output.writeString(this.sdkVersion);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DefaultRuleCity = function(args) {
  this.cityId = null;
  this.cityName = null;
  this.stateId = null;
  this.stateName = null;
  this.countryId = null;
  this.countryName = null;
  this.region = null;
  if (args) {
    if (args.cityId !== undefined && args.cityId !== null) {
      this.cityId = args.cityId;
    }
    if (args.cityName !== undefined && args.cityName !== null) {
      this.cityName = args.cityName;
    }
    if (args.stateId !== undefined && args.stateId !== null) {
      this.stateId = args.stateId;
    }
    if (args.stateName !== undefined && args.stateName !== null) {
      this.stateName = args.stateName;
    }
    if (args.countryId !== undefined && args.countryId !== null) {
      this.countryId = args.countryId;
    }
    if (args.countryName !== undefined && args.countryName !== null) {
      this.countryName = args.countryName;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
  }
};
DefaultRuleCity.prototype = {};
DefaultRuleCity.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.cityId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.cityName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.stateId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.stateName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.countryId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.countryName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.region = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DefaultRuleCity.prototype.write = function(output) {
  output.writeStructBegin('DefaultRuleCity');
  if (this.cityId !== null && this.cityId !== undefined) {
    output.writeFieldBegin('cityId', Thrift.Type.I64, 1);
    output.writeI64(this.cityId);
    output.writeFieldEnd();
  }
  if (this.cityName !== null && this.cityName !== undefined) {
    output.writeFieldBegin('cityName', Thrift.Type.STRING, 2);
    output.writeString(this.cityName);
    output.writeFieldEnd();
  }
  if (this.stateId !== null && this.stateId !== undefined) {
    output.writeFieldBegin('stateId', Thrift.Type.I64, 3);
    output.writeI64(this.stateId);
    output.writeFieldEnd();
  }
  if (this.stateName !== null && this.stateName !== undefined) {
    output.writeFieldBegin('stateName', Thrift.Type.STRING, 4);
    output.writeString(this.stateName);
    output.writeFieldEnd();
  }
  if (this.countryId !== null && this.countryId !== undefined) {
    output.writeFieldBegin('countryId', Thrift.Type.I64, 5);
    output.writeI64(this.countryId);
    output.writeFieldEnd();
  }
  if (this.countryName !== null && this.countryName !== undefined) {
    output.writeFieldBegin('countryName', Thrift.Type.STRING, 6);
    output.writeString(this.countryName);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.STRING, 7);
    output.writeString(this.region);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DefaultTargetingRule = function(args) {
  this.sdkVersions = null;
  this.cities = null;
  if (args) {
    if (args.sdkVersions !== undefined && args.sdkVersions !== null) {
      this.sdkVersions = Thrift.copyList(args.sdkVersions, [SDKVersion]);
    }
    if (args.cities !== undefined && args.cities !== null) {
      this.cities = Thrift.copyList(args.cities, [DefaultRuleCity]);
    }
  }
};
DefaultTargetingRule.prototype = {};
DefaultTargetingRule.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1028 = 0;
        var _rtmp31032;
        this.sdkVersions = [];
        var _etype1031 = 0;
        _rtmp31032 = input.readListBegin();
        _etype1031 = _rtmp31032.etype;
        _size1028 = _rtmp31032.size;
        for (var _i1033 = 0; _i1033 < _size1028; ++_i1033)
        {
          var elem1034 = null;
          elem1034 = new SDKVersion();
          elem1034.read(input);
          this.sdkVersions.push(elem1034);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1035 = 0;
        var _rtmp31039;
        this.cities = [];
        var _etype1038 = 0;
        _rtmp31039 = input.readListBegin();
        _etype1038 = _rtmp31039.etype;
        _size1035 = _rtmp31039.size;
        for (var _i1040 = 0; _i1040 < _size1035; ++_i1040)
        {
          var elem1041 = null;
          elem1041 = new DefaultRuleCity();
          elem1041.read(input);
          this.cities.push(elem1041);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DefaultTargetingRule.prototype.write = function(output) {
  output.writeStructBegin('DefaultTargetingRule');
  if (this.sdkVersions !== null && this.sdkVersions !== undefined) {
    output.writeFieldBegin('sdkVersions', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.sdkVersions.length);
    for (var iter1042 in this.sdkVersions)
    {
      if (this.sdkVersions.hasOwnProperty(iter1042))
      {
        iter1042 = this.sdkVersions[iter1042];
        iter1042.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.cities !== null && this.cities !== undefined) {
    output.writeFieldBegin('cities', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.cities.length);
    for (var iter1043 in this.cities)
    {
      if (this.cities.hasOwnProperty(iter1043))
      {
        iter1043 = this.cities[iter1043];
        iter1043.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

StudioAppConfig = function(args) {
  this.cdnPrefix = null;
  this.currentToken = null;
  this.partners = null;
  this.partnerMetas = null;
  this.publishers = null;
  this.categories = null;
  this.experiments = null;
  this.cities = null;
  this.segments = null;
  this.glanceLanguages = null;
  this.sqlSegments = null;
  this.segmentTargetingRule = null;
  this.apps = null;
  this.bubbles = null;
  this.carrierMetas = null;
  this.cityGroups = null;
  this.notificationTemplates = null;
  this.defaultTargetingRule = null;
  if (args) {
    if (args.cdnPrefix !== undefined && args.cdnPrefix !== null) {
      this.cdnPrefix = args.cdnPrefix;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field cdnPrefix is unset!');
    }
    if (args.currentToken !== undefined && args.currentToken !== null) {
      this.currentToken = new StudioSessionToken(args.currentToken);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field currentToken is unset!');
    }
    if (args.partners !== undefined && args.partners !== null) {
      this.partners = Thrift.copyList(args.partners, [Partner]);
    }
    if (args.partnerMetas !== undefined && args.partnerMetas !== null) {
      this.partnerMetas = Thrift.copyList(args.partnerMetas, [PartnerMeta]);
    }
    if (args.publishers !== undefined && args.publishers !== null) {
      this.publishers = Thrift.copyList(args.publishers, [Publisher]);
    }
    if (args.categories !== undefined && args.categories !== null) {
      this.categories = Thrift.copyList(args.categories, [StudioCategory]);
    }
    if (args.experiments !== undefined && args.experiments !== null) {
      this.experiments = Thrift.copyList(args.experiments, [Experiment]);
    }
    if (args.cities !== undefined && args.cities !== null) {
      this.cities = Thrift.copyList(args.cities, [City]);
    }
    if (args.segments !== undefined && args.segments !== null) {
      this.segments = Thrift.copyList(args.segments, [null]);
    }
    if (args.glanceLanguages !== undefined && args.glanceLanguages !== null) {
      this.glanceLanguages = Thrift.copyList(args.glanceLanguages, [GlanceLanguage]);
    }
    if (args.sqlSegments !== undefined && args.sqlSegments !== null) {
      this.sqlSegments = Thrift.copyList(args.sqlSegments, [null]);
    }
    if (args.segmentTargetingRule !== undefined && args.segmentTargetingRule !== null) {
      this.segmentTargetingRule = Thrift.copyList(args.segmentTargetingRule, [SegmentTargetingRule]);
    }
    if (args.apps !== undefined && args.apps !== null) {
      this.apps = Thrift.copyList(args.apps, [App]);
    }
    if (args.bubbles !== undefined && args.bubbles !== null) {
      this.bubbles = Thrift.copyList(args.bubbles, [Bubble]);
    }
    if (args.carrierMetas !== undefined && args.carrierMetas !== null) {
      this.carrierMetas = Thrift.copyList(args.carrierMetas, [null]);
    }
    if (args.cityGroups !== undefined && args.cityGroups !== null) {
      this.cityGroups = Thrift.copyList(args.cityGroups, [null]);
    }
    if (args.notificationTemplates !== undefined && args.notificationTemplates !== null) {
      this.notificationTemplates = Thrift.copyList(args.notificationTemplates, [null]);
    }
    if (args.defaultTargetingRule !== undefined && args.defaultTargetingRule !== null) {
      this.defaultTargetingRule = new DefaultTargetingRule(args.defaultTargetingRule);
    }
  }
};
StudioAppConfig.prototype = {};
StudioAppConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.cdnPrefix = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.currentToken = new StudioSessionToken();
        this.currentToken.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size1044 = 0;
        var _rtmp31048;
        this.partners = [];
        var _etype1047 = 0;
        _rtmp31048 = input.readListBegin();
        _etype1047 = _rtmp31048.etype;
        _size1044 = _rtmp31048.size;
        for (var _i1049 = 0; _i1049 < _size1044; ++_i1049)
        {
          var elem1050 = null;
          elem1050 = new Partner();
          elem1050.read(input);
          this.partners.push(elem1050);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size1051 = 0;
        var _rtmp31055;
        this.partnerMetas = [];
        var _etype1054 = 0;
        _rtmp31055 = input.readListBegin();
        _etype1054 = _rtmp31055.etype;
        _size1051 = _rtmp31055.size;
        for (var _i1056 = 0; _i1056 < _size1051; ++_i1056)
        {
          var elem1057 = null;
          elem1057 = new PartnerMeta();
          elem1057.read(input);
          this.partnerMetas.push(elem1057);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size1058 = 0;
        var _rtmp31062;
        this.publishers = [];
        var _etype1061 = 0;
        _rtmp31062 = input.readListBegin();
        _etype1061 = _rtmp31062.etype;
        _size1058 = _rtmp31062.size;
        for (var _i1063 = 0; _i1063 < _size1058; ++_i1063)
        {
          var elem1064 = null;
          elem1064 = new Publisher();
          elem1064.read(input);
          this.publishers.push(elem1064);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size1065 = 0;
        var _rtmp31069;
        this.categories = [];
        var _etype1068 = 0;
        _rtmp31069 = input.readListBegin();
        _etype1068 = _rtmp31069.etype;
        _size1065 = _rtmp31069.size;
        for (var _i1070 = 0; _i1070 < _size1065; ++_i1070)
        {
          var elem1071 = null;
          elem1071 = new StudioCategory();
          elem1071.read(input);
          this.categories.push(elem1071);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size1072 = 0;
        var _rtmp31076;
        this.experiments = [];
        var _etype1075 = 0;
        _rtmp31076 = input.readListBegin();
        _etype1075 = _rtmp31076.etype;
        _size1072 = _rtmp31076.size;
        for (var _i1077 = 0; _i1077 < _size1072; ++_i1077)
        {
          var elem1078 = null;
          elem1078 = new Experiment();
          elem1078.read(input);
          this.experiments.push(elem1078);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size1079 = 0;
        var _rtmp31083;
        this.cities = [];
        var _etype1082 = 0;
        _rtmp31083 = input.readListBegin();
        _etype1082 = _rtmp31083.etype;
        _size1079 = _rtmp31083.size;
        for (var _i1084 = 0; _i1084 < _size1079; ++_i1084)
        {
          var elem1085 = null;
          elem1085 = new City();
          elem1085.read(input);
          this.cities.push(elem1085);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size1086 = 0;
        var _rtmp31090;
        this.segments = [];
        var _etype1089 = 0;
        _rtmp31090 = input.readListBegin();
        _etype1089 = _rtmp31090.etype;
        _size1086 = _rtmp31090.size;
        for (var _i1091 = 0; _i1091 < _size1086; ++_i1091)
        {
          var elem1092 = null;
          elem1092 = new StudioSegment();
          elem1092.read(input);
          this.segments.push(elem1092);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.LIST) {
        var _size1093 = 0;
        var _rtmp31097;
        this.glanceLanguages = [];
        var _etype1096 = 0;
        _rtmp31097 = input.readListBegin();
        _etype1096 = _rtmp31097.etype;
        _size1093 = _rtmp31097.size;
        for (var _i1098 = 0; _i1098 < _size1093; ++_i1098)
        {
          var elem1099 = null;
          elem1099 = new GlanceLanguage();
          elem1099.read(input);
          this.glanceLanguages.push(elem1099);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size1100 = 0;
        var _rtmp31104;
        this.sqlSegments = [];
        var _etype1103 = 0;
        _rtmp31104 = input.readListBegin();
        _etype1103 = _rtmp31104.etype;
        _size1100 = _rtmp31104.size;
        for (var _i1105 = 0; _i1105 < _size1100; ++_i1105)
        {
          var elem1106 = null;
          elem1106 = new SqlStudioSegment();
          elem1106.read(input);
          this.sqlSegments.push(elem1106);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.LIST) {
        var _size1107 = 0;
        var _rtmp31111;
        this.segmentTargetingRule = [];
        var _etype1110 = 0;
        _rtmp31111 = input.readListBegin();
        _etype1110 = _rtmp31111.etype;
        _size1107 = _rtmp31111.size;
        for (var _i1112 = 0; _i1112 < _size1107; ++_i1112)
        {
          var elem1113 = null;
          elem1113 = new SegmentTargetingRule();
          elem1113.read(input);
          this.segmentTargetingRule.push(elem1113);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size1114 = 0;
        var _rtmp31118;
        this.apps = [];
        var _etype1117 = 0;
        _rtmp31118 = input.readListBegin();
        _etype1117 = _rtmp31118.etype;
        _size1114 = _rtmp31118.size;
        for (var _i1119 = 0; _i1119 < _size1114; ++_i1119)
        {
          var elem1120 = null;
          elem1120 = new App();
          elem1120.read(input);
          this.apps.push(elem1120);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.LIST) {
        var _size1121 = 0;
        var _rtmp31125;
        this.bubbles = [];
        var _etype1124 = 0;
        _rtmp31125 = input.readListBegin();
        _etype1124 = _rtmp31125.etype;
        _size1121 = _rtmp31125.size;
        for (var _i1126 = 0; _i1126 < _size1121; ++_i1126)
        {
          var elem1127 = null;
          elem1127 = new Bubble();
          elem1127.read(input);
          this.bubbles.push(elem1127);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.LIST) {
        var _size1128 = 0;
        var _rtmp31132;
        this.carrierMetas = [];
        var _etype1131 = 0;
        _rtmp31132 = input.readListBegin();
        _etype1131 = _rtmp31132.etype;
        _size1128 = _rtmp31132.size;
        for (var _i1133 = 0; _i1133 < _size1128; ++_i1133)
        {
          var elem1134 = null;
          elem1134 = new Carrier();
          elem1134.read(input);
          this.carrierMetas.push(elem1134);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.LIST) {
        var _size1135 = 0;
        var _rtmp31139;
        this.cityGroups = [];
        var _etype1138 = 0;
        _rtmp31139 = input.readListBegin();
        _etype1138 = _rtmp31139.etype;
        _size1135 = _rtmp31139.size;
        for (var _i1140 = 0; _i1140 < _size1135; ++_i1140)
        {
          var elem1141 = null;
          elem1141 = new CityGroup();
          elem1141.read(input);
          this.cityGroups.push(elem1141);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.LIST) {
        var _size1142 = 0;
        var _rtmp31146;
        this.notificationTemplates = [];
        var _etype1145 = 0;
        _rtmp31146 = input.readListBegin();
        _etype1145 = _rtmp31146.etype;
        _size1142 = _rtmp31146.size;
        for (var _i1147 = 0; _i1147 < _size1142; ++_i1147)
        {
          var elem1148 = null;
          elem1148 = new NotificationTemplate();
          elem1148.read(input);
          this.notificationTemplates.push(elem1148);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRUCT) {
        this.defaultTargetingRule = new DefaultTargetingRule();
        this.defaultTargetingRule.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

StudioAppConfig.prototype.write = function(output) {
  output.writeStructBegin('StudioAppConfig');
  if (this.cdnPrefix !== null && this.cdnPrefix !== undefined) {
    output.writeFieldBegin('cdnPrefix', Thrift.Type.STRING, 1);
    output.writeString(this.cdnPrefix);
    output.writeFieldEnd();
  }
  if (this.currentToken !== null && this.currentToken !== undefined) {
    output.writeFieldBegin('currentToken', Thrift.Type.STRUCT, 2);
    this.currentToken.write(output);
    output.writeFieldEnd();
  }
  if (this.partners !== null && this.partners !== undefined) {
    output.writeFieldBegin('partners', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.partners.length);
    for (var iter1149 in this.partners)
    {
      if (this.partners.hasOwnProperty(iter1149))
      {
        iter1149 = this.partners[iter1149];
        iter1149.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.partnerMetas !== null && this.partnerMetas !== undefined) {
    output.writeFieldBegin('partnerMetas', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRUCT, this.partnerMetas.length);
    for (var iter1150 in this.partnerMetas)
    {
      if (this.partnerMetas.hasOwnProperty(iter1150))
      {
        iter1150 = this.partnerMetas[iter1150];
        iter1150.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.publishers !== null && this.publishers !== undefined) {
    output.writeFieldBegin('publishers', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.publishers.length);
    for (var iter1151 in this.publishers)
    {
      if (this.publishers.hasOwnProperty(iter1151))
      {
        iter1151 = this.publishers[iter1151];
        iter1151.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.categories !== null && this.categories !== undefined) {
    output.writeFieldBegin('categories', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.STRUCT, this.categories.length);
    for (var iter1152 in this.categories)
    {
      if (this.categories.hasOwnProperty(iter1152))
      {
        iter1152 = this.categories[iter1152];
        iter1152.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.experiments !== null && this.experiments !== undefined) {
    output.writeFieldBegin('experiments', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRUCT, this.experiments.length);
    for (var iter1153 in this.experiments)
    {
      if (this.experiments.hasOwnProperty(iter1153))
      {
        iter1153 = this.experiments[iter1153];
        iter1153.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.cities !== null && this.cities !== undefined) {
    output.writeFieldBegin('cities', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRUCT, this.cities.length);
    for (var iter1154 in this.cities)
    {
      if (this.cities.hasOwnProperty(iter1154))
      {
        iter1154 = this.cities[iter1154];
        iter1154.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.segments !== null && this.segments !== undefined) {
    output.writeFieldBegin('segments', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRUCT, this.segments.length);
    for (var iter1155 in this.segments)
    {
      if (this.segments.hasOwnProperty(iter1155))
      {
        iter1155 = this.segments[iter1155];
        iter1155.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.glanceLanguages !== null && this.glanceLanguages !== undefined) {
    output.writeFieldBegin('glanceLanguages', Thrift.Type.LIST, 10);
    output.writeListBegin(Thrift.Type.STRUCT, this.glanceLanguages.length);
    for (var iter1156 in this.glanceLanguages)
    {
      if (this.glanceLanguages.hasOwnProperty(iter1156))
      {
        iter1156 = this.glanceLanguages[iter1156];
        iter1156.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.sqlSegments !== null && this.sqlSegments !== undefined) {
    output.writeFieldBegin('sqlSegments', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRUCT, this.sqlSegments.length);
    for (var iter1157 in this.sqlSegments)
    {
      if (this.sqlSegments.hasOwnProperty(iter1157))
      {
        iter1157 = this.sqlSegments[iter1157];
        iter1157.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.segmentTargetingRule !== null && this.segmentTargetingRule !== undefined) {
    output.writeFieldBegin('segmentTargetingRule', Thrift.Type.LIST, 12);
    output.writeListBegin(Thrift.Type.STRUCT, this.segmentTargetingRule.length);
    for (var iter1158 in this.segmentTargetingRule)
    {
      if (this.segmentTargetingRule.hasOwnProperty(iter1158))
      {
        iter1158 = this.segmentTargetingRule[iter1158];
        iter1158.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.apps !== null && this.apps !== undefined) {
    output.writeFieldBegin('apps', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRUCT, this.apps.length);
    for (var iter1159 in this.apps)
    {
      if (this.apps.hasOwnProperty(iter1159))
      {
        iter1159 = this.apps[iter1159];
        iter1159.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.bubbles !== null && this.bubbles !== undefined) {
    output.writeFieldBegin('bubbles', Thrift.Type.LIST, 14);
    output.writeListBegin(Thrift.Type.STRUCT, this.bubbles.length);
    for (var iter1160 in this.bubbles)
    {
      if (this.bubbles.hasOwnProperty(iter1160))
      {
        iter1160 = this.bubbles[iter1160];
        iter1160.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.carrierMetas !== null && this.carrierMetas !== undefined) {
    output.writeFieldBegin('carrierMetas', Thrift.Type.LIST, 15);
    output.writeListBegin(Thrift.Type.STRUCT, this.carrierMetas.length);
    for (var iter1161 in this.carrierMetas)
    {
      if (this.carrierMetas.hasOwnProperty(iter1161))
      {
        iter1161 = this.carrierMetas[iter1161];
        iter1161.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.cityGroups !== null && this.cityGroups !== undefined) {
    output.writeFieldBegin('cityGroups', Thrift.Type.LIST, 16);
    output.writeListBegin(Thrift.Type.STRUCT, this.cityGroups.length);
    for (var iter1162 in this.cityGroups)
    {
      if (this.cityGroups.hasOwnProperty(iter1162))
      {
        iter1162 = this.cityGroups[iter1162];
        iter1162.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.notificationTemplates !== null && this.notificationTemplates !== undefined) {
    output.writeFieldBegin('notificationTemplates', Thrift.Type.LIST, 17);
    output.writeListBegin(Thrift.Type.STRUCT, this.notificationTemplates.length);
    for (var iter1163 in this.notificationTemplates)
    {
      if (this.notificationTemplates.hasOwnProperty(iter1163))
      {
        iter1163 = this.notificationTemplates[iter1163];
        iter1163.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.defaultTargetingRule !== null && this.defaultTargetingRule !== undefined) {
    output.writeFieldBegin('defaultTargetingRule', Thrift.Type.STRUCT, 18);
    this.defaultTargetingRule.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

StudioUser = function(args) {
  this.email = null;
  this.name = null;
  this.role = null;
  this.passwordHash = null;
  this.pictureUrl = null;
  this.partner = null;
  this.partners = null;
  if (args) {
    if (args.email !== undefined && args.email !== null) {
      this.email = args.email;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field email is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.role !== undefined && args.role !== null) {
      this.role = args.role;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field role is unset!');
    }
    if (args.passwordHash !== undefined && args.passwordHash !== null) {
      this.passwordHash = args.passwordHash;
    }
    if (args.pictureUrl !== undefined && args.pictureUrl !== null) {
      this.pictureUrl = args.pictureUrl;
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    }
    if (args.partners !== undefined && args.partners !== null) {
      this.partners = Thrift.copyList(args.partners, [Partner]);
    }
  }
};
StudioUser.prototype = {};
StudioUser.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.email = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.role = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.passwordHash = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.pictureUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size1164 = 0;
        var _rtmp31168;
        this.partners = [];
        var _etype1167 = 0;
        _rtmp31168 = input.readListBegin();
        _etype1167 = _rtmp31168.etype;
        _size1164 = _rtmp31168.size;
        for (var _i1169 = 0; _i1169 < _size1164; ++_i1169)
        {
          var elem1170 = null;
          elem1170 = new Partner();
          elem1170.read(input);
          this.partners.push(elem1170);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

StudioUser.prototype.write = function(output) {
  output.writeStructBegin('StudioUser');
  if (this.email !== null && this.email !== undefined) {
    output.writeFieldBegin('email', Thrift.Type.STRING, 1);
    output.writeString(this.email);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.role !== null && this.role !== undefined) {
    output.writeFieldBegin('role', Thrift.Type.I32, 3);
    output.writeI32(this.role);
    output.writeFieldEnd();
  }
  if (this.passwordHash !== null && this.passwordHash !== undefined) {
    output.writeFieldBegin('passwordHash', Thrift.Type.STRING, 4);
    output.writeString(this.passwordHash);
    output.writeFieldEnd();
  }
  if (this.pictureUrl !== null && this.pictureUrl !== undefined) {
    output.writeFieldBegin('pictureUrl', Thrift.Type.STRING, 5);
    output.writeString(this.pictureUrl);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 6);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.partners !== null && this.partners !== undefined) {
    output.writeFieldBegin('partners', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRUCT, this.partners.length);
    for (var iter1171 in this.partners)
    {
      if (this.partners.hasOwnProperty(iter1171))
      {
        iter1171 = this.partners[iter1171];
        iter1171.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ScreenInfo = function(args) {
  this.width = null;
  this.height = null;
  this.dpi = null;
  if (args) {
    if (args.width !== undefined && args.width !== null) {
      this.width = args.width;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field width is unset!');
    }
    if (args.height !== undefined && args.height !== null) {
      this.height = args.height;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field height is unset!');
    }
    if (args.dpi !== undefined && args.dpi !== null) {
      this.dpi = args.dpi;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field dpi is unset!');
    }
  }
};
ScreenInfo.prototype = {};
ScreenInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.width = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.height = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.dpi = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ScreenInfo.prototype.write = function(output) {
  output.writeStructBegin('ScreenInfo');
  if (this.width !== null && this.width !== undefined) {
    output.writeFieldBegin('width', Thrift.Type.I32, 1);
    output.writeI32(this.width);
    output.writeFieldEnd();
  }
  if (this.height !== null && this.height !== undefined) {
    output.writeFieldBegin('height', Thrift.Type.I32, 2);
    output.writeI32(this.height);
    output.writeFieldEnd();
  }
  if (this.dpi !== null && this.dpi !== undefined) {
    output.writeFieldBegin('dpi', Thrift.Type.I32, 3);
    output.writeI32(this.dpi);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Channel = function(args) {
  this.id = null;
  this.enabled = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    }
  }
};
Channel.prototype = {};
Channel.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Channel.prototype.write = function(output) {
  output.writeStructBegin('Channel');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 2);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

NotificationInfo = function(args) {
  this.enabledAtApp = null;
  this.channels = null;
  if (args) {
    if (args.enabledAtApp !== undefined && args.enabledAtApp !== null) {
      this.enabledAtApp = args.enabledAtApp;
    }
    if (args.channels !== undefined && args.channels !== null) {
      this.channels = Thrift.copyList(args.channels, [Channel]);
    }
  }
};
NotificationInfo.prototype = {};
NotificationInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.enabledAtApp = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1172 = 0;
        var _rtmp31176;
        this.channels = [];
        var _etype1175 = 0;
        _rtmp31176 = input.readListBegin();
        _etype1175 = _rtmp31176.etype;
        _size1172 = _rtmp31176.size;
        for (var _i1177 = 0; _i1177 < _size1172; ++_i1177)
        {
          var elem1178 = null;
          elem1178 = new Channel();
          elem1178.read(input);
          this.channels.push(elem1178);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

NotificationInfo.prototype.write = function(output) {
  output.writeStructBegin('NotificationInfo');
  if (this.enabledAtApp !== null && this.enabledAtApp !== undefined) {
    output.writeFieldBegin('enabledAtApp', Thrift.Type.BOOL, 1);
    output.writeBool(this.enabledAtApp);
    output.writeFieldEnd();
  }
  if (this.channels !== null && this.channels !== undefined) {
    output.writeFieldBegin('channels', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.channels.length);
    for (var iter1179 in this.channels)
    {
      if (this.channels.hasOwnProperty(iter1179))
      {
        iter1179 = this.channels[iter1179];
        iter1179.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppInfo = function(args) {
  this.versionName = null;
  this.minSdkVersion = null;
  this.targetSdkVersion = null;
  this.packageName = null;
  this.versionCode = null;
  if (args) {
    if (args.versionName !== undefined && args.versionName !== null) {
      this.versionName = args.versionName;
    }
    if (args.minSdkVersion !== undefined && args.minSdkVersion !== null) {
      this.minSdkVersion = args.minSdkVersion;
    }
    if (args.targetSdkVersion !== undefined && args.targetSdkVersion !== null) {
      this.targetSdkVersion = args.targetSdkVersion;
    }
    if (args.packageName !== undefined && args.packageName !== null) {
      this.packageName = args.packageName;
    }
    if (args.versionCode !== undefined && args.versionCode !== null) {
      this.versionCode = args.versionCode;
    }
  }
};
AppInfo.prototype = {};
AppInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.versionName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.minSdkVersion = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.targetSdkVersion = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.packageName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.versionCode = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppInfo.prototype.write = function(output) {
  output.writeStructBegin('AppInfo');
  if (this.versionName !== null && this.versionName !== undefined) {
    output.writeFieldBegin('versionName', Thrift.Type.STRING, 1);
    output.writeString(this.versionName);
    output.writeFieldEnd();
  }
  if (this.minSdkVersion !== null && this.minSdkVersion !== undefined) {
    output.writeFieldBegin('minSdkVersion', Thrift.Type.I64, 2);
    output.writeI64(this.minSdkVersion);
    output.writeFieldEnd();
  }
  if (this.targetSdkVersion !== null && this.targetSdkVersion !== undefined) {
    output.writeFieldBegin('targetSdkVersion', Thrift.Type.I64, 3);
    output.writeI64(this.targetSdkVersion);
    output.writeFieldEnd();
  }
  if (this.packageName !== null && this.packageName !== undefined) {
    output.writeFieldBegin('packageName', Thrift.Type.STRING, 4);
    output.writeString(this.packageName);
    output.writeFieldEnd();
  }
  if (this.versionCode !== null && this.versionCode !== undefined) {
    output.writeFieldBegin('versionCode', Thrift.Type.I64, 5);
    output.writeI64(this.versionCode);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

InputInfo = function(args) {
  this.incognitoEnabled = null;
  this.defaultMethod = null;
  if (args) {
    if (args.incognitoEnabled !== undefined && args.incognitoEnabled !== null) {
      this.incognitoEnabled = args.incognitoEnabled;
    }
    if (args.defaultMethod !== undefined && args.defaultMethod !== null) {
      this.defaultMethod = args.defaultMethod;
    }
  }
};
InputInfo.prototype = {};
InputInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.incognitoEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.defaultMethod = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

InputInfo.prototype.write = function(output) {
  output.writeStructBegin('InputInfo');
  if (this.incognitoEnabled !== null && this.incognitoEnabled !== undefined) {
    output.writeFieldBegin('incognitoEnabled', Thrift.Type.BOOL, 1);
    output.writeBool(this.incognitoEnabled);
    output.writeFieldEnd();
  }
  if (this.defaultMethod !== null && this.defaultMethod !== undefined) {
    output.writeFieldBegin('defaultMethod', Thrift.Type.STRING, 2);
    output.writeString(this.defaultMethod);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DeviceInfo = function(args) {
  this.andoridSdk = null;
  this.brand = null;
  this.manufacturer = null;
  this.model = null;
  this.board = null;
  this.screenInfo = null;
  this.hardware = null;
  this.notificationInfo = null;
  this.appInfo = null;
  this.inputInfo = null;
  this.locales = null;
  this.ramMb = null;
  this.price = null;
  this.sku = null;
  this.buildType = null;
  if (args) {
    if (args.andoridSdk !== undefined && args.andoridSdk !== null) {
      this.andoridSdk = args.andoridSdk;
    }
    if (args.brand !== undefined && args.brand !== null) {
      this.brand = args.brand;
    }
    if (args.manufacturer !== undefined && args.manufacturer !== null) {
      this.manufacturer = args.manufacturer;
    }
    if (args.model !== undefined && args.model !== null) {
      this.model = args.model;
    }
    if (args.board !== undefined && args.board !== null) {
      this.board = args.board;
    }
    if (args.screenInfo !== undefined && args.screenInfo !== null) {
      this.screenInfo = new ScreenInfo(args.screenInfo);
    }
    if (args.hardware !== undefined && args.hardware !== null) {
      this.hardware = args.hardware;
    }
    if (args.notificationInfo !== undefined && args.notificationInfo !== null) {
      this.notificationInfo = new NotificationInfo(args.notificationInfo);
    }
    if (args.appInfo !== undefined && args.appInfo !== null) {
      this.appInfo = new AppInfo(args.appInfo);
    }
    if (args.inputInfo !== undefined && args.inputInfo !== null) {
      this.inputInfo = new InputInfo(args.inputInfo);
    }
    if (args.locales !== undefined && args.locales !== null) {
      this.locales = Thrift.copyList(args.locales, [null]);
    }
    if (args.ramMb !== undefined && args.ramMb !== null) {
      this.ramMb = args.ramMb;
    }
    if (args.price !== undefined && args.price !== null) {
      this.price = args.price;
    }
    if (args.sku !== undefined && args.sku !== null) {
      this.sku = args.sku;
    }
    if (args.buildType !== undefined && args.buildType !== null) {
      this.buildType = args.buildType;
    }
  }
};
DeviceInfo.prototype = {};
DeviceInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.andoridSdk = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.brand = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.manufacturer = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.model = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.board = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.screenInfo = new ScreenInfo();
        this.screenInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.hardware = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRUCT) {
        this.notificationInfo = new NotificationInfo();
        this.notificationInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRUCT) {
        this.appInfo = new AppInfo();
        this.appInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRUCT) {
        this.inputInfo = new InputInfo();
        this.inputInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.SET) {
        var _size1180 = 0;
        var _rtmp31184;
        this.locales = [];
        var _etype1183 = 0;
        _rtmp31184 = input.readSetBegin();
        _etype1183 = _rtmp31184.etype;
        _size1180 = _rtmp31184.size;
        for (var _i1185 = 0; _i1185 < _size1180; ++_i1185)
        {
          var elem1186 = null;
          elem1186 = input.readString().value;
          this.locales.push(elem1186);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I32) {
        this.ramMb = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.I32) {
        this.price = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.sku = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.buildType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DeviceInfo.prototype.write = function(output) {
  output.writeStructBegin('DeviceInfo');
  if (this.andoridSdk !== null && this.andoridSdk !== undefined) {
    output.writeFieldBegin('andoridSdk', Thrift.Type.I32, 1);
    output.writeI32(this.andoridSdk);
    output.writeFieldEnd();
  }
  if (this.brand !== null && this.brand !== undefined) {
    output.writeFieldBegin('brand', Thrift.Type.STRING, 2);
    output.writeString(this.brand);
    output.writeFieldEnd();
  }
  if (this.manufacturer !== null && this.manufacturer !== undefined) {
    output.writeFieldBegin('manufacturer', Thrift.Type.STRING, 3);
    output.writeString(this.manufacturer);
    output.writeFieldEnd();
  }
  if (this.model !== null && this.model !== undefined) {
    output.writeFieldBegin('model', Thrift.Type.STRING, 4);
    output.writeString(this.model);
    output.writeFieldEnd();
  }
  if (this.board !== null && this.board !== undefined) {
    output.writeFieldBegin('board', Thrift.Type.STRING, 5);
    output.writeString(this.board);
    output.writeFieldEnd();
  }
  if (this.screenInfo !== null && this.screenInfo !== undefined) {
    output.writeFieldBegin('screenInfo', Thrift.Type.STRUCT, 6);
    this.screenInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.hardware !== null && this.hardware !== undefined) {
    output.writeFieldBegin('hardware', Thrift.Type.STRING, 7);
    output.writeString(this.hardware);
    output.writeFieldEnd();
  }
  if (this.notificationInfo !== null && this.notificationInfo !== undefined) {
    output.writeFieldBegin('notificationInfo', Thrift.Type.STRUCT, 8);
    this.notificationInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.appInfo !== null && this.appInfo !== undefined) {
    output.writeFieldBegin('appInfo', Thrift.Type.STRUCT, 9);
    this.appInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.inputInfo !== null && this.inputInfo !== undefined) {
    output.writeFieldBegin('inputInfo', Thrift.Type.STRUCT, 10);
    this.inputInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.locales !== null && this.locales !== undefined) {
    output.writeFieldBegin('locales', Thrift.Type.SET, 11);
    output.writeSetBegin(Thrift.Type.STRING, this.locales.length);
    for (var iter1187 in this.locales)
    {
      if (this.locales.hasOwnProperty(iter1187))
      {
        iter1187 = this.locales[iter1187];
        output.writeString(iter1187);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.ramMb !== null && this.ramMb !== undefined) {
    output.writeFieldBegin('ramMb', Thrift.Type.I32, 12);
    output.writeI32(this.ramMb);
    output.writeFieldEnd();
  }
  if (this.price !== null && this.price !== undefined) {
    output.writeFieldBegin('price', Thrift.Type.I32, 13);
    output.writeI32(this.price);
    output.writeFieldEnd();
  }
  if (this.sku !== null && this.sku !== undefined) {
    output.writeFieldBegin('sku', Thrift.Type.STRING, 14);
    output.writeString(this.sku);
    output.writeFieldEnd();
  }
  if (this.buildType !== null && this.buildType !== undefined) {
    output.writeFieldBegin('buildType', Thrift.Type.STRING, 15);
    output.writeString(this.buildType);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SpaceMetaData = function(args) {
  this.id = null;
  this.active = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.active !== undefined && args.active !== null) {
      this.active = args.active;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field active is unset!');
    }
  }
};
SpaceMetaData.prototype = {};
SpaceMetaData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.active = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SpaceMetaData.prototype.write = function(output) {
  output.writeStructBegin('SpaceMetaData');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.active !== null && this.active !== undefined) {
    output.writeFieldBegin('active', Thrift.Type.BOOL, 2);
    output.writeBool(this.active);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SpaceCard = function(args) {
  this.spaceId = null;
  this.enabled = null;
  this.name = null;
  this.metaData = null;
  if (args) {
    if (args.spaceId !== undefined && args.spaceId !== null) {
      this.spaceId = args.spaceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field spaceId is unset!');
    }
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field enabled is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.metaData !== undefined && args.metaData !== null) {
      this.metaData = Thrift.copyList(args.metaData, [SpaceMetaData]);
    }
  }
};
SpaceCard.prototype = {};
SpaceCard.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.spaceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size1188 = 0;
        var _rtmp31192;
        this.metaData = [];
        var _etype1191 = 0;
        _rtmp31192 = input.readListBegin();
        _etype1191 = _rtmp31192.etype;
        _size1188 = _rtmp31192.size;
        for (var _i1193 = 0; _i1193 < _size1188; ++_i1193)
        {
          var elem1194 = null;
          elem1194 = new SpaceMetaData();
          elem1194.read(input);
          this.metaData.push(elem1194);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SpaceCard.prototype.write = function(output) {
  output.writeStructBegin('SpaceCard');
  if (this.spaceId !== null && this.spaceId !== undefined) {
    output.writeFieldBegin('spaceId', Thrift.Type.STRING, 1);
    output.writeString(this.spaceId);
    output.writeFieldEnd();
  }
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 2);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 3);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.metaData !== null && this.metaData !== undefined) {
    output.writeFieldBegin('metaData', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRUCT, this.metaData.length);
    for (var iter1195 in this.metaData)
    {
      if (this.metaData.hasOwnProperty(iter1195))
      {
        iter1195 = this.metaData[iter1195];
        iter1195.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SpacePreferences = function(args) {
  this.spaces = null;
  if (args) {
    if (args.spaces !== undefined && args.spaces !== null) {
      this.spaces = Thrift.copyList(args.spaces, [SpaceCard]);
    }
  }
};
SpacePreferences.prototype = {};
SpacePreferences.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1196 = 0;
        var _rtmp31200;
        this.spaces = [];
        var _etype1199 = 0;
        _rtmp31200 = input.readListBegin();
        _etype1199 = _rtmp31200.etype;
        _size1196 = _rtmp31200.size;
        for (var _i1201 = 0; _i1201 < _size1196; ++_i1201)
        {
          var elem1202 = null;
          elem1202 = new SpaceCard();
          elem1202.read(input);
          this.spaces.push(elem1202);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SpacePreferences.prototype.write = function(output) {
  output.writeStructBegin('SpacePreferences');
  if (this.spaces !== null && this.spaces !== undefined) {
    output.writeFieldBegin('spaces', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.spaces.length);
    for (var iter1203 in this.spaces)
    {
      if (this.spaces.hasOwnProperty(iter1203))
      {
        iter1203 = this.spaces[iter1203];
        iter1203.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Geo = function(args) {
  this.latitude = null;
  this.longitude = null;
  this.city = null;
  if (args) {
    if (args.latitude !== undefined && args.latitude !== null) {
      this.latitude = args.latitude;
    }
    if (args.longitude !== undefined && args.longitude !== null) {
      this.longitude = args.longitude;
    }
    if (args.city !== undefined && args.city !== null) {
      this.city = args.city;
    }
  }
};
Geo.prototype = {};
Geo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.DOUBLE) {
        this.latitude = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.longitude = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.city = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Geo.prototype.write = function(output) {
  output.writeStructBegin('Geo');
  if (this.latitude !== null && this.latitude !== undefined) {
    output.writeFieldBegin('latitude', Thrift.Type.DOUBLE, 1);
    output.writeDouble(this.latitude);
    output.writeFieldEnd();
  }
  if (this.longitude !== null && this.longitude !== undefined) {
    output.writeFieldBegin('longitude', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.longitude);
    output.writeFieldEnd();
  }
  if (this.city !== null && this.city !== undefined) {
    output.writeFieldBegin('city', Thrift.Type.STRING, 3);
    output.writeString(this.city);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceOpportunities = function(args) {
  this.total = null;
  this.forGlance = null;
  if (args) {
    if (args.total !== undefined && args.total !== null) {
      this.total = args.total;
    }
    if (args.forGlance !== undefined && args.forGlance !== null) {
      this.forGlance = args.forGlance;
    }
  }
};
GlanceOpportunities.prototype = {};
GlanceOpportunities.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.total = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.forGlance = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceOpportunities.prototype.write = function(output) {
  output.writeStructBegin('GlanceOpportunities');
  if (this.total !== null && this.total !== undefined) {
    output.writeFieldBegin('total', Thrift.Type.I32, 1);
    output.writeI32(this.total);
    output.writeFieldEnd();
  }
  if (this.forGlance !== null && this.forGlance !== undefined) {
    output.writeFieldBegin('forGlance', Thrift.Type.I32, 2);
    output.writeI32(this.forGlance);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UserExtras = function(args) {
  this.notificationsEnabled = null;
  this.glanceOpportunities = null;
  this.eulaDevNet = null;
  if (args) {
    if (args.notificationsEnabled !== undefined && args.notificationsEnabled !== null) {
      this.notificationsEnabled = args.notificationsEnabled;
    }
    if (args.glanceOpportunities !== undefined && args.glanceOpportunities !== null) {
      this.glanceOpportunities = new GlanceOpportunities(args.glanceOpportunities);
    }
    if (args.eulaDevNet !== undefined && args.eulaDevNet !== null) {
      this.eulaDevNet = args.eulaDevNet;
    }
  }
};
UserExtras.prototype = {};
UserExtras.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.notificationsEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceOpportunities = new GlanceOpportunities();
        this.glanceOpportunities.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.eulaDevNet = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserExtras.prototype.write = function(output) {
  output.writeStructBegin('UserExtras');
  if (this.notificationsEnabled !== null && this.notificationsEnabled !== undefined) {
    output.writeFieldBegin('notificationsEnabled', Thrift.Type.BOOL, 1);
    output.writeBool(this.notificationsEnabled);
    output.writeFieldEnd();
  }
  if (this.glanceOpportunities !== null && this.glanceOpportunities !== undefined) {
    output.writeFieldBegin('glanceOpportunities', Thrift.Type.STRUCT, 2);
    this.glanceOpportunities.write(output);
    output.writeFieldEnd();
  }
  if (this.eulaDevNet !== null && this.eulaDevNet !== undefined) {
    output.writeFieldBegin('eulaDevNet', Thrift.Type.I32, 3);
    output.writeI32(this.eulaDevNet);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SystemFlags = function(args) {
  this.lehuaStatus = null;
  this.wallpaperStatus = null;
  this.glanceStatus = null;
  if (args) {
    if (args.lehuaStatus !== undefined && args.lehuaStatus !== null) {
      this.lehuaStatus = args.lehuaStatus;
    }
    if (args.wallpaperStatus !== undefined && args.wallpaperStatus !== null) {
      this.wallpaperStatus = args.wallpaperStatus;
    }
    if (args.glanceStatus !== undefined && args.glanceStatus !== null) {
      this.glanceStatus = args.glanceStatus;
    }
  }
};
SystemFlags.prototype = {};
SystemFlags.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.lehuaStatus = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.wallpaperStatus = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.glanceStatus = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SystemFlags.prototype.write = function(output) {
  output.writeStructBegin('SystemFlags');
  if (this.lehuaStatus !== null && this.lehuaStatus !== undefined) {
    output.writeFieldBegin('lehuaStatus', Thrift.Type.BOOL, 1);
    output.writeBool(this.lehuaStatus);
    output.writeFieldEnd();
  }
  if (this.wallpaperStatus !== null && this.wallpaperStatus !== undefined) {
    output.writeFieldBegin('wallpaperStatus', Thrift.Type.BOOL, 2);
    output.writeBool(this.wallpaperStatus);
    output.writeFieldEnd();
  }
  if (this.glanceStatus !== null && this.glanceStatus !== undefined) {
    output.writeFieldBegin('glanceStatus', Thrift.Type.BOOL, 3);
    output.writeBool(this.glanceStatus);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Segment = function(args) {
  this.id = null;
  this.ingestionTime = null;
  this.validForDays = null;
  this.source = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.ingestionTime !== undefined && args.ingestionTime !== null) {
      this.ingestionTime = args.ingestionTime;
    }
    if (args.validForDays !== undefined && args.validForDays !== null) {
      this.validForDays = args.validForDays;
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    }
  }
};
Segment.prototype = {};
Segment.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.id = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.ingestionTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.validForDays = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Segment.prototype.write = function(output) {
  output.writeStructBegin('Segment');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I32, 1);
    output.writeI32(this.id);
    output.writeFieldEnd();
  }
  if (this.ingestionTime !== null && this.ingestionTime !== undefined) {
    output.writeFieldBegin('ingestionTime', Thrift.Type.I64, 2);
    output.writeI64(this.ingestionTime);
    output.writeFieldEnd();
  }
  if (this.validForDays !== null && this.validForDays !== undefined) {
    output.writeFieldBegin('validForDays', Thrift.Type.I64, 3);
    output.writeI64(this.validForDays);
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.STRING, 4);
    output.writeString(this.source);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

LanguageSubscription = function(args) {
  this.glanceLanguageId = null;
  this.subscriptionType = null;
  this.updatedAtInSecs = null;
  if (args) {
    if (args.glanceLanguageId !== undefined && args.glanceLanguageId !== null) {
      this.glanceLanguageId = args.glanceLanguageId;
    }
    if (args.subscriptionType !== undefined && args.subscriptionType !== null) {
      this.subscriptionType = args.subscriptionType;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
  }
};
LanguageSubscription.prototype = {};
LanguageSubscription.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceLanguageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.subscriptionType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

LanguageSubscription.prototype.write = function(output) {
  output.writeStructBegin('LanguageSubscription');
  if (this.glanceLanguageId !== null && this.glanceLanguageId !== undefined) {
    output.writeFieldBegin('glanceLanguageId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceLanguageId);
    output.writeFieldEnd();
  }
  if (this.subscriptionType !== null && this.subscriptionType !== undefined) {
    output.writeFieldBegin('subscriptionType', Thrift.Type.I32, 2);
    output.writeI32(this.subscriptionType);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 3);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UserEngagement = function(args) {
  this.lastPeekedInSecs = null;
  this.firstGlanceSeenInSecs = null;
  if (args) {
    if (args.lastPeekedInSecs !== undefined && args.lastPeekedInSecs !== null) {
      this.lastPeekedInSecs = args.lastPeekedInSecs;
    }
    if (args.firstGlanceSeenInSecs !== undefined && args.firstGlanceSeenInSecs !== null) {
      this.firstGlanceSeenInSecs = args.firstGlanceSeenInSecs;
    }
  }
};
UserEngagement.prototype = {};
UserEngagement.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.lastPeekedInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.firstGlanceSeenInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserEngagement.prototype.write = function(output) {
  output.writeStructBegin('UserEngagement');
  if (this.lastPeekedInSecs !== null && this.lastPeekedInSecs !== undefined) {
    output.writeFieldBegin('lastPeekedInSecs', Thrift.Type.I64, 1);
    output.writeI64(this.lastPeekedInSecs);
    output.writeFieldEnd();
  }
  if (this.firstGlanceSeenInSecs !== null && this.firstGlanceSeenInSecs !== undefined) {
    output.writeFieldBegin('firstGlanceSeenInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.firstGlanceSeenInSecs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

HighlightsDetails = function(args) {
  this.enabled = null;
  if (args) {
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    }
  }
};
HighlightsDetails.prototype = {};
HighlightsDetails.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

HighlightsDetails.prototype.write = function(output) {
  output.writeStructBegin('HighlightsDetails');
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 1);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SdkFeatures = function(args) {
  this.rewardsMvp = null;
  this.rewardsSurveys = null;
  this.dynamicProfile = null;
  if (args) {
    if (args.rewardsMvp !== undefined && args.rewardsMvp !== null) {
      this.rewardsMvp = args.rewardsMvp;
    }
    if (args.rewardsSurveys !== undefined && args.rewardsSurveys !== null) {
      this.rewardsSurveys = args.rewardsSurveys;
    }
    if (args.dynamicProfile !== undefined && args.dynamicProfile !== null) {
      this.dynamicProfile = args.dynamicProfile;
    }
  }
};
SdkFeatures.prototype = {};
SdkFeatures.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.rewardsMvp = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.rewardsSurveys = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.dynamicProfile = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SdkFeatures.prototype.write = function(output) {
  output.writeStructBegin('SdkFeatures');
  if (this.rewardsMvp !== null && this.rewardsMvp !== undefined) {
    output.writeFieldBegin('rewardsMvp', Thrift.Type.BOOL, 1);
    output.writeBool(this.rewardsMvp);
    output.writeFieldEnd();
  }
  if (this.rewardsSurveys !== null && this.rewardsSurveys !== undefined) {
    output.writeFieldBegin('rewardsSurveys', Thrift.Type.BOOL, 2);
    output.writeBool(this.rewardsSurveys);
    output.writeFieldEnd();
  }
  if (this.dynamicProfile !== null && this.dynamicProfile !== undefined) {
    output.writeFieldBegin('dynamicProfile', Thrift.Type.BOOL, 3);
    output.writeBool(this.dynamicProfile);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppLocale = function(args) {
  this.currentAppLocale = null;
  this.appLocaleSet = null;
  if (args) {
    if (args.currentAppLocale !== undefined && args.currentAppLocale !== null) {
      this.currentAppLocale = args.currentAppLocale;
    }
    if (args.appLocaleSet !== undefined && args.appLocaleSet !== null) {
      this.appLocaleSet = Thrift.copyList(args.appLocaleSet, [null]);
    }
  }
};
AppLocale.prototype = {};
AppLocale.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.currentAppLocale = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.SET) {
        var _size1204 = 0;
        var _rtmp31208;
        this.appLocaleSet = [];
        var _etype1207 = 0;
        _rtmp31208 = input.readSetBegin();
        _etype1207 = _rtmp31208.etype;
        _size1204 = _rtmp31208.size;
        for (var _i1209 = 0; _i1209 < _size1204; ++_i1209)
        {
          var elem1210 = null;
          elem1210 = input.readString().value;
          this.appLocaleSet.push(elem1210);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppLocale.prototype.write = function(output) {
  output.writeStructBegin('AppLocale');
  if (this.currentAppLocale !== null && this.currentAppLocale !== undefined) {
    output.writeFieldBegin('currentAppLocale', Thrift.Type.STRING, 1);
    output.writeString(this.currentAppLocale);
    output.writeFieldEnd();
  }
  if (this.appLocaleSet !== null && this.appLocaleSet !== undefined) {
    output.writeFieldBegin('appLocaleSet', Thrift.Type.SET, 2);
    output.writeSetBegin(Thrift.Type.STRING, this.appLocaleSet.length);
    for (var iter1211 in this.appLocaleSet)
    {
      if (this.appLocaleSet.hasOwnProperty(iter1211))
      {
        iter1211 = this.appLocaleSet[iter1211];
        output.writeString(iter1211);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UserStated = function(args) {
  this.userStated = null;
  if (args) {
    if (args.userStated !== undefined && args.userStated !== null) {
      this.userStated = Thrift.copyMap(args.userStated, [null]);
    }
  }
};
UserStated.prototype = {};
UserStated.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.MAP) {
        var _size1212 = 0;
        var _rtmp31216;
        this.userStated = {};
        var _ktype1213 = 0;
        var _vtype1214 = 0;
        _rtmp31216 = input.readMapBegin();
        _ktype1213 = _rtmp31216.ktype;
        _vtype1214 = _rtmp31216.vtype;
        _size1212 = _rtmp31216.size;
        for (var _i1217 = 0; _i1217 < _size1212; ++_i1217)
        {
          if (_i1217 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key1218 = null;
          var val1219 = null;
          key1218 = input.readString().value;
          val1219 = input.readString().value;
          this.userStated[key1218] = val1219;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserStated.prototype.write = function(output) {
  output.writeStructBegin('UserStated');
  if (this.userStated !== null && this.userStated !== undefined) {
    output.writeFieldBegin('userStated', Thrift.Type.MAP, 1);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.STRING, Thrift.objectLength(this.userStated));
    for (var kiter1220 in this.userStated)
    {
      if (this.userStated.hasOwnProperty(kiter1220))
      {
        var viter1221 = this.userStated[kiter1220];
        output.writeString(kiter1220);
        output.writeString(viter1221);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

LocationInfo = function(args) {
  this.userInputCityId = null;
  this.userInputStateId = null;
  if (args) {
    if (args.userInputCityId !== undefined && args.userInputCityId !== null) {
      this.userInputCityId = args.userInputCityId;
    }
    if (args.userInputStateId !== undefined && args.userInputStateId !== null) {
      this.userInputStateId = args.userInputStateId;
    }
  }
};
LocationInfo.prototype = {};
LocationInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.userInputCityId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.userInputStateId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

LocationInfo.prototype.write = function(output) {
  output.writeStructBegin('LocationInfo');
  if (this.userInputCityId !== null && this.userInputCityId !== undefined) {
    output.writeFieldBegin('userInputCityId', Thrift.Type.I64, 1);
    output.writeI64(this.userInputCityId);
    output.writeFieldEnd();
  }
  if (this.userInputStateId !== null && this.userInputStateId !== undefined) {
    output.writeFieldBegin('userInputStateId', Thrift.Type.I64, 2);
    output.writeI64(this.userInputStateId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

User = function(args) {
  this.userId = null;
  this.apiKey = null;
  this.partnerId = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.deviceInfo = null;
  this.eulaAcceptedState = null;
  this.eulaAcceptedStateUpdatedAtInSecs = null;
  this.enabledState = null;
  this.enabledStateUpdatedAtInSecs = null;
  this.gpId = null;
  this.subscriptions = null;
  this.preferredNetworkType = null;
  this.extras = null;
  this.preferredCategories = null;
  this.version = null;
  this.deviceId = null;
  this.disableActionType = null;
  this.enableReferrer = null;
  this.adPersonalizationEnabled = null;
  this.firstEnableReferrer = null;
  this.clientVersion = null;
  this.subscribedCategories = null;
  this.segments = null;
  this.showRecommendations = null;
  this.firstEnableAtInSecs = null;
  this.inmobiAdOptOut = null;
  this.categoryOptOut = null;
  this.languageSubscriptions = null;
  this.id = null;
  this.nickname = null;
  this.sdkVersion = null;
  this.state = null;
  this.city = null;
  this.region = null;
  this.locale = null;
  this.dynamicSegments = null;
  this.userEngagement = null;
  this.fcmToken = null;
  this.dataSaverEnabled = null;
  this.ownedAppPackageNames = null;
  this.resetCacheAtInSecs = null;
  this.highlightsDetails = null;
  this.userStated = null;
  this.sdkFeatures = null;
  this.isChildLockEnabled = null;
  this.childLockTimeLimit = null;
  this.secondaryFcmToken = null;
  this.monthAndYearOfBirth = null;
  this.locationInfo = null;
  this.isUnderAge = null;
  this.appLocale = null;
  this.systemFlags = null;
  this.systemFlagsUpdatedAtInSecs = null;
  this.reactivationUpdatedAtInSecs = null;
  this.optOutOfSale = null;
  this.tappableTaglineState = null;
  this.lockscreenState = null;
  this.systemUiVersion = null;
  this.age = null;
  this.gender = null;
  if (args) {
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    }
    if (args.apiKey !== undefined && args.apiKey !== null) {
      this.apiKey = args.apiKey;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field apiKey is unset!');
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partnerId is unset!');
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field createdAtInSecs is unset!');
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field updatedAtInSecs is unset!');
    }
    if (args.deviceInfo !== undefined && args.deviceInfo !== null) {
      this.deviceInfo = new DeviceInfo(args.deviceInfo);
    }
    if (args.eulaAcceptedState !== undefined && args.eulaAcceptedState !== null) {
      this.eulaAcceptedState = args.eulaAcceptedState;
    }
    if (args.eulaAcceptedStateUpdatedAtInSecs !== undefined && args.eulaAcceptedStateUpdatedAtInSecs !== null) {
      this.eulaAcceptedStateUpdatedAtInSecs = args.eulaAcceptedStateUpdatedAtInSecs;
    }
    if (args.enabledState !== undefined && args.enabledState !== null) {
      this.enabledState = args.enabledState;
    }
    if (args.enabledStateUpdatedAtInSecs !== undefined && args.enabledStateUpdatedAtInSecs !== null) {
      this.enabledStateUpdatedAtInSecs = args.enabledStateUpdatedAtInSecs;
    }
    if (args.gpId !== undefined && args.gpId !== null) {
      this.gpId = args.gpId;
    }
    if (args.subscriptions !== undefined && args.subscriptions !== null) {
      this.subscriptions = Thrift.copyList(args.subscriptions, [SubscriptionMeta]);
    }
    if (args.preferredNetworkType !== undefined && args.preferredNetworkType !== null) {
      this.preferredNetworkType = args.preferredNetworkType;
    }
    if (args.extras !== undefined && args.extras !== null) {
      this.extras = new UserExtras(args.extras);
    }
    if (args.preferredCategories !== undefined && args.preferredCategories !== null) {
      this.preferredCategories = Thrift.copyList(args.preferredCategories, [null]);
    }
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
    if (args.deviceId !== undefined && args.deviceId !== null) {
      this.deviceId = args.deviceId;
    }
    if (args.disableActionType !== undefined && args.disableActionType !== null) {
      this.disableActionType = args.disableActionType;
    }
    if (args.enableReferrer !== undefined && args.enableReferrer !== null) {
      this.enableReferrer = args.enableReferrer;
    }
    if (args.adPersonalizationEnabled !== undefined && args.adPersonalizationEnabled !== null) {
      this.adPersonalizationEnabled = args.adPersonalizationEnabled;
    }
    if (args.firstEnableReferrer !== undefined && args.firstEnableReferrer !== null) {
      this.firstEnableReferrer = args.firstEnableReferrer;
    }
    if (args.clientVersion !== undefined && args.clientVersion !== null) {
      this.clientVersion = args.clientVersion;
    }
    if (args.subscribedCategories !== undefined && args.subscribedCategories !== null) {
      this.subscribedCategories = Thrift.copyMap(args.subscribedCategories, [null]);
    }
    if (args.segments !== undefined && args.segments !== null) {
      this.segments = Thrift.copyList(args.segments, [Segment]);
    }
    if (args.showRecommendations !== undefined && args.showRecommendations !== null) {
      this.showRecommendations = args.showRecommendations;
    }
    if (args.firstEnableAtInSecs !== undefined && args.firstEnableAtInSecs !== null) {
      this.firstEnableAtInSecs = args.firstEnableAtInSecs;
    }
    if (args.inmobiAdOptOut !== undefined && args.inmobiAdOptOut !== null) {
      this.inmobiAdOptOut = args.inmobiAdOptOut;
    }
    if (args.categoryOptOut !== undefined && args.categoryOptOut !== null) {
      this.categoryOptOut = args.categoryOptOut;
    }
    if (args.languageSubscriptions !== undefined && args.languageSubscriptions !== null) {
      this.languageSubscriptions = Thrift.copyList(args.languageSubscriptions, [LanguageSubscription]);
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.nickname !== undefined && args.nickname !== null) {
      this.nickname = args.nickname;
    }
    if (args.sdkVersion !== undefined && args.sdkVersion !== null) {
      this.sdkVersion = args.sdkVersion;
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    }
    if (args.city !== undefined && args.city !== null) {
      this.city = args.city;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.locale !== undefined && args.locale !== null) {
      this.locale = args.locale;
    }
    if (args.dynamicSegments !== undefined && args.dynamicSegments !== null) {
      this.dynamicSegments = Thrift.copyList(args.dynamicSegments, [null]);
    }
    if (args.userEngagement !== undefined && args.userEngagement !== null) {
      this.userEngagement = new UserEngagement(args.userEngagement);
    }
    if (args.fcmToken !== undefined && args.fcmToken !== null) {
      this.fcmToken = args.fcmToken;
    }
    if (args.dataSaverEnabled !== undefined && args.dataSaverEnabled !== null) {
      this.dataSaverEnabled = args.dataSaverEnabled;
    }
    if (args.ownedAppPackageNames !== undefined && args.ownedAppPackageNames !== null) {
      this.ownedAppPackageNames = Thrift.copyList(args.ownedAppPackageNames, [null]);
    }
    if (args.resetCacheAtInSecs !== undefined && args.resetCacheAtInSecs !== null) {
      this.resetCacheAtInSecs = args.resetCacheAtInSecs;
    }
    if (args.highlightsDetails !== undefined && args.highlightsDetails !== null) {
      this.highlightsDetails = new HighlightsDetails(args.highlightsDetails);
    }
    if (args.userStated !== undefined && args.userStated !== null) {
      this.userStated = new UserStated(args.userStated);
    }
    if (args.sdkFeatures !== undefined && args.sdkFeatures !== null) {
      this.sdkFeatures = new SdkFeatures(args.sdkFeatures);
    }
    if (args.isChildLockEnabled !== undefined && args.isChildLockEnabled !== null) {
      this.isChildLockEnabled = args.isChildLockEnabled;
    }
    if (args.childLockTimeLimit !== undefined && args.childLockTimeLimit !== null) {
      this.childLockTimeLimit = args.childLockTimeLimit;
    }
    if (args.secondaryFcmToken !== undefined && args.secondaryFcmToken !== null) {
      this.secondaryFcmToken = args.secondaryFcmToken;
    }
    if (args.monthAndYearOfBirth !== undefined && args.monthAndYearOfBirth !== null) {
      this.monthAndYearOfBirth = args.monthAndYearOfBirth;
    }
    if (args.locationInfo !== undefined && args.locationInfo !== null) {
      this.locationInfo = new LocationInfo(args.locationInfo);
    }
    if (args.isUnderAge !== undefined && args.isUnderAge !== null) {
      this.isUnderAge = args.isUnderAge;
    }
    if (args.appLocale !== undefined && args.appLocale !== null) {
      this.appLocale = new AppLocale(args.appLocale);
    }
    if (args.systemFlags !== undefined && args.systemFlags !== null) {
      this.systemFlags = new SystemFlags(args.systemFlags);
    }
    if (args.systemFlagsUpdatedAtInSecs !== undefined && args.systemFlagsUpdatedAtInSecs !== null) {
      this.systemFlagsUpdatedAtInSecs = args.systemFlagsUpdatedAtInSecs;
    }
    if (args.reactivationUpdatedAtInSecs !== undefined && args.reactivationUpdatedAtInSecs !== null) {
      this.reactivationUpdatedAtInSecs = args.reactivationUpdatedAtInSecs;
    }
    if (args.optOutOfSale !== undefined && args.optOutOfSale !== null) {
      this.optOutOfSale = args.optOutOfSale;
    }
    if (args.tappableTaglineState !== undefined && args.tappableTaglineState !== null) {
      this.tappableTaglineState = args.tappableTaglineState;
    }
    if (args.lockscreenState !== undefined && args.lockscreenState !== null) {
      this.lockscreenState = args.lockscreenState;
    }
    if (args.systemUiVersion !== undefined && args.systemUiVersion !== null) {
      this.systemUiVersion = args.systemUiVersion;
    }
    if (args.age !== undefined && args.age !== null) {
      this.age = args.age;
    }
    if (args.gender !== undefined && args.gender !== null) {
      this.gender = args.gender;
    }
  }
};
User.prototype = {};
User.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.apiKey = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.deviceInfo = new DeviceInfo();
        this.deviceInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.eulaAcceptedState = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I64) {
        this.eulaAcceptedStateUpdatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.BOOL) {
        this.enabledState = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I64) {
        this.enabledStateUpdatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.gpId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size1222 = 0;
        var _rtmp31226;
        this.subscriptions = [];
        var _etype1225 = 0;
        _rtmp31226 = input.readListBegin();
        _etype1225 = _rtmp31226.etype;
        _size1222 = _rtmp31226.size;
        for (var _i1227 = 0; _i1227 < _size1222; ++_i1227)
        {
          var elem1228 = null;
          elem1228 = new SubscriptionMeta();
          elem1228.read(input);
          this.subscriptions.push(elem1228);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.I32) {
        this.preferredNetworkType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRUCT) {
        this.extras = new UserExtras();
        this.extras.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.LIST) {
        var _size1229 = 0;
        var _rtmp31233;
        this.preferredCategories = [];
        var _etype1232 = 0;
        _rtmp31233 = input.readListBegin();
        _etype1232 = _rtmp31233.etype;
        _size1229 = _rtmp31233.size;
        for (var _i1234 = 0; _i1234 < _size1229; ++_i1234)
        {
          var elem1235 = null;
          elem1235 = input.readString().value;
          this.preferredCategories.push(elem1235);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.I64) {
        this.version = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.deviceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.disableActionType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.enableReferrer = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.BOOL) {
        this.adPersonalizationEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRING) {
        this.firstEnableReferrer = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.clientVersion = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.MAP) {
        var _size1236 = 0;
        var _rtmp31240;
        this.subscribedCategories = {};
        var _ktype1237 = 0;
        var _vtype1238 = 0;
        _rtmp31240 = input.readMapBegin();
        _ktype1237 = _rtmp31240.ktype;
        _vtype1238 = _rtmp31240.vtype;
        _size1236 = _rtmp31240.size;
        for (var _i1241 = 0; _i1241 < _size1236; ++_i1241)
        {
          if (_i1241 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key1242 = null;
          var val1243 = null;
          key1242 = input.readString().value;
          val1243 = input.readI32().value;
          this.subscribedCategories[key1242] = val1243;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.LIST) {
        var _size1244 = 0;
        var _rtmp31248;
        this.segments = [];
        var _etype1247 = 0;
        _rtmp31248 = input.readListBegin();
        _etype1247 = _rtmp31248.etype;
        _size1244 = _rtmp31248.size;
        for (var _i1249 = 0; _i1249 < _size1244; ++_i1249)
        {
          var elem1250 = null;
          elem1250 = new Segment();
          elem1250.read(input);
          this.segments.push(elem1250);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.BOOL) {
        this.showRecommendations = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.I64) {
        this.firstEnableAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.BOOL) {
        this.inmobiAdOptOut = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.BOOL) {
        this.categoryOptOut = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.LIST) {
        var _size1251 = 0;
        var _rtmp31255;
        this.languageSubscriptions = [];
        var _etype1254 = 0;
        _rtmp31255 = input.readListBegin();
        _etype1254 = _rtmp31255.etype;
        _size1251 = _rtmp31255.size;
        for (var _i1256 = 0; _i1256 < _size1251; ++_i1256)
        {
          var elem1257 = null;
          elem1257 = new LanguageSubscription();
          elem1257.read(input);
          this.languageSubscriptions.push(elem1257);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRING) {
        this.nickname = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.I64) {
        this.sdkVersion = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.I64) {
        this.state = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.I64) {
        this.city = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 36:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 37:
      if (ftype == Thrift.Type.STRING) {
        this.locale = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 38:
      if (ftype == Thrift.Type.SET) {
        var _size1258 = 0;
        var _rtmp31262;
        this.dynamicSegments = [];
        var _etype1261 = 0;
        _rtmp31262 = input.readSetBegin();
        _etype1261 = _rtmp31262.etype;
        _size1258 = _rtmp31262.size;
        for (var _i1263 = 0; _i1263 < _size1258; ++_i1263)
        {
          var elem1264 = null;
          elem1264 = input.readString().value;
          this.dynamicSegments.push(elem1264);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 39:
      if (ftype == Thrift.Type.STRUCT) {
        this.userEngagement = new UserEngagement();
        this.userEngagement.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 40:
      if (ftype == Thrift.Type.STRING) {
        this.fcmToken = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 41:
      if (ftype == Thrift.Type.BOOL) {
        this.dataSaverEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 42:
      if (ftype == Thrift.Type.SET) {
        var _size1265 = 0;
        var _rtmp31269;
        this.ownedAppPackageNames = [];
        var _etype1268 = 0;
        _rtmp31269 = input.readSetBegin();
        _etype1268 = _rtmp31269.etype;
        _size1265 = _rtmp31269.size;
        for (var _i1270 = 0; _i1270 < _size1265; ++_i1270)
        {
          var elem1271 = null;
          elem1271 = input.readString().value;
          this.ownedAppPackageNames.push(elem1271);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 43:
      if (ftype == Thrift.Type.I64) {
        this.resetCacheAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 44:
      if (ftype == Thrift.Type.STRUCT) {
        this.highlightsDetails = new HighlightsDetails();
        this.highlightsDetails.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 45:
      if (ftype == Thrift.Type.STRUCT) {
        this.userStated = new UserStated();
        this.userStated.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 46:
      if (ftype == Thrift.Type.STRUCT) {
        this.sdkFeatures = new SdkFeatures();
        this.sdkFeatures.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 47:
      if (ftype == Thrift.Type.BOOL) {
        this.isChildLockEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 48:
      if (ftype == Thrift.Type.I64) {
        this.childLockTimeLimit = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 49:
      if (ftype == Thrift.Type.STRING) {
        this.secondaryFcmToken = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 50:
      if (ftype == Thrift.Type.STRING) {
        this.monthAndYearOfBirth = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 51:
      if (ftype == Thrift.Type.STRUCT) {
        this.locationInfo = new LocationInfo();
        this.locationInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 52:
      if (ftype == Thrift.Type.BOOL) {
        this.isUnderAge = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 53:
      if (ftype == Thrift.Type.STRUCT) {
        this.appLocale = new AppLocale();
        this.appLocale.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 54:
      if (ftype == Thrift.Type.STRUCT) {
        this.systemFlags = new SystemFlags();
        this.systemFlags.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 55:
      if (ftype == Thrift.Type.I64) {
        this.systemFlagsUpdatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 56:
      if (ftype == Thrift.Type.I64) {
        this.reactivationUpdatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 57:
      if (ftype == Thrift.Type.BOOL) {
        this.optOutOfSale = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 58:
      if (ftype == Thrift.Type.BOOL) {
        this.tappableTaglineState = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 59:
      if (ftype == Thrift.Type.STRING) {
        this.lockscreenState = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 60:
      if (ftype == Thrift.Type.STRING) {
        this.systemUiVersion = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 61:
      if (ftype == Thrift.Type.STRING) {
        this.age = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 62:
      if (ftype == Thrift.Type.STRING) {
        this.gender = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

User.prototype.write = function(output) {
  output.writeStructBegin('User');
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 1);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.apiKey !== null && this.apiKey !== undefined) {
    output.writeFieldBegin('apiKey', Thrift.Type.STRING, 2);
    output.writeString(this.apiKey);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 3);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.deviceInfo !== null && this.deviceInfo !== undefined) {
    output.writeFieldBegin('deviceInfo', Thrift.Type.STRUCT, 6);
    this.deviceInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.eulaAcceptedState !== null && this.eulaAcceptedState !== undefined) {
    output.writeFieldBegin('eulaAcceptedState', Thrift.Type.BOOL, 7);
    output.writeBool(this.eulaAcceptedState);
    output.writeFieldEnd();
  }
  if (this.eulaAcceptedStateUpdatedAtInSecs !== null && this.eulaAcceptedStateUpdatedAtInSecs !== undefined) {
    output.writeFieldBegin('eulaAcceptedStateUpdatedAtInSecs', Thrift.Type.I64, 8);
    output.writeI64(this.eulaAcceptedStateUpdatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.enabledState !== null && this.enabledState !== undefined) {
    output.writeFieldBegin('enabledState', Thrift.Type.BOOL, 9);
    output.writeBool(this.enabledState);
    output.writeFieldEnd();
  }
  if (this.enabledStateUpdatedAtInSecs !== null && this.enabledStateUpdatedAtInSecs !== undefined) {
    output.writeFieldBegin('enabledStateUpdatedAtInSecs', Thrift.Type.I64, 10);
    output.writeI64(this.enabledStateUpdatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.gpId !== null && this.gpId !== undefined) {
    output.writeFieldBegin('gpId', Thrift.Type.STRING, 12);
    output.writeString(this.gpId);
    output.writeFieldEnd();
  }
  if (this.subscriptions !== null && this.subscriptions !== undefined) {
    output.writeFieldBegin('subscriptions', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRUCT, this.subscriptions.length);
    for (var iter1272 in this.subscriptions)
    {
      if (this.subscriptions.hasOwnProperty(iter1272))
      {
        iter1272 = this.subscriptions[iter1272];
        iter1272.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.preferredNetworkType !== null && this.preferredNetworkType !== undefined) {
    output.writeFieldBegin('preferredNetworkType', Thrift.Type.I32, 14);
    output.writeI32(this.preferredNetworkType);
    output.writeFieldEnd();
  }
  if (this.extras !== null && this.extras !== undefined) {
    output.writeFieldBegin('extras', Thrift.Type.STRUCT, 15);
    this.extras.write(output);
    output.writeFieldEnd();
  }
  if (this.preferredCategories !== null && this.preferredCategories !== undefined) {
    output.writeFieldBegin('preferredCategories', Thrift.Type.LIST, 16);
    output.writeListBegin(Thrift.Type.STRING, this.preferredCategories.length);
    for (var iter1273 in this.preferredCategories)
    {
      if (this.preferredCategories.hasOwnProperty(iter1273))
      {
        iter1273 = this.preferredCategories[iter1273];
        output.writeString(iter1273);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.I64, 17);
    output.writeI64(this.version);
    output.writeFieldEnd();
  }
  if (this.deviceId !== null && this.deviceId !== undefined) {
    output.writeFieldBegin('deviceId', Thrift.Type.STRING, 18);
    output.writeString(this.deviceId);
    output.writeFieldEnd();
  }
  if (this.disableActionType !== null && this.disableActionType !== undefined) {
    output.writeFieldBegin('disableActionType', Thrift.Type.STRING, 19);
    output.writeString(this.disableActionType);
    output.writeFieldEnd();
  }
  if (this.enableReferrer !== null && this.enableReferrer !== undefined) {
    output.writeFieldBegin('enableReferrer', Thrift.Type.STRING, 20);
    output.writeString(this.enableReferrer);
    output.writeFieldEnd();
  }
  if (this.adPersonalizationEnabled !== null && this.adPersonalizationEnabled !== undefined) {
    output.writeFieldBegin('adPersonalizationEnabled', Thrift.Type.BOOL, 21);
    output.writeBool(this.adPersonalizationEnabled);
    output.writeFieldEnd();
  }
  if (this.firstEnableReferrer !== null && this.firstEnableReferrer !== undefined) {
    output.writeFieldBegin('firstEnableReferrer', Thrift.Type.STRING, 22);
    output.writeString(this.firstEnableReferrer);
    output.writeFieldEnd();
  }
  if (this.clientVersion !== null && this.clientVersion !== undefined) {
    output.writeFieldBegin('clientVersion', Thrift.Type.STRING, 23);
    output.writeString(this.clientVersion);
    output.writeFieldEnd();
  }
  if (this.subscribedCategories !== null && this.subscribedCategories !== undefined) {
    output.writeFieldBegin('subscribedCategories', Thrift.Type.MAP, 24);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.I32, Thrift.objectLength(this.subscribedCategories));
    for (var kiter1274 in this.subscribedCategories)
    {
      if (this.subscribedCategories.hasOwnProperty(kiter1274))
      {
        var viter1275 = this.subscribedCategories[kiter1274];
        output.writeString(kiter1274);
        output.writeI32(viter1275);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.segments !== null && this.segments !== undefined) {
    output.writeFieldBegin('segments', Thrift.Type.LIST, 25);
    output.writeListBegin(Thrift.Type.STRUCT, this.segments.length);
    for (var iter1276 in this.segments)
    {
      if (this.segments.hasOwnProperty(iter1276))
      {
        iter1276 = this.segments[iter1276];
        iter1276.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.showRecommendations !== null && this.showRecommendations !== undefined) {
    output.writeFieldBegin('showRecommendations', Thrift.Type.BOOL, 26);
    output.writeBool(this.showRecommendations);
    output.writeFieldEnd();
  }
  if (this.firstEnableAtInSecs !== null && this.firstEnableAtInSecs !== undefined) {
    output.writeFieldBegin('firstEnableAtInSecs', Thrift.Type.I64, 27);
    output.writeI64(this.firstEnableAtInSecs);
    output.writeFieldEnd();
  }
  if (this.inmobiAdOptOut !== null && this.inmobiAdOptOut !== undefined) {
    output.writeFieldBegin('inmobiAdOptOut', Thrift.Type.BOOL, 28);
    output.writeBool(this.inmobiAdOptOut);
    output.writeFieldEnd();
  }
  if (this.categoryOptOut !== null && this.categoryOptOut !== undefined) {
    output.writeFieldBegin('categoryOptOut', Thrift.Type.BOOL, 29);
    output.writeBool(this.categoryOptOut);
    output.writeFieldEnd();
  }
  if (this.languageSubscriptions !== null && this.languageSubscriptions !== undefined) {
    output.writeFieldBegin('languageSubscriptions', Thrift.Type.LIST, 30);
    output.writeListBegin(Thrift.Type.STRUCT, this.languageSubscriptions.length);
    for (var iter1277 in this.languageSubscriptions)
    {
      if (this.languageSubscriptions.hasOwnProperty(iter1277))
      {
        iter1277 = this.languageSubscriptions[iter1277];
        iter1277.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 31);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.nickname !== null && this.nickname !== undefined) {
    output.writeFieldBegin('nickname', Thrift.Type.STRING, 32);
    output.writeString(this.nickname);
    output.writeFieldEnd();
  }
  if (this.sdkVersion !== null && this.sdkVersion !== undefined) {
    output.writeFieldBegin('sdkVersion', Thrift.Type.I64, 33);
    output.writeI64(this.sdkVersion);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.I64, 34);
    output.writeI64(this.state);
    output.writeFieldEnd();
  }
  if (this.city !== null && this.city !== undefined) {
    output.writeFieldBegin('city', Thrift.Type.I64, 35);
    output.writeI64(this.city);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 36);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.locale !== null && this.locale !== undefined) {
    output.writeFieldBegin('locale', Thrift.Type.STRING, 37);
    output.writeString(this.locale);
    output.writeFieldEnd();
  }
  if (this.dynamicSegments !== null && this.dynamicSegments !== undefined) {
    output.writeFieldBegin('dynamicSegments', Thrift.Type.SET, 38);
    output.writeSetBegin(Thrift.Type.STRING, this.dynamicSegments.length);
    for (var iter1278 in this.dynamicSegments)
    {
      if (this.dynamicSegments.hasOwnProperty(iter1278))
      {
        iter1278 = this.dynamicSegments[iter1278];
        output.writeString(iter1278);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.userEngagement !== null && this.userEngagement !== undefined) {
    output.writeFieldBegin('userEngagement', Thrift.Type.STRUCT, 39);
    this.userEngagement.write(output);
    output.writeFieldEnd();
  }
  if (this.fcmToken !== null && this.fcmToken !== undefined) {
    output.writeFieldBegin('fcmToken', Thrift.Type.STRING, 40);
    output.writeString(this.fcmToken);
    output.writeFieldEnd();
  }
  if (this.dataSaverEnabled !== null && this.dataSaverEnabled !== undefined) {
    output.writeFieldBegin('dataSaverEnabled', Thrift.Type.BOOL, 41);
    output.writeBool(this.dataSaverEnabled);
    output.writeFieldEnd();
  }
  if (this.ownedAppPackageNames !== null && this.ownedAppPackageNames !== undefined) {
    output.writeFieldBegin('ownedAppPackageNames', Thrift.Type.SET, 42);
    output.writeSetBegin(Thrift.Type.STRING, this.ownedAppPackageNames.length);
    for (var iter1279 in this.ownedAppPackageNames)
    {
      if (this.ownedAppPackageNames.hasOwnProperty(iter1279))
      {
        iter1279 = this.ownedAppPackageNames[iter1279];
        output.writeString(iter1279);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.resetCacheAtInSecs !== null && this.resetCacheAtInSecs !== undefined) {
    output.writeFieldBegin('resetCacheAtInSecs', Thrift.Type.I64, 43);
    output.writeI64(this.resetCacheAtInSecs);
    output.writeFieldEnd();
  }
  if (this.highlightsDetails !== null && this.highlightsDetails !== undefined) {
    output.writeFieldBegin('highlightsDetails', Thrift.Type.STRUCT, 44);
    this.highlightsDetails.write(output);
    output.writeFieldEnd();
  }
  if (this.userStated !== null && this.userStated !== undefined) {
    output.writeFieldBegin('userStated', Thrift.Type.STRUCT, 45);
    this.userStated.write(output);
    output.writeFieldEnd();
  }
  if (this.sdkFeatures !== null && this.sdkFeatures !== undefined) {
    output.writeFieldBegin('sdkFeatures', Thrift.Type.STRUCT, 46);
    this.sdkFeatures.write(output);
    output.writeFieldEnd();
  }
  if (this.isChildLockEnabled !== null && this.isChildLockEnabled !== undefined) {
    output.writeFieldBegin('isChildLockEnabled', Thrift.Type.BOOL, 47);
    output.writeBool(this.isChildLockEnabled);
    output.writeFieldEnd();
  }
  if (this.childLockTimeLimit !== null && this.childLockTimeLimit !== undefined) {
    output.writeFieldBegin('childLockTimeLimit', Thrift.Type.I64, 48);
    output.writeI64(this.childLockTimeLimit);
    output.writeFieldEnd();
  }
  if (this.secondaryFcmToken !== null && this.secondaryFcmToken !== undefined) {
    output.writeFieldBegin('secondaryFcmToken', Thrift.Type.STRING, 49);
    output.writeString(this.secondaryFcmToken);
    output.writeFieldEnd();
  }
  if (this.monthAndYearOfBirth !== null && this.monthAndYearOfBirth !== undefined) {
    output.writeFieldBegin('monthAndYearOfBirth', Thrift.Type.STRING, 50);
    output.writeString(this.monthAndYearOfBirth);
    output.writeFieldEnd();
  }
  if (this.locationInfo !== null && this.locationInfo !== undefined) {
    output.writeFieldBegin('locationInfo', Thrift.Type.STRUCT, 51);
    this.locationInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.isUnderAge !== null && this.isUnderAge !== undefined) {
    output.writeFieldBegin('isUnderAge', Thrift.Type.BOOL, 52);
    output.writeBool(this.isUnderAge);
    output.writeFieldEnd();
  }
  if (this.appLocale !== null && this.appLocale !== undefined) {
    output.writeFieldBegin('appLocale', Thrift.Type.STRUCT, 53);
    this.appLocale.write(output);
    output.writeFieldEnd();
  }
  if (this.systemFlags !== null && this.systemFlags !== undefined) {
    output.writeFieldBegin('systemFlags', Thrift.Type.STRUCT, 54);
    this.systemFlags.write(output);
    output.writeFieldEnd();
  }
  if (this.systemFlagsUpdatedAtInSecs !== null && this.systemFlagsUpdatedAtInSecs !== undefined) {
    output.writeFieldBegin('systemFlagsUpdatedAtInSecs', Thrift.Type.I64, 55);
    output.writeI64(this.systemFlagsUpdatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.reactivationUpdatedAtInSecs !== null && this.reactivationUpdatedAtInSecs !== undefined) {
    output.writeFieldBegin('reactivationUpdatedAtInSecs', Thrift.Type.I64, 56);
    output.writeI64(this.reactivationUpdatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.optOutOfSale !== null && this.optOutOfSale !== undefined) {
    output.writeFieldBegin('optOutOfSale', Thrift.Type.BOOL, 57);
    output.writeBool(this.optOutOfSale);
    output.writeFieldEnd();
  }
  if (this.tappableTaglineState !== null && this.tappableTaglineState !== undefined) {
    output.writeFieldBegin('tappableTaglineState', Thrift.Type.BOOL, 58);
    output.writeBool(this.tappableTaglineState);
    output.writeFieldEnd();
  }
  if (this.lockscreenState !== null && this.lockscreenState !== undefined) {
    output.writeFieldBegin('lockscreenState', Thrift.Type.STRING, 59);
    output.writeString(this.lockscreenState);
    output.writeFieldEnd();
  }
  if (this.systemUiVersion !== null && this.systemUiVersion !== undefined) {
    output.writeFieldBegin('systemUiVersion', Thrift.Type.STRING, 60);
    output.writeString(this.systemUiVersion);
    output.writeFieldEnd();
  }
  if (this.age !== null && this.age !== undefined) {
    output.writeFieldBegin('age', Thrift.Type.STRING, 61);
    output.writeString(this.age);
    output.writeFieldEnd();
  }
  if (this.gender !== null && this.gender !== undefined) {
    output.writeFieldBegin('gender', Thrift.Type.STRING, 62);
    output.writeString(this.gender);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GamingCoupon = function(args) {
  this.userId = null;
  this.tournamentId = null;
  this.couponCode = null;
  if (args) {
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    }
    if (args.tournamentId !== undefined && args.tournamentId !== null) {
      this.tournamentId = args.tournamentId;
    }
    if (args.couponCode !== undefined && args.couponCode !== null) {
      this.couponCode = args.couponCode;
    }
  }
};
GamingCoupon.prototype = {};
GamingCoupon.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.tournamentId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.couponCode = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GamingCoupon.prototype.write = function(output) {
  output.writeStructBegin('GamingCoupon');
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 1);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.tournamentId !== null && this.tournamentId !== undefined) {
    output.writeFieldBegin('tournamentId', Thrift.Type.STRING, 2);
    output.writeString(this.tournamentId);
    output.writeFieldEnd();
  }
  if (this.couponCode !== null && this.couponCode !== undefined) {
    output.writeFieldBegin('couponCode', Thrift.Type.STRING, 3);
    output.writeString(this.couponCode);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

StudioSegment = function(args) {
  this.id = null;
  this.name = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
  }
};
StudioSegment.prototype = {};
StudioSegment.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.id = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

StudioSegment.prototype.write = function(output) {
  output.writeStructBegin('StudioSegment');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I32, 1);
    output.writeI32(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SqlStudioSegment = function(args) {
  this.id = null;
  this.name = null;
  this.displayName = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.source = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.displayName !== undefined && args.displayName !== null) {
      this.displayName = args.displayName;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field displayName is unset!');
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field createdAtInSecs is unset!');
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field updatedAtInSecs is unset!');
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field source is unset!');
    }
  }
};
SqlStudioSegment.prototype = {};
SqlStudioSegment.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.id = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.displayName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SqlStudioSegment.prototype.write = function(output) {
  output.writeStructBegin('SqlStudioSegment');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I32, 1);
    output.writeI32(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.displayName !== null && this.displayName !== undefined) {
    output.writeFieldBegin('displayName', Thrift.Type.STRING, 3);
    output.writeString(this.displayName);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.STRING, 6);
    output.writeString(this.source);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Keyword = function(args) {
  this.text = null;
  this.score = null;
  if (args) {
    if (args.text !== undefined && args.text !== null) {
      this.text = args.text;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field text is unset!');
    }
    if (args.score !== undefined && args.score !== null) {
      this.score = args.score;
    }
  }
};
Keyword.prototype = {};
Keyword.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.score = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Keyword.prototype.write = function(output) {
  output.writeStructBegin('Keyword');
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRING, 1);
    output.writeString(this.text);
    output.writeFieldEnd();
  }
  if (this.score !== null && this.score !== undefined) {
    output.writeFieldBegin('score', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.score);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Entity = function(args) {
  this.text = null;
  this.score = null;
  this.type = null;
  this.refUrl = null;
  if (args) {
    if (args.text !== undefined && args.text !== null) {
      this.text = args.text;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field text is unset!');
    }
    if (args.score !== undefined && args.score !== null) {
      this.score = args.score;
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    }
    if (args.refUrl !== undefined && args.refUrl !== null) {
      this.refUrl = args.refUrl;
    }
  }
};
Entity.prototype = {};
Entity.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.score = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.type = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.refUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Entity.prototype.write = function(output) {
  output.writeStructBegin('Entity');
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRING, 1);
    output.writeString(this.text);
    output.writeFieldEnd();
  }
  if (this.score !== null && this.score !== undefined) {
    output.writeFieldBegin('score', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.score);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.STRING, 3);
    output.writeString(this.type);
    output.writeFieldEnd();
  }
  if (this.refUrl !== null && this.refUrl !== undefined) {
    output.writeFieldBegin('refUrl', Thrift.Type.STRING, 4);
    output.writeString(this.refUrl);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Concept = function(args) {
  this.text = null;
  this.score = null;
  if (args) {
    if (args.text !== undefined && args.text !== null) {
      this.text = args.text;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field text is unset!');
    }
    if (args.score !== undefined && args.score !== null) {
      this.score = args.score;
    }
  }
};
Concept.prototype = {};
Concept.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.score = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Concept.prototype.write = function(output) {
  output.writeStructBegin('Concept');
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRING, 1);
    output.writeString(this.text);
    output.writeFieldEnd();
  }
  if (this.score !== null && this.score !== undefined) {
    output.writeFieldBegin('score', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.score);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Category = function(args) {
  this.label = null;
  this.score = null;
  if (args) {
    if (args.label !== undefined && args.label !== null) {
      this.label = args.label;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field label is unset!');
    }
    if (args.score !== undefined && args.score !== null) {
      this.score = args.score;
    }
  }
};
Category.prototype = {};
Category.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.label = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.score = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Category.prototype.write = function(output) {
  output.writeStructBegin('Category');
  if (this.label !== null && this.label !== undefined) {
    output.writeFieldBegin('label', Thrift.Type.STRING, 1);
    output.writeString(this.label);
    output.writeFieldEnd();
  }
  if (this.score !== null && this.score !== undefined) {
    output.writeFieldBegin('score', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.score);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

NlpFeatures = function(args) {
  this.keywords = null;
  this.entities = null;
  this.concepts = null;
  this.categories = null;
  if (args) {
    if (args.keywords !== undefined && args.keywords !== null) {
      this.keywords = Thrift.copyList(args.keywords, [Keyword]);
    }
    if (args.entities !== undefined && args.entities !== null) {
      this.entities = Thrift.copyList(args.entities, [Entity]);
    }
    if (args.concepts !== undefined && args.concepts !== null) {
      this.concepts = Thrift.copyList(args.concepts, [Concept]);
    }
    if (args.categories !== undefined && args.categories !== null) {
      this.categories = Thrift.copyList(args.categories, [Category]);
    }
  }
};
NlpFeatures.prototype = {};
NlpFeatures.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1280 = 0;
        var _rtmp31284;
        this.keywords = [];
        var _etype1283 = 0;
        _rtmp31284 = input.readListBegin();
        _etype1283 = _rtmp31284.etype;
        _size1280 = _rtmp31284.size;
        for (var _i1285 = 0; _i1285 < _size1280; ++_i1285)
        {
          var elem1286 = null;
          elem1286 = new Keyword();
          elem1286.read(input);
          this.keywords.push(elem1286);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1287 = 0;
        var _rtmp31291;
        this.entities = [];
        var _etype1290 = 0;
        _rtmp31291 = input.readListBegin();
        _etype1290 = _rtmp31291.etype;
        _size1287 = _rtmp31291.size;
        for (var _i1292 = 0; _i1292 < _size1287; ++_i1292)
        {
          var elem1293 = null;
          elem1293 = new Entity();
          elem1293.read(input);
          this.entities.push(elem1293);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size1294 = 0;
        var _rtmp31298;
        this.concepts = [];
        var _etype1297 = 0;
        _rtmp31298 = input.readListBegin();
        _etype1297 = _rtmp31298.etype;
        _size1294 = _rtmp31298.size;
        for (var _i1299 = 0; _i1299 < _size1294; ++_i1299)
        {
          var elem1300 = null;
          elem1300 = new Concept();
          elem1300.read(input);
          this.concepts.push(elem1300);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size1301 = 0;
        var _rtmp31305;
        this.categories = [];
        var _etype1304 = 0;
        _rtmp31305 = input.readListBegin();
        _etype1304 = _rtmp31305.etype;
        _size1301 = _rtmp31305.size;
        for (var _i1306 = 0; _i1306 < _size1301; ++_i1306)
        {
          var elem1307 = null;
          elem1307 = new Category();
          elem1307.read(input);
          this.categories.push(elem1307);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

NlpFeatures.prototype.write = function(output) {
  output.writeStructBegin('NlpFeatures');
  if (this.keywords !== null && this.keywords !== undefined) {
    output.writeFieldBegin('keywords', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.keywords.length);
    for (var iter1308 in this.keywords)
    {
      if (this.keywords.hasOwnProperty(iter1308))
      {
        iter1308 = this.keywords[iter1308];
        iter1308.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.entities !== null && this.entities !== undefined) {
    output.writeFieldBegin('entities', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.entities.length);
    for (var iter1309 in this.entities)
    {
      if (this.entities.hasOwnProperty(iter1309))
      {
        iter1309 = this.entities[iter1309];
        iter1309.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.concepts !== null && this.concepts !== undefined) {
    output.writeFieldBegin('concepts', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.concepts.length);
    for (var iter1310 in this.concepts)
    {
      if (this.concepts.hasOwnProperty(iter1310))
      {
        iter1310 = this.concepts[iter1310];
        iter1310.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.categories !== null && this.categories !== undefined) {
    output.writeFieldBegin('categories', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRUCT, this.categories.length);
    for (var iter1311 in this.categories)
    {
      if (this.categories.hasOwnProperty(iter1311))
      {
        iter1311 = this.categories[iter1311];
        iter1311.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceNlpFeatures = function(args) {
  this.glanceId = null;
  this.createdAtInSecs = null;
  this.titleFeatures = null;
  this.summaryFeatures = null;
  this.urlFeatures = null;
  if (args) {
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field createdAtInSecs is unset!');
    }
    if (args.titleFeatures !== undefined && args.titleFeatures !== null) {
      this.titleFeatures = new NlpFeatures(args.titleFeatures);
    }
    if (args.summaryFeatures !== undefined && args.summaryFeatures !== null) {
      this.summaryFeatures = new NlpFeatures(args.summaryFeatures);
    }
    if (args.urlFeatures !== undefined && args.urlFeatures !== null) {
      this.urlFeatures = new NlpFeatures(args.urlFeatures);
    }
  }
};
GlanceNlpFeatures.prototype = {};
GlanceNlpFeatures.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.titleFeatures = new NlpFeatures();
        this.titleFeatures.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.summaryFeatures = new NlpFeatures();
        this.summaryFeatures.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.urlFeatures = new NlpFeatures();
        this.urlFeatures.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceNlpFeatures.prototype.write = function(output) {
  output.writeStructBegin('GlanceNlpFeatures');
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.titleFeatures !== null && this.titleFeatures !== undefined) {
    output.writeFieldBegin('titleFeatures', Thrift.Type.STRUCT, 3);
    this.titleFeatures.write(output);
    output.writeFieldEnd();
  }
  if (this.summaryFeatures !== null && this.summaryFeatures !== undefined) {
    output.writeFieldBegin('summaryFeatures', Thrift.Type.STRUCT, 4);
    this.summaryFeatures.write(output);
    output.writeFieldEnd();
  }
  if (this.urlFeatures !== null && this.urlFeatures !== undefined) {
    output.writeFieldBegin('urlFeatures', Thrift.Type.STRUCT, 5);
    this.urlFeatures.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ImageSuggestionMeta = function(args) {
  this.type = null;
  this.url = null;
  if (args) {
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field type is unset!');
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
  }
};
ImageSuggestionMeta.prototype = {};
ImageSuggestionMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.type = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ImageSuggestionMeta.prototype.write = function(output) {
  output.writeStructBegin('ImageSuggestionMeta');
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.STRING, 1);
    output.writeString(this.type);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 2);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ImageSuggestion = function(args) {
  this.id = null;
  this.imageMetas = null;
  this.source = null;
  this.aspectRatio = null;
  this.title = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.imageMetas !== undefined && args.imageMetas !== null) {
      this.imageMetas = Thrift.copyList(args.imageMetas, [ImageSuggestionMeta]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field imageMetas is unset!');
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    }
    if (args.aspectRatio !== undefined && args.aspectRatio !== null) {
      this.aspectRatio = new AspectRatio(args.aspectRatio);
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
  }
};
ImageSuggestion.prototype = {};
ImageSuggestion.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1312 = 0;
        var _rtmp31316;
        this.imageMetas = [];
        var _etype1315 = 0;
        _rtmp31316 = input.readListBegin();
        _etype1315 = _rtmp31316.etype;
        _size1312 = _rtmp31316.size;
        for (var _i1317 = 0; _i1317 < _size1312; ++_i1317)
        {
          var elem1318 = null;
          elem1318 = new ImageSuggestionMeta();
          elem1318.read(input);
          this.imageMetas.push(elem1318);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.aspectRatio = new AspectRatio();
        this.aspectRatio.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ImageSuggestion.prototype.write = function(output) {
  output.writeStructBegin('ImageSuggestion');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.imageMetas !== null && this.imageMetas !== undefined) {
    output.writeFieldBegin('imageMetas', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.imageMetas.length);
    for (var iter1319 in this.imageMetas)
    {
      if (this.imageMetas.hasOwnProperty(iter1319))
      {
        iter1319 = this.imageMetas[iter1319];
        iter1319.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.STRING, 3);
    output.writeString(this.source);
    output.writeFieldEnd();
  }
  if (this.aspectRatio !== null && this.aspectRatio !== undefined) {
    output.writeFieldBegin('aspectRatio', Thrift.Type.STRUCT, 4);
    this.aspectRatio.write(output);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 5);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ImageSuggestionsForSearchPhrase = function(args) {
  this.searchwords = null;
  this.imageSuggestions = null;
  if (args) {
    if (args.searchwords !== undefined && args.searchwords !== null) {
      this.searchwords = Thrift.copyList(args.searchwords, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field searchwords is unset!');
    }
    if (args.imageSuggestions !== undefined && args.imageSuggestions !== null) {
      this.imageSuggestions = Thrift.copyList(args.imageSuggestions, [ImageSuggestion]);
    }
  }
};
ImageSuggestionsForSearchPhrase.prototype = {};
ImageSuggestionsForSearchPhrase.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1320 = 0;
        var _rtmp31324;
        this.searchwords = [];
        var _etype1323 = 0;
        _rtmp31324 = input.readListBegin();
        _etype1323 = _rtmp31324.etype;
        _size1320 = _rtmp31324.size;
        for (var _i1325 = 0; _i1325 < _size1320; ++_i1325)
        {
          var elem1326 = null;
          elem1326 = input.readString().value;
          this.searchwords.push(elem1326);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1327 = 0;
        var _rtmp31331;
        this.imageSuggestions = [];
        var _etype1330 = 0;
        _rtmp31331 = input.readListBegin();
        _etype1330 = _rtmp31331.etype;
        _size1327 = _rtmp31331.size;
        for (var _i1332 = 0; _i1332 < _size1327; ++_i1332)
        {
          var elem1333 = null;
          elem1333 = new ImageSuggestion();
          elem1333.read(input);
          this.imageSuggestions.push(elem1333);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ImageSuggestionsForSearchPhrase.prototype.write = function(output) {
  output.writeStructBegin('ImageSuggestionsForSearchPhrase');
  if (this.searchwords !== null && this.searchwords !== undefined) {
    output.writeFieldBegin('searchwords', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.searchwords.length);
    for (var iter1334 in this.searchwords)
    {
      if (this.searchwords.hasOwnProperty(iter1334))
      {
        iter1334 = this.searchwords[iter1334];
        output.writeString(iter1334);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.imageSuggestions !== null && this.imageSuggestions !== undefined) {
    output.writeFieldBegin('imageSuggestions', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.imageSuggestions.length);
    for (var iter1335 in this.imageSuggestions)
    {
      if (this.imageSuggestions.hasOwnProperty(iter1335))
      {
        iter1335 = this.imageSuggestions[iter1335];
        iter1335.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ImageSuggestionResponse = function(args) {
  this.searchwords = null;
  this.imageSuggestionList = null;
  if (args) {
    if (args.searchwords !== undefined && args.searchwords !== null) {
      this.searchwords = Thrift.copyList(args.searchwords, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field searchwords is unset!');
    }
    if (args.imageSuggestionList !== undefined && args.imageSuggestionList !== null) {
      this.imageSuggestionList = Thrift.copyList(args.imageSuggestionList, [ImageSuggestionsForSearchPhrase]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field imageSuggestionList is unset!');
    }
  }
};
ImageSuggestionResponse.prototype = {};
ImageSuggestionResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1336 = 0;
        var _rtmp31340;
        this.searchwords = [];
        var _etype1339 = 0;
        _rtmp31340 = input.readListBegin();
        _etype1339 = _rtmp31340.etype;
        _size1336 = _rtmp31340.size;
        for (var _i1341 = 0; _i1341 < _size1336; ++_i1341)
        {
          var elem1342 = null;
          elem1342 = input.readString().value;
          this.searchwords.push(elem1342);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1343 = 0;
        var _rtmp31347;
        this.imageSuggestionList = [];
        var _etype1346 = 0;
        _rtmp31347 = input.readListBegin();
        _etype1346 = _rtmp31347.etype;
        _size1343 = _rtmp31347.size;
        for (var _i1348 = 0; _i1348 < _size1343; ++_i1348)
        {
          var elem1349 = null;
          elem1349 = new ImageSuggestionsForSearchPhrase();
          elem1349.read(input);
          this.imageSuggestionList.push(elem1349);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ImageSuggestionResponse.prototype.write = function(output) {
  output.writeStructBegin('ImageSuggestionResponse');
  if (this.searchwords !== null && this.searchwords !== undefined) {
    output.writeFieldBegin('searchwords', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.searchwords.length);
    for (var iter1350 in this.searchwords)
    {
      if (this.searchwords.hasOwnProperty(iter1350))
      {
        iter1350 = this.searchwords[iter1350];
        output.writeString(iter1350);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.imageSuggestionList !== null && this.imageSuggestionList !== undefined) {
    output.writeFieldBegin('imageSuggestionList', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.imageSuggestionList.length);
    for (var iter1351 in this.imageSuggestionList)
    {
      if (this.imageSuggestionList.hasOwnProperty(iter1351))
      {
        iter1351 = this.imageSuggestionList[iter1351];
        iter1351.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceImageSuggestions = function(args) {
  this.id = null;
  this.titleImageSuggestions = null;
  this.summaryImageSuggestions = null;
  this.urlImageSuggestions = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.titleImageSuggestions !== undefined && args.titleImageSuggestions !== null) {
      this.titleImageSuggestions = new ImageSuggestionResponse(args.titleImageSuggestions);
    }
    if (args.summaryImageSuggestions !== undefined && args.summaryImageSuggestions !== null) {
      this.summaryImageSuggestions = new ImageSuggestionResponse(args.summaryImageSuggestions);
    }
    if (args.urlImageSuggestions !== undefined && args.urlImageSuggestions !== null) {
      this.urlImageSuggestions = new ImageSuggestionResponse(args.urlImageSuggestions);
    }
  }
};
GlanceImageSuggestions.prototype = {};
GlanceImageSuggestions.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.titleImageSuggestions = new ImageSuggestionResponse();
        this.titleImageSuggestions.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.summaryImageSuggestions = new ImageSuggestionResponse();
        this.summaryImageSuggestions.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.urlImageSuggestions = new ImageSuggestionResponse();
        this.urlImageSuggestions.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceImageSuggestions.prototype.write = function(output) {
  output.writeStructBegin('GlanceImageSuggestions');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.titleImageSuggestions !== null && this.titleImageSuggestions !== undefined) {
    output.writeFieldBegin('titleImageSuggestions', Thrift.Type.STRUCT, 2);
    this.titleImageSuggestions.write(output);
    output.writeFieldEnd();
  }
  if (this.summaryImageSuggestions !== null && this.summaryImageSuggestions !== undefined) {
    output.writeFieldBegin('summaryImageSuggestions', Thrift.Type.STRUCT, 3);
    this.summaryImageSuggestions.write(output);
    output.writeFieldEnd();
  }
  if (this.urlImageSuggestions !== null && this.urlImageSuggestions !== undefined) {
    output.writeFieldBegin('urlImageSuggestions', Thrift.Type.STRUCT, 4);
    this.urlImageSuggestions.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SpacesCity = function(args) {
  this.id = null;
  this.name = null;
  this.countryId = null;
  this.stateId = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.countryId !== undefined && args.countryId !== null) {
      this.countryId = args.countryId;
    }
    if (args.stateId !== undefined && args.stateId !== null) {
      this.stateId = args.stateId;
    }
  }
};
SpacesCity.prototype = {};
SpacesCity.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.countryId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.stateId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SpacesCity.prototype.write = function(output) {
  output.writeStructBegin('SpacesCity');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.countryId !== null && this.countryId !== undefined) {
    output.writeFieldBegin('countryId', Thrift.Type.I64, 3);
    output.writeI64(this.countryId);
    output.writeFieldEnd();
  }
  if (this.stateId !== null && this.stateId !== undefined) {
    output.writeFieldBegin('stateId', Thrift.Type.STRING, 4);
    output.writeString(this.stateId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CityState = function(args) {
  this.stateId = null;
  this.stateName = null;
  this.countryId = null;
  this.region = null;
  this.cities = null;
  if (args) {
    if (args.stateId !== undefined && args.stateId !== null) {
      this.stateId = args.stateId;
    }
    if (args.stateName !== undefined && args.stateName !== null) {
      this.stateName = args.stateName;
    }
    if (args.countryId !== undefined && args.countryId !== null) {
      this.countryId = args.countryId;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.cities !== undefined && args.cities !== null) {
      this.cities = Thrift.copyList(args.cities, [City]);
    }
  }
};
CityState.prototype = {};
CityState.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.stateId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.stateName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.countryId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.region = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size1352 = 0;
        var _rtmp31356;
        this.cities = [];
        var _etype1355 = 0;
        _rtmp31356 = input.readListBegin();
        _etype1355 = _rtmp31356.etype;
        _size1352 = _rtmp31356.size;
        for (var _i1357 = 0; _i1357 < _size1352; ++_i1357)
        {
          var elem1358 = null;
          elem1358 = new City();
          elem1358.read(input);
          this.cities.push(elem1358);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CityState.prototype.write = function(output) {
  output.writeStructBegin('CityState');
  if (this.stateId !== null && this.stateId !== undefined) {
    output.writeFieldBegin('stateId', Thrift.Type.I64, 1);
    output.writeI64(this.stateId);
    output.writeFieldEnd();
  }
  if (this.stateName !== null && this.stateName !== undefined) {
    output.writeFieldBegin('stateName', Thrift.Type.STRING, 2);
    output.writeString(this.stateName);
    output.writeFieldEnd();
  }
  if (this.countryId !== null && this.countryId !== undefined) {
    output.writeFieldBegin('countryId', Thrift.Type.I64, 3);
    output.writeI64(this.countryId);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.STRING, 4);
    output.writeString(this.region);
    output.writeFieldEnd();
  }
  if (this.cities !== null && this.cities !== undefined) {
    output.writeFieldBegin('cities', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.cities.length);
    for (var iter1359 in this.cities)
    {
      if (this.cities.hasOwnProperty(iter1359))
      {
        iter1359 = this.cities[iter1359];
        iter1359.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SpacesCityState = function(args) {
  this.stateId = null;
  this.stateName = null;
  this.countryId = null;
  this.region = null;
  this.cities = null;
  if (args) {
    if (args.stateId !== undefined && args.stateId !== null) {
      this.stateId = args.stateId;
    }
    if (args.stateName !== undefined && args.stateName !== null) {
      this.stateName = args.stateName;
    }
    if (args.countryId !== undefined && args.countryId !== null) {
      this.countryId = args.countryId;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.cities !== undefined && args.cities !== null) {
      this.cities = Thrift.copyList(args.cities, [SpacesCity]);
    }
  }
};
SpacesCityState.prototype = {};
SpacesCityState.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.stateId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.stateName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.countryId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.region = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size1360 = 0;
        var _rtmp31364;
        this.cities = [];
        var _etype1363 = 0;
        _rtmp31364 = input.readListBegin();
        _etype1363 = _rtmp31364.etype;
        _size1360 = _rtmp31364.size;
        for (var _i1365 = 0; _i1365 < _size1360; ++_i1365)
        {
          var elem1366 = null;
          elem1366 = new SpacesCity();
          elem1366.read(input);
          this.cities.push(elem1366);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SpacesCityState.prototype.write = function(output) {
  output.writeStructBegin('SpacesCityState');
  if (this.stateId !== null && this.stateId !== undefined) {
    output.writeFieldBegin('stateId', Thrift.Type.STRING, 1);
    output.writeString(this.stateId);
    output.writeFieldEnd();
  }
  if (this.stateName !== null && this.stateName !== undefined) {
    output.writeFieldBegin('stateName', Thrift.Type.STRING, 2);
    output.writeString(this.stateName);
    output.writeFieldEnd();
  }
  if (this.countryId !== null && this.countryId !== undefined) {
    output.writeFieldBegin('countryId', Thrift.Type.I64, 3);
    output.writeI64(this.countryId);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.STRING, 4);
    output.writeString(this.region);
    output.writeFieldEnd();
  }
  if (this.cities !== null && this.cities !== undefined) {
    output.writeFieldBegin('cities', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.cities.length);
    for (var iter1367 in this.cities)
    {
      if (this.cities.hasOwnProperty(iter1367))
      {
        iter1367 = this.cities[iter1367];
        iter1367.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CityGroup = function(args) {
  this.groupName = null;
  this.cities = null;
  if (args) {
    if (args.groupName !== undefined && args.groupName !== null) {
      this.groupName = args.groupName;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field groupName is unset!');
    }
    if (args.cities !== undefined && args.cities !== null) {
      this.cities = Thrift.copyList(args.cities, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field cities is unset!');
    }
  }
};
CityGroup.prototype = {};
CityGroup.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.groupName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1368 = 0;
        var _rtmp31372;
        this.cities = [];
        var _etype1371 = 0;
        _rtmp31372 = input.readListBegin();
        _etype1371 = _rtmp31372.etype;
        _size1368 = _rtmp31372.size;
        for (var _i1373 = 0; _i1373 < _size1368; ++_i1373)
        {
          var elem1374 = null;
          elem1374 = input.readI64().value;
          this.cities.push(elem1374);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CityGroup.prototype.write = function(output) {
  output.writeStructBegin('CityGroup');
  if (this.groupName !== null && this.groupName !== undefined) {
    output.writeFieldBegin('groupName', Thrift.Type.STRING, 1);
    output.writeString(this.groupName);
    output.writeFieldEnd();
  }
  if (this.cities !== null && this.cities !== undefined) {
    output.writeFieldBegin('cities', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.I64, this.cities.length);
    for (var iter1375 in this.cities)
    {
      if (this.cities.hasOwnProperty(iter1375))
      {
        iter1375 = this.cities[iter1375];
        output.writeI64(iter1375);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Carrier = function(args) {
  this.carrierId = null;
  this.carrierName = null;
  if (args) {
    if (args.carrierId !== undefined && args.carrierId !== null) {
      this.carrierId = args.carrierId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field carrierId is unset!');
    }
    if (args.carrierName !== undefined && args.carrierName !== null) {
      this.carrierName = args.carrierName;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field carrierName is unset!');
    }
  }
};
Carrier.prototype = {};
Carrier.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.carrierId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.carrierName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Carrier.prototype.write = function(output) {
  output.writeStructBegin('Carrier');
  if (this.carrierId !== null && this.carrierId !== undefined) {
    output.writeFieldBegin('carrierId', Thrift.Type.I64, 1);
    output.writeI64(this.carrierId);
    output.writeFieldEnd();
  }
  if (this.carrierName !== null && this.carrierName !== undefined) {
    output.writeFieldBegin('carrierName', Thrift.Type.STRING, 2);
    output.writeString(this.carrierName);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CcidCarrier = function(args) {
  this.id = null;
  this.countryId = null;
  this.carrierId = null;
  this.targetable = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.countryId !== undefined && args.countryId !== null) {
      this.countryId = args.countryId;
    }
    if (args.carrierId !== undefined && args.carrierId !== null) {
      this.carrierId = args.carrierId;
    }
    if (args.targetable !== undefined && args.targetable !== null) {
      this.targetable = args.targetable;
    }
  }
};
CcidCarrier.prototype = {};
CcidCarrier.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.id = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.countryId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.carrierId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.targetable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CcidCarrier.prototype.write = function(output) {
  output.writeStructBegin('CcidCarrier');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I64, 1);
    output.writeI64(this.id);
    output.writeFieldEnd();
  }
  if (this.countryId !== null && this.countryId !== undefined) {
    output.writeFieldBegin('countryId', Thrift.Type.I64, 2);
    output.writeI64(this.countryId);
    output.writeFieldEnd();
  }
  if (this.carrierId !== null && this.carrierId !== undefined) {
    output.writeFieldBegin('carrierId', Thrift.Type.I64, 3);
    output.writeI64(this.carrierId);
    output.writeFieldEnd();
  }
  if (this.targetable !== null && this.targetable !== undefined) {
    output.writeFieldBegin('targetable', Thrift.Type.BOOL, 4);
    output.writeBool(this.targetable);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceServedMeta = function(args) {
  this.glanceId = null;
  this.categoryIds = null;
  this.mode = null;
  if (args) {
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
    if (args.categoryIds !== undefined && args.categoryIds !== null) {
      this.categoryIds = Thrift.copyList(args.categoryIds, [null]);
    }
    if (args.mode !== undefined && args.mode !== null) {
      this.mode = args.mode;
    }
  }
};
GlanceServedMeta.prototype = {};
GlanceServedMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1376 = 0;
        var _rtmp31380;
        this.categoryIds = [];
        var _etype1379 = 0;
        _rtmp31380 = input.readListBegin();
        _etype1379 = _rtmp31380.etype;
        _size1376 = _rtmp31380.size;
        for (var _i1381 = 0; _i1381 < _size1376; ++_i1381)
        {
          var elem1382 = null;
          elem1382 = input.readString().value;
          this.categoryIds.push(elem1382);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.mode = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceServedMeta.prototype.write = function(output) {
  output.writeStructBegin('GlanceServedMeta');
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.categoryIds !== null && this.categoryIds !== undefined) {
    output.writeFieldBegin('categoryIds', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.categoryIds.length);
    for (var iter1383 in this.categoryIds)
    {
      if (this.categoryIds.hasOwnProperty(iter1383))
      {
        iter1383 = this.categoryIds[iter1383];
        output.writeString(iter1383);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.mode !== null && this.mode !== undefined) {
    output.writeFieldBegin('mode', Thrift.Type.I32, 3);
    output.writeI32(this.mode);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancesServed = function(args) {
  this.servedAtInSecs = null;
  this.glanceServedMetaMap = null;
  this.glanceIds = null;
  this.seenGlanceIds = null;
  this.isOnDemandServing = null;
  if (args) {
    if (args.servedAtInSecs !== undefined && args.servedAtInSecs !== null) {
      this.servedAtInSecs = args.servedAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field servedAtInSecs is unset!');
    }
    if (args.glanceServedMetaMap !== undefined && args.glanceServedMetaMap !== null) {
      this.glanceServedMetaMap = Thrift.copyMap(args.glanceServedMetaMap, [Thrift.copyList, GlanceServedMeta]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceServedMetaMap is unset!');
    }
    if (args.glanceIds !== undefined && args.glanceIds !== null) {
      this.glanceIds = Thrift.copyList(args.glanceIds, [null]);
    }
    if (args.seenGlanceIds !== undefined && args.seenGlanceIds !== null) {
      this.seenGlanceIds = Thrift.copyList(args.seenGlanceIds, [null]);
    }
    if (args.isOnDemandServing !== undefined && args.isOnDemandServing !== null) {
      this.isOnDemandServing = args.isOnDemandServing;
    }
  }
};
GlancesServed.prototype = {};
GlancesServed.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.servedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.MAP) {
        var _size1384 = 0;
        var _rtmp31388;
        this.glanceServedMetaMap = {};
        var _ktype1385 = 0;
        var _vtype1386 = 0;
        _rtmp31388 = input.readMapBegin();
        _ktype1385 = _rtmp31388.ktype;
        _vtype1386 = _rtmp31388.vtype;
        _size1384 = _rtmp31388.size;
        for (var _i1389 = 0; _i1389 < _size1384; ++_i1389)
        {
          if (_i1389 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key1390 = null;
          var val1391 = null;
          key1390 = input.readString().value;
          var _size1392 = 0;
          var _rtmp31396;
          val1391 = [];
          var _etype1395 = 0;
          _rtmp31396 = input.readSetBegin();
          _etype1395 = _rtmp31396.etype;
          _size1392 = _rtmp31396.size;
          for (var _i1397 = 0; _i1397 < _size1392; ++_i1397)
          {
            var elem1398 = null;
            elem1398 = new GlanceServedMeta();
            elem1398.read(input);
            val1391.push(elem1398);
          }
          input.readSetEnd();
          this.glanceServedMetaMap[key1390] = val1391;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size1399 = 0;
        var _rtmp31403;
        this.glanceIds = [];
        var _etype1402 = 0;
        _rtmp31403 = input.readListBegin();
        _etype1402 = _rtmp31403.etype;
        _size1399 = _rtmp31403.size;
        for (var _i1404 = 0; _i1404 < _size1399; ++_i1404)
        {
          var elem1405 = null;
          elem1405 = input.readI64().value;
          this.glanceIds.push(elem1405);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.SET) {
        var _size1406 = 0;
        var _rtmp31410;
        this.seenGlanceIds = [];
        var _etype1409 = 0;
        _rtmp31410 = input.readSetBegin();
        _etype1409 = _rtmp31410.etype;
        _size1406 = _rtmp31410.size;
        for (var _i1411 = 0; _i1411 < _size1406; ++_i1411)
        {
          var elem1412 = null;
          elem1412 = input.readI64().value;
          this.seenGlanceIds.push(elem1412);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.isOnDemandServing = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancesServed.prototype.write = function(output) {
  output.writeStructBegin('GlancesServed');
  if (this.servedAtInSecs !== null && this.servedAtInSecs !== undefined) {
    output.writeFieldBegin('servedAtInSecs', Thrift.Type.I64, 1);
    output.writeI64(this.servedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.glanceServedMetaMap !== null && this.glanceServedMetaMap !== undefined) {
    output.writeFieldBegin('glanceServedMetaMap', Thrift.Type.MAP, 2);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.SET, Thrift.objectLength(this.glanceServedMetaMap));
    for (var kiter1413 in this.glanceServedMetaMap)
    {
      if (this.glanceServedMetaMap.hasOwnProperty(kiter1413))
      {
        var viter1414 = this.glanceServedMetaMap[kiter1413];
        output.writeString(kiter1413);
        output.writeSetBegin(Thrift.Type.STRUCT, viter1414.length);
        for (var iter1415 in viter1414)
        {
          if (viter1414.hasOwnProperty(iter1415))
          {
            iter1415 = viter1414[iter1415];
            iter1415.write(output);
          }
        }
        output.writeSetEnd();
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.glanceIds !== null && this.glanceIds !== undefined) {
    output.writeFieldBegin('glanceIds', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.I64, this.glanceIds.length);
    for (var iter1416 in this.glanceIds)
    {
      if (this.glanceIds.hasOwnProperty(iter1416))
      {
        iter1416 = this.glanceIds[iter1416];
        output.writeI64(iter1416);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.seenGlanceIds !== null && this.seenGlanceIds !== undefined) {
    output.writeFieldBegin('seenGlanceIds', Thrift.Type.SET, 4);
    output.writeSetBegin(Thrift.Type.I64, this.seenGlanceIds.length);
    for (var iter1417 in this.seenGlanceIds)
    {
      if (this.seenGlanceIds.hasOwnProperty(iter1417))
      {
        iter1417 = this.seenGlanceIds[iter1417];
        output.writeI64(iter1417);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.isOnDemandServing !== null && this.isOnDemandServing !== undefined) {
    output.writeFieldBegin('isOnDemandServing', Thrift.Type.BOOL, 5);
    output.writeBool(this.isOnDemandServing);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OnlineGlancesServedWithSeen = function(args) {
  this.servedAtInSecs = null;
  this.glanceIds = null;
  if (args) {
    if (args.servedAtInSecs !== undefined && args.servedAtInSecs !== null) {
      this.servedAtInSecs = args.servedAtInSecs;
    }
    if (args.glanceIds !== undefined && args.glanceIds !== null) {
      this.glanceIds = Thrift.copyList(args.glanceIds, [null]);
    }
  }
};
OnlineGlancesServedWithSeen.prototype = {};
OnlineGlancesServedWithSeen.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.servedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.SET) {
        var _size1418 = 0;
        var _rtmp31422;
        this.glanceIds = [];
        var _etype1421 = 0;
        _rtmp31422 = input.readSetBegin();
        _etype1421 = _rtmp31422.etype;
        _size1418 = _rtmp31422.size;
        for (var _i1423 = 0; _i1423 < _size1418; ++_i1423)
        {
          var elem1424 = null;
          elem1424 = input.readI64().value;
          this.glanceIds.push(elem1424);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OnlineGlancesServedWithSeen.prototype.write = function(output) {
  output.writeStructBegin('OnlineGlancesServedWithSeen');
  if (this.servedAtInSecs !== null && this.servedAtInSecs !== undefined) {
    output.writeFieldBegin('servedAtInSecs', Thrift.Type.I64, 1);
    output.writeI64(this.servedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.glanceIds !== null && this.glanceIds !== undefined) {
    output.writeFieldBegin('glanceIds', Thrift.Type.SET, 2);
    output.writeSetBegin(Thrift.Type.I64, this.glanceIds.length);
    for (var iter1425 in this.glanceIds)
    {
      if (this.glanceIds.hasOwnProperty(iter1425))
      {
        iter1425 = this.glanceIds[iter1425];
        output.writeI64(iter1425);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ServedGlance = function(args) {
  this.glanceId = null;
  this.seen = null;
  this.bubbleId = null;
  if (args) {
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
    if (args.seen !== undefined && args.seen !== null) {
      this.seen = args.seen;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field seen is unset!');
    }
    if (args.bubbleId !== undefined && args.bubbleId !== null) {
      this.bubbleId = args.bubbleId;
    }
  }
};
ServedGlance.prototype = {};
ServedGlance.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.glanceId = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.seen = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.bubbleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ServedGlance.prototype.write = function(output) {
  output.writeStructBegin('ServedGlance');
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.I64, 1);
    output.writeI64(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.seen !== null && this.seen !== undefined) {
    output.writeFieldBegin('seen', Thrift.Type.BOOL, 2);
    output.writeBool(this.seen);
    output.writeFieldEnd();
  }
  if (this.bubbleId !== null && this.bubbleId !== undefined) {
    output.writeFieldBegin('bubbleId', Thrift.Type.STRING, 3);
    output.writeString(this.bubbleId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ServedGlancesInSession = function(args) {
  this.servedAtInSecs = null;
  this.glanceIds = null;
  if (args) {
    if (args.servedAtInSecs !== undefined && args.servedAtInSecs !== null) {
      this.servedAtInSecs = args.servedAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field servedAtInSecs is unset!');
    }
    if (args.glanceIds !== undefined && args.glanceIds !== null) {
      this.glanceIds = Thrift.copyList(args.glanceIds, [ServedGlance]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceIds is unset!');
    }
  }
};
ServedGlancesInSession.prototype = {};
ServedGlancesInSession.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.servedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.SET) {
        var _size1426 = 0;
        var _rtmp31430;
        this.glanceIds = [];
        var _etype1429 = 0;
        _rtmp31430 = input.readSetBegin();
        _etype1429 = _rtmp31430.etype;
        _size1426 = _rtmp31430.size;
        for (var _i1431 = 0; _i1431 < _size1426; ++_i1431)
        {
          var elem1432 = null;
          elem1432 = new ServedGlance();
          elem1432.read(input);
          this.glanceIds.push(elem1432);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ServedGlancesInSession.prototype.write = function(output) {
  output.writeStructBegin('ServedGlancesInSession');
  if (this.servedAtInSecs !== null && this.servedAtInSecs !== undefined) {
    output.writeFieldBegin('servedAtInSecs', Thrift.Type.I64, 1);
    output.writeI64(this.servedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.glanceIds !== null && this.glanceIds !== undefined) {
    output.writeFieldBegin('glanceIds', Thrift.Type.SET, 2);
    output.writeSetBegin(Thrift.Type.STRUCT, this.glanceIds.length);
    for (var iter1433 in this.glanceIds)
    {
      if (this.glanceIds.hasOwnProperty(iter1433))
      {
        iter1433 = this.glanceIds[iter1433];
        iter1433.write(output);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OnlineGlancesServed = function(args) {
  this.servedAtInSecs = null;
  this.sessionGlanceMap = null;
  if (args) {
    if (args.servedAtInSecs !== undefined && args.servedAtInSecs !== null) {
      this.servedAtInSecs = args.servedAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field servedAtInSecs is unset!');
    }
    if (args.sessionGlanceMap !== undefined && args.sessionGlanceMap !== null) {
      this.sessionGlanceMap = Thrift.copyMap(args.sessionGlanceMap, [ServedGlancesInSession]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field sessionGlanceMap is unset!');
    }
  }
};
OnlineGlancesServed.prototype = {};
OnlineGlancesServed.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.servedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.MAP) {
        var _size1434 = 0;
        var _rtmp31438;
        this.sessionGlanceMap = {};
        var _ktype1435 = 0;
        var _vtype1436 = 0;
        _rtmp31438 = input.readMapBegin();
        _ktype1435 = _rtmp31438.ktype;
        _vtype1436 = _rtmp31438.vtype;
        _size1434 = _rtmp31438.size;
        for (var _i1439 = 0; _i1439 < _size1434; ++_i1439)
        {
          if (_i1439 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key1440 = null;
          var val1441 = null;
          key1440 = input.readString().value;
          val1441 = new ServedGlancesInSession();
          val1441.read(input);
          this.sessionGlanceMap[key1440] = val1441;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OnlineGlancesServed.prototype.write = function(output) {
  output.writeStructBegin('OnlineGlancesServed');
  if (this.servedAtInSecs !== null && this.servedAtInSecs !== undefined) {
    output.writeFieldBegin('servedAtInSecs', Thrift.Type.I64, 1);
    output.writeI64(this.servedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.sessionGlanceMap !== null && this.sessionGlanceMap !== undefined) {
    output.writeFieldBegin('sessionGlanceMap', Thrift.Type.MAP, 2);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.STRUCT, Thrift.objectLength(this.sessionGlanceMap));
    for (var kiter1442 in this.sessionGlanceMap)
    {
      if (this.sessionGlanceMap.hasOwnProperty(kiter1442))
      {
        var viter1443 = this.sessionGlanceMap[kiter1442];
        output.writeString(kiter1442);
        viter1443.write(output);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

HourlyGlanceImpressions = function(args) {
  this.hour = null;
  this.glanceIds = null;
  if (args) {
    if (args.hour !== undefined && args.hour !== null) {
      this.hour = args.hour;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field hour is unset!');
    }
    if (args.glanceIds !== undefined && args.glanceIds !== null) {
      this.glanceIds = Thrift.copyList(args.glanceIds, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceIds is unset!');
    }
  }
};
HourlyGlanceImpressions.prototype = {};
HourlyGlanceImpressions.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.hour = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1444 = 0;
        var _rtmp31448;
        this.glanceIds = [];
        var _etype1447 = 0;
        _rtmp31448 = input.readListBegin();
        _etype1447 = _rtmp31448.etype;
        _size1444 = _rtmp31448.size;
        for (var _i1449 = 0; _i1449 < _size1444; ++_i1449)
        {
          var elem1450 = null;
          elem1450 = input.readString().value;
          this.glanceIds.push(elem1450);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

HourlyGlanceImpressions.prototype.write = function(output) {
  output.writeStructBegin('HourlyGlanceImpressions');
  if (this.hour !== null && this.hour !== undefined) {
    output.writeFieldBegin('hour', Thrift.Type.I32, 1);
    output.writeI32(this.hour);
    output.writeFieldEnd();
  }
  if (this.glanceIds !== null && this.glanceIds !== undefined) {
    output.writeFieldBegin('glanceIds', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.glanceIds.length);
    for (var iter1451 in this.glanceIds)
    {
      if (this.glanceIds.hasOwnProperty(iter1451))
      {
        iter1451 = this.glanceIds[iter1451];
        output.writeString(iter1451);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PredictedImpressions = function(args) {
  this.hourlyGlanceImpressions = null;
  this.lastServedAtInSecs = null;
  if (args) {
    if (args.hourlyGlanceImpressions !== undefined && args.hourlyGlanceImpressions !== null) {
      this.hourlyGlanceImpressions = Thrift.copyList(args.hourlyGlanceImpressions, [HourlyGlanceImpressions]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field hourlyGlanceImpressions is unset!');
    }
    if (args.lastServedAtInSecs !== undefined && args.lastServedAtInSecs !== null) {
      this.lastServedAtInSecs = args.lastServedAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field lastServedAtInSecs is unset!');
    }
  }
};
PredictedImpressions.prototype = {};
PredictedImpressions.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1452 = 0;
        var _rtmp31456;
        this.hourlyGlanceImpressions = [];
        var _etype1455 = 0;
        _rtmp31456 = input.readListBegin();
        _etype1455 = _rtmp31456.etype;
        _size1452 = _rtmp31456.size;
        for (var _i1457 = 0; _i1457 < _size1452; ++_i1457)
        {
          var elem1458 = null;
          elem1458 = new HourlyGlanceImpressions();
          elem1458.read(input);
          this.hourlyGlanceImpressions.push(elem1458);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.lastServedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PredictedImpressions.prototype.write = function(output) {
  output.writeStructBegin('PredictedImpressions');
  if (this.hourlyGlanceImpressions !== null && this.hourlyGlanceImpressions !== undefined) {
    output.writeFieldBegin('hourlyGlanceImpressions', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.hourlyGlanceImpressions.length);
    for (var iter1459 in this.hourlyGlanceImpressions)
    {
      if (this.hourlyGlanceImpressions.hasOwnProperty(iter1459))
      {
        iter1459 = this.hourlyGlanceImpressions[iter1459];
        iter1459.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.lastServedAtInSecs !== null && this.lastServedAtInSecs !== undefined) {
    output.writeFieldBegin('lastServedAtInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.lastServedAtInSecs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UserSummary = function(args) {
  this.id = null;
  this.userId = null;
  this.servingHistory = null;
  this.ver = null;
  this.onlineServingHistory = null;
  this.onlineGlancesServed = null;
  this.onlineServedGlanceIds = null;
  this.offlineSeenGlanceIds = null;
  this.onlineUnseenGlanceIds = null;
  this.predictedImpressions = null;
  this.appsOnDevice = null;
  this.partnerId = null;
  this.region = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field userId is unset!');
    }
    if (args.servingHistory !== undefined && args.servingHistory !== null) {
      this.servingHistory = Thrift.copyList(args.servingHistory, [GlancesServed]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field servingHistory is unset!');
    }
    if (args.ver !== undefined && args.ver !== null) {
      this.ver = args.ver;
    }
    if (args.onlineServingHistory !== undefined && args.onlineServingHistory !== null) {
      this.onlineServingHistory = Thrift.copyList(args.onlineServingHistory, [OnlineGlancesServedWithSeen]);
    }
    if (args.onlineGlancesServed !== undefined && args.onlineGlancesServed !== null) {
      this.onlineGlancesServed = new OnlineGlancesServed(args.onlineGlancesServed);
    }
    if (args.onlineServedGlanceIds !== undefined && args.onlineServedGlanceIds !== null) {
      this.onlineServedGlanceIds = Thrift.copyList(args.onlineServedGlanceIds, [null]);
    }
    if (args.offlineSeenGlanceIds !== undefined && args.offlineSeenGlanceIds !== null) {
      this.offlineSeenGlanceIds = Thrift.copyList(args.offlineSeenGlanceIds, [null]);
    }
    if (args.onlineUnseenGlanceIds !== undefined && args.onlineUnseenGlanceIds !== null) {
      this.onlineUnseenGlanceIds = Thrift.copyList(args.onlineUnseenGlanceIds, [null]);
    }
    if (args.predictedImpressions !== undefined && args.predictedImpressions !== null) {
      this.predictedImpressions = new PredictedImpressions(args.predictedImpressions);
    }
    if (args.appsOnDevice !== undefined && args.appsOnDevice !== null) {
      this.appsOnDevice = Thrift.copyList(args.appsOnDevice, [null]);
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
  }
};
UserSummary.prototype = {};
UserSummary.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size1460 = 0;
        var _rtmp31464;
        this.servingHistory = [];
        var _etype1463 = 0;
        _rtmp31464 = input.readListBegin();
        _etype1463 = _rtmp31464.etype;
        _size1460 = _rtmp31464.size;
        for (var _i1465 = 0; _i1465 < _size1460; ++_i1465)
        {
          var elem1466 = null;
          elem1466 = new GlancesServed();
          elem1466.read(input);
          this.servingHistory.push(elem1466);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.ver = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size1467 = 0;
        var _rtmp31471;
        this.onlineServingHistory = [];
        var _etype1470 = 0;
        _rtmp31471 = input.readListBegin();
        _etype1470 = _rtmp31471.etype;
        _size1467 = _rtmp31471.size;
        for (var _i1472 = 0; _i1472 < _size1467; ++_i1472)
        {
          var elem1473 = null;
          elem1473 = new OnlineGlancesServedWithSeen();
          elem1473.read(input);
          this.onlineServingHistory.push(elem1473);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.onlineGlancesServed = new OnlineGlancesServed();
        this.onlineGlancesServed.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.SET) {
        var _size1474 = 0;
        var _rtmp31478;
        this.onlineServedGlanceIds = [];
        var _etype1477 = 0;
        _rtmp31478 = input.readSetBegin();
        _etype1477 = _rtmp31478.etype;
        _size1474 = _rtmp31478.size;
        for (var _i1479 = 0; _i1479 < _size1474; ++_i1479)
        {
          var elem1480 = null;
          elem1480 = input.readI64().value;
          this.onlineServedGlanceIds.push(elem1480);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.SET) {
        var _size1481 = 0;
        var _rtmp31485;
        this.offlineSeenGlanceIds = [];
        var _etype1484 = 0;
        _rtmp31485 = input.readSetBegin();
        _etype1484 = _rtmp31485.etype;
        _size1481 = _rtmp31485.size;
        for (var _i1486 = 0; _i1486 < _size1481; ++_i1486)
        {
          var elem1487 = null;
          elem1487 = input.readI64().value;
          this.offlineSeenGlanceIds.push(elem1487);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.SET) {
        var _size1488 = 0;
        var _rtmp31492;
        this.onlineUnseenGlanceIds = [];
        var _etype1491 = 0;
        _rtmp31492 = input.readSetBegin();
        _etype1491 = _rtmp31492.etype;
        _size1488 = _rtmp31492.size;
        for (var _i1493 = 0; _i1493 < _size1488; ++_i1493)
        {
          var elem1494 = null;
          elem1494 = input.readI64().value;
          this.onlineUnseenGlanceIds.push(elem1494);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRUCT) {
        this.predictedImpressions = new PredictedImpressions();
        this.predictedImpressions.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size1495 = 0;
        var _rtmp31499;
        this.appsOnDevice = [];
        var _etype1498 = 0;
        _rtmp31499 = input.readListBegin();
        _etype1498 = _rtmp31499.etype;
        _size1495 = _rtmp31499.size;
        for (var _i1500 = 0; _i1500 < _size1495; ++_i1500)
        {
          var elem1501 = null;
          elem1501 = input.readString().value;
          this.appsOnDevice.push(elem1501);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.region = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserSummary.prototype.write = function(output) {
  output.writeStructBegin('UserSummary');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 2);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.servingHistory !== null && this.servingHistory !== undefined) {
    output.writeFieldBegin('servingHistory', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.servingHistory.length);
    for (var iter1502 in this.servingHistory)
    {
      if (this.servingHistory.hasOwnProperty(iter1502))
      {
        iter1502 = this.servingHistory[iter1502];
        iter1502.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.ver !== null && this.ver !== undefined) {
    output.writeFieldBegin('ver', Thrift.Type.I32, 4);
    output.writeI32(this.ver);
    output.writeFieldEnd();
  }
  if (this.onlineServingHistory !== null && this.onlineServingHistory !== undefined) {
    output.writeFieldBegin('onlineServingHistory', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.onlineServingHistory.length);
    for (var iter1503 in this.onlineServingHistory)
    {
      if (this.onlineServingHistory.hasOwnProperty(iter1503))
      {
        iter1503 = this.onlineServingHistory[iter1503];
        iter1503.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.onlineGlancesServed !== null && this.onlineGlancesServed !== undefined) {
    output.writeFieldBegin('onlineGlancesServed', Thrift.Type.STRUCT, 6);
    this.onlineGlancesServed.write(output);
    output.writeFieldEnd();
  }
  if (this.onlineServedGlanceIds !== null && this.onlineServedGlanceIds !== undefined) {
    output.writeFieldBegin('onlineServedGlanceIds', Thrift.Type.SET, 7);
    output.writeSetBegin(Thrift.Type.I64, this.onlineServedGlanceIds.length);
    for (var iter1504 in this.onlineServedGlanceIds)
    {
      if (this.onlineServedGlanceIds.hasOwnProperty(iter1504))
      {
        iter1504 = this.onlineServedGlanceIds[iter1504];
        output.writeI64(iter1504);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.offlineSeenGlanceIds !== null && this.offlineSeenGlanceIds !== undefined) {
    output.writeFieldBegin('offlineSeenGlanceIds', Thrift.Type.SET, 8);
    output.writeSetBegin(Thrift.Type.I64, this.offlineSeenGlanceIds.length);
    for (var iter1505 in this.offlineSeenGlanceIds)
    {
      if (this.offlineSeenGlanceIds.hasOwnProperty(iter1505))
      {
        iter1505 = this.offlineSeenGlanceIds[iter1505];
        output.writeI64(iter1505);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.onlineUnseenGlanceIds !== null && this.onlineUnseenGlanceIds !== undefined) {
    output.writeFieldBegin('onlineUnseenGlanceIds', Thrift.Type.SET, 9);
    output.writeSetBegin(Thrift.Type.I64, this.onlineUnseenGlanceIds.length);
    for (var iter1506 in this.onlineUnseenGlanceIds)
    {
      if (this.onlineUnseenGlanceIds.hasOwnProperty(iter1506))
      {
        iter1506 = this.onlineUnseenGlanceIds[iter1506];
        output.writeI64(iter1506);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.predictedImpressions !== null && this.predictedImpressions !== undefined) {
    output.writeFieldBegin('predictedImpressions', Thrift.Type.STRUCT, 10);
    this.predictedImpressions.write(output);
    output.writeFieldEnd();
  }
  if (this.appsOnDevice !== null && this.appsOnDevice !== undefined) {
    output.writeFieldBegin('appsOnDevice', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRING, this.appsOnDevice.length);
    for (var iter1507 in this.appsOnDevice)
    {
      if (this.appsOnDevice.hasOwnProperty(iter1507))
      {
        iter1507 = this.appsOnDevice[iter1507];
        output.writeString(iter1507);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 12);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.STRING, 13);
    output.writeString(this.region);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RewardCredit = function(args) {
  this.transId = null;
  this.ruleId = null;
  this.evtName = null;
  this.transAmt = null;
  this.balance = null;
  this.ts = null;
  this.status = null;
  this.streakDay = null;
  this.source = null;
  this.rewardType = null;
  this.rewardAsset = null;
  if (args) {
    if (args.transId !== undefined && args.transId !== null) {
      this.transId = args.transId;
    }
    if (args.ruleId !== undefined && args.ruleId !== null) {
      this.ruleId = args.ruleId;
    }
    if (args.evtName !== undefined && args.evtName !== null) {
      this.evtName = args.evtName;
    }
    if (args.transAmt !== undefined && args.transAmt !== null) {
      this.transAmt = args.transAmt;
    }
    if (args.balance !== undefined && args.balance !== null) {
      this.balance = args.balance;
    }
    if (args.ts !== undefined && args.ts !== null) {
      this.ts = args.ts;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.streakDay !== undefined && args.streakDay !== null) {
      this.streakDay = args.streakDay;
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    }
    if (args.rewardType !== undefined && args.rewardType !== null) {
      this.rewardType = args.rewardType;
    }
    if (args.rewardAsset !== undefined && args.rewardAsset !== null) {
      this.rewardAsset = args.rewardAsset;
    }
  }
};
RewardCredit.prototype = {};
RewardCredit.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.transId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.ruleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.evtName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.transAmt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.balance = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.ts = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.status = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.streakDay = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.rewardType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.rewardAsset = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RewardCredit.prototype.write = function(output) {
  output.writeStructBegin('RewardCredit');
  if (this.transId !== null && this.transId !== undefined) {
    output.writeFieldBegin('transId', Thrift.Type.STRING, 1);
    output.writeString(this.transId);
    output.writeFieldEnd();
  }
  if (this.ruleId !== null && this.ruleId !== undefined) {
    output.writeFieldBegin('ruleId', Thrift.Type.STRING, 2);
    output.writeString(this.ruleId);
    output.writeFieldEnd();
  }
  if (this.evtName !== null && this.evtName !== undefined) {
    output.writeFieldBegin('evtName', Thrift.Type.STRING, 3);
    output.writeString(this.evtName);
    output.writeFieldEnd();
  }
  if (this.transAmt !== null && this.transAmt !== undefined) {
    output.writeFieldBegin('transAmt', Thrift.Type.I64, 4);
    output.writeI64(this.transAmt);
    output.writeFieldEnd();
  }
  if (this.balance !== null && this.balance !== undefined) {
    output.writeFieldBegin('balance', Thrift.Type.I64, 5);
    output.writeI64(this.balance);
    output.writeFieldEnd();
  }
  if (this.ts !== null && this.ts !== undefined) {
    output.writeFieldBegin('ts', Thrift.Type.I64, 6);
    output.writeI64(this.ts);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I32, 7);
    output.writeI32(this.status);
    output.writeFieldEnd();
  }
  if (this.streakDay !== null && this.streakDay !== undefined) {
    output.writeFieldBegin('streakDay', Thrift.Type.STRING, 8);
    output.writeString(this.streakDay);
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.STRING, 9);
    output.writeString(this.source);
    output.writeFieldEnd();
  }
  if (this.rewardType !== null && this.rewardType !== undefined) {
    output.writeFieldBegin('rewardType', Thrift.Type.STRING, 10);
    output.writeString(this.rewardType);
    output.writeFieldEnd();
  }
  if (this.rewardAsset !== null && this.rewardAsset !== undefined) {
    output.writeFieldBegin('rewardAsset', Thrift.Type.STRING, 11);
    output.writeString(this.rewardAsset);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RewardDebit = function(args) {
  this.transId = null;
  this.ruleId = null;
  this.transType = null;
  this.transAmt = null;
  this.balance = null;
  this.ts = null;
  this.typeCount = null;
  this.status = null;
  this.source = null;
  if (args) {
    if (args.transId !== undefined && args.transId !== null) {
      this.transId = args.transId;
    }
    if (args.ruleId !== undefined && args.ruleId !== null) {
      this.ruleId = args.ruleId;
    }
    if (args.transType !== undefined && args.transType !== null) {
      this.transType = args.transType;
    }
    if (args.transAmt !== undefined && args.transAmt !== null) {
      this.transAmt = args.transAmt;
    }
    if (args.balance !== undefined && args.balance !== null) {
      this.balance = args.balance;
    }
    if (args.ts !== undefined && args.ts !== null) {
      this.ts = args.ts;
    }
    if (args.typeCount !== undefined && args.typeCount !== null) {
      this.typeCount = args.typeCount;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    }
  }
};
RewardDebit.prototype = {};
RewardDebit.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.transId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.ruleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.transType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.transAmt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.balance = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.ts = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.typeCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.status = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RewardDebit.prototype.write = function(output) {
  output.writeStructBegin('RewardDebit');
  if (this.transId !== null && this.transId !== undefined) {
    output.writeFieldBegin('transId', Thrift.Type.STRING, 1);
    output.writeString(this.transId);
    output.writeFieldEnd();
  }
  if (this.ruleId !== null && this.ruleId !== undefined) {
    output.writeFieldBegin('ruleId', Thrift.Type.STRING, 2);
    output.writeString(this.ruleId);
    output.writeFieldEnd();
  }
  if (this.transType !== null && this.transType !== undefined) {
    output.writeFieldBegin('transType', Thrift.Type.I32, 3);
    output.writeI32(this.transType);
    output.writeFieldEnd();
  }
  if (this.transAmt !== null && this.transAmt !== undefined) {
    output.writeFieldBegin('transAmt', Thrift.Type.I64, 4);
    output.writeI64(this.transAmt);
    output.writeFieldEnd();
  }
  if (this.balance !== null && this.balance !== undefined) {
    output.writeFieldBegin('balance', Thrift.Type.I64, 5);
    output.writeI64(this.balance);
    output.writeFieldEnd();
  }
  if (this.ts !== null && this.ts !== undefined) {
    output.writeFieldBegin('ts', Thrift.Type.I64, 6);
    output.writeI64(this.ts);
    output.writeFieldEnd();
  }
  if (this.typeCount !== null && this.typeCount !== undefined) {
    output.writeFieldBegin('typeCount', Thrift.Type.I32, 7);
    output.writeI32(this.typeCount);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I32, 8);
    output.writeI32(this.status);
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.STRING, 9);
    output.writeString(this.source);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RewardTransaction = function(args) {
  this.debits = null;
  this.credits = null;
  if (args) {
    if (args.debits !== undefined && args.debits !== null) {
      this.debits = Thrift.copyList(args.debits, [RewardDebit]);
    }
    if (args.credits !== undefined && args.credits !== null) {
      this.credits = Thrift.copyList(args.credits, [RewardCredit]);
    }
  }
};
RewardTransaction.prototype = {};
RewardTransaction.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1508 = 0;
        var _rtmp31512;
        this.debits = [];
        var _etype1511 = 0;
        _rtmp31512 = input.readListBegin();
        _etype1511 = _rtmp31512.etype;
        _size1508 = _rtmp31512.size;
        for (var _i1513 = 0; _i1513 < _size1508; ++_i1513)
        {
          var elem1514 = null;
          elem1514 = new RewardDebit();
          elem1514.read(input);
          this.debits.push(elem1514);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1515 = 0;
        var _rtmp31519;
        this.credits = [];
        var _etype1518 = 0;
        _rtmp31519 = input.readListBegin();
        _etype1518 = _rtmp31519.etype;
        _size1515 = _rtmp31519.size;
        for (var _i1520 = 0; _i1520 < _size1515; ++_i1520)
        {
          var elem1521 = null;
          elem1521 = new RewardCredit();
          elem1521.read(input);
          this.credits.push(elem1521);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RewardTransaction.prototype.write = function(output) {
  output.writeStructBegin('RewardTransaction');
  if (this.debits !== null && this.debits !== undefined) {
    output.writeFieldBegin('debits', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.debits.length);
    for (var iter1522 in this.debits)
    {
      if (this.debits.hasOwnProperty(iter1522))
      {
        iter1522 = this.debits[iter1522];
        iter1522.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.credits !== null && this.credits !== undefined) {
    output.writeFieldBegin('credits', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.credits.length);
    for (var iter1523 in this.credits)
    {
      if (this.credits.hasOwnProperty(iter1523))
      {
        iter1523 = this.credits[iter1523];
        iter1523.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Winning = function(args) {
  this.id = null;
  this.type = null;
  this.amt = null;
  this.ts = null;
  this.curr = null;
  this.status = null;
  this.redeemed = null;
  this.refId = null;
  this.transId = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    }
    if (args.amt !== undefined && args.amt !== null) {
      this.amt = args.amt;
    }
    if (args.ts !== undefined && args.ts !== null) {
      this.ts = args.ts;
    }
    if (args.curr !== undefined && args.curr !== null) {
      this.curr = args.curr;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.redeemed !== undefined && args.redeemed !== null) {
      this.redeemed = args.redeemed;
    }
    if (args.refId !== undefined && args.refId !== null) {
      this.refId = args.refId;
    }
    if (args.transId !== undefined && args.transId !== null) {
      this.transId = args.transId;
    }
  }
};
Winning.prototype = {};
Winning.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.DOUBLE) {
        this.amt = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.ts = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.curr = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.status = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.redeemed = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.refId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.transId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Winning.prototype.write = function(output) {
  output.writeStructBegin('Winning');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.I32, 2);
    output.writeI32(this.type);
    output.writeFieldEnd();
  }
  if (this.amt !== null && this.amt !== undefined) {
    output.writeFieldBegin('amt', Thrift.Type.DOUBLE, 3);
    output.writeDouble(this.amt);
    output.writeFieldEnd();
  }
  if (this.ts !== null && this.ts !== undefined) {
    output.writeFieldBegin('ts', Thrift.Type.I64, 4);
    output.writeI64(this.ts);
    output.writeFieldEnd();
  }
  if (this.curr !== null && this.curr !== undefined) {
    output.writeFieldBegin('curr', Thrift.Type.STRING, 5);
    output.writeString(this.curr);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I32, 6);
    output.writeI32(this.status);
    output.writeFieldEnd();
  }
  if (this.redeemed !== null && this.redeemed !== undefined) {
    output.writeFieldBegin('redeemed', Thrift.Type.BOOL, 7);
    output.writeBool(this.redeemed);
    output.writeFieldEnd();
  }
  if (this.refId !== null && this.refId !== undefined) {
    output.writeFieldBegin('refId', Thrift.Type.STRING, 8);
    output.writeString(this.refId);
    output.writeFieldEnd();
  }
  if (this.transId !== null && this.transId !== undefined) {
    output.writeFieldBegin('transId', Thrift.Type.STRING, 9);
    output.writeString(this.transId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RewardEvent = function(args) {
  this.id = null;
  this.name = null;
  this.dayCount = null;
  this.lifeCount = null;
  this.createdAt = null;
  this.updatedAt = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.dayCount !== undefined && args.dayCount !== null) {
      this.dayCount = args.dayCount;
    }
    if (args.lifeCount !== undefined && args.lifeCount !== null) {
      this.lifeCount = args.lifeCount;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
  }
};
RewardEvent.prototype = {};
RewardEvent.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.dayCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.lifeCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.createdAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.updatedAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RewardEvent.prototype.write = function(output) {
  output.writeStructBegin('RewardEvent');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.dayCount !== null && this.dayCount !== undefined) {
    output.writeFieldBegin('dayCount', Thrift.Type.I32, 3);
    output.writeI32(this.dayCount);
    output.writeFieldEnd();
  }
  if (this.lifeCount !== null && this.lifeCount !== undefined) {
    output.writeFieldBegin('lifeCount', Thrift.Type.I32, 4);
    output.writeI32(this.lifeCount);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.I64, 5);
    output.writeI64(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.I64, 6);
    output.writeI64(this.updatedAt);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UserReward = function(args) {
  this.id = null;
  this.userId = null;
  this.cSpent = null;
  this.cEarn = null;
  this.balance = null;
  this.winnings = null;
  this.transactions = null;
  this.events = null;
  this.cEarnToday = null;
  this.cEarnUpdatedAt = null;
  this._etag = null;
  this.engmtLvl = null;
  this.streakDay = null;
  this.engmtLvlUpdatedAt = null;
  this.streakDayUpdatedAt = null;
  this.sourceTransactLog = null;
  this.currStreakFirstEarnAt = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    }
    if (args.cSpent !== undefined && args.cSpent !== null) {
      this.cSpent = args.cSpent;
    }
    if (args.cEarn !== undefined && args.cEarn !== null) {
      this.cEarn = args.cEarn;
    }
    if (args.balance !== undefined && args.balance !== null) {
      this.balance = args.balance;
    }
    if (args.winnings !== undefined && args.winnings !== null) {
      this.winnings = Thrift.copyList(args.winnings, [Winning]);
    }
    if (args.transactions !== undefined && args.transactions !== null) {
      this.transactions = new RewardTransaction(args.transactions);
    }
    if (args.events !== undefined && args.events !== null) {
      this.events = Thrift.copyList(args.events, [RewardEvent]);
    }
    if (args.cEarnToday !== undefined && args.cEarnToday !== null) {
      this.cEarnToday = args.cEarnToday;
    }
    if (args.cEarnUpdatedAt !== undefined && args.cEarnUpdatedAt !== null) {
      this.cEarnUpdatedAt = args.cEarnUpdatedAt;
    }
    if (args._etag !== undefined && args._etag !== null) {
      this._etag = args._etag;
    }
    if (args.engmtLvl !== undefined && args.engmtLvl !== null) {
      this.engmtLvl = args.engmtLvl;
    }
    if (args.streakDay !== undefined && args.streakDay !== null) {
      this.streakDay = args.streakDay;
    }
    if (args.engmtLvlUpdatedAt !== undefined && args.engmtLvlUpdatedAt !== null) {
      this.engmtLvlUpdatedAt = args.engmtLvlUpdatedAt;
    }
    if (args.streakDayUpdatedAt !== undefined && args.streakDayUpdatedAt !== null) {
      this.streakDayUpdatedAt = args.streakDayUpdatedAt;
    }
    if (args.sourceTransactLog !== undefined && args.sourceTransactLog !== null) {
      this.sourceTransactLog = Thrift.copyMap(args.sourceTransactLog, [null]);
    }
    if (args.currStreakFirstEarnAt !== undefined && args.currStreakFirstEarnAt !== null) {
      this.currStreakFirstEarnAt = args.currStreakFirstEarnAt;
    }
  }
};
UserReward.prototype = {};
UserReward.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.cSpent = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.cEarn = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.balance = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size1524 = 0;
        var _rtmp31528;
        this.winnings = [];
        var _etype1527 = 0;
        _rtmp31528 = input.readListBegin();
        _etype1527 = _rtmp31528.etype;
        _size1524 = _rtmp31528.size;
        for (var _i1529 = 0; _i1529 < _size1524; ++_i1529)
        {
          var elem1530 = null;
          elem1530 = new Winning();
          elem1530.read(input);
          this.winnings.push(elem1530);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRUCT) {
        this.transactions = new RewardTransaction();
        this.transactions.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size1531 = 0;
        var _rtmp31535;
        this.events = [];
        var _etype1534 = 0;
        _rtmp31535 = input.readListBegin();
        _etype1534 = _rtmp31535.etype;
        _size1531 = _rtmp31535.size;
        for (var _i1536 = 0; _i1536 < _size1531; ++_i1536)
        {
          var elem1537 = null;
          elem1537 = new RewardEvent();
          elem1537.read(input);
          this.events.push(elem1537);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I64) {
        this.cEarnToday = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I64) {
        this.cEarnUpdatedAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this._etag = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.engmtLvl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.streakDay = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.I64) {
        this.engmtLvlUpdatedAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.I64) {
        this.streakDayUpdatedAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.MAP) {
        var _size1538 = 0;
        var _rtmp31542;
        this.sourceTransactLog = {};
        var _ktype1539 = 0;
        var _vtype1540 = 0;
        _rtmp31542 = input.readMapBegin();
        _ktype1539 = _rtmp31542.ktype;
        _vtype1540 = _rtmp31542.vtype;
        _size1538 = _rtmp31542.size;
        for (var _i1543 = 0; _i1543 < _size1538; ++_i1543)
        {
          if (_i1543 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key1544 = null;
          var val1545 = null;
          key1544 = input.readString().value;
          val1545 = input.readI64().value;
          this.sourceTransactLog[key1544] = val1545;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.I64) {
        this.currStreakFirstEarnAt = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserReward.prototype.write = function(output) {
  output.writeStructBegin('UserReward');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 2);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.cSpent !== null && this.cSpent !== undefined) {
    output.writeFieldBegin('cSpent', Thrift.Type.I64, 3);
    output.writeI64(this.cSpent);
    output.writeFieldEnd();
  }
  if (this.cEarn !== null && this.cEarn !== undefined) {
    output.writeFieldBegin('cEarn', Thrift.Type.I64, 4);
    output.writeI64(this.cEarn);
    output.writeFieldEnd();
  }
  if (this.balance !== null && this.balance !== undefined) {
    output.writeFieldBegin('balance', Thrift.Type.I64, 5);
    output.writeI64(this.balance);
    output.writeFieldEnd();
  }
  if (this.winnings !== null && this.winnings !== undefined) {
    output.writeFieldBegin('winnings', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.STRUCT, this.winnings.length);
    for (var iter1546 in this.winnings)
    {
      if (this.winnings.hasOwnProperty(iter1546))
      {
        iter1546 = this.winnings[iter1546];
        iter1546.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.transactions !== null && this.transactions !== undefined) {
    output.writeFieldBegin('transactions', Thrift.Type.STRUCT, 7);
    this.transactions.write(output);
    output.writeFieldEnd();
  }
  if (this.events !== null && this.events !== undefined) {
    output.writeFieldBegin('events', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRUCT, this.events.length);
    for (var iter1547 in this.events)
    {
      if (this.events.hasOwnProperty(iter1547))
      {
        iter1547 = this.events[iter1547];
        iter1547.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.cEarnToday !== null && this.cEarnToday !== undefined) {
    output.writeFieldBegin('cEarnToday', Thrift.Type.I64, 9);
    output.writeI64(this.cEarnToday);
    output.writeFieldEnd();
  }
  if (this.cEarnUpdatedAt !== null && this.cEarnUpdatedAt !== undefined) {
    output.writeFieldBegin('cEarnUpdatedAt', Thrift.Type.I64, 10);
    output.writeI64(this.cEarnUpdatedAt);
    output.writeFieldEnd();
  }
  if (this._etag !== null && this._etag !== undefined) {
    output.writeFieldBegin('_etag', Thrift.Type.STRING, 11);
    output.writeString(this._etag);
    output.writeFieldEnd();
  }
  if (this.engmtLvl !== null && this.engmtLvl !== undefined) {
    output.writeFieldBegin('engmtLvl', Thrift.Type.STRING, 12);
    output.writeString(this.engmtLvl);
    output.writeFieldEnd();
  }
  if (this.streakDay !== null && this.streakDay !== undefined) {
    output.writeFieldBegin('streakDay', Thrift.Type.STRING, 13);
    output.writeString(this.streakDay);
    output.writeFieldEnd();
  }
  if (this.engmtLvlUpdatedAt !== null && this.engmtLvlUpdatedAt !== undefined) {
    output.writeFieldBegin('engmtLvlUpdatedAt', Thrift.Type.I64, 14);
    output.writeI64(this.engmtLvlUpdatedAt);
    output.writeFieldEnd();
  }
  if (this.streakDayUpdatedAt !== null && this.streakDayUpdatedAt !== undefined) {
    output.writeFieldBegin('streakDayUpdatedAt', Thrift.Type.I64, 15);
    output.writeI64(this.streakDayUpdatedAt);
    output.writeFieldEnd();
  }
  if (this.sourceTransactLog !== null && this.sourceTransactLog !== undefined) {
    output.writeFieldBegin('sourceTransactLog', Thrift.Type.MAP, 16);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.I64, Thrift.objectLength(this.sourceTransactLog));
    for (var kiter1548 in this.sourceTransactLog)
    {
      if (this.sourceTransactLog.hasOwnProperty(kiter1548))
      {
        var viter1549 = this.sourceTransactLog[kiter1548];
        output.writeString(kiter1548);
        output.writeI64(viter1549);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.currStreakFirstEarnAt !== null && this.currStreakFirstEarnAt !== undefined) {
    output.writeFieldBegin('currStreakFirstEarnAt', Thrift.Type.I64, 17);
    output.writeI64(this.currStreakFirstEarnAt);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OnlineRequestSummaryMessage = function(args) {
  this.userId = null;
  this.servedTimeInSecs = null;
  this.onlineServedGlancesDelta = null;
  this.offlineSeenGlancesDelta = null;
  this.onlineUnseenGlancesDelta = null;
  if (args) {
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    }
    if (args.servedTimeInSecs !== undefined && args.servedTimeInSecs !== null) {
      this.servedTimeInSecs = args.servedTimeInSecs;
    }
    if (args.onlineServedGlancesDelta !== undefined && args.onlineServedGlancesDelta !== null) {
      this.onlineServedGlancesDelta = Thrift.copyList(args.onlineServedGlancesDelta, [null]);
    }
    if (args.offlineSeenGlancesDelta !== undefined && args.offlineSeenGlancesDelta !== null) {
      this.offlineSeenGlancesDelta = Thrift.copyList(args.offlineSeenGlancesDelta, [null]);
    }
    if (args.onlineUnseenGlancesDelta !== undefined && args.onlineUnseenGlancesDelta !== null) {
      this.onlineUnseenGlancesDelta = Thrift.copyList(args.onlineUnseenGlancesDelta, [null]);
    }
  }
};
OnlineRequestSummaryMessage.prototype = {};
OnlineRequestSummaryMessage.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.servedTimeInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.SET) {
        var _size1550 = 0;
        var _rtmp31554;
        this.onlineServedGlancesDelta = [];
        var _etype1553 = 0;
        _rtmp31554 = input.readSetBegin();
        _etype1553 = _rtmp31554.etype;
        _size1550 = _rtmp31554.size;
        for (var _i1555 = 0; _i1555 < _size1550; ++_i1555)
        {
          var elem1556 = null;
          elem1556 = input.readI64().value;
          this.onlineServedGlancesDelta.push(elem1556);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.SET) {
        var _size1557 = 0;
        var _rtmp31561;
        this.offlineSeenGlancesDelta = [];
        var _etype1560 = 0;
        _rtmp31561 = input.readSetBegin();
        _etype1560 = _rtmp31561.etype;
        _size1557 = _rtmp31561.size;
        for (var _i1562 = 0; _i1562 < _size1557; ++_i1562)
        {
          var elem1563 = null;
          elem1563 = input.readI64().value;
          this.offlineSeenGlancesDelta.push(elem1563);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.SET) {
        var _size1564 = 0;
        var _rtmp31568;
        this.onlineUnseenGlancesDelta = [];
        var _etype1567 = 0;
        _rtmp31568 = input.readSetBegin();
        _etype1567 = _rtmp31568.etype;
        _size1564 = _rtmp31568.size;
        for (var _i1569 = 0; _i1569 < _size1564; ++_i1569)
        {
          var elem1570 = null;
          elem1570 = input.readI64().value;
          this.onlineUnseenGlancesDelta.push(elem1570);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OnlineRequestSummaryMessage.prototype.write = function(output) {
  output.writeStructBegin('OnlineRequestSummaryMessage');
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 1);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.servedTimeInSecs !== null && this.servedTimeInSecs !== undefined) {
    output.writeFieldBegin('servedTimeInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.servedTimeInSecs);
    output.writeFieldEnd();
  }
  if (this.onlineServedGlancesDelta !== null && this.onlineServedGlancesDelta !== undefined) {
    output.writeFieldBegin('onlineServedGlancesDelta', Thrift.Type.SET, 3);
    output.writeSetBegin(Thrift.Type.I64, this.onlineServedGlancesDelta.length);
    for (var iter1571 in this.onlineServedGlancesDelta)
    {
      if (this.onlineServedGlancesDelta.hasOwnProperty(iter1571))
      {
        iter1571 = this.onlineServedGlancesDelta[iter1571];
        output.writeI64(iter1571);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.offlineSeenGlancesDelta !== null && this.offlineSeenGlancesDelta !== undefined) {
    output.writeFieldBegin('offlineSeenGlancesDelta', Thrift.Type.SET, 4);
    output.writeSetBegin(Thrift.Type.I64, this.offlineSeenGlancesDelta.length);
    for (var iter1572 in this.offlineSeenGlancesDelta)
    {
      if (this.offlineSeenGlancesDelta.hasOwnProperty(iter1572))
      {
        iter1572 = this.offlineSeenGlancesDelta[iter1572];
        output.writeI64(iter1572);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.onlineUnseenGlancesDelta !== null && this.onlineUnseenGlancesDelta !== undefined) {
    output.writeFieldBegin('onlineUnseenGlancesDelta', Thrift.Type.SET, 5);
    output.writeSetBegin(Thrift.Type.I64, this.onlineUnseenGlancesDelta.length);
    for (var iter1573 in this.onlineUnseenGlancesDelta)
    {
      if (this.onlineUnseenGlancesDelta.hasOwnProperty(iter1573))
      {
        iter1573 = this.onlineUnseenGlancesDelta[iter1573];
        output.writeI64(iter1573);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

NotificationCta = function(args) {
  this.text = null;
  this.id = null;
  this.link = null;
  if (args) {
    if (args.text !== undefined && args.text !== null) {
      this.text = args.text;
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.link !== undefined && args.link !== null) {
      this.link = args.link;
    }
  }
};
NotificationCta.prototype = {};
NotificationCta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.link = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

NotificationCta.prototype.write = function(output) {
  output.writeStructBegin('NotificationCta');
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRING, 1);
    output.writeString(this.text);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 2);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.link !== null && this.link !== undefined) {
    output.writeFieldBegin('link', Thrift.Type.STRING, 3);
    output.writeString(this.link);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UserEngageNotification = function(args) {
  this.title = null;
  this.body = null;
  this.image = null;
  this.action = null;
  this.deeplink = null;
  this.alert = null;
  this.partnerId = null;
  this.testPush = null;
  this.regionId = null;
  this.sticky = null;
  this.targetFilePath = null;
  this.glanceEnabled = null;
  this.id = null;
  this.cta = null;
  if (args) {
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.body !== undefined && args.body !== null) {
      this.body = args.body;
    }
    if (args.image !== undefined && args.image !== null) {
      this.image = args.image;
    }
    if (args.action !== undefined && args.action !== null) {
      this.action = args.action;
    }
    if (args.deeplink !== undefined && args.deeplink !== null) {
      this.deeplink = args.deeplink;
    }
    if (args.alert !== undefined && args.alert !== null) {
      this.alert = args.alert;
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    }
    if (args.testPush !== undefined && args.testPush !== null) {
      this.testPush = args.testPush;
    }
    if (args.regionId !== undefined && args.regionId !== null) {
      this.regionId = args.regionId;
    }
    if (args.sticky !== undefined && args.sticky !== null) {
      this.sticky = args.sticky;
    }
    if (args.targetFilePath !== undefined && args.targetFilePath !== null) {
      this.targetFilePath = args.targetFilePath;
    }
    if (args.glanceEnabled !== undefined && args.glanceEnabled !== null) {
      this.glanceEnabled = args.glanceEnabled;
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.cta !== undefined && args.cta !== null) {
      this.cta = Thrift.copyList(args.cta, [NotificationCta]);
    }
  }
};
UserEngageNotification.prototype = {};
UserEngageNotification.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.body = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.image = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.action = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.deeplink = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.alert = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.testPush = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.regionId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.BOOL) {
        this.sticky = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.targetFilePath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.BOOL) {
        this.glanceEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.LIST) {
        var _size1574 = 0;
        var _rtmp31578;
        this.cta = [];
        var _etype1577 = 0;
        _rtmp31578 = input.readListBegin();
        _etype1577 = _rtmp31578.etype;
        _size1574 = _rtmp31578.size;
        for (var _i1579 = 0; _i1579 < _size1574; ++_i1579)
        {
          var elem1580 = null;
          elem1580 = new NotificationCta();
          elem1580.read(input);
          this.cta.push(elem1580);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserEngageNotification.prototype.write = function(output) {
  output.writeStructBegin('UserEngageNotification');
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 1);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.body !== null && this.body !== undefined) {
    output.writeFieldBegin('body', Thrift.Type.STRING, 2);
    output.writeString(this.body);
    output.writeFieldEnd();
  }
  if (this.image !== null && this.image !== undefined) {
    output.writeFieldBegin('image', Thrift.Type.STRING, 3);
    output.writeString(this.image);
    output.writeFieldEnd();
  }
  if (this.action !== null && this.action !== undefined) {
    output.writeFieldBegin('action', Thrift.Type.STRING, 4);
    output.writeString(this.action);
    output.writeFieldEnd();
  }
  if (this.deeplink !== null && this.deeplink !== undefined) {
    output.writeFieldBegin('deeplink', Thrift.Type.STRING, 5);
    output.writeString(this.deeplink);
    output.writeFieldEnd();
  }
  if (this.alert !== null && this.alert !== undefined) {
    output.writeFieldBegin('alert', Thrift.Type.BOOL, 6);
    output.writeBool(this.alert);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 7);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.testPush !== null && this.testPush !== undefined) {
    output.writeFieldBegin('testPush', Thrift.Type.BOOL, 8);
    output.writeBool(this.testPush);
    output.writeFieldEnd();
  }
  if (this.regionId !== null && this.regionId !== undefined) {
    output.writeFieldBegin('regionId', Thrift.Type.STRING, 9);
    output.writeString(this.regionId);
    output.writeFieldEnd();
  }
  if (this.sticky !== null && this.sticky !== undefined) {
    output.writeFieldBegin('sticky', Thrift.Type.BOOL, 10);
    output.writeBool(this.sticky);
    output.writeFieldEnd();
  }
  if (this.targetFilePath !== null && this.targetFilePath !== undefined) {
    output.writeFieldBegin('targetFilePath', Thrift.Type.STRING, 11);
    output.writeString(this.targetFilePath);
    output.writeFieldEnd();
  }
  if (this.glanceEnabled !== null && this.glanceEnabled !== undefined) {
    output.writeFieldBegin('glanceEnabled', Thrift.Type.BOOL, 12);
    output.writeBool(this.glanceEnabled);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 13);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.cta !== null && this.cta !== undefined) {
    output.writeFieldBegin('cta', Thrift.Type.LIST, 14);
    output.writeListBegin(Thrift.Type.STRUCT, this.cta.length);
    for (var iter1581 in this.cta)
    {
      if (this.cta.hasOwnProperty(iter1581))
      {
        iter1581 = this.cta[iter1581];
        iter1581.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancePublishData = function(args) {
  this.glancePair = null;
  this.partner = null;
  this.partnerMeta = null;
  this.region = null;
  if (args) {
    if (args.glancePair !== undefined && args.glancePair !== null) {
      this.glancePair = new GlancePair(args.glancePair);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glancePair is unset!');
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
    if (args.partnerMeta !== undefined && args.partnerMeta !== null) {
      this.partnerMeta = new PartnerMeta(args.partnerMeta);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partnerMeta is unset!');
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field region is unset!');
    }
  }
};
GlancePublishData.prototype = {};
GlancePublishData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.glancePair = new GlancePair();
        this.glancePair.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.partnerMeta = new PartnerMeta();
        this.partnerMeta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancePublishData.prototype.write = function(output) {
  output.writeStructBegin('GlancePublishData');
  if (this.glancePair !== null && this.glancePair !== undefined) {
    output.writeFieldBegin('glancePair', Thrift.Type.STRUCT, 1);
    this.glancePair.write(output);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 2);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.partnerMeta !== null && this.partnerMeta !== undefined) {
    output.writeFieldBegin('partnerMeta', Thrift.Type.STRUCT, 3);
    this.partnerMeta.write(output);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 4);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceTvTranscodedVideoMeta = function(args) {
  this.eId = null;
  this.categoryIds = null;
  this.createdBy = null;
  this.title = null;
  this.deeplink = null;
  this.originalUrl = null;
  this.transcodedUrl = null;
  this.state = null;
  this.gpId = null;
  this.uId = null;
  this.channelNames = null;
  this.userHandle = null;
  this.origin = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.updatedAt = null;
  this.languageId = null;
  this.videoCreatedAtInSecs = null;
  this.dataSaverVideoUrl = null;
  if (args) {
    if (args.eId !== undefined && args.eId !== null) {
      this.eId = args.eId;
    }
    if (args.categoryIds !== undefined && args.categoryIds !== null) {
      this.categoryIds = Thrift.copyList(args.categoryIds, [null]);
    }
    if (args.createdBy !== undefined && args.createdBy !== null) {
      this.createdBy = args.createdBy;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.deeplink !== undefined && args.deeplink !== null) {
      this.deeplink = args.deeplink;
    }
    if (args.originalUrl !== undefined && args.originalUrl !== null) {
      this.originalUrl = args.originalUrl;
    }
    if (args.transcodedUrl !== undefined && args.transcodedUrl !== null) {
      this.transcodedUrl = args.transcodedUrl;
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    }
    if (args.gpId !== undefined && args.gpId !== null) {
      this.gpId = args.gpId;
    }
    if (args.uId !== undefined && args.uId !== null) {
      this.uId = args.uId;
    }
    if (args.channelNames !== undefined && args.channelNames !== null) {
      this.channelNames = Thrift.copyList(args.channelNames, [null]);
    }
    if (args.userHandle !== undefined && args.userHandle !== null) {
      this.userHandle = args.userHandle;
    }
    if (args.origin !== undefined && args.origin !== null) {
      this.origin = args.origin;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.languageId !== undefined && args.languageId !== null) {
      this.languageId = args.languageId;
    }
    if (args.videoCreatedAtInSecs !== undefined && args.videoCreatedAtInSecs !== null) {
      this.videoCreatedAtInSecs = args.videoCreatedAtInSecs;
    }
    if (args.dataSaverVideoUrl !== undefined && args.dataSaverVideoUrl !== null) {
      this.dataSaverVideoUrl = args.dataSaverVideoUrl;
    }
  }
};
GlanceTvTranscodedVideoMeta.prototype = {};
GlanceTvTranscodedVideoMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.eId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1582 = 0;
        var _rtmp31586;
        this.categoryIds = [];
        var _etype1585 = 0;
        _rtmp31586 = input.readListBegin();
        _etype1585 = _rtmp31586.etype;
        _size1582 = _rtmp31586.size;
        for (var _i1587 = 0; _i1587 < _size1582; ++_i1587)
        {
          var elem1588 = null;
          elem1588 = input.readString().value;
          this.categoryIds.push(elem1588);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.createdBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.deeplink = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.originalUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.transcodedUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.state = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.gpId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.uId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size1589 = 0;
        var _rtmp31593;
        this.channelNames = [];
        var _etype1592 = 0;
        _rtmp31593 = input.readListBegin();
        _etype1592 = _rtmp31593.etype;
        _size1589 = _rtmp31593.size;
        for (var _i1594 = 0; _i1594 < _size1589; ++_i1594)
        {
          var elem1595 = null;
          elem1595 = input.readString().value;
          this.channelNames.push(elem1595);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.userHandle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.origin = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.languageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.I64) {
        this.videoCreatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.dataSaverVideoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceTvTranscodedVideoMeta.prototype.write = function(output) {
  output.writeStructBegin('GlanceTvTranscodedVideoMeta');
  if (this.eId !== null && this.eId !== undefined) {
    output.writeFieldBegin('eId', Thrift.Type.STRING, 1);
    output.writeString(this.eId);
    output.writeFieldEnd();
  }
  if (this.categoryIds !== null && this.categoryIds !== undefined) {
    output.writeFieldBegin('categoryIds', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.categoryIds.length);
    for (var iter1596 in this.categoryIds)
    {
      if (this.categoryIds.hasOwnProperty(iter1596))
      {
        iter1596 = this.categoryIds[iter1596];
        output.writeString(iter1596);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.createdBy !== null && this.createdBy !== undefined) {
    output.writeFieldBegin('createdBy', Thrift.Type.STRING, 3);
    output.writeString(this.createdBy);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 4);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.deeplink !== null && this.deeplink !== undefined) {
    output.writeFieldBegin('deeplink', Thrift.Type.STRING, 5);
    output.writeString(this.deeplink);
    output.writeFieldEnd();
  }
  if (this.originalUrl !== null && this.originalUrl !== undefined) {
    output.writeFieldBegin('originalUrl', Thrift.Type.STRING, 6);
    output.writeString(this.originalUrl);
    output.writeFieldEnd();
  }
  if (this.transcodedUrl !== null && this.transcodedUrl !== undefined) {
    output.writeFieldBegin('transcodedUrl', Thrift.Type.STRING, 7);
    output.writeString(this.transcodedUrl);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.STRING, 8);
    output.writeString(this.state);
    output.writeFieldEnd();
  }
  if (this.gpId !== null && this.gpId !== undefined) {
    output.writeFieldBegin('gpId', Thrift.Type.STRING, 9);
    output.writeString(this.gpId);
    output.writeFieldEnd();
  }
  if (this.uId !== null && this.uId !== undefined) {
    output.writeFieldBegin('uId', Thrift.Type.STRING, 10);
    output.writeString(this.uId);
    output.writeFieldEnd();
  }
  if (this.channelNames !== null && this.channelNames !== undefined) {
    output.writeFieldBegin('channelNames', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRING, this.channelNames.length);
    for (var iter1597 in this.channelNames)
    {
      if (this.channelNames.hasOwnProperty(iter1597))
      {
        iter1597 = this.channelNames[iter1597];
        output.writeString(iter1597);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.userHandle !== null && this.userHandle !== undefined) {
    output.writeFieldBegin('userHandle', Thrift.Type.STRING, 12);
    output.writeString(this.userHandle);
    output.writeFieldEnd();
  }
  if (this.origin !== null && this.origin !== undefined) {
    output.writeFieldBegin('origin', Thrift.Type.STRING, 13);
    output.writeString(this.origin);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 14);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 15);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 16);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.languageId !== null && this.languageId !== undefined) {
    output.writeFieldBegin('languageId', Thrift.Type.STRING, 17);
    output.writeString(this.languageId);
    output.writeFieldEnd();
  }
  if (this.videoCreatedAtInSecs !== null && this.videoCreatedAtInSecs !== undefined) {
    output.writeFieldBegin('videoCreatedAtInSecs', Thrift.Type.I64, 18);
    output.writeI64(this.videoCreatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.dataSaverVideoUrl !== null && this.dataSaverVideoUrl !== undefined) {
    output.writeFieldBegin('dataSaverVideoUrl', Thrift.Type.STRING, 19);
    output.writeString(this.dataSaverVideoUrl);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceTvVideo = function(args) {
  this.videoId = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.title = null;
  this.url = null;
  this.glanceProvider = null;
  this.status = null;
  this.suggestedCategories = null;
  this.category = null;
  this.isArchived = null;
  this.rejectionReason = null;
  if (args) {
    if (args.videoId !== undefined && args.videoId !== null) {
      this.videoId = args.videoId;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.glanceProvider !== undefined && args.glanceProvider !== null) {
      this.glanceProvider = new GtvProvider(args.glanceProvider);
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.suggestedCategories !== undefined && args.suggestedCategories !== null) {
      this.suggestedCategories = Thrift.copyList(args.suggestedCategories, [null]);
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.isArchived !== undefined && args.isArchived !== null) {
      this.isArchived = args.isArchived;
    }
    if (args.rejectionReason !== undefined && args.rejectionReason !== null) {
      this.rejectionReason = args.rejectionReason;
    }
  }
};
GlanceTvVideo.prototype = {};
GlanceTvVideo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.videoId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceProvider = new GtvProvider();
        this.glanceProvider.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.status = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size1598 = 0;
        var _rtmp31602;
        this.suggestedCategories = [];
        var _etype1601 = 0;
        _rtmp31602 = input.readListBegin();
        _etype1601 = _rtmp31602.etype;
        _size1598 = _rtmp31602.size;
        for (var _i1603 = 0; _i1603 < _size1598; ++_i1603)
        {
          var elem1604 = null;
          elem1604 = input.readString().value;
          this.suggestedCategories.push(elem1604);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.BOOL) {
        this.isArchived = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.rejectionReason = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceTvVideo.prototype.write = function(output) {
  output.writeStructBegin('GlanceTvVideo');
  if (this.videoId !== null && this.videoId !== undefined) {
    output.writeFieldBegin('videoId', Thrift.Type.STRING, 1);
    output.writeString(this.videoId);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 3);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 4);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 5);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.glanceProvider !== null && this.glanceProvider !== undefined) {
    output.writeFieldBegin('glanceProvider', Thrift.Type.STRUCT, 6);
    this.glanceProvider.write(output);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I32, 7);
    output.writeI32(this.status);
    output.writeFieldEnd();
  }
  if (this.suggestedCategories !== null && this.suggestedCategories !== undefined) {
    output.writeFieldBegin('suggestedCategories', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRING, this.suggestedCategories.length);
    for (var iter1605 in this.suggestedCategories)
    {
      if (this.suggestedCategories.hasOwnProperty(iter1605))
      {
        iter1605 = this.suggestedCategories[iter1605];
        output.writeString(iter1605);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 9);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.isArchived !== null && this.isArchived !== undefined) {
    output.writeFieldBegin('isArchived', Thrift.Type.BOOL, 10);
    output.writeBool(this.isArchived);
    output.writeFieldEnd();
  }
  if (this.rejectionReason !== null && this.rejectionReason !== undefined) {
    output.writeFieldBegin('rejectionReason', Thrift.Type.STRING, 11);
    output.writeString(this.rejectionReason);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceTvCategoryMap = function(args) {
  this.mapId = null;
  this.glanceCategory = null;
  this.nlpClientCategory = null;
  if (args) {
    if (args.mapId !== undefined && args.mapId !== null) {
      this.mapId = args.mapId;
    }
    if (args.glanceCategory !== undefined && args.glanceCategory !== null) {
      this.glanceCategory = args.glanceCategory;
    }
    if (args.nlpClientCategory !== undefined && args.nlpClientCategory !== null) {
      this.nlpClientCategory = args.nlpClientCategory;
    }
  }
};
GlanceTvCategoryMap.prototype = {};
GlanceTvCategoryMap.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.mapId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.glanceCategory = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.nlpClientCategory = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceTvCategoryMap.prototype.write = function(output) {
  output.writeStructBegin('GlanceTvCategoryMap');
  if (this.mapId !== null && this.mapId !== undefined) {
    output.writeFieldBegin('mapId', Thrift.Type.STRING, 1);
    output.writeString(this.mapId);
    output.writeFieldEnd();
  }
  if (this.glanceCategory !== null && this.glanceCategory !== undefined) {
    output.writeFieldBegin('glanceCategory', Thrift.Type.STRING, 2);
    output.writeString(this.glanceCategory);
    output.writeFieldEnd();
  }
  if (this.nlpClientCategory !== null && this.nlpClientCategory !== undefined) {
    output.writeFieldBegin('nlpClientCategory', Thrift.Type.STRING, 3);
    output.writeString(this.nlpClientCategory);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SiloGlanceTvVideo = function(args) {
  this.videoId = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.title = null;
  this.url = null;
  this.glanceProvider = null;
  this.status = null;
  this.suggestedCategories = null;
  this.category = null;
  this.isArchived = null;
  this.rejectionReason = null;
  if (args) {
    if (args.videoId !== undefined && args.videoId !== null) {
      this.videoId = args.videoId;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.glanceProvider !== undefined && args.glanceProvider !== null) {
      this.glanceProvider = args.glanceProvider;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.suggestedCategories !== undefined && args.suggestedCategories !== null) {
      this.suggestedCategories = Thrift.copyList(args.suggestedCategories, [null]);
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.isArchived !== undefined && args.isArchived !== null) {
      this.isArchived = args.isArchived;
    }
    if (args.rejectionReason !== undefined && args.rejectionReason !== null) {
      this.rejectionReason = args.rejectionReason;
    }
  }
};
SiloGlanceTvVideo.prototype = {};
SiloGlanceTvVideo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.videoId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.glanceProvider = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.status = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size1606 = 0;
        var _rtmp31610;
        this.suggestedCategories = [];
        var _etype1609 = 0;
        _rtmp31610 = input.readListBegin();
        _etype1609 = _rtmp31610.etype;
        _size1606 = _rtmp31610.size;
        for (var _i1611 = 0; _i1611 < _size1606; ++_i1611)
        {
          var elem1612 = null;
          elem1612 = input.readString().value;
          this.suggestedCategories.push(elem1612);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.BOOL) {
        this.isArchived = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.rejectionReason = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SiloGlanceTvVideo.prototype.write = function(output) {
  output.writeStructBegin('SiloGlanceTvVideo');
  if (this.videoId !== null && this.videoId !== undefined) {
    output.writeFieldBegin('videoId', Thrift.Type.STRING, 1);
    output.writeString(this.videoId);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 3);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 4);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 5);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.glanceProvider !== null && this.glanceProvider !== undefined) {
    output.writeFieldBegin('glanceProvider', Thrift.Type.STRING, 6);
    output.writeString(this.glanceProvider);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I32, 7);
    output.writeI32(this.status);
    output.writeFieldEnd();
  }
  if (this.suggestedCategories !== null && this.suggestedCategories !== undefined) {
    output.writeFieldBegin('suggestedCategories', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRING, this.suggestedCategories.length);
    for (var iter1613 in this.suggestedCategories)
    {
      if (this.suggestedCategories.hasOwnProperty(iter1613))
      {
        iter1613 = this.suggestedCategories[iter1613];
        output.writeString(iter1613);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 9);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.isArchived !== null && this.isArchived !== undefined) {
    output.writeFieldBegin('isArchived', Thrift.Type.BOOL, 10);
    output.writeBool(this.isArchived);
    output.writeFieldEnd();
  }
  if (this.rejectionReason !== null && this.rejectionReason !== undefined) {
    output.writeFieldBegin('rejectionReason', Thrift.Type.STRING, 11);
    output.writeString(this.rejectionReason);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GameTournament = function(args) {
  this.id = null;
  this.name = null;
  this.startTime = null;
  this.endTime = null;
  this.isActive = null;
  this.status = null;
  this.gameSerpScore = null;
  this.gameBannerAssetUrl = null;
  this.gameId = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.startTime !== undefined && args.startTime !== null) {
      this.startTime = args.startTime;
    }
    if (args.endTime !== undefined && args.endTime !== null) {
      this.endTime = args.endTime;
    }
    if (args.isActive !== undefined && args.isActive !== null) {
      this.isActive = args.isActive;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.gameSerpScore !== undefined && args.gameSerpScore !== null) {
      this.gameSerpScore = args.gameSerpScore;
    }
    if (args.gameBannerAssetUrl !== undefined && args.gameBannerAssetUrl !== null) {
      this.gameBannerAssetUrl = args.gameBannerAssetUrl;
    }
    if (args.gameId !== undefined && args.gameId !== null) {
      this.gameId = args.gameId;
    }
  }
};
GameTournament.prototype = {};
GameTournament.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.startTime = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.endTime = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.isActive = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.status = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I64) {
        this.gameSerpScore = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.gameBannerAssetUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.gameId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GameTournament.prototype.write = function(output) {
  output.writeStructBegin('GameTournament');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.startTime !== null && this.startTime !== undefined) {
    output.writeFieldBegin('startTime', Thrift.Type.STRING, 3);
    output.writeString(this.startTime);
    output.writeFieldEnd();
  }
  if (this.endTime !== null && this.endTime !== undefined) {
    output.writeFieldBegin('endTime', Thrift.Type.STRING, 4);
    output.writeString(this.endTime);
    output.writeFieldEnd();
  }
  if (this.isActive !== null && this.isActive !== undefined) {
    output.writeFieldBegin('isActive', Thrift.Type.BOOL, 5);
    output.writeBool(this.isActive);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.I32, 6);
    output.writeI32(this.status);
    output.writeFieldEnd();
  }
  if (this.gameSerpScore !== null && this.gameSerpScore !== undefined) {
    output.writeFieldBegin('gameSerpScore', Thrift.Type.I64, 7);
    output.writeI64(this.gameSerpScore);
    output.writeFieldEnd();
  }
  if (this.gameBannerAssetUrl !== null && this.gameBannerAssetUrl !== undefined) {
    output.writeFieldBegin('gameBannerAssetUrl', Thrift.Type.STRING, 8);
    output.writeString(this.gameBannerAssetUrl);
    output.writeFieldEnd();
  }
  if (this.gameId !== null && this.gameId !== undefined) {
    output.writeFieldBegin('gameId', Thrift.Type.STRING, 9);
    output.writeString(this.gameId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

IpBlacklistEntity = function(args) {
  this.ipStart = null;
  this.ipEnd = null;
  if (args) {
    if (args.ipStart !== undefined && args.ipStart !== null) {
      this.ipStart = args.ipStart;
    }
    if (args.ipEnd !== undefined && args.ipEnd !== null) {
      this.ipEnd = args.ipEnd;
    }
  }
};
IpBlacklistEntity.prototype = {};
IpBlacklistEntity.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.ipStart = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.ipEnd = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

IpBlacklistEntity.prototype.write = function(output) {
  output.writeStructBegin('IpBlacklistEntity');
  if (this.ipStart !== null && this.ipStart !== undefined) {
    output.writeFieldBegin('ipStart', Thrift.Type.I64, 1);
    output.writeI64(this.ipStart);
    output.writeFieldEnd();
  }
  if (this.ipEnd !== null && this.ipEnd !== undefined) {
    output.writeFieldBegin('ipEnd', Thrift.Type.I64, 2);
    output.writeI64(this.ipEnd);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UserAgentEntity = function(args) {
  this.id = null;
  this.patternType = null;
  this.regex = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.patternType !== undefined && args.patternType !== null) {
      this.patternType = args.patternType;
    }
    if (args.regex !== undefined && args.regex !== null) {
      this.regex = args.regex;
    }
  }
};
UserAgentEntity.prototype = {};
UserAgentEntity.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.patternType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.regex = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserAgentEntity.prototype.write = function(output) {
  output.writeStructBegin('UserAgentEntity');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.patternType !== null && this.patternType !== undefined) {
    output.writeFieldBegin('patternType', Thrift.Type.STRING, 2);
    output.writeString(this.patternType);
    output.writeFieldEnd();
  }
  if (this.regex !== null && this.regex !== undefined) {
    output.writeFieldBegin('regex', Thrift.Type.STRING, 3);
    output.writeString(this.regex);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CampaignObjective = function(args) {
  this.name = null;
  if (args) {
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
  }
};
CampaignObjective.prototype = {};
CampaignObjective.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CampaignObjective.prototype.write = function(output) {
  output.writeStructBegin('CampaignObjective');
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 1);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceImpressionCapping = function(args) {
  this.glanceId = null;
  this.dailyImpressionsLimit = null;
  this.totalImpressionsLimit = null;
  this.dailyImpressionsRendered = null;
  this.totalImpressionsRendered = null;
  this.isActive = null;
  this.limitPercentage = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.isActiveUpdatedAtInSecs = null;
  this.updatedAt = null;
  this.impressionCappingId = null;
  this.dailyImpressionsRunningDate = null;
  this.beaconDailyImpressionsRendered = null;
  this.beaconTotalImpressionsRendered = null;
  this.servingToleranceFactor = null;
  this.servingToleranceRuleVersion = null;
  this.impressionsLimitType = null;
  this.impRate = null;
  if (args) {
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    }
    if (args.dailyImpressionsLimit !== undefined && args.dailyImpressionsLimit !== null) {
      this.dailyImpressionsLimit = args.dailyImpressionsLimit;
    }
    if (args.totalImpressionsLimit !== undefined && args.totalImpressionsLimit !== null) {
      this.totalImpressionsLimit = args.totalImpressionsLimit;
    }
    if (args.dailyImpressionsRendered !== undefined && args.dailyImpressionsRendered !== null) {
      this.dailyImpressionsRendered = args.dailyImpressionsRendered;
    }
    if (args.totalImpressionsRendered !== undefined && args.totalImpressionsRendered !== null) {
      this.totalImpressionsRendered = args.totalImpressionsRendered;
    }
    if (args.isActive !== undefined && args.isActive !== null) {
      this.isActive = args.isActive;
    }
    if (args.limitPercentage !== undefined && args.limitPercentage !== null) {
      this.limitPercentage = args.limitPercentage;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.isActiveUpdatedAtInSecs !== undefined && args.isActiveUpdatedAtInSecs !== null) {
      this.isActiveUpdatedAtInSecs = args.isActiveUpdatedAtInSecs;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.impressionCappingId !== undefined && args.impressionCappingId !== null) {
      this.impressionCappingId = args.impressionCappingId;
    }
    if (args.dailyImpressionsRunningDate !== undefined && args.dailyImpressionsRunningDate !== null) {
      this.dailyImpressionsRunningDate = args.dailyImpressionsRunningDate;
    }
    if (args.beaconDailyImpressionsRendered !== undefined && args.beaconDailyImpressionsRendered !== null) {
      this.beaconDailyImpressionsRendered = args.beaconDailyImpressionsRendered;
    }
    if (args.beaconTotalImpressionsRendered !== undefined && args.beaconTotalImpressionsRendered !== null) {
      this.beaconTotalImpressionsRendered = args.beaconTotalImpressionsRendered;
    }
    if (args.servingToleranceFactor !== undefined && args.servingToleranceFactor !== null) {
      this.servingToleranceFactor = args.servingToleranceFactor;
    }
    if (args.servingToleranceRuleVersion !== undefined && args.servingToleranceRuleVersion !== null) {
      this.servingToleranceRuleVersion = args.servingToleranceRuleVersion;
    }
    if (args.impressionsLimitType !== undefined && args.impressionsLimitType !== null) {
      this.impressionsLimitType = args.impressionsLimitType;
    }
    if (args.impRate !== undefined && args.impRate !== null) {
      this.impRate = args.impRate;
    }
  }
};
GlanceImpressionCapping.prototype = {};
GlanceImpressionCapping.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.dailyImpressionsLimit = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.totalImpressionsLimit = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.dailyImpressionsRendered = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.totalImpressionsRendered = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.isActive = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.DOUBLE) {
        this.limitPercentage = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I64) {
        this.isActiveUpdatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.impressionCappingId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.dailyImpressionsRunningDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.I64) {
        this.beaconDailyImpressionsRendered = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.I64) {
        this.beaconTotalImpressionsRendered = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.DOUBLE) {
        this.servingToleranceFactor = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.servingToleranceRuleVersion = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.I32) {
        this.impressionsLimitType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.DOUBLE) {
        this.impRate = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceImpressionCapping.prototype.write = function(output) {
  output.writeStructBegin('GlanceImpressionCapping');
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.dailyImpressionsLimit !== null && this.dailyImpressionsLimit !== undefined) {
    output.writeFieldBegin('dailyImpressionsLimit', Thrift.Type.I64, 2);
    output.writeI64(this.dailyImpressionsLimit);
    output.writeFieldEnd();
  }
  if (this.totalImpressionsLimit !== null && this.totalImpressionsLimit !== undefined) {
    output.writeFieldBegin('totalImpressionsLimit', Thrift.Type.I64, 3);
    output.writeI64(this.totalImpressionsLimit);
    output.writeFieldEnd();
  }
  if (this.dailyImpressionsRendered !== null && this.dailyImpressionsRendered !== undefined) {
    output.writeFieldBegin('dailyImpressionsRendered', Thrift.Type.I64, 4);
    output.writeI64(this.dailyImpressionsRendered);
    output.writeFieldEnd();
  }
  if (this.totalImpressionsRendered !== null && this.totalImpressionsRendered !== undefined) {
    output.writeFieldBegin('totalImpressionsRendered', Thrift.Type.I64, 5);
    output.writeI64(this.totalImpressionsRendered);
    output.writeFieldEnd();
  }
  if (this.isActive !== null && this.isActive !== undefined) {
    output.writeFieldBegin('isActive', Thrift.Type.BOOL, 6);
    output.writeBool(this.isActive);
    output.writeFieldEnd();
  }
  if (this.limitPercentage !== null && this.limitPercentage !== undefined) {
    output.writeFieldBegin('limitPercentage', Thrift.Type.DOUBLE, 7);
    output.writeDouble(this.limitPercentage);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 8);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 9);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.isActiveUpdatedAtInSecs !== null && this.isActiveUpdatedAtInSecs !== undefined) {
    output.writeFieldBegin('isActiveUpdatedAtInSecs', Thrift.Type.I64, 10);
    output.writeI64(this.isActiveUpdatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 11);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.impressionCappingId !== null && this.impressionCappingId !== undefined) {
    output.writeFieldBegin('impressionCappingId', Thrift.Type.STRING, 12);
    output.writeString(this.impressionCappingId);
    output.writeFieldEnd();
  }
  if (this.dailyImpressionsRunningDate !== null && this.dailyImpressionsRunningDate !== undefined) {
    output.writeFieldBegin('dailyImpressionsRunningDate', Thrift.Type.STRING, 13);
    output.writeString(this.dailyImpressionsRunningDate);
    output.writeFieldEnd();
  }
  if (this.beaconDailyImpressionsRendered !== null && this.beaconDailyImpressionsRendered !== undefined) {
    output.writeFieldBegin('beaconDailyImpressionsRendered', Thrift.Type.I64, 14);
    output.writeI64(this.beaconDailyImpressionsRendered);
    output.writeFieldEnd();
  }
  if (this.beaconTotalImpressionsRendered !== null && this.beaconTotalImpressionsRendered !== undefined) {
    output.writeFieldBegin('beaconTotalImpressionsRendered', Thrift.Type.I64, 15);
    output.writeI64(this.beaconTotalImpressionsRendered);
    output.writeFieldEnd();
  }
  if (this.servingToleranceFactor !== null && this.servingToleranceFactor !== undefined) {
    output.writeFieldBegin('servingToleranceFactor', Thrift.Type.DOUBLE, 16);
    output.writeDouble(this.servingToleranceFactor);
    output.writeFieldEnd();
  }
  if (this.servingToleranceRuleVersion !== null && this.servingToleranceRuleVersion !== undefined) {
    output.writeFieldBegin('servingToleranceRuleVersion', Thrift.Type.STRING, 17);
    output.writeString(this.servingToleranceRuleVersion);
    output.writeFieldEnd();
  }
  if (this.impressionsLimitType !== null && this.impressionsLimitType !== undefined) {
    output.writeFieldBegin('impressionsLimitType', Thrift.Type.I32, 18);
    output.writeI32(this.impressionsLimitType);
    output.writeFieldEnd();
  }
  if (this.impRate !== null && this.impRate !== undefined) {
    output.writeFieldBegin('impRate', Thrift.Type.DOUBLE, 19);
    output.writeDouble(this.impRate);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CardpressImgMeta = function(args) {
  this.id = null;
  this.url = null;
  this.title = null;
  this.source_name = null;
  this.vernacular_title = null;
  this.headline = null;
  this.modified_headline = null;
  this.description = null;
  this.vernacular_description = null;
  this.binge_text = null;
  this.modified_binge_text = null;
  this.image_ids = null;
  this.binge_cards = null;
  this.category = null;
  this.image_template_selected = null;
  this.image_selected = null;
  this.status = null;
  this.cta_text = null;
  this.template_position = null;
  this.summary = null;
  this.bubble = null;
  this.language = null;
  this.image_author = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.source_name !== undefined && args.source_name !== null) {
      this.source_name = args.source_name;
    }
    if (args.vernacular_title !== undefined && args.vernacular_title !== null) {
      this.vernacular_title = args.vernacular_title;
    }
    if (args.headline !== undefined && args.headline !== null) {
      this.headline = args.headline;
    }
    if (args.modified_headline !== undefined && args.modified_headline !== null) {
      this.modified_headline = args.modified_headline;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.vernacular_description !== undefined && args.vernacular_description !== null) {
      this.vernacular_description = args.vernacular_description;
    }
    if (args.binge_text !== undefined && args.binge_text !== null) {
      this.binge_text = args.binge_text;
    }
    if (args.modified_binge_text !== undefined && args.modified_binge_text !== null) {
      this.modified_binge_text = args.modified_binge_text;
    }
    if (args.image_ids !== undefined && args.image_ids !== null) {
      this.image_ids = Thrift.copyList(args.image_ids, [null]);
    }
    if (args.binge_cards !== undefined && args.binge_cards !== null) {
      this.binge_cards = Thrift.copyList(args.binge_cards, [null]);
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.image_template_selected !== undefined && args.image_template_selected !== null) {
      this.image_template_selected = args.image_template_selected;
    }
    if (args.image_selected !== undefined && args.image_selected !== null) {
      this.image_selected = args.image_selected;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.cta_text !== undefined && args.cta_text !== null) {
      this.cta_text = args.cta_text;
    }
    if (args.template_position !== undefined && args.template_position !== null) {
      this.template_position = args.template_position;
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.bubble !== undefined && args.bubble !== null) {
      this.bubble = new Bubble(args.bubble);
    }
    if (args.language !== undefined && args.language !== null) {
      this.language = args.language;
    }
    if (args.image_author !== undefined && args.image_author !== null) {
      this.image_author = args.image_author;
    }
  }
};
CardpressImgMeta.prototype = {};
CardpressImgMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.source_name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.vernacular_title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.headline = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.modified_headline = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.vernacular_description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.binge_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.modified_binge_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.LIST) {
        var _size1614 = 0;
        var _rtmp31618;
        this.image_ids = [];
        var _etype1617 = 0;
        _rtmp31618 = input.readListBegin();
        _etype1617 = _rtmp31618.etype;
        _size1614 = _rtmp31618.size;
        for (var _i1619 = 0; _i1619 < _size1614; ++_i1619)
        {
          var elem1620 = null;
          elem1620 = input.readString().value;
          this.image_ids.push(elem1620);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size1621 = 0;
        var _rtmp31625;
        this.binge_cards = [];
        var _etype1624 = 0;
        _rtmp31625 = input.readListBegin();
        _etype1624 = _rtmp31625.etype;
        _size1621 = _rtmp31625.size;
        for (var _i1626 = 0; _i1626 < _size1621; ++_i1626)
        {
          var elem1627 = null;
          elem1627 = input.readString().value;
          this.binge_cards.push(elem1627);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.image_template_selected = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.image_selected = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.cta_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.template_position = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRUCT) {
        this.bubble = new Bubble();
        this.bubble.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRING) {
        this.language = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.image_author = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CardpressImgMeta.prototype.write = function(output) {
  output.writeStructBegin('CardpressImgMeta');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 2);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 3);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.source_name !== null && this.source_name !== undefined) {
    output.writeFieldBegin('source_name', Thrift.Type.STRING, 4);
    output.writeString(this.source_name);
    output.writeFieldEnd();
  }
  if (this.vernacular_title !== null && this.vernacular_title !== undefined) {
    output.writeFieldBegin('vernacular_title', Thrift.Type.STRING, 5);
    output.writeString(this.vernacular_title);
    output.writeFieldEnd();
  }
  if (this.headline !== null && this.headline !== undefined) {
    output.writeFieldBegin('headline', Thrift.Type.STRING, 6);
    output.writeString(this.headline);
    output.writeFieldEnd();
  }
  if (this.modified_headline !== null && this.modified_headline !== undefined) {
    output.writeFieldBegin('modified_headline', Thrift.Type.STRING, 7);
    output.writeString(this.modified_headline);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 8);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.vernacular_description !== null && this.vernacular_description !== undefined) {
    output.writeFieldBegin('vernacular_description', Thrift.Type.STRING, 9);
    output.writeString(this.vernacular_description);
    output.writeFieldEnd();
  }
  if (this.binge_text !== null && this.binge_text !== undefined) {
    output.writeFieldBegin('binge_text', Thrift.Type.STRING, 10);
    output.writeString(this.binge_text);
    output.writeFieldEnd();
  }
  if (this.modified_binge_text !== null && this.modified_binge_text !== undefined) {
    output.writeFieldBegin('modified_binge_text', Thrift.Type.STRING, 11);
    output.writeString(this.modified_binge_text);
    output.writeFieldEnd();
  }
  if (this.image_ids !== null && this.image_ids !== undefined) {
    output.writeFieldBegin('image_ids', Thrift.Type.LIST, 12);
    output.writeListBegin(Thrift.Type.STRING, this.image_ids.length);
    for (var iter1628 in this.image_ids)
    {
      if (this.image_ids.hasOwnProperty(iter1628))
      {
        iter1628 = this.image_ids[iter1628];
        output.writeString(iter1628);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.binge_cards !== null && this.binge_cards !== undefined) {
    output.writeFieldBegin('binge_cards', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRING, this.binge_cards.length);
    for (var iter1629 in this.binge_cards)
    {
      if (this.binge_cards.hasOwnProperty(iter1629))
      {
        iter1629 = this.binge_cards[iter1629];
        output.writeString(iter1629);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 14);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.image_template_selected !== null && this.image_template_selected !== undefined) {
    output.writeFieldBegin('image_template_selected', Thrift.Type.STRING, 15);
    output.writeString(this.image_template_selected);
    output.writeFieldEnd();
  }
  if (this.image_selected !== null && this.image_selected !== undefined) {
    output.writeFieldBegin('image_selected', Thrift.Type.STRING, 16);
    output.writeString(this.image_selected);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 17);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  if (this.cta_text !== null && this.cta_text !== undefined) {
    output.writeFieldBegin('cta_text', Thrift.Type.STRING, 18);
    output.writeString(this.cta_text);
    output.writeFieldEnd();
  }
  if (this.template_position !== null && this.template_position !== undefined) {
    output.writeFieldBegin('template_position', Thrift.Type.STRING, 19);
    output.writeString(this.template_position);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 20);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.bubble !== null && this.bubble !== undefined) {
    output.writeFieldBegin('bubble', Thrift.Type.STRUCT, 21);
    this.bubble.write(output);
    output.writeFieldEnd();
  }
  if (this.language !== null && this.language !== undefined) {
    output.writeFieldBegin('language', Thrift.Type.STRING, 22);
    output.writeString(this.language);
    output.writeFieldEnd();
  }
  if (this.image_author !== null && this.image_author !== undefined) {
    output.writeFieldBegin('image_author', Thrift.Type.STRING, 23);
    output.writeString(this.image_author);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OverlaySpec = function(args) {
  this.version = null;
  this.overlay = null;
  this.template = null;
  this.position = null;
  this.language = null;
  this.logo = null;
  this.font = null;
  this.url = null;
  this.otd_url = null;
  if (args) {
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
    if (args.overlay !== undefined && args.overlay !== null) {
      this.overlay = args.overlay;
    }
    if (args.template !== undefined && args.template !== null) {
      this.template = args.template;
    }
    if (args.position !== undefined && args.position !== null) {
      this.position = args.position;
    }
    if (args.language !== undefined && args.language !== null) {
      this.language = args.language;
    }
    if (args.logo !== undefined && args.logo !== null) {
      this.logo = args.logo;
    }
    if (args.font !== undefined && args.font !== null) {
      this.font = args.font;
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.otd_url !== undefined && args.otd_url !== null) {
      this.otd_url = args.otd_url;
    }
  }
};
OverlaySpec.prototype = {};
OverlaySpec.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.version = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.overlay = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.template = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.position = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.language = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.logo = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.font = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.otd_url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OverlaySpec.prototype.write = function(output) {
  output.writeStructBegin('OverlaySpec');
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.STRING, 1);
    output.writeString(this.version);
    output.writeFieldEnd();
  }
  if (this.overlay !== null && this.overlay !== undefined) {
    output.writeFieldBegin('overlay', Thrift.Type.STRING, 2);
    output.writeString(this.overlay);
    output.writeFieldEnd();
  }
  if (this.template !== null && this.template !== undefined) {
    output.writeFieldBegin('template', Thrift.Type.STRING, 3);
    output.writeString(this.template);
    output.writeFieldEnd();
  }
  if (this.position !== null && this.position !== undefined) {
    output.writeFieldBegin('position', Thrift.Type.STRING, 4);
    output.writeString(this.position);
    output.writeFieldEnd();
  }
  if (this.language !== null && this.language !== undefined) {
    output.writeFieldBegin('language', Thrift.Type.STRING, 5);
    output.writeString(this.language);
    output.writeFieldEnd();
  }
  if (this.logo !== null && this.logo !== undefined) {
    output.writeFieldBegin('logo', Thrift.Type.STRING, 6);
    output.writeString(this.logo);
    output.writeFieldEnd();
  }
  if (this.font !== null && this.font !== undefined) {
    output.writeFieldBegin('font', Thrift.Type.STRING, 7);
    output.writeString(this.font);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 8);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.otd_url !== null && this.otd_url !== undefined) {
    output.writeFieldBegin('otd_url', Thrift.Type.STRING, 9);
    output.writeString(this.otd_url);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PublishingOptions = function(args) {
  this.online_hl_only = null;
  this.seed_card_id = null;
  this.regionRequests = null;
  this.daily_digest = null;
  this.binge_only = null;
  this.glance_context_type = null;
  this.stickinessCount = null;
  this.priority = null;
  this.liveVideo = null;
  this.liveVideoPeek = null;
  this.videoCtaUrl = null;
  this.publishing_error = null;
  this.skipSummary = null;
  this.curationReason = null;
  this.shouldUnlock = null;
  this.userLevelDuration = null;
  this.noAds = null;
  this.online_only = null;
  if (args) {
    if (args.online_hl_only !== undefined && args.online_hl_only !== null) {
      this.online_hl_only = args.online_hl_only;
    }
    if (args.seed_card_id !== undefined && args.seed_card_id !== null) {
      this.seed_card_id = args.seed_card_id;
    }
    if (args.regionRequests !== undefined && args.regionRequests !== null) {
      this.regionRequests = Thrift.copyList(args.regionRequests, [RegionRequest]);
    }
    if (args.daily_digest !== undefined && args.daily_digest !== null) {
      this.daily_digest = args.daily_digest;
    }
    if (args.binge_only !== undefined && args.binge_only !== null) {
      this.binge_only = args.binge_only;
    }
    if (args.glance_context_type !== undefined && args.glance_context_type !== null) {
      this.glance_context_type = args.glance_context_type;
    }
    if (args.stickinessCount !== undefined && args.stickinessCount !== null) {
      this.stickinessCount = args.stickinessCount;
    }
    if (args.priority !== undefined && args.priority !== null) {
      this.priority = args.priority;
    }
    if (args.liveVideo !== undefined && args.liveVideo !== null) {
      this.liveVideo = args.liveVideo;
    }
    if (args.liveVideoPeek !== undefined && args.liveVideoPeek !== null) {
      this.liveVideoPeek = new LiveVideoPeek(args.liveVideoPeek);
    }
    if (args.videoCtaUrl !== undefined && args.videoCtaUrl !== null) {
      this.videoCtaUrl = args.videoCtaUrl;
    }
    if (args.publishing_error !== undefined && args.publishing_error !== null) {
      this.publishing_error = args.publishing_error;
    }
    if (args.skipSummary !== undefined && args.skipSummary !== null) {
      this.skipSummary = args.skipSummary;
    }
    if (args.curationReason !== undefined && args.curationReason !== null) {
      this.curationReason = args.curationReason;
    }
    if (args.shouldUnlock !== undefined && args.shouldUnlock !== null) {
      this.shouldUnlock = args.shouldUnlock;
    }
    if (args.userLevelDuration !== undefined && args.userLevelDuration !== null) {
      this.userLevelDuration = new Duration(args.userLevelDuration);
    }
    if (args.noAds !== undefined && args.noAds !== null) {
      this.noAds = args.noAds;
    }
    if (args.online_only !== undefined && args.online_only !== null) {
      this.online_only = args.online_only;
    }
  }
};
PublishingOptions.prototype = {};
PublishingOptions.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.online_hl_only = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.seed_card_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size1630 = 0;
        var _rtmp31634;
        this.regionRequests = [];
        var _etype1633 = 0;
        _rtmp31634 = input.readListBegin();
        _etype1633 = _rtmp31634.etype;
        _size1630 = _rtmp31634.size;
        for (var _i1635 = 0; _i1635 < _size1630; ++_i1635)
        {
          var elem1636 = null;
          elem1636 = new RegionRequest();
          elem1636.read(input);
          this.regionRequests.push(elem1636);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.daily_digest = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.binge_only = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.glance_context_type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.stickinessCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.priority = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.BOOL) {
        this.liveVideo = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRUCT) {
        this.liveVideoPeek = new LiveVideoPeek();
        this.liveVideoPeek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.videoCtaUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.publishing_error = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.BOOL) {
        this.skipSummary = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.curationReason = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldUnlock = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRUCT) {
        this.userLevelDuration = new Duration();
        this.userLevelDuration.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.BOOL) {
        this.noAds = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.BOOL) {
        this.online_only = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PublishingOptions.prototype.write = function(output) {
  output.writeStructBegin('PublishingOptions');
  if (this.online_hl_only !== null && this.online_hl_only !== undefined) {
    output.writeFieldBegin('online_hl_only', Thrift.Type.BOOL, 1);
    output.writeBool(this.online_hl_only);
    output.writeFieldEnd();
  }
  if (this.seed_card_id !== null && this.seed_card_id !== undefined) {
    output.writeFieldBegin('seed_card_id', Thrift.Type.STRING, 2);
    output.writeString(this.seed_card_id);
    output.writeFieldEnd();
  }
  if (this.regionRequests !== null && this.regionRequests !== undefined) {
    output.writeFieldBegin('regionRequests', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.regionRequests.length);
    for (var iter1637 in this.regionRequests)
    {
      if (this.regionRequests.hasOwnProperty(iter1637))
      {
        iter1637 = this.regionRequests[iter1637];
        iter1637.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.daily_digest !== null && this.daily_digest !== undefined) {
    output.writeFieldBegin('daily_digest', Thrift.Type.BOOL, 4);
    output.writeBool(this.daily_digest);
    output.writeFieldEnd();
  }
  if (this.binge_only !== null && this.binge_only !== undefined) {
    output.writeFieldBegin('binge_only', Thrift.Type.BOOL, 5);
    output.writeBool(this.binge_only);
    output.writeFieldEnd();
  }
  if (this.glance_context_type !== null && this.glance_context_type !== undefined) {
    output.writeFieldBegin('glance_context_type', Thrift.Type.I32, 6);
    output.writeI32(this.glance_context_type);
    output.writeFieldEnd();
  }
  if (this.stickinessCount !== null && this.stickinessCount !== undefined) {
    output.writeFieldBegin('stickinessCount', Thrift.Type.I32, 7);
    output.writeI32(this.stickinessCount);
    output.writeFieldEnd();
  }
  if (this.priority !== null && this.priority !== undefined) {
    output.writeFieldBegin('priority', Thrift.Type.STRING, 8);
    output.writeString(this.priority);
    output.writeFieldEnd();
  }
  if (this.liveVideo !== null && this.liveVideo !== undefined) {
    output.writeFieldBegin('liveVideo', Thrift.Type.BOOL, 9);
    output.writeBool(this.liveVideo);
    output.writeFieldEnd();
  }
  if (this.liveVideoPeek !== null && this.liveVideoPeek !== undefined) {
    output.writeFieldBegin('liveVideoPeek', Thrift.Type.STRUCT, 10);
    this.liveVideoPeek.write(output);
    output.writeFieldEnd();
  }
  if (this.videoCtaUrl !== null && this.videoCtaUrl !== undefined) {
    output.writeFieldBegin('videoCtaUrl', Thrift.Type.STRING, 11);
    output.writeString(this.videoCtaUrl);
    output.writeFieldEnd();
  }
  if (this.publishing_error !== null && this.publishing_error !== undefined) {
    output.writeFieldBegin('publishing_error', Thrift.Type.STRING, 12);
    output.writeString(this.publishing_error);
    output.writeFieldEnd();
  }
  if (this.skipSummary !== null && this.skipSummary !== undefined) {
    output.writeFieldBegin('skipSummary', Thrift.Type.BOOL, 13);
    output.writeBool(this.skipSummary);
    output.writeFieldEnd();
  }
  if (this.curationReason !== null && this.curationReason !== undefined) {
    output.writeFieldBegin('curationReason', Thrift.Type.STRING, 14);
    output.writeString(this.curationReason);
    output.writeFieldEnd();
  }
  if (this.shouldUnlock !== null && this.shouldUnlock !== undefined) {
    output.writeFieldBegin('shouldUnlock', Thrift.Type.BOOL, 15);
    output.writeBool(this.shouldUnlock);
    output.writeFieldEnd();
  }
  if (this.userLevelDuration !== null && this.userLevelDuration !== undefined) {
    output.writeFieldBegin('userLevelDuration', Thrift.Type.STRUCT, 16);
    this.userLevelDuration.write(output);
    output.writeFieldEnd();
  }
  if (this.noAds !== null && this.noAds !== undefined) {
    output.writeFieldBegin('noAds', Thrift.Type.BOOL, 17);
    output.writeBool(this.noAds);
    output.writeFieldEnd();
  }
  if (this.online_only !== null && this.online_only !== undefined) {
    output.writeFieldBegin('online_only', Thrift.Type.BOOL, 18);
    output.writeBool(this.online_only);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UrlMeta = function(args) {
  this.raw = null;
  if (args) {
    if (args.raw !== undefined && args.raw !== null) {
      this.raw = args.raw;
    }
  }
};
UrlMeta.prototype = {};
UrlMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.raw = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UrlMeta.prototype.write = function(output) {
  output.writeStructBegin('UrlMeta');
  if (this.raw !== null && this.raw !== undefined) {
    output.writeFieldBegin('raw', Thrift.Type.STRING, 1);
    output.writeString(this.raw);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancifiedVideoChunkInfo = function(args) {
  this.url = null;
  this.duration = null;
  this.sequence = null;
  if (args) {
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.duration !== undefined && args.duration !== null) {
      this.duration = args.duration;
    }
    if (args.sequence !== undefined && args.sequence !== null) {
      this.sequence = args.sequence;
    }
  }
};
GlancifiedVideoChunkInfo.prototype = {};
GlancifiedVideoChunkInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.duration = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.sequence = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancifiedVideoChunkInfo.prototype.write = function(output) {
  output.writeStructBegin('GlancifiedVideoChunkInfo');
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 1);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.duration !== null && this.duration !== undefined) {
    output.writeFieldBegin('duration', Thrift.Type.I32, 2);
    output.writeI32(this.duration);
    output.writeFieldEnd();
  }
  if (this.sequence !== null && this.sequence !== undefined) {
    output.writeFieldBegin('sequence', Thrift.Type.I32, 3);
    output.writeI32(this.sequence);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancifyVideoData = function(args) {
  this.start_time = null;
  this.end_time = null;
  this.glancified_video_chunk_info_list = null;
  this.request_id = null;
  this.bg_video_template_id = null;
  this.overlay_text = null;
  this.error_message = null;
  this.glancification_start_time = null;
  if (args) {
    if (args.start_time !== undefined && args.start_time !== null) {
      this.start_time = args.start_time;
    }
    if (args.end_time !== undefined && args.end_time !== null) {
      this.end_time = args.end_time;
    }
    if (args.glancified_video_chunk_info_list !== undefined && args.glancified_video_chunk_info_list !== null) {
      this.glancified_video_chunk_info_list = Thrift.copyList(args.glancified_video_chunk_info_list, [GlancifiedVideoChunkInfo]);
    }
    if (args.request_id !== undefined && args.request_id !== null) {
      this.request_id = args.request_id;
    }
    if (args.bg_video_template_id !== undefined && args.bg_video_template_id !== null) {
      this.bg_video_template_id = args.bg_video_template_id;
    }
    if (args.overlay_text !== undefined && args.overlay_text !== null) {
      this.overlay_text = args.overlay_text;
    }
    if (args.error_message !== undefined && args.error_message !== null) {
      this.error_message = args.error_message;
    }
    if (args.glancification_start_time !== undefined && args.glancification_start_time !== null) {
      this.glancification_start_time = args.glancification_start_time;
    }
  }
};
GlancifyVideoData.prototype = {};
GlancifyVideoData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.start_time = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.end_time = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size1638 = 0;
        var _rtmp31642;
        this.glancified_video_chunk_info_list = [];
        var _etype1641 = 0;
        _rtmp31642 = input.readListBegin();
        _etype1641 = _rtmp31642.etype;
        _size1638 = _rtmp31642.size;
        for (var _i1643 = 0; _i1643 < _size1638; ++_i1643)
        {
          var elem1644 = null;
          elem1644 = new GlancifiedVideoChunkInfo();
          elem1644.read(input);
          this.glancified_video_chunk_info_list.push(elem1644);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.request_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.bg_video_template_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.overlay_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.error_message = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.glancification_start_time = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancifyVideoData.prototype.write = function(output) {
  output.writeStructBegin('GlancifyVideoData');
  if (this.start_time !== null && this.start_time !== undefined) {
    output.writeFieldBegin('start_time', Thrift.Type.I32, 1);
    output.writeI32(this.start_time);
    output.writeFieldEnd();
  }
  if (this.end_time !== null && this.end_time !== undefined) {
    output.writeFieldBegin('end_time', Thrift.Type.I32, 2);
    output.writeI32(this.end_time);
    output.writeFieldEnd();
  }
  if (this.glancified_video_chunk_info_list !== null && this.glancified_video_chunk_info_list !== undefined) {
    output.writeFieldBegin('glancified_video_chunk_info_list', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.glancified_video_chunk_info_list.length);
    for (var iter1645 in this.glancified_video_chunk_info_list)
    {
      if (this.glancified_video_chunk_info_list.hasOwnProperty(iter1645))
      {
        iter1645 = this.glancified_video_chunk_info_list[iter1645];
        iter1645.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.request_id !== null && this.request_id !== undefined) {
    output.writeFieldBegin('request_id', Thrift.Type.STRING, 4);
    output.writeString(this.request_id);
    output.writeFieldEnd();
  }
  if (this.bg_video_template_id !== null && this.bg_video_template_id !== undefined) {
    output.writeFieldBegin('bg_video_template_id', Thrift.Type.STRING, 5);
    output.writeString(this.bg_video_template_id);
    output.writeFieldEnd();
  }
  if (this.overlay_text !== null && this.overlay_text !== undefined) {
    output.writeFieldBegin('overlay_text', Thrift.Type.STRING, 6);
    output.writeString(this.overlay_text);
    output.writeFieldEnd();
  }
  if (this.error_message !== null && this.error_message !== undefined) {
    output.writeFieldBegin('error_message', Thrift.Type.STRING, 8);
    output.writeString(this.error_message);
    output.writeFieldEnd();
  }
  if (this.glancification_start_time !== null && this.glancification_start_time !== undefined) {
    output.writeFieldBegin('glancification_start_time', Thrift.Type.STRING, 9);
    output.writeString(this.glancification_start_time);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OppoImageAssets = function(args) {
  this.url = null;
  this.image_size = null;
  if (args) {
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.image_size !== undefined && args.image_size !== null) {
      this.image_size = args.image_size;
    }
  }
};
OppoImageAssets.prototype = {};
OppoImageAssets.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.image_size = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OppoImageAssets.prototype.write = function(output) {
  output.writeStructBegin('OppoImageAssets');
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 1);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.image_size !== null && this.image_size !== undefined) {
    output.writeFieldBegin('image_size', Thrift.Type.STRING, 2);
    output.writeString(this.image_size);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

FastCardAssets = function(args) {
  this.ai_summary = null;
  this.fallback_summary = null;
  this.publisher_summary = null;
  this.banner_image_cropped = null;
  this.article_image_cropped = null;
  this.automated_image_cropped = null;
  this.summary = null;
  this.image = null;
  this.dominant_colour = null;
  this.pointer_summary = null;
  this.oppo_image_assets = null;
  if (args) {
    if (args.ai_summary !== undefined && args.ai_summary !== null) {
      this.ai_summary = args.ai_summary;
    }
    if (args.fallback_summary !== undefined && args.fallback_summary !== null) {
      this.fallback_summary = args.fallback_summary;
    }
    if (args.publisher_summary !== undefined && args.publisher_summary !== null) {
      this.publisher_summary = args.publisher_summary;
    }
    if (args.banner_image_cropped !== undefined && args.banner_image_cropped !== null) {
      this.banner_image_cropped = args.banner_image_cropped;
    }
    if (args.article_image_cropped !== undefined && args.article_image_cropped !== null) {
      this.article_image_cropped = args.article_image_cropped;
    }
    if (args.automated_image_cropped !== undefined && args.automated_image_cropped !== null) {
      this.automated_image_cropped = args.automated_image_cropped;
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.image !== undefined && args.image !== null) {
      this.image = args.image;
    }
    if (args.dominant_colour !== undefined && args.dominant_colour !== null) {
      this.dominant_colour = args.dominant_colour;
    }
    if (args.pointer_summary !== undefined && args.pointer_summary !== null) {
      this.pointer_summary = Thrift.copyMap(args.pointer_summary, [null]);
    }
    if (args.oppo_image_assets !== undefined && args.oppo_image_assets !== null) {
      this.oppo_image_assets = Thrift.copyList(args.oppo_image_assets, [OppoImageAssets]);
    }
  }
};
FastCardAssets.prototype = {};
FastCardAssets.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.ai_summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.fallback_summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.publisher_summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.banner_image_cropped = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.article_image_cropped = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.automated_image_cropped = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.image = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.dominant_colour = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.MAP) {
        var _size1646 = 0;
        var _rtmp31650;
        this.pointer_summary = {};
        var _ktype1647 = 0;
        var _vtype1648 = 0;
        _rtmp31650 = input.readMapBegin();
        _ktype1647 = _rtmp31650.ktype;
        _vtype1648 = _rtmp31650.vtype;
        _size1646 = _rtmp31650.size;
        for (var _i1651 = 0; _i1651 < _size1646; ++_i1651)
        {
          if (_i1651 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key1652 = null;
          var val1653 = null;
          key1652 = input.readString().value;
          val1653 = input.readString().value;
          this.pointer_summary[key1652] = val1653;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size1654 = 0;
        var _rtmp31658;
        this.oppo_image_assets = [];
        var _etype1657 = 0;
        _rtmp31658 = input.readListBegin();
        _etype1657 = _rtmp31658.etype;
        _size1654 = _rtmp31658.size;
        for (var _i1659 = 0; _i1659 < _size1654; ++_i1659)
        {
          var elem1660 = null;
          elem1660 = new OppoImageAssets();
          elem1660.read(input);
          this.oppo_image_assets.push(elem1660);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

FastCardAssets.prototype.write = function(output) {
  output.writeStructBegin('FastCardAssets');
  if (this.ai_summary !== null && this.ai_summary !== undefined) {
    output.writeFieldBegin('ai_summary', Thrift.Type.STRING, 1);
    output.writeString(this.ai_summary);
    output.writeFieldEnd();
  }
  if (this.fallback_summary !== null && this.fallback_summary !== undefined) {
    output.writeFieldBegin('fallback_summary', Thrift.Type.STRING, 2);
    output.writeString(this.fallback_summary);
    output.writeFieldEnd();
  }
  if (this.publisher_summary !== null && this.publisher_summary !== undefined) {
    output.writeFieldBegin('publisher_summary', Thrift.Type.STRING, 3);
    output.writeString(this.publisher_summary);
    output.writeFieldEnd();
  }
  if (this.banner_image_cropped !== null && this.banner_image_cropped !== undefined) {
    output.writeFieldBegin('banner_image_cropped', Thrift.Type.STRING, 4);
    output.writeString(this.banner_image_cropped);
    output.writeFieldEnd();
  }
  if (this.article_image_cropped !== null && this.article_image_cropped !== undefined) {
    output.writeFieldBegin('article_image_cropped', Thrift.Type.STRING, 5);
    output.writeString(this.article_image_cropped);
    output.writeFieldEnd();
  }
  if (this.automated_image_cropped !== null && this.automated_image_cropped !== undefined) {
    output.writeFieldBegin('automated_image_cropped', Thrift.Type.STRING, 6);
    output.writeString(this.automated_image_cropped);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 7);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.image !== null && this.image !== undefined) {
    output.writeFieldBegin('image', Thrift.Type.STRING, 8);
    output.writeString(this.image);
    output.writeFieldEnd();
  }
  if (this.dominant_colour !== null && this.dominant_colour !== undefined) {
    output.writeFieldBegin('dominant_colour', Thrift.Type.STRING, 9);
    output.writeString(this.dominant_colour);
    output.writeFieldEnd();
  }
  if (this.pointer_summary !== null && this.pointer_summary !== undefined) {
    output.writeFieldBegin('pointer_summary', Thrift.Type.MAP, 10);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.STRING, Thrift.objectLength(this.pointer_summary));
    for (var kiter1661 in this.pointer_summary)
    {
      if (this.pointer_summary.hasOwnProperty(kiter1661))
      {
        var viter1662 = this.pointer_summary[kiter1661];
        output.writeString(kiter1661);
        output.writeString(viter1662);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.oppo_image_assets !== null && this.oppo_image_assets !== undefined) {
    output.writeFieldBegin('oppo_image_assets', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRUCT, this.oppo_image_assets.length);
    for (var iter1663 in this.oppo_image_assets)
    {
      if (this.oppo_image_assets.hasOwnProperty(iter1663))
      {
        iter1663 = this.oppo_image_assets[iter1663];
        iter1663.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TextAssets = function(args) {
  this.ai_summary = null;
  this.pointer_summary = null;
  if (args) {
    if (args.ai_summary !== undefined && args.ai_summary !== null) {
      this.ai_summary = args.ai_summary;
    }
    if (args.pointer_summary !== undefined && args.pointer_summary !== null) {
      this.pointer_summary = Thrift.copyMap(args.pointer_summary, [null]);
    }
  }
};
TextAssets.prototype = {};
TextAssets.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.ai_summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.MAP) {
        var _size1664 = 0;
        var _rtmp31668;
        this.pointer_summary = {};
        var _ktype1665 = 0;
        var _vtype1666 = 0;
        _rtmp31668 = input.readMapBegin();
        _ktype1665 = _rtmp31668.ktype;
        _vtype1666 = _rtmp31668.vtype;
        _size1664 = _rtmp31668.size;
        for (var _i1669 = 0; _i1669 < _size1664; ++_i1669)
        {
          if (_i1669 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key1670 = null;
          var val1671 = null;
          key1670 = input.readString().value;
          val1671 = input.readString().value;
          this.pointer_summary[key1670] = val1671;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TextAssets.prototype.write = function(output) {
  output.writeStructBegin('TextAssets');
  if (this.ai_summary !== null && this.ai_summary !== undefined) {
    output.writeFieldBegin('ai_summary', Thrift.Type.STRING, 1);
    output.writeString(this.ai_summary);
    output.writeFieldEnd();
  }
  if (this.pointer_summary !== null && this.pointer_summary !== undefined) {
    output.writeFieldBegin('pointer_summary', Thrift.Type.MAP, 2);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.STRING, Thrift.objectLength(this.pointer_summary));
    for (var kiter1672 in this.pointer_summary)
    {
      if (this.pointer_summary.hasOwnProperty(kiter1672))
      {
        var viter1673 = this.pointer_summary[kiter1672];
        output.writeString(kiter1672);
        output.writeString(viter1673);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TrendsData = function(args) {
  this.fastcard_assets = null;
  this.en_text = null;
  this.endless_shp_fastcard_assets = null;
  if (args) {
    if (args.fastcard_assets !== undefined && args.fastcard_assets !== null) {
      this.fastcard_assets = new FastCardAssets(args.fastcard_assets);
    }
    if (args.en_text !== undefined && args.en_text !== null) {
      this.en_text = args.en_text;
    }
    if (args.endless_shp_fastcard_assets !== undefined && args.endless_shp_fastcard_assets !== null) {
      this.endless_shp_fastcard_assets = new FastCardAssets(args.endless_shp_fastcard_assets);
    }
  }
};
TrendsData.prototype = {};
TrendsData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.fastcard_assets = new FastCardAssets();
        this.fastcard_assets.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.en_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.endless_shp_fastcard_assets = new FastCardAssets();
        this.endless_shp_fastcard_assets.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TrendsData.prototype.write = function(output) {
  output.writeStructBegin('TrendsData');
  if (this.fastcard_assets !== null && this.fastcard_assets !== undefined) {
    output.writeFieldBegin('fastcard_assets', Thrift.Type.STRUCT, 1);
    this.fastcard_assets.write(output);
    output.writeFieldEnd();
  }
  if (this.en_text !== null && this.en_text !== undefined) {
    output.writeFieldBegin('en_text', Thrift.Type.STRING, 2);
    output.writeString(this.en_text);
    output.writeFieldEnd();
  }
  if (this.endless_shp_fastcard_assets !== null && this.endless_shp_fastcard_assets !== undefined) {
    output.writeFieldBegin('endless_shp_fastcard_assets', Thrift.Type.STRUCT, 3);
    this.endless_shp_fastcard_assets.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

VideoAssets = function(args) {
  this.hls = null;
  if (args) {
    if (args.hls !== undefined && args.hls !== null) {
      this.hls = Thrift.copyList(args.hls, [null]);
    }
  }
};
VideoAssets.prototype = {};
VideoAssets.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1674 = 0;
        var _rtmp31678;
        this.hls = [];
        var _etype1677 = 0;
        _rtmp31678 = input.readListBegin();
        _etype1677 = _rtmp31678.etype;
        _size1674 = _rtmp31678.size;
        for (var _i1679 = 0; _i1679 < _size1674; ++_i1679)
        {
          var elem1680 = null;
          elem1680 = input.readString().value;
          this.hls.push(elem1680);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

VideoAssets.prototype.write = function(output) {
  output.writeStructBegin('VideoAssets');
  if (this.hls !== null && this.hls !== undefined) {
    output.writeFieldBegin('hls', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.hls.length);
    for (var iter1681 in this.hls)
    {
      if (this.hls.hasOwnProperty(iter1681))
      {
        iter1681 = this.hls[iter1681];
        output.writeString(iter1681);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Assets = function(args) {
  this.text = null;
  this.video = null;
  if (args) {
    if (args.text !== undefined && args.text !== null) {
      this.text = new TextAssets(args.text);
    }
    if (args.video !== undefined && args.video !== null) {
      this.video = new VideoAssets(args.video);
    }
  }
};
Assets.prototype = {};
Assets.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.text = new TextAssets();
        this.text.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.video = new VideoAssets();
        this.video.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Assets.prototype.write = function(output) {
  output.writeStructBegin('Assets');
  if (this.text !== null && this.text !== undefined) {
    output.writeFieldBegin('text', Thrift.Type.STRUCT, 1);
    this.text.write(output);
    output.writeFieldEnd();
  }
  if (this.video !== null && this.video !== undefined) {
    output.writeFieldBegin('video', Thrift.Type.STRUCT, 2);
    this.video.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CardMetaData = function(args) {
  this.l3_tags = null;
  this.rejection_list_default = null;
  this.rejection_custom = null;
  this.card_id = null;
  this.created_by = null;
  this.modified_by = null;
  this.is_otd_sticker_enabled = null;
  this.is_beautiful_wallpaper_enabled = null;
  this.space_data = null;
  this.publishing_destination = null;
  this.tags = null;
  this.event = null;
  this.live_event_data = null;
  this.safety_score = null;
  this.trending_score = null;
  this.origin_tag = null;
  this.sport_eu_space_data = null;
  this.spaces = null;
  this.trendz_data = null;
  this.rule_ids = null;
  this.eu = null;
  this.value_prop = null;
  this.eus = null;
  this.value_props = null;
  this.locations = null;
  this.assets = null;
  if (args) {
    if (args.l3_tags !== undefined && args.l3_tags !== null) {
      this.l3_tags = Thrift.copyList(args.l3_tags, [null]);
    }
    if (args.rejection_list_default !== undefined && args.rejection_list_default !== null) {
      this.rejection_list_default = Thrift.copyList(args.rejection_list_default, [null]);
    }
    if (args.rejection_custom !== undefined && args.rejection_custom !== null) {
      this.rejection_custom = args.rejection_custom;
    }
    if (args.card_id !== undefined && args.card_id !== null) {
      this.card_id = args.card_id;
    }
    if (args.created_by !== undefined && args.created_by !== null) {
      this.created_by = args.created_by;
    }
    if (args.modified_by !== undefined && args.modified_by !== null) {
      this.modified_by = args.modified_by;
    }
    if (args.is_otd_sticker_enabled !== undefined && args.is_otd_sticker_enabled !== null) {
      this.is_otd_sticker_enabled = args.is_otd_sticker_enabled;
    }
    if (args.is_beautiful_wallpaper_enabled !== undefined && args.is_beautiful_wallpaper_enabled !== null) {
      this.is_beautiful_wallpaper_enabled = args.is_beautiful_wallpaper_enabled;
    }
    if (args.space_data !== undefined && args.space_data !== null) {
      this.space_data = new SpaceData(args.space_data);
    }
    if (args.publishing_destination !== undefined && args.publishing_destination !== null) {
      this.publishing_destination = args.publishing_destination;
    }
    if (args.tags !== undefined && args.tags !== null) {
      this.tags = Thrift.copyList(args.tags, [null]);
    }
    if (args.event !== undefined && args.event !== null) {
      this.event = args.event;
    }
    if (args.live_event_data !== undefined && args.live_event_data !== null) {
      this.live_event_data = new LiveEventData(args.live_event_data);
    }
    if (args.safety_score !== undefined && args.safety_score !== null) {
      this.safety_score = args.safety_score;
    }
    if (args.trending_score !== undefined && args.trending_score !== null) {
      this.trending_score = args.trending_score;
    }
    if (args.origin_tag !== undefined && args.origin_tag !== null) {
      this.origin_tag = args.origin_tag;
    }
    if (args.sport_eu_space_data !== undefined && args.sport_eu_space_data !== null) {
      this.sport_eu_space_data = new SportEuSpaceData(args.sport_eu_space_data);
    }
    if (args.spaces !== undefined && args.spaces !== null) {
      this.spaces = Thrift.copyList(args.spaces, [null]);
    }
    if (args.trendz_data !== undefined && args.trendz_data !== null) {
      this.trendz_data = new TrendsData(args.trendz_data);
    }
    if (args.rule_ids !== undefined && args.rule_ids !== null) {
      this.rule_ids = Thrift.copyList(args.rule_ids, [null]);
    }
    if (args.eu !== undefined && args.eu !== null) {
      this.eu = args.eu;
    }
    if (args.value_prop !== undefined && args.value_prop !== null) {
      this.value_prop = args.value_prop;
    }
    if (args.eus !== undefined && args.eus !== null) {
      this.eus = Thrift.copyList(args.eus, [EuInfoThrift]);
    }
    if (args.value_props !== undefined && args.value_props !== null) {
      this.value_props = Thrift.copyList(args.value_props, [ValuePropInfoThrift]);
    }
    if (args.locations !== undefined && args.locations !== null) {
      this.locations = new Location(args.locations);
    }
    if (args.assets !== undefined && args.assets !== null) {
      this.assets = new Assets(args.assets);
    }
  }
};
CardMetaData.prototype = {};
CardMetaData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1682 = 0;
        var _rtmp31686;
        this.l3_tags = [];
        var _etype1685 = 0;
        _rtmp31686 = input.readListBegin();
        _etype1685 = _rtmp31686.etype;
        _size1682 = _rtmp31686.size;
        for (var _i1687 = 0; _i1687 < _size1682; ++_i1687)
        {
          var elem1688 = null;
          elem1688 = input.readString().value;
          this.l3_tags.push(elem1688);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1689 = 0;
        var _rtmp31693;
        this.rejection_list_default = [];
        var _etype1692 = 0;
        _rtmp31693 = input.readListBegin();
        _etype1692 = _rtmp31693.etype;
        _size1689 = _rtmp31693.size;
        for (var _i1694 = 0; _i1694 < _size1689; ++_i1694)
        {
          var elem1695 = null;
          elem1695 = input.readI32().value;
          this.rejection_list_default.push(elem1695);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.rejection_custom = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.card_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.created_by = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.modified_by = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.is_otd_sticker_enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.is_beautiful_wallpaper_enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRUCT) {
        this.space_data = new SpaceData();
        this.space_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I32) {
        this.publishing_destination = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.LIST) {
        var _size1696 = 0;
        var _rtmp31700;
        this.tags = [];
        var _etype1699 = 0;
        _rtmp31700 = input.readListBegin();
        _etype1699 = _rtmp31700.etype;
        _size1696 = _rtmp31700.size;
        for (var _i1701 = 0; _i1701 < _size1696; ++_i1701)
        {
          var elem1702 = null;
          elem1702 = input.readString().value;
          this.tags.push(elem1702);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I32) {
        this.event = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRUCT) {
        this.live_event_data = new LiveEventData();
        this.live_event_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.safety_score = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.trending_score = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.origin_tag = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRUCT) {
        this.sport_eu_space_data = new SportEuSpaceData();
        this.sport_eu_space_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.LIST) {
        var _size1703 = 0;
        var _rtmp31707;
        this.spaces = [];
        var _etype1706 = 0;
        _rtmp31707 = input.readListBegin();
        _etype1706 = _rtmp31707.etype;
        _size1703 = _rtmp31707.size;
        for (var _i1708 = 0; _i1708 < _size1703; ++_i1708)
        {
          var elem1709 = null;
          elem1709 = input.readI32().value;
          this.spaces.push(elem1709);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRUCT) {
        this.trendz_data = new TrendsData();
        this.trendz_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.LIST) {
        var _size1710 = 0;
        var _rtmp31714;
        this.rule_ids = [];
        var _etype1713 = 0;
        _rtmp31714 = input.readListBegin();
        _etype1713 = _rtmp31714.etype;
        _size1710 = _rtmp31714.size;
        for (var _i1715 = 0; _i1715 < _size1710; ++_i1715)
        {
          var elem1716 = null;
          elem1716 = input.readString().value;
          this.rule_ids.push(elem1716);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.I32) {
        this.eu = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRING) {
        this.value_prop = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.LIST) {
        var _size1717 = 0;
        var _rtmp31721;
        this.eus = [];
        var _etype1720 = 0;
        _rtmp31721 = input.readListBegin();
        _etype1720 = _rtmp31721.etype;
        _size1717 = _rtmp31721.size;
        for (var _i1722 = 0; _i1722 < _size1717; ++_i1722)
        {
          var elem1723 = null;
          elem1723 = new EuInfoThrift();
          elem1723.read(input);
          this.eus.push(elem1723);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.LIST) {
        var _size1724 = 0;
        var _rtmp31728;
        this.value_props = [];
        var _etype1727 = 0;
        _rtmp31728 = input.readListBegin();
        _etype1727 = _rtmp31728.etype;
        _size1724 = _rtmp31728.size;
        for (var _i1729 = 0; _i1729 < _size1724; ++_i1729)
        {
          var elem1730 = null;
          elem1730 = new ValuePropInfoThrift();
          elem1730.read(input);
          this.value_props.push(elem1730);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.STRUCT) {
        this.locations = new Location();
        this.locations.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.STRUCT) {
        this.assets = new Assets();
        this.assets.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CardMetaData.prototype.write = function(output) {
  output.writeStructBegin('CardMetaData');
  if (this.l3_tags !== null && this.l3_tags !== undefined) {
    output.writeFieldBegin('l3_tags', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.l3_tags.length);
    for (var iter1731 in this.l3_tags)
    {
      if (this.l3_tags.hasOwnProperty(iter1731))
      {
        iter1731 = this.l3_tags[iter1731];
        output.writeString(iter1731);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.rejection_list_default !== null && this.rejection_list_default !== undefined) {
    output.writeFieldBegin('rejection_list_default', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.I32, this.rejection_list_default.length);
    for (var iter1732 in this.rejection_list_default)
    {
      if (this.rejection_list_default.hasOwnProperty(iter1732))
      {
        iter1732 = this.rejection_list_default[iter1732];
        output.writeI32(iter1732);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.rejection_custom !== null && this.rejection_custom !== undefined) {
    output.writeFieldBegin('rejection_custom', Thrift.Type.STRING, 3);
    output.writeString(this.rejection_custom);
    output.writeFieldEnd();
  }
  if (this.card_id !== null && this.card_id !== undefined) {
    output.writeFieldBegin('card_id', Thrift.Type.STRING, 4);
    output.writeString(this.card_id);
    output.writeFieldEnd();
  }
  if (this.created_by !== null && this.created_by !== undefined) {
    output.writeFieldBegin('created_by', Thrift.Type.STRING, 5);
    output.writeString(this.created_by);
    output.writeFieldEnd();
  }
  if (this.modified_by !== null && this.modified_by !== undefined) {
    output.writeFieldBegin('modified_by', Thrift.Type.STRING, 6);
    output.writeString(this.modified_by);
    output.writeFieldEnd();
  }
  if (this.is_otd_sticker_enabled !== null && this.is_otd_sticker_enabled !== undefined) {
    output.writeFieldBegin('is_otd_sticker_enabled', Thrift.Type.BOOL, 7);
    output.writeBool(this.is_otd_sticker_enabled);
    output.writeFieldEnd();
  }
  if (this.is_beautiful_wallpaper_enabled !== null && this.is_beautiful_wallpaper_enabled !== undefined) {
    output.writeFieldBegin('is_beautiful_wallpaper_enabled', Thrift.Type.BOOL, 8);
    output.writeBool(this.is_beautiful_wallpaper_enabled);
    output.writeFieldEnd();
  }
  if (this.space_data !== null && this.space_data !== undefined) {
    output.writeFieldBegin('space_data', Thrift.Type.STRUCT, 9);
    this.space_data.write(output);
    output.writeFieldEnd();
  }
  if (this.publishing_destination !== null && this.publishing_destination !== undefined) {
    output.writeFieldBegin('publishing_destination', Thrift.Type.I32, 10);
    output.writeI32(this.publishing_destination);
    output.writeFieldEnd();
  }
  if (this.tags !== null && this.tags !== undefined) {
    output.writeFieldBegin('tags', Thrift.Type.LIST, 11);
    output.writeListBegin(Thrift.Type.STRING, this.tags.length);
    for (var iter1733 in this.tags)
    {
      if (this.tags.hasOwnProperty(iter1733))
      {
        iter1733 = this.tags[iter1733];
        output.writeString(iter1733);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.event !== null && this.event !== undefined) {
    output.writeFieldBegin('event', Thrift.Type.I32, 12);
    output.writeI32(this.event);
    output.writeFieldEnd();
  }
  if (this.live_event_data !== null && this.live_event_data !== undefined) {
    output.writeFieldBegin('live_event_data', Thrift.Type.STRUCT, 13);
    this.live_event_data.write(output);
    output.writeFieldEnd();
  }
  if (this.safety_score !== null && this.safety_score !== undefined) {
    output.writeFieldBegin('safety_score', Thrift.Type.STRING, 14);
    output.writeString(this.safety_score);
    output.writeFieldEnd();
  }
  if (this.trending_score !== null && this.trending_score !== undefined) {
    output.writeFieldBegin('trending_score', Thrift.Type.STRING, 15);
    output.writeString(this.trending_score);
    output.writeFieldEnd();
  }
  if (this.origin_tag !== null && this.origin_tag !== undefined) {
    output.writeFieldBegin('origin_tag', Thrift.Type.STRING, 16);
    output.writeString(this.origin_tag);
    output.writeFieldEnd();
  }
  if (this.sport_eu_space_data !== null && this.sport_eu_space_data !== undefined) {
    output.writeFieldBegin('sport_eu_space_data', Thrift.Type.STRUCT, 17);
    this.sport_eu_space_data.write(output);
    output.writeFieldEnd();
  }
  if (this.spaces !== null && this.spaces !== undefined) {
    output.writeFieldBegin('spaces', Thrift.Type.LIST, 18);
    output.writeListBegin(Thrift.Type.I32, this.spaces.length);
    for (var iter1734 in this.spaces)
    {
      if (this.spaces.hasOwnProperty(iter1734))
      {
        iter1734 = this.spaces[iter1734];
        output.writeI32(iter1734);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.trendz_data !== null && this.trendz_data !== undefined) {
    output.writeFieldBegin('trendz_data', Thrift.Type.STRUCT, 19);
    this.trendz_data.write(output);
    output.writeFieldEnd();
  }
  if (this.rule_ids !== null && this.rule_ids !== undefined) {
    output.writeFieldBegin('rule_ids', Thrift.Type.LIST, 20);
    output.writeListBegin(Thrift.Type.STRING, this.rule_ids.length);
    for (var iter1735 in this.rule_ids)
    {
      if (this.rule_ids.hasOwnProperty(iter1735))
      {
        iter1735 = this.rule_ids[iter1735];
        output.writeString(iter1735);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.eu !== null && this.eu !== undefined) {
    output.writeFieldBegin('eu', Thrift.Type.I32, 21);
    output.writeI32(this.eu);
    output.writeFieldEnd();
  }
  if (this.value_prop !== null && this.value_prop !== undefined) {
    output.writeFieldBegin('value_prop', Thrift.Type.STRING, 22);
    output.writeString(this.value_prop);
    output.writeFieldEnd();
  }
  if (this.eus !== null && this.eus !== undefined) {
    output.writeFieldBegin('eus', Thrift.Type.LIST, 23);
    output.writeListBegin(Thrift.Type.STRUCT, this.eus.length);
    for (var iter1736 in this.eus)
    {
      if (this.eus.hasOwnProperty(iter1736))
      {
        iter1736 = this.eus[iter1736];
        iter1736.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.value_props !== null && this.value_props !== undefined) {
    output.writeFieldBegin('value_props', Thrift.Type.LIST, 24);
    output.writeListBegin(Thrift.Type.STRUCT, this.value_props.length);
    for (var iter1737 in this.value_props)
    {
      if (this.value_props.hasOwnProperty(iter1737))
      {
        iter1737 = this.value_props[iter1737];
        iter1737.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.locations !== null && this.locations !== undefined) {
    output.writeFieldBegin('locations', Thrift.Type.STRUCT, 25);
    this.locations.write(output);
    output.writeFieldEnd();
  }
  if (this.assets !== null && this.assets !== undefined) {
    output.writeFieldBegin('assets', Thrift.Type.STRUCT, 26);
    this.assets.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CardInfo = function(args) {
  this.id = null;
  this.category = null;
  this.title = null;
  this.description = null;
  this.url = null;
  this.source_name = null;
  this.old_template_position = null;
  this.cta_text = null;
  this.modified_headline = null;
  this.modified_binge_text = null;
  this.actionOption = null;
  this.selected_image = null;
  this.selected_binge_image = null;
  this.bubble_qualifier = null;
  this.binge_text = null;
  this.headline = null;
  this.language = null;
  this.modified_language = null;
  this.summary = null;
  this.topic = null;
  this.user = null;
  this.story_type = null;
  this.publishing_options = null;
  this.template_position = null;
  this.publish_to_user = null;
  this.categories = null;
  this.template = null;
  this.publishing_queue = null;
  this.image_search_text = null;
  this.asset_type = null;
  this.segmentIds = null;
  this.cta_url = null;
  this.published_at = null;
  this.publishing_partner_id = null;
  this.glancify_video_data = null;
  this.sub_categories = null;
  this.suggested_sub_category = null;
  this.cityIds = null;
  this.card_meta_data = null;
  this.peekType = null;
  this.selected_overlay_template = null;
  this.shouldUnlock = null;
  this.source_identifier = null;
  this.antiSegmentIds = null;
  this.rule_ids = null;
  this.eu = null;
  this.fast_card_data = null;
  this.allow_republish = null;
  this.republish_after_days = null;
  this.republish_counter = null;
  this.publish_as_article = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.source_name !== undefined && args.source_name !== null) {
      this.source_name = args.source_name;
    }
    if (args.old_template_position !== undefined && args.old_template_position !== null) {
      this.old_template_position = args.old_template_position;
    }
    if (args.cta_text !== undefined && args.cta_text !== null) {
      this.cta_text = args.cta_text;
    }
    if (args.modified_headline !== undefined && args.modified_headline !== null) {
      this.modified_headline = args.modified_headline;
    }
    if (args.modified_binge_text !== undefined && args.modified_binge_text !== null) {
      this.modified_binge_text = args.modified_binge_text;
    }
    if (args.actionOption !== undefined && args.actionOption !== null) {
      this.actionOption = args.actionOption;
    }
    if (args.selected_image !== undefined && args.selected_image !== null) {
      this.selected_image = args.selected_image;
    }
    if (args.selected_binge_image !== undefined && args.selected_binge_image !== null) {
      this.selected_binge_image = args.selected_binge_image;
    }
    if (args.bubble_qualifier !== undefined && args.bubble_qualifier !== null) {
      this.bubble_qualifier = args.bubble_qualifier;
    }
    if (args.binge_text !== undefined && args.binge_text !== null) {
      this.binge_text = args.binge_text;
    }
    if (args.headline !== undefined && args.headline !== null) {
      this.headline = args.headline;
    }
    if (args.language !== undefined && args.language !== null) {
      this.language = args.language;
    }
    if (args.modified_language !== undefined && args.modified_language !== null) {
      this.modified_language = args.modified_language;
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.topic !== undefined && args.topic !== null) {
      this.topic = args.topic;
    }
    if (args.user !== undefined && args.user !== null) {
      this.user = args.user;
    }
    if (args.story_type !== undefined && args.story_type !== null) {
      this.story_type = args.story_type;
    }
    if (args.publishing_options !== undefined && args.publishing_options !== null) {
      this.publishing_options = new PublishingOptions(args.publishing_options);
    }
    if (args.template_position !== undefined && args.template_position !== null) {
      this.template_position = args.template_position;
    }
    if (args.publish_to_user !== undefined && args.publish_to_user !== null) {
      this.publish_to_user = args.publish_to_user;
    }
    if (args.categories !== undefined && args.categories !== null) {
      this.categories = Thrift.copyList(args.categories, [null]);
    }
    if (args.template !== undefined && args.template !== null) {
      this.template = args.template;
    }
    if (args.publishing_queue !== undefined && args.publishing_queue !== null) {
      this.publishing_queue = args.publishing_queue;
    }
    if (args.image_search_text !== undefined && args.image_search_text !== null) {
      this.image_search_text = args.image_search_text;
    }
    if (args.asset_type !== undefined && args.asset_type !== null) {
      this.asset_type = args.asset_type;
    }
    if (args.segmentIds !== undefined && args.segmentIds !== null) {
      this.segmentIds = Thrift.copyList(args.segmentIds, [null]);
    }
    if (args.cta_url !== undefined && args.cta_url !== null) {
      this.cta_url = args.cta_url;
    }
    if (args.published_at !== undefined && args.published_at !== null) {
      this.published_at = args.published_at;
    }
    if (args.publishing_partner_id !== undefined && args.publishing_partner_id !== null) {
      this.publishing_partner_id = args.publishing_partner_id;
    }
    if (args.glancify_video_data !== undefined && args.glancify_video_data !== null) {
      this.glancify_video_data = new GlancifyVideoData(args.glancify_video_data);
    }
    if (args.sub_categories !== undefined && args.sub_categories !== null) {
      this.sub_categories = Thrift.copyList(args.sub_categories, [null]);
    }
    if (args.suggested_sub_category !== undefined && args.suggested_sub_category !== null) {
      this.suggested_sub_category = args.suggested_sub_category;
    }
    if (args.cityIds !== undefined && args.cityIds !== null) {
      this.cityIds = Thrift.copyList(args.cityIds, [null]);
    }
    if (args.card_meta_data !== undefined && args.card_meta_data !== null) {
      this.card_meta_data = new CardMetaData(args.card_meta_data);
    }
    if (args.peekType !== undefined && args.peekType !== null) {
      this.peekType = args.peekType;
    }
    if (args.selected_overlay_template !== undefined && args.selected_overlay_template !== null) {
      this.selected_overlay_template = args.selected_overlay_template;
    }
    if (args.shouldUnlock !== undefined && args.shouldUnlock !== null) {
      this.shouldUnlock = args.shouldUnlock;
    }
    if (args.source_identifier !== undefined && args.source_identifier !== null) {
      this.source_identifier = args.source_identifier;
    }
    if (args.antiSegmentIds !== undefined && args.antiSegmentIds !== null) {
      this.antiSegmentIds = Thrift.copyList(args.antiSegmentIds, [null]);
    }
    if (args.rule_ids !== undefined && args.rule_ids !== null) {
      this.rule_ids = Thrift.copyList(args.rule_ids, [null]);
    }
    if (args.eu !== undefined && args.eu !== null) {
      this.eu = args.eu;
    }
    if (args.fast_card_data !== undefined && args.fast_card_data !== null) {
      this.fast_card_data = new FastCardData(args.fast_card_data);
    }
    if (args.allow_republish !== undefined && args.allow_republish !== null) {
      this.allow_republish = args.allow_republish;
    }
    if (args.republish_after_days !== undefined && args.republish_after_days !== null) {
      this.republish_after_days = args.republish_after_days;
    }
    if (args.republish_counter !== undefined && args.republish_counter !== null) {
      this.republish_counter = args.republish_counter;
    }
    if (args.publish_as_article !== undefined && args.publish_as_article !== null) {
      this.publish_as_article = args.publish_as_article;
    }
  }
};
CardInfo.prototype = {};
CardInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.source_name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.old_template_position = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.cta_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.modified_headline = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.modified_binge_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.actionOption = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.selected_image = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.selected_binge_image = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.bubble_qualifier = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.binge_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.headline = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.language = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.modified_language = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.topic = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRING) {
        this.user = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.I32) {
        this.story_type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRUCT) {
        this.publishing_options = new PublishingOptions();
        this.publishing_options.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.STRING) {
        this.template_position = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.BOOL) {
        this.publish_to_user = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.LIST) {
        var _size1738 = 0;
        var _rtmp31742;
        this.categories = [];
        var _etype1741 = 0;
        _rtmp31742 = input.readListBegin();
        _etype1741 = _rtmp31742.etype;
        _size1738 = _rtmp31742.size;
        for (var _i1743 = 0; _i1743 < _size1738; ++_i1743)
        {
          var elem1744 = null;
          elem1744 = input.readString().value;
          this.categories.push(elem1744);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.STRING) {
        this.template = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.BOOL) {
        this.publishing_queue = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.STRING) {
        this.image_search_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.I32) {
        this.asset_type = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.SET) {
        var _size1745 = 0;
        var _rtmp31749;
        this.segmentIds = [];
        var _etype1748 = 0;
        _rtmp31749 = input.readSetBegin();
        _etype1748 = _rtmp31749.etype;
        _size1745 = _rtmp31749.size;
        for (var _i1750 = 0; _i1750 < _size1745; ++_i1750)
        {
          var elem1751 = null;
          elem1751 = input.readI32().value;
          this.segmentIds.push(elem1751);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRING) {
        this.cta_url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.STRING) {
        this.published_at = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.STRING) {
        this.publishing_partner_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.STRUCT) {
        this.glancify_video_data = new GlancifyVideoData();
        this.glancify_video_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 36:
      if (ftype == Thrift.Type.LIST) {
        var _size1752 = 0;
        var _rtmp31756;
        this.sub_categories = [];
        var _etype1755 = 0;
        _rtmp31756 = input.readListBegin();
        _etype1755 = _rtmp31756.etype;
        _size1752 = _rtmp31756.size;
        for (var _i1757 = 0; _i1757 < _size1752; ++_i1757)
        {
          var elem1758 = null;
          elem1758 = input.readString().value;
          this.sub_categories.push(elem1758);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 37:
      if (ftype == Thrift.Type.STRING) {
        this.suggested_sub_category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 38:
      if (ftype == Thrift.Type.SET) {
        var _size1759 = 0;
        var _rtmp31763;
        this.cityIds = [];
        var _etype1762 = 0;
        _rtmp31763 = input.readSetBegin();
        _etype1762 = _rtmp31763.etype;
        _size1759 = _rtmp31763.size;
        for (var _i1764 = 0; _i1764 < _size1759; ++_i1764)
        {
          var elem1765 = null;
          elem1765 = input.readI32().value;
          this.cityIds.push(elem1765);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 39:
      if (ftype == Thrift.Type.STRUCT) {
        this.card_meta_data = new CardMetaData();
        this.card_meta_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 40:
      if (ftype == Thrift.Type.STRING) {
        this.peekType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 41:
      if (ftype == Thrift.Type.STRING) {
        this.selected_overlay_template = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 42:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldUnlock = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 43:
      if (ftype == Thrift.Type.I32) {
        this.source_identifier = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 44:
      if (ftype == Thrift.Type.SET) {
        var _size1766 = 0;
        var _rtmp31770;
        this.antiSegmentIds = [];
        var _etype1769 = 0;
        _rtmp31770 = input.readSetBegin();
        _etype1769 = _rtmp31770.etype;
        _size1766 = _rtmp31770.size;
        for (var _i1771 = 0; _i1771 < _size1766; ++_i1771)
        {
          var elem1772 = null;
          elem1772 = input.readI32().value;
          this.antiSegmentIds.push(elem1772);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 45:
      if (ftype == Thrift.Type.LIST) {
        var _size1773 = 0;
        var _rtmp31777;
        this.rule_ids = [];
        var _etype1776 = 0;
        _rtmp31777 = input.readListBegin();
        _etype1776 = _rtmp31777.etype;
        _size1773 = _rtmp31777.size;
        for (var _i1778 = 0; _i1778 < _size1773; ++_i1778)
        {
          var elem1779 = null;
          elem1779 = input.readString().value;
          this.rule_ids.push(elem1779);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 46:
      if (ftype == Thrift.Type.I32) {
        this.eu = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 47:
      if (ftype == Thrift.Type.STRUCT) {
        this.fast_card_data = new FastCardData();
        this.fast_card_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 48:
      if (ftype == Thrift.Type.BOOL) {
        this.allow_republish = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 49:
      if (ftype == Thrift.Type.I32) {
        this.republish_after_days = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 50:
      if (ftype == Thrift.Type.I32) {
        this.republish_counter = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 51:
      if (ftype == Thrift.Type.BOOL) {
        this.publish_as_article = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CardInfo.prototype.write = function(output) {
  output.writeStructBegin('CardInfo');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 2);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 3);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 4);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 5);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.source_name !== null && this.source_name !== undefined) {
    output.writeFieldBegin('source_name', Thrift.Type.STRING, 6);
    output.writeString(this.source_name);
    output.writeFieldEnd();
  }
  if (this.old_template_position !== null && this.old_template_position !== undefined) {
    output.writeFieldBegin('old_template_position', Thrift.Type.STRING, 7);
    output.writeString(this.old_template_position);
    output.writeFieldEnd();
  }
  if (this.cta_text !== null && this.cta_text !== undefined) {
    output.writeFieldBegin('cta_text', Thrift.Type.STRING, 8);
    output.writeString(this.cta_text);
    output.writeFieldEnd();
  }
  if (this.modified_headline !== null && this.modified_headline !== undefined) {
    output.writeFieldBegin('modified_headline', Thrift.Type.STRING, 9);
    output.writeString(this.modified_headline);
    output.writeFieldEnd();
  }
  if (this.modified_binge_text !== null && this.modified_binge_text !== undefined) {
    output.writeFieldBegin('modified_binge_text', Thrift.Type.STRING, 10);
    output.writeString(this.modified_binge_text);
    output.writeFieldEnd();
  }
  if (this.actionOption !== null && this.actionOption !== undefined) {
    output.writeFieldBegin('actionOption', Thrift.Type.STRING, 11);
    output.writeString(this.actionOption);
    output.writeFieldEnd();
  }
  if (this.selected_image !== null && this.selected_image !== undefined) {
    output.writeFieldBegin('selected_image', Thrift.Type.STRING, 12);
    output.writeString(this.selected_image);
    output.writeFieldEnd();
  }
  if (this.selected_binge_image !== null && this.selected_binge_image !== undefined) {
    output.writeFieldBegin('selected_binge_image', Thrift.Type.STRING, 13);
    output.writeString(this.selected_binge_image);
    output.writeFieldEnd();
  }
  if (this.bubble_qualifier !== null && this.bubble_qualifier !== undefined) {
    output.writeFieldBegin('bubble_qualifier', Thrift.Type.STRING, 14);
    output.writeString(this.bubble_qualifier);
    output.writeFieldEnd();
  }
  if (this.binge_text !== null && this.binge_text !== undefined) {
    output.writeFieldBegin('binge_text', Thrift.Type.STRING, 15);
    output.writeString(this.binge_text);
    output.writeFieldEnd();
  }
  if (this.headline !== null && this.headline !== undefined) {
    output.writeFieldBegin('headline', Thrift.Type.STRING, 16);
    output.writeString(this.headline);
    output.writeFieldEnd();
  }
  if (this.language !== null && this.language !== undefined) {
    output.writeFieldBegin('language', Thrift.Type.STRING, 17);
    output.writeString(this.language);
    output.writeFieldEnd();
  }
  if (this.modified_language !== null && this.modified_language !== undefined) {
    output.writeFieldBegin('modified_language', Thrift.Type.STRING, 18);
    output.writeString(this.modified_language);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 19);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.topic !== null && this.topic !== undefined) {
    output.writeFieldBegin('topic', Thrift.Type.STRING, 20);
    output.writeString(this.topic);
    output.writeFieldEnd();
  }
  if (this.user !== null && this.user !== undefined) {
    output.writeFieldBegin('user', Thrift.Type.STRING, 21);
    output.writeString(this.user);
    output.writeFieldEnd();
  }
  if (this.story_type !== null && this.story_type !== undefined) {
    output.writeFieldBegin('story_type', Thrift.Type.I32, 22);
    output.writeI32(this.story_type);
    output.writeFieldEnd();
  }
  if (this.publishing_options !== null && this.publishing_options !== undefined) {
    output.writeFieldBegin('publishing_options', Thrift.Type.STRUCT, 23);
    this.publishing_options.write(output);
    output.writeFieldEnd();
  }
  if (this.template_position !== null && this.template_position !== undefined) {
    output.writeFieldBegin('template_position', Thrift.Type.STRING, 24);
    output.writeString(this.template_position);
    output.writeFieldEnd();
  }
  if (this.publish_to_user !== null && this.publish_to_user !== undefined) {
    output.writeFieldBegin('publish_to_user', Thrift.Type.BOOL, 25);
    output.writeBool(this.publish_to_user);
    output.writeFieldEnd();
  }
  if (this.categories !== null && this.categories !== undefined) {
    output.writeFieldBegin('categories', Thrift.Type.LIST, 26);
    output.writeListBegin(Thrift.Type.STRING, this.categories.length);
    for (var iter1780 in this.categories)
    {
      if (this.categories.hasOwnProperty(iter1780))
      {
        iter1780 = this.categories[iter1780];
        output.writeString(iter1780);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.template !== null && this.template !== undefined) {
    output.writeFieldBegin('template', Thrift.Type.STRING, 27);
    output.writeString(this.template);
    output.writeFieldEnd();
  }
  if (this.publishing_queue !== null && this.publishing_queue !== undefined) {
    output.writeFieldBegin('publishing_queue', Thrift.Type.BOOL, 28);
    output.writeBool(this.publishing_queue);
    output.writeFieldEnd();
  }
  if (this.image_search_text !== null && this.image_search_text !== undefined) {
    output.writeFieldBegin('image_search_text', Thrift.Type.STRING, 29);
    output.writeString(this.image_search_text);
    output.writeFieldEnd();
  }
  if (this.asset_type !== null && this.asset_type !== undefined) {
    output.writeFieldBegin('asset_type', Thrift.Type.I32, 30);
    output.writeI32(this.asset_type);
    output.writeFieldEnd();
  }
  if (this.segmentIds !== null && this.segmentIds !== undefined) {
    output.writeFieldBegin('segmentIds', Thrift.Type.SET, 31);
    output.writeSetBegin(Thrift.Type.I32, this.segmentIds.length);
    for (var iter1781 in this.segmentIds)
    {
      if (this.segmentIds.hasOwnProperty(iter1781))
      {
        iter1781 = this.segmentIds[iter1781];
        output.writeI32(iter1781);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.cta_url !== null && this.cta_url !== undefined) {
    output.writeFieldBegin('cta_url', Thrift.Type.STRING, 32);
    output.writeString(this.cta_url);
    output.writeFieldEnd();
  }
  if (this.published_at !== null && this.published_at !== undefined) {
    output.writeFieldBegin('published_at', Thrift.Type.STRING, 33);
    output.writeString(this.published_at);
    output.writeFieldEnd();
  }
  if (this.publishing_partner_id !== null && this.publishing_partner_id !== undefined) {
    output.writeFieldBegin('publishing_partner_id', Thrift.Type.STRING, 34);
    output.writeString(this.publishing_partner_id);
    output.writeFieldEnd();
  }
  if (this.glancify_video_data !== null && this.glancify_video_data !== undefined) {
    output.writeFieldBegin('glancify_video_data', Thrift.Type.STRUCT, 35);
    this.glancify_video_data.write(output);
    output.writeFieldEnd();
  }
  if (this.sub_categories !== null && this.sub_categories !== undefined) {
    output.writeFieldBegin('sub_categories', Thrift.Type.LIST, 36);
    output.writeListBegin(Thrift.Type.STRING, this.sub_categories.length);
    for (var iter1782 in this.sub_categories)
    {
      if (this.sub_categories.hasOwnProperty(iter1782))
      {
        iter1782 = this.sub_categories[iter1782];
        output.writeString(iter1782);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.suggested_sub_category !== null && this.suggested_sub_category !== undefined) {
    output.writeFieldBegin('suggested_sub_category', Thrift.Type.STRING, 37);
    output.writeString(this.suggested_sub_category);
    output.writeFieldEnd();
  }
  if (this.cityIds !== null && this.cityIds !== undefined) {
    output.writeFieldBegin('cityIds', Thrift.Type.SET, 38);
    output.writeSetBegin(Thrift.Type.I32, this.cityIds.length);
    for (var iter1783 in this.cityIds)
    {
      if (this.cityIds.hasOwnProperty(iter1783))
      {
        iter1783 = this.cityIds[iter1783];
        output.writeI32(iter1783);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.card_meta_data !== null && this.card_meta_data !== undefined) {
    output.writeFieldBegin('card_meta_data', Thrift.Type.STRUCT, 39);
    this.card_meta_data.write(output);
    output.writeFieldEnd();
  }
  if (this.peekType !== null && this.peekType !== undefined) {
    output.writeFieldBegin('peekType', Thrift.Type.STRING, 40);
    output.writeString(this.peekType);
    output.writeFieldEnd();
  }
  if (this.selected_overlay_template !== null && this.selected_overlay_template !== undefined) {
    output.writeFieldBegin('selected_overlay_template', Thrift.Type.STRING, 41);
    output.writeString(this.selected_overlay_template);
    output.writeFieldEnd();
  }
  if (this.shouldUnlock !== null && this.shouldUnlock !== undefined) {
    output.writeFieldBegin('shouldUnlock', Thrift.Type.BOOL, 42);
    output.writeBool(this.shouldUnlock);
    output.writeFieldEnd();
  }
  if (this.source_identifier !== null && this.source_identifier !== undefined) {
    output.writeFieldBegin('source_identifier', Thrift.Type.I32, 43);
    output.writeI32(this.source_identifier);
    output.writeFieldEnd();
  }
  if (this.antiSegmentIds !== null && this.antiSegmentIds !== undefined) {
    output.writeFieldBegin('antiSegmentIds', Thrift.Type.SET, 44);
    output.writeSetBegin(Thrift.Type.I32, this.antiSegmentIds.length);
    for (var iter1784 in this.antiSegmentIds)
    {
      if (this.antiSegmentIds.hasOwnProperty(iter1784))
      {
        iter1784 = this.antiSegmentIds[iter1784];
        output.writeI32(iter1784);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.rule_ids !== null && this.rule_ids !== undefined) {
    output.writeFieldBegin('rule_ids', Thrift.Type.LIST, 45);
    output.writeListBegin(Thrift.Type.STRING, this.rule_ids.length);
    for (var iter1785 in this.rule_ids)
    {
      if (this.rule_ids.hasOwnProperty(iter1785))
      {
        iter1785 = this.rule_ids[iter1785];
        output.writeString(iter1785);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.eu !== null && this.eu !== undefined) {
    output.writeFieldBegin('eu', Thrift.Type.I32, 46);
    output.writeI32(this.eu);
    output.writeFieldEnd();
  }
  if (this.fast_card_data !== null && this.fast_card_data !== undefined) {
    output.writeFieldBegin('fast_card_data', Thrift.Type.STRUCT, 47);
    this.fast_card_data.write(output);
    output.writeFieldEnd();
  }
  if (this.allow_republish !== null && this.allow_republish !== undefined) {
    output.writeFieldBegin('allow_republish', Thrift.Type.BOOL, 48);
    output.writeBool(this.allow_republish);
    output.writeFieldEnd();
  }
  if (this.republish_after_days !== null && this.republish_after_days !== undefined) {
    output.writeFieldBegin('republish_after_days', Thrift.Type.I32, 49);
    output.writeI32(this.republish_after_days);
    output.writeFieldEnd();
  }
  if (this.republish_counter !== null && this.republish_counter !== undefined) {
    output.writeFieldBegin('republish_counter', Thrift.Type.I32, 50);
    output.writeI32(this.republish_counter);
    output.writeFieldEnd();
  }
  if (this.publish_as_article !== null && this.publish_as_article !== undefined) {
    output.writeFieldBegin('publish_as_article', Thrift.Type.BOOL, 51);
    output.writeBool(this.publish_as_article);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

JavelinUser = function(args) {
  this.email = null;
  this.name = null;
  this.role = null;
  this.publisherId = null;
  this.country = null;
  this.id = null;
  if (args) {
    if (args.email !== undefined && args.email !== null) {
      this.email = args.email;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.role !== undefined && args.role !== null) {
      this.role = args.role;
    }
    if (args.publisherId !== undefined && args.publisherId !== null) {
      this.publisherId = args.publisherId;
    }
    if (args.country !== undefined && args.country !== null) {
      this.country = args.country;
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
  }
};
JavelinUser.prototype = {};
JavelinUser.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.email = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.role = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.publisherId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.country = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

JavelinUser.prototype.write = function(output) {
  output.writeStructBegin('JavelinUser');
  if (this.email !== null && this.email !== undefined) {
    output.writeFieldBegin('email', Thrift.Type.STRING, 1);
    output.writeString(this.email);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.role !== null && this.role !== undefined) {
    output.writeFieldBegin('role', Thrift.Type.I32, 3);
    output.writeI32(this.role);
    output.writeFieldEnd();
  }
  if (this.publisherId !== null && this.publisherId !== undefined) {
    output.writeFieldBegin('publisherId', Thrift.Type.STRING, 4);
    output.writeString(this.publisherId);
    output.writeFieldEnd();
  }
  if (this.country !== null && this.country !== undefined) {
    output.writeFieldBegin('country', Thrift.Type.STRING, 5);
    output.writeString(this.country);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 6);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SourceStatistics = function(args) {
  this.likes = null;
  this.views = null;
  this.shares = null;
  if (args) {
    if (args.likes !== undefined && args.likes !== null) {
      this.likes = args.likes;
    }
    if (args.views !== undefined && args.views !== null) {
      this.views = args.views;
    }
    if (args.shares !== undefined && args.shares !== null) {
      this.shares = args.shares;
    }
  }
};
SourceStatistics.prototype = {};
SourceStatistics.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.likes = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.views = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.shares = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SourceStatistics.prototype.write = function(output) {
  output.writeStructBegin('SourceStatistics');
  if (this.likes !== null && this.likes !== undefined) {
    output.writeFieldBegin('likes', Thrift.Type.I64, 1);
    output.writeI64(this.likes);
    output.writeFieldEnd();
  }
  if (this.views !== null && this.views !== undefined) {
    output.writeFieldBegin('views', Thrift.Type.I64, 2);
    output.writeI64(this.views);
    output.writeFieldEnd();
  }
  if (this.shares !== null && this.shares !== undefined) {
    output.writeFieldBegin('shares', Thrift.Type.I64, 3);
    output.writeI64(this.shares);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Card = function(args) {
  this.id = null;
  this.unique_card_id = null;
  this.source_name = null;
  this.author = null;
  this.title = null;
  this.description = null;
  this.url = null;
  this.url_to_image = null;
  this.published_at = null;
  this.category = null;
  this.score = null;
  this.vernacular_title = null;
  this.vernacular_description = null;
  this.binge_text = null;
  this.headline = null;
  this.image_ids = null;
  this.image_selected = null;
  this.image_template_selected = null;
  this.status = null;
  this.modified_at = null;
  this.created_at = null;
  this.modified_by = null;
  this.language = null;
  this.country = null;
  this.cta_text = null;
  this.modified_headline = null;
  this.modified_binge_text = null;
  this.owner = null;
  this.skipped_users = null;
  this.curator = null;
  this.url_alternates = null;
  this.url_selected = null;
  this.topic = null;
  this.source_name_selected = null;
  this.overlay_spec = null;
  this.summary = null;
  this.publishing_options = null;
  this.source = null;
  this.is_curated = null;
  this.hotness = null;
  this.categories = null;
  this.glance_published_at = null;
  this.url_meta = null;
  this.is_published = null;
  this.card_type = null;
  this.source_id = null;
  this.publishing_queue_inserted_at = null;
  this.source_publisher_id = null;
  this.image_search_text = null;
  this.segment_ids = null;
  this.glancify_video_data = null;
  this.publishing_partner_id = null;
  this.launch_time = null;
  this.expiry_time = null;
  this.state = null;
  this.binge_cards = null;
  this.magazine = null;
  this.template_position = null;
  this.bubble = null;
  this.draft_id = null;
  this.sub_categories = null;
  this.suggested_sub_category = null;
  this.product_tiles = null;
  this.other_topics = null;
  this.city_ids = null;
  this.assignee = null;
  this.card_meta_data = null;
  this.peek_type = null;
  this.selected_overlay_template = null;
  this.selected_binge_image = null;
  this.collected_by = null;
  this.source_statistics = null;
  this.source_refreshed_at = null;
  this.uploaded_overlay_images = null;
  this.publisher_name = null;
  this.publisher_logo_url = null;
  this.source_identifier = null;
  this.banner_image = null;
  this.logo = null;
  this.read_duration = null;
  this.content = null;
  this.anti_segment_ids = null;
  this.fast_card_data = null;
  this.moderation_status = null;
  this.allow_republish = null;
  this.republish_after_days = null;
  this.republish_counter = null;
  this.publish_as_article = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.unique_card_id !== undefined && args.unique_card_id !== null) {
      this.unique_card_id = args.unique_card_id;
    }
    if (args.source_name !== undefined && args.source_name !== null) {
      this.source_name = args.source_name;
    }
    if (args.author !== undefined && args.author !== null) {
      this.author = args.author;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.url_to_image !== undefined && args.url_to_image !== null) {
      this.url_to_image = args.url_to_image;
    }
    if (args.published_at !== undefined && args.published_at !== null) {
      this.published_at = args.published_at;
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.score !== undefined && args.score !== null) {
      this.score = args.score;
    }
    if (args.vernacular_title !== undefined && args.vernacular_title !== null) {
      this.vernacular_title = args.vernacular_title;
    }
    if (args.vernacular_description !== undefined && args.vernacular_description !== null) {
      this.vernacular_description = args.vernacular_description;
    }
    if (args.binge_text !== undefined && args.binge_text !== null) {
      this.binge_text = args.binge_text;
    }
    if (args.headline !== undefined && args.headline !== null) {
      this.headline = args.headline;
    }
    if (args.image_ids !== undefined && args.image_ids !== null) {
      this.image_ids = Thrift.copyList(args.image_ids, [null]);
    }
    if (args.image_selected !== undefined && args.image_selected !== null) {
      this.image_selected = args.image_selected;
    }
    if (args.image_template_selected !== undefined && args.image_template_selected !== null) {
      this.image_template_selected = args.image_template_selected;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.modified_at !== undefined && args.modified_at !== null) {
      this.modified_at = args.modified_at;
    }
    if (args.created_at !== undefined && args.created_at !== null) {
      this.created_at = args.created_at;
    }
    if (args.modified_by !== undefined && args.modified_by !== null) {
      this.modified_by = args.modified_by;
    }
    if (args.language !== undefined && args.language !== null) {
      this.language = args.language;
    }
    if (args.country !== undefined && args.country !== null) {
      this.country = args.country;
    }
    if (args.cta_text !== undefined && args.cta_text !== null) {
      this.cta_text = args.cta_text;
    }
    if (args.modified_headline !== undefined && args.modified_headline !== null) {
      this.modified_headline = args.modified_headline;
    }
    if (args.modified_binge_text !== undefined && args.modified_binge_text !== null) {
      this.modified_binge_text = args.modified_binge_text;
    }
    if (args.owner !== undefined && args.owner !== null) {
      this.owner = Thrift.copyList(args.owner, [null]);
    }
    if (args.skipped_users !== undefined && args.skipped_users !== null) {
      this.skipped_users = args.skipped_users;
    }
    if (args.curator !== undefined && args.curator !== null) {
      this.curator = args.curator;
    }
    if (args.url_alternates !== undefined && args.url_alternates !== null) {
      this.url_alternates = Thrift.copyList(args.url_alternates, [null]);
    }
    if (args.url_selected !== undefined && args.url_selected !== null) {
      this.url_selected = args.url_selected;
    }
    if (args.topic !== undefined && args.topic !== null) {
      this.topic = args.topic;
    }
    if (args.source_name_selected !== undefined && args.source_name_selected !== null) {
      this.source_name_selected = args.source_name_selected;
    }
    if (args.overlay_spec !== undefined && args.overlay_spec !== null) {
      this.overlay_spec = new OverlaySpec(args.overlay_spec);
    }
    if (args.summary !== undefined && args.summary !== null) {
      this.summary = args.summary;
    }
    if (args.publishing_options !== undefined && args.publishing_options !== null) {
      this.publishing_options = new PublishingOptions(args.publishing_options);
    }
    if (args.source !== undefined && args.source !== null) {
      this.source = args.source;
    }
    if (args.is_curated !== undefined && args.is_curated !== null) {
      this.is_curated = args.is_curated;
    }
    if (args.hotness !== undefined && args.hotness !== null) {
      this.hotness = args.hotness;
    }
    if (args.categories !== undefined && args.categories !== null) {
      this.categories = Thrift.copyList(args.categories, [null]);
    }
    if (args.glance_published_at !== undefined && args.glance_published_at !== null) {
      this.glance_published_at = args.glance_published_at;
    }
    if (args.url_meta !== undefined && args.url_meta !== null) {
      this.url_meta = new UrlMeta(args.url_meta);
    }
    if (args.is_published !== undefined && args.is_published !== null) {
      this.is_published = args.is_published;
    }
    if (args.card_type !== undefined && args.card_type !== null) {
      this.card_type = args.card_type;
    }
    if (args.source_id !== undefined && args.source_id !== null) {
      this.source_id = args.source_id;
    }
    if (args.publishing_queue_inserted_at !== undefined && args.publishing_queue_inserted_at !== null) {
      this.publishing_queue_inserted_at = args.publishing_queue_inserted_at;
    }
    if (args.source_publisher_id !== undefined && args.source_publisher_id !== null) {
      this.source_publisher_id = args.source_publisher_id;
    }
    if (args.image_search_text !== undefined && args.image_search_text !== null) {
      this.image_search_text = args.image_search_text;
    }
    if (args.segment_ids !== undefined && args.segment_ids !== null) {
      this.segment_ids = Thrift.copyList(args.segment_ids, [null]);
    }
    if (args.glancify_video_data !== undefined && args.glancify_video_data !== null) {
      this.glancify_video_data = new GlancifyVideoData(args.glancify_video_data);
    }
    if (args.publishing_partner_id !== undefined && args.publishing_partner_id !== null) {
      this.publishing_partner_id = args.publishing_partner_id;
    }
    if (args.launch_time !== undefined && args.launch_time !== null) {
      this.launch_time = args.launch_time;
    }
    if (args.expiry_time !== undefined && args.expiry_time !== null) {
      this.expiry_time = args.expiry_time;
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    }
    if (args.binge_cards !== undefined && args.binge_cards !== null) {
      this.binge_cards = Thrift.copyList(args.binge_cards, [null]);
    }
    if (args.magazine !== undefined && args.magazine !== null) {
      this.magazine = Thrift.copyMap(args.magazine, [Thrift.copyList, null]);
    }
    if (args.template_position !== undefined && args.template_position !== null) {
      this.template_position = args.template_position;
    }
    if (args.bubble !== undefined && args.bubble !== null) {
      this.bubble = new Bubble(args.bubble);
    }
    if (args.draft_id !== undefined && args.draft_id !== null) {
      this.draft_id = args.draft_id;
    }
    if (args.sub_categories !== undefined && args.sub_categories !== null) {
      this.sub_categories = Thrift.copyList(args.sub_categories, [null]);
    }
    if (args.suggested_sub_category !== undefined && args.suggested_sub_category !== null) {
      this.suggested_sub_category = args.suggested_sub_category;
    }
    if (args.product_tiles !== undefined && args.product_tiles !== null) {
      this.product_tiles = new ProductTiles(args.product_tiles);
    }
    if (args.other_topics !== undefined && args.other_topics !== null) {
      this.other_topics = Thrift.copyList(args.other_topics, [null]);
    }
    if (args.city_ids !== undefined && args.city_ids !== null) {
      this.city_ids = Thrift.copyList(args.city_ids, [null]);
    }
    if (args.assignee !== undefined && args.assignee !== null) {
      this.assignee = new JavelinUser(args.assignee);
    }
    if (args.card_meta_data !== undefined && args.card_meta_data !== null) {
      this.card_meta_data = new CardMetaData(args.card_meta_data);
    }
    if (args.peek_type !== undefined && args.peek_type !== null) {
      this.peek_type = args.peek_type;
    }
    if (args.selected_overlay_template !== undefined && args.selected_overlay_template !== null) {
      this.selected_overlay_template = args.selected_overlay_template;
    }
    if (args.selected_binge_image !== undefined && args.selected_binge_image !== null) {
      this.selected_binge_image = args.selected_binge_image;
    }
    if (args.collected_by !== undefined && args.collected_by !== null) {
      this.collected_by = args.collected_by;
    }
    if (args.source_statistics !== undefined && args.source_statistics !== null) {
      this.source_statistics = new SourceStatistics(args.source_statistics);
    }
    if (args.source_refreshed_at !== undefined && args.source_refreshed_at !== null) {
      this.source_refreshed_at = args.source_refreshed_at;
    }
    if (args.uploaded_overlay_images !== undefined && args.uploaded_overlay_images !== null) {
      this.uploaded_overlay_images = Thrift.copyList(args.uploaded_overlay_images, [null]);
    }
    if (args.publisher_name !== undefined && args.publisher_name !== null) {
      this.publisher_name = args.publisher_name;
    }
    if (args.publisher_logo_url !== undefined && args.publisher_logo_url !== null) {
      this.publisher_logo_url = args.publisher_logo_url;
    }
    if (args.source_identifier !== undefined && args.source_identifier !== null) {
      this.source_identifier = args.source_identifier;
    }
    if (args.banner_image !== undefined && args.banner_image !== null) {
      this.banner_image = args.banner_image;
    }
    if (args.logo !== undefined && args.logo !== null) {
      this.logo = args.logo;
    }
    if (args.read_duration !== undefined && args.read_duration !== null) {
      this.read_duration = args.read_duration;
    }
    if (args.content !== undefined && args.content !== null) {
      this.content = args.content;
    }
    if (args.anti_segment_ids !== undefined && args.anti_segment_ids !== null) {
      this.anti_segment_ids = Thrift.copyList(args.anti_segment_ids, [null]);
    }
    if (args.fast_card_data !== undefined && args.fast_card_data !== null) {
      this.fast_card_data = new FastCardData(args.fast_card_data);
    }
    if (args.moderation_status !== undefined && args.moderation_status !== null) {
      this.moderation_status = args.moderation_status;
    }
    if (args.allow_republish !== undefined && args.allow_republish !== null) {
      this.allow_republish = args.allow_republish;
    }
    if (args.republish_after_days !== undefined && args.republish_after_days !== null) {
      this.republish_after_days = args.republish_after_days;
    }
    if (args.republish_counter !== undefined && args.republish_counter !== null) {
      this.republish_counter = args.republish_counter;
    }
    if (args.publish_as_article !== undefined && args.publish_as_article !== null) {
      this.publish_as_article = args.publish_as_article;
    }
  }
};
Card.prototype = {};
Card.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.unique_card_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.source_name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.author = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.url_to_image = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.published_at = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.DOUBLE) {
        this.score = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.vernacular_title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.vernacular_description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.binge_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.headline = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.LIST) {
        var _size1786 = 0;
        var _rtmp31790;
        this.image_ids = [];
        var _etype1789 = 0;
        _rtmp31790 = input.readListBegin();
        _etype1789 = _rtmp31790.etype;
        _size1786 = _rtmp31790.size;
        for (var _i1791 = 0; _i1791 < _size1786; ++_i1791)
        {
          var elem1792 = null;
          elem1792 = input.readString().value;
          this.image_ids.push(elem1792);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.image_selected = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.image_template_selected = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.modified_at = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRING) {
        this.created_at = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRING) {
        this.modified_by = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.language = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.STRING) {
        this.country = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.STRING) {
        this.cta_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.STRING) {
        this.modified_headline = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.STRING) {
        this.modified_binge_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.LIST) {
        var _size1793 = 0;
        var _rtmp31797;
        this.owner = [];
        var _etype1796 = 0;
        _rtmp31797 = input.readListBegin();
        _etype1796 = _rtmp31797.etype;
        _size1793 = _rtmp31797.size;
        for (var _i1798 = 0; _i1798 < _size1793; ++_i1798)
        {
          var elem1799 = null;
          elem1799 = input.readString().value;
          this.owner.push(elem1799);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.STRING) {
        this.skipped_users = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.STRING) {
        this.curator = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.LIST) {
        var _size1800 = 0;
        var _rtmp31804;
        this.url_alternates = [];
        var _etype1803 = 0;
        _rtmp31804 = input.readListBegin();
        _etype1803 = _rtmp31804.etype;
        _size1800 = _rtmp31804.size;
        for (var _i1805 = 0; _i1805 < _size1800; ++_i1805)
        {
          var elem1806 = null;
          elem1806 = input.readString().value;
          this.url_alternates.push(elem1806);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRING) {
        this.url_selected = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.STRING) {
        this.topic = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.STRING) {
        this.source_name_selected = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.STRUCT) {
        this.overlay_spec = new OverlaySpec();
        this.overlay_spec.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 36:
      if (ftype == Thrift.Type.STRING) {
        this.summary = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 37:
      if (ftype == Thrift.Type.STRUCT) {
        this.publishing_options = new PublishingOptions();
        this.publishing_options.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 38:
      if (ftype == Thrift.Type.STRING) {
        this.source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 39:
      if (ftype == Thrift.Type.BOOL) {
        this.is_curated = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 40:
      if (ftype == Thrift.Type.DOUBLE) {
        this.hotness = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 41:
      if (ftype == Thrift.Type.LIST) {
        var _size1807 = 0;
        var _rtmp31811;
        this.categories = [];
        var _etype1810 = 0;
        _rtmp31811 = input.readListBegin();
        _etype1810 = _rtmp31811.etype;
        _size1807 = _rtmp31811.size;
        for (var _i1812 = 0; _i1812 < _size1807; ++_i1812)
        {
          var elem1813 = null;
          elem1813 = input.readString().value;
          this.categories.push(elem1813);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 42:
      if (ftype == Thrift.Type.STRING) {
        this.glance_published_at = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 43:
      if (ftype == Thrift.Type.STRUCT) {
        this.url_meta = new UrlMeta();
        this.url_meta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 44:
      if (ftype == Thrift.Type.BOOL) {
        this.is_published = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 45:
      if (ftype == Thrift.Type.STRING) {
        this.card_type = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 46:
      if (ftype == Thrift.Type.STRING) {
        this.source_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 47:
      if (ftype == Thrift.Type.STRING) {
        this.publishing_queue_inserted_at = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 48:
      if (ftype == Thrift.Type.STRING) {
        this.source_publisher_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 49:
      if (ftype == Thrift.Type.STRING) {
        this.image_search_text = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 50:
      if (ftype == Thrift.Type.SET) {
        var _size1814 = 0;
        var _rtmp31818;
        this.segment_ids = [];
        var _etype1817 = 0;
        _rtmp31818 = input.readSetBegin();
        _etype1817 = _rtmp31818.etype;
        _size1814 = _rtmp31818.size;
        for (var _i1819 = 0; _i1819 < _size1814; ++_i1819)
        {
          var elem1820 = null;
          elem1820 = input.readI32().value;
          this.segment_ids.push(elem1820);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 51:
      if (ftype == Thrift.Type.STRUCT) {
        this.glancify_video_data = new GlancifyVideoData();
        this.glancify_video_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 52:
      if (ftype == Thrift.Type.STRING) {
        this.publishing_partner_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 53:
      if (ftype == Thrift.Type.I64) {
        this.launch_time = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 54:
      if (ftype == Thrift.Type.I64) {
        this.expiry_time = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 55:
      if (ftype == Thrift.Type.STRING) {
        this.state = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 56:
      if (ftype == Thrift.Type.LIST) {
        var _size1821 = 0;
        var _rtmp31825;
        this.binge_cards = [];
        var _etype1824 = 0;
        _rtmp31825 = input.readListBegin();
        _etype1824 = _rtmp31825.etype;
        _size1821 = _rtmp31825.size;
        for (var _i1826 = 0; _i1826 < _size1821; ++_i1826)
        {
          var elem1827 = null;
          elem1827 = input.readString().value;
          this.binge_cards.push(elem1827);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 57:
      if (ftype == Thrift.Type.MAP) {
        var _size1828 = 0;
        var _rtmp31832;
        this.magazine = {};
        var _ktype1829 = 0;
        var _vtype1830 = 0;
        _rtmp31832 = input.readMapBegin();
        _ktype1829 = _rtmp31832.ktype;
        _vtype1830 = _rtmp31832.vtype;
        _size1828 = _rtmp31832.size;
        for (var _i1833 = 0; _i1833 < _size1828; ++_i1833)
        {
          if (_i1833 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key1834 = null;
          var val1835 = null;
          key1834 = input.readString().value;
          var _size1836 = 0;
          var _rtmp31840;
          val1835 = [];
          var _etype1839 = 0;
          _rtmp31840 = input.readListBegin();
          _etype1839 = _rtmp31840.etype;
          _size1836 = _rtmp31840.size;
          for (var _i1841 = 0; _i1841 < _size1836; ++_i1841)
          {
            var elem1842 = null;
            elem1842 = input.readString().value;
            val1835.push(elem1842);
          }
          input.readListEnd();
          this.magazine[key1834] = val1835;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 58:
      if (ftype == Thrift.Type.STRING) {
        this.template_position = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 59:
      if (ftype == Thrift.Type.STRUCT) {
        this.bubble = new Bubble();
        this.bubble.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 60:
      if (ftype == Thrift.Type.STRING) {
        this.draft_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 61:
      if (ftype == Thrift.Type.LIST) {
        var _size1843 = 0;
        var _rtmp31847;
        this.sub_categories = [];
        var _etype1846 = 0;
        _rtmp31847 = input.readListBegin();
        _etype1846 = _rtmp31847.etype;
        _size1843 = _rtmp31847.size;
        for (var _i1848 = 0; _i1848 < _size1843; ++_i1848)
        {
          var elem1849 = null;
          elem1849 = input.readString().value;
          this.sub_categories.push(elem1849);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 62:
      if (ftype == Thrift.Type.STRING) {
        this.suggested_sub_category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 63:
      if (ftype == Thrift.Type.STRUCT) {
        this.product_tiles = new ProductTiles();
        this.product_tiles.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 64:
      if (ftype == Thrift.Type.LIST) {
        var _size1850 = 0;
        var _rtmp31854;
        this.other_topics = [];
        var _etype1853 = 0;
        _rtmp31854 = input.readListBegin();
        _etype1853 = _rtmp31854.etype;
        _size1850 = _rtmp31854.size;
        for (var _i1855 = 0; _i1855 < _size1850; ++_i1855)
        {
          var elem1856 = null;
          elem1856 = input.readString().value;
          this.other_topics.push(elem1856);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 65:
      if (ftype == Thrift.Type.SET) {
        var _size1857 = 0;
        var _rtmp31861;
        this.city_ids = [];
        var _etype1860 = 0;
        _rtmp31861 = input.readSetBegin();
        _etype1860 = _rtmp31861.etype;
        _size1857 = _rtmp31861.size;
        for (var _i1862 = 0; _i1862 < _size1857; ++_i1862)
        {
          var elem1863 = null;
          elem1863 = input.readI32().value;
          this.city_ids.push(elem1863);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 66:
      if (ftype == Thrift.Type.STRUCT) {
        this.assignee = new JavelinUser();
        this.assignee.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 67:
      if (ftype == Thrift.Type.STRUCT) {
        this.card_meta_data = new CardMetaData();
        this.card_meta_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 68:
      if (ftype == Thrift.Type.STRING) {
        this.peek_type = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 69:
      if (ftype == Thrift.Type.STRING) {
        this.selected_overlay_template = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 70:
      if (ftype == Thrift.Type.STRING) {
        this.selected_binge_image = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 71:
      if (ftype == Thrift.Type.STRING) {
        this.collected_by = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 72:
      if (ftype == Thrift.Type.STRUCT) {
        this.source_statistics = new SourceStatistics();
        this.source_statistics.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 73:
      if (ftype == Thrift.Type.STRING) {
        this.source_refreshed_at = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 74:
      if (ftype == Thrift.Type.LIST) {
        var _size1864 = 0;
        var _rtmp31868;
        this.uploaded_overlay_images = [];
        var _etype1867 = 0;
        _rtmp31868 = input.readListBegin();
        _etype1867 = _rtmp31868.etype;
        _size1864 = _rtmp31868.size;
        for (var _i1869 = 0; _i1869 < _size1864; ++_i1869)
        {
          var elem1870 = null;
          elem1870 = input.readString().value;
          this.uploaded_overlay_images.push(elem1870);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 75:
      if (ftype == Thrift.Type.STRING) {
        this.publisher_name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 76:
      if (ftype == Thrift.Type.STRING) {
        this.publisher_logo_url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 77:
      if (ftype == Thrift.Type.I32) {
        this.source_identifier = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 78:
      if (ftype == Thrift.Type.STRING) {
        this.banner_image = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 79:
      if (ftype == Thrift.Type.STRING) {
        this.logo = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 80:
      if (ftype == Thrift.Type.STRING) {
        this.read_duration = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 81:
      if (ftype == Thrift.Type.STRING) {
        this.content = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 82:
      if (ftype == Thrift.Type.SET) {
        var _size1871 = 0;
        var _rtmp31875;
        this.anti_segment_ids = [];
        var _etype1874 = 0;
        _rtmp31875 = input.readSetBegin();
        _etype1874 = _rtmp31875.etype;
        _size1871 = _rtmp31875.size;
        for (var _i1876 = 0; _i1876 < _size1871; ++_i1876)
        {
          var elem1877 = null;
          elem1877 = input.readI32().value;
          this.anti_segment_ids.push(elem1877);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 83:
      if (ftype == Thrift.Type.STRUCT) {
        this.fast_card_data = new FastCardData();
        this.fast_card_data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 84:
      if (ftype == Thrift.Type.I32) {
        this.moderation_status = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 85:
      if (ftype == Thrift.Type.BOOL) {
        this.allow_republish = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 86:
      if (ftype == Thrift.Type.I32) {
        this.republish_after_days = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 87:
      if (ftype == Thrift.Type.I32) {
        this.republish_counter = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 88:
      if (ftype == Thrift.Type.BOOL) {
        this.publish_as_article = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Card.prototype.write = function(output) {
  output.writeStructBegin('Card');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.unique_card_id !== null && this.unique_card_id !== undefined) {
    output.writeFieldBegin('unique_card_id', Thrift.Type.STRING, 2);
    output.writeString(this.unique_card_id);
    output.writeFieldEnd();
  }
  if (this.source_name !== null && this.source_name !== undefined) {
    output.writeFieldBegin('source_name', Thrift.Type.STRING, 3);
    output.writeString(this.source_name);
    output.writeFieldEnd();
  }
  if (this.author !== null && this.author !== undefined) {
    output.writeFieldBegin('author', Thrift.Type.STRING, 4);
    output.writeString(this.author);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 5);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 6);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 7);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.url_to_image !== null && this.url_to_image !== undefined) {
    output.writeFieldBegin('url_to_image', Thrift.Type.STRING, 8);
    output.writeString(this.url_to_image);
    output.writeFieldEnd();
  }
  if (this.published_at !== null && this.published_at !== undefined) {
    output.writeFieldBegin('published_at', Thrift.Type.STRING, 9);
    output.writeString(this.published_at);
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 10);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.score !== null && this.score !== undefined) {
    output.writeFieldBegin('score', Thrift.Type.DOUBLE, 11);
    output.writeDouble(this.score);
    output.writeFieldEnd();
  }
  if (this.vernacular_title !== null && this.vernacular_title !== undefined) {
    output.writeFieldBegin('vernacular_title', Thrift.Type.STRING, 12);
    output.writeString(this.vernacular_title);
    output.writeFieldEnd();
  }
  if (this.vernacular_description !== null && this.vernacular_description !== undefined) {
    output.writeFieldBegin('vernacular_description', Thrift.Type.STRING, 13);
    output.writeString(this.vernacular_description);
    output.writeFieldEnd();
  }
  if (this.binge_text !== null && this.binge_text !== undefined) {
    output.writeFieldBegin('binge_text', Thrift.Type.STRING, 14);
    output.writeString(this.binge_text);
    output.writeFieldEnd();
  }
  if (this.headline !== null && this.headline !== undefined) {
    output.writeFieldBegin('headline', Thrift.Type.STRING, 15);
    output.writeString(this.headline);
    output.writeFieldEnd();
  }
  if (this.image_ids !== null && this.image_ids !== undefined) {
    output.writeFieldBegin('image_ids', Thrift.Type.LIST, 16);
    output.writeListBegin(Thrift.Type.STRING, this.image_ids.length);
    for (var iter1878 in this.image_ids)
    {
      if (this.image_ids.hasOwnProperty(iter1878))
      {
        iter1878 = this.image_ids[iter1878];
        output.writeString(iter1878);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.image_selected !== null && this.image_selected !== undefined) {
    output.writeFieldBegin('image_selected', Thrift.Type.STRING, 17);
    output.writeString(this.image_selected);
    output.writeFieldEnd();
  }
  if (this.image_template_selected !== null && this.image_template_selected !== undefined) {
    output.writeFieldBegin('image_template_selected', Thrift.Type.STRING, 18);
    output.writeString(this.image_template_selected);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 19);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  if (this.modified_at !== null && this.modified_at !== undefined) {
    output.writeFieldBegin('modified_at', Thrift.Type.STRING, 20);
    output.writeString(this.modified_at);
    output.writeFieldEnd();
  }
  if (this.created_at !== null && this.created_at !== undefined) {
    output.writeFieldBegin('created_at', Thrift.Type.STRING, 21);
    output.writeString(this.created_at);
    output.writeFieldEnd();
  }
  if (this.modified_by !== null && this.modified_by !== undefined) {
    output.writeFieldBegin('modified_by', Thrift.Type.STRING, 22);
    output.writeString(this.modified_by);
    output.writeFieldEnd();
  }
  if (this.language !== null && this.language !== undefined) {
    output.writeFieldBegin('language', Thrift.Type.STRING, 23);
    output.writeString(this.language);
    output.writeFieldEnd();
  }
  if (this.country !== null && this.country !== undefined) {
    output.writeFieldBegin('country', Thrift.Type.STRING, 24);
    output.writeString(this.country);
    output.writeFieldEnd();
  }
  if (this.cta_text !== null && this.cta_text !== undefined) {
    output.writeFieldBegin('cta_text', Thrift.Type.STRING, 25);
    output.writeString(this.cta_text);
    output.writeFieldEnd();
  }
  if (this.modified_headline !== null && this.modified_headline !== undefined) {
    output.writeFieldBegin('modified_headline', Thrift.Type.STRING, 26);
    output.writeString(this.modified_headline);
    output.writeFieldEnd();
  }
  if (this.modified_binge_text !== null && this.modified_binge_text !== undefined) {
    output.writeFieldBegin('modified_binge_text', Thrift.Type.STRING, 27);
    output.writeString(this.modified_binge_text);
    output.writeFieldEnd();
  }
  if (this.owner !== null && this.owner !== undefined) {
    output.writeFieldBegin('owner', Thrift.Type.LIST, 28);
    output.writeListBegin(Thrift.Type.STRING, this.owner.length);
    for (var iter1879 in this.owner)
    {
      if (this.owner.hasOwnProperty(iter1879))
      {
        iter1879 = this.owner[iter1879];
        output.writeString(iter1879);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.skipped_users !== null && this.skipped_users !== undefined) {
    output.writeFieldBegin('skipped_users', Thrift.Type.STRING, 29);
    output.writeString(this.skipped_users);
    output.writeFieldEnd();
  }
  if (this.curator !== null && this.curator !== undefined) {
    output.writeFieldBegin('curator', Thrift.Type.STRING, 30);
    output.writeString(this.curator);
    output.writeFieldEnd();
  }
  if (this.url_alternates !== null && this.url_alternates !== undefined) {
    output.writeFieldBegin('url_alternates', Thrift.Type.LIST, 31);
    output.writeListBegin(Thrift.Type.STRING, this.url_alternates.length);
    for (var iter1880 in this.url_alternates)
    {
      if (this.url_alternates.hasOwnProperty(iter1880))
      {
        iter1880 = this.url_alternates[iter1880];
        output.writeString(iter1880);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.url_selected !== null && this.url_selected !== undefined) {
    output.writeFieldBegin('url_selected', Thrift.Type.STRING, 32);
    output.writeString(this.url_selected);
    output.writeFieldEnd();
  }
  if (this.topic !== null && this.topic !== undefined) {
    output.writeFieldBegin('topic', Thrift.Type.STRING, 33);
    output.writeString(this.topic);
    output.writeFieldEnd();
  }
  if (this.source_name_selected !== null && this.source_name_selected !== undefined) {
    output.writeFieldBegin('source_name_selected', Thrift.Type.STRING, 34);
    output.writeString(this.source_name_selected);
    output.writeFieldEnd();
  }
  if (this.overlay_spec !== null && this.overlay_spec !== undefined) {
    output.writeFieldBegin('overlay_spec', Thrift.Type.STRUCT, 35);
    this.overlay_spec.write(output);
    output.writeFieldEnd();
  }
  if (this.summary !== null && this.summary !== undefined) {
    output.writeFieldBegin('summary', Thrift.Type.STRING, 36);
    output.writeString(this.summary);
    output.writeFieldEnd();
  }
  if (this.publishing_options !== null && this.publishing_options !== undefined) {
    output.writeFieldBegin('publishing_options', Thrift.Type.STRUCT, 37);
    this.publishing_options.write(output);
    output.writeFieldEnd();
  }
  if (this.source !== null && this.source !== undefined) {
    output.writeFieldBegin('source', Thrift.Type.STRING, 38);
    output.writeString(this.source);
    output.writeFieldEnd();
  }
  if (this.is_curated !== null && this.is_curated !== undefined) {
    output.writeFieldBegin('is_curated', Thrift.Type.BOOL, 39);
    output.writeBool(this.is_curated);
    output.writeFieldEnd();
  }
  if (this.hotness !== null && this.hotness !== undefined) {
    output.writeFieldBegin('hotness', Thrift.Type.DOUBLE, 40);
    output.writeDouble(this.hotness);
    output.writeFieldEnd();
  }
  if (this.categories !== null && this.categories !== undefined) {
    output.writeFieldBegin('categories', Thrift.Type.LIST, 41);
    output.writeListBegin(Thrift.Type.STRING, this.categories.length);
    for (var iter1881 in this.categories)
    {
      if (this.categories.hasOwnProperty(iter1881))
      {
        iter1881 = this.categories[iter1881];
        output.writeString(iter1881);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.glance_published_at !== null && this.glance_published_at !== undefined) {
    output.writeFieldBegin('glance_published_at', Thrift.Type.STRING, 42);
    output.writeString(this.glance_published_at);
    output.writeFieldEnd();
  }
  if (this.url_meta !== null && this.url_meta !== undefined) {
    output.writeFieldBegin('url_meta', Thrift.Type.STRUCT, 43);
    this.url_meta.write(output);
    output.writeFieldEnd();
  }
  if (this.is_published !== null && this.is_published !== undefined) {
    output.writeFieldBegin('is_published', Thrift.Type.BOOL, 44);
    output.writeBool(this.is_published);
    output.writeFieldEnd();
  }
  if (this.card_type !== null && this.card_type !== undefined) {
    output.writeFieldBegin('card_type', Thrift.Type.STRING, 45);
    output.writeString(this.card_type);
    output.writeFieldEnd();
  }
  if (this.source_id !== null && this.source_id !== undefined) {
    output.writeFieldBegin('source_id', Thrift.Type.STRING, 46);
    output.writeString(this.source_id);
    output.writeFieldEnd();
  }
  if (this.publishing_queue_inserted_at !== null && this.publishing_queue_inserted_at !== undefined) {
    output.writeFieldBegin('publishing_queue_inserted_at', Thrift.Type.STRING, 47);
    output.writeString(this.publishing_queue_inserted_at);
    output.writeFieldEnd();
  }
  if (this.source_publisher_id !== null && this.source_publisher_id !== undefined) {
    output.writeFieldBegin('source_publisher_id', Thrift.Type.STRING, 48);
    output.writeString(this.source_publisher_id);
    output.writeFieldEnd();
  }
  if (this.image_search_text !== null && this.image_search_text !== undefined) {
    output.writeFieldBegin('image_search_text', Thrift.Type.STRING, 49);
    output.writeString(this.image_search_text);
    output.writeFieldEnd();
  }
  if (this.segment_ids !== null && this.segment_ids !== undefined) {
    output.writeFieldBegin('segment_ids', Thrift.Type.SET, 50);
    output.writeSetBegin(Thrift.Type.I32, this.segment_ids.length);
    for (var iter1882 in this.segment_ids)
    {
      if (this.segment_ids.hasOwnProperty(iter1882))
      {
        iter1882 = this.segment_ids[iter1882];
        output.writeI32(iter1882);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.glancify_video_data !== null && this.glancify_video_data !== undefined) {
    output.writeFieldBegin('glancify_video_data', Thrift.Type.STRUCT, 51);
    this.glancify_video_data.write(output);
    output.writeFieldEnd();
  }
  if (this.publishing_partner_id !== null && this.publishing_partner_id !== undefined) {
    output.writeFieldBegin('publishing_partner_id', Thrift.Type.STRING, 52);
    output.writeString(this.publishing_partner_id);
    output.writeFieldEnd();
  }
  if (this.launch_time !== null && this.launch_time !== undefined) {
    output.writeFieldBegin('launch_time', Thrift.Type.I64, 53);
    output.writeI64(this.launch_time);
    output.writeFieldEnd();
  }
  if (this.expiry_time !== null && this.expiry_time !== undefined) {
    output.writeFieldBegin('expiry_time', Thrift.Type.I64, 54);
    output.writeI64(this.expiry_time);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.STRING, 55);
    output.writeString(this.state);
    output.writeFieldEnd();
  }
  if (this.binge_cards !== null && this.binge_cards !== undefined) {
    output.writeFieldBegin('binge_cards', Thrift.Type.LIST, 56);
    output.writeListBegin(Thrift.Type.STRING, this.binge_cards.length);
    for (var iter1883 in this.binge_cards)
    {
      if (this.binge_cards.hasOwnProperty(iter1883))
      {
        iter1883 = this.binge_cards[iter1883];
        output.writeString(iter1883);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.magazine !== null && this.magazine !== undefined) {
    output.writeFieldBegin('magazine', Thrift.Type.MAP, 57);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.LIST, Thrift.objectLength(this.magazine));
    for (var kiter1884 in this.magazine)
    {
      if (this.magazine.hasOwnProperty(kiter1884))
      {
        var viter1885 = this.magazine[kiter1884];
        output.writeString(kiter1884);
        output.writeListBegin(Thrift.Type.STRING, viter1885.length);
        for (var iter1886 in viter1885)
        {
          if (viter1885.hasOwnProperty(iter1886))
          {
            iter1886 = viter1885[iter1886];
            output.writeString(iter1886);
          }
        }
        output.writeListEnd();
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.template_position !== null && this.template_position !== undefined) {
    output.writeFieldBegin('template_position', Thrift.Type.STRING, 58);
    output.writeString(this.template_position);
    output.writeFieldEnd();
  }
  if (this.bubble !== null && this.bubble !== undefined) {
    output.writeFieldBegin('bubble', Thrift.Type.STRUCT, 59);
    this.bubble.write(output);
    output.writeFieldEnd();
  }
  if (this.draft_id !== null && this.draft_id !== undefined) {
    output.writeFieldBegin('draft_id', Thrift.Type.STRING, 60);
    output.writeString(this.draft_id);
    output.writeFieldEnd();
  }
  if (this.sub_categories !== null && this.sub_categories !== undefined) {
    output.writeFieldBegin('sub_categories', Thrift.Type.LIST, 61);
    output.writeListBegin(Thrift.Type.STRING, this.sub_categories.length);
    for (var iter1887 in this.sub_categories)
    {
      if (this.sub_categories.hasOwnProperty(iter1887))
      {
        iter1887 = this.sub_categories[iter1887];
        output.writeString(iter1887);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.suggested_sub_category !== null && this.suggested_sub_category !== undefined) {
    output.writeFieldBegin('suggested_sub_category', Thrift.Type.STRING, 62);
    output.writeString(this.suggested_sub_category);
    output.writeFieldEnd();
  }
  if (this.product_tiles !== null && this.product_tiles !== undefined) {
    output.writeFieldBegin('product_tiles', Thrift.Type.STRUCT, 63);
    this.product_tiles.write(output);
    output.writeFieldEnd();
  }
  if (this.other_topics !== null && this.other_topics !== undefined) {
    output.writeFieldBegin('other_topics', Thrift.Type.LIST, 64);
    output.writeListBegin(Thrift.Type.STRING, this.other_topics.length);
    for (var iter1888 in this.other_topics)
    {
      if (this.other_topics.hasOwnProperty(iter1888))
      {
        iter1888 = this.other_topics[iter1888];
        output.writeString(iter1888);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.city_ids !== null && this.city_ids !== undefined) {
    output.writeFieldBegin('city_ids', Thrift.Type.SET, 65);
    output.writeSetBegin(Thrift.Type.I32, this.city_ids.length);
    for (var iter1889 in this.city_ids)
    {
      if (this.city_ids.hasOwnProperty(iter1889))
      {
        iter1889 = this.city_ids[iter1889];
        output.writeI32(iter1889);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.assignee !== null && this.assignee !== undefined) {
    output.writeFieldBegin('assignee', Thrift.Type.STRUCT, 66);
    this.assignee.write(output);
    output.writeFieldEnd();
  }
  if (this.card_meta_data !== null && this.card_meta_data !== undefined) {
    output.writeFieldBegin('card_meta_data', Thrift.Type.STRUCT, 67);
    this.card_meta_data.write(output);
    output.writeFieldEnd();
  }
  if (this.peek_type !== null && this.peek_type !== undefined) {
    output.writeFieldBegin('peek_type', Thrift.Type.STRING, 68);
    output.writeString(this.peek_type);
    output.writeFieldEnd();
  }
  if (this.selected_overlay_template !== null && this.selected_overlay_template !== undefined) {
    output.writeFieldBegin('selected_overlay_template', Thrift.Type.STRING, 69);
    output.writeString(this.selected_overlay_template);
    output.writeFieldEnd();
  }
  if (this.selected_binge_image !== null && this.selected_binge_image !== undefined) {
    output.writeFieldBegin('selected_binge_image', Thrift.Type.STRING, 70);
    output.writeString(this.selected_binge_image);
    output.writeFieldEnd();
  }
  if (this.collected_by !== null && this.collected_by !== undefined) {
    output.writeFieldBegin('collected_by', Thrift.Type.STRING, 71);
    output.writeString(this.collected_by);
    output.writeFieldEnd();
  }
  if (this.source_statistics !== null && this.source_statistics !== undefined) {
    output.writeFieldBegin('source_statistics', Thrift.Type.STRUCT, 72);
    this.source_statistics.write(output);
    output.writeFieldEnd();
  }
  if (this.source_refreshed_at !== null && this.source_refreshed_at !== undefined) {
    output.writeFieldBegin('source_refreshed_at', Thrift.Type.STRING, 73);
    output.writeString(this.source_refreshed_at);
    output.writeFieldEnd();
  }
  if (this.uploaded_overlay_images !== null && this.uploaded_overlay_images !== undefined) {
    output.writeFieldBegin('uploaded_overlay_images', Thrift.Type.LIST, 74);
    output.writeListBegin(Thrift.Type.STRING, this.uploaded_overlay_images.length);
    for (var iter1890 in this.uploaded_overlay_images)
    {
      if (this.uploaded_overlay_images.hasOwnProperty(iter1890))
      {
        iter1890 = this.uploaded_overlay_images[iter1890];
        output.writeString(iter1890);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.publisher_name !== null && this.publisher_name !== undefined) {
    output.writeFieldBegin('publisher_name', Thrift.Type.STRING, 75);
    output.writeString(this.publisher_name);
    output.writeFieldEnd();
  }
  if (this.publisher_logo_url !== null && this.publisher_logo_url !== undefined) {
    output.writeFieldBegin('publisher_logo_url', Thrift.Type.STRING, 76);
    output.writeString(this.publisher_logo_url);
    output.writeFieldEnd();
  }
  if (this.source_identifier !== null && this.source_identifier !== undefined) {
    output.writeFieldBegin('source_identifier', Thrift.Type.I32, 77);
    output.writeI32(this.source_identifier);
    output.writeFieldEnd();
  }
  if (this.banner_image !== null && this.banner_image !== undefined) {
    output.writeFieldBegin('banner_image', Thrift.Type.STRING, 78);
    output.writeString(this.banner_image);
    output.writeFieldEnd();
  }
  if (this.logo !== null && this.logo !== undefined) {
    output.writeFieldBegin('logo', Thrift.Type.STRING, 79);
    output.writeString(this.logo);
    output.writeFieldEnd();
  }
  if (this.read_duration !== null && this.read_duration !== undefined) {
    output.writeFieldBegin('read_duration', Thrift.Type.STRING, 80);
    output.writeString(this.read_duration);
    output.writeFieldEnd();
  }
  if (this.content !== null && this.content !== undefined) {
    output.writeFieldBegin('content', Thrift.Type.STRING, 81);
    output.writeString(this.content);
    output.writeFieldEnd();
  }
  if (this.anti_segment_ids !== null && this.anti_segment_ids !== undefined) {
    output.writeFieldBegin('anti_segment_ids', Thrift.Type.SET, 82);
    output.writeSetBegin(Thrift.Type.I32, this.anti_segment_ids.length);
    for (var iter1891 in this.anti_segment_ids)
    {
      if (this.anti_segment_ids.hasOwnProperty(iter1891))
      {
        iter1891 = this.anti_segment_ids[iter1891];
        output.writeI32(iter1891);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.fast_card_data !== null && this.fast_card_data !== undefined) {
    output.writeFieldBegin('fast_card_data', Thrift.Type.STRUCT, 83);
    this.fast_card_data.write(output);
    output.writeFieldEnd();
  }
  if (this.moderation_status !== null && this.moderation_status !== undefined) {
    output.writeFieldBegin('moderation_status', Thrift.Type.I32, 84);
    output.writeI32(this.moderation_status);
    output.writeFieldEnd();
  }
  if (this.allow_republish !== null && this.allow_republish !== undefined) {
    output.writeFieldBegin('allow_republish', Thrift.Type.BOOL, 85);
    output.writeBool(this.allow_republish);
    output.writeFieldEnd();
  }
  if (this.republish_after_days !== null && this.republish_after_days !== undefined) {
    output.writeFieldBegin('republish_after_days', Thrift.Type.I32, 86);
    output.writeI32(this.republish_after_days);
    output.writeFieldEnd();
  }
  if (this.republish_counter !== null && this.republish_counter !== undefined) {
    output.writeFieldBegin('republish_counter', Thrift.Type.I32, 87);
    output.writeI32(this.republish_counter);
    output.writeFieldEnd();
  }
  if (this.publish_as_article !== null && this.publish_as_article !== undefined) {
    output.writeFieldBegin('publish_as_article', Thrift.Type.BOOL, 88);
    output.writeBool(this.publish_as_article);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ImageTag = function(args) {
  this.fetch = null;
  this.table_source = null;
  this.image_source = null;
  this.image_id = null;
  this.created_by = null;
  if (args) {
    if (args.fetch !== undefined && args.fetch !== null) {
      this.fetch = args.fetch;
    }
    if (args.table_source !== undefined && args.table_source !== null) {
      this.table_source = args.table_source;
    }
    if (args.image_source !== undefined && args.image_source !== null) {
      this.image_source = args.image_source;
    }
    if (args.image_id !== undefined && args.image_id !== null) {
      this.image_id = args.image_id;
    }
    if (args.created_by !== undefined && args.created_by !== null) {
      this.created_by = args.created_by;
    }
  }
};
ImageTag.prototype = {};
ImageTag.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.fetch = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.table_source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.image_source = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.image_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.created_by = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ImageTag.prototype.write = function(output) {
  output.writeStructBegin('ImageTag');
  if (this.fetch !== null && this.fetch !== undefined) {
    output.writeFieldBegin('fetch', Thrift.Type.BOOL, 1);
    output.writeBool(this.fetch);
    output.writeFieldEnd();
  }
  if (this.table_source !== null && this.table_source !== undefined) {
    output.writeFieldBegin('table_source', Thrift.Type.STRING, 2);
    output.writeString(this.table_source);
    output.writeFieldEnd();
  }
  if (this.image_source !== null && this.image_source !== undefined) {
    output.writeFieldBegin('image_source', Thrift.Type.STRING, 3);
    output.writeString(this.image_source);
    output.writeFieldEnd();
  }
  if (this.image_id !== null && this.image_id !== undefined) {
    output.writeFieldBegin('image_id', Thrift.Type.STRING, 4);
    output.writeString(this.image_id);
    output.writeFieldEnd();
  }
  if (this.created_by !== null && this.created_by !== undefined) {
    output.writeFieldBegin('created_by', Thrift.Type.STRING, 5);
    output.writeString(this.created_by);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Condition = function(args) {
  this.startCta = null;
  this.endCta = null;
  this.type = null;
  if (args) {
    if (args.startCta !== undefined && args.startCta !== null) {
      this.startCta = args.startCta;
    }
    if (args.endCta !== undefined && args.endCta !== null) {
      this.endCta = args.endCta;
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    }
  }
};
Condition.prototype = {};
Condition.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.startCta = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.endCta = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.type = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Condition.prototype.write = function(output) {
  output.writeStructBegin('Condition');
  if (this.startCta !== null && this.startCta !== undefined) {
    output.writeFieldBegin('startCta', Thrift.Type.I32, 1);
    output.writeI32(this.startCta);
    output.writeFieldEnd();
  }
  if (this.endCta !== null && this.endCta !== undefined) {
    output.writeFieldBegin('endCta', Thrift.Type.I32, 2);
    output.writeI32(this.endCta);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.STRING, 3);
    output.writeString(this.type);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RewardRuleItem = function(args) {
  this.ruleItemId = null;
  this.condition = null;
  this.name = null;
  this.coins = null;
  this.status = null;
  this.ruleGroup = null;
  if (args) {
    if (args.ruleItemId !== undefined && args.ruleItemId !== null) {
      this.ruleItemId = args.ruleItemId;
    }
    if (args.condition !== undefined && args.condition !== null) {
      this.condition = Thrift.copyList(args.condition, [Condition]);
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.coins !== undefined && args.coins !== null) {
      this.coins = args.coins;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.ruleGroup !== undefined && args.ruleGroup !== null) {
      this.ruleGroup = args.ruleGroup;
    }
  }
};
RewardRuleItem.prototype = {};
RewardRuleItem.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.ruleItemId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1892 = 0;
        var _rtmp31896;
        this.condition = [];
        var _etype1895 = 0;
        _rtmp31896 = input.readListBegin();
        _etype1895 = _rtmp31896.etype;
        _size1892 = _rtmp31896.size;
        for (var _i1897 = 0; _i1897 < _size1892; ++_i1897)
        {
          var elem1898 = null;
          elem1898 = new Condition();
          elem1898.read(input);
          this.condition.push(elem1898);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.coins = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.ruleGroup = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RewardRuleItem.prototype.write = function(output) {
  output.writeStructBegin('RewardRuleItem');
  if (this.ruleItemId !== null && this.ruleItemId !== undefined) {
    output.writeFieldBegin('ruleItemId', Thrift.Type.STRING, 1);
    output.writeString(this.ruleItemId);
    output.writeFieldEnd();
  }
  if (this.condition !== null && this.condition !== undefined) {
    output.writeFieldBegin('condition', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.condition.length);
    for (var iter1899 in this.condition)
    {
      if (this.condition.hasOwnProperty(iter1899))
      {
        iter1899 = this.condition[iter1899];
        iter1899.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 3);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.coins !== null && this.coins !== undefined) {
    output.writeFieldBegin('coins', Thrift.Type.I32, 4);
    output.writeI32(this.coins);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 5);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  if (this.ruleGroup !== null && this.ruleGroup !== undefined) {
    output.writeFieldBegin('ruleGroup', Thrift.Type.STRING, 6);
    output.writeString(this.ruleGroup);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RewardRuleGroup = function(args) {
  this.ruleGroupId = null;
  this.name = null;
  this.description = null;
  this.type = null;
  this.status = null;
  this.eventName = null;
  this.startDate = null;
  this.endDate = null;
  this.ruleItems = null;
  if (args) {
    if (args.ruleGroupId !== undefined && args.ruleGroupId !== null) {
      this.ruleGroupId = args.ruleGroupId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.type !== undefined && args.type !== null) {
      this.type = args.type;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.eventName !== undefined && args.eventName !== null) {
      this.eventName = args.eventName;
    }
    if (args.startDate !== undefined && args.startDate !== null) {
      this.startDate = args.startDate;
    }
    if (args.endDate !== undefined && args.endDate !== null) {
      this.endDate = args.endDate;
    }
    if (args.ruleItems !== undefined && args.ruleItems !== null) {
      this.ruleItems = Thrift.copyList(args.ruleItems, [RewardRuleItem]);
    }
  }
};
RewardRuleGroup.prototype = {};
RewardRuleGroup.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.ruleGroupId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.type = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.eventName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.startDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.endDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size1900 = 0;
        var _rtmp31904;
        this.ruleItems = [];
        var _etype1903 = 0;
        _rtmp31904 = input.readListBegin();
        _etype1903 = _rtmp31904.etype;
        _size1900 = _rtmp31904.size;
        for (var _i1905 = 0; _i1905 < _size1900; ++_i1905)
        {
          var elem1906 = null;
          elem1906 = new RewardRuleItem();
          elem1906.read(input);
          this.ruleItems.push(elem1906);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RewardRuleGroup.prototype.write = function(output) {
  output.writeStructBegin('RewardRuleGroup');
  if (this.ruleGroupId !== null && this.ruleGroupId !== undefined) {
    output.writeFieldBegin('ruleGroupId', Thrift.Type.STRING, 1);
    output.writeString(this.ruleGroupId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 3);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.type !== null && this.type !== undefined) {
    output.writeFieldBegin('type', Thrift.Type.STRING, 4);
    output.writeString(this.type);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 5);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  if (this.eventName !== null && this.eventName !== undefined) {
    output.writeFieldBegin('eventName', Thrift.Type.STRING, 6);
    output.writeString(this.eventName);
    output.writeFieldEnd();
  }
  if (this.startDate !== null && this.startDate !== undefined) {
    output.writeFieldBegin('startDate', Thrift.Type.STRING, 7);
    output.writeString(this.startDate);
    output.writeFieldEnd();
  }
  if (this.endDate !== null && this.endDate !== undefined) {
    output.writeFieldBegin('endDate', Thrift.Type.STRING, 8);
    output.writeString(this.endDate);
    output.writeFieldEnd();
  }
  if (this.ruleItems !== null && this.ruleItems !== undefined) {
    output.writeFieldBegin('ruleItems', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRUCT, this.ruleItems.length);
    for (var iter1907 in this.ruleItems)
    {
      if (this.ruleItems.hasOwnProperty(iter1907))
      {
        iter1907 = this.ruleItems[iter1907];
        iter1907.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Quiz = function(args) {
  this.quizId = null;
  this.title = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.announcementAt = null;
  this.totalPrizeAmount = null;
  this.status = null;
  if (args) {
    if (args.quizId !== undefined && args.quizId !== null) {
      this.quizId = args.quizId;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.announcementAt !== undefined && args.announcementAt !== null) {
      this.announcementAt = args.announcementAt;
    }
    if (args.totalPrizeAmount !== undefined && args.totalPrizeAmount !== null) {
      this.totalPrizeAmount = args.totalPrizeAmount;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
  }
};
Quiz.prototype = {};
Quiz.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.quizId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.announcementAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.DOUBLE) {
        this.totalPrizeAmount = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Quiz.prototype.write = function(output) {
  output.writeStructBegin('Quiz');
  if (this.quizId !== null && this.quizId !== undefined) {
    output.writeFieldBegin('quizId', Thrift.Type.STRING, 1);
    output.writeString(this.quizId);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 2);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 3);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 4);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.announcementAt !== null && this.announcementAt !== undefined) {
    output.writeFieldBegin('announcementAt', Thrift.Type.STRING, 5);
    output.writeString(this.announcementAt);
    output.writeFieldEnd();
  }
  if (this.totalPrizeAmount !== null && this.totalPrizeAmount !== undefined) {
    output.writeFieldBegin('totalPrizeAmount', Thrift.Type.DOUBLE, 6);
    output.writeDouble(this.totalPrizeAmount);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 7);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RewardUserEvent = function(args) {
  this.userId = null;
  this.events = null;
  if (args) {
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    }
    if (args.events !== undefined && args.events !== null) {
      this.events = Thrift.copyMap(args.events, [null]);
    }
  }
};
RewardUserEvent.prototype = {};
RewardUserEvent.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.MAP) {
        var _size1908 = 0;
        var _rtmp31912;
        this.events = {};
        var _ktype1909 = 0;
        var _vtype1910 = 0;
        _rtmp31912 = input.readMapBegin();
        _ktype1909 = _rtmp31912.ktype;
        _vtype1910 = _rtmp31912.vtype;
        _size1908 = _rtmp31912.size;
        for (var _i1913 = 0; _i1913 < _size1908; ++_i1913)
        {
          if (_i1913 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key1914 = null;
          var val1915 = null;
          key1914 = input.readString().value;
          val1915 = input.readI32().value;
          this.events[key1914] = val1915;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RewardUserEvent.prototype.write = function(output) {
  output.writeStructBegin('RewardUserEvent');
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 1);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.events !== null && this.events !== undefined) {
    output.writeFieldBegin('events', Thrift.Type.MAP, 2);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.I32, Thrift.objectLength(this.events));
    for (var kiter1916 in this.events)
    {
      if (this.events.hasOwnProperty(kiter1916))
      {
        var viter1917 = this.events[kiter1916];
        output.writeString(kiter1916);
        output.writeI32(viter1917);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CardsByBubbleResponse = function(args) {
  this.primary_card_id = null;
  if (args) {
    if (args.primary_card_id !== undefined && args.primary_card_id !== null) {
      this.primary_card_id = args.primary_card_id;
    }
  }
};
CardsByBubbleResponse.prototype = {};
CardsByBubbleResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.primary_card_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CardsByBubbleResponse.prototype.write = function(output) {
  output.writeStructBegin('CardsByBubbleResponse');
  if (this.primary_card_id !== null && this.primary_card_id !== undefined) {
    output.writeFieldBegin('primary_card_id', Thrift.Type.STRING, 1);
    output.writeString(this.primary_card_id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PublishingQueue = function(args) {
  this.cardId = null;
  this.bubbleQualifier = null;
  this.publishToUser = null;
  if (args) {
    if (args.cardId !== undefined && args.cardId !== null) {
      this.cardId = args.cardId;
    }
    if (args.bubbleQualifier !== undefined && args.bubbleQualifier !== null) {
      this.bubbleQualifier = args.bubbleQualifier;
    }
    if (args.publishToUser !== undefined && args.publishToUser !== null) {
      this.publishToUser = args.publishToUser;
    }
  }
};
PublishingQueue.prototype = {};
PublishingQueue.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.cardId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.bubbleQualifier = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.publishToUser = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PublishingQueue.prototype.write = function(output) {
  output.writeStructBegin('PublishingQueue');
  if (this.cardId !== null && this.cardId !== undefined) {
    output.writeFieldBegin('cardId', Thrift.Type.STRING, 1);
    output.writeString(this.cardId);
    output.writeFieldEnd();
  }
  if (this.bubbleQualifier !== null && this.bubbleQualifier !== undefined) {
    output.writeFieldBegin('bubbleQualifier', Thrift.Type.STRING, 2);
    output.writeString(this.bubbleQualifier);
    output.writeFieldEnd();
  }
  if (this.publishToUser !== null && this.publishToUser !== undefined) {
    output.writeFieldBegin('publishToUser', Thrift.Type.BOOL, 3);
    output.writeBool(this.publishToUser);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CardBubble = function(args) {
  this.cardId = null;
  this.bubbleId = null;
  this.createdBy = null;
  if (args) {
    if (args.cardId !== undefined && args.cardId !== null) {
      this.cardId = args.cardId;
    }
    if (args.bubbleId !== undefined && args.bubbleId !== null) {
      this.bubbleId = args.bubbleId;
    }
    if (args.createdBy !== undefined && args.createdBy !== null) {
      this.createdBy = args.createdBy;
    }
  }
};
CardBubble.prototype = {};
CardBubble.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.cardId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.bubbleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.createdBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CardBubble.prototype.write = function(output) {
  output.writeStructBegin('CardBubble');
  if (this.cardId !== null && this.cardId !== undefined) {
    output.writeFieldBegin('cardId', Thrift.Type.STRING, 1);
    output.writeString(this.cardId);
    output.writeFieldEnd();
  }
  if (this.bubbleId !== null && this.bubbleId !== undefined) {
    output.writeFieldBegin('bubbleId', Thrift.Type.STRING, 2);
    output.writeString(this.bubbleId);
    output.writeFieldEnd();
  }
  if (this.createdBy !== null && this.createdBy !== undefined) {
    output.writeFieldBegin('createdBy', Thrift.Type.STRING, 3);
    output.writeString(this.createdBy);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Sweepstake = function(args) {
  this.sweepStakeId = null;
  this.name = null;
  this.headline = null;
  this.winningPrize = null;
  this.winningUnit = null;
  this.isWinningMonetaryType = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.startDate = null;
  this.endDate = null;
  this.announcementDate = null;
  this.ticketPrice = null;
  this.maxWinners = null;
  this.status = null;
  this.imagePath = null;
  this.tagName = null;
  this.description = null;
  this.termsNCondition = null;
  if (args) {
    if (args.sweepStakeId !== undefined && args.sweepStakeId !== null) {
      this.sweepStakeId = args.sweepStakeId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.headline !== undefined && args.headline !== null) {
      this.headline = args.headline;
    }
    if (args.winningPrize !== undefined && args.winningPrize !== null) {
      this.winningPrize = args.winningPrize;
    }
    if (args.winningUnit !== undefined && args.winningUnit !== null) {
      this.winningUnit = args.winningUnit;
    }
    if (args.isWinningMonetaryType !== undefined && args.isWinningMonetaryType !== null) {
      this.isWinningMonetaryType = args.isWinningMonetaryType;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.startDate !== undefined && args.startDate !== null) {
      this.startDate = args.startDate;
    }
    if (args.endDate !== undefined && args.endDate !== null) {
      this.endDate = args.endDate;
    }
    if (args.announcementDate !== undefined && args.announcementDate !== null) {
      this.announcementDate = args.announcementDate;
    }
    if (args.ticketPrice !== undefined && args.ticketPrice !== null) {
      this.ticketPrice = args.ticketPrice;
    }
    if (args.maxWinners !== undefined && args.maxWinners !== null) {
      this.maxWinners = args.maxWinners;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.imagePath !== undefined && args.imagePath !== null) {
      this.imagePath = args.imagePath;
    }
    if (args.tagName !== undefined && args.tagName !== null) {
      this.tagName = args.tagName;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.termsNCondition !== undefined && args.termsNCondition !== null) {
      this.termsNCondition = args.termsNCondition;
    }
  }
};
Sweepstake.prototype = {};
Sweepstake.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.sweepStakeId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.headline = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.winningPrize = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.DOUBLE) {
        this.winningUnit = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.isWinningMonetaryType = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.startDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.endDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.announcementDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I32) {
        this.ticketPrice = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.I32) {
        this.maxWinners = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.imagePath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.I32) {
        this.tagName = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.termsNCondition = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Sweepstake.prototype.write = function(output) {
  output.writeStructBegin('Sweepstake');
  if (this.sweepStakeId !== null && this.sweepStakeId !== undefined) {
    output.writeFieldBegin('sweepStakeId', Thrift.Type.STRING, 1);
    output.writeString(this.sweepStakeId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.headline !== null && this.headline !== undefined) {
    output.writeFieldBegin('headline', Thrift.Type.STRING, 3);
    output.writeString(this.headline);
    output.writeFieldEnd();
  }
  if (this.winningPrize !== null && this.winningPrize !== undefined) {
    output.writeFieldBegin('winningPrize', Thrift.Type.STRING, 4);
    output.writeString(this.winningPrize);
    output.writeFieldEnd();
  }
  if (this.winningUnit !== null && this.winningUnit !== undefined) {
    output.writeFieldBegin('winningUnit', Thrift.Type.DOUBLE, 5);
    output.writeDouble(this.winningUnit);
    output.writeFieldEnd();
  }
  if (this.isWinningMonetaryType !== null && this.isWinningMonetaryType !== undefined) {
    output.writeFieldBegin('isWinningMonetaryType', Thrift.Type.BOOL, 6);
    output.writeBool(this.isWinningMonetaryType);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 7);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 8);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.startDate !== null && this.startDate !== undefined) {
    output.writeFieldBegin('startDate', Thrift.Type.STRING, 9);
    output.writeString(this.startDate);
    output.writeFieldEnd();
  }
  if (this.endDate !== null && this.endDate !== undefined) {
    output.writeFieldBegin('endDate', Thrift.Type.STRING, 10);
    output.writeString(this.endDate);
    output.writeFieldEnd();
  }
  if (this.announcementDate !== null && this.announcementDate !== undefined) {
    output.writeFieldBegin('announcementDate', Thrift.Type.STRING, 11);
    output.writeString(this.announcementDate);
    output.writeFieldEnd();
  }
  if (this.ticketPrice !== null && this.ticketPrice !== undefined) {
    output.writeFieldBegin('ticketPrice', Thrift.Type.I32, 12);
    output.writeI32(this.ticketPrice);
    output.writeFieldEnd();
  }
  if (this.maxWinners !== null && this.maxWinners !== undefined) {
    output.writeFieldBegin('maxWinners', Thrift.Type.I32, 13);
    output.writeI32(this.maxWinners);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 14);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  if (this.imagePath !== null && this.imagePath !== undefined) {
    output.writeFieldBegin('imagePath', Thrift.Type.STRING, 15);
    output.writeString(this.imagePath);
    output.writeFieldEnd();
  }
  if (this.tagName !== null && this.tagName !== undefined) {
    output.writeFieldBegin('tagName', Thrift.Type.I32, 16);
    output.writeI32(this.tagName);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 17);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.termsNCondition !== null && this.termsNCondition !== undefined) {
    output.writeFieldBegin('termsNCondition', Thrift.Type.STRING, 18);
    output.writeString(this.termsNCondition);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DeviceAttestationData = function(args) {
  this.nonce = null;
  this.createdAtInSecs = null;
  this.isAttested = null;
  if (args) {
    if (args.nonce !== undefined && args.nonce !== null) {
      this.nonce = args.nonce;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.isAttested !== undefined && args.isAttested !== null) {
      this.isAttested = args.isAttested;
    }
  }
};
DeviceAttestationData.prototype = {};
DeviceAttestationData.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.nonce = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.isAttested = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DeviceAttestationData.prototype.write = function(output) {
  output.writeStructBegin('DeviceAttestationData');
  if (this.nonce !== null && this.nonce !== undefined) {
    output.writeFieldBegin('nonce', Thrift.Type.STRING, 1);
    output.writeString(this.nonce);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.isAttested !== null && this.isAttested !== undefined) {
    output.writeFieldBegin('isAttested', Thrift.Type.BOOL, 3);
    output.writeBool(this.isAttested);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppOwnershipCampaignGlanceCreatedMeta = function(args) {
  this.glanceId = null;
  this.userSet = null;
  this.appSet = null;
  this.scheduledTime = null;
  if (args) {
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    }
    if (args.userSet !== undefined && args.userSet !== null) {
      this.userSet = args.userSet;
    }
    if (args.appSet !== undefined && args.appSet !== null) {
      this.appSet = args.appSet;
    }
    if (args.scheduledTime !== undefined && args.scheduledTime !== null) {
      this.scheduledTime = args.scheduledTime;
    }
  }
};
AppOwnershipCampaignGlanceCreatedMeta.prototype = {};
AppOwnershipCampaignGlanceCreatedMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.userSet = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.appSet = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.scheduledTime = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppOwnershipCampaignGlanceCreatedMeta.prototype.write = function(output) {
  output.writeStructBegin('AppOwnershipCampaignGlanceCreatedMeta');
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.userSet !== null && this.userSet !== undefined) {
    output.writeFieldBegin('userSet', Thrift.Type.I64, 2);
    output.writeI64(this.userSet);
    output.writeFieldEnd();
  }
  if (this.appSet !== null && this.appSet !== undefined) {
    output.writeFieldBegin('appSet', Thrift.Type.I64, 3);
    output.writeI64(this.appSet);
    output.writeFieldEnd();
  }
  if (this.scheduledTime !== null && this.scheduledTime !== undefined) {
    output.writeFieldBegin('scheduledTime', Thrift.Type.I64, 4);
    output.writeI64(this.scheduledTime);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppOwnershipCampaign = function(args) {
  this.campaignId = null;
  this.runningDate = null;
  this.glancesCreatedMeta = null;
  this.startTimeInSecs = null;
  this.propogateToDS = null;
  this.region = null;
  this.partnerId = null;
  if (args) {
    if (args.campaignId !== undefined && args.campaignId !== null) {
      this.campaignId = args.campaignId;
    }
    if (args.runningDate !== undefined && args.runningDate !== null) {
      this.runningDate = args.runningDate;
    }
    if (args.glancesCreatedMeta !== undefined && args.glancesCreatedMeta !== null) {
      this.glancesCreatedMeta = Thrift.copyList(args.glancesCreatedMeta, [AppOwnershipCampaignGlanceCreatedMeta]);
    }
    if (args.startTimeInSecs !== undefined && args.startTimeInSecs !== null) {
      this.startTimeInSecs = args.startTimeInSecs;
    }
    if (args.propogateToDS !== undefined && args.propogateToDS !== null) {
      this.propogateToDS = args.propogateToDS;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    }
  }
};
AppOwnershipCampaign.prototype = {};
AppOwnershipCampaign.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.campaignId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.runningDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size1918 = 0;
        var _rtmp31922;
        this.glancesCreatedMeta = [];
        var _etype1921 = 0;
        _rtmp31922 = input.readListBegin();
        _etype1921 = _rtmp31922.etype;
        _size1918 = _rtmp31922.size;
        for (var _i1923 = 0; _i1923 < _size1918; ++_i1923)
        {
          var elem1924 = null;
          elem1924 = new AppOwnershipCampaignGlanceCreatedMeta();
          elem1924.read(input);
          this.glancesCreatedMeta.push(elem1924);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.startTimeInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.propogateToDS = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.region = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppOwnershipCampaign.prototype.write = function(output) {
  output.writeStructBegin('AppOwnershipCampaign');
  if (this.campaignId !== null && this.campaignId !== undefined) {
    output.writeFieldBegin('campaignId', Thrift.Type.STRING, 1);
    output.writeString(this.campaignId);
    output.writeFieldEnd();
  }
  if (this.runningDate !== null && this.runningDate !== undefined) {
    output.writeFieldBegin('runningDate', Thrift.Type.STRING, 2);
    output.writeString(this.runningDate);
    output.writeFieldEnd();
  }
  if (this.glancesCreatedMeta !== null && this.glancesCreatedMeta !== undefined) {
    output.writeFieldBegin('glancesCreatedMeta', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.glancesCreatedMeta.length);
    for (var iter1925 in this.glancesCreatedMeta)
    {
      if (this.glancesCreatedMeta.hasOwnProperty(iter1925))
      {
        iter1925 = this.glancesCreatedMeta[iter1925];
        iter1925.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.startTimeInSecs !== null && this.startTimeInSecs !== undefined) {
    output.writeFieldBegin('startTimeInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.startTimeInSecs);
    output.writeFieldEnd();
  }
  if (this.propogateToDS !== null && this.propogateToDS !== undefined) {
    output.writeFieldBegin('propogateToDS', Thrift.Type.BOOL, 5);
    output.writeBool(this.propogateToDS);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.STRING, 6);
    output.writeString(this.region);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 7);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

NotificationTemplate = function(args) {
  this.notifTemplateId = null;
  this.title = null;
  this.body = null;
  this.image = null;
  this.deeplink = null;
  this.action = null;
  this.alert = null;
  this.sticky = null;
  this.ctaText = null;
  this.ctaUrl = null;
  this.appId = null;
  this.notifQualifier = null;
  this.segmentId = null;
  this.apiKey = null;
  this.scheduledTimeMs = null;
  if (args) {
    if (args.notifTemplateId !== undefined && args.notifTemplateId !== null) {
      this.notifTemplateId = args.notifTemplateId;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.body !== undefined && args.body !== null) {
      this.body = args.body;
    }
    if (args.image !== undefined && args.image !== null) {
      this.image = args.image;
    }
    if (args.deeplink !== undefined && args.deeplink !== null) {
      this.deeplink = args.deeplink;
    }
    if (args.action !== undefined && args.action !== null) {
      this.action = args.action;
    }
    if (args.alert !== undefined && args.alert !== null) {
      this.alert = args.alert;
    }
    if (args.sticky !== undefined && args.sticky !== null) {
      this.sticky = args.sticky;
    }
    if (args.ctaText !== undefined && args.ctaText !== null) {
      this.ctaText = args.ctaText;
    }
    if (args.ctaUrl !== undefined && args.ctaUrl !== null) {
      this.ctaUrl = args.ctaUrl;
    }
    if (args.appId !== undefined && args.appId !== null) {
      this.appId = args.appId;
    }
    if (args.notifQualifier !== undefined && args.notifQualifier !== null) {
      this.notifQualifier = args.notifQualifier;
    }
    if (args.segmentId !== undefined && args.segmentId !== null) {
      this.segmentId = args.segmentId;
    }
    if (args.apiKey !== undefined && args.apiKey !== null) {
      this.apiKey = args.apiKey;
    }
    if (args.scheduledTimeMs !== undefined && args.scheduledTimeMs !== null) {
      this.scheduledTimeMs = args.scheduledTimeMs;
    }
  }
};
NotificationTemplate.prototype = {};
NotificationTemplate.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.notifTemplateId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.body = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.image = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.deeplink = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.action = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.alert = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.sticky = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.ctaText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.ctaUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.appId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.notifQualifier = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.segmentId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.apiKey = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.I64) {
        this.scheduledTimeMs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

NotificationTemplate.prototype.write = function(output) {
  output.writeStructBegin('NotificationTemplate');
  if (this.notifTemplateId !== null && this.notifTemplateId !== undefined) {
    output.writeFieldBegin('notifTemplateId', Thrift.Type.STRING, 1);
    output.writeString(this.notifTemplateId);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 2);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.body !== null && this.body !== undefined) {
    output.writeFieldBegin('body', Thrift.Type.STRING, 3);
    output.writeString(this.body);
    output.writeFieldEnd();
  }
  if (this.image !== null && this.image !== undefined) {
    output.writeFieldBegin('image', Thrift.Type.STRING, 4);
    output.writeString(this.image);
    output.writeFieldEnd();
  }
  if (this.deeplink !== null && this.deeplink !== undefined) {
    output.writeFieldBegin('deeplink', Thrift.Type.STRING, 5);
    output.writeString(this.deeplink);
    output.writeFieldEnd();
  }
  if (this.action !== null && this.action !== undefined) {
    output.writeFieldBegin('action', Thrift.Type.STRING, 6);
    output.writeString(this.action);
    output.writeFieldEnd();
  }
  if (this.alert !== null && this.alert !== undefined) {
    output.writeFieldBegin('alert', Thrift.Type.BOOL, 7);
    output.writeBool(this.alert);
    output.writeFieldEnd();
  }
  if (this.sticky !== null && this.sticky !== undefined) {
    output.writeFieldBegin('sticky', Thrift.Type.BOOL, 8);
    output.writeBool(this.sticky);
    output.writeFieldEnd();
  }
  if (this.ctaText !== null && this.ctaText !== undefined) {
    output.writeFieldBegin('ctaText', Thrift.Type.STRING, 9);
    output.writeString(this.ctaText);
    output.writeFieldEnd();
  }
  if (this.ctaUrl !== null && this.ctaUrl !== undefined) {
    output.writeFieldBegin('ctaUrl', Thrift.Type.STRING, 10);
    output.writeString(this.ctaUrl);
    output.writeFieldEnd();
  }
  if (this.appId !== null && this.appId !== undefined) {
    output.writeFieldBegin('appId', Thrift.Type.STRING, 11);
    output.writeString(this.appId);
    output.writeFieldEnd();
  }
  if (this.notifQualifier !== null && this.notifQualifier !== undefined) {
    output.writeFieldBegin('notifQualifier', Thrift.Type.STRING, 12);
    output.writeString(this.notifQualifier);
    output.writeFieldEnd();
  }
  if (this.segmentId !== null && this.segmentId !== undefined) {
    output.writeFieldBegin('segmentId', Thrift.Type.STRING, 13);
    output.writeString(this.segmentId);
    output.writeFieldEnd();
  }
  if (this.apiKey !== null && this.apiKey !== undefined) {
    output.writeFieldBegin('apiKey', Thrift.Type.STRING, 14);
    output.writeString(this.apiKey);
    output.writeFieldEnd();
  }
  if (this.scheduledTimeMs !== null && this.scheduledTimeMs !== undefined) {
    output.writeFieldBegin('scheduledTimeMs', Thrift.Type.I64, 15);
    output.writeI64(this.scheduledTimeMs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DSGenerateCard = function(args) {
  this.overlay_spec = null;
  this.image_id = null;
  this.card_id = null;
  if (args) {
    if (args.overlay_spec !== undefined && args.overlay_spec !== null) {
      this.overlay_spec = args.overlay_spec;
    }
    if (args.image_id !== undefined && args.image_id !== null) {
      this.image_id = args.image_id;
    }
    if (args.card_id !== undefined && args.card_id !== null) {
      this.card_id = args.card_id;
    }
  }
};
DSGenerateCard.prototype = {};
DSGenerateCard.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.overlay_spec = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.image_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.card_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DSGenerateCard.prototype.write = function(output) {
  output.writeStructBegin('DSGenerateCard');
  if (this.overlay_spec !== null && this.overlay_spec !== undefined) {
    output.writeFieldBegin('overlay_spec', Thrift.Type.STRING, 1);
    output.writeString(this.overlay_spec);
    output.writeFieldEnd();
  }
  if (this.image_id !== null && this.image_id !== undefined) {
    output.writeFieldBegin('image_id', Thrift.Type.STRING, 2);
    output.writeString(this.image_id);
    output.writeFieldEnd();
  }
  if (this.card_id !== null && this.card_id !== undefined) {
    output.writeFieldBegin('card_id', Thrift.Type.STRING, 3);
    output.writeString(this.card_id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

BingeCardAssets = function(args) {
  this.finalImageUrl = null;
  this.overlayImageUrl = null;
  this.templateName = null;
  if (args) {
    if (args.finalImageUrl !== undefined && args.finalImageUrl !== null) {
      this.finalImageUrl = args.finalImageUrl;
    }
    if (args.overlayImageUrl !== undefined && args.overlayImageUrl !== null) {
      this.overlayImageUrl = args.overlayImageUrl;
    }
    if (args.templateName !== undefined && args.templateName !== null) {
      this.templateName = args.templateName;
    }
  }
};
BingeCardAssets.prototype = {};
BingeCardAssets.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.finalImageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.overlayImageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.templateName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

BingeCardAssets.prototype.write = function(output) {
  output.writeStructBegin('BingeCardAssets');
  if (this.finalImageUrl !== null && this.finalImageUrl !== undefined) {
    output.writeFieldBegin('finalImageUrl', Thrift.Type.STRING, 1);
    output.writeString(this.finalImageUrl);
    output.writeFieldEnd();
  }
  if (this.overlayImageUrl !== null && this.overlayImageUrl !== undefined) {
    output.writeFieldBegin('overlayImageUrl', Thrift.Type.STRING, 2);
    output.writeString(this.overlayImageUrl);
    output.writeFieldEnd();
  }
  if (this.templateName !== null && this.templateName !== undefined) {
    output.writeFieldBegin('templateName', Thrift.Type.STRING, 3);
    output.writeString(this.templateName);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DSGenerateResponse = function(args) {
  this.bingeCardAssets = null;
  if (args) {
    if (args.bingeCardAssets !== undefined && args.bingeCardAssets !== null) {
      this.bingeCardAssets = Thrift.copyList(args.bingeCardAssets, [BingeCardAssets]);
    }
  }
};
DSGenerateResponse.prototype = {};
DSGenerateResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size1926 = 0;
        var _rtmp31930;
        this.bingeCardAssets = [];
        var _etype1929 = 0;
        _rtmp31930 = input.readListBegin();
        _etype1929 = _rtmp31930.etype;
        _size1926 = _rtmp31930.size;
        for (var _i1931 = 0; _i1931 < _size1926; ++_i1931)
        {
          var elem1932 = null;
          elem1932 = new BingeCardAssets();
          elem1932.read(input);
          this.bingeCardAssets.push(elem1932);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DSGenerateResponse.prototype.write = function(output) {
  output.writeStructBegin('DSGenerateResponse');
  if (this.bingeCardAssets !== null && this.bingeCardAssets !== undefined) {
    output.writeFieldBegin('bingeCardAssets', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.bingeCardAssets.length);
    for (var iter1933 in this.bingeCardAssets)
    {
      if (this.bingeCardAssets.hasOwnProperty(iter1933))
      {
        iter1933 = this.bingeCardAssets[iter1933];
        iter1933.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

SourceCreatorMeta = function(args) {
  this.id = null;
  this.name = null;
  this.handle = null;
  this.profilePicUrl = null;
  this.followCount = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.verified = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.handle !== undefined && args.handle !== null) {
      this.handle = args.handle;
    }
    if (args.profilePicUrl !== undefined && args.profilePicUrl !== null) {
      this.profilePicUrl = args.profilePicUrl;
    }
    if (args.followCount !== undefined && args.followCount !== null) {
      this.followCount = args.followCount;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.verified !== undefined && args.verified !== null) {
      this.verified = args.verified;
    }
  }
};
SourceCreatorMeta.prototype = {};
SourceCreatorMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.handle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.profilePicUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.followCount = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.verified = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

SourceCreatorMeta.prototype.write = function(output) {
  output.writeStructBegin('SourceCreatorMeta');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.handle !== null && this.handle !== undefined) {
    output.writeFieldBegin('handle', Thrift.Type.STRING, 3);
    output.writeString(this.handle);
    output.writeFieldEnd();
  }
  if (this.profilePicUrl !== null && this.profilePicUrl !== undefined) {
    output.writeFieldBegin('profilePicUrl', Thrift.Type.STRING, 4);
    output.writeString(this.profilePicUrl);
    output.writeFieldEnd();
  }
  if (this.followCount !== null && this.followCount !== undefined) {
    output.writeFieldBegin('followCount', Thrift.Type.I64, 5);
    output.writeI64(this.followCount);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 6);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 7);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.verified !== null && this.verified !== undefined) {
    output.writeFieldBegin('verified', Thrift.Type.BOOL, 8);
    output.writeBool(this.verified);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Offer = function(args) {
  this.offerId = null;
  this.name = null;
  this.category = null;
  this.status = null;
  this.image = null;
  this.logoUrl = null;
  this.brandUrl = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.termsNCondition = null;
  this.expiredAt = null;
  this.offerType = null;
  this.promoCode = null;
  this.triggerId = null;
  if (args) {
    if (args.offerId !== undefined && args.offerId !== null) {
      this.offerId = args.offerId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.image !== undefined && args.image !== null) {
      this.image = args.image;
    }
    if (args.logoUrl !== undefined && args.logoUrl !== null) {
      this.logoUrl = args.logoUrl;
    }
    if (args.brandUrl !== undefined && args.brandUrl !== null) {
      this.brandUrl = args.brandUrl;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.termsNCondition !== undefined && args.termsNCondition !== null) {
      this.termsNCondition = args.termsNCondition;
    }
    if (args.expiredAt !== undefined && args.expiredAt !== null) {
      this.expiredAt = args.expiredAt;
    }
    if (args.offerType !== undefined && args.offerType !== null) {
      this.offerType = args.offerType;
    }
    if (args.promoCode !== undefined && args.promoCode !== null) {
      this.promoCode = args.promoCode;
    }
    if (args.triggerId !== undefined && args.triggerId !== null) {
      this.triggerId = args.triggerId;
    }
  }
};
Offer.prototype = {};
Offer.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.offerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.image = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.logoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.brandUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.termsNCondition = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.expiredAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.offerType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.promoCode = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.triggerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Offer.prototype.write = function(output) {
  output.writeStructBegin('Offer');
  if (this.offerId !== null && this.offerId !== undefined) {
    output.writeFieldBegin('offerId', Thrift.Type.STRING, 1);
    output.writeString(this.offerId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 3);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 4);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  if (this.image !== null && this.image !== undefined) {
    output.writeFieldBegin('image', Thrift.Type.STRING, 5);
    output.writeString(this.image);
    output.writeFieldEnd();
  }
  if (this.logoUrl !== null && this.logoUrl !== undefined) {
    output.writeFieldBegin('logoUrl', Thrift.Type.STRING, 6);
    output.writeString(this.logoUrl);
    output.writeFieldEnd();
  }
  if (this.brandUrl !== null && this.brandUrl !== undefined) {
    output.writeFieldBegin('brandUrl', Thrift.Type.STRING, 7);
    output.writeString(this.brandUrl);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 8);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 9);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.termsNCondition !== null && this.termsNCondition !== undefined) {
    output.writeFieldBegin('termsNCondition', Thrift.Type.STRING, 10);
    output.writeString(this.termsNCondition);
    output.writeFieldEnd();
  }
  if (this.expiredAt !== null && this.expiredAt !== undefined) {
    output.writeFieldBegin('expiredAt', Thrift.Type.STRING, 11);
    output.writeString(this.expiredAt);
    output.writeFieldEnd();
  }
  if (this.offerType !== null && this.offerType !== undefined) {
    output.writeFieldBegin('offerType', Thrift.Type.STRING, 12);
    output.writeString(this.offerType);
    output.writeFieldEnd();
  }
  if (this.promoCode !== null && this.promoCode !== undefined) {
    output.writeFieldBegin('promoCode', Thrift.Type.STRING, 13);
    output.writeString(this.promoCode);
    output.writeFieldEnd();
  }
  if (this.triggerId !== null && this.triggerId !== undefined) {
    output.writeFieldBegin('triggerId', Thrift.Type.STRING, 14);
    output.writeString(this.triggerId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

VoucherMeta = function(args) {
  this.category = null;
  if (args) {
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
  }
};
VoucherMeta.prototype = {};
VoucherMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

VoucherMeta.prototype.write = function(output) {
  output.writeStructBegin('VoucherMeta');
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 1);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PitaraMeta = function(args) {
  this.streakDay = null;
  if (args) {
    if (args.streakDay !== undefined && args.streakDay !== null) {
      this.streakDay = args.streakDay;
    }
  }
};
PitaraMeta.prototype = {};
PitaraMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.streakDay = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PitaraMeta.prototype.write = function(output) {
  output.writeStructBegin('PitaraMeta');
  if (this.streakDay !== null && this.streakDay !== undefined) {
    output.writeFieldBegin('streakDay', Thrift.Type.STRING, 1);
    output.writeString(this.streakDay);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RuleMeta = function(args) {
  this.voucher = null;
  this.pitara = null;
  if (args) {
    if (args.voucher !== undefined && args.voucher !== null) {
      this.voucher = new VoucherMeta(args.voucher);
    }
    if (args.pitara !== undefined && args.pitara !== null) {
      this.pitara = new PitaraMeta(args.pitara);
    }
  }
};
RuleMeta.prototype = {};
RuleMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.voucher = new VoucherMeta();
        this.voucher.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.pitara = new PitaraMeta();
        this.pitara.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RuleMeta.prototype.write = function(output) {
  output.writeStructBegin('RuleMeta');
  if (this.voucher !== null && this.voucher !== undefined) {
    output.writeFieldBegin('voucher', Thrift.Type.STRUCT, 1);
    this.voucher.write(output);
    output.writeFieldEnd();
  }
  if (this.pitara !== null && this.pitara !== undefined) {
    output.writeFieldBegin('pitara', Thrift.Type.STRUCT, 2);
    this.pitara.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Rule = function(args) {
  this.ruleId = null;
  this.triggerId = null;
  this.triggerDetails = null;
  this.segmentId = null;
  this.experimentId = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.variantId = null;
  this.triggerType = null;
  this.rewardAsset = null;
  this.rewardType = null;
  this.startValue = null;
  this.endValue = null;
  this.ruleMeta = null;
  this.status = null;
  if (args) {
    if (args.ruleId !== undefined && args.ruleId !== null) {
      this.ruleId = args.ruleId;
    }
    if (args.triggerId !== undefined && args.triggerId !== null) {
      this.triggerId = args.triggerId;
    }
    if (args.triggerDetails !== undefined && args.triggerDetails !== null) {
      this.triggerDetails = args.triggerDetails;
    }
    if (args.segmentId !== undefined && args.segmentId !== null) {
      this.segmentId = args.segmentId;
    }
    if (args.experimentId !== undefined && args.experimentId !== null) {
      this.experimentId = args.experimentId;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.variantId !== undefined && args.variantId !== null) {
      this.variantId = args.variantId;
    }
    if (args.triggerType !== undefined && args.triggerType !== null) {
      this.triggerType = args.triggerType;
    }
    if (args.rewardAsset !== undefined && args.rewardAsset !== null) {
      this.rewardAsset = args.rewardAsset;
    }
    if (args.rewardType !== undefined && args.rewardType !== null) {
      this.rewardType = args.rewardType;
    }
    if (args.startValue !== undefined && args.startValue !== null) {
      this.startValue = args.startValue;
    }
    if (args.endValue !== undefined && args.endValue !== null) {
      this.endValue = args.endValue;
    }
    if (args.ruleMeta !== undefined && args.ruleMeta !== null) {
      this.ruleMeta = new RuleMeta(args.ruleMeta);
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
  }
};
Rule.prototype = {};
Rule.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.ruleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.triggerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.triggerDetails = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.segmentId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.experimentId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.variantId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.triggerType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.rewardAsset = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.rewardType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.DOUBLE) {
        this.startValue = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.DOUBLE) {
        this.endValue = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRUCT) {
        this.ruleMeta = new RuleMeta();
        this.ruleMeta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Rule.prototype.write = function(output) {
  output.writeStructBegin('Rule');
  if (this.ruleId !== null && this.ruleId !== undefined) {
    output.writeFieldBegin('ruleId', Thrift.Type.STRING, 1);
    output.writeString(this.ruleId);
    output.writeFieldEnd();
  }
  if (this.triggerId !== null && this.triggerId !== undefined) {
    output.writeFieldBegin('triggerId', Thrift.Type.STRING, 2);
    output.writeString(this.triggerId);
    output.writeFieldEnd();
  }
  if (this.triggerDetails !== null && this.triggerDetails !== undefined) {
    output.writeFieldBegin('triggerDetails', Thrift.Type.STRING, 3);
    output.writeString(this.triggerDetails);
    output.writeFieldEnd();
  }
  if (this.segmentId !== null && this.segmentId !== undefined) {
    output.writeFieldBegin('segmentId', Thrift.Type.STRING, 4);
    output.writeString(this.segmentId);
    output.writeFieldEnd();
  }
  if (this.experimentId !== null && this.experimentId !== undefined) {
    output.writeFieldBegin('experimentId', Thrift.Type.STRING, 5);
    output.writeString(this.experimentId);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 6);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 7);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.variantId !== null && this.variantId !== undefined) {
    output.writeFieldBegin('variantId', Thrift.Type.STRING, 8);
    output.writeString(this.variantId);
    output.writeFieldEnd();
  }
  if (this.triggerType !== null && this.triggerType !== undefined) {
    output.writeFieldBegin('triggerType', Thrift.Type.STRING, 9);
    output.writeString(this.triggerType);
    output.writeFieldEnd();
  }
  if (this.rewardAsset !== null && this.rewardAsset !== undefined) {
    output.writeFieldBegin('rewardAsset', Thrift.Type.STRING, 10);
    output.writeString(this.rewardAsset);
    output.writeFieldEnd();
  }
  if (this.rewardType !== null && this.rewardType !== undefined) {
    output.writeFieldBegin('rewardType', Thrift.Type.STRING, 11);
    output.writeString(this.rewardType);
    output.writeFieldEnd();
  }
  if (this.startValue !== null && this.startValue !== undefined) {
    output.writeFieldBegin('startValue', Thrift.Type.DOUBLE, 12);
    output.writeDouble(this.startValue);
    output.writeFieldEnd();
  }
  if (this.endValue !== null && this.endValue !== undefined) {
    output.writeFieldBegin('endValue', Thrift.Type.DOUBLE, 13);
    output.writeDouble(this.endValue);
    output.writeFieldEnd();
  }
  if (this.ruleMeta !== null && this.ruleMeta !== undefined) {
    output.writeFieldBegin('ruleMeta', Thrift.Type.STRUCT, 14);
    this.ruleMeta.write(output);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 15);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

JavelinSessionToken = function(args) {
  this.createdAtInSecs = null;
  this.users = null;
  if (args) {
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.users !== undefined && args.users !== null) {
      this.users = Thrift.copyList(args.users, [JavelinUser]);
    }
  }
};
JavelinSessionToken.prototype = {};
JavelinSessionToken.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1934 = 0;
        var _rtmp31938;
        this.users = [];
        var _etype1937 = 0;
        _rtmp31938 = input.readListBegin();
        _etype1937 = _rtmp31938.etype;
        _size1934 = _rtmp31938.size;
        for (var _i1939 = 0; _i1939 < _size1934; ++_i1939)
        {
          var elem1940 = null;
          elem1940 = new JavelinUser();
          elem1940.read(input);
          this.users.push(elem1940);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

JavelinSessionToken.prototype.write = function(output) {
  output.writeStructBegin('JavelinSessionToken');
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 1);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.users !== null && this.users !== undefined) {
    output.writeFieldBegin('users', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.users.length);
    for (var iter1941 in this.users)
    {
      if (this.users.hasOwnProperty(iter1941))
      {
        iter1941 = this.users[iter1941];
        iter1941.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftInfo = function(args) {
  this.draftId = null;
  this.sequence = null;
  this.peek = null;
  this.publishAsArticle = null;
  if (args) {
    if (args.draftId !== undefined && args.draftId !== null) {
      this.draftId = args.draftId;
    }
    if (args.sequence !== undefined && args.sequence !== null) {
      this.sequence = args.sequence;
    }
    if (args.peek !== undefined && args.peek !== null) {
      this.peek = new PeekDraft(args.peek);
    }
    if (args.publishAsArticle !== undefined && args.publishAsArticle !== null) {
      this.publishAsArticle = args.publishAsArticle;
    }
  }
};
GlanceDraftInfo.prototype = {};
GlanceDraftInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.draftId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.sequence = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.peek = new PeekDraft();
        this.peek.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.publishAsArticle = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftInfo.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftInfo');
  if (this.draftId !== null && this.draftId !== undefined) {
    output.writeFieldBegin('draftId', Thrift.Type.STRING, 1);
    output.writeString(this.draftId);
    output.writeFieldEnd();
  }
  if (this.sequence !== null && this.sequence !== undefined) {
    output.writeFieldBegin('sequence', Thrift.Type.I32, 2);
    output.writeI32(this.sequence);
    output.writeFieldEnd();
  }
  if (this.peek !== null && this.peek !== undefined) {
    output.writeFieldBegin('peek', Thrift.Type.STRUCT, 3);
    this.peek.write(output);
    output.writeFieldEnd();
  }
  if (this.publishAsArticle !== null && this.publishAsArticle !== undefined) {
    output.writeFieldBegin('publishAsArticle', Thrift.Type.BOOL, 4);
    output.writeBool(this.publishAsArticle);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UnpublishCard = function(args) {
  this.cardId = null;
  if (args) {
    if (args.cardId !== undefined && args.cardId !== null) {
      this.cardId = args.cardId;
    }
  }
};
UnpublishCard.prototype = {};
UnpublishCard.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.cardId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UnpublishCard.prototype.write = function(output) {
  output.writeStructBegin('UnpublishCard');
  if (this.cardId !== null && this.cardId !== undefined) {
    output.writeFieldBegin('cardId', Thrift.Type.STRING, 1);
    output.writeString(this.cardId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CardBubbleInfo = function(args) {
  this.cardId = null;
  this.bubble = null;
  this.glanceDraftInfo = null;
  this.country = null;
  if (args) {
    if (args.cardId !== undefined && args.cardId !== null) {
      this.cardId = args.cardId;
    }
    if (args.bubble !== undefined && args.bubble !== null) {
      this.bubble = new Bubble(args.bubble);
    }
    if (args.glanceDraftInfo !== undefined && args.glanceDraftInfo !== null) {
      this.glanceDraftInfo = new GlanceDraftInfo(args.glanceDraftInfo);
    }
    if (args.country !== undefined && args.country !== null) {
      this.country = args.country;
    }
  }
};
CardBubbleInfo.prototype = {};
CardBubbleInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.cardId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.bubble = new Bubble();
        this.bubble.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.glanceDraftInfo = new GlanceDraftInfo();
        this.glanceDraftInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.country = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CardBubbleInfo.prototype.write = function(output) {
  output.writeStructBegin('CardBubbleInfo');
  if (this.cardId !== null && this.cardId !== undefined) {
    output.writeFieldBegin('cardId', Thrift.Type.STRING, 1);
    output.writeString(this.cardId);
    output.writeFieldEnd();
  }
  if (this.bubble !== null && this.bubble !== undefined) {
    output.writeFieldBegin('bubble', Thrift.Type.STRUCT, 2);
    this.bubble.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceDraftInfo !== null && this.glanceDraftInfo !== undefined) {
    output.writeFieldBegin('glanceDraftInfo', Thrift.Type.STRUCT, 3);
    this.glanceDraftInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.country !== null && this.country !== undefined) {
    output.writeFieldBegin('country', Thrift.Type.STRING, 4);
    output.writeString(this.country);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

JavelinPublishingPartner = function(args) {
  this.id = null;
  this.name = null;
  this.country = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.createdBy = null;
  this.updatedBy = null;
  this.url = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.country !== undefined && args.country !== null) {
      this.country = args.country;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.createdBy !== undefined && args.createdBy !== null) {
      this.createdBy = args.createdBy;
    }
    if (args.updatedBy !== undefined && args.updatedBy !== null) {
      this.updatedBy = args.updatedBy;
    }
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
  }
};
JavelinPublishingPartner.prototype = {};
JavelinPublishingPartner.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.country = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.createdBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.updatedBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

JavelinPublishingPartner.prototype.write = function(output) {
  output.writeStructBegin('JavelinPublishingPartner');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.country !== null && this.country !== undefined) {
    output.writeFieldBegin('country', Thrift.Type.STRING, 3);
    output.writeString(this.country);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.createdBy !== null && this.createdBy !== undefined) {
    output.writeFieldBegin('createdBy', Thrift.Type.STRING, 6);
    output.writeString(this.createdBy);
    output.writeFieldEnd();
  }
  if (this.updatedBy !== null && this.updatedBy !== undefined) {
    output.writeFieldBegin('updatedBy', Thrift.Type.STRING, 7);
    output.writeString(this.updatedBy);
    output.writeFieldEnd();
  }
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 8);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GameBracket = function(args) {
  this.gameId = null;
  this.scoreBracket = null;
  this.totalUsers = null;
  this.updatedAtInSecs = null;
  if (args) {
    if (args.gameId !== undefined && args.gameId !== null) {
      this.gameId = args.gameId;
    }
    if (args.scoreBracket !== undefined && args.scoreBracket !== null) {
      this.scoreBracket = Thrift.copyList(args.scoreBracket, [null]);
    }
    if (args.totalUsers !== undefined && args.totalUsers !== null) {
      this.totalUsers = args.totalUsers;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
  }
};
GameBracket.prototype = {};
GameBracket.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.gameId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1942 = 0;
        var _rtmp31946;
        this.scoreBracket = [];
        var _etype1945 = 0;
        _rtmp31946 = input.readListBegin();
        _etype1945 = _rtmp31946.etype;
        _size1942 = _rtmp31946.size;
        for (var _i1947 = 0; _i1947 < _size1942; ++_i1947)
        {
          var elem1948 = null;
          elem1948 = input.readI64().value;
          this.scoreBracket.push(elem1948);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.totalUsers = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GameBracket.prototype.write = function(output) {
  output.writeStructBegin('GameBracket');
  if (this.gameId !== null && this.gameId !== undefined) {
    output.writeFieldBegin('gameId', Thrift.Type.STRING, 1);
    output.writeString(this.gameId);
    output.writeFieldEnd();
  }
  if (this.scoreBracket !== null && this.scoreBracket !== undefined) {
    output.writeFieldBegin('scoreBracket', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.I64, this.scoreBracket.length);
    for (var iter1949 in this.scoreBracket)
    {
      if (this.scoreBracket.hasOwnProperty(iter1949))
      {
        iter1949 = this.scoreBracket[iter1949];
        output.writeI64(iter1949);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.totalUsers !== null && this.totalUsers !== undefined) {
    output.writeFieldBegin('totalUsers', Thrift.Type.I64, 3);
    output.writeI64(this.totalUsers);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TournamentBracket = function(args) {
  this.tournamentId = null;
  this.scoreBracket = null;
  this.totalUsers = null;
  this.updatedAtInSecs = null;
  if (args) {
    if (args.tournamentId !== undefined && args.tournamentId !== null) {
      this.tournamentId = args.tournamentId;
    }
    if (args.scoreBracket !== undefined && args.scoreBracket !== null) {
      this.scoreBracket = Thrift.copyList(args.scoreBracket, [null]);
    }
    if (args.totalUsers !== undefined && args.totalUsers !== null) {
      this.totalUsers = args.totalUsers;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
  }
};
TournamentBracket.prototype = {};
TournamentBracket.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.tournamentId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1950 = 0;
        var _rtmp31954;
        this.scoreBracket = [];
        var _etype1953 = 0;
        _rtmp31954 = input.readListBegin();
        _etype1953 = _rtmp31954.etype;
        _size1950 = _rtmp31954.size;
        for (var _i1955 = 0; _i1955 < _size1950; ++_i1955)
        {
          var elem1956 = null;
          elem1956 = input.readI64().value;
          this.scoreBracket.push(elem1956);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.totalUsers = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TournamentBracket.prototype.write = function(output) {
  output.writeStructBegin('TournamentBracket');
  if (this.tournamentId !== null && this.tournamentId !== undefined) {
    output.writeFieldBegin('tournamentId', Thrift.Type.STRING, 1);
    output.writeString(this.tournamentId);
    output.writeFieldEnd();
  }
  if (this.scoreBracket !== null && this.scoreBracket !== undefined) {
    output.writeFieldBegin('scoreBracket', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.I64, this.scoreBracket.length);
    for (var iter1957 in this.scoreBracket)
    {
      if (this.scoreBracket.hasOwnProperty(iter1957))
      {
        iter1957 = this.scoreBracket[iter1957];
        output.writeI64(iter1957);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.totalUsers !== null && this.totalUsers !== undefined) {
    output.writeFieldBegin('totalUsers', Thrift.Type.I64, 3);
    output.writeI64(this.totalUsers);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

FilteringConfig = function(args) {
  this.expMeta = null;
  this.partnerIds = null;
  this.regions = null;
  this.userAgeMin = null;
  this.userAgeMax = null;
  this.participatingCategories = null;
  this.maxPercentageContent = null;
  this.apiKeys = null;
  if (args) {
    if (args.expMeta !== undefined && args.expMeta !== null) {
      this.expMeta = new ExperimentMeta(args.expMeta);
    }
    if (args.partnerIds !== undefined && args.partnerIds !== null) {
      this.partnerIds = Thrift.copyList(args.partnerIds, [null]);
    }
    if (args.regions !== undefined && args.regions !== null) {
      this.regions = Thrift.copyList(args.regions, [null]);
    }
    if (args.userAgeMin !== undefined && args.userAgeMin !== null) {
      this.userAgeMin = args.userAgeMin;
    }
    if (args.userAgeMax !== undefined && args.userAgeMax !== null) {
      this.userAgeMax = args.userAgeMax;
    }
    if (args.participatingCategories !== undefined && args.participatingCategories !== null) {
      this.participatingCategories = Thrift.copyList(args.participatingCategories, [null]);
    }
    if (args.maxPercentageContent !== undefined && args.maxPercentageContent !== null) {
      this.maxPercentageContent = args.maxPercentageContent;
    }
    if (args.apiKeys !== undefined && args.apiKeys !== null) {
      this.apiKeys = Thrift.copyList(args.apiKeys, [null]);
    }
  }
};
FilteringConfig.prototype = {};
FilteringConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.expMeta = new ExperimentMeta();
        this.expMeta.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size1958 = 0;
        var _rtmp31962;
        this.partnerIds = [];
        var _etype1961 = 0;
        _rtmp31962 = input.readListBegin();
        _etype1961 = _rtmp31962.etype;
        _size1958 = _rtmp31962.size;
        for (var _i1963 = 0; _i1963 < _size1958; ++_i1963)
        {
          var elem1964 = null;
          elem1964 = input.readString().value;
          this.partnerIds.push(elem1964);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size1965 = 0;
        var _rtmp31969;
        this.regions = [];
        var _etype1968 = 0;
        _rtmp31969 = input.readListBegin();
        _etype1968 = _rtmp31969.etype;
        _size1965 = _rtmp31969.size;
        for (var _i1970 = 0; _i1970 < _size1965; ++_i1970)
        {
          var elem1971 = null;
          elem1971 = input.readI32().value;
          this.regions.push(elem1971);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.userAgeMin = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.userAgeMax = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.SET) {
        var _size1972 = 0;
        var _rtmp31976;
        this.participatingCategories = [];
        var _etype1975 = 0;
        _rtmp31976 = input.readSetBegin();
        _etype1975 = _rtmp31976.etype;
        _size1972 = _rtmp31976.size;
        for (var _i1977 = 0; _i1977 < _size1972; ++_i1977)
        {
          var elem1978 = null;
          elem1978 = input.readString().value;
          this.participatingCategories.push(elem1978);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.maxPercentageContent = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size1979 = 0;
        var _rtmp31983;
        this.apiKeys = [];
        var _etype1982 = 0;
        _rtmp31983 = input.readListBegin();
        _etype1982 = _rtmp31983.etype;
        _size1979 = _rtmp31983.size;
        for (var _i1984 = 0; _i1984 < _size1979; ++_i1984)
        {
          var elem1985 = null;
          elem1985 = input.readString().value;
          this.apiKeys.push(elem1985);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

FilteringConfig.prototype.write = function(output) {
  output.writeStructBegin('FilteringConfig');
  if (this.expMeta !== null && this.expMeta !== undefined) {
    output.writeFieldBegin('expMeta', Thrift.Type.STRUCT, 1);
    this.expMeta.write(output);
    output.writeFieldEnd();
  }
  if (this.partnerIds !== null && this.partnerIds !== undefined) {
    output.writeFieldBegin('partnerIds', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.partnerIds.length);
    for (var iter1986 in this.partnerIds)
    {
      if (this.partnerIds.hasOwnProperty(iter1986))
      {
        iter1986 = this.partnerIds[iter1986];
        output.writeString(iter1986);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.regions !== null && this.regions !== undefined) {
    output.writeFieldBegin('regions', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.I32, this.regions.length);
    for (var iter1987 in this.regions)
    {
      if (this.regions.hasOwnProperty(iter1987))
      {
        iter1987 = this.regions[iter1987];
        output.writeI32(iter1987);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.userAgeMin !== null && this.userAgeMin !== undefined) {
    output.writeFieldBegin('userAgeMin', Thrift.Type.I32, 4);
    output.writeI32(this.userAgeMin);
    output.writeFieldEnd();
  }
  if (this.userAgeMax !== null && this.userAgeMax !== undefined) {
    output.writeFieldBegin('userAgeMax', Thrift.Type.I32, 5);
    output.writeI32(this.userAgeMax);
    output.writeFieldEnd();
  }
  if (this.participatingCategories !== null && this.participatingCategories !== undefined) {
    output.writeFieldBegin('participatingCategories', Thrift.Type.SET, 6);
    output.writeSetBegin(Thrift.Type.STRING, this.participatingCategories.length);
    for (var iter1988 in this.participatingCategories)
    {
      if (this.participatingCategories.hasOwnProperty(iter1988))
      {
        iter1988 = this.participatingCategories[iter1988];
        output.writeString(iter1988);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.maxPercentageContent !== null && this.maxPercentageContent !== undefined) {
    output.writeFieldBegin('maxPercentageContent', Thrift.Type.I32, 7);
    output.writeI32(this.maxPercentageContent);
    output.writeFieldEnd();
  }
  if (this.apiKeys !== null && this.apiKeys !== undefined) {
    output.writeFieldBegin('apiKeys', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRING, this.apiKeys.length);
    for (var iter1989 in this.apiKeys)
    {
      if (this.apiKeys.hasOwnProperty(iter1989))
      {
        iter1989 = this.apiKeys[iter1989];
        output.writeString(iter1989);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

UserBubbleDislikeSummary = function(args) {
  this.id = null;
  this.userId = null;
  this.dislikedBubbles = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    }
    if (args.dislikedBubbles !== undefined && args.dislikedBubbles !== null) {
      this.dislikedBubbles = Thrift.copyList(args.dislikedBubbles, [null]);
    }
  }
};
UserBubbleDislikeSummary.prototype = {};
UserBubbleDislikeSummary.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size1990 = 0;
        var _rtmp31994;
        this.dislikedBubbles = [];
        var _etype1993 = 0;
        _rtmp31994 = input.readListBegin();
        _etype1993 = _rtmp31994.etype;
        _size1990 = _rtmp31994.size;
        for (var _i1995 = 0; _i1995 < _size1990; ++_i1995)
        {
          var elem1996 = null;
          elem1996 = new DislikedBubble();
          elem1996.read(input);
          this.dislikedBubbles.push(elem1996);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserBubbleDislikeSummary.prototype.write = function(output) {
  output.writeStructBegin('UserBubbleDislikeSummary');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 2);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.dislikedBubbles !== null && this.dislikedBubbles !== undefined) {
    output.writeFieldBegin('dislikedBubbles', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.dislikedBubbles.length);
    for (var iter1997 in this.dislikedBubbles)
    {
      if (this.dislikedBubbles.hasOwnProperty(iter1997))
      {
        iter1997 = this.dislikedBubbles[iter1997];
        iter1997.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

DislikedBubble = function(args) {
  this.bubbleId = null;
  this.timestamp = null;
  if (args) {
    if (args.bubbleId !== undefined && args.bubbleId !== null) {
      this.bubbleId = args.bubbleId;
    }
    if (args.timestamp !== undefined && args.timestamp !== null) {
      this.timestamp = args.timestamp;
    }
  }
};
DislikedBubble.prototype = {};
DislikedBubble.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.bubbleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.timestamp = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

DislikedBubble.prototype.write = function(output) {
  output.writeStructBegin('DislikedBubble');
  if (this.bubbleId !== null && this.bubbleId !== undefined) {
    output.writeFieldBegin('bubbleId', Thrift.Type.STRING, 1);
    output.writeString(this.bubbleId);
    output.writeFieldEnd();
  }
  if (this.timestamp !== null && this.timestamp !== undefined) {
    output.writeFieldBegin('timestamp', Thrift.Type.I64, 2);
    output.writeI64(this.timestamp);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OtdImage = function(args) {
  this.imageId = null;
  this.isEnabled = null;
  this.isActive = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.createdBy = null;
  this.modifiedBy = null;
  if (args) {
    if (args.imageId !== undefined && args.imageId !== null) {
      this.imageId = args.imageId;
    }
    if (args.isEnabled !== undefined && args.isEnabled !== null) {
      this.isEnabled = args.isEnabled;
    }
    if (args.isActive !== undefined && args.isActive !== null) {
      this.isActive = args.isActive;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.createdBy !== undefined && args.createdBy !== null) {
      this.createdBy = args.createdBy;
    }
    if (args.modifiedBy !== undefined && args.modifiedBy !== null) {
      this.modifiedBy = args.modifiedBy;
    }
  }
};
OtdImage.prototype = {};
OtdImage.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.imageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.isEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.isActive = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.createdBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.modifiedBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OtdImage.prototype.write = function(output) {
  output.writeStructBegin('OtdImage');
  if (this.imageId !== null && this.imageId !== undefined) {
    output.writeFieldBegin('imageId', Thrift.Type.STRING, 1);
    output.writeString(this.imageId);
    output.writeFieldEnd();
  }
  if (this.isEnabled !== null && this.isEnabled !== undefined) {
    output.writeFieldBegin('isEnabled', Thrift.Type.BOOL, 2);
    output.writeBool(this.isEnabled);
    output.writeFieldEnd();
  }
  if (this.isActive !== null && this.isActive !== undefined) {
    output.writeFieldBegin('isActive', Thrift.Type.BOOL, 3);
    output.writeBool(this.isActive);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.createdBy !== null && this.createdBy !== undefined) {
    output.writeFieldBegin('createdBy', Thrift.Type.STRING, 6);
    output.writeString(this.createdBy);
    output.writeFieldEnd();
  }
  if (this.modifiedBy !== null && this.modifiedBy !== undefined) {
    output.writeFieldBegin('modifiedBy', Thrift.Type.STRING, 7);
    output.writeString(this.modifiedBy);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

ShortVideoAdConfig = function(args) {
  this.enable = null;
  this.adStartMs = null;
  this.adEndMs = null;
  this.webPageUrl = null;
  this.adProvider = null;
  if (args) {
    if (args.enable !== undefined && args.enable !== null) {
      this.enable = args.enable;
    }
    if (args.adStartMs !== undefined && args.adStartMs !== null) {
      this.adStartMs = args.adStartMs;
    }
    if (args.adEndMs !== undefined && args.adEndMs !== null) {
      this.adEndMs = args.adEndMs;
    }
    if (args.webPageUrl !== undefined && args.webPageUrl !== null) {
      this.webPageUrl = args.webPageUrl;
    }
    if (args.adProvider !== undefined && args.adProvider !== null) {
      this.adProvider = args.adProvider;
    }
  }
};
ShortVideoAdConfig.prototype = {};
ShortVideoAdConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.enable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.adStartMs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.adEndMs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.webPageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.adProvider = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

ShortVideoAdConfig.prototype.write = function(output) {
  output.writeStructBegin('ShortVideoAdConfig');
  if (this.enable !== null && this.enable !== undefined) {
    output.writeFieldBegin('enable', Thrift.Type.BOOL, 1);
    output.writeBool(this.enable);
    output.writeFieldEnd();
  }
  if (this.adStartMs !== null && this.adStartMs !== undefined) {
    output.writeFieldBegin('adStartMs', Thrift.Type.I64, 2);
    output.writeI64(this.adStartMs);
    output.writeFieldEnd();
  }
  if (this.adEndMs !== null && this.adEndMs !== undefined) {
    output.writeFieldBegin('adEndMs', Thrift.Type.I64, 3);
    output.writeI64(this.adEndMs);
    output.writeFieldEnd();
  }
  if (this.webPageUrl !== null && this.webPageUrl !== undefined) {
    output.writeFieldBegin('webPageUrl', Thrift.Type.STRING, 4);
    output.writeString(this.webPageUrl);
    output.writeFieldEnd();
  }
  if (this.adProvider !== null && this.adProvider !== undefined) {
    output.writeFieldBegin('adProvider', Thrift.Type.STRING, 5);
    output.writeString(this.adProvider);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Banner = function(args) {
  this.adUnit = null;
  this.adHeight = null;
  this.adWidth = null;
  this.xCoordinate = null;
  this.yCoordinate = null;
  this.disableHardwareAcceleration = null;
  if (args) {
    if (args.adUnit !== undefined && args.adUnit !== null) {
      this.adUnit = args.adUnit;
    }
    if (args.adHeight !== undefined && args.adHeight !== null) {
      this.adHeight = args.adHeight;
    }
    if (args.adWidth !== undefined && args.adWidth !== null) {
      this.adWidth = args.adWidth;
    }
    if (args.xCoordinate !== undefined && args.xCoordinate !== null) {
      this.xCoordinate = args.xCoordinate;
    }
    if (args.yCoordinate !== undefined && args.yCoordinate !== null) {
      this.yCoordinate = args.yCoordinate;
    }
    if (args.disableHardwareAcceleration !== undefined && args.disableHardwareAcceleration !== null) {
      this.disableHardwareAcceleration = args.disableHardwareAcceleration;
    }
  }
};
Banner.prototype = {};
Banner.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adUnit = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.adHeight = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.adWidth = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.xCoordinate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.yCoordinate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.disableHardwareAcceleration = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Banner.prototype.write = function(output) {
  output.writeStructBegin('Banner');
  if (this.adUnit !== null && this.adUnit !== undefined) {
    output.writeFieldBegin('adUnit', Thrift.Type.STRING, 1);
    output.writeString(this.adUnit);
    output.writeFieldEnd();
  }
  if (this.adHeight !== null && this.adHeight !== undefined) {
    output.writeFieldBegin('adHeight', Thrift.Type.I32, 2);
    output.writeI32(this.adHeight);
    output.writeFieldEnd();
  }
  if (this.adWidth !== null && this.adWidth !== undefined) {
    output.writeFieldBegin('adWidth', Thrift.Type.I32, 3);
    output.writeI32(this.adWidth);
    output.writeFieldEnd();
  }
  if (this.xCoordinate !== null && this.xCoordinate !== undefined) {
    output.writeFieldBegin('xCoordinate', Thrift.Type.STRING, 4);
    output.writeString(this.xCoordinate);
    output.writeFieldEnd();
  }
  if (this.yCoordinate !== null && this.yCoordinate !== undefined) {
    output.writeFieldBegin('yCoordinate', Thrift.Type.STRING, 5);
    output.writeString(this.yCoordinate);
    output.writeFieldEnd();
  }
  if (this.disableHardwareAcceleration !== null && this.disableHardwareAcceleration !== undefined) {
    output.writeFieldBegin('disableHardwareAcceleration', Thrift.Type.BOOL, 6);
    output.writeBool(this.disableHardwareAcceleration);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Fallback = function(args) {
  this.rewarded = null;
  this.interstitial = null;
  this.banner = null;
  if (args) {
    if (args.rewarded !== undefined && args.rewarded !== null) {
      this.rewarded = args.rewarded;
    }
    if (args.interstitial !== undefined && args.interstitial !== null) {
      this.interstitial = args.interstitial;
    }
    if (args.banner !== undefined && args.banner !== null) {
      this.banner = new Banner(args.banner);
    }
  }
};
Fallback.prototype = {};
Fallback.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.rewarded = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.interstitial = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.banner = new Banner();
        this.banner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Fallback.prototype.write = function(output) {
  output.writeStructBegin('Fallback');
  if (this.rewarded !== null && this.rewarded !== undefined) {
    output.writeFieldBegin('rewarded', Thrift.Type.STRING, 1);
    output.writeString(this.rewarded);
    output.writeFieldEnd();
  }
  if (this.interstitial !== null && this.interstitial !== undefined) {
    output.writeFieldBegin('interstitial', Thrift.Type.STRING, 2);
    output.writeString(this.interstitial);
    output.writeFieldEnd();
  }
  if (this.banner !== null && this.banner !== undefined) {
    output.writeFieldBegin('banner', Thrift.Type.STRUCT, 3);
    this.banner.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

MesonAdConfig = function(args) {
  this.enabled = null;
  this.appId = null;
  this.fallbackAdUnits = null;
  this.svConfig = null;
  if (args) {
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    }
    if (args.appId !== undefined && args.appId !== null) {
      this.appId = args.appId;
    }
    if (args.fallbackAdUnits !== undefined && args.fallbackAdUnits !== null) {
      this.fallbackAdUnits = new Fallback(args.fallbackAdUnits);
    }
    if (args.svConfig !== undefined && args.svConfig !== null) {
      this.svConfig = new ShortVideoAdConfig(args.svConfig);
    }
  }
};
MesonAdConfig.prototype = {};
MesonAdConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.appId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.fallbackAdUnits = new Fallback();
        this.fallbackAdUnits.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.svConfig = new ShortVideoAdConfig();
        this.svConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

MesonAdConfig.prototype.write = function(output) {
  output.writeStructBegin('MesonAdConfig');
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 1);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  if (this.appId !== null && this.appId !== undefined) {
    output.writeFieldBegin('appId', Thrift.Type.STRING, 2);
    output.writeString(this.appId);
    output.writeFieldEnd();
  }
  if (this.fallbackAdUnits !== null && this.fallbackAdUnits !== undefined) {
    output.writeFieldBegin('fallbackAdUnits', Thrift.Type.STRUCT, 3);
    this.fallbackAdUnits.write(output);
    output.writeFieldEnd();
  }
  if (this.svConfig !== null && this.svConfig !== undefined) {
    output.writeFieldBegin('svConfig', Thrift.Type.STRUCT, 4);
    this.svConfig.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

InternalMesonAdConfig = function(args) {
  this.appPackageName = null;
  this.partnerId = null;
  this.config = null;
  this.createdAt = null;
  this.updatedAt = null;
  if (args) {
    if (args.appPackageName !== undefined && args.appPackageName !== null) {
      this.appPackageName = args.appPackageName;
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    }
    if (args.config !== undefined && args.config !== null) {
      this.config = new MesonAdConfig(args.config);
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
  }
};
InternalMesonAdConfig.prototype = {};
InternalMesonAdConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.appPackageName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.config = new MesonAdConfig();
        this.config.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

InternalMesonAdConfig.prototype.write = function(output) {
  output.writeStructBegin('InternalMesonAdConfig');
  if (this.appPackageName !== null && this.appPackageName !== undefined) {
    output.writeFieldBegin('appPackageName', Thrift.Type.STRING, 1);
    output.writeString(this.appPackageName);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 2);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.config !== null && this.config !== undefined) {
    output.writeFieldBegin('config', Thrift.Type.STRUCT, 3);
    this.config.write(output);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 4);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 5);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceIdEuIdentifierMapping = function(args) {
  this.glanceIdToEuIdentifierMapping = null;
  if (args) {
    if (args.glanceIdToEuIdentifierMapping !== undefined && args.glanceIdToEuIdentifierMapping !== null) {
      this.glanceIdToEuIdentifierMapping = Thrift.copyMap(args.glanceIdToEuIdentifierMapping, [null]);
    }
  }
};
GlanceIdEuIdentifierMapping.prototype = {};
GlanceIdEuIdentifierMapping.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.MAP) {
        var _size1998 = 0;
        var _rtmp32002;
        this.glanceIdToEuIdentifierMapping = {};
        var _ktype1999 = 0;
        var _vtype2000 = 0;
        _rtmp32002 = input.readMapBegin();
        _ktype1999 = _rtmp32002.ktype;
        _vtype2000 = _rtmp32002.vtype;
        _size1998 = _rtmp32002.size;
        for (var _i2003 = 0; _i2003 < _size1998; ++_i2003)
        {
          if (_i2003 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key2004 = null;
          var val2005 = null;
          key2004 = input.readString().value;
          val2005 = input.readString().value;
          this.glanceIdToEuIdentifierMapping[key2004] = val2005;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceIdEuIdentifierMapping.prototype.write = function(output) {
  output.writeStructBegin('GlanceIdEuIdentifierMapping');
  if (this.glanceIdToEuIdentifierMapping !== null && this.glanceIdToEuIdentifierMapping !== undefined) {
    output.writeFieldBegin('glanceIdToEuIdentifierMapping', Thrift.Type.MAP, 1);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.STRING, Thrift.objectLength(this.glanceIdToEuIdentifierMapping));
    for (var kiter2006 in this.glanceIdToEuIdentifierMapping)
    {
      if (this.glanceIdToEuIdentifierMapping.hasOwnProperty(kiter2006))
      {
        var viter2007 = this.glanceIdToEuIdentifierMapping[kiter2006];
        output.writeString(kiter2006);
        output.writeString(viter2007);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RoundUpCardInfo = function(args) {
  this.id = null;
  this.imagePath = null;
  this.publisherLogoUrl = null;
  this.publisherName = null;
  this.title = null;
  this.category = null;
  this.state = null;
  this.peekType = null;
  this.createdAt = null;
  this.croppedImageUrl = null;
  this.imageMap = null;
  this.ctaText = null;
  this.ctaUrl = null;
  this.glanceId = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.imagePath !== undefined && args.imagePath !== null) {
      this.imagePath = args.imagePath;
    }
    if (args.publisherLogoUrl !== undefined && args.publisherLogoUrl !== null) {
      this.publisherLogoUrl = args.publisherLogoUrl;
    }
    if (args.publisherName !== undefined && args.publisherName !== null) {
      this.publisherName = args.publisherName;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.category !== undefined && args.category !== null) {
      this.category = args.category;
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    }
    if (args.peekType !== undefined && args.peekType !== null) {
      this.peekType = args.peekType;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.croppedImageUrl !== undefined && args.croppedImageUrl !== null) {
      this.croppedImageUrl = args.croppedImageUrl;
    }
    if (args.imageMap !== undefined && args.imageMap !== null) {
      this.imageMap = new ImageMap(args.imageMap);
    }
    if (args.ctaText !== undefined && args.ctaText !== null) {
      this.ctaText = args.ctaText;
    }
    if (args.ctaUrl !== undefined && args.ctaUrl !== null) {
      this.ctaUrl = args.ctaUrl;
    }
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    }
  }
};
RoundUpCardInfo.prototype = {};
RoundUpCardInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.imagePath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.publisherLogoUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.publisherName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.category = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.state = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.peekType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.croppedImageUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRUCT) {
        this.imageMap = new ImageMap();
        this.imageMap.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.ctaText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.ctaUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RoundUpCardInfo.prototype.write = function(output) {
  output.writeStructBegin('RoundUpCardInfo');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.imagePath !== null && this.imagePath !== undefined) {
    output.writeFieldBegin('imagePath', Thrift.Type.STRING, 2);
    output.writeString(this.imagePath);
    output.writeFieldEnd();
  }
  if (this.publisherLogoUrl !== null && this.publisherLogoUrl !== undefined) {
    output.writeFieldBegin('publisherLogoUrl', Thrift.Type.STRING, 3);
    output.writeString(this.publisherLogoUrl);
    output.writeFieldEnd();
  }
  if (this.publisherName !== null && this.publisherName !== undefined) {
    output.writeFieldBegin('publisherName', Thrift.Type.STRING, 4);
    output.writeString(this.publisherName);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 5);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.category !== null && this.category !== undefined) {
    output.writeFieldBegin('category', Thrift.Type.STRING, 6);
    output.writeString(this.category);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.STRING, 7);
    output.writeString(this.state);
    output.writeFieldEnd();
  }
  if (this.peekType !== null && this.peekType !== undefined) {
    output.writeFieldBegin('peekType', Thrift.Type.STRING, 8);
    output.writeString(this.peekType);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 9);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.croppedImageUrl !== null && this.croppedImageUrl !== undefined) {
    output.writeFieldBegin('croppedImageUrl', Thrift.Type.STRING, 10);
    output.writeString(this.croppedImageUrl);
    output.writeFieldEnd();
  }
  if (this.imageMap !== null && this.imageMap !== undefined) {
    output.writeFieldBegin('imageMap', Thrift.Type.STRUCT, 11);
    this.imageMap.write(output);
    output.writeFieldEnd();
  }
  if (this.ctaText !== null && this.ctaText !== undefined) {
    output.writeFieldBegin('ctaText', Thrift.Type.STRING, 12);
    output.writeString(this.ctaText);
    output.writeFieldEnd();
  }
  if (this.ctaUrl !== null && this.ctaUrl !== undefined) {
    output.writeFieldBegin('ctaUrl', Thrift.Type.STRING, 13);
    output.writeString(this.ctaUrl);
    output.writeFieldEnd();
  }
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 14);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RoundUpMeta = function(args) {
  this.id = null;
  this.roundUpId = null;
  this.partnerId = null;
  this.region = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.experiments = null;
  this.segmentIds = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field id is unset!');
    }
    if (args.roundUpId !== undefined && args.roundUpId !== null) {
      this.roundUpId = args.roundUpId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field roundUpId is unset!');
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    }
    if (args.experiments !== undefined && args.experiments !== null) {
      this.experiments = Thrift.copyList(args.experiments, [ExperimentMeta]);
    }
    if (args.segmentIds !== undefined && args.segmentIds !== null) {
      this.segmentIds = Thrift.copyList(args.segmentIds, [null]);
    }
  }
};
RoundUpMeta.prototype = {};
RoundUpMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.roundUpId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size2008 = 0;
        var _rtmp32012;
        this.experiments = [];
        var _etype2011 = 0;
        _rtmp32012 = input.readListBegin();
        _etype2011 = _rtmp32012.etype;
        _size2008 = _rtmp32012.size;
        for (var _i2013 = 0; _i2013 < _size2008; ++_i2013)
        {
          var elem2014 = null;
          elem2014 = new ExperimentMeta();
          elem2014.read(input);
          this.experiments.push(elem2014);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.SET) {
        var _size2015 = 0;
        var _rtmp32019;
        this.segmentIds = [];
        var _etype2018 = 0;
        _rtmp32019 = input.readSetBegin();
        _etype2018 = _rtmp32019.etype;
        _size2015 = _rtmp32019.size;
        for (var _i2020 = 0; _i2020 < _size2015; ++_i2020)
        {
          var elem2021 = null;
          elem2021 = input.readI32().value;
          this.segmentIds.push(elem2021);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RoundUpMeta.prototype.write = function(output) {
  output.writeStructBegin('RoundUpMeta');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.roundUpId !== null && this.roundUpId !== undefined) {
    output.writeFieldBegin('roundUpId', Thrift.Type.STRING, 2);
    output.writeString(this.roundUpId);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 3);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 4);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 6);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.experiments !== null && this.experiments !== undefined) {
    output.writeFieldBegin('experiments', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRUCT, this.experiments.length);
    for (var iter2022 in this.experiments)
    {
      if (this.experiments.hasOwnProperty(iter2022))
      {
        iter2022 = this.experiments[iter2022];
        iter2022.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.segmentIds !== null && this.segmentIds !== undefined) {
    output.writeFieldBegin('segmentIds', Thrift.Type.SET, 9);
    output.writeSetBegin(Thrift.Type.I32, this.segmentIds.length);
    for (var iter2023 in this.segmentIds)
    {
      if (this.segmentIds.hasOwnProperty(iter2023))
      {
        iter2023 = this.segmentIds[iter2023];
        output.writeI32(iter2023);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RoundUp = function(args) {
  this.roundUpId = null;
  this.name = null;
  this.description = null;
  this.startAtInSecs = null;
  this.endAtInSecs = null;
  this.createdAtInSecs = null;
  this.modifiedAtInSecs = null;
  this.roundUpCards = null;
  this.videoGlanceId = null;
  this.createdBy = null;
  this.updatedBy = null;
  this.publishingOptions = null;
  this.state = null;
  this.videoPath = null;
  this.iconPath = null;
  this.bgColor = null;
  this.bgLockscreenImage = null;
  this.spaceType = null;
  this.tagDetails = null;
  this.spaces = null;
  this.sportEuSpaceData = null;
  this.contentTypes = null;
  this.zappWidgetIds = null;
  this.l0ZappWidgetIds = null;
  if (args) {
    if (args.roundUpId !== undefined && args.roundUpId !== null) {
      this.roundUpId = args.roundUpId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field roundUpId is unset!');
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field name is unset!');
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.startAtInSecs !== undefined && args.startAtInSecs !== null) {
      this.startAtInSecs = args.startAtInSecs;
    }
    if (args.endAtInSecs !== undefined && args.endAtInSecs !== null) {
      this.endAtInSecs = args.endAtInSecs;
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    }
    if (args.modifiedAtInSecs !== undefined && args.modifiedAtInSecs !== null) {
      this.modifiedAtInSecs = args.modifiedAtInSecs;
    }
    if (args.roundUpCards !== undefined && args.roundUpCards !== null) {
      this.roundUpCards = Thrift.copyMap(args.roundUpCards, [RoundUpCardInfo]);
    }
    if (args.videoGlanceId !== undefined && args.videoGlanceId !== null) {
      this.videoGlanceId = args.videoGlanceId;
    }
    if (args.createdBy !== undefined && args.createdBy !== null) {
      this.createdBy = args.createdBy;
    }
    if (args.updatedBy !== undefined && args.updatedBy !== null) {
      this.updatedBy = args.updatedBy;
    }
    if (args.publishingOptions !== undefined && args.publishingOptions !== null) {
      this.publishingOptions = new PublishingOptions(args.publishingOptions);
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    }
    if (args.videoPath !== undefined && args.videoPath !== null) {
      this.videoPath = args.videoPath;
    }
    if (args.iconPath !== undefined && args.iconPath !== null) {
      this.iconPath = args.iconPath;
    }
    if (args.bgColor !== undefined && args.bgColor !== null) {
      this.bgColor = args.bgColor;
    }
    if (args.bgLockscreenImage !== undefined && args.bgLockscreenImage !== null) {
      this.bgLockscreenImage = args.bgLockscreenImage;
    }
    if (args.spaceType !== undefined && args.spaceType !== null) {
      this.spaceType = args.spaceType;
    }
    if (args.tagDetails !== undefined && args.tagDetails !== null) {
      this.tagDetails = new WidgetTagDetails(args.tagDetails);
    }
    if (args.spaces !== undefined && args.spaces !== null) {
      this.spaces = Thrift.copyList(args.spaces, [null]);
    }
    if (args.sportEuSpaceData !== undefined && args.sportEuSpaceData !== null) {
      this.sportEuSpaceData = new SportEuSpaceData(args.sportEuSpaceData);
    }
    if (args.contentTypes !== undefined && args.contentTypes !== null) {
      this.contentTypes = Thrift.copyList(args.contentTypes, [null]);
    }
    if (args.zappWidgetIds !== undefined && args.zappWidgetIds !== null) {
      this.zappWidgetIds = Thrift.copyList(args.zappWidgetIds, [null]);
    }
    if (args.l0ZappWidgetIds !== undefined && args.l0ZappWidgetIds !== null) {
      this.l0ZappWidgetIds = Thrift.copyList(args.l0ZappWidgetIds, [null]);
    }
  }
};
RoundUp.prototype = {};
RoundUp.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.roundUpId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.startAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.endAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I64) {
        this.modifiedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.MAP) {
        var _size2024 = 0;
        var _rtmp32028;
        this.roundUpCards = {};
        var _ktype2025 = 0;
        var _vtype2026 = 0;
        _rtmp32028 = input.readMapBegin();
        _ktype2025 = _rtmp32028.ktype;
        _vtype2026 = _rtmp32028.vtype;
        _size2024 = _rtmp32028.size;
        for (var _i2029 = 0; _i2029 < _size2024; ++_i2029)
        {
          if (_i2029 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key2030 = null;
          var val2031 = null;
          key2030 = input.readI32().value;
          val2031 = new RoundUpCardInfo();
          val2031.read(input);
          this.roundUpCards[key2030] = val2031;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.videoGlanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.createdBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.updatedBy = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRUCT) {
        this.publishingOptions = new PublishingOptions();
        this.publishingOptions.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.state = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.videoPath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.iconPath = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.bgColor = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.bgLockscreenImage = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.I32) {
        this.spaceType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRUCT) {
        this.tagDetails = new WidgetTagDetails();
        this.tagDetails.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.LIST) {
        var _size2032 = 0;
        var _rtmp32036;
        this.spaces = [];
        var _etype2035 = 0;
        _rtmp32036 = input.readListBegin();
        _etype2035 = _rtmp32036.etype;
        _size2032 = _rtmp32036.size;
        for (var _i2037 = 0; _i2037 < _size2032; ++_i2037)
        {
          var elem2038 = null;
          elem2038 = input.readI32().value;
          this.spaces.push(elem2038);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.STRUCT) {
        this.sportEuSpaceData = new SportEuSpaceData();
        this.sportEuSpaceData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.LIST) {
        var _size2039 = 0;
        var _rtmp32043;
        this.contentTypes = [];
        var _etype2042 = 0;
        _rtmp32043 = input.readListBegin();
        _etype2042 = _rtmp32043.etype;
        _size2039 = _rtmp32043.size;
        for (var _i2044 = 0; _i2044 < _size2039; ++_i2044)
        {
          var elem2045 = null;
          elem2045 = input.readI32().value;
          this.contentTypes.push(elem2045);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.LIST) {
        var _size2046 = 0;
        var _rtmp32050;
        this.zappWidgetIds = [];
        var _etype2049 = 0;
        _rtmp32050 = input.readListBegin();
        _etype2049 = _rtmp32050.etype;
        _size2046 = _rtmp32050.size;
        for (var _i2051 = 0; _i2051 < _size2046; ++_i2051)
        {
          var elem2052 = null;
          elem2052 = input.readString().value;
          this.zappWidgetIds.push(elem2052);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.LIST) {
        var _size2053 = 0;
        var _rtmp32057;
        this.l0ZappWidgetIds = [];
        var _etype2056 = 0;
        _rtmp32057 = input.readListBegin();
        _etype2056 = _rtmp32057.etype;
        _size2053 = _rtmp32057.size;
        for (var _i2058 = 0; _i2058 < _size2053; ++_i2058)
        {
          var elem2059 = null;
          elem2059 = input.readString().value;
          this.l0ZappWidgetIds.push(elem2059);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RoundUp.prototype.write = function(output) {
  output.writeStructBegin('RoundUp');
  if (this.roundUpId !== null && this.roundUpId !== undefined) {
    output.writeFieldBegin('roundUpId', Thrift.Type.STRING, 1);
    output.writeString(this.roundUpId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 3);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.startAtInSecs !== null && this.startAtInSecs !== undefined) {
    output.writeFieldBegin('startAtInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.startAtInSecs);
    output.writeFieldEnd();
  }
  if (this.endAtInSecs !== null && this.endAtInSecs !== undefined) {
    output.writeFieldBegin('endAtInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.endAtInSecs);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 6);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.modifiedAtInSecs !== null && this.modifiedAtInSecs !== undefined) {
    output.writeFieldBegin('modifiedAtInSecs', Thrift.Type.I64, 7);
    output.writeI64(this.modifiedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.roundUpCards !== null && this.roundUpCards !== undefined) {
    output.writeFieldBegin('roundUpCards', Thrift.Type.MAP, 8);
    output.writeMapBegin(Thrift.Type.I32, Thrift.Type.STRUCT, Thrift.objectLength(this.roundUpCards));
    for (var kiter2060 in this.roundUpCards)
    {
      if (this.roundUpCards.hasOwnProperty(kiter2060))
      {
        var viter2061 = this.roundUpCards[kiter2060];
        output.writeI32(kiter2060);
        viter2061.write(output);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.videoGlanceId !== null && this.videoGlanceId !== undefined) {
    output.writeFieldBegin('videoGlanceId', Thrift.Type.STRING, 9);
    output.writeString(this.videoGlanceId);
    output.writeFieldEnd();
  }
  if (this.createdBy !== null && this.createdBy !== undefined) {
    output.writeFieldBegin('createdBy', Thrift.Type.STRING, 10);
    output.writeString(this.createdBy);
    output.writeFieldEnd();
  }
  if (this.updatedBy !== null && this.updatedBy !== undefined) {
    output.writeFieldBegin('updatedBy', Thrift.Type.STRING, 11);
    output.writeString(this.updatedBy);
    output.writeFieldEnd();
  }
  if (this.publishingOptions !== null && this.publishingOptions !== undefined) {
    output.writeFieldBegin('publishingOptions', Thrift.Type.STRUCT, 12);
    this.publishingOptions.write(output);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.STRING, 13);
    output.writeString(this.state);
    output.writeFieldEnd();
  }
  if (this.videoPath !== null && this.videoPath !== undefined) {
    output.writeFieldBegin('videoPath', Thrift.Type.STRING, 14);
    output.writeString(this.videoPath);
    output.writeFieldEnd();
  }
  if (this.iconPath !== null && this.iconPath !== undefined) {
    output.writeFieldBegin('iconPath', Thrift.Type.STRING, 15);
    output.writeString(this.iconPath);
    output.writeFieldEnd();
  }
  if (this.bgColor !== null && this.bgColor !== undefined) {
    output.writeFieldBegin('bgColor', Thrift.Type.STRING, 16);
    output.writeString(this.bgColor);
    output.writeFieldEnd();
  }
  if (this.bgLockscreenImage !== null && this.bgLockscreenImage !== undefined) {
    output.writeFieldBegin('bgLockscreenImage', Thrift.Type.STRING, 17);
    output.writeString(this.bgLockscreenImage);
    output.writeFieldEnd();
  }
  if (this.spaceType !== null && this.spaceType !== undefined) {
    output.writeFieldBegin('spaceType', Thrift.Type.I32, 18);
    output.writeI32(this.spaceType);
    output.writeFieldEnd();
  }
  if (this.tagDetails !== null && this.tagDetails !== undefined) {
    output.writeFieldBegin('tagDetails', Thrift.Type.STRUCT, 19);
    this.tagDetails.write(output);
    output.writeFieldEnd();
  }
  if (this.spaces !== null && this.spaces !== undefined) {
    output.writeFieldBegin('spaces', Thrift.Type.LIST, 20);
    output.writeListBegin(Thrift.Type.I32, this.spaces.length);
    for (var iter2062 in this.spaces)
    {
      if (this.spaces.hasOwnProperty(iter2062))
      {
        iter2062 = this.spaces[iter2062];
        output.writeI32(iter2062);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.sportEuSpaceData !== null && this.sportEuSpaceData !== undefined) {
    output.writeFieldBegin('sportEuSpaceData', Thrift.Type.STRUCT, 21);
    this.sportEuSpaceData.write(output);
    output.writeFieldEnd();
  }
  if (this.contentTypes !== null && this.contentTypes !== undefined) {
    output.writeFieldBegin('contentTypes', Thrift.Type.LIST, 22);
    output.writeListBegin(Thrift.Type.I32, this.contentTypes.length);
    for (var iter2063 in this.contentTypes)
    {
      if (this.contentTypes.hasOwnProperty(iter2063))
      {
        iter2063 = this.contentTypes[iter2063];
        output.writeI32(iter2063);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.zappWidgetIds !== null && this.zappWidgetIds !== undefined) {
    output.writeFieldBegin('zappWidgetIds', Thrift.Type.LIST, 23);
    output.writeListBegin(Thrift.Type.STRING, this.zappWidgetIds.length);
    for (var iter2064 in this.zappWidgetIds)
    {
      if (this.zappWidgetIds.hasOwnProperty(iter2064))
      {
        iter2064 = this.zappWidgetIds[iter2064];
        output.writeString(iter2064);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.l0ZappWidgetIds !== null && this.l0ZappWidgetIds !== undefined) {
    output.writeFieldBegin('l0ZappWidgetIds', Thrift.Type.LIST, 24);
    output.writeListBegin(Thrift.Type.STRING, this.l0ZappWidgetIds.length);
    for (var iter2065 in this.l0ZappWidgetIds)
    {
      if (this.l0ZappWidgetIds.hasOwnProperty(iter2065))
      {
        iter2065 = this.l0ZappWidgetIds[iter2065];
        output.writeString(iter2065);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

RoundUpPair = function(args) {
  this.roundUp = null;
  this.roundUpMetas = null;
  if (args) {
    if (args.roundUp !== undefined && args.roundUp !== null) {
      this.roundUp = new RoundUp(args.roundUp);
    }
    if (args.roundUpMetas !== undefined && args.roundUpMetas !== null) {
      this.roundUpMetas = Thrift.copyList(args.roundUpMetas, [RoundUpMeta]);
    }
  }
};
RoundUpPair.prototype = {};
RoundUpPair.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.roundUp = new RoundUp();
        this.roundUp.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size2066 = 0;
        var _rtmp32070;
        this.roundUpMetas = [];
        var _etype2069 = 0;
        _rtmp32070 = input.readListBegin();
        _etype2069 = _rtmp32070.etype;
        _size2066 = _rtmp32070.size;
        for (var _i2071 = 0; _i2071 < _size2066; ++_i2071)
        {
          var elem2072 = null;
          elem2072 = new RoundUpMeta();
          elem2072.read(input);
          this.roundUpMetas.push(elem2072);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

RoundUpPair.prototype.write = function(output) {
  output.writeStructBegin('RoundUpPair');
  if (this.roundUp !== null && this.roundUp !== undefined) {
    output.writeFieldBegin('roundUp', Thrift.Type.STRUCT, 1);
    this.roundUp.write(output);
    output.writeFieldEnd();
  }
  if (this.roundUpMetas !== null && this.roundUpMetas !== undefined) {
    output.writeFieldBegin('roundUpMetas', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.roundUpMetas.length);
    for (var iter2073 in this.roundUpMetas)
    {
      if (this.roundUpMetas.hasOwnProperty(iter2073))
      {
        iter2073 = this.roundUpMetas[iter2073];
        iter2073.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TargetingRuleCity = function(args) {
  this.cityId = null;
  this.cityName = null;
  this.state = null;
  if (args) {
    if (args.cityId !== undefined && args.cityId !== null) {
      this.cityId = args.cityId;
    }
    if (args.cityName !== undefined && args.cityName !== null) {
      this.cityName = args.cityName;
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    }
  }
};
TargetingRuleCity.prototype = {};
TargetingRuleCity.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.cityId = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.cityName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.state = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TargetingRuleCity.prototype.write = function(output) {
  output.writeStructBegin('TargetingRuleCity');
  if (this.cityId !== null && this.cityId !== undefined) {
    output.writeFieldBegin('cityId', Thrift.Type.I32, 1);
    output.writeI32(this.cityId);
    output.writeFieldEnd();
  }
  if (this.cityName !== null && this.cityName !== undefined) {
    output.writeFieldBegin('cityName', Thrift.Type.STRING, 2);
    output.writeString(this.cityName);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.I32, 3);
    output.writeI32(this.state);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TargetingRuleGender = function(args) {
  this.genderId = null;
  this.genderName = null;
  if (args) {
    if (args.genderId !== undefined && args.genderId !== null) {
      this.genderId = args.genderId;
    }
    if (args.genderName !== undefined && args.genderName !== null) {
      this.genderName = args.genderName;
    }
  }
};
TargetingRuleGender.prototype = {};
TargetingRuleGender.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.genderId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.genderName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TargetingRuleGender.prototype.write = function(output) {
  output.writeStructBegin('TargetingRuleGender');
  if (this.genderId !== null && this.genderId !== undefined) {
    output.writeFieldBegin('genderId', Thrift.Type.STRING, 1);
    output.writeString(this.genderId);
    output.writeFieldEnd();
  }
  if (this.genderName !== null && this.genderName !== undefined) {
    output.writeFieldBegin('genderName', Thrift.Type.STRING, 2);
    output.writeString(this.genderName);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TargetingRuleHandsetInfo = function(args) {
  this.infoId = null;
  this.brand = null;
  this.model = null;
  this.price = null;
  if (args) {
    if (args.infoId !== undefined && args.infoId !== null) {
      this.infoId = args.infoId;
    }
    if (args.brand !== undefined && args.brand !== null) {
      this.brand = args.brand;
    }
    if (args.model !== undefined && args.model !== null) {
      this.model = args.model;
    }
    if (args.price !== undefined && args.price !== null) {
      this.price = args.price;
    }
  }
};
TargetingRuleHandsetInfo.prototype = {};
TargetingRuleHandsetInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.infoId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.brand = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.model = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.price = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TargetingRuleHandsetInfo.prototype.write = function(output) {
  output.writeStructBegin('TargetingRuleHandsetInfo');
  if (this.infoId !== null && this.infoId !== undefined) {
    output.writeFieldBegin('infoId', Thrift.Type.STRING, 1);
    output.writeString(this.infoId);
    output.writeFieldEnd();
  }
  if (this.brand !== null && this.brand !== undefined) {
    output.writeFieldBegin('brand', Thrift.Type.STRING, 2);
    output.writeString(this.brand);
    output.writeFieldEnd();
  }
  if (this.model !== null && this.model !== undefined) {
    output.writeFieldBegin('model', Thrift.Type.STRING, 3);
    output.writeString(this.model);
    output.writeFieldEnd();
  }
  if (this.price !== null && this.price !== undefined) {
    output.writeFieldBegin('price', Thrift.Type.I32, 4);
    output.writeI32(this.price);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TargetingRuleSdkVersion = function(args) {
  this.sdkVersion = null;
  if (args) {
    if (args.sdkVersion !== undefined && args.sdkVersion !== null) {
      this.sdkVersion = args.sdkVersion;
    }
  }
};
TargetingRuleSdkVersion.prototype = {};
TargetingRuleSdkVersion.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.sdkVersion = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TargetingRuleSdkVersion.prototype.write = function(output) {
  output.writeStructBegin('TargetingRuleSdkVersion');
  if (this.sdkVersion !== null && this.sdkVersion !== undefined) {
    output.writeFieldBegin('sdkVersion', Thrift.Type.I32, 1);
    output.writeI32(this.sdkVersion);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TargetingRuleSegmentQuery = function(args) {
  this.segmentId = null;
  this.segmentName = null;
  if (args) {
    if (args.segmentId !== undefined && args.segmentId !== null) {
      this.segmentId = args.segmentId;
    }
    if (args.segmentName !== undefined && args.segmentName !== null) {
      this.segmentName = args.segmentName;
    }
  }
};
TargetingRuleSegmentQuery.prototype = {};
TargetingRuleSegmentQuery.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.segmentId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.segmentName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TargetingRuleSegmentQuery.prototype.write = function(output) {
  output.writeStructBegin('TargetingRuleSegmentQuery');
  if (this.segmentId !== null && this.segmentId !== undefined) {
    output.writeFieldBegin('segmentId', Thrift.Type.STRING, 1);
    output.writeString(this.segmentId);
    output.writeFieldEnd();
  }
  if (this.segmentName !== null && this.segmentName !== undefined) {
    output.writeFieldBegin('segmentName', Thrift.Type.STRING, 2);
    output.writeString(this.segmentName);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TargetingRuleState = function(args) {
  this.stateId = null;
  this.stateName = null;
  this.country = null;
  if (args) {
    if (args.stateId !== undefined && args.stateId !== null) {
      this.stateId = args.stateId;
    }
    if (args.stateName !== undefined && args.stateName !== null) {
      this.stateName = args.stateName;
    }
    if (args.country !== undefined && args.country !== null) {
      this.country = args.country;
    }
  }
};
TargetingRuleState.prototype = {};
TargetingRuleState.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.stateId = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.stateName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.country = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TargetingRuleState.prototype.write = function(output) {
  output.writeStructBegin('TargetingRuleState');
  if (this.stateId !== null && this.stateId !== undefined) {
    output.writeFieldBegin('stateId', Thrift.Type.I32, 1);
    output.writeI32(this.stateId);
    output.writeFieldEnd();
  }
  if (this.stateName !== null && this.stateName !== undefined) {
    output.writeFieldBegin('stateName', Thrift.Type.STRING, 2);
    output.writeString(this.stateName);
    output.writeFieldEnd();
  }
  if (this.country !== null && this.country !== undefined) {
    output.writeFieldBegin('country', Thrift.Type.I32, 3);
    output.writeI32(this.country);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TargetingRulePriceLimit = function(args) {
  this.min = null;
  this.max = null;
  if (args) {
    if (args.min !== undefined && args.min !== null) {
      this.min = args.min;
    }
    if (args.max !== undefined && args.max !== null) {
      this.max = args.max;
    }
  }
};
TargetingRulePriceLimit.prototype = {};
TargetingRulePriceLimit.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.min = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.max = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TargetingRulePriceLimit.prototype.write = function(output) {
  output.writeStructBegin('TargetingRulePriceLimit');
  if (this.min !== null && this.min !== undefined) {
    output.writeFieldBegin('min', Thrift.Type.I32, 1);
    output.writeI32(this.min);
    output.writeFieldEnd();
  }
  if (this.max !== null && this.max !== undefined) {
    output.writeFieldBegin('max', Thrift.Type.I32, 2);
    output.writeI32(this.max);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TargetingRule = function(args) {
  this.ruleId = null;
  this.cities = null;
  this.states = null;
  this.handsetInfos = null;
  this.segmentQueries = null;
  this.sdkVersions = null;
  this.genders = null;
  this.name = null;
  this.description = null;
  this.status = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.startDate = null;
  this.endDate = null;
  this.devicePriceLimit = null;
  if (args) {
    if (args.ruleId !== undefined && args.ruleId !== null) {
      this.ruleId = args.ruleId;
    }
    if (args.cities !== undefined && args.cities !== null) {
      this.cities = Thrift.copyList(args.cities, [TargetingRuleCity]);
    }
    if (args.states !== undefined && args.states !== null) {
      this.states = Thrift.copyList(args.states, [TargetingRuleState]);
    }
    if (args.handsetInfos !== undefined && args.handsetInfos !== null) {
      this.handsetInfos = Thrift.copyList(args.handsetInfos, [TargetingRuleHandsetInfo]);
    }
    if (args.segmentQueries !== undefined && args.segmentQueries !== null) {
      this.segmentQueries = Thrift.copyList(args.segmentQueries, [TargetingRuleSegmentQuery]);
    }
    if (args.sdkVersions !== undefined && args.sdkVersions !== null) {
      this.sdkVersions = Thrift.copyList(args.sdkVersions, [TargetingRuleSdkVersion]);
    }
    if (args.genders !== undefined && args.genders !== null) {
      this.genders = Thrift.copyList(args.genders, [TargetingRuleGender]);
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.startDate !== undefined && args.startDate !== null) {
      this.startDate = args.startDate;
    }
    if (args.endDate !== undefined && args.endDate !== null) {
      this.endDate = args.endDate;
    }
    if (args.devicePriceLimit !== undefined && args.devicePriceLimit !== null) {
      this.devicePriceLimit = new TargetingRulePriceLimit(args.devicePriceLimit);
    }
  }
};
TargetingRule.prototype = {};
TargetingRule.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.ruleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size2074 = 0;
        var _rtmp32078;
        this.cities = [];
        var _etype2077 = 0;
        _rtmp32078 = input.readListBegin();
        _etype2077 = _rtmp32078.etype;
        _size2074 = _rtmp32078.size;
        for (var _i2079 = 0; _i2079 < _size2074; ++_i2079)
        {
          var elem2080 = null;
          elem2080 = new TargetingRuleCity();
          elem2080.read(input);
          this.cities.push(elem2080);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size2081 = 0;
        var _rtmp32085;
        this.states = [];
        var _etype2084 = 0;
        _rtmp32085 = input.readListBegin();
        _etype2084 = _rtmp32085.etype;
        _size2081 = _rtmp32085.size;
        for (var _i2086 = 0; _i2086 < _size2081; ++_i2086)
        {
          var elem2087 = null;
          elem2087 = new TargetingRuleState();
          elem2087.read(input);
          this.states.push(elem2087);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size2088 = 0;
        var _rtmp32092;
        this.handsetInfos = [];
        var _etype2091 = 0;
        _rtmp32092 = input.readListBegin();
        _etype2091 = _rtmp32092.etype;
        _size2088 = _rtmp32092.size;
        for (var _i2093 = 0; _i2093 < _size2088; ++_i2093)
        {
          var elem2094 = null;
          elem2094 = new TargetingRuleHandsetInfo();
          elem2094.read(input);
          this.handsetInfos.push(elem2094);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.LIST) {
        var _size2095 = 0;
        var _rtmp32099;
        this.segmentQueries = [];
        var _etype2098 = 0;
        _rtmp32099 = input.readListBegin();
        _etype2098 = _rtmp32099.etype;
        _size2095 = _rtmp32099.size;
        for (var _i2100 = 0; _i2100 < _size2095; ++_i2100)
        {
          var elem2101 = null;
          elem2101 = new TargetingRuleSegmentQuery();
          elem2101.read(input);
          this.segmentQueries.push(elem2101);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size2102 = 0;
        var _rtmp32106;
        this.sdkVersions = [];
        var _etype2105 = 0;
        _rtmp32106 = input.readListBegin();
        _etype2105 = _rtmp32106.etype;
        _size2102 = _rtmp32106.size;
        for (var _i2107 = 0; _i2107 < _size2102; ++_i2107)
        {
          var elem2108 = null;
          elem2108 = new TargetingRuleSdkVersion();
          elem2108.read(input);
          this.sdkVersions.push(elem2108);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size2109 = 0;
        var _rtmp32113;
        this.genders = [];
        var _etype2112 = 0;
        _rtmp32113 = input.readListBegin();
        _etype2112 = _rtmp32113.etype;
        _size2109 = _rtmp32113.size;
        for (var _i2114 = 0; _i2114 < _size2109; ++_i2114)
        {
          var elem2115 = null;
          elem2115 = new TargetingRuleGender();
          elem2115.read(input);
          this.genders.push(elem2115);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.STRING) {
        this.startDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.endDate = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRUCT) {
        this.devicePriceLimit = new TargetingRulePriceLimit();
        this.devicePriceLimit.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TargetingRule.prototype.write = function(output) {
  output.writeStructBegin('TargetingRule');
  if (this.ruleId !== null && this.ruleId !== undefined) {
    output.writeFieldBegin('ruleId', Thrift.Type.STRING, 1);
    output.writeString(this.ruleId);
    output.writeFieldEnd();
  }
  if (this.cities !== null && this.cities !== undefined) {
    output.writeFieldBegin('cities', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.cities.length);
    for (var iter2116 in this.cities)
    {
      if (this.cities.hasOwnProperty(iter2116))
      {
        iter2116 = this.cities[iter2116];
        iter2116.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.states !== null && this.states !== undefined) {
    output.writeFieldBegin('states', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.states.length);
    for (var iter2117 in this.states)
    {
      if (this.states.hasOwnProperty(iter2117))
      {
        iter2117 = this.states[iter2117];
        iter2117.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.handsetInfos !== null && this.handsetInfos !== undefined) {
    output.writeFieldBegin('handsetInfos', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.STRUCT, this.handsetInfos.length);
    for (var iter2118 in this.handsetInfos)
    {
      if (this.handsetInfos.hasOwnProperty(iter2118))
      {
        iter2118 = this.handsetInfos[iter2118];
        iter2118.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.segmentQueries !== null && this.segmentQueries !== undefined) {
    output.writeFieldBegin('segmentQueries', Thrift.Type.LIST, 5);
    output.writeListBegin(Thrift.Type.STRUCT, this.segmentQueries.length);
    for (var iter2119 in this.segmentQueries)
    {
      if (this.segmentQueries.hasOwnProperty(iter2119))
      {
        iter2119 = this.segmentQueries[iter2119];
        iter2119.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.sdkVersions !== null && this.sdkVersions !== undefined) {
    output.writeFieldBegin('sdkVersions', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.STRUCT, this.sdkVersions.length);
    for (var iter2120 in this.sdkVersions)
    {
      if (this.sdkVersions.hasOwnProperty(iter2120))
      {
        iter2120 = this.sdkVersions[iter2120];
        iter2120.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.genders !== null && this.genders !== undefined) {
    output.writeFieldBegin('genders', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRUCT, this.genders.length);
    for (var iter2121 in this.genders)
    {
      if (this.genders.hasOwnProperty(iter2121))
      {
        iter2121 = this.genders[iter2121];
        iter2121.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 8);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 9);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 10);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 11);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 12);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.startDate !== null && this.startDate !== undefined) {
    output.writeFieldBegin('startDate', Thrift.Type.STRING, 13);
    output.writeString(this.startDate);
    output.writeFieldEnd();
  }
  if (this.endDate !== null && this.endDate !== undefined) {
    output.writeFieldBegin('endDate', Thrift.Type.STRING, 14);
    output.writeString(this.endDate);
    output.writeFieldEnd();
  }
  if (this.devicePriceLimit !== null && this.devicePriceLimit !== undefined) {
    output.writeFieldBegin('devicePriceLimit', Thrift.Type.STRUCT, 15);
    this.devicePriceLimit.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

MlibCohort = function(args) {
  this.oem = null;
  this.app = null;
  this.surface = null;
  this.userId = null;
  this.gpId = null;
  this.version = null;
  if (args) {
    if (args.oem !== undefined && args.oem !== null) {
      this.oem = args.oem;
    }
    if (args.app !== undefined && args.app !== null) {
      this.app = args.app;
    }
    if (args.surface !== undefined && args.surface !== null) {
      this.surface = args.surface;
    }
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    }
    if (args.gpId !== undefined && args.gpId !== null) {
      this.gpId = args.gpId;
    }
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
  }
};
MlibCohort.prototype = {};
MlibCohort.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.oem = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.app = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.surface = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.gpId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.version = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

MlibCohort.prototype.write = function(output) {
  output.writeStructBegin('MlibCohort');
  if (this.oem !== null && this.oem !== undefined) {
    output.writeFieldBegin('oem', Thrift.Type.STRING, 1);
    output.writeString(this.oem);
    output.writeFieldEnd();
  }
  if (this.app !== null && this.app !== undefined) {
    output.writeFieldBegin('app', Thrift.Type.STRING, 2);
    output.writeString(this.app);
    output.writeFieldEnd();
  }
  if (this.surface !== null && this.surface !== undefined) {
    output.writeFieldBegin('surface', Thrift.Type.STRING, 3);
    output.writeString(this.surface);
    output.writeFieldEnd();
  }
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 4);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.gpId !== null && this.gpId !== undefined) {
    output.writeFieldBegin('gpId', Thrift.Type.STRING, 5);
    output.writeString(this.gpId);
    output.writeFieldEnd();
  }
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.STRING, 6);
    output.writeString(this.version);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

MlibCohortResponse = function(args) {
  this.path = null;
  this.version = null;
  if (args) {
    if (args.path !== undefined && args.path !== null) {
      this.path = args.path;
    }
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
  }
};
MlibCohortResponse.prototype = {};
MlibCohortResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.path = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.version = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

MlibCohortResponse.prototype.write = function(output) {
  output.writeStructBegin('MlibCohortResponse');
  if (this.path !== null && this.path !== undefined) {
    output.writeFieldBegin('path', Thrift.Type.STRING, 1);
    output.writeString(this.path);
    output.writeFieldEnd();
  }
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.STRING, 2);
    output.writeString(this.version);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

HandsetInfo = function(args) {
  this.model = null;
  this.price = null;
  this.fpSensorType = null;
  if (args) {
    if (args.model !== undefined && args.model !== null) {
      this.model = args.model;
    }
    if (args.price !== undefined && args.price !== null) {
      this.price = args.price;
    }
    if (args.fpSensorType !== undefined && args.fpSensorType !== null) {
      this.fpSensorType = args.fpSensorType;
    }
  }
};
HandsetInfo.prototype = {};
HandsetInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.model = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.price = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.fpSensorType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

HandsetInfo.prototype.write = function(output) {
  output.writeStructBegin('HandsetInfo');
  if (this.model !== null && this.model !== undefined) {
    output.writeFieldBegin('model', Thrift.Type.STRING, 1);
    output.writeString(this.model);
    output.writeFieldEnd();
  }
  if (this.price !== null && this.price !== undefined) {
    output.writeFieldBegin('price', Thrift.Type.I32, 2);
    output.writeI32(this.price);
    output.writeFieldEnd();
  }
  if (this.fpSensorType !== null && this.fpSensorType !== undefined) {
    output.writeFieldBegin('fpSensorType', Thrift.Type.STRING, 3);
    output.writeString(this.fpSensorType);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdPersonalization = function(args) {
  this.bundleId = null;
  this.sourceName = null;
  if (args) {
    if (args.bundleId !== undefined && args.bundleId !== null) {
      this.bundleId = args.bundleId;
    }
    if (args.sourceName !== undefined && args.sourceName !== null) {
      this.sourceName = Thrift.copyList(args.sourceName, [null]);
    }
  }
};
AdPersonalization.prototype = {};
AdPersonalization.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.bundleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size2122 = 0;
        var _rtmp32126;
        this.sourceName = [];
        var _etype2125 = 0;
        _rtmp32126 = input.readListBegin();
        _etype2125 = _rtmp32126.etype;
        _size2122 = _rtmp32126.size;
        for (var _i2127 = 0; _i2127 < _size2122; ++_i2127)
        {
          var elem2128 = null;
          elem2128 = input.readString().value;
          this.sourceName.push(elem2128);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdPersonalization.prototype.write = function(output) {
  output.writeStructBegin('AdPersonalization');
  if (this.bundleId !== null && this.bundleId !== undefined) {
    output.writeFieldBegin('bundleId', Thrift.Type.STRING, 1);
    output.writeString(this.bundleId);
    output.writeFieldEnd();
  }
  if (this.sourceName !== null && this.sourceName !== undefined) {
    output.writeFieldBegin('sourceName', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.sourceName.length);
    for (var iter2129 in this.sourceName)
    {
      if (this.sourceName.hasOwnProperty(iter2129))
      {
        iter2129 = this.sourceName[iter2129];
        output.writeString(iter2129);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdUnits = function(args) {
  this.au = null;
  this.eScore = null;
  if (args) {
    if (args.au !== undefined && args.au !== null) {
      this.au = args.au;
    }
    if (args.eScore !== undefined && args.eScore !== null) {
      this.eScore = args.eScore;
    }
  }
};
AdUnits.prototype = {};
AdUnits.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.au = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.eScore = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdUnits.prototype.write = function(output) {
  output.writeStructBegin('AdUnits');
  if (this.au !== null && this.au !== undefined) {
    output.writeFieldBegin('au', Thrift.Type.STRING, 1);
    output.writeString(this.au);
    output.writeFieldEnd();
  }
  if (this.eScore !== null && this.eScore !== undefined) {
    output.writeFieldBegin('eScore', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.eScore);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AdMonConfig = function(args) {
  this.configId = null;
  this.region = null;
  this.apiKey = null;
  this.sdkRange = null;
  this.gmaEnable = null;
  this.iwgs = null;
  this.placementName = null;
  this.format = null;
  this.maxSupportedAspectRatio = null;
  this.intervalRange = null;
  this.retryPolicyEnable = null;
  this.retryCount = null;
  this.coolOffPeriod = null;
  this.ctaPrefix = null;
  this.eCpm = null;
  this.eCpm360 = null;
  this.sampleAppAdUnit = null;
  this.appAdUnit = null;
  this.preferredAspectRatio = null;
  this.videoAds = null;
  this.heu = null;
  this.meu = null;
  this.leuH = null;
  this.leuL = null;
  this.defaultt = null;
  this.uccEnable = null;
  this.dailyCounter = null;
  this.lifeTimeCounter = null;
  this.rejectedCookiesPeriod = null;
  this.modifyCookiesPeriod = null;
  this.title = null;
  this.modifyTitle = null;
  this.shortText = null;
  this.fullText = null;
  this.firstPartyCookieText = null;
  this.thirdPartyCookieText = null;
  this.isDefaultCookiesAccepted = null;
  this.manageSettingsEnabled = null;
  this.cookiesSettingsTitle = null;
  this.cookiesSettingsSubTitle = null;
  this.loaderScreenTimeInMillis = null;
  this.cookieConsentDurationInDays = null;
  this.dismissCookiesPeriod = null;
  this.removeCookiesPeriodicallyWebView = null;
  this.cookiesDurationDaysWebView = null;
  this.thirdPartyCookiesEnabledWebView = null;
  this.enableWebViewCache = null;
  this.webViewCacheDurationInDays = null;
  this.removeCookiesPeriodicallyWebViewBypassConsent = null;
  this.userConsentEnable = null;
  this.cookiesDurationDaysWebViewBypassConsent = null;
  this.thirdPartyCookiesEnabledWebViewBypassConsent = null;
  this.acceptButtonText = null;
  this.modifyButtonText = null;
  this.firstPartyCookieSubtitle = null;
  this.thirdPartyCookieSubtitle = null;
  this.isAdditionalCookieToggled = null;
  this.saveExitCookieButtonText = null;
  this.clearAllButtonText = null;
  this.modifyButtonEnabled = null;
  this.dpdpaEnable = null;
  this.cookiePolicyUrl = null;
  this.adde = null;
  this.timeToRefresh = null;
  this.overlayPlacementName = null;
  this.overlayAdUnits = null;
  this.firstAdRequestPosition = null;
  if (args) {
    if (args.configId !== undefined && args.configId !== null) {
      this.configId = args.configId;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = Thrift.copyList(args.region, [null]);
    }
    if (args.apiKey !== undefined && args.apiKey !== null) {
      this.apiKey = args.apiKey;
    }
    if (args.sdkRange !== undefined && args.sdkRange !== null) {
      this.sdkRange = Thrift.copyList(args.sdkRange, [null]);
    }
    if (args.gmaEnable !== undefined && args.gmaEnable !== null) {
      this.gmaEnable = args.gmaEnable;
    }
    if (args.iwgs !== undefined && args.iwgs !== null) {
      this.iwgs = args.iwgs;
    }
    if (args.placementName !== undefined && args.placementName !== null) {
      this.placementName = args.placementName;
    }
    if (args.format !== undefined && args.format !== null) {
      this.format = args.format;
    }
    if (args.maxSupportedAspectRatio !== undefined && args.maxSupportedAspectRatio !== null) {
      this.maxSupportedAspectRatio = args.maxSupportedAspectRatio;
    }
    if (args.intervalRange !== undefined && args.intervalRange !== null) {
      this.intervalRange = Thrift.copyList(args.intervalRange, [null]);
    }
    if (args.retryPolicyEnable !== undefined && args.retryPolicyEnable !== null) {
      this.retryPolicyEnable = args.retryPolicyEnable;
    }
    if (args.retryCount !== undefined && args.retryCount !== null) {
      this.retryCount = args.retryCount;
    }
    if (args.coolOffPeriod !== undefined && args.coolOffPeriod !== null) {
      this.coolOffPeriod = args.coolOffPeriod;
    }
    if (args.ctaPrefix !== undefined && args.ctaPrefix !== null) {
      this.ctaPrefix = args.ctaPrefix;
    }
    if (args.eCpm !== undefined && args.eCpm !== null) {
      this.eCpm = args.eCpm;
    }
    if (args.eCpm360 !== undefined && args.eCpm360 !== null) {
      this.eCpm360 = args.eCpm360;
    }
    if (args.sampleAppAdUnit !== undefined && args.sampleAppAdUnit !== null) {
      this.sampleAppAdUnit = args.sampleAppAdUnit;
    }
    if (args.appAdUnit !== undefined && args.appAdUnit !== null) {
      this.appAdUnit = args.appAdUnit;
    }
    if (args.preferredAspectRatio !== undefined && args.preferredAspectRatio !== null) {
      this.preferredAspectRatio = args.preferredAspectRatio;
    }
    if (args.videoAds !== undefined && args.videoAds !== null) {
      this.videoAds = args.videoAds;
    }
    if (args.heu !== undefined && args.heu !== null) {
      this.heu = args.heu;
    }
    if (args.meu !== undefined && args.meu !== null) {
      this.meu = args.meu;
    }
    if (args.leuH !== undefined && args.leuH !== null) {
      this.leuH = args.leuH;
    }
    if (args.leuL !== undefined && args.leuL !== null) {
      this.leuL = args.leuL;
    }
    if (args.defaultt !== undefined && args.defaultt !== null) {
      this.defaultt = args.defaultt;
    }
    if (args.uccEnable !== undefined && args.uccEnable !== null) {
      this.uccEnable = args.uccEnable;
    }
    if (args.dailyCounter !== undefined && args.dailyCounter !== null) {
      this.dailyCounter = args.dailyCounter;
    }
    if (args.lifeTimeCounter !== undefined && args.lifeTimeCounter !== null) {
      this.lifeTimeCounter = args.lifeTimeCounter;
    }
    if (args.rejectedCookiesPeriod !== undefined && args.rejectedCookiesPeriod !== null) {
      this.rejectedCookiesPeriod = args.rejectedCookiesPeriod;
    }
    if (args.modifyCookiesPeriod !== undefined && args.modifyCookiesPeriod !== null) {
      this.modifyCookiesPeriod = args.modifyCookiesPeriod;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.modifyTitle !== undefined && args.modifyTitle !== null) {
      this.modifyTitle = args.modifyTitle;
    }
    if (args.shortText !== undefined && args.shortText !== null) {
      this.shortText = args.shortText;
    }
    if (args.fullText !== undefined && args.fullText !== null) {
      this.fullText = args.fullText;
    }
    if (args.firstPartyCookieText !== undefined && args.firstPartyCookieText !== null) {
      this.firstPartyCookieText = args.firstPartyCookieText;
    }
    if (args.thirdPartyCookieText !== undefined && args.thirdPartyCookieText !== null) {
      this.thirdPartyCookieText = args.thirdPartyCookieText;
    }
    if (args.isDefaultCookiesAccepted !== undefined && args.isDefaultCookiesAccepted !== null) {
      this.isDefaultCookiesAccepted = args.isDefaultCookiesAccepted;
    }
    if (args.manageSettingsEnabled !== undefined && args.manageSettingsEnabled !== null) {
      this.manageSettingsEnabled = args.manageSettingsEnabled;
    }
    if (args.cookiesSettingsTitle !== undefined && args.cookiesSettingsTitle !== null) {
      this.cookiesSettingsTitle = args.cookiesSettingsTitle;
    }
    if (args.cookiesSettingsSubTitle !== undefined && args.cookiesSettingsSubTitle !== null) {
      this.cookiesSettingsSubTitle = args.cookiesSettingsSubTitle;
    }
    if (args.loaderScreenTimeInMillis !== undefined && args.loaderScreenTimeInMillis !== null) {
      this.loaderScreenTimeInMillis = args.loaderScreenTimeInMillis;
    }
    if (args.cookieConsentDurationInDays !== undefined && args.cookieConsentDurationInDays !== null) {
      this.cookieConsentDurationInDays = args.cookieConsentDurationInDays;
    }
    if (args.dismissCookiesPeriod !== undefined && args.dismissCookiesPeriod !== null) {
      this.dismissCookiesPeriod = args.dismissCookiesPeriod;
    }
    if (args.removeCookiesPeriodicallyWebView !== undefined && args.removeCookiesPeriodicallyWebView !== null) {
      this.removeCookiesPeriodicallyWebView = args.removeCookiesPeriodicallyWebView;
    }
    if (args.cookiesDurationDaysWebView !== undefined && args.cookiesDurationDaysWebView !== null) {
      this.cookiesDurationDaysWebView = args.cookiesDurationDaysWebView;
    }
    if (args.thirdPartyCookiesEnabledWebView !== undefined && args.thirdPartyCookiesEnabledWebView !== null) {
      this.thirdPartyCookiesEnabledWebView = args.thirdPartyCookiesEnabledWebView;
    }
    if (args.enableWebViewCache !== undefined && args.enableWebViewCache !== null) {
      this.enableWebViewCache = args.enableWebViewCache;
    }
    if (args.webViewCacheDurationInDays !== undefined && args.webViewCacheDurationInDays !== null) {
      this.webViewCacheDurationInDays = args.webViewCacheDurationInDays;
    }
    if (args.removeCookiesPeriodicallyWebViewBypassConsent !== undefined && args.removeCookiesPeriodicallyWebViewBypassConsent !== null) {
      this.removeCookiesPeriodicallyWebViewBypassConsent = args.removeCookiesPeriodicallyWebViewBypassConsent;
    }
    if (args.userConsentEnable !== undefined && args.userConsentEnable !== null) {
      this.userConsentEnable = args.userConsentEnable;
    }
    if (args.cookiesDurationDaysWebViewBypassConsent !== undefined && args.cookiesDurationDaysWebViewBypassConsent !== null) {
      this.cookiesDurationDaysWebViewBypassConsent = args.cookiesDurationDaysWebViewBypassConsent;
    }
    if (args.thirdPartyCookiesEnabledWebViewBypassConsent !== undefined && args.thirdPartyCookiesEnabledWebViewBypassConsent !== null) {
      this.thirdPartyCookiesEnabledWebViewBypassConsent = args.thirdPartyCookiesEnabledWebViewBypassConsent;
    }
    if (args.acceptButtonText !== undefined && args.acceptButtonText !== null) {
      this.acceptButtonText = args.acceptButtonText;
    }
    if (args.modifyButtonText !== undefined && args.modifyButtonText !== null) {
      this.modifyButtonText = args.modifyButtonText;
    }
    if (args.firstPartyCookieSubtitle !== undefined && args.firstPartyCookieSubtitle !== null) {
      this.firstPartyCookieSubtitle = args.firstPartyCookieSubtitle;
    }
    if (args.thirdPartyCookieSubtitle !== undefined && args.thirdPartyCookieSubtitle !== null) {
      this.thirdPartyCookieSubtitle = args.thirdPartyCookieSubtitle;
    }
    if (args.isAdditionalCookieToggled !== undefined && args.isAdditionalCookieToggled !== null) {
      this.isAdditionalCookieToggled = args.isAdditionalCookieToggled;
    }
    if (args.saveExitCookieButtonText !== undefined && args.saveExitCookieButtonText !== null) {
      this.saveExitCookieButtonText = args.saveExitCookieButtonText;
    }
    if (args.clearAllButtonText !== undefined && args.clearAllButtonText !== null) {
      this.clearAllButtonText = args.clearAllButtonText;
    }
    if (args.modifyButtonEnabled !== undefined && args.modifyButtonEnabled !== null) {
      this.modifyButtonEnabled = args.modifyButtonEnabled;
    }
    if (args.dpdpaEnable !== undefined && args.dpdpaEnable !== null) {
      this.dpdpaEnable = args.dpdpaEnable;
    }
    if (args.cookiePolicyUrl !== undefined && args.cookiePolicyUrl !== null) {
      this.cookiePolicyUrl = args.cookiePolicyUrl;
    }
    if (args.adde !== undefined && args.adde !== null) {
      this.adde = args.adde;
    }
    if (args.timeToRefresh !== undefined && args.timeToRefresh !== null) {
      this.timeToRefresh = args.timeToRefresh;
    }
    if (args.overlayPlacementName !== undefined && args.overlayPlacementName !== null) {
      this.overlayPlacementName = args.overlayPlacementName;
    }
    if (args.overlayAdUnits !== undefined && args.overlayAdUnits !== null) {
      this.overlayAdUnits = Thrift.copyList(args.overlayAdUnits, [AdUnits]);
    }
    if (args.firstAdRequestPosition !== undefined && args.firstAdRequestPosition !== null) {
      this.firstAdRequestPosition = args.firstAdRequestPosition;
    }
  }
};
AdMonConfig.prototype = {};
AdMonConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.configId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size2130 = 0;
        var _rtmp32134;
        this.region = [];
        var _etype2133 = 0;
        _rtmp32134 = input.readListBegin();
        _etype2133 = _rtmp32134.etype;
        _size2130 = _rtmp32134.size;
        for (var _i2135 = 0; _i2135 < _size2130; ++_i2135)
        {
          var elem2136 = null;
          elem2136 = input.readString().value;
          this.region.push(elem2136);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.apiKey = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.LIST) {
        var _size2137 = 0;
        var _rtmp32141;
        this.sdkRange = [];
        var _etype2140 = 0;
        _rtmp32141 = input.readListBegin();
        _etype2140 = _rtmp32141.etype;
        _size2137 = _rtmp32141.size;
        for (var _i2142 = 0; _i2142 < _size2137; ++_i2142)
        {
          var elem2143 = null;
          elem2143 = input.readI32().value;
          this.sdkRange.push(elem2143);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.gmaEnable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.iwgs = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.placementName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.format = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.DOUBLE) {
        this.maxSupportedAspectRatio = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.LIST) {
        var _size2144 = 0;
        var _rtmp32148;
        this.intervalRange = [];
        var _etype2147 = 0;
        _rtmp32148 = input.readListBegin();
        _etype2147 = _rtmp32148.etype;
        _size2144 = _rtmp32148.size;
        for (var _i2149 = 0; _i2149 < _size2144; ++_i2149)
        {
          var elem2150 = null;
          elem2150 = input.readI32().value;
          this.intervalRange.push(elem2150);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.BOOL) {
        this.retryPolicyEnable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I32) {
        this.retryCount = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.I32) {
        this.coolOffPeriod = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.STRING) {
        this.ctaPrefix = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.DOUBLE) {
        this.eCpm = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.DOUBLE) {
        this.eCpm360 = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.sampleAppAdUnit = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.appAdUnit = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.I32) {
        this.preferredAspectRatio = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.BOOL) {
        this.videoAds = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.I32) {
        this.heu = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.I32) {
        this.meu = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.I32) {
        this.leuH = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.I32) {
        this.leuL = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.I32) {
        this.defaultt = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.BOOL) {
        this.uccEnable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.I32) {
        this.dailyCounter = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.I32) {
        this.lifeTimeCounter = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.I32) {
        this.rejectedCookiesPeriod = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.I32) {
        this.modifyCookiesPeriod = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRING) {
        this.modifyTitle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.STRING) {
        this.shortText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.STRING) {
        this.fullText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.STRING) {
        this.firstPartyCookieText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 36:
      if (ftype == Thrift.Type.STRING) {
        this.thirdPartyCookieText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 37:
      if (ftype == Thrift.Type.BOOL) {
        this.isDefaultCookiesAccepted = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 38:
      if (ftype == Thrift.Type.BOOL) {
        this.manageSettingsEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 39:
      if (ftype == Thrift.Type.STRING) {
        this.cookiesSettingsTitle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 40:
      if (ftype == Thrift.Type.STRING) {
        this.cookiesSettingsSubTitle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 41:
      if (ftype == Thrift.Type.I32) {
        this.loaderScreenTimeInMillis = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 42:
      if (ftype == Thrift.Type.I32) {
        this.cookieConsentDurationInDays = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 43:
      if (ftype == Thrift.Type.I32) {
        this.dismissCookiesPeriod = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 44:
      if (ftype == Thrift.Type.BOOL) {
        this.removeCookiesPeriodicallyWebView = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 45:
      if (ftype == Thrift.Type.I32) {
        this.cookiesDurationDaysWebView = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 46:
      if (ftype == Thrift.Type.BOOL) {
        this.thirdPartyCookiesEnabledWebView = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 47:
      if (ftype == Thrift.Type.BOOL) {
        this.enableWebViewCache = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 48:
      if (ftype == Thrift.Type.I32) {
        this.webViewCacheDurationInDays = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 49:
      if (ftype == Thrift.Type.BOOL) {
        this.removeCookiesPeriodicallyWebViewBypassConsent = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 50:
      if (ftype == Thrift.Type.BOOL) {
        this.userConsentEnable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 51:
      if (ftype == Thrift.Type.I32) {
        this.cookiesDurationDaysWebViewBypassConsent = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 52:
      if (ftype == Thrift.Type.BOOL) {
        this.thirdPartyCookiesEnabledWebViewBypassConsent = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 53:
      if (ftype == Thrift.Type.STRING) {
        this.acceptButtonText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 54:
      if (ftype == Thrift.Type.STRING) {
        this.modifyButtonText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 55:
      if (ftype == Thrift.Type.STRING) {
        this.firstPartyCookieSubtitle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 56:
      if (ftype == Thrift.Type.STRING) {
        this.thirdPartyCookieSubtitle = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 57:
      if (ftype == Thrift.Type.BOOL) {
        this.isAdditionalCookieToggled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 58:
      if (ftype == Thrift.Type.STRING) {
        this.saveExitCookieButtonText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 59:
      if (ftype == Thrift.Type.STRING) {
        this.clearAllButtonText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 60:
      if (ftype == Thrift.Type.BOOL) {
        this.modifyButtonEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 61:
      if (ftype == Thrift.Type.BOOL) {
        this.dpdpaEnable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 62:
      if (ftype == Thrift.Type.STRING) {
        this.cookiePolicyUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 63:
      if (ftype == Thrift.Type.BOOL) {
        this.adde = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 64:
      if (ftype == Thrift.Type.I32) {
        this.timeToRefresh = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 65:
      if (ftype == Thrift.Type.STRING) {
        this.overlayPlacementName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 66:
      if (ftype == Thrift.Type.LIST) {
        var _size2151 = 0;
        var _rtmp32155;
        this.overlayAdUnits = [];
        var _etype2154 = 0;
        _rtmp32155 = input.readListBegin();
        _etype2154 = _rtmp32155.etype;
        _size2151 = _rtmp32155.size;
        for (var _i2156 = 0; _i2156 < _size2151; ++_i2156)
        {
          var elem2157 = null;
          elem2157 = new AdUnits();
          elem2157.read(input);
          this.overlayAdUnits.push(elem2157);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 67:
      if (ftype == Thrift.Type.I32) {
        this.firstAdRequestPosition = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AdMonConfig.prototype.write = function(output) {
  output.writeStructBegin('AdMonConfig');
  if (this.configId !== null && this.configId !== undefined) {
    output.writeFieldBegin('configId', Thrift.Type.STRING, 1);
    output.writeString(this.configId);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.region.length);
    for (var iter2158 in this.region)
    {
      if (this.region.hasOwnProperty(iter2158))
      {
        iter2158 = this.region[iter2158];
        output.writeString(iter2158);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.apiKey !== null && this.apiKey !== undefined) {
    output.writeFieldBegin('apiKey', Thrift.Type.STRING, 3);
    output.writeString(this.apiKey);
    output.writeFieldEnd();
  }
  if (this.sdkRange !== null && this.sdkRange !== undefined) {
    output.writeFieldBegin('sdkRange', Thrift.Type.LIST, 4);
    output.writeListBegin(Thrift.Type.I32, this.sdkRange.length);
    for (var iter2159 in this.sdkRange)
    {
      if (this.sdkRange.hasOwnProperty(iter2159))
      {
        iter2159 = this.sdkRange[iter2159];
        output.writeI32(iter2159);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.gmaEnable !== null && this.gmaEnable !== undefined) {
    output.writeFieldBegin('gmaEnable', Thrift.Type.BOOL, 5);
    output.writeBool(this.gmaEnable);
    output.writeFieldEnd();
  }
  if (this.iwgs !== null && this.iwgs !== undefined) {
    output.writeFieldBegin('iwgs', Thrift.Type.BOOL, 6);
    output.writeBool(this.iwgs);
    output.writeFieldEnd();
  }
  if (this.placementName !== null && this.placementName !== undefined) {
    output.writeFieldBegin('placementName', Thrift.Type.STRING, 7);
    output.writeString(this.placementName);
    output.writeFieldEnd();
  }
  if (this.format !== null && this.format !== undefined) {
    output.writeFieldBegin('format', Thrift.Type.I32, 8);
    output.writeI32(this.format);
    output.writeFieldEnd();
  }
  if (this.maxSupportedAspectRatio !== null && this.maxSupportedAspectRatio !== undefined) {
    output.writeFieldBegin('maxSupportedAspectRatio', Thrift.Type.DOUBLE, 9);
    output.writeDouble(this.maxSupportedAspectRatio);
    output.writeFieldEnd();
  }
  if (this.intervalRange !== null && this.intervalRange !== undefined) {
    output.writeFieldBegin('intervalRange', Thrift.Type.LIST, 10);
    output.writeListBegin(Thrift.Type.I32, this.intervalRange.length);
    for (var iter2160 in this.intervalRange)
    {
      if (this.intervalRange.hasOwnProperty(iter2160))
      {
        iter2160 = this.intervalRange[iter2160];
        output.writeI32(iter2160);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.retryPolicyEnable !== null && this.retryPolicyEnable !== undefined) {
    output.writeFieldBegin('retryPolicyEnable', Thrift.Type.BOOL, 11);
    output.writeBool(this.retryPolicyEnable);
    output.writeFieldEnd();
  }
  if (this.retryCount !== null && this.retryCount !== undefined) {
    output.writeFieldBegin('retryCount', Thrift.Type.I32, 12);
    output.writeI32(this.retryCount);
    output.writeFieldEnd();
  }
  if (this.coolOffPeriod !== null && this.coolOffPeriod !== undefined) {
    output.writeFieldBegin('coolOffPeriod', Thrift.Type.I32, 13);
    output.writeI32(this.coolOffPeriod);
    output.writeFieldEnd();
  }
  if (this.ctaPrefix !== null && this.ctaPrefix !== undefined) {
    output.writeFieldBegin('ctaPrefix', Thrift.Type.STRING, 14);
    output.writeString(this.ctaPrefix);
    output.writeFieldEnd();
  }
  if (this.eCpm !== null && this.eCpm !== undefined) {
    output.writeFieldBegin('eCpm', Thrift.Type.DOUBLE, 15);
    output.writeDouble(this.eCpm);
    output.writeFieldEnd();
  }
  if (this.eCpm360 !== null && this.eCpm360 !== undefined) {
    output.writeFieldBegin('eCpm360', Thrift.Type.DOUBLE, 16);
    output.writeDouble(this.eCpm360);
    output.writeFieldEnd();
  }
  if (this.sampleAppAdUnit !== null && this.sampleAppAdUnit !== undefined) {
    output.writeFieldBegin('sampleAppAdUnit', Thrift.Type.STRING, 17);
    output.writeString(this.sampleAppAdUnit);
    output.writeFieldEnd();
  }
  if (this.appAdUnit !== null && this.appAdUnit !== undefined) {
    output.writeFieldBegin('appAdUnit', Thrift.Type.STRING, 18);
    output.writeString(this.appAdUnit);
    output.writeFieldEnd();
  }
  if (this.preferredAspectRatio !== null && this.preferredAspectRatio !== undefined) {
    output.writeFieldBegin('preferredAspectRatio', Thrift.Type.I32, 19);
    output.writeI32(this.preferredAspectRatio);
    output.writeFieldEnd();
  }
  if (this.videoAds !== null && this.videoAds !== undefined) {
    output.writeFieldBegin('videoAds', Thrift.Type.BOOL, 20);
    output.writeBool(this.videoAds);
    output.writeFieldEnd();
  }
  if (this.heu !== null && this.heu !== undefined) {
    output.writeFieldBegin('heu', Thrift.Type.I32, 21);
    output.writeI32(this.heu);
    output.writeFieldEnd();
  }
  if (this.meu !== null && this.meu !== undefined) {
    output.writeFieldBegin('meu', Thrift.Type.I32, 22);
    output.writeI32(this.meu);
    output.writeFieldEnd();
  }
  if (this.leuH !== null && this.leuH !== undefined) {
    output.writeFieldBegin('leuH', Thrift.Type.I32, 23);
    output.writeI32(this.leuH);
    output.writeFieldEnd();
  }
  if (this.leuL !== null && this.leuL !== undefined) {
    output.writeFieldBegin('leuL', Thrift.Type.I32, 24);
    output.writeI32(this.leuL);
    output.writeFieldEnd();
  }
  if (this.defaultt !== null && this.defaultt !== undefined) {
    output.writeFieldBegin('defaultt', Thrift.Type.I32, 25);
    output.writeI32(this.defaultt);
    output.writeFieldEnd();
  }
  if (this.uccEnable !== null && this.uccEnable !== undefined) {
    output.writeFieldBegin('uccEnable', Thrift.Type.BOOL, 26);
    output.writeBool(this.uccEnable);
    output.writeFieldEnd();
  }
  if (this.dailyCounter !== null && this.dailyCounter !== undefined) {
    output.writeFieldBegin('dailyCounter', Thrift.Type.I32, 27);
    output.writeI32(this.dailyCounter);
    output.writeFieldEnd();
  }
  if (this.lifeTimeCounter !== null && this.lifeTimeCounter !== undefined) {
    output.writeFieldBegin('lifeTimeCounter', Thrift.Type.I32, 28);
    output.writeI32(this.lifeTimeCounter);
    output.writeFieldEnd();
  }
  if (this.rejectedCookiesPeriod !== null && this.rejectedCookiesPeriod !== undefined) {
    output.writeFieldBegin('rejectedCookiesPeriod', Thrift.Type.I32, 29);
    output.writeI32(this.rejectedCookiesPeriod);
    output.writeFieldEnd();
  }
  if (this.modifyCookiesPeriod !== null && this.modifyCookiesPeriod !== undefined) {
    output.writeFieldBegin('modifyCookiesPeriod', Thrift.Type.I32, 30);
    output.writeI32(this.modifyCookiesPeriod);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 31);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.modifyTitle !== null && this.modifyTitle !== undefined) {
    output.writeFieldBegin('modifyTitle', Thrift.Type.STRING, 32);
    output.writeString(this.modifyTitle);
    output.writeFieldEnd();
  }
  if (this.shortText !== null && this.shortText !== undefined) {
    output.writeFieldBegin('shortText', Thrift.Type.STRING, 33);
    output.writeString(this.shortText);
    output.writeFieldEnd();
  }
  if (this.fullText !== null && this.fullText !== undefined) {
    output.writeFieldBegin('fullText', Thrift.Type.STRING, 34);
    output.writeString(this.fullText);
    output.writeFieldEnd();
  }
  if (this.firstPartyCookieText !== null && this.firstPartyCookieText !== undefined) {
    output.writeFieldBegin('firstPartyCookieText', Thrift.Type.STRING, 35);
    output.writeString(this.firstPartyCookieText);
    output.writeFieldEnd();
  }
  if (this.thirdPartyCookieText !== null && this.thirdPartyCookieText !== undefined) {
    output.writeFieldBegin('thirdPartyCookieText', Thrift.Type.STRING, 36);
    output.writeString(this.thirdPartyCookieText);
    output.writeFieldEnd();
  }
  if (this.isDefaultCookiesAccepted !== null && this.isDefaultCookiesAccepted !== undefined) {
    output.writeFieldBegin('isDefaultCookiesAccepted', Thrift.Type.BOOL, 37);
    output.writeBool(this.isDefaultCookiesAccepted);
    output.writeFieldEnd();
  }
  if (this.manageSettingsEnabled !== null && this.manageSettingsEnabled !== undefined) {
    output.writeFieldBegin('manageSettingsEnabled', Thrift.Type.BOOL, 38);
    output.writeBool(this.manageSettingsEnabled);
    output.writeFieldEnd();
  }
  if (this.cookiesSettingsTitle !== null && this.cookiesSettingsTitle !== undefined) {
    output.writeFieldBegin('cookiesSettingsTitle', Thrift.Type.STRING, 39);
    output.writeString(this.cookiesSettingsTitle);
    output.writeFieldEnd();
  }
  if (this.cookiesSettingsSubTitle !== null && this.cookiesSettingsSubTitle !== undefined) {
    output.writeFieldBegin('cookiesSettingsSubTitle', Thrift.Type.STRING, 40);
    output.writeString(this.cookiesSettingsSubTitle);
    output.writeFieldEnd();
  }
  if (this.loaderScreenTimeInMillis !== null && this.loaderScreenTimeInMillis !== undefined) {
    output.writeFieldBegin('loaderScreenTimeInMillis', Thrift.Type.I32, 41);
    output.writeI32(this.loaderScreenTimeInMillis);
    output.writeFieldEnd();
  }
  if (this.cookieConsentDurationInDays !== null && this.cookieConsentDurationInDays !== undefined) {
    output.writeFieldBegin('cookieConsentDurationInDays', Thrift.Type.I32, 42);
    output.writeI32(this.cookieConsentDurationInDays);
    output.writeFieldEnd();
  }
  if (this.dismissCookiesPeriod !== null && this.dismissCookiesPeriod !== undefined) {
    output.writeFieldBegin('dismissCookiesPeriod', Thrift.Type.I32, 43);
    output.writeI32(this.dismissCookiesPeriod);
    output.writeFieldEnd();
  }
  if (this.removeCookiesPeriodicallyWebView !== null && this.removeCookiesPeriodicallyWebView !== undefined) {
    output.writeFieldBegin('removeCookiesPeriodicallyWebView', Thrift.Type.BOOL, 44);
    output.writeBool(this.removeCookiesPeriodicallyWebView);
    output.writeFieldEnd();
  }
  if (this.cookiesDurationDaysWebView !== null && this.cookiesDurationDaysWebView !== undefined) {
    output.writeFieldBegin('cookiesDurationDaysWebView', Thrift.Type.I32, 45);
    output.writeI32(this.cookiesDurationDaysWebView);
    output.writeFieldEnd();
  }
  if (this.thirdPartyCookiesEnabledWebView !== null && this.thirdPartyCookiesEnabledWebView !== undefined) {
    output.writeFieldBegin('thirdPartyCookiesEnabledWebView', Thrift.Type.BOOL, 46);
    output.writeBool(this.thirdPartyCookiesEnabledWebView);
    output.writeFieldEnd();
  }
  if (this.enableWebViewCache !== null && this.enableWebViewCache !== undefined) {
    output.writeFieldBegin('enableWebViewCache', Thrift.Type.BOOL, 47);
    output.writeBool(this.enableWebViewCache);
    output.writeFieldEnd();
  }
  if (this.webViewCacheDurationInDays !== null && this.webViewCacheDurationInDays !== undefined) {
    output.writeFieldBegin('webViewCacheDurationInDays', Thrift.Type.I32, 48);
    output.writeI32(this.webViewCacheDurationInDays);
    output.writeFieldEnd();
  }
  if (this.removeCookiesPeriodicallyWebViewBypassConsent !== null && this.removeCookiesPeriodicallyWebViewBypassConsent !== undefined) {
    output.writeFieldBegin('removeCookiesPeriodicallyWebViewBypassConsent', Thrift.Type.BOOL, 49);
    output.writeBool(this.removeCookiesPeriodicallyWebViewBypassConsent);
    output.writeFieldEnd();
  }
  if (this.userConsentEnable !== null && this.userConsentEnable !== undefined) {
    output.writeFieldBegin('userConsentEnable', Thrift.Type.BOOL, 50);
    output.writeBool(this.userConsentEnable);
    output.writeFieldEnd();
  }
  if (this.cookiesDurationDaysWebViewBypassConsent !== null && this.cookiesDurationDaysWebViewBypassConsent !== undefined) {
    output.writeFieldBegin('cookiesDurationDaysWebViewBypassConsent', Thrift.Type.I32, 51);
    output.writeI32(this.cookiesDurationDaysWebViewBypassConsent);
    output.writeFieldEnd();
  }
  if (this.thirdPartyCookiesEnabledWebViewBypassConsent !== null && this.thirdPartyCookiesEnabledWebViewBypassConsent !== undefined) {
    output.writeFieldBegin('thirdPartyCookiesEnabledWebViewBypassConsent', Thrift.Type.BOOL, 52);
    output.writeBool(this.thirdPartyCookiesEnabledWebViewBypassConsent);
    output.writeFieldEnd();
  }
  if (this.acceptButtonText !== null && this.acceptButtonText !== undefined) {
    output.writeFieldBegin('acceptButtonText', Thrift.Type.STRING, 53);
    output.writeString(this.acceptButtonText);
    output.writeFieldEnd();
  }
  if (this.modifyButtonText !== null && this.modifyButtonText !== undefined) {
    output.writeFieldBegin('modifyButtonText', Thrift.Type.STRING, 54);
    output.writeString(this.modifyButtonText);
    output.writeFieldEnd();
  }
  if (this.firstPartyCookieSubtitle !== null && this.firstPartyCookieSubtitle !== undefined) {
    output.writeFieldBegin('firstPartyCookieSubtitle', Thrift.Type.STRING, 55);
    output.writeString(this.firstPartyCookieSubtitle);
    output.writeFieldEnd();
  }
  if (this.thirdPartyCookieSubtitle !== null && this.thirdPartyCookieSubtitle !== undefined) {
    output.writeFieldBegin('thirdPartyCookieSubtitle', Thrift.Type.STRING, 56);
    output.writeString(this.thirdPartyCookieSubtitle);
    output.writeFieldEnd();
  }
  if (this.isAdditionalCookieToggled !== null && this.isAdditionalCookieToggled !== undefined) {
    output.writeFieldBegin('isAdditionalCookieToggled', Thrift.Type.BOOL, 57);
    output.writeBool(this.isAdditionalCookieToggled);
    output.writeFieldEnd();
  }
  if (this.saveExitCookieButtonText !== null && this.saveExitCookieButtonText !== undefined) {
    output.writeFieldBegin('saveExitCookieButtonText', Thrift.Type.STRING, 58);
    output.writeString(this.saveExitCookieButtonText);
    output.writeFieldEnd();
  }
  if (this.clearAllButtonText !== null && this.clearAllButtonText !== undefined) {
    output.writeFieldBegin('clearAllButtonText', Thrift.Type.STRING, 59);
    output.writeString(this.clearAllButtonText);
    output.writeFieldEnd();
  }
  if (this.modifyButtonEnabled !== null && this.modifyButtonEnabled !== undefined) {
    output.writeFieldBegin('modifyButtonEnabled', Thrift.Type.BOOL, 60);
    output.writeBool(this.modifyButtonEnabled);
    output.writeFieldEnd();
  }
  if (this.dpdpaEnable !== null && this.dpdpaEnable !== undefined) {
    output.writeFieldBegin('dpdpaEnable', Thrift.Type.BOOL, 61);
    output.writeBool(this.dpdpaEnable);
    output.writeFieldEnd();
  }
  if (this.cookiePolicyUrl !== null && this.cookiePolicyUrl !== undefined) {
    output.writeFieldBegin('cookiePolicyUrl', Thrift.Type.STRING, 62);
    output.writeString(this.cookiePolicyUrl);
    output.writeFieldEnd();
  }
  if (this.adde !== null && this.adde !== undefined) {
    output.writeFieldBegin('adde', Thrift.Type.BOOL, 63);
    output.writeBool(this.adde);
    output.writeFieldEnd();
  }
  if (this.timeToRefresh !== null && this.timeToRefresh !== undefined) {
    output.writeFieldBegin('timeToRefresh', Thrift.Type.I32, 64);
    output.writeI32(this.timeToRefresh);
    output.writeFieldEnd();
  }
  if (this.overlayPlacementName !== null && this.overlayPlacementName !== undefined) {
    output.writeFieldBegin('overlayPlacementName', Thrift.Type.STRING, 65);
    output.writeString(this.overlayPlacementName);
    output.writeFieldEnd();
  }
  if (this.overlayAdUnits !== null && this.overlayAdUnits !== undefined) {
    output.writeFieldBegin('overlayAdUnits', Thrift.Type.LIST, 66);
    output.writeListBegin(Thrift.Type.STRUCT, this.overlayAdUnits.length);
    for (var iter2161 in this.overlayAdUnits)
    {
      if (this.overlayAdUnits.hasOwnProperty(iter2161))
      {
        iter2161 = this.overlayAdUnits[iter2161];
        iter2161.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.firstAdRequestPosition !== null && this.firstAdRequestPosition !== undefined) {
    output.writeFieldBegin('firstAdRequestPosition', Thrift.Type.I32, 67);
    output.writeI32(this.firstAdRequestPosition);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CardRequest = function(args) {
  this.eventType = null;
  this.cardId = null;
  this.cardData = null;
  this.cardMetaData = null;
  this.publishToUser = null;
  this.publishingQueueEnabled = null;
  this.publishingDestinations = null;
  this.associateBubbleId = null;
  if (args) {
    if (args.eventType !== undefined && args.eventType !== null) {
      this.eventType = args.eventType;
    }
    if (args.cardId !== undefined && args.cardId !== null) {
      this.cardId = args.cardId;
    }
    if (args.cardData !== undefined && args.cardData !== null) {
      this.cardData = new Card(args.cardData);
    }
    if (args.cardMetaData !== undefined && args.cardMetaData !== null) {
      this.cardMetaData = new CardMetaData(args.cardMetaData);
    }
    if (args.publishToUser !== undefined && args.publishToUser !== null) {
      this.publishToUser = args.publishToUser;
    }
    if (args.publishingQueueEnabled !== undefined && args.publishingQueueEnabled !== null) {
      this.publishingQueueEnabled = args.publishingQueueEnabled;
    }
    if (args.publishingDestinations !== undefined && args.publishingDestinations !== null) {
      this.publishingDestinations = Thrift.copyList(args.publishingDestinations, [null]);
    }
    if (args.associateBubbleId !== undefined && args.associateBubbleId !== null) {
      this.associateBubbleId = args.associateBubbleId;
    }
  }
};
CardRequest.prototype = {};
CardRequest.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.eventType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.cardId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.cardData = new Card();
        this.cardData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.cardMetaData = new CardMetaData();
        this.cardMetaData.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.BOOL) {
        this.publishToUser = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.publishingQueueEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size2162 = 0;
        var _rtmp32166;
        this.publishingDestinations = [];
        var _etype2165 = 0;
        _rtmp32166 = input.readListBegin();
        _etype2165 = _rtmp32166.etype;
        _size2162 = _rtmp32166.size;
        for (var _i2167 = 0; _i2167 < _size2162; ++_i2167)
        {
          var elem2168 = null;
          elem2168 = input.readString().value;
          this.publishingDestinations.push(elem2168);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.associateBubbleId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CardRequest.prototype.write = function(output) {
  output.writeStructBegin('CardRequest');
  if (this.eventType !== null && this.eventType !== undefined) {
    output.writeFieldBegin('eventType', Thrift.Type.I32, 1);
    output.writeI32(this.eventType);
    output.writeFieldEnd();
  }
  if (this.cardId !== null && this.cardId !== undefined) {
    output.writeFieldBegin('cardId', Thrift.Type.STRING, 2);
    output.writeString(this.cardId);
    output.writeFieldEnd();
  }
  if (this.cardData !== null && this.cardData !== undefined) {
    output.writeFieldBegin('cardData', Thrift.Type.STRUCT, 3);
    this.cardData.write(output);
    output.writeFieldEnd();
  }
  if (this.cardMetaData !== null && this.cardMetaData !== undefined) {
    output.writeFieldBegin('cardMetaData', Thrift.Type.STRUCT, 4);
    this.cardMetaData.write(output);
    output.writeFieldEnd();
  }
  if (this.publishToUser !== null && this.publishToUser !== undefined) {
    output.writeFieldBegin('publishToUser', Thrift.Type.BOOL, 5);
    output.writeBool(this.publishToUser);
    output.writeFieldEnd();
  }
  if (this.publishingQueueEnabled !== null && this.publishingQueueEnabled !== undefined) {
    output.writeFieldBegin('publishingQueueEnabled', Thrift.Type.BOOL, 6);
    output.writeBool(this.publishingQueueEnabled);
    output.writeFieldEnd();
  }
  if (this.publishingDestinations !== null && this.publishingDestinations !== undefined) {
    output.writeFieldBegin('publishingDestinations', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRING, this.publishingDestinations.length);
    for (var iter2169 in this.publishingDestinations)
    {
      if (this.publishingDestinations.hasOwnProperty(iter2169))
      {
        iter2169 = this.publishingDestinations[iter2169];
        output.writeString(iter2169);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.associateBubbleId !== null && this.associateBubbleId !== undefined) {
    output.writeFieldBegin('associateBubbleId', Thrift.Type.STRING, 8);
    output.writeString(this.associateBubbleId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AutoStackRankingRequest = function(args) {
  this.adUnitId = null;
  if (args) {
    if (args.adUnitId !== undefined && args.adUnitId !== null) {
      this.adUnitId = args.adUnitId;
    }
  }
};
AutoStackRankingRequest.prototype = {};
AutoStackRankingRequest.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adUnitId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AutoStackRankingRequest.prototype.write = function(output) {
  output.writeStructBegin('AutoStackRankingRequest');
  if (this.adUnitId !== null && this.adUnitId !== undefined) {
    output.writeFieldBegin('adUnitId', Thrift.Type.STRING, 1);
    output.writeString(this.adUnitId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AutoStackRankingEcpm = function(args) {
  this.updateInterval = null;
  this.google = null;
  this.meson = null;
  this.direct = null;
  if (args) {
    if (args.updateInterval !== undefined && args.updateInterval !== null) {
      this.updateInterval = args.updateInterval;
    }
    if (args.google !== undefined && args.google !== null) {
      this.google = args.google;
    }
    if (args.meson !== undefined && args.meson !== null) {
      this.meson = args.meson;
    }
    if (args.direct !== undefined && args.direct !== null) {
      this.direct = args.direct;
    }
  }
};
AutoStackRankingEcpm.prototype = {};
AutoStackRankingEcpm.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.updateInterval = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.google = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.DOUBLE) {
        this.meson = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.DOUBLE) {
        this.direct = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AutoStackRankingEcpm.prototype.write = function(output) {
  output.writeStructBegin('AutoStackRankingEcpm');
  if (this.updateInterval !== null && this.updateInterval !== undefined) {
    output.writeFieldBegin('updateInterval', Thrift.Type.STRING, 1);
    output.writeString(this.updateInterval);
    output.writeFieldEnd();
  }
  if (this.google !== null && this.google !== undefined) {
    output.writeFieldBegin('google', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.google);
    output.writeFieldEnd();
  }
  if (this.meson !== null && this.meson !== undefined) {
    output.writeFieldBegin('meson', Thrift.Type.DOUBLE, 3);
    output.writeDouble(this.meson);
    output.writeFieldEnd();
  }
  if (this.direct !== null && this.direct !== undefined) {
    output.writeFieldBegin('direct', Thrift.Type.DOUBLE, 4);
    output.writeDouble(this.direct);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AutoStackRankingResponse = function(args) {
  this.adUnitId = null;
  this.expectedEcpm = null;
  if (args) {
    if (args.adUnitId !== undefined && args.adUnitId !== null) {
      this.adUnitId = args.adUnitId;
    }
    if (args.expectedEcpm !== undefined && args.expectedEcpm !== null) {
      this.expectedEcpm = new AutoStackRankingEcpm(args.expectedEcpm);
    }
  }
};
AutoStackRankingResponse.prototype = {};
AutoStackRankingResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.adUnitId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.expectedEcpm = new AutoStackRankingEcpm();
        this.expectedEcpm.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AutoStackRankingResponse.prototype.write = function(output) {
  output.writeStructBegin('AutoStackRankingResponse');
  if (this.adUnitId !== null && this.adUnitId !== undefined) {
    output.writeFieldBegin('adUnitId', Thrift.Type.STRING, 1);
    output.writeString(this.adUnitId);
    output.writeFieldEnd();
  }
  if (this.expectedEcpm !== null && this.expectedEcpm !== undefined) {
    output.writeFieldBegin('expectedEcpm', Thrift.Type.STRUCT, 2);
    this.expectedEcpm.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

BaseCategory = function(args) {
  this.id = null;
  this.name = null;
  if (args) {
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
  }
};
BaseCategory.prototype = {};
BaseCategory.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

BaseCategory.prototype.write = function(output) {
  output.writeStructBegin('BaseCategory');
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 1);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TVLanguageSubscription = function(args) {
  this.language = null;
  this.isActive = null;
  if (args) {
    if (args.language !== undefined && args.language !== null) {
      this.language = args.language;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field language is unset!');
    }
    if (args.isActive !== undefined && args.isActive !== null) {
      this.isActive = args.isActive;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field isActive is unset!');
    }
  }
};
TVLanguageSubscription.prototype = {};
TVLanguageSubscription.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.language = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.isActive = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TVLanguageSubscription.prototype.write = function(output) {
  output.writeStructBegin('TVLanguageSubscription');
  if (this.language !== null && this.language !== undefined) {
    output.writeFieldBegin('language', Thrift.Type.STRING, 1);
    output.writeString(this.language);
    output.writeFieldEnd();
  }
  if (this.isActive !== null && this.isActive !== undefined) {
    output.writeFieldBegin('isActive', Thrift.Type.BOOL, 2);
    output.writeBool(this.isActive);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TvGeo = function(args) {
  this.coarseLat = null;
  this.coarseLong = null;
  this.fineLat = null;
  this.fineLong = null;
  this.active = null;
  if (args) {
    if (args.coarseLat !== undefined && args.coarseLat !== null) {
      this.coarseLat = args.coarseLat;
    }
    if (args.coarseLong !== undefined && args.coarseLong !== null) {
      this.coarseLong = args.coarseLong;
    }
    if (args.fineLat !== undefined && args.fineLat !== null) {
      this.fineLat = args.fineLat;
    }
    if (args.fineLong !== undefined && args.fineLong !== null) {
      this.fineLong = args.fineLong;
    }
    if (args.active !== undefined && args.active !== null) {
      this.active = args.active;
    }
  }
};
TvGeo.prototype = {};
TvGeo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.DOUBLE) {
        this.coarseLat = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.DOUBLE) {
        this.coarseLong = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.DOUBLE) {
        this.fineLat = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.DOUBLE) {
        this.fineLong = input.readDouble().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.active = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TvGeo.prototype.write = function(output) {
  output.writeStructBegin('TvGeo');
  if (this.coarseLat !== null && this.coarseLat !== undefined) {
    output.writeFieldBegin('coarseLat', Thrift.Type.DOUBLE, 1);
    output.writeDouble(this.coarseLat);
    output.writeFieldEnd();
  }
  if (this.coarseLong !== null && this.coarseLong !== undefined) {
    output.writeFieldBegin('coarseLong', Thrift.Type.DOUBLE, 2);
    output.writeDouble(this.coarseLong);
    output.writeFieldEnd();
  }
  if (this.fineLat !== null && this.fineLat !== undefined) {
    output.writeFieldBegin('fineLat', Thrift.Type.DOUBLE, 3);
    output.writeDouble(this.fineLat);
    output.writeFieldEnd();
  }
  if (this.fineLong !== null && this.fineLong !== undefined) {
    output.writeFieldBegin('fineLong', Thrift.Type.DOUBLE, 4);
    output.writeDouble(this.fineLong);
    output.writeFieldEnd();
  }
  if (this.active !== null && this.active !== undefined) {
    output.writeFieldBegin('active', Thrift.Type.I32, 5);
    output.writeI32(this.active);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


OciBeacons = function(args) {
  this.appSubmitBeacons = null;
  this.installCompleteBeacons = null;
  this.notificationTapBeacons = null;
  if (args) {
    if (args.appSubmitBeacons !== undefined && args.appSubmitBeacons !== null) {
      this.appSubmitBeacons = Thrift.copyList(args.appSubmitBeacons, [null]);
    }
    if (args.installCompleteBeacons !== undefined && args.installCompleteBeacons !== null) {
      this.installCompleteBeacons = Thrift.copyList(args.installCompleteBeacons, [null]);
    }
    if (args.notificationTapBeacons !== undefined && args.notificationTapBeacons !== null) {
      this.notificationTapBeacons = Thrift.copyList(args.notificationTapBeacons, [null]);
    }
  }
};
OciBeacons.prototype = {};
OciBeacons.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size0 = 0;
        var _rtmp34;
        this.appSubmitBeacons = [];
        var _etype3 = 0;
        _rtmp34 = input.readListBegin();
        _etype3 = _rtmp34.etype;
        _size0 = _rtmp34.size;
        for (var _i5 = 0; _i5 < _size0; ++_i5)
        {
          var elem6 = null;
          elem6 = input.readString().value;
          this.appSubmitBeacons.push(elem6);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size7 = 0;
        var _rtmp311;
        this.installCompleteBeacons = [];
        var _etype10 = 0;
        _rtmp311 = input.readListBegin();
        _etype10 = _rtmp311.etype;
        _size7 = _rtmp311.size;
        for (var _i12 = 0; _i12 < _size7; ++_i12)
        {
          var elem13 = null;
          elem13 = input.readString().value;
          this.installCompleteBeacons.push(elem13);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size14 = 0;
        var _rtmp318;
        this.notificationTapBeacons = [];
        var _etype17 = 0;
        _rtmp318 = input.readListBegin();
        _etype17 = _rtmp318.etype;
        _size14 = _rtmp318.size;
        for (var _i19 = 0; _i19 < _size14; ++_i19)
        {
          var elem20 = null;
          elem20 = input.readString().value;
          this.notificationTapBeacons.push(elem20);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OciBeacons.prototype.write = function(output) {
  output.writeStructBegin('OciBeacons');
  if (this.appSubmitBeacons !== null && this.appSubmitBeacons !== undefined) {
    output.writeFieldBegin('appSubmitBeacons', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.appSubmitBeacons.length);
    for (var iter21 in this.appSubmitBeacons)
    {
      if (this.appSubmitBeacons.hasOwnProperty(iter21))
      {
        iter21 = this.appSubmitBeacons[iter21];
        output.writeString(iter21);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.installCompleteBeacons !== null && this.installCompleteBeacons !== undefined) {
    output.writeFieldBegin('installCompleteBeacons', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.installCompleteBeacons.length);
    for (var iter22 in this.installCompleteBeacons)
    {
      if (this.installCompleteBeacons.hasOwnProperty(iter22))
      {
        iter22 = this.installCompleteBeacons[iter22];
        output.writeString(iter22);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.notificationTapBeacons !== null && this.notificationTapBeacons !== undefined) {
    output.writeFieldBegin('notificationTapBeacons', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRING, this.notificationTapBeacons.length);
    for (var iter23 in this.notificationTapBeacons)
    {
      if (this.notificationTapBeacons.hasOwnProperty(iter23))
      {
        iter23 = this.notificationTapBeacons[iter23];
        output.writeString(iter23);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppOpenNudgeConfig = function(args) {
  this.showNudge = null;
  this.weburl = null;
  this.hwe = null;
  this.nudgeCtaText = null;
  this.nudgeDescription = null;
  this.nudgeMode = null;
  if (args) {
    if (args.showNudge !== undefined && args.showNudge !== null) {
      this.showNudge = args.showNudge;
    }
    if (args.weburl !== undefined && args.weburl !== null) {
      this.weburl = args.weburl;
    }
    if (args.hwe !== undefined && args.hwe !== null) {
      this.hwe = args.hwe;
    }
    if (args.nudgeCtaText !== undefined && args.nudgeCtaText !== null) {
      this.nudgeCtaText = args.nudgeCtaText;
    }
    if (args.nudgeDescription !== undefined && args.nudgeDescription !== null) {
      this.nudgeDescription = args.nudgeDescription;
    }
    if (args.nudgeMode !== undefined && args.nudgeMode !== null) {
      this.nudgeMode = args.nudgeMode;
    }
  }
};
AppOpenNudgeConfig.prototype = {};
AppOpenNudgeConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.showNudge = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.weburl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.hwe = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.nudgeCtaText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.nudgeDescription = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I32) {
        this.nudgeMode = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppOpenNudgeConfig.prototype.write = function(output) {
  output.writeStructBegin('AppOpenNudgeConfig');
  if (this.showNudge !== null && this.showNudge !== undefined) {
    output.writeFieldBegin('showNudge', Thrift.Type.BOOL, 1);
    output.writeBool(this.showNudge);
    output.writeFieldEnd();
  }
  if (this.weburl !== null && this.weburl !== undefined) {
    output.writeFieldBegin('weburl', Thrift.Type.STRING, 2);
    output.writeString(this.weburl);
    output.writeFieldEnd();
  }
  if (this.hwe !== null && this.hwe !== undefined) {
    output.writeFieldBegin('hwe', Thrift.Type.BOOL, 3);
    output.writeBool(this.hwe);
    output.writeFieldEnd();
  }
  if (this.nudgeCtaText !== null && this.nudgeCtaText !== undefined) {
    output.writeFieldBegin('nudgeCtaText', Thrift.Type.STRING, 4);
    output.writeString(this.nudgeCtaText);
    output.writeFieldEnd();
  }
  if (this.nudgeDescription !== null && this.nudgeDescription !== undefined) {
    output.writeFieldBegin('nudgeDescription', Thrift.Type.STRING, 5);
    output.writeString(this.nudgeDescription);
    output.writeFieldEnd();
  }
  if (this.nudgeMode !== null && this.nudgeMode !== undefined) {
    output.writeFieldBegin('nudgeMode', Thrift.Type.I32, 6);
    output.writeI32(this.nudgeMode);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AppOpenNotificationConfig = function(args) {
  this.enabled = null;
  this.iconUrl = null;
  this.title = null;
  this.description = null;
  this.ctaText = null;
  if (args) {
    if (args.enabled !== undefined && args.enabled !== null) {
      this.enabled = args.enabled;
    }
    if (args.iconUrl !== undefined && args.iconUrl !== null) {
      this.iconUrl = args.iconUrl;
    }
    if (args.title !== undefined && args.title !== null) {
      this.title = args.title;
    }
    if (args.description !== undefined && args.description !== null) {
      this.description = args.description;
    }
    if (args.ctaText !== undefined && args.ctaText !== null) {
      this.ctaText = args.ctaText;
    }
  }
};
AppOpenNotificationConfig.prototype = {};
AppOpenNotificationConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.enabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.iconUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.title = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.description = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.ctaText = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AppOpenNotificationConfig.prototype.write = function(output) {
  output.writeStructBegin('AppOpenNotificationConfig');
  if (this.enabled !== null && this.enabled !== undefined) {
    output.writeFieldBegin('enabled', Thrift.Type.BOOL, 1);
    output.writeBool(this.enabled);
    output.writeFieldEnd();
  }
  if (this.iconUrl !== null && this.iconUrl !== undefined) {
    output.writeFieldBegin('iconUrl', Thrift.Type.STRING, 2);
    output.writeString(this.iconUrl);
    output.writeFieldEnd();
  }
  if (this.title !== null && this.title !== undefined) {
    output.writeFieldBegin('title', Thrift.Type.STRING, 3);
    output.writeString(this.title);
    output.writeFieldEnd();
  }
  if (this.description !== null && this.description !== undefined) {
    output.writeFieldBegin('description', Thrift.Type.STRING, 4);
    output.writeString(this.description);
    output.writeFieldEnd();
  }
  if (this.ctaText !== null && this.ctaText !== undefined) {
    output.writeFieldBegin('ctaText', Thrift.Type.STRING, 5);
    output.writeString(this.ctaText);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OciAppConfig = function(args) {
  this.installLater = null;
  this.autoAppOpen = null;
  this.autoAppOpenDelay = null;
  this.showNudgeScreen = null;
  this.appInstallStartDelayInSec = null;
  this.exposeOciState = null;
  if (args) {
    if (args.installLater !== undefined && args.installLater !== null) {
      this.installLater = args.installLater;
    }
    if (args.autoAppOpen !== undefined && args.autoAppOpen !== null) {
      this.autoAppOpen = args.autoAppOpen;
    }
    if (args.autoAppOpenDelay !== undefined && args.autoAppOpenDelay !== null) {
      this.autoAppOpenDelay = args.autoAppOpenDelay;
    }
    if (args.showNudgeScreen !== undefined && args.showNudgeScreen !== null) {
      this.showNudgeScreen = args.showNudgeScreen;
    }
    if (args.appInstallStartDelayInSec !== undefined && args.appInstallStartDelayInSec !== null) {
      this.appInstallStartDelayInSec = args.appInstallStartDelayInSec;
    }
    if (args.exposeOciState !== undefined && args.exposeOciState !== null) {
      this.exposeOciState = args.exposeOciState;
    }
  }
};
OciAppConfig.prototype = {};
OciAppConfig.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.BOOL) {
        this.installLater = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.autoAppOpen = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.autoAppOpenDelay = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.BOOL) {
        this.showNudgeScreen = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I32) {
        this.appInstallStartDelayInSec = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.exposeOciState = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OciAppConfig.prototype.write = function(output) {
  output.writeStructBegin('OciAppConfig');
  if (this.installLater !== null && this.installLater !== undefined) {
    output.writeFieldBegin('installLater', Thrift.Type.BOOL, 1);
    output.writeBool(this.installLater);
    output.writeFieldEnd();
  }
  if (this.autoAppOpen !== null && this.autoAppOpen !== undefined) {
    output.writeFieldBegin('autoAppOpen', Thrift.Type.BOOL, 2);
    output.writeBool(this.autoAppOpen);
    output.writeFieldEnd();
  }
  if (this.autoAppOpenDelay !== null && this.autoAppOpenDelay !== undefined) {
    output.writeFieldBegin('autoAppOpenDelay', Thrift.Type.I32, 3);
    output.writeI32(this.autoAppOpenDelay);
    output.writeFieldEnd();
  }
  if (this.showNudgeScreen !== null && this.showNudgeScreen !== undefined) {
    output.writeFieldBegin('showNudgeScreen', Thrift.Type.BOOL, 4);
    output.writeBool(this.showNudgeScreen);
    output.writeFieldEnd();
  }
  if (this.appInstallStartDelayInSec !== null && this.appInstallStartDelayInSec !== undefined) {
    output.writeFieldBegin('appInstallStartDelayInSec', Thrift.Type.I32, 5);
    output.writeI32(this.appInstallStartDelayInSec);
    output.writeFieldEnd();
  }
  if (this.exposeOciState !== null && this.exposeOciState !== undefined) {
    output.writeFieldBegin('exposeOciState', Thrift.Type.BOOL, 6);
    output.writeBool(this.exposeOciState);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

CampaignTrackerInfo = function(args) {
  this.packageId = null;
  this.ecpm = null;
  this.coverage = null;
  this.region = null;
  this.additionalDetails = null;
  this.updatedAt = null;
  if (args) {
    if (args.packageId !== undefined && args.packageId !== null) {
      this.packageId = args.packageId;
    }
    if (args.ecpm !== undefined && args.ecpm !== null) {
      this.ecpm = args.ecpm;
    }
    if (args.coverage !== undefined && args.coverage !== null) {
      this.coverage = args.coverage;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.additionalDetails !== undefined && args.additionalDetails !== null) {
      this.additionalDetails = args.additionalDetails;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
  }
};
CampaignTrackerInfo.prototype = {};
CampaignTrackerInfo.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.packageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.ecpm = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.coverage = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.region = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRING) {
        this.additionalDetails = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

CampaignTrackerInfo.prototype.write = function(output) {
  output.writeStructBegin('CampaignTrackerInfo');
  if (this.packageId !== null && this.packageId !== undefined) {
    output.writeFieldBegin('packageId', Thrift.Type.STRING, 1);
    output.writeString(this.packageId);
    output.writeFieldEnd();
  }
  if (this.ecpm !== null && this.ecpm !== undefined) {
    output.writeFieldBegin('ecpm', Thrift.Type.I32, 2);
    output.writeI32(this.ecpm);
    output.writeFieldEnd();
  }
  if (this.coverage !== null && this.coverage !== undefined) {
    output.writeFieldBegin('coverage', Thrift.Type.I32, 3);
    output.writeI32(this.coverage);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.STRING, 4);
    output.writeString(this.region);
    output.writeFieldEnd();
  }
  if (this.additionalDetails !== null && this.additionalDetails !== undefined) {
    output.writeFieldBegin('additionalDetails', Thrift.Type.STRING, 5);
    output.writeString(this.additionalDetails);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 6);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

OciCampaignMeta = function(args) {
  this.campaignId = null;
  this.name = null;
  this.partnerId = null;
  this.startTimeEpochSecs = null;
  this.endTimeEpochSecs = null;
  this.appPackageName = null;
  this.beacons = null;
  this.fallbackUrl = null;
  this.updatedAt = null;
  this.isLive = null;
  this.appName = null;
  this.ganMode = null;
  this.dtf = null;
  this.hwe = null;
  this.weburl = null;
  this.ganPartner = null;
  this.ociAppConfig = null;
  this.nudgeConfig = null;
  this.appOpenNotificationConfig = null;
  this.apkUrl = null;
  this.shouldNotify = null;
  this.preloadEnable = null;
  if (args) {
    if (args.campaignId !== undefined && args.campaignId !== null) {
      this.campaignId = args.campaignId;
    }
    if (args.name !== undefined && args.name !== null) {
      this.name = args.name;
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    }
    if (args.startTimeEpochSecs !== undefined && args.startTimeEpochSecs !== null) {
      this.startTimeEpochSecs = args.startTimeEpochSecs;
    }
    if (args.endTimeEpochSecs !== undefined && args.endTimeEpochSecs !== null) {
      this.endTimeEpochSecs = args.endTimeEpochSecs;
    }
    if (args.appPackageName !== undefined && args.appPackageName !== null) {
      this.appPackageName = args.appPackageName;
    }
    if (args.beacons !== undefined && args.beacons !== null) {
      this.beacons = new OciBeacons(args.beacons);
    }
    if (args.fallbackUrl !== undefined && args.fallbackUrl !== null) {
      this.fallbackUrl = args.fallbackUrl;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.isLive !== undefined && args.isLive !== null) {
      this.isLive = args.isLive;
    }
    if (args.appName !== undefined && args.appName !== null) {
      this.appName = args.appName;
    }
    if (args.ganMode !== undefined && args.ganMode !== null) {
      this.ganMode = args.ganMode;
    }
    if (args.dtf !== undefined && args.dtf !== null) {
      this.dtf = args.dtf;
    }
    if (args.hwe !== undefined && args.hwe !== null) {
      this.hwe = args.hwe;
    }
    if (args.weburl !== undefined && args.weburl !== null) {
      this.weburl = args.weburl;
    }
    if (args.ganPartner !== undefined && args.ganPartner !== null) {
      this.ganPartner = args.ganPartner;
    }
    if (args.ociAppConfig !== undefined && args.ociAppConfig !== null) {
      this.ociAppConfig = new OciAppConfig(args.ociAppConfig);
    }
    if (args.nudgeConfig !== undefined && args.nudgeConfig !== null) {
      this.nudgeConfig = new AppOpenNudgeConfig(args.nudgeConfig);
    }
    if (args.appOpenNotificationConfig !== undefined && args.appOpenNotificationConfig !== null) {
      this.appOpenNotificationConfig = new AppOpenNotificationConfig(args.appOpenNotificationConfig);
    }
    if (args.apkUrl !== undefined && args.apkUrl !== null) {
      this.apkUrl = args.apkUrl;
    }
    if (args.shouldNotify !== undefined && args.shouldNotify !== null) {
      this.shouldNotify = args.shouldNotify;
    }
    if (args.preloadEnable !== undefined && args.preloadEnable !== null) {
      this.preloadEnable = args.preloadEnable;
    }
  }
};
OciCampaignMeta.prototype = {};
OciCampaignMeta.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.campaignId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.name = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.startTimeEpochSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.endTimeEpochSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRING) {
        this.appPackageName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRUCT) {
        this.beacons = new OciBeacons();
        this.beacons.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRING) {
        this.fallbackUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.BOOL) {
        this.isLive = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.appName = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.I16) {
        this.ganMode = input.readI16().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.BOOL) {
        this.dtf = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.BOOL) {
        this.hwe = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRING) {
        this.weburl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.STRING) {
        this.ganPartner = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRUCT) {
        this.ociAppConfig = new OciAppConfig();
        this.ociAppConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRUCT) {
        this.nudgeConfig = new AppOpenNudgeConfig();
        this.nudgeConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRUCT) {
        this.appOpenNotificationConfig = new AppOpenNotificationConfig();
        this.appOpenNotificationConfig.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.apkUrl = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.BOOL) {
        this.shouldNotify = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.BOOL) {
        this.preloadEnable = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

OciCampaignMeta.prototype.write = function(output) {
  output.writeStructBegin('OciCampaignMeta');
  if (this.campaignId !== null && this.campaignId !== undefined) {
    output.writeFieldBegin('campaignId', Thrift.Type.STRING, 1);
    output.writeString(this.campaignId);
    output.writeFieldEnd();
  }
  if (this.name !== null && this.name !== undefined) {
    output.writeFieldBegin('name', Thrift.Type.STRING, 2);
    output.writeString(this.name);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 3);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.startTimeEpochSecs !== null && this.startTimeEpochSecs !== undefined) {
    output.writeFieldBegin('startTimeEpochSecs', Thrift.Type.I64, 4);
    output.writeI64(this.startTimeEpochSecs);
    output.writeFieldEnd();
  }
  if (this.endTimeEpochSecs !== null && this.endTimeEpochSecs !== undefined) {
    output.writeFieldBegin('endTimeEpochSecs', Thrift.Type.I64, 5);
    output.writeI64(this.endTimeEpochSecs);
    output.writeFieldEnd();
  }
  if (this.appPackageName !== null && this.appPackageName !== undefined) {
    output.writeFieldBegin('appPackageName', Thrift.Type.STRING, 6);
    output.writeString(this.appPackageName);
    output.writeFieldEnd();
  }
  if (this.beacons !== null && this.beacons !== undefined) {
    output.writeFieldBegin('beacons', Thrift.Type.STRUCT, 7);
    this.beacons.write(output);
    output.writeFieldEnd();
  }
  if (this.fallbackUrl !== null && this.fallbackUrl !== undefined) {
    output.writeFieldBegin('fallbackUrl', Thrift.Type.STRING, 8);
    output.writeString(this.fallbackUrl);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 9);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.isLive !== null && this.isLive !== undefined) {
    output.writeFieldBegin('isLive', Thrift.Type.BOOL, 10);
    output.writeBool(this.isLive);
    output.writeFieldEnd();
  }
  if (this.appName !== null && this.appName !== undefined) {
    output.writeFieldBegin('appName', Thrift.Type.STRING, 11);
    output.writeString(this.appName);
    output.writeFieldEnd();
  }
  if (this.ganMode !== null && this.ganMode !== undefined) {
    output.writeFieldBegin('ganMode', Thrift.Type.I16, 12);
    output.writeI16(this.ganMode);
    output.writeFieldEnd();
  }
  if (this.dtf !== null && this.dtf !== undefined) {
    output.writeFieldBegin('dtf', Thrift.Type.BOOL, 13);
    output.writeBool(this.dtf);
    output.writeFieldEnd();
  }
  if (this.hwe !== null && this.hwe !== undefined) {
    output.writeFieldBegin('hwe', Thrift.Type.BOOL, 14);
    output.writeBool(this.hwe);
    output.writeFieldEnd();
  }
  if (this.weburl !== null && this.weburl !== undefined) {
    output.writeFieldBegin('weburl', Thrift.Type.STRING, 15);
    output.writeString(this.weburl);
    output.writeFieldEnd();
  }
  if (this.ganPartner !== null && this.ganPartner !== undefined) {
    output.writeFieldBegin('ganPartner', Thrift.Type.STRING, 16);
    output.writeString(this.ganPartner);
    output.writeFieldEnd();
  }
  if (this.ociAppConfig !== null && this.ociAppConfig !== undefined) {
    output.writeFieldBegin('ociAppConfig', Thrift.Type.STRUCT, 17);
    this.ociAppConfig.write(output);
    output.writeFieldEnd();
  }
  if (this.nudgeConfig !== null && this.nudgeConfig !== undefined) {
    output.writeFieldBegin('nudgeConfig', Thrift.Type.STRUCT, 18);
    this.nudgeConfig.write(output);
    output.writeFieldEnd();
  }
  if (this.appOpenNotificationConfig !== null && this.appOpenNotificationConfig !== undefined) {
    output.writeFieldBegin('appOpenNotificationConfig', Thrift.Type.STRUCT, 19);
    this.appOpenNotificationConfig.write(output);
    output.writeFieldEnd();
  }
  if (this.apkUrl !== null && this.apkUrl !== undefined) {
    output.writeFieldBegin('apkUrl', Thrift.Type.STRING, 20);
    output.writeString(this.apkUrl);
    output.writeFieldEnd();
  }
  if (this.shouldNotify !== null && this.shouldNotify !== undefined) {
    output.writeFieldBegin('shouldNotify', Thrift.Type.BOOL, 21);
    output.writeBool(this.shouldNotify);
    output.writeFieldEnd();
  }
  if (this.preloadEnable !== null && this.preloadEnable !== undefined) {
    output.writeFieldBegin('preloadEnable', Thrift.Type.BOOL, 22);
    output.writeBool(this.preloadEnable);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

MetricCacheModel = function(args) {
  this.advertiser_id = null;
  this.date = null;
  this.installs = null;
  this.clicks = null;
  this.impressions = null;
  this.event_count = null;
  this.id = null;
  if (args) {
    if (args.advertiser_id !== undefined && args.advertiser_id !== null) {
      this.advertiser_id = args.advertiser_id;
    }
    if (args.date !== undefined && args.date !== null) {
      this.date = args.date;
    }
    if (args.installs !== undefined && args.installs !== null) {
      this.installs = args.installs;
    }
    if (args.clicks !== undefined && args.clicks !== null) {
      this.clicks = args.clicks;
    }
    if (args.impressions !== undefined && args.impressions !== null) {
      this.impressions = args.impressions;
    }
    if (args.event_count !== undefined && args.event_count !== null) {
      this.event_count = args.event_count;
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
  }
};
MetricCacheModel.prototype = {};
MetricCacheModel.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.advertiser_id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.date = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I64) {
        this.installs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.clicks = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.impressions = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.event_count = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.I32) {
        this.id = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

MetricCacheModel.prototype.write = function(output) {
  output.writeStructBegin('MetricCacheModel');
  if (this.advertiser_id !== null && this.advertiser_id !== undefined) {
    output.writeFieldBegin('advertiser_id', Thrift.Type.STRING, 1);
    output.writeString(this.advertiser_id);
    output.writeFieldEnd();
  }
  if (this.date !== null && this.date !== undefined) {
    output.writeFieldBegin('date', Thrift.Type.STRING, 2);
    output.writeString(this.date);
    output.writeFieldEnd();
  }
  if (this.installs !== null && this.installs !== undefined) {
    output.writeFieldBegin('installs', Thrift.Type.I64, 3);
    output.writeI64(this.installs);
    output.writeFieldEnd();
  }
  if (this.clicks !== null && this.clicks !== undefined) {
    output.writeFieldBegin('clicks', Thrift.Type.I64, 4);
    output.writeI64(this.clicks);
    output.writeFieldEnd();
  }
  if (this.impressions !== null && this.impressions !== undefined) {
    output.writeFieldBegin('impressions', Thrift.Type.I64, 5);
    output.writeI64(this.impressions);
    output.writeFieldEnd();
  }
  if (this.event_count !== null && this.event_count !== undefined) {
    output.writeFieldBegin('event_count', Thrift.Type.I64, 6);
    output.writeI64(this.event_count);
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.I32, 7);
    output.writeI32(this.id);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


GlanceDraftResponse = function(args) {
  this.draft = null;
  if (args) {
    if (args.draft !== undefined && args.draft !== null) {
      this.draft = new GlanceDraft(args.draft);
    }
  }
};
GlanceDraftResponse.prototype = {};
GlanceDraftResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.draft = new GlanceDraft();
        this.draft.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftResponse.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftResponse');
  if (this.draft !== null && this.draft !== undefined) {
    output.writeFieldBegin('draft', Thrift.Type.STRUCT, 1);
    this.draft.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

Pagination = function(args) {
  this.limit = null;
  this.offset = null;
  this.search = null;
  if (args) {
    if (args.limit !== undefined && args.limit !== null) {
      this.limit = args.limit;
    }
    if (args.offset !== undefined && args.offset !== null) {
      this.offset = args.offset;
    }
    if (args.search !== undefined && args.search !== null) {
      this.search = args.search;
    }
  }
};
Pagination.prototype = {};
Pagination.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I64) {
        this.limit = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.offset = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.search = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

Pagination.prototype.write = function(output) {
  output.writeStructBegin('Pagination');
  if (this.limit !== null && this.limit !== undefined) {
    output.writeFieldBegin('limit', Thrift.Type.I64, 1);
    output.writeI64(this.limit);
    output.writeFieldEnd();
  }
  if (this.offset !== null && this.offset !== undefined) {
    output.writeFieldBegin('offset', Thrift.Type.I64, 2);
    output.writeI64(this.offset);
    output.writeFieldEnd();
  }
  if (this.search !== null && this.search !== undefined) {
    output.writeFieldBegin('search', Thrift.Type.STRING, 3);
    output.writeString(this.search);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftSearchQuery = function(args) {
  this.draftIds = null;
  this.createdAfterInSecs = null;
  this.partner = null;
  this.region = null;
  this.pagination = null;
  this.glanceType = null;
  this.categoryIds = null;
  this.storyType = null;
  this.publisherId = null;
  this.status = null;
  if (args) {
    if (args.draftIds !== undefined && args.draftIds !== null) {
      this.draftIds = Thrift.copyList(args.draftIds, [null]);
    }
    if (args.createdAfterInSecs !== undefined && args.createdAfterInSecs !== null) {
      this.createdAfterInSecs = args.createdAfterInSecs;
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.pagination !== undefined && args.pagination !== null) {
      this.pagination = new Pagination(args.pagination);
    }
    if (args.glanceType !== undefined && args.glanceType !== null) {
      this.glanceType = Thrift.copyList(args.glanceType, [null]);
    }
    if (args.categoryIds !== undefined && args.categoryIds !== null) {
      this.categoryIds = Thrift.copyList(args.categoryIds, [null]);
    }
    if (args.storyType !== undefined && args.storyType !== null) {
      this.storyType = Thrift.copyList(args.storyType, [null]);
    }
    if (args.publisherId !== undefined && args.publisherId !== null) {
      this.publisherId = args.publisherId;
    }
    if (args.status !== undefined && args.status !== null) {
      this.status = args.status;
    }
  }
};
GlanceDraftSearchQuery.prototype = {};
GlanceDraftSearchQuery.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size0 = 0;
        var _rtmp34;
        this.draftIds = [];
        var _etype3 = 0;
        _rtmp34 = input.readListBegin();
        _etype3 = _rtmp34.etype;
        _size0 = _rtmp34.size;
        for (var _i5 = 0; _i5 < _size0; ++_i5)
        {
          var elem6 = null;
          elem6 = input.readString().value;
          this.draftIds.push(elem6);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.createdAfterInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.pagination = new Pagination();
        this.pagination.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size7 = 0;
        var _rtmp311;
        this.glanceType = [];
        var _etype10 = 0;
        _rtmp311 = input.readListBegin();
        _etype10 = _rtmp311.etype;
        _size7 = _rtmp311.size;
        for (var _i12 = 0; _i12 < _size7; ++_i12)
        {
          var elem13 = null;
          elem13 = input.readString().value;
          this.glanceType.push(elem13);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size14 = 0;
        var _rtmp318;
        this.categoryIds = [];
        var _etype17 = 0;
        _rtmp318 = input.readListBegin();
        _etype17 = _rtmp318.etype;
        _size14 = _rtmp318.size;
        for (var _i19 = 0; _i19 < _size14; ++_i19)
        {
          var elem20 = null;
          elem20 = input.readString().value;
          this.categoryIds.push(elem20);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.LIST) {
        var _size21 = 0;
        var _rtmp325;
        this.storyType = [];
        var _etype24 = 0;
        _rtmp325 = input.readListBegin();
        _etype24 = _rtmp325.etype;
        _size21 = _rtmp325.size;
        for (var _i26 = 0; _i26 < _size21; ++_i26)
        {
          var elem27 = null;
          elem27 = input.readString().value;
          this.storyType.push(elem27);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRING) {
        this.publisherId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.STRING) {
        this.status = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftSearchQuery.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftSearchQuery');
  if (this.draftIds !== null && this.draftIds !== undefined) {
    output.writeFieldBegin('draftIds', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.draftIds.length);
    for (var iter28 in this.draftIds)
    {
      if (this.draftIds.hasOwnProperty(iter28))
      {
        iter28 = this.draftIds[iter28];
        output.writeString(iter28);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.createdAfterInSecs !== null && this.createdAfterInSecs !== undefined) {
    output.writeFieldBegin('createdAfterInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.createdAfterInSecs);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 3);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 4);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.pagination !== null && this.pagination !== undefined) {
    output.writeFieldBegin('pagination', Thrift.Type.STRUCT, 5);
    this.pagination.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceType !== null && this.glanceType !== undefined) {
    output.writeFieldBegin('glanceType', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.STRING, this.glanceType.length);
    for (var iter29 in this.glanceType)
    {
      if (this.glanceType.hasOwnProperty(iter29))
      {
        iter29 = this.glanceType[iter29];
        output.writeString(iter29);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.categoryIds !== null && this.categoryIds !== undefined) {
    output.writeFieldBegin('categoryIds', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRING, this.categoryIds.length);
    for (var iter30 in this.categoryIds)
    {
      if (this.categoryIds.hasOwnProperty(iter30))
      {
        iter30 = this.categoryIds[iter30];
        output.writeString(iter30);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.storyType !== null && this.storyType !== undefined) {
    output.writeFieldBegin('storyType', Thrift.Type.LIST, 8);
    output.writeListBegin(Thrift.Type.STRING, this.storyType.length);
    for (var iter31 in this.storyType)
    {
      if (this.storyType.hasOwnProperty(iter31))
      {
        iter31 = this.storyType[iter31];
        output.writeString(iter31);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.publisherId !== null && this.publisherId !== undefined) {
    output.writeFieldBegin('publisherId', Thrift.Type.STRING, 9);
    output.writeString(this.publisherId);
    output.writeFieldEnd();
  }
  if (this.status !== null && this.status !== undefined) {
    output.writeFieldBegin('status', Thrift.Type.STRING, 10);
    output.writeString(this.status);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftSearchResponse = function(args) {
  this.drafts = null;
  this.count = null;
  if (args) {
    if (args.drafts !== undefined && args.drafts !== null) {
      this.drafts = Thrift.copyList(args.drafts, [GlanceDraft]);
    }
    if (args.count !== undefined && args.count !== null) {
      this.count = args.count;
    }
  }
};
GlanceDraftSearchResponse.prototype = {};
GlanceDraftSearchResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size32 = 0;
        var _rtmp336;
        this.drafts = [];
        var _etype35 = 0;
        _rtmp336 = input.readListBegin();
        _etype35 = _rtmp336.etype;
        _size32 = _rtmp336.size;
        for (var _i37 = 0; _i37 < _size32; ++_i37)
        {
          var elem38 = null;
          elem38 = new GlanceDraft();
          elem38.read(input);
          this.drafts.push(elem38);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.count = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftSearchResponse.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftSearchResponse');
  if (this.drafts !== null && this.drafts !== undefined) {
    output.writeFieldBegin('drafts', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.drafts.length);
    for (var iter39 in this.drafts)
    {
      if (this.drafts.hasOwnProperty(iter39))
      {
        iter39 = this.drafts[iter39];
        iter39.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.count !== null && this.count !== undefined) {
    output.writeFieldBegin('count', Thrift.Type.I64, 2);
    output.writeI64(this.count);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancePublishResponse = function(args) {
  this.glanceId = null;
  this.data = null;
  this.metas = null;
  if (args) {
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
    if (args.data !== undefined && args.data !== null) {
      this.data = new GlanceData(args.data);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field data is unset!');
    }
    if (args.metas !== undefined && args.metas !== null) {
      this.metas = Thrift.copyList(args.metas, [GlanceMeta]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field metas is unset!');
    }
  }
};
GlancePublishResponse.prototype = {};
GlancePublishResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.data = new GlanceData();
        this.data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size40 = 0;
        var _rtmp344;
        this.metas = [];
        var _etype43 = 0;
        _rtmp344 = input.readListBegin();
        _etype43 = _rtmp344.etype;
        _size40 = _rtmp344.size;
        for (var _i45 = 0; _i45 < _size40; ++_i45)
        {
          var elem46 = null;
          elem46 = new GlanceMeta();
          elem46.read(input);
          this.metas.push(elem46);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancePublishResponse.prototype.write = function(output) {
  output.writeStructBegin('GlancePublishResponse');
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 1);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.data !== null && this.data !== undefined) {
    output.writeFieldBegin('data', Thrift.Type.STRUCT, 2);
    this.data.write(output);
    output.writeFieldEnd();
  }
  if (this.metas !== null && this.metas !== undefined) {
    output.writeFieldBegin('metas', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRUCT, this.metas.length);
    for (var iter47 in this.metas)
    {
      if (this.metas.hasOwnProperty(iter47))
      {
        iter47 = this.metas[iter47];
        iter47.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PublishedMetaResponse = function(args) {
  this.draftId = null;
  this.metas = null;
  if (args) {
    if (args.draftId !== undefined && args.draftId !== null) {
      this.draftId = args.draftId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field draftId is unset!');
    }
    if (args.metas !== undefined && args.metas !== null) {
      this.metas = Thrift.copyList(args.metas, [GlanceMeta]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field metas is unset!');
    }
  }
};
PublishedMetaResponse.prototype = {};
PublishedMetaResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.draftId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size48 = 0;
        var _rtmp352;
        this.metas = [];
        var _etype51 = 0;
        _rtmp352 = input.readListBegin();
        _etype51 = _rtmp352.etype;
        _size48 = _rtmp352.size;
        for (var _i53 = 0; _i53 < _size48; ++_i53)
        {
          var elem54 = null;
          elem54 = new GlanceMeta();
          elem54.read(input);
          this.metas.push(elem54);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PublishedMetaResponse.prototype.write = function(output) {
  output.writeStructBegin('PublishedMetaResponse');
  if (this.draftId !== null && this.draftId !== undefined) {
    output.writeFieldBegin('draftId', Thrift.Type.STRING, 1);
    output.writeString(this.draftId);
    output.writeFieldEnd();
  }
  if (this.metas !== null && this.metas !== undefined) {
    output.writeFieldBegin('metas', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRUCT, this.metas.length);
    for (var iter55 in this.metas)
    {
      if (this.metas.hasOwnProperty(iter55))
      {
        iter55 = this.metas[iter55];
        iter55.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PublishedMetaMapResponse = function(args) {
  this.draftToMetaMap = null;
  if (args) {
    if (args.draftToMetaMap !== undefined && args.draftToMetaMap !== null) {
      this.draftToMetaMap = Thrift.copyMap(args.draftToMetaMap, [Thrift.copyList, GlanceMeta]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field draftToMetaMap is unset!');
    }
  }
};
PublishedMetaMapResponse.prototype = {};
PublishedMetaMapResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.MAP) {
        var _size56 = 0;
        var _rtmp360;
        this.draftToMetaMap = {};
        var _ktype57 = 0;
        var _vtype58 = 0;
        _rtmp360 = input.readMapBegin();
        _ktype57 = _rtmp360.ktype;
        _vtype58 = _rtmp360.vtype;
        _size56 = _rtmp360.size;
        for (var _i61 = 0; _i61 < _size56; ++_i61)
        {
          if (_i61 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key62 = null;
          var val63 = null;
          key62 = input.readString().value;
          var _size64 = 0;
          var _rtmp368;
          val63 = [];
          var _etype67 = 0;
          _rtmp368 = input.readListBegin();
          _etype67 = _rtmp368.etype;
          _size64 = _rtmp368.size;
          for (var _i69 = 0; _i69 < _size64; ++_i69)
          {
            var elem70 = null;
            elem70 = new GlanceMeta();
            elem70.read(input);
            val63.push(elem70);
          }
          input.readListEnd();
          this.draftToMetaMap[key62] = val63;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PublishedMetaMapResponse.prototype.write = function(output) {
  output.writeStructBegin('PublishedMetaMapResponse');
  if (this.draftToMetaMap !== null && this.draftToMetaMap !== undefined) {
    output.writeFieldBegin('draftToMetaMap', Thrift.Type.MAP, 1);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.LIST, Thrift.objectLength(this.draftToMetaMap));
    for (var kiter71 in this.draftToMetaMap)
    {
      if (this.draftToMetaMap.hasOwnProperty(kiter71))
      {
        var viter72 = this.draftToMetaMap[kiter71];
        output.writeString(kiter71);
        output.writeListBegin(Thrift.Type.STRUCT, viter72.length);
        for (var iter73 in viter72)
        {
          if (viter72.hasOwnProperty(iter73))
          {
            iter73 = viter72[iter73];
            iter73.write(output);
          }
        }
        output.writeListEnd();
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftMetaSearchResponse = function(args) {
  this.drafts = null;
  this.count = null;
  if (args) {
    if (args.drafts !== undefined && args.drafts !== null) {
      this.drafts = Thrift.copyList(args.drafts, [GlanceDraft]);
    }
    if (args.count !== undefined && args.count !== null) {
      this.count = args.count;
    }
  }
};
GlanceDraftMetaSearchResponse.prototype = {};
GlanceDraftMetaSearchResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size74 = 0;
        var _rtmp378;
        this.drafts = [];
        var _etype77 = 0;
        _rtmp378 = input.readListBegin();
        _etype77 = _rtmp378.etype;
        _size74 = _rtmp378.size;
        for (var _i79 = 0; _i79 < _size74; ++_i79)
        {
          var elem80 = null;
          elem80 = new GlanceDraft();
          elem80.read(input);
          this.drafts.push(elem80);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.count = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftMetaSearchResponse.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftMetaSearchResponse');
  if (this.drafts !== null && this.drafts !== undefined) {
    output.writeFieldBegin('drafts', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.drafts.length);
    for (var iter81 in this.drafts)
    {
      if (this.drafts.hasOwnProperty(iter81))
      {
        iter81 = this.drafts[iter81];
        iter81.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.count !== null && this.count !== undefined) {
    output.writeFieldBegin('count', Thrift.Type.I64, 2);
    output.writeI64(this.count);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftMetaSearchQuery = function(args) {
  this.draftIds = null;
  this.createdAfterInSecs = null;
  this.partner = null;
  this.region = null;
  this.pagination = null;
  this.glanceType = null;
  this.categoryIds = null;
  this.publishedOnly = null;
  this.storyType = null;
  if (args) {
    if (args.draftIds !== undefined && args.draftIds !== null) {
      this.draftIds = Thrift.copyList(args.draftIds, [null]);
    }
    if (args.createdAfterInSecs !== undefined && args.createdAfterInSecs !== null) {
      this.createdAfterInSecs = args.createdAfterInSecs;
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.pagination !== undefined && args.pagination !== null) {
      this.pagination = new Pagination(args.pagination);
    }
    if (args.glanceType !== undefined && args.glanceType !== null) {
      this.glanceType = Thrift.copyList(args.glanceType, [null]);
    }
    if (args.categoryIds !== undefined && args.categoryIds !== null) {
      this.categoryIds = Thrift.copyList(args.categoryIds, [null]);
    }
    if (args.publishedOnly !== undefined && args.publishedOnly !== null) {
      this.publishedOnly = args.publishedOnly;
    }
    if (args.storyType !== undefined && args.storyType !== null) {
      this.storyType = Thrift.copyList(args.storyType, [null]);
    }
  }
};
GlanceDraftMetaSearchQuery.prototype = {};
GlanceDraftMetaSearchQuery.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size82 = 0;
        var _rtmp386;
        this.draftIds = [];
        var _etype85 = 0;
        _rtmp386 = input.readListBegin();
        _etype85 = _rtmp386.etype;
        _size82 = _rtmp386.size;
        for (var _i87 = 0; _i87 < _size82; ++_i87)
        {
          var elem88 = null;
          elem88 = input.readString().value;
          this.draftIds.push(elem88);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.createdAfterInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.pagination = new Pagination();
        this.pagination.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.LIST) {
        var _size89 = 0;
        var _rtmp393;
        this.glanceType = [];
        var _etype92 = 0;
        _rtmp393 = input.readListBegin();
        _etype92 = _rtmp393.etype;
        _size89 = _rtmp393.size;
        for (var _i94 = 0; _i94 < _size89; ++_i94)
        {
          var elem95 = null;
          elem95 = input.readString().value;
          this.glanceType.push(elem95);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size96 = 0;
        var _rtmp3100;
        this.categoryIds = [];
        var _etype99 = 0;
        _rtmp3100 = input.readListBegin();
        _etype99 = _rtmp3100.etype;
        _size96 = _rtmp3100.size;
        for (var _i101 = 0; _i101 < _size96; ++_i101)
        {
          var elem102 = null;
          elem102 = input.readString().value;
          this.categoryIds.push(elem102);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.BOOL) {
        this.publishedOnly = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size103 = 0;
        var _rtmp3107;
        this.storyType = [];
        var _etype106 = 0;
        _rtmp3107 = input.readListBegin();
        _etype106 = _rtmp3107.etype;
        _size103 = _rtmp3107.size;
        for (var _i108 = 0; _i108 < _size103; ++_i108)
        {
          var elem109 = null;
          elem109 = input.readString().value;
          this.storyType.push(elem109);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftMetaSearchQuery.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftMetaSearchQuery');
  if (this.draftIds !== null && this.draftIds !== undefined) {
    output.writeFieldBegin('draftIds', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.draftIds.length);
    for (var iter110 in this.draftIds)
    {
      if (this.draftIds.hasOwnProperty(iter110))
      {
        iter110 = this.draftIds[iter110];
        output.writeString(iter110);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.createdAfterInSecs !== null && this.createdAfterInSecs !== undefined) {
    output.writeFieldBegin('createdAfterInSecs', Thrift.Type.I64, 2);
    output.writeI64(this.createdAfterInSecs);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 3);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 4);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.pagination !== null && this.pagination !== undefined) {
    output.writeFieldBegin('pagination', Thrift.Type.STRUCT, 5);
    this.pagination.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceType !== null && this.glanceType !== undefined) {
    output.writeFieldBegin('glanceType', Thrift.Type.LIST, 6);
    output.writeListBegin(Thrift.Type.STRING, this.glanceType.length);
    for (var iter111 in this.glanceType)
    {
      if (this.glanceType.hasOwnProperty(iter111))
      {
        iter111 = this.glanceType[iter111];
        output.writeString(iter111);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.categoryIds !== null && this.categoryIds !== undefined) {
    output.writeFieldBegin('categoryIds', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRING, this.categoryIds.length);
    for (var iter112 in this.categoryIds)
    {
      if (this.categoryIds.hasOwnProperty(iter112))
      {
        iter112 = this.categoryIds[iter112];
        output.writeString(iter112);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.publishedOnly !== null && this.publishedOnly !== undefined) {
    output.writeFieldBegin('publishedOnly', Thrift.Type.BOOL, 8);
    output.writeBool(this.publishedOnly);
    output.writeFieldEnd();
  }
  if (this.storyType !== null && this.storyType !== undefined) {
    output.writeFieldBegin('storyType', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRING, this.storyType.length);
    for (var iter113 in this.storyType)
    {
      if (this.storyType.hasOwnProperty(iter113))
      {
        iter113 = this.storyType[iter113];
        output.writeString(iter113);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancePairSearchQuery = function(args) {
  this.ids = null;
  this.partner = null;
  this.region = null;
  this.startsAt = null;
  this.endsAt = null;
  this.createdAfterInSecs = null;
  this.glanceType = null;
  this.pagination = null;
  this.state = null;
  this.sourceName = null;
  this.expired = null;
  this.categoryIds = null;
  this.storyType = null;
  this.priority = null;
  this.languages = null;
  if (args) {
    if (args.ids !== undefined && args.ids !== null) {
      this.ids = Thrift.copyList(args.ids, [null]);
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.startsAt !== undefined && args.startsAt !== null) {
      this.startsAt = new RelativeTime(args.startsAt);
    }
    if (args.endsAt !== undefined && args.endsAt !== null) {
      this.endsAt = new RelativeTime(args.endsAt);
    }
    if (args.createdAfterInSecs !== undefined && args.createdAfterInSecs !== null) {
      this.createdAfterInSecs = args.createdAfterInSecs;
    }
    if (args.glanceType !== undefined && args.glanceType !== null) {
      this.glanceType = Thrift.copyList(args.glanceType, [null]);
    }
    if (args.pagination !== undefined && args.pagination !== null) {
      this.pagination = new Pagination(args.pagination);
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = Thrift.copyList(args.state, [null]);
    }
    if (args.sourceName !== undefined && args.sourceName !== null) {
      this.sourceName = Thrift.copyList(args.sourceName, [null]);
    }
    if (args.expired !== undefined && args.expired !== null) {
      this.expired = args.expired;
    }
    if (args.categoryIds !== undefined && args.categoryIds !== null) {
      this.categoryIds = Thrift.copyList(args.categoryIds, [null]);
    }
    if (args.storyType !== undefined && args.storyType !== null) {
      this.storyType = Thrift.copyList(args.storyType, [null]);
    }
    if (args.priority !== undefined && args.priority !== null) {
      this.priority = args.priority;
    }
    if (args.languages !== undefined && args.languages !== null) {
      this.languages = Thrift.copyList(args.languages, [null]);
    }
  }
};
GlancePairSearchQuery.prototype = {};
GlancePairSearchQuery.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size114 = 0;
        var _rtmp3118;
        this.ids = [];
        var _etype117 = 0;
        _rtmp3118 = input.readListBegin();
        _etype117 = _rtmp3118.etype;
        _size114 = _rtmp3118.size;
        for (var _i119 = 0; _i119 < _size114; ++_i119)
        {
          var elem120 = null;
          elem120 = input.readString().value;
          this.ids.push(elem120);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.startsAt = new RelativeTime();
        this.startsAt.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.endsAt = new RelativeTime();
        this.endsAt.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.I64) {
        this.createdAfterInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size121 = 0;
        var _rtmp3125;
        this.glanceType = [];
        var _etype124 = 0;
        _rtmp3125 = input.readListBegin();
        _etype124 = _rtmp3125.etype;
        _size121 = _rtmp3125.size;
        for (var _i126 = 0; _i126 < _size121; ++_i126)
        {
          var elem127 = null;
          elem127 = input.readString().value;
          this.glanceType.push(elem127);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRUCT) {
        this.pagination = new Pagination();
        this.pagination.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size128 = 0;
        var _rtmp3132;
        this.state = [];
        var _etype131 = 0;
        _rtmp3132 = input.readListBegin();
        _etype131 = _rtmp3132.etype;
        _size128 = _rtmp3132.size;
        for (var _i133 = 0; _i133 < _size128; ++_i133)
        {
          var elem134 = null;
          elem134 = input.readString().value;
          this.state.push(elem134);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.LIST) {
        var _size135 = 0;
        var _rtmp3139;
        this.sourceName = [];
        var _etype138 = 0;
        _rtmp3139 = input.readListBegin();
        _etype138 = _rtmp3139.etype;
        _size135 = _rtmp3139.size;
        for (var _i140 = 0; _i140 < _size135; ++_i140)
        {
          var elem141 = null;
          elem141 = input.readString().value;
          this.sourceName.push(elem141);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.BOOL) {
        this.expired = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.LIST) {
        var _size142 = 0;
        var _rtmp3146;
        this.categoryIds = [];
        var _etype145 = 0;
        _rtmp3146 = input.readListBegin();
        _etype145 = _rtmp3146.etype;
        _size142 = _rtmp3146.size;
        for (var _i147 = 0; _i147 < _size142; ++_i147)
        {
          var elem148 = null;
          elem148 = input.readString().value;
          this.categoryIds.push(elem148);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size149 = 0;
        var _rtmp3153;
        this.storyType = [];
        var _etype152 = 0;
        _rtmp3153 = input.readListBegin();
        _etype152 = _rtmp3153.etype;
        _size149 = _rtmp3153.size;
        for (var _i154 = 0; _i154 < _size149; ++_i154)
        {
          var elem155 = null;
          elem155 = input.readString().value;
          this.storyType.push(elem155);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.I32) {
        this.priority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.LIST) {
        var _size156 = 0;
        var _rtmp3160;
        this.languages = [];
        var _etype159 = 0;
        _rtmp3160 = input.readListBegin();
        _etype159 = _rtmp3160.etype;
        _size156 = _rtmp3160.size;
        for (var _i161 = 0; _i161 < _size156; ++_i161)
        {
          var elem162 = null;
          elem162 = input.readString().value;
          this.languages.push(elem162);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancePairSearchQuery.prototype.write = function(output) {
  output.writeStructBegin('GlancePairSearchQuery');
  if (this.ids !== null && this.ids !== undefined) {
    output.writeFieldBegin('ids', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.ids.length);
    for (var iter163 in this.ids)
    {
      if (this.ids.hasOwnProperty(iter163))
      {
        iter163 = this.ids[iter163];
        output.writeString(iter163);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 2);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 3);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.startsAt !== null && this.startsAt !== undefined) {
    output.writeFieldBegin('startsAt', Thrift.Type.STRUCT, 4);
    this.startsAt.write(output);
    output.writeFieldEnd();
  }
  if (this.endsAt !== null && this.endsAt !== undefined) {
    output.writeFieldBegin('endsAt', Thrift.Type.STRUCT, 5);
    this.endsAt.write(output);
    output.writeFieldEnd();
  }
  if (this.createdAfterInSecs !== null && this.createdAfterInSecs !== undefined) {
    output.writeFieldBegin('createdAfterInSecs', Thrift.Type.I64, 6);
    output.writeI64(this.createdAfterInSecs);
    output.writeFieldEnd();
  }
  if (this.glanceType !== null && this.glanceType !== undefined) {
    output.writeFieldBegin('glanceType', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRING, this.glanceType.length);
    for (var iter164 in this.glanceType)
    {
      if (this.glanceType.hasOwnProperty(iter164))
      {
        iter164 = this.glanceType[iter164];
        output.writeString(iter164);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.pagination !== null && this.pagination !== undefined) {
    output.writeFieldBegin('pagination', Thrift.Type.STRUCT, 8);
    this.pagination.write(output);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRING, this.state.length);
    for (var iter165 in this.state)
    {
      if (this.state.hasOwnProperty(iter165))
      {
        iter165 = this.state[iter165];
        output.writeString(iter165);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.sourceName !== null && this.sourceName !== undefined) {
    output.writeFieldBegin('sourceName', Thrift.Type.LIST, 10);
    output.writeListBegin(Thrift.Type.STRING, this.sourceName.length);
    for (var iter166 in this.sourceName)
    {
      if (this.sourceName.hasOwnProperty(iter166))
      {
        iter166 = this.sourceName[iter166];
        output.writeString(iter166);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.expired !== null && this.expired !== undefined) {
    output.writeFieldBegin('expired', Thrift.Type.BOOL, 11);
    output.writeBool(this.expired);
    output.writeFieldEnd();
  }
  if (this.categoryIds !== null && this.categoryIds !== undefined) {
    output.writeFieldBegin('categoryIds', Thrift.Type.LIST, 12);
    output.writeListBegin(Thrift.Type.STRING, this.categoryIds.length);
    for (var iter167 in this.categoryIds)
    {
      if (this.categoryIds.hasOwnProperty(iter167))
      {
        iter167 = this.categoryIds[iter167];
        output.writeString(iter167);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.storyType !== null && this.storyType !== undefined) {
    output.writeFieldBegin('storyType', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRING, this.storyType.length);
    for (var iter168 in this.storyType)
    {
      if (this.storyType.hasOwnProperty(iter168))
      {
        iter168 = this.storyType[iter168];
        output.writeString(iter168);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.priority !== null && this.priority !== undefined) {
    output.writeFieldBegin('priority', Thrift.Type.I32, 14);
    output.writeI32(this.priority);
    output.writeFieldEnd();
  }
  if (this.languages !== null && this.languages !== undefined) {
    output.writeFieldBegin('languages', Thrift.Type.LIST, 15);
    output.writeListBegin(Thrift.Type.STRING, this.languages.length);
    for (var iter169 in this.languages)
    {
      if (this.languages.hasOwnProperty(iter169))
      {
        iter169 = this.languages[iter169];
        output.writeString(iter169);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancePauseRequest = function(args) {
  this.ids = null;
  this.pause = null;
  if (args) {
    if (args.ids !== undefined && args.ids !== null) {
      this.ids = Thrift.copyList(args.ids, [null]);
    }
    if (args.pause !== undefined && args.pause !== null) {
      this.pause = args.pause;
    }
  }
};
GlancePauseRequest.prototype = {};
GlancePauseRequest.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size170 = 0;
        var _rtmp3174;
        this.ids = [];
        var _etype173 = 0;
        _rtmp3174 = input.readListBegin();
        _etype173 = _rtmp3174.etype;
        _size170 = _rtmp3174.size;
        for (var _i175 = 0; _i175 < _size170; ++_i175)
        {
          var elem176 = null;
          elem176 = input.readString().value;
          this.ids.push(elem176);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.BOOL) {
        this.pause = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancePauseRequest.prototype.write = function(output) {
  output.writeStructBegin('GlancePauseRequest');
  if (this.ids !== null && this.ids !== undefined) {
    output.writeFieldBegin('ids', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.ids.length);
    for (var iter177 in this.ids)
    {
      if (this.ids.hasOwnProperty(iter177))
      {
        iter177 = this.ids[iter177];
        output.writeString(iter177);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.pause !== null && this.pause !== undefined) {
    output.writeFieldBegin('pause', Thrift.Type.BOOL, 2);
    output.writeBool(this.pause);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancePauseResponse = function(args) {
};
GlancePauseResponse.prototype = {};
GlancePauseResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancePauseResponse.prototype.write = function(output) {
  output.writeStructBegin('GlancePauseResponse');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerPairSearchQuery = function(args) {
  this.ids = null;
  this.region = null;
  this.startsAt = null;
  this.endsAt = null;
  this.createdAfterInSecs = null;
  this.partner = null;
  this.glanceType = null;
  this.pagination = null;
  this.state = null;
  this.sourceName = null;
  this.expired = null;
  this.categoryIds = null;
  this.storyType = null;
  this.priority = null;
  this.languages = null;
  if (args) {
    if (args.ids !== undefined && args.ids !== null) {
      this.ids = Thrift.copyList(args.ids, [null]);
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.startsAt !== undefined && args.startsAt !== null) {
      this.startsAt = new RelativeTime(args.startsAt);
    }
    if (args.endsAt !== undefined && args.endsAt !== null) {
      this.endsAt = new RelativeTime(args.endsAt);
    }
    if (args.createdAfterInSecs !== undefined && args.createdAfterInSecs !== null) {
      this.createdAfterInSecs = args.createdAfterInSecs;
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    }
    if (args.glanceType !== undefined && args.glanceType !== null) {
      this.glanceType = Thrift.copyList(args.glanceType, [null]);
    }
    if (args.pagination !== undefined && args.pagination !== null) {
      this.pagination = new Pagination(args.pagination);
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = Thrift.copyList(args.state, [null]);
    }
    if (args.sourceName !== undefined && args.sourceName !== null) {
      this.sourceName = Thrift.copyList(args.sourceName, [null]);
    }
    if (args.expired !== undefined && args.expired !== null) {
      this.expired = args.expired;
    }
    if (args.categoryIds !== undefined && args.categoryIds !== null) {
      this.categoryIds = Thrift.copyList(args.categoryIds, [null]);
    }
    if (args.storyType !== undefined && args.storyType !== null) {
      this.storyType = Thrift.copyList(args.storyType, [null]);
    }
    if (args.priority !== undefined && args.priority !== null) {
      this.priority = args.priority;
    }
    if (args.languages !== undefined && args.languages !== null) {
      this.languages = Thrift.copyList(args.languages, [null]);
    }
  }
};
PartnerPairSearchQuery.prototype = {};
PartnerPairSearchQuery.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size178 = 0;
        var _rtmp3182;
        this.ids = [];
        var _etype181 = 0;
        _rtmp3182 = input.readListBegin();
        _etype181 = _rtmp3182.etype;
        _size178 = _rtmp3182.size;
        for (var _i183 = 0; _i183 < _size178; ++_i183)
        {
          var elem184 = null;
          elem184 = input.readString().value;
          this.ids.push(elem184);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.startsAt = new RelativeTime();
        this.startsAt.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRUCT) {
        this.endsAt = new RelativeTime();
        this.endsAt.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.createdAfterInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.LIST) {
        var _size185 = 0;
        var _rtmp3189;
        this.glanceType = [];
        var _etype188 = 0;
        _rtmp3189 = input.readListBegin();
        _etype188 = _rtmp3189.etype;
        _size185 = _rtmp3189.size;
        for (var _i190 = 0; _i190 < _size185; ++_i190)
        {
          var elem191 = null;
          elem191 = input.readString().value;
          this.glanceType.push(elem191);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.STRUCT) {
        this.pagination = new Pagination();
        this.pagination.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.LIST) {
        var _size192 = 0;
        var _rtmp3196;
        this.state = [];
        var _etype195 = 0;
        _rtmp3196 = input.readListBegin();
        _etype195 = _rtmp3196.etype;
        _size192 = _rtmp3196.size;
        for (var _i197 = 0; _i197 < _size192; ++_i197)
        {
          var elem198 = null;
          elem198 = input.readString().value;
          this.state.push(elem198);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.LIST) {
        var _size199 = 0;
        var _rtmp3203;
        this.sourceName = [];
        var _etype202 = 0;
        _rtmp3203 = input.readListBegin();
        _etype202 = _rtmp3203.etype;
        _size199 = _rtmp3203.size;
        for (var _i204 = 0; _i204 < _size199; ++_i204)
        {
          var elem205 = null;
          elem205 = input.readString().value;
          this.sourceName.push(elem205);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.BOOL) {
        this.expired = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.LIST) {
        var _size206 = 0;
        var _rtmp3210;
        this.categoryIds = [];
        var _etype209 = 0;
        _rtmp3210 = input.readListBegin();
        _etype209 = _rtmp3210.etype;
        _size206 = _rtmp3210.size;
        for (var _i211 = 0; _i211 < _size206; ++_i211)
        {
          var elem212 = null;
          elem212 = input.readString().value;
          this.categoryIds.push(elem212);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size213 = 0;
        var _rtmp3217;
        this.storyType = [];
        var _etype216 = 0;
        _rtmp3217 = input.readListBegin();
        _etype216 = _rtmp3217.etype;
        _size213 = _rtmp3217.size;
        for (var _i218 = 0; _i218 < _size213; ++_i218)
        {
          var elem219 = null;
          elem219 = input.readString().value;
          this.storyType.push(elem219);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.I32) {
        this.priority = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.LIST) {
        var _size220 = 0;
        var _rtmp3224;
        this.languages = [];
        var _etype223 = 0;
        _rtmp3224 = input.readListBegin();
        _etype223 = _rtmp3224.etype;
        _size220 = _rtmp3224.size;
        for (var _i225 = 0; _i225 < _size220; ++_i225)
        {
          var elem226 = null;
          elem226 = input.readString().value;
          this.languages.push(elem226);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerPairSearchQuery.prototype.write = function(output) {
  output.writeStructBegin('PartnerPairSearchQuery');
  if (this.ids !== null && this.ids !== undefined) {
    output.writeFieldBegin('ids', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRING, this.ids.length);
    for (var iter227 in this.ids)
    {
      if (this.ids.hasOwnProperty(iter227))
      {
        iter227 = this.ids[iter227];
        output.writeString(iter227);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 2);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.startsAt !== null && this.startsAt !== undefined) {
    output.writeFieldBegin('startsAt', Thrift.Type.STRUCT, 3);
    this.startsAt.write(output);
    output.writeFieldEnd();
  }
  if (this.endsAt !== null && this.endsAt !== undefined) {
    output.writeFieldBegin('endsAt', Thrift.Type.STRUCT, 4);
    this.endsAt.write(output);
    output.writeFieldEnd();
  }
  if (this.createdAfterInSecs !== null && this.createdAfterInSecs !== undefined) {
    output.writeFieldBegin('createdAfterInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.createdAfterInSecs);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 6);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.glanceType !== null && this.glanceType !== undefined) {
    output.writeFieldBegin('glanceType', Thrift.Type.LIST, 7);
    output.writeListBegin(Thrift.Type.STRING, this.glanceType.length);
    for (var iter228 in this.glanceType)
    {
      if (this.glanceType.hasOwnProperty(iter228))
      {
        iter228 = this.glanceType[iter228];
        output.writeString(iter228);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.pagination !== null && this.pagination !== undefined) {
    output.writeFieldBegin('pagination', Thrift.Type.STRUCT, 8);
    this.pagination.write(output);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.LIST, 9);
    output.writeListBegin(Thrift.Type.STRING, this.state.length);
    for (var iter229 in this.state)
    {
      if (this.state.hasOwnProperty(iter229))
      {
        iter229 = this.state[iter229];
        output.writeString(iter229);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.sourceName !== null && this.sourceName !== undefined) {
    output.writeFieldBegin('sourceName', Thrift.Type.LIST, 10);
    output.writeListBegin(Thrift.Type.STRING, this.sourceName.length);
    for (var iter230 in this.sourceName)
    {
      if (this.sourceName.hasOwnProperty(iter230))
      {
        iter230 = this.sourceName[iter230];
        output.writeString(iter230);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.expired !== null && this.expired !== undefined) {
    output.writeFieldBegin('expired', Thrift.Type.BOOL, 11);
    output.writeBool(this.expired);
    output.writeFieldEnd();
  }
  if (this.categoryIds !== null && this.categoryIds !== undefined) {
    output.writeFieldBegin('categoryIds', Thrift.Type.LIST, 12);
    output.writeListBegin(Thrift.Type.STRING, this.categoryIds.length);
    for (var iter231 in this.categoryIds)
    {
      if (this.categoryIds.hasOwnProperty(iter231))
      {
        iter231 = this.categoryIds[iter231];
        output.writeString(iter231);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.storyType !== null && this.storyType !== undefined) {
    output.writeFieldBegin('storyType', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRING, this.storyType.length);
    for (var iter232 in this.storyType)
    {
      if (this.storyType.hasOwnProperty(iter232))
      {
        iter232 = this.storyType[iter232];
        output.writeString(iter232);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.priority !== null && this.priority !== undefined) {
    output.writeFieldBegin('priority', Thrift.Type.I32, 14);
    output.writeI32(this.priority);
    output.writeFieldEnd();
  }
  if (this.languages !== null && this.languages !== undefined) {
    output.writeFieldBegin('languages', Thrift.Type.LIST, 15);
    output.writeListBegin(Thrift.Type.STRING, this.languages.length);
    for (var iter233 in this.languages)
    {
      if (this.languages.hasOwnProperty(iter233))
      {
        iter233 = this.languages[iter233];
        output.writeString(iter233);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlancePairSearchResponse = function(args) {
  this.pairs = null;
  this.count = null;
  if (args) {
    if (args.pairs !== undefined && args.pairs !== null) {
      this.pairs = Thrift.copyList(args.pairs, [GlancePair]);
    }
    if (args.count !== undefined && args.count !== null) {
      this.count = args.count;
    }
  }
};
GlancePairSearchResponse.prototype = {};
GlancePairSearchResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.LIST) {
        var _size234 = 0;
        var _rtmp3238;
        this.pairs = [];
        var _etype237 = 0;
        _rtmp3238 = input.readListBegin();
        _etype237 = _rtmp3238.etype;
        _size234 = _rtmp3238.size;
        for (var _i239 = 0; _i239 < _size234; ++_i239)
        {
          var elem240 = null;
          elem240 = new GlancePair();
          elem240.read(input);
          this.pairs.push(elem240);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I64) {
        this.count = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlancePairSearchResponse.prototype.write = function(output) {
  output.writeStructBegin('GlancePairSearchResponse');
  if (this.pairs !== null && this.pairs !== undefined) {
    output.writeFieldBegin('pairs', Thrift.Type.LIST, 1);
    output.writeListBegin(Thrift.Type.STRUCT, this.pairs.length);
    for (var iter241 in this.pairs)
    {
      if (this.pairs.hasOwnProperty(iter241))
      {
        iter241 = this.pairs[iter241];
        iter241.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.count !== null && this.count !== undefined) {
    output.writeFieldBegin('count', Thrift.Type.I64, 2);
    output.writeI64(this.count);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceApproveResponse = function(args) {
  this.data = null;
  if (args) {
    if (args.data !== undefined && args.data !== null) {
      this.data = new GlanceData(args.data);
    }
  }
};
GlanceApproveResponse.prototype = {};
GlanceApproveResponse.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.data = new GlanceData();
        this.data.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceApproveResponse.prototype.write = function(output) {
  output.writeStructBegin('GlanceApproveResponse');
  if (this.data !== null && this.data !== undefined) {
    output.writeFieldBegin('data', Thrift.Type.STRUCT, 1);
    this.data.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthException = function(args) {
  this.message = null;
  if (args) {
    if (args.message !== undefined && args.message !== null) {
      this.message = args.message;
    }
  }
};
Thrift.inherits(AuthException, Thrift.TException);
AuthException.prototype.name = 'AuthException';
AuthException.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.message = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthException.prototype.write = function(output) {
  output.writeStructBegin('AuthException');
  if (this.message !== null && this.message !== undefined) {
    output.writeFieldBegin('message', Thrift.Type.STRING, 1);
    output.writeString(this.message);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


TvUserDiff = function(args) {
  this.currentUser = null;
  this.updatedUser = null;
  if (args) {
    if (args.currentUser !== undefined && args.currentUser !== null) {
      this.currentUser = new TvUser(args.currentUser);
    }
    if (args.updatedUser !== undefined && args.updatedUser !== null) {
      this.updatedUser = new TvUser(args.updatedUser);
    }
  }
};
TvUserDiff.prototype = {};
TvUserDiff.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.currentUser = new TvUser();
        this.currentUser.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.updatedUser = new TvUser();
        this.updatedUser.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TvUserDiff.prototype.write = function(output) {
  output.writeStructBegin('TvUserDiff');
  if (this.currentUser !== null && this.currentUser !== undefined) {
    output.writeFieldBegin('currentUser', Thrift.Type.STRUCT, 1);
    this.currentUser.write(output);
    output.writeFieldEnd();
  }
  if (this.updatedUser !== null && this.updatedUser !== undefined) {
    output.writeFieldBegin('updatedUser', Thrift.Type.STRUCT, 2);
    this.updatedUser.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TvUser = function(args) {
  this.userId = null;
  this.apiKey = null;
  this.partnerId = null;
  this.createdAtInSecs = null;
  this.updatedAtInSecs = null;
  this.deviceInfo = null;
  this.eulaAcceptedState = null;
  this.eulaAcceptedStateUpdatedAtInSecs = null;
  this.enabledState = null;
  this.enabledStateUpdatedAtInSecs = null;
  this.gpId = null;
  this.subscriptions = null;
  this.preferredNetworkType = null;
  this.extras = null;
  this.preferredCategories = null;
  this.version = null;
  this.deviceId = null;
  this.disableActionType = null;
  this.enableReferrer = null;
  this.adPersonalizationEnabled = null;
  this.firstEnableReferrer = null;
  this.clientVersion = null;
  this.subscribedCategories = null;
  this.segments = null;
  this.showRecommendations = null;
  this.firstEnableAtInSecs = null;
  this.inmobiAdOptOut = null;
  this.categoryOptOut = null;
  this.languageSubscriptions = null;
  this.id = null;
  this.nickname = null;
  this.sdkVersion = null;
  this.state = null;
  this.city = null;
  this.region = null;
  this.locale = null;
  this.dynamicSegments = null;
  this.userEngagement = null;
  this.fcmToken = null;
  this.dataSaverEnabled = null;
  this.ownedAppPackageNames = null;
  this.resetCacheAtInSecs = null;
  this.highlightsDetails = null;
  this.userStated = null;
  this.sdkFeatures = null;
  this.isChildLockEnabled = null;
  this.childLockTimeLimit = null;
  this.secondaryFcmToken = null;
  this.monthAndYearOfBirth = null;
  this.locationInfo = null;
  this.isUnderAge = null;
  this.appLocale = null;
  this.systemFlags = null;
  this.spacePreferences = null;
  this.geo = null;
  this.isFineLocation = null;
  if (args) {
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    }
    if (args.apiKey !== undefined && args.apiKey !== null) {
      this.apiKey = args.apiKey;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field apiKey is unset!');
    }
    if (args.partnerId !== undefined && args.partnerId !== null) {
      this.partnerId = args.partnerId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partnerId is unset!');
    }
    if (args.createdAtInSecs !== undefined && args.createdAtInSecs !== null) {
      this.createdAtInSecs = args.createdAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field createdAtInSecs is unset!');
    }
    if (args.updatedAtInSecs !== undefined && args.updatedAtInSecs !== null) {
      this.updatedAtInSecs = args.updatedAtInSecs;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field updatedAtInSecs is unset!');
    }
    if (args.deviceInfo !== undefined && args.deviceInfo !== null) {
      this.deviceInfo = new DeviceInfo(args.deviceInfo);
    }
    if (args.eulaAcceptedState !== undefined && args.eulaAcceptedState !== null) {
      this.eulaAcceptedState = args.eulaAcceptedState;
    }
    if (args.eulaAcceptedStateUpdatedAtInSecs !== undefined && args.eulaAcceptedStateUpdatedAtInSecs !== null) {
      this.eulaAcceptedStateUpdatedAtInSecs = args.eulaAcceptedStateUpdatedAtInSecs;
    }
    if (args.enabledState !== undefined && args.enabledState !== null) {
      this.enabledState = args.enabledState;
    }
    if (args.enabledStateUpdatedAtInSecs !== undefined && args.enabledStateUpdatedAtInSecs !== null) {
      this.enabledStateUpdatedAtInSecs = args.enabledStateUpdatedAtInSecs;
    }
    if (args.gpId !== undefined && args.gpId !== null) {
      this.gpId = args.gpId;
    }
    if (args.subscriptions !== undefined && args.subscriptions !== null) {
      this.subscriptions = Thrift.copyList(args.subscriptions, [SubscriptionMeta]);
    }
    if (args.preferredNetworkType !== undefined && args.preferredNetworkType !== null) {
      this.preferredNetworkType = args.preferredNetworkType;
    }
    if (args.extras !== undefined && args.extras !== null) {
      this.extras = new UserExtras(args.extras);
    }
    if (args.preferredCategories !== undefined && args.preferredCategories !== null) {
      this.preferredCategories = Thrift.copyList(args.preferredCategories, [null]);
    }
    if (args.version !== undefined && args.version !== null) {
      this.version = args.version;
    }
    if (args.deviceId !== undefined && args.deviceId !== null) {
      this.deviceId = args.deviceId;
    }
    if (args.disableActionType !== undefined && args.disableActionType !== null) {
      this.disableActionType = args.disableActionType;
    }
    if (args.enableReferrer !== undefined && args.enableReferrer !== null) {
      this.enableReferrer = args.enableReferrer;
    }
    if (args.adPersonalizationEnabled !== undefined && args.adPersonalizationEnabled !== null) {
      this.adPersonalizationEnabled = args.adPersonalizationEnabled;
    }
    if (args.firstEnableReferrer !== undefined && args.firstEnableReferrer !== null) {
      this.firstEnableReferrer = args.firstEnableReferrer;
    }
    if (args.clientVersion !== undefined && args.clientVersion !== null) {
      this.clientVersion = args.clientVersion;
    }
    if (args.subscribedCategories !== undefined && args.subscribedCategories !== null) {
      this.subscribedCategories = Thrift.copyMap(args.subscribedCategories, [null]);
    }
    if (args.segments !== undefined && args.segments !== null) {
      this.segments = Thrift.copyList(args.segments, [Segment]);
    }
    if (args.showRecommendations !== undefined && args.showRecommendations !== null) {
      this.showRecommendations = args.showRecommendations;
    }
    if (args.firstEnableAtInSecs !== undefined && args.firstEnableAtInSecs !== null) {
      this.firstEnableAtInSecs = args.firstEnableAtInSecs;
    }
    if (args.inmobiAdOptOut !== undefined && args.inmobiAdOptOut !== null) {
      this.inmobiAdOptOut = args.inmobiAdOptOut;
    }
    if (args.categoryOptOut !== undefined && args.categoryOptOut !== null) {
      this.categoryOptOut = args.categoryOptOut;
    }
    if (args.languageSubscriptions !== undefined && args.languageSubscriptions !== null) {
      this.languageSubscriptions = Thrift.copyList(args.languageSubscriptions, [LanguageSubscription]);
    }
    if (args.id !== undefined && args.id !== null) {
      this.id = args.id;
    }
    if (args.nickname !== undefined && args.nickname !== null) {
      this.nickname = args.nickname;
    }
    if (args.sdkVersion !== undefined && args.sdkVersion !== null) {
      this.sdkVersion = args.sdkVersion;
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    }
    if (args.city !== undefined && args.city !== null) {
      this.city = args.city;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.locale !== undefined && args.locale !== null) {
      this.locale = args.locale;
    }
    if (args.dynamicSegments !== undefined && args.dynamicSegments !== null) {
      this.dynamicSegments = Thrift.copyList(args.dynamicSegments, [null]);
    }
    if (args.userEngagement !== undefined && args.userEngagement !== null) {
      this.userEngagement = new UserEngagement(args.userEngagement);
    }
    if (args.fcmToken !== undefined && args.fcmToken !== null) {
      this.fcmToken = args.fcmToken;
    }
    if (args.dataSaverEnabled !== undefined && args.dataSaverEnabled !== null) {
      this.dataSaverEnabled = args.dataSaverEnabled;
    }
    if (args.ownedAppPackageNames !== undefined && args.ownedAppPackageNames !== null) {
      this.ownedAppPackageNames = Thrift.copyList(args.ownedAppPackageNames, [null]);
    }
    if (args.resetCacheAtInSecs !== undefined && args.resetCacheAtInSecs !== null) {
      this.resetCacheAtInSecs = args.resetCacheAtInSecs;
    }
    if (args.highlightsDetails !== undefined && args.highlightsDetails !== null) {
      this.highlightsDetails = new HighlightsDetails(args.highlightsDetails);
    }
    if (args.userStated !== undefined && args.userStated !== null) {
      this.userStated = new UserStated(args.userStated);
    }
    if (args.sdkFeatures !== undefined && args.sdkFeatures !== null) {
      this.sdkFeatures = new SdkFeatures(args.sdkFeatures);
    }
    if (args.isChildLockEnabled !== undefined && args.isChildLockEnabled !== null) {
      this.isChildLockEnabled = args.isChildLockEnabled;
    }
    if (args.childLockTimeLimit !== undefined && args.childLockTimeLimit !== null) {
      this.childLockTimeLimit = args.childLockTimeLimit;
    }
    if (args.secondaryFcmToken !== undefined && args.secondaryFcmToken !== null) {
      this.secondaryFcmToken = args.secondaryFcmToken;
    }
    if (args.monthAndYearOfBirth !== undefined && args.monthAndYearOfBirth !== null) {
      this.monthAndYearOfBirth = args.monthAndYearOfBirth;
    }
    if (args.locationInfo !== undefined && args.locationInfo !== null) {
      this.locationInfo = new LocationInfo(args.locationInfo);
    }
    if (args.isUnderAge !== undefined && args.isUnderAge !== null) {
      this.isUnderAge = args.isUnderAge;
    }
    if (args.appLocale !== undefined && args.appLocale !== null) {
      this.appLocale = new AppLocale(args.appLocale);
    }
    if (args.systemFlags !== undefined && args.systemFlags !== null) {
      this.systemFlags = new SystemFlags(args.systemFlags);
    }
    if (args.spacePreferences !== undefined && args.spacePreferences !== null) {
      this.spacePreferences = new SpacePreferences(args.spacePreferences);
    }
    if (args.geo !== undefined && args.geo !== null) {
      this.geo = new Geo(args.geo);
    }
    if (args.isFineLocation !== undefined && args.isFineLocation !== null) {
      this.isFineLocation = args.isFineLocation;
    }
  }
};
TvUser.prototype = {};
TvUser.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.apiKey = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.partnerId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.I64) {
        this.createdAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.I64) {
        this.updatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.STRUCT) {
        this.deviceInfo = new DeviceInfo();
        this.deviceInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.BOOL) {
        this.eulaAcceptedState = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I64) {
        this.eulaAcceptedStateUpdatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.BOOL) {
        this.enabledState = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.I64) {
        this.enabledStateUpdatedAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.STRING) {
        this.gpId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size0 = 0;
        var _rtmp34;
        this.subscriptions = [];
        var _etype3 = 0;
        _rtmp34 = input.readListBegin();
        _etype3 = _rtmp34.etype;
        _size0 = _rtmp34.size;
        for (var _i5 = 0; _i5 < _size0; ++_i5)
        {
          var elem6 = null;
          elem6 = new SubscriptionMeta();
          elem6.read(input);
          this.subscriptions.push(elem6);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.I32) {
        this.preferredNetworkType = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.STRUCT) {
        this.extras = new UserExtras();
        this.extras.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.LIST) {
        var _size7 = 0;
        var _rtmp311;
        this.preferredCategories = [];
        var _etype10 = 0;
        _rtmp311 = input.readListBegin();
        _etype10 = _rtmp311.etype;
        _size7 = _rtmp311.size;
        for (var _i12 = 0; _i12 < _size7; ++_i12)
        {
          var elem13 = null;
          elem13 = input.readString().value;
          this.preferredCategories.push(elem13);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.I64) {
        this.version = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.deviceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.disableActionType = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.enableReferrer = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.BOOL) {
        this.adPersonalizationEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.STRING) {
        this.firstEnableReferrer = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.clientVersion = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.MAP) {
        var _size14 = 0;
        var _rtmp318;
        this.subscribedCategories = {};
        var _ktype15 = 0;
        var _vtype16 = 0;
        _rtmp318 = input.readMapBegin();
        _ktype15 = _rtmp318.ktype;
        _vtype16 = _rtmp318.vtype;
        _size14 = _rtmp318.size;
        for (var _i19 = 0; _i19 < _size14; ++_i19)
        {
          if (_i19 > 0 ) {
            if (input.rstack.length > input.rpos[input.rpos.length -1] + 1) {
              input.rstack.pop();
            }
          }
          var key20 = null;
          var val21 = null;
          key20 = input.readString().value;
          val21 = input.readI32().value;
          this.subscribedCategories[key20] = val21;
        }
        input.readMapEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.LIST) {
        var _size22 = 0;
        var _rtmp326;
        this.segments = [];
        var _etype25 = 0;
        _rtmp326 = input.readListBegin();
        _etype25 = _rtmp326.etype;
        _size22 = _rtmp326.size;
        for (var _i27 = 0; _i27 < _size22; ++_i27)
        {
          var elem28 = null;
          elem28 = new Segment();
          elem28.read(input);
          this.segments.push(elem28);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.BOOL) {
        this.showRecommendations = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.I64) {
        this.firstEnableAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.BOOL) {
        this.inmobiAdOptOut = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 29:
      if (ftype == Thrift.Type.BOOL) {
        this.categoryOptOut = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 30:
      if (ftype == Thrift.Type.LIST) {
        var _size29 = 0;
        var _rtmp333;
        this.languageSubscriptions = [];
        var _etype32 = 0;
        _rtmp333 = input.readListBegin();
        _etype32 = _rtmp333.etype;
        _size29 = _rtmp333.size;
        for (var _i34 = 0; _i34 < _size29; ++_i34)
        {
          var elem35 = null;
          elem35 = new LanguageSubscription();
          elem35.read(input);
          this.languageSubscriptions.push(elem35);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 31:
      if (ftype == Thrift.Type.STRING) {
        this.id = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 32:
      if (ftype == Thrift.Type.STRING) {
        this.nickname = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 33:
      if (ftype == Thrift.Type.I64) {
        this.sdkVersion = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 34:
      if (ftype == Thrift.Type.I64) {
        this.state = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 35:
      if (ftype == Thrift.Type.I64) {
        this.city = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 36:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 37:
      if (ftype == Thrift.Type.STRING) {
        this.locale = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 38:
      if (ftype == Thrift.Type.SET) {
        var _size36 = 0;
        var _rtmp340;
        this.dynamicSegments = [];
        var _etype39 = 0;
        _rtmp340 = input.readSetBegin();
        _etype39 = _rtmp340.etype;
        _size36 = _rtmp340.size;
        for (var _i41 = 0; _i41 < _size36; ++_i41)
        {
          var elem42 = null;
          elem42 = input.readString().value;
          this.dynamicSegments.push(elem42);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 39:
      if (ftype == Thrift.Type.STRUCT) {
        this.userEngagement = new UserEngagement();
        this.userEngagement.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 40:
      if (ftype == Thrift.Type.STRING) {
        this.fcmToken = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 41:
      if (ftype == Thrift.Type.BOOL) {
        this.dataSaverEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 42:
      if (ftype == Thrift.Type.SET) {
        var _size43 = 0;
        var _rtmp347;
        this.ownedAppPackageNames = [];
        var _etype46 = 0;
        _rtmp347 = input.readSetBegin();
        _etype46 = _rtmp347.etype;
        _size43 = _rtmp347.size;
        for (var _i48 = 0; _i48 < _size43; ++_i48)
        {
          var elem49 = null;
          elem49 = input.readString().value;
          this.ownedAppPackageNames.push(elem49);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 43:
      if (ftype == Thrift.Type.I64) {
        this.resetCacheAtInSecs = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 44:
      if (ftype == Thrift.Type.STRUCT) {
        this.highlightsDetails = new HighlightsDetails();
        this.highlightsDetails.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 45:
      if (ftype == Thrift.Type.STRUCT) {
        this.userStated = new UserStated();
        this.userStated.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 46:
      if (ftype == Thrift.Type.STRUCT) {
        this.sdkFeatures = new SdkFeatures();
        this.sdkFeatures.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 47:
      if (ftype == Thrift.Type.BOOL) {
        this.isChildLockEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 48:
      if (ftype == Thrift.Type.I64) {
        this.childLockTimeLimit = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 49:
      if (ftype == Thrift.Type.STRING) {
        this.secondaryFcmToken = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 50:
      if (ftype == Thrift.Type.STRING) {
        this.monthAndYearOfBirth = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 51:
      if (ftype == Thrift.Type.STRUCT) {
        this.locationInfo = new LocationInfo();
        this.locationInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 52:
      if (ftype == Thrift.Type.BOOL) {
        this.isUnderAge = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 53:
      if (ftype == Thrift.Type.STRUCT) {
        this.appLocale = new AppLocale();
        this.appLocale.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 54:
      if (ftype == Thrift.Type.STRUCT) {
        this.systemFlags = new SystemFlags();
        this.systemFlags.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 55:
      if (ftype == Thrift.Type.STRUCT) {
        this.spacePreferences = new SpacePreferences();
        this.spacePreferences.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 56:
      if (ftype == Thrift.Type.STRUCT) {
        this.geo = new Geo();
        this.geo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 57:
      if (ftype == Thrift.Type.BOOL) {
        this.isFineLocation = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TvUser.prototype.write = function(output) {
  output.writeStructBegin('TvUser');
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 1);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.apiKey !== null && this.apiKey !== undefined) {
    output.writeFieldBegin('apiKey', Thrift.Type.STRING, 2);
    output.writeString(this.apiKey);
    output.writeFieldEnd();
  }
  if (this.partnerId !== null && this.partnerId !== undefined) {
    output.writeFieldBegin('partnerId', Thrift.Type.STRING, 3);
    output.writeString(this.partnerId);
    output.writeFieldEnd();
  }
  if (this.createdAtInSecs !== null && this.createdAtInSecs !== undefined) {
    output.writeFieldBegin('createdAtInSecs', Thrift.Type.I64, 4);
    output.writeI64(this.createdAtInSecs);
    output.writeFieldEnd();
  }
  if (this.updatedAtInSecs !== null && this.updatedAtInSecs !== undefined) {
    output.writeFieldBegin('updatedAtInSecs', Thrift.Type.I64, 5);
    output.writeI64(this.updatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.deviceInfo !== null && this.deviceInfo !== undefined) {
    output.writeFieldBegin('deviceInfo', Thrift.Type.STRUCT, 6);
    this.deviceInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.eulaAcceptedState !== null && this.eulaAcceptedState !== undefined) {
    output.writeFieldBegin('eulaAcceptedState', Thrift.Type.BOOL, 7);
    output.writeBool(this.eulaAcceptedState);
    output.writeFieldEnd();
  }
  if (this.eulaAcceptedStateUpdatedAtInSecs !== null && this.eulaAcceptedStateUpdatedAtInSecs !== undefined) {
    output.writeFieldBegin('eulaAcceptedStateUpdatedAtInSecs', Thrift.Type.I64, 8);
    output.writeI64(this.eulaAcceptedStateUpdatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.enabledState !== null && this.enabledState !== undefined) {
    output.writeFieldBegin('enabledState', Thrift.Type.BOOL, 9);
    output.writeBool(this.enabledState);
    output.writeFieldEnd();
  }
  if (this.enabledStateUpdatedAtInSecs !== null && this.enabledStateUpdatedAtInSecs !== undefined) {
    output.writeFieldBegin('enabledStateUpdatedAtInSecs', Thrift.Type.I64, 10);
    output.writeI64(this.enabledStateUpdatedAtInSecs);
    output.writeFieldEnd();
  }
  if (this.gpId !== null && this.gpId !== undefined) {
    output.writeFieldBegin('gpId', Thrift.Type.STRING, 12);
    output.writeString(this.gpId);
    output.writeFieldEnd();
  }
  if (this.subscriptions !== null && this.subscriptions !== undefined) {
    output.writeFieldBegin('subscriptions', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRUCT, this.subscriptions.length);
    for (var iter50 in this.subscriptions)
    {
      if (this.subscriptions.hasOwnProperty(iter50))
      {
        iter50 = this.subscriptions[iter50];
        iter50.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.preferredNetworkType !== null && this.preferredNetworkType !== undefined) {
    output.writeFieldBegin('preferredNetworkType', Thrift.Type.I32, 14);
    output.writeI32(this.preferredNetworkType);
    output.writeFieldEnd();
  }
  if (this.extras !== null && this.extras !== undefined) {
    output.writeFieldBegin('extras', Thrift.Type.STRUCT, 15);
    this.extras.write(output);
    output.writeFieldEnd();
  }
  if (this.preferredCategories !== null && this.preferredCategories !== undefined) {
    output.writeFieldBegin('preferredCategories', Thrift.Type.LIST, 16);
    output.writeListBegin(Thrift.Type.STRING, this.preferredCategories.length);
    for (var iter51 in this.preferredCategories)
    {
      if (this.preferredCategories.hasOwnProperty(iter51))
      {
        iter51 = this.preferredCategories[iter51];
        output.writeString(iter51);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.version !== null && this.version !== undefined) {
    output.writeFieldBegin('version', Thrift.Type.I64, 17);
    output.writeI64(this.version);
    output.writeFieldEnd();
  }
  if (this.deviceId !== null && this.deviceId !== undefined) {
    output.writeFieldBegin('deviceId', Thrift.Type.STRING, 18);
    output.writeString(this.deviceId);
    output.writeFieldEnd();
  }
  if (this.disableActionType !== null && this.disableActionType !== undefined) {
    output.writeFieldBegin('disableActionType', Thrift.Type.STRING, 19);
    output.writeString(this.disableActionType);
    output.writeFieldEnd();
  }
  if (this.enableReferrer !== null && this.enableReferrer !== undefined) {
    output.writeFieldBegin('enableReferrer', Thrift.Type.STRING, 20);
    output.writeString(this.enableReferrer);
    output.writeFieldEnd();
  }
  if (this.adPersonalizationEnabled !== null && this.adPersonalizationEnabled !== undefined) {
    output.writeFieldBegin('adPersonalizationEnabled', Thrift.Type.BOOL, 21);
    output.writeBool(this.adPersonalizationEnabled);
    output.writeFieldEnd();
  }
  if (this.firstEnableReferrer !== null && this.firstEnableReferrer !== undefined) {
    output.writeFieldBegin('firstEnableReferrer', Thrift.Type.STRING, 22);
    output.writeString(this.firstEnableReferrer);
    output.writeFieldEnd();
  }
  if (this.clientVersion !== null && this.clientVersion !== undefined) {
    output.writeFieldBegin('clientVersion', Thrift.Type.STRING, 23);
    output.writeString(this.clientVersion);
    output.writeFieldEnd();
  }
  if (this.subscribedCategories !== null && this.subscribedCategories !== undefined) {
    output.writeFieldBegin('subscribedCategories', Thrift.Type.MAP, 24);
    output.writeMapBegin(Thrift.Type.STRING, Thrift.Type.I32, Thrift.objectLength(this.subscribedCategories));
    for (var kiter52 in this.subscribedCategories)
    {
      if (this.subscribedCategories.hasOwnProperty(kiter52))
      {
        var viter53 = this.subscribedCategories[kiter52];
        output.writeString(kiter52);
        output.writeI32(viter53);
      }
    }
    output.writeMapEnd();
    output.writeFieldEnd();
  }
  if (this.segments !== null && this.segments !== undefined) {
    output.writeFieldBegin('segments', Thrift.Type.LIST, 25);
    output.writeListBegin(Thrift.Type.STRUCT, this.segments.length);
    for (var iter54 in this.segments)
    {
      if (this.segments.hasOwnProperty(iter54))
      {
        iter54 = this.segments[iter54];
        iter54.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.showRecommendations !== null && this.showRecommendations !== undefined) {
    output.writeFieldBegin('showRecommendations', Thrift.Type.BOOL, 26);
    output.writeBool(this.showRecommendations);
    output.writeFieldEnd();
  }
  if (this.firstEnableAtInSecs !== null && this.firstEnableAtInSecs !== undefined) {
    output.writeFieldBegin('firstEnableAtInSecs', Thrift.Type.I64, 27);
    output.writeI64(this.firstEnableAtInSecs);
    output.writeFieldEnd();
  }
  if (this.inmobiAdOptOut !== null && this.inmobiAdOptOut !== undefined) {
    output.writeFieldBegin('inmobiAdOptOut', Thrift.Type.BOOL, 28);
    output.writeBool(this.inmobiAdOptOut);
    output.writeFieldEnd();
  }
  if (this.categoryOptOut !== null && this.categoryOptOut !== undefined) {
    output.writeFieldBegin('categoryOptOut', Thrift.Type.BOOL, 29);
    output.writeBool(this.categoryOptOut);
    output.writeFieldEnd();
  }
  if (this.languageSubscriptions !== null && this.languageSubscriptions !== undefined) {
    output.writeFieldBegin('languageSubscriptions', Thrift.Type.LIST, 30);
    output.writeListBegin(Thrift.Type.STRUCT, this.languageSubscriptions.length);
    for (var iter55 in this.languageSubscriptions)
    {
      if (this.languageSubscriptions.hasOwnProperty(iter55))
      {
        iter55 = this.languageSubscriptions[iter55];
        iter55.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.id !== null && this.id !== undefined) {
    output.writeFieldBegin('id', Thrift.Type.STRING, 31);
    output.writeString(this.id);
    output.writeFieldEnd();
  }
  if (this.nickname !== null && this.nickname !== undefined) {
    output.writeFieldBegin('nickname', Thrift.Type.STRING, 32);
    output.writeString(this.nickname);
    output.writeFieldEnd();
  }
  if (this.sdkVersion !== null && this.sdkVersion !== undefined) {
    output.writeFieldBegin('sdkVersion', Thrift.Type.I64, 33);
    output.writeI64(this.sdkVersion);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.I64, 34);
    output.writeI64(this.state);
    output.writeFieldEnd();
  }
  if (this.city !== null && this.city !== undefined) {
    output.writeFieldBegin('city', Thrift.Type.I64, 35);
    output.writeI64(this.city);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 36);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.locale !== null && this.locale !== undefined) {
    output.writeFieldBegin('locale', Thrift.Type.STRING, 37);
    output.writeString(this.locale);
    output.writeFieldEnd();
  }
  if (this.dynamicSegments !== null && this.dynamicSegments !== undefined) {
    output.writeFieldBegin('dynamicSegments', Thrift.Type.SET, 38);
    output.writeSetBegin(Thrift.Type.STRING, this.dynamicSegments.length);
    for (var iter56 in this.dynamicSegments)
    {
      if (this.dynamicSegments.hasOwnProperty(iter56))
      {
        iter56 = this.dynamicSegments[iter56];
        output.writeString(iter56);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.userEngagement !== null && this.userEngagement !== undefined) {
    output.writeFieldBegin('userEngagement', Thrift.Type.STRUCT, 39);
    this.userEngagement.write(output);
    output.writeFieldEnd();
  }
  if (this.fcmToken !== null && this.fcmToken !== undefined) {
    output.writeFieldBegin('fcmToken', Thrift.Type.STRING, 40);
    output.writeString(this.fcmToken);
    output.writeFieldEnd();
  }
  if (this.dataSaverEnabled !== null && this.dataSaverEnabled !== undefined) {
    output.writeFieldBegin('dataSaverEnabled', Thrift.Type.BOOL, 41);
    output.writeBool(this.dataSaverEnabled);
    output.writeFieldEnd();
  }
  if (this.ownedAppPackageNames !== null && this.ownedAppPackageNames !== undefined) {
    output.writeFieldBegin('ownedAppPackageNames', Thrift.Type.SET, 42);
    output.writeSetBegin(Thrift.Type.STRING, this.ownedAppPackageNames.length);
    for (var iter57 in this.ownedAppPackageNames)
    {
      if (this.ownedAppPackageNames.hasOwnProperty(iter57))
      {
        iter57 = this.ownedAppPackageNames[iter57];
        output.writeString(iter57);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  if (this.resetCacheAtInSecs !== null && this.resetCacheAtInSecs !== undefined) {
    output.writeFieldBegin('resetCacheAtInSecs', Thrift.Type.I64, 43);
    output.writeI64(this.resetCacheAtInSecs);
    output.writeFieldEnd();
  }
  if (this.highlightsDetails !== null && this.highlightsDetails !== undefined) {
    output.writeFieldBegin('highlightsDetails', Thrift.Type.STRUCT, 44);
    this.highlightsDetails.write(output);
    output.writeFieldEnd();
  }
  if (this.userStated !== null && this.userStated !== undefined) {
    output.writeFieldBegin('userStated', Thrift.Type.STRUCT, 45);
    this.userStated.write(output);
    output.writeFieldEnd();
  }
  if (this.sdkFeatures !== null && this.sdkFeatures !== undefined) {
    output.writeFieldBegin('sdkFeatures', Thrift.Type.STRUCT, 46);
    this.sdkFeatures.write(output);
    output.writeFieldEnd();
  }
  if (this.isChildLockEnabled !== null && this.isChildLockEnabled !== undefined) {
    output.writeFieldBegin('isChildLockEnabled', Thrift.Type.BOOL, 47);
    output.writeBool(this.isChildLockEnabled);
    output.writeFieldEnd();
  }
  if (this.childLockTimeLimit !== null && this.childLockTimeLimit !== undefined) {
    output.writeFieldBegin('childLockTimeLimit', Thrift.Type.I64, 48);
    output.writeI64(this.childLockTimeLimit);
    output.writeFieldEnd();
  }
  if (this.secondaryFcmToken !== null && this.secondaryFcmToken !== undefined) {
    output.writeFieldBegin('secondaryFcmToken', Thrift.Type.STRING, 49);
    output.writeString(this.secondaryFcmToken);
    output.writeFieldEnd();
  }
  if (this.monthAndYearOfBirth !== null && this.monthAndYearOfBirth !== undefined) {
    output.writeFieldBegin('monthAndYearOfBirth', Thrift.Type.STRING, 50);
    output.writeString(this.monthAndYearOfBirth);
    output.writeFieldEnd();
  }
  if (this.locationInfo !== null && this.locationInfo !== undefined) {
    output.writeFieldBegin('locationInfo', Thrift.Type.STRUCT, 51);
    this.locationInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.isUnderAge !== null && this.isUnderAge !== undefined) {
    output.writeFieldBegin('isUnderAge', Thrift.Type.BOOL, 52);
    output.writeBool(this.isUnderAge);
    output.writeFieldEnd();
  }
  if (this.appLocale !== null && this.appLocale !== undefined) {
    output.writeFieldBegin('appLocale', Thrift.Type.STRUCT, 53);
    this.appLocale.write(output);
    output.writeFieldEnd();
  }
  if (this.systemFlags !== null && this.systemFlags !== undefined) {
    output.writeFieldBegin('systemFlags', Thrift.Type.STRUCT, 54);
    this.systemFlags.write(output);
    output.writeFieldEnd();
  }
  if (this.spacePreferences !== null && this.spacePreferences !== undefined) {
    output.writeFieldBegin('spacePreferences', Thrift.Type.STRUCT, 55);
    this.spacePreferences.write(output);
    output.writeFieldEnd();
  }
  if (this.geo !== null && this.geo !== undefined) {
    output.writeFieldBegin('geo', Thrift.Type.STRUCT, 56);
    this.geo.write(output);
    output.writeFieldEnd();
  }
  if (this.isFineLocation !== null && this.isFineLocation !== undefined) {
    output.writeFieldBegin('isFineLocation', Thrift.Type.BOOL, 57);
    output.writeBool(this.isFineLocation);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

TvUserV1 = function(args) {
  this.userId = null;
  this.oemKey = null;
  this.createdAt = null;
  this.updatedAt = null;
  this.deviceInfo = null;
  this.eulaAccepted = null;
  this.eulaAcceptedAt = null;
  this.preferredNetwork = null;
  this.extras = null;
  this.preferredCategories = null;
  this.apiVersion = null;
  this.adPersonalizationEnabled = null;
  this.recommendedCategories = null;
  this.showRecommendations = null;
  this.languageSubscriptions = null;
  this.sdkVersion = null;
  this.state = null;
  this.city = null;
  this.region = null;
  this.locale = null;
  this.dataSaverEnabled = null;
  this.ownedAppPackageNames = null;
  this.resetCacheAt = null;
  this.optOutOfSale = null;
  this.systemFlags = null;
  this.systemFlagsUpdatedAt = null;
  this.gpId = null;
  this.geo = null;
  if (args) {
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field userId is unset!');
    }
    if (args.oemKey !== undefined && args.oemKey !== null) {
      this.oemKey = args.oemKey;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field oemKey is unset!');
    }
    if (args.createdAt !== undefined && args.createdAt !== null) {
      this.createdAt = args.createdAt;
    }
    if (args.updatedAt !== undefined && args.updatedAt !== null) {
      this.updatedAt = args.updatedAt;
    }
    if (args.deviceInfo !== undefined && args.deviceInfo !== null) {
      this.deviceInfo = new DeviceInfo(args.deviceInfo);
    }
    if (args.eulaAccepted !== undefined && args.eulaAccepted !== null) {
      this.eulaAccepted = args.eulaAccepted;
    }
    if (args.eulaAcceptedAt !== undefined && args.eulaAcceptedAt !== null) {
      this.eulaAcceptedAt = args.eulaAcceptedAt;
    }
    if (args.preferredNetwork !== undefined && args.preferredNetwork !== null) {
      this.preferredNetwork = args.preferredNetwork;
    }
    if (args.extras !== undefined && args.extras !== null) {
      this.extras = new UserExtras(args.extras);
    }
    if (args.preferredCategories !== undefined && args.preferredCategories !== null) {
      this.preferredCategories = Thrift.copyList(args.preferredCategories, [BaseCategory]);
    }
    if (args.apiVersion !== undefined && args.apiVersion !== null) {
      this.apiVersion = args.apiVersion;
    }
    if (args.adPersonalizationEnabled !== undefined && args.adPersonalizationEnabled !== null) {
      this.adPersonalizationEnabled = args.adPersonalizationEnabled;
    }
    if (args.recommendedCategories !== undefined && args.recommendedCategories !== null) {
      this.recommendedCategories = Thrift.copyList(args.recommendedCategories, [BaseCategory]);
    }
    if (args.showRecommendations !== undefined && args.showRecommendations !== null) {
      this.showRecommendations = args.showRecommendations;
    }
    if (args.languageSubscriptions !== undefined && args.languageSubscriptions !== null) {
      this.languageSubscriptions = Thrift.copyList(args.languageSubscriptions, [TVLanguageSubscription]);
    }
    if (args.sdkVersion !== undefined && args.sdkVersion !== null) {
      this.sdkVersion = args.sdkVersion;
    }
    if (args.state !== undefined && args.state !== null) {
      this.state = args.state;
    }
    if (args.city !== undefined && args.city !== null) {
      this.city = args.city;
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.locale !== undefined && args.locale !== null) {
      this.locale = args.locale;
    }
    if (args.dataSaverEnabled !== undefined && args.dataSaverEnabled !== null) {
      this.dataSaverEnabled = args.dataSaverEnabled;
    }
    if (args.ownedAppPackageNames !== undefined && args.ownedAppPackageNames !== null) {
      this.ownedAppPackageNames = Thrift.copyList(args.ownedAppPackageNames, [null]);
    }
    if (args.resetCacheAt !== undefined && args.resetCacheAt !== null) {
      this.resetCacheAt = args.resetCacheAt;
    }
    if (args.optOutOfSale !== undefined && args.optOutOfSale !== null) {
      this.optOutOfSale = args.optOutOfSale;
    }
    if (args.systemFlags !== undefined && args.systemFlags !== null) {
      this.systemFlags = Thrift.copyList(args.systemFlags, [SystemFlags]);
    }
    if (args.systemFlagsUpdatedAt !== undefined && args.systemFlagsUpdatedAt !== null) {
      this.systemFlagsUpdatedAt = args.systemFlagsUpdatedAt;
    }
    if (args.gpId !== undefined && args.gpId !== null) {
      this.gpId = args.gpId;
    }
    if (args.geo !== undefined && args.geo !== null) {
      this.geo = new TvGeo(args.geo);
    }
  }
};
TvUserV1.prototype = {};
TvUserV1.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.oemKey = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.createdAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 4:
      if (ftype == Thrift.Type.STRING) {
        this.updatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 5:
      if (ftype == Thrift.Type.STRUCT) {
        this.deviceInfo = new DeviceInfo();
        this.deviceInfo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 6:
      if (ftype == Thrift.Type.BOOL) {
        this.eulaAccepted = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 7:
      if (ftype == Thrift.Type.STRING) {
        this.eulaAcceptedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 8:
      if (ftype == Thrift.Type.I32) {
        this.preferredNetwork = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 9:
      if (ftype == Thrift.Type.STRUCT) {
        this.extras = new UserExtras();
        this.extras.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 10:
      if (ftype == Thrift.Type.LIST) {
        var _size58 = 0;
        var _rtmp362;
        this.preferredCategories = [];
        var _etype61 = 0;
        _rtmp362 = input.readListBegin();
        _etype61 = _rtmp362.etype;
        _size58 = _rtmp362.size;
        for (var _i63 = 0; _i63 < _size58; ++_i63)
        {
          var elem64 = null;
          elem64 = new BaseCategory();
          elem64.read(input);
          this.preferredCategories.push(elem64);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 11:
      if (ftype == Thrift.Type.STRING) {
        this.apiVersion = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 12:
      if (ftype == Thrift.Type.BOOL) {
        this.adPersonalizationEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 13:
      if (ftype == Thrift.Type.LIST) {
        var _size65 = 0;
        var _rtmp369;
        this.recommendedCategories = [];
        var _etype68 = 0;
        _rtmp369 = input.readListBegin();
        _etype68 = _rtmp369.etype;
        _size65 = _rtmp369.size;
        for (var _i70 = 0; _i70 < _size65; ++_i70)
        {
          var elem71 = null;
          elem71 = new BaseCategory();
          elem71.read(input);
          this.recommendedCategories.push(elem71);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 14:
      if (ftype == Thrift.Type.BOOL) {
        this.showRecommendations = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 15:
      if (ftype == Thrift.Type.LIST) {
        var _size72 = 0;
        var _rtmp376;
        this.languageSubscriptions = [];
        var _etype75 = 0;
        _rtmp376 = input.readListBegin();
        _etype75 = _rtmp376.etype;
        _size72 = _rtmp376.size;
        for (var _i77 = 0; _i77 < _size72; ++_i77)
        {
          var elem78 = null;
          elem78 = new TVLanguageSubscription();
          elem78.read(input);
          this.languageSubscriptions.push(elem78);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 16:
      if (ftype == Thrift.Type.I64) {
        this.sdkVersion = input.readI64().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 17:
      if (ftype == Thrift.Type.STRING) {
        this.state = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 18:
      if (ftype == Thrift.Type.STRING) {
        this.city = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 19:
      if (ftype == Thrift.Type.STRING) {
        this.region = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 20:
      if (ftype == Thrift.Type.STRING) {
        this.locale = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 21:
      if (ftype == Thrift.Type.BOOL) {
        this.dataSaverEnabled = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 22:
      if (ftype == Thrift.Type.LIST) {
        var _size79 = 0;
        var _rtmp383;
        this.ownedAppPackageNames = [];
        var _etype82 = 0;
        _rtmp383 = input.readListBegin();
        _etype82 = _rtmp383.etype;
        _size79 = _rtmp383.size;
        for (var _i84 = 0; _i84 < _size79; ++_i84)
        {
          var elem85 = null;
          elem85 = input.readString().value;
          this.ownedAppPackageNames.push(elem85);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 23:
      if (ftype == Thrift.Type.STRING) {
        this.resetCacheAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 24:
      if (ftype == Thrift.Type.BOOL) {
        this.optOutOfSale = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 25:
      if (ftype == Thrift.Type.LIST) {
        var _size86 = 0;
        var _rtmp390;
        this.systemFlags = [];
        var _etype89 = 0;
        _rtmp390 = input.readListBegin();
        _etype89 = _rtmp390.etype;
        _size86 = _rtmp390.size;
        for (var _i91 = 0; _i91 < _size86; ++_i91)
        {
          var elem92 = null;
          elem92 = new SystemFlags();
          elem92.read(input);
          this.systemFlags.push(elem92);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 26:
      if (ftype == Thrift.Type.STRING) {
        this.systemFlagsUpdatedAt = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 27:
      if (ftype == Thrift.Type.STRING) {
        this.gpId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 28:
      if (ftype == Thrift.Type.STRUCT) {
        this.geo = new TvGeo();
        this.geo.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

TvUserV1.prototype.write = function(output) {
  output.writeStructBegin('TvUserV1');
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 1);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.oemKey !== null && this.oemKey !== undefined) {
    output.writeFieldBegin('oemKey', Thrift.Type.STRING, 2);
    output.writeString(this.oemKey);
    output.writeFieldEnd();
  }
  if (this.createdAt !== null && this.createdAt !== undefined) {
    output.writeFieldBegin('createdAt', Thrift.Type.STRING, 3);
    output.writeString(this.createdAt);
    output.writeFieldEnd();
  }
  if (this.updatedAt !== null && this.updatedAt !== undefined) {
    output.writeFieldBegin('updatedAt', Thrift.Type.STRING, 4);
    output.writeString(this.updatedAt);
    output.writeFieldEnd();
  }
  if (this.deviceInfo !== null && this.deviceInfo !== undefined) {
    output.writeFieldBegin('deviceInfo', Thrift.Type.STRUCT, 5);
    this.deviceInfo.write(output);
    output.writeFieldEnd();
  }
  if (this.eulaAccepted !== null && this.eulaAccepted !== undefined) {
    output.writeFieldBegin('eulaAccepted', Thrift.Type.BOOL, 6);
    output.writeBool(this.eulaAccepted);
    output.writeFieldEnd();
  }
  if (this.eulaAcceptedAt !== null && this.eulaAcceptedAt !== undefined) {
    output.writeFieldBegin('eulaAcceptedAt', Thrift.Type.STRING, 7);
    output.writeString(this.eulaAcceptedAt);
    output.writeFieldEnd();
  }
  if (this.preferredNetwork !== null && this.preferredNetwork !== undefined) {
    output.writeFieldBegin('preferredNetwork', Thrift.Type.I32, 8);
    output.writeI32(this.preferredNetwork);
    output.writeFieldEnd();
  }
  if (this.extras !== null && this.extras !== undefined) {
    output.writeFieldBegin('extras', Thrift.Type.STRUCT, 9);
    this.extras.write(output);
    output.writeFieldEnd();
  }
  if (this.preferredCategories !== null && this.preferredCategories !== undefined) {
    output.writeFieldBegin('preferredCategories', Thrift.Type.LIST, 10);
    output.writeListBegin(Thrift.Type.STRUCT, this.preferredCategories.length);
    for (var iter93 in this.preferredCategories)
    {
      if (this.preferredCategories.hasOwnProperty(iter93))
      {
        iter93 = this.preferredCategories[iter93];
        iter93.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.apiVersion !== null && this.apiVersion !== undefined) {
    output.writeFieldBegin('apiVersion', Thrift.Type.STRING, 11);
    output.writeString(this.apiVersion);
    output.writeFieldEnd();
  }
  if (this.adPersonalizationEnabled !== null && this.adPersonalizationEnabled !== undefined) {
    output.writeFieldBegin('adPersonalizationEnabled', Thrift.Type.BOOL, 12);
    output.writeBool(this.adPersonalizationEnabled);
    output.writeFieldEnd();
  }
  if (this.recommendedCategories !== null && this.recommendedCategories !== undefined) {
    output.writeFieldBegin('recommendedCategories', Thrift.Type.LIST, 13);
    output.writeListBegin(Thrift.Type.STRUCT, this.recommendedCategories.length);
    for (var iter94 in this.recommendedCategories)
    {
      if (this.recommendedCategories.hasOwnProperty(iter94))
      {
        iter94 = this.recommendedCategories[iter94];
        iter94.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.showRecommendations !== null && this.showRecommendations !== undefined) {
    output.writeFieldBegin('showRecommendations', Thrift.Type.BOOL, 14);
    output.writeBool(this.showRecommendations);
    output.writeFieldEnd();
  }
  if (this.languageSubscriptions !== null && this.languageSubscriptions !== undefined) {
    output.writeFieldBegin('languageSubscriptions', Thrift.Type.LIST, 15);
    output.writeListBegin(Thrift.Type.STRUCT, this.languageSubscriptions.length);
    for (var iter95 in this.languageSubscriptions)
    {
      if (this.languageSubscriptions.hasOwnProperty(iter95))
      {
        iter95 = this.languageSubscriptions[iter95];
        iter95.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.sdkVersion !== null && this.sdkVersion !== undefined) {
    output.writeFieldBegin('sdkVersion', Thrift.Type.I64, 16);
    output.writeI64(this.sdkVersion);
    output.writeFieldEnd();
  }
  if (this.state !== null && this.state !== undefined) {
    output.writeFieldBegin('state', Thrift.Type.STRING, 17);
    output.writeString(this.state);
    output.writeFieldEnd();
  }
  if (this.city !== null && this.city !== undefined) {
    output.writeFieldBegin('city', Thrift.Type.STRING, 18);
    output.writeString(this.city);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.STRING, 19);
    output.writeString(this.region);
    output.writeFieldEnd();
  }
  if (this.locale !== null && this.locale !== undefined) {
    output.writeFieldBegin('locale', Thrift.Type.STRING, 20);
    output.writeString(this.locale);
    output.writeFieldEnd();
  }
  if (this.dataSaverEnabled !== null && this.dataSaverEnabled !== undefined) {
    output.writeFieldBegin('dataSaverEnabled', Thrift.Type.BOOL, 21);
    output.writeBool(this.dataSaverEnabled);
    output.writeFieldEnd();
  }
  if (this.ownedAppPackageNames !== null && this.ownedAppPackageNames !== undefined) {
    output.writeFieldBegin('ownedAppPackageNames', Thrift.Type.LIST, 22);
    output.writeListBegin(Thrift.Type.STRING, this.ownedAppPackageNames.length);
    for (var iter96 in this.ownedAppPackageNames)
    {
      if (this.ownedAppPackageNames.hasOwnProperty(iter96))
      {
        iter96 = this.ownedAppPackageNames[iter96];
        output.writeString(iter96);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.resetCacheAt !== null && this.resetCacheAt !== undefined) {
    output.writeFieldBegin('resetCacheAt', Thrift.Type.STRING, 23);
    output.writeString(this.resetCacheAt);
    output.writeFieldEnd();
  }
  if (this.optOutOfSale !== null && this.optOutOfSale !== undefined) {
    output.writeFieldBegin('optOutOfSale', Thrift.Type.BOOL, 24);
    output.writeBool(this.optOutOfSale);
    output.writeFieldEnd();
  }
  if (this.systemFlags !== null && this.systemFlags !== undefined) {
    output.writeFieldBegin('systemFlags', Thrift.Type.LIST, 25);
    output.writeListBegin(Thrift.Type.STRUCT, this.systemFlags.length);
    for (var iter97 in this.systemFlags)
    {
      if (this.systemFlags.hasOwnProperty(iter97))
      {
        iter97 = this.systemFlags[iter97];
        iter97.write(output);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.systemFlagsUpdatedAt !== null && this.systemFlagsUpdatedAt !== undefined) {
    output.writeFieldBegin('systemFlagsUpdatedAt', Thrift.Type.STRING, 26);
    output.writeString(this.systemFlagsUpdatedAt);
    output.writeFieldEnd();
  }
  if (this.gpId !== null && this.gpId !== undefined) {
    output.writeFieldBegin('gpId', Thrift.Type.STRING, 27);
    output.writeString(this.gpId);
    output.writeFieldEnd();
  }
  if (this.geo !== null && this.geo !== undefined) {
    output.writeFieldBegin('geo', Thrift.Type.STRUCT, 28);
    this.geo.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


UserDiff = function(args) {
  this.currentUser = null;
  this.updatedUser = null;
  if (args) {
    if (args.currentUser !== undefined && args.currentUser !== null) {
      this.currentUser = new User(args.currentUser);
    }
    if (args.updatedUser !== undefined && args.updatedUser !== null) {
      this.updatedUser = new User(args.updatedUser);
    }
  }
};
UserDiff.prototype = {};
UserDiff.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.currentUser = new User();
        this.currentUser.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.updatedUser = new User();
        this.updatedUser.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

UserDiff.prototype.write = function(output) {
  output.writeStructBegin('UserDiff');
  if (this.currentUser !== null && this.currentUser !== undefined) {
    output.writeFieldBegin('currentUser', Thrift.Type.STRUCT, 1);
    this.currentUser.write(output);
    output.writeFieldEnd();
  }
  if (this.updatedUser !== null && this.updatedUser !== undefined) {
    output.writeFieldBegin('updatedUser', Thrift.Type.STRUCT, 2);
    this.updatedUser.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


//HELPER FUNCTIONS AND STRUCTURES

AuthService_loginUsingEmailAndPassword_args = function(args) {
  this.email = null;
  this.password = null;
  if (args) {
    if (args.email !== undefined && args.email !== null) {
      this.email = args.email;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field email is unset!');
    }
    if (args.password !== undefined && args.password !== null) {
      this.password = args.password;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field password is unset!');
    }
  }
};
AuthService_loginUsingEmailAndPassword_args.prototype = {};
AuthService_loginUsingEmailAndPassword_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.email = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.password = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_loginUsingEmailAndPassword_args.prototype.write = function(output) {
  output.writeStructBegin('AuthService_loginUsingEmailAndPassword_args');
  if (this.email !== null && this.email !== undefined) {
    output.writeFieldBegin('email', Thrift.Type.STRING, 1);
    output.writeString(this.email);
    output.writeFieldEnd();
  }
  if (this.password !== null && this.password !== undefined) {
    output.writeFieldBegin('password', Thrift.Type.STRING, 2);
    output.writeString(this.password);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthService_loginUsingEmailAndPassword_result = function(args) {
  this.success = null;
  this.authException = null;
  if (args instanceof AuthException) {
    this.authException = args;
    return;
  }
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new StudioSessionToken(args.success);
    }
    if (args.authException !== undefined && args.authException !== null) {
      this.authException = args.authException;
    }
  }
};
AuthService_loginUsingEmailAndPassword_result.prototype = {};
AuthService_loginUsingEmailAndPassword_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new StudioSessionToken();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.authException = new AuthException();
        this.authException.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_loginUsingEmailAndPassword_result.prototype.write = function(output) {
  output.writeStructBegin('AuthService_loginUsingEmailAndPassword_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  if (this.authException !== null && this.authException !== undefined) {
    output.writeFieldBegin('authException', Thrift.Type.STRUCT, 1);
    this.authException.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthService_loginUsingGoogleAuth_args = function(args) {
  this.authToken = null;
  if (args) {
    if (args.authToken !== undefined && args.authToken !== null) {
      this.authToken = args.authToken;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field authToken is unset!');
    }
  }
};
AuthService_loginUsingGoogleAuth_args.prototype = {};
AuthService_loginUsingGoogleAuth_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.authToken = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_loginUsingGoogleAuth_args.prototype.write = function(output) {
  output.writeStructBegin('AuthService_loginUsingGoogleAuth_args');
  if (this.authToken !== null && this.authToken !== undefined) {
    output.writeFieldBegin('authToken', Thrift.Type.STRING, 1);
    output.writeString(this.authToken);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthService_loginUsingGoogleAuth_result = function(args) {
  this.success = null;
  this.authException = null;
  if (args instanceof AuthException) {
    this.authException = args;
    return;
  }
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new StudioSessionToken(args.success);
    }
    if (args.authException !== undefined && args.authException !== null) {
      this.authException = args.authException;
    }
  }
};
AuthService_loginUsingGoogleAuth_result.prototype = {};
AuthService_loginUsingGoogleAuth_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new StudioSessionToken();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.authException = new AuthException();
        this.authException.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_loginUsingGoogleAuth_result.prototype.write = function(output) {
  output.writeStructBegin('AuthService_loginUsingGoogleAuth_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  if (this.authException !== null && this.authException !== undefined) {
    output.writeFieldBegin('authException', Thrift.Type.STRUCT, 1);
    this.authException.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthService_loginUsingMicrosoft_args = function(args) {
  this.authToken = null;
  if (args) {
    if (args.authToken !== undefined && args.authToken !== null) {
      this.authToken = args.authToken;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field authToken is unset!');
    }
  }
};
AuthService_loginUsingMicrosoft_args.prototype = {};
AuthService_loginUsingMicrosoft_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.authToken = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_loginUsingMicrosoft_args.prototype.write = function(output) {
  output.writeStructBegin('AuthService_loginUsingMicrosoft_args');
  if (this.authToken !== null && this.authToken !== undefined) {
    output.writeFieldBegin('authToken', Thrift.Type.STRING, 1);
    output.writeString(this.authToken);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthService_loginUsingMicrosoft_result = function(args) {
  this.success = null;
  this.authException = null;
  if (args instanceof AuthException) {
    this.authException = args;
    return;
  }
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new StudioSessionToken(args.success);
    }
    if (args.authException !== undefined && args.authException !== null) {
      this.authException = args.authException;
    }
  }
};
AuthService_loginUsingMicrosoft_result.prototype = {};
AuthService_loginUsingMicrosoft_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new StudioSessionToken();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.authException = new AuthException();
        this.authException.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_loginUsingMicrosoft_result.prototype.write = function(output) {
  output.writeStructBegin('AuthService_loginUsingMicrosoft_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  if (this.authException !== null && this.authException !== undefined) {
    output.writeFieldBegin('authException', Thrift.Type.STRUCT, 1);
    this.authException.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthService_generatePasswordHash_args = function(args) {
  this.password = null;
  if (args) {
    if (args.password !== undefined && args.password !== null) {
      this.password = args.password;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field password is unset!');
    }
  }
};
AuthService_generatePasswordHash_args.prototype = {};
AuthService_generatePasswordHash_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.password = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_generatePasswordHash_args.prototype.write = function(output) {
  output.writeStructBegin('AuthService_generatePasswordHash_args');
  if (this.password !== null && this.password !== undefined) {
    output.writeFieldBegin('password', Thrift.Type.STRING, 1);
    output.writeString(this.password);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthService_generatePasswordHash_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = args.success;
    }
  }
};
AuthService_generatePasswordHash_result.prototype = {};
AuthService_generatePasswordHash_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRING) {
        this.success = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_generatePasswordHash_result.prototype.write = function(output) {
  output.writeStructBegin('AuthService_generatePasswordHash_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRING, 0);
    output.writeString(this.success);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthService_logout_args = function(args) {
};
AuthService_logout_args.prototype = {};
AuthService_logout_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_logout_args.prototype.write = function(output) {
  output.writeStructBegin('AuthService_logout_args');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthService_logout_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = args.success;
    }
  }
};
AuthService_logout_result.prototype = {};
AuthService_logout_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.BOOL) {
        this.success = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

AuthService_logout_result.prototype.write = function(output) {
  output.writeStructBegin('AuthService_logout_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.BOOL, 0);
    output.writeBool(this.success);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

AuthServiceClient = function(input, output) {
    this.input = input;
    this.output = (!output) ? input : output;
    this.seqid = 0;
};
AuthServiceClient.prototype = {};
AuthServiceClient.prototype.loginUsingEmailAndPassword = function(email, password, callback) {
  if (callback === undefined) {
    this.send_loginUsingEmailAndPassword(email, password);
    return this.recv_loginUsingEmailAndPassword();
  } else {
    var postData = this.send_loginUsingEmailAndPassword(email, password, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_loginUsingEmailAndPassword);
  }
};

AuthServiceClient.prototype.send_loginUsingEmailAndPassword = function(email, password, callback) {
  this.output.writeMessageBegin('loginUsingEmailAndPassword', Thrift.MessageType.CALL, this.seqid);
  var params = {
    email: email,
    password: password
  };
  var args = new AuthService_loginUsingEmailAndPassword_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

AuthServiceClient.prototype.recv_loginUsingEmailAndPassword = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new AuthService_loginUsingEmailAndPassword_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.authException) {
    throw result.authException;
  }
  if (null !== result.success) {
    return result.success;
  }
  throw 'loginUsingEmailAndPassword failed: unknown result';
};
AuthServiceClient.prototype.loginUsingGoogleAuth = function(authToken, callback) {
  if (callback === undefined) {
    this.send_loginUsingGoogleAuth(authToken);
    return this.recv_loginUsingGoogleAuth();
  } else {
    var postData = this.send_loginUsingGoogleAuth(authToken, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_loginUsingGoogleAuth);
  }
};

AuthServiceClient.prototype.send_loginUsingGoogleAuth = function(authToken, callback) {
  this.output.writeMessageBegin('loginUsingGoogleAuth', Thrift.MessageType.CALL, this.seqid);
  var params = {
    authToken: authToken
  };
  var args = new AuthService_loginUsingGoogleAuth_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

AuthServiceClient.prototype.recv_loginUsingGoogleAuth = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new AuthService_loginUsingGoogleAuth_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.authException) {
    throw result.authException;
  }
  if (null !== result.success) {
    return result.success;
  }
  throw 'loginUsingGoogleAuth failed: unknown result';
};
AuthServiceClient.prototype.loginUsingMicrosoft = function(authToken, callback) {
  if (callback === undefined) {
    this.send_loginUsingMicrosoft(authToken);
    return this.recv_loginUsingMicrosoft();
  } else {
    var postData = this.send_loginUsingMicrosoft(authToken, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_loginUsingMicrosoft);
  }
};

AuthServiceClient.prototype.send_loginUsingMicrosoft = function(authToken, callback) {
  this.output.writeMessageBegin('loginUsingMicrosoft', Thrift.MessageType.CALL, this.seqid);
  var params = {
    authToken: authToken
  };
  var args = new AuthService_loginUsingMicrosoft_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

AuthServiceClient.prototype.recv_loginUsingMicrosoft = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new AuthService_loginUsingMicrosoft_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.authException) {
    throw result.authException;
  }
  if (null !== result.success) {
    return result.success;
  }
  throw 'loginUsingMicrosoft failed: unknown result';
};
AuthServiceClient.prototype.generatePasswordHash = function(password, callback) {
  if (callback === undefined) {
    this.send_generatePasswordHash(password);
    return this.recv_generatePasswordHash();
  } else {
    var postData = this.send_generatePasswordHash(password, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_generatePasswordHash);
  }
};

AuthServiceClient.prototype.send_generatePasswordHash = function(password, callback) {
  this.output.writeMessageBegin('generatePasswordHash', Thrift.MessageType.CALL, this.seqid);
  var params = {
    password: password
  };
  var args = new AuthService_generatePasswordHash_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

AuthServiceClient.prototype.recv_generatePasswordHash = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new AuthService_generatePasswordHash_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'generatePasswordHash failed: unknown result';
};
AuthServiceClient.prototype.logout = function(callback) {
  if (callback === undefined) {
    this.send_logout();
    return this.recv_logout();
  } else {
    var postData = this.send_logout(true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_logout);
  }
};

AuthServiceClient.prototype.send_logout = function(callback) {
  this.output.writeMessageBegin('logout', Thrift.MessageType.CALL, this.seqid);
  var args = new AuthService_logout_args();
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

AuthServiceClient.prototype.recv_logout = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new AuthService_logout_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'logout failed: unknown result';
};
//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


//HELPER FUNCTIONS AND STRUCTURES

GlanceConfigService_getGlobalConfig_args = function(args) {
  this.userId = null;
  this.partner = null;
  if (args) {
    if (args.userId !== undefined && args.userId !== null) {
      this.userId = args.userId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field userId is unset!');
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
  }
};
GlanceConfigService_getGlobalConfig_args.prototype = {};
GlanceConfigService_getGlobalConfig_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.userId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceConfigService_getGlobalConfig_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceConfigService_getGlobalConfig_args');
  if (this.userId !== null && this.userId !== undefined) {
    output.writeFieldBegin('userId', Thrift.Type.STRING, 1);
    output.writeString(this.userId);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 2);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceConfigService_getGlobalConfig_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceGlobalConfig(args.success);
    }
  }
};
GlanceConfigService_getGlobalConfig_result.prototype = {};
GlanceConfigService_getGlobalConfig_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceGlobalConfig();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceConfigService_getGlobalConfig_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceConfigService_getGlobalConfig_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceConfigService_updateGlobalConfig_args = function(args) {
  this.partner = null;
  this.config = null;
  if (args) {
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
    if (args.config !== undefined && args.config !== null) {
      this.config = new GlanceGlobalConfig(args.config);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field config is unset!');
    }
  }
};
GlanceConfigService_updateGlobalConfig_args.prototype = {};
GlanceConfigService_updateGlobalConfig_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.config = new GlanceGlobalConfig();
        this.config.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceConfigService_updateGlobalConfig_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceConfigService_updateGlobalConfig_args');
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 1);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.config !== null && this.config !== undefined) {
    output.writeFieldBegin('config', Thrift.Type.STRUCT, 2);
    this.config.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceConfigService_updateGlobalConfig_result = function(args) {
};
GlanceConfigService_updateGlobalConfig_result.prototype = {};
GlanceConfigService_updateGlobalConfig_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceConfigService_updateGlobalConfig_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceConfigService_updateGlobalConfig_result');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceConfigService_getStudioAppConfig_args = function(args) {
};
GlanceConfigService_getStudioAppConfig_args.prototype = {};
GlanceConfigService_getStudioAppConfig_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceConfigService_getStudioAppConfig_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceConfigService_getStudioAppConfig_args');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceConfigService_getStudioAppConfig_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new StudioAppConfig(args.success);
    }
  }
};
GlanceConfigService_getStudioAppConfig_result.prototype = {};
GlanceConfigService_getStudioAppConfig_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new StudioAppConfig();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceConfigService_getStudioAppConfig_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceConfigService_getStudioAppConfig_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceConfigServiceClient = function(input, output) {
    this.input = input;
    this.output = (!output) ? input : output;
    this.seqid = 0;
};
GlanceConfigServiceClient.prototype = {};
GlanceConfigServiceClient.prototype.getGlobalConfig = function(userId, partner, callback) {
  if (callback === undefined) {
    this.send_getGlobalConfig(userId, partner);
    return this.recv_getGlobalConfig();
  } else {
    var postData = this.send_getGlobalConfig(userId, partner, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_getGlobalConfig);
  }
};

GlanceConfigServiceClient.prototype.send_getGlobalConfig = function(userId, partner, callback) {
  this.output.writeMessageBegin('getGlobalConfig', Thrift.MessageType.CALL, this.seqid);
  var params = {
    userId: userId,
    partner: partner
  };
  var args = new GlanceConfigService_getGlobalConfig_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceConfigServiceClient.prototype.recv_getGlobalConfig = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceConfigService_getGlobalConfig_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'getGlobalConfig failed: unknown result';
};
GlanceConfigServiceClient.prototype.updateGlobalConfig = function(partner, config, callback) {
  if (callback === undefined) {
    this.send_updateGlobalConfig(partner, config);
    this.recv_updateGlobalConfig();
  } else {
    var postData = this.send_updateGlobalConfig(partner, config, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_updateGlobalConfig);
  }
};

GlanceConfigServiceClient.prototype.send_updateGlobalConfig = function(partner, config, callback) {
  this.output.writeMessageBegin('updateGlobalConfig', Thrift.MessageType.CALL, this.seqid);
  var params = {
    partner: partner,
    config: config
  };
  var args = new GlanceConfigService_updateGlobalConfig_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceConfigServiceClient.prototype.recv_updateGlobalConfig = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceConfigService_updateGlobalConfig_result();
  result.read(this.input);
  this.input.readMessageEnd();

  return;
};
GlanceConfigServiceClient.prototype.getStudioAppConfig = function(callback) {
  if (callback === undefined) {
    this.send_getStudioAppConfig();
    return this.recv_getStudioAppConfig();
  } else {
    var postData = this.send_getStudioAppConfig(true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_getStudioAppConfig);
  }
};

GlanceConfigServiceClient.prototype.send_getStudioAppConfig = function(callback) {
  this.output.writeMessageBegin('getStudioAppConfig', Thrift.MessageType.CALL, this.seqid);
  var args = new GlanceConfigService_getStudioAppConfig_args();
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceConfigServiceClient.prototype.recv_getStudioAppConfig = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceConfigService_getStudioAppConfig_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'getStudioAppConfig failed: unknown result';
};
//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


//HELPER FUNCTIONS AND STRUCTURES

GlanceDataService_publish_args = function(args) {
  this.request = null;
  if (args) {
    if (args.request !== undefined && args.request !== null) {
      this.request = new PublishRequest(args.request);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field request is unset!');
    }
  }
};
GlanceDataService_publish_args.prototype = {};
GlanceDataService_publish_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.request = new PublishRequest();
        this.request.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_publish_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_publish_args');
  if (this.request !== null && this.request !== undefined) {
    output.writeFieldBegin('request', Thrift.Type.STRUCT, 1);
    this.request.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_publish_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlancePublishResponse(args.success);
    }
  }
};
GlanceDataService_publish_result.prototype = {};
GlanceDataService_publish_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlancePublishResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_publish_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_publish_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_overrideData_args = function(args) {
  this.draft = null;
  if (args) {
    if (args.draft !== undefined && args.draft !== null) {
      this.draft = new GlanceDraft(args.draft);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field draft is unset!');
    }
  }
};
GlanceDataService_overrideData_args.prototype = {};
GlanceDataService_overrideData_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.draft = new GlanceDraft();
        this.draft.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_overrideData_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_overrideData_args');
  if (this.draft !== null && this.draft !== undefined) {
    output.writeFieldBegin('draft', Thrift.Type.STRUCT, 1);
    this.draft.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_overrideData_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceData(args.success);
    }
  }
};
GlanceDataService_overrideData_result.prototype = {};
GlanceDataService_overrideData_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceData();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_overrideData_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_overrideData_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_removeMeta_args = function(args) {
  this.partner = null;
  this.region = null;
  this.glanceId = null;
  if (args) {
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
  }
};
GlanceDataService_removeMeta_args.prototype = {};
GlanceDataService_removeMeta_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_removeMeta_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_removeMeta_args');
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 1);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 2);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 3);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_removeMeta_result = function(args) {
};
GlanceDataService_removeMeta_result.prototype = {};
GlanceDataService_removeMeta_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_removeMeta_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_removeMeta_result');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_removeMetas_args = function(args) {
  this.partner = null;
  this.region = null;
  this.glanceIds = null;
  if (args) {
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.glanceIds !== undefined && args.glanceIds !== null) {
      this.glanceIds = Thrift.copyList(args.glanceIds, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceIds is unset!');
    }
  }
};
GlanceDataService_removeMetas_args.prototype = {};
GlanceDataService_removeMetas_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.LIST) {
        var _size250 = 0;
        var _rtmp3254;
        this.glanceIds = [];
        var _etype253 = 0;
        _rtmp3254 = input.readListBegin();
        _etype253 = _rtmp3254.etype;
        _size250 = _rtmp3254.size;
        for (var _i255 = 0; _i255 < _size250; ++_i255)
        {
          var elem256 = null;
          elem256 = input.readString().value;
          this.glanceIds.push(elem256);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_removeMetas_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_removeMetas_args');
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 1);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 2);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.glanceIds !== null && this.glanceIds !== undefined) {
    output.writeFieldBegin('glanceIds', Thrift.Type.LIST, 3);
    output.writeListBegin(Thrift.Type.STRING, this.glanceIds.length);
    for (var iter257 in this.glanceIds)
    {
      if (this.glanceIds.hasOwnProperty(iter257))
      {
        iter257 = this.glanceIds[iter257];
        output.writeString(iter257);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_removeMetas_result = function(args) {
};
GlanceDataService_removeMetas_result.prototype = {};
GlanceDataService_removeMetas_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_removeMetas_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_removeMetas_result');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_search_args = function(args) {
  this.query = null;
  if (args) {
    if (args.query !== undefined && args.query !== null) {
      this.query = new GlancePairSearchQuery(args.query);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field query is unset!');
    }
  }
};
GlanceDataService_search_args.prototype = {};
GlanceDataService_search_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.query = new GlancePairSearchQuery();
        this.query.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_search_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_search_args');
  if (this.query !== null && this.query !== undefined) {
    output.writeFieldBegin('query', Thrift.Type.STRUCT, 1);
    this.query.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_search_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlancePairSearchResponse(args.success);
    }
  }
};
GlanceDataService_search_result.prototype = {};
GlanceDataService_search_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlancePairSearchResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_search_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_search_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_pauseGlance_args = function(args) {
  this.request = null;
  if (args) {
    if (args.request !== undefined && args.request !== null) {
      this.request = new GlancePauseRequest(args.request);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field request is unset!');
    }
  }
};
GlanceDataService_pauseGlance_args.prototype = {};
GlanceDataService_pauseGlance_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.request = new GlancePauseRequest();
        this.request.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_pauseGlance_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_pauseGlance_args');
  if (this.request !== null && this.request !== undefined) {
    output.writeFieldBegin('request', Thrift.Type.STRUCT, 1);
    this.request.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataService_pauseGlance_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlancePauseResponse(args.success);
    }
  }
};
GlanceDataService_pauseGlance_result.prototype = {};
GlanceDataService_pauseGlance_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlancePauseResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDataService_pauseGlance_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDataService_pauseGlance_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDataServiceClient = function(input, output) {
    this.input = input;
    this.output = (!output) ? input : output;
    this.seqid = 0;
};
GlanceDataServiceClient.prototype = {};
GlanceDataServiceClient.prototype.publish = function(request, callback) {
  if (callback === undefined) {
    this.send_publish(request);
    return this.recv_publish();
  } else {
    var postData = this.send_publish(request, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_publish);
  }
};

GlanceDataServiceClient.prototype.send_publish = function(request, callback) {
  this.output.writeMessageBegin('publish', Thrift.MessageType.CALL, this.seqid);
  var params = {
    request: request
  };
  var args = new GlanceDataService_publish_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDataServiceClient.prototype.recv_publish = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDataService_publish_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'publish failed: unknown result';
};
GlanceDataServiceClient.prototype.overrideData = function(draft, callback) {
  if (callback === undefined) {
    this.send_overrideData(draft);
    return this.recv_overrideData();
  } else {
    var postData = this.send_overrideData(draft, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_overrideData);
  }
};

GlanceDataServiceClient.prototype.send_overrideData = function(draft, callback) {
  this.output.writeMessageBegin('overrideData', Thrift.MessageType.CALL, this.seqid);
  var params = {
    draft: draft
  };
  var args = new GlanceDataService_overrideData_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDataServiceClient.prototype.recv_overrideData = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDataService_overrideData_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'overrideData failed: unknown result';
};
GlanceDataServiceClient.prototype.removeMeta = function(partner, region, glanceId, callback) {
  if (callback === undefined) {
    this.send_removeMeta(partner, region, glanceId);
    this.recv_removeMeta();
  } else {
    var postData = this.send_removeMeta(partner, region, glanceId, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_removeMeta);
  }
};

GlanceDataServiceClient.prototype.send_removeMeta = function(partner, region, glanceId, callback) {
  this.output.writeMessageBegin('removeMeta', Thrift.MessageType.CALL, this.seqid);
  var params = {
    partner: partner,
    region: region,
    glanceId: glanceId
  };
  var args = new GlanceDataService_removeMeta_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDataServiceClient.prototype.recv_removeMeta = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDataService_removeMeta_result();
  result.read(this.input);
  this.input.readMessageEnd();

  return;
};
GlanceDataServiceClient.prototype.removeMetas = function(partner, region, glanceIds, callback) {
  if (callback === undefined) {
    this.send_removeMetas(partner, region, glanceIds);
    this.recv_removeMetas();
  } else {
    var postData = this.send_removeMetas(partner, region, glanceIds, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_removeMetas);
  }
};

GlanceDataServiceClient.prototype.send_removeMetas = function(partner, region, glanceIds, callback) {
  this.output.writeMessageBegin('removeMetas', Thrift.MessageType.CALL, this.seqid);
  var params = {
    partner: partner,
    region: region,
    glanceIds: glanceIds
  };
  var args = new GlanceDataService_removeMetas_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDataServiceClient.prototype.recv_removeMetas = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDataService_removeMetas_result();
  result.read(this.input);
  this.input.readMessageEnd();

  return;
};
GlanceDataServiceClient.prototype.search = function(query, callback) {
  if (callback === undefined) {
    this.send_search(query);
    return this.recv_search();
  } else {
    var postData = this.send_search(query, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_search);
  }
};

GlanceDataServiceClient.prototype.send_search = function(query, callback) {
  this.output.writeMessageBegin('search', Thrift.MessageType.CALL, this.seqid);
  var params = {
    query: query
  };
  var args = new GlanceDataService_search_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDataServiceClient.prototype.recv_search = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDataService_search_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'search failed: unknown result';
};
GlanceDataServiceClient.prototype.pauseGlance = function(request, callback) {
  if (callback === undefined) {
    this.send_pauseGlance(request);
    return this.recv_pauseGlance();
  } else {
    var postData = this.send_pauseGlance(request, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_pauseGlance);
  }
};

GlanceDataServiceClient.prototype.send_pauseGlance = function(request, callback) {
  this.output.writeMessageBegin('pauseGlance', Thrift.MessageType.CALL, this.seqid);
  var params = {
    request: request
  };
  var args = new GlanceDataService_pauseGlance_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDataServiceClient.prototype.recv_pauseGlance = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDataService_pauseGlance_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'pauseGlance failed: unknown result';
};
//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


//HELPER FUNCTIONS AND STRUCTURES

GlanceDraftService_create_args = function(args) {
  this.draft = null;
  if (args) {
    if (args.draft !== undefined && args.draft !== null) {
      this.draft = new GlanceDraft(args.draft);
    }
  }
};
GlanceDraftService_create_args.prototype = {};
GlanceDraftService_create_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.draft = new GlanceDraft();
        this.draft.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_create_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_create_args');
  if (this.draft !== null && this.draft !== undefined) {
    output.writeFieldBegin('draft', Thrift.Type.STRUCT, 1);
    this.draft.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_create_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceDraftResponse(args.success);
    }
  }
};
GlanceDraftService_create_result.prototype = {};
GlanceDraftService_create_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceDraftResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_create_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_create_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_createFromUrl_args = function(args) {
  this.url = null;
  this.targetDeviceProperties = null;
  this.testMode = null;
  if (args) {
    if (args.url !== undefined && args.url !== null) {
      this.url = args.url;
    }
    if (args.targetDeviceProperties !== undefined && args.targetDeviceProperties !== null) {
      this.targetDeviceProperties = new TargetDeviceProperties(args.targetDeviceProperties);
    }
    if (args.testMode !== undefined && args.testMode !== null) {
      this.testMode = args.testMode;
    }
  }
};
GlanceDraftService_createFromUrl_args.prototype = {};
GlanceDraftService_createFromUrl_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.url = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRUCT) {
        this.targetDeviceProperties = new TargetDeviceProperties();
        this.targetDeviceProperties.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.BOOL) {
        this.testMode = input.readBool().value;
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_createFromUrl_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_createFromUrl_args');
  if (this.url !== null && this.url !== undefined) {
    output.writeFieldBegin('url', Thrift.Type.STRING, 1);
    output.writeString(this.url);
    output.writeFieldEnd();
  }
  if (this.targetDeviceProperties !== null && this.targetDeviceProperties !== undefined) {
    output.writeFieldBegin('targetDeviceProperties', Thrift.Type.STRUCT, 2);
    this.targetDeviceProperties.write(output);
    output.writeFieldEnd();
  }
  if (this.testMode !== null && this.testMode !== undefined) {
    output.writeFieldBegin('testMode', Thrift.Type.BOOL, 3);
    output.writeBool(this.testMode);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_createFromUrl_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceDraftResponse(args.success);
    }
  }
};
GlanceDraftService_createFromUrl_result.prototype = {};
GlanceDraftService_createFromUrl_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceDraftResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_createFromUrl_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_createFromUrl_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_createFromImage_args = function(args) {
  this.imageId = null;
  if (args) {
    if (args.imageId !== undefined && args.imageId !== null) {
      this.imageId = args.imageId;
    }
  }
};
GlanceDraftService_createFromImage_args.prototype = {};
GlanceDraftService_createFromImage_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.imageId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_createFromImage_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_createFromImage_args');
  if (this.imageId !== null && this.imageId !== undefined) {
    output.writeFieldBegin('imageId', Thrift.Type.STRING, 1);
    output.writeString(this.imageId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_createFromImage_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceDraftResponse(args.success);
    }
  }
};
GlanceDraftService_createFromImage_result.prototype = {};
GlanceDraftService_createFromImage_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceDraftResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_createFromImage_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_createFromImage_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_update_args = function(args) {
  this.draft = null;
  if (args) {
    if (args.draft !== undefined && args.draft !== null) {
      this.draft = new GlanceDraft(args.draft);
    }
  }
};
GlanceDraftService_update_args.prototype = {};
GlanceDraftService_update_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.draft = new GlanceDraft();
        this.draft.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_update_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_update_args');
  if (this.draft !== null && this.draft !== undefined) {
    output.writeFieldBegin('draft', Thrift.Type.STRUCT, 1);
    this.draft.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_update_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceDraftResponse(args.success);
    }
  }
};
GlanceDraftService_update_result.prototype = {};
GlanceDraftService_update_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceDraftResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_update_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_update_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_remove_args = function(args) {
  this.draftId = null;
  if (args) {
    if (args.draftId !== undefined && args.draftId !== null) {
      this.draftId = args.draftId;
    }
  }
};
GlanceDraftService_remove_args.prototype = {};
GlanceDraftService_remove_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.draftId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_remove_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_remove_args');
  if (this.draftId !== null && this.draftId !== undefined) {
    output.writeFieldBegin('draftId', Thrift.Type.STRING, 1);
    output.writeString(this.draftId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_remove_result = function(args) {
};
GlanceDraftService_remove_result.prototype = {};
GlanceDraftService_remove_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_remove_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_remove_result');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_get_args = function(args) {
  this.draftId = null;
  if (args) {
    if (args.draftId !== undefined && args.draftId !== null) {
      this.draftId = args.draftId;
    }
  }
};
GlanceDraftService_get_args.prototype = {};
GlanceDraftService_get_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.draftId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_get_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_get_args');
  if (this.draftId !== null && this.draftId !== undefined) {
    output.writeFieldBegin('draftId', Thrift.Type.STRING, 1);
    output.writeString(this.draftId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_get_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceDraftResponse(args.success);
    }
  }
};
GlanceDraftService_get_result.prototype = {};
GlanceDraftService_get_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceDraftResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_get_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_get_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_searchDrafts_args = function(args) {
  this.query = null;
  if (args) {
    if (args.query !== undefined && args.query !== null) {
      this.query = new GlanceDraftSearchQuery(args.query);
    }
  }
};
GlanceDraftService_searchDrafts_args.prototype = {};
GlanceDraftService_searchDrafts_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.query = new GlanceDraftSearchQuery();
        this.query.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_searchDrafts_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_searchDrafts_args');
  if (this.query !== null && this.query !== undefined) {
    output.writeFieldBegin('query', Thrift.Type.STRUCT, 1);
    this.query.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_searchDrafts_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceDraftSearchResponse(args.success);
    }
  }
};
GlanceDraftService_searchDrafts_result.prototype = {};
GlanceDraftService_searchDrafts_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceDraftSearchResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_searchDrafts_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_searchDrafts_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_getPublishedMeta_args = function(args) {
  this.draftId = null;
  if (args) {
    if (args.draftId !== undefined && args.draftId !== null) {
      this.draftId = args.draftId;
    }
  }
};
GlanceDraftService_getPublishedMeta_args.prototype = {};
GlanceDraftService_getPublishedMeta_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRING) {
        this.draftId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_getPublishedMeta_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_getPublishedMeta_args');
  if (this.draftId !== null && this.draftId !== undefined) {
    output.writeFieldBegin('draftId', Thrift.Type.STRING, 1);
    output.writeString(this.draftId);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_getPublishedMeta_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new PublishedMetaResponse(args.success);
    }
  }
};
GlanceDraftService_getPublishedMeta_result.prototype = {};
GlanceDraftService_getPublishedMeta_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new PublishedMetaResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_getPublishedMeta_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_getPublishedMeta_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_getPublishedMetaForDrafts_args = function(args) {
  this.draftIds = null;
  if (args) {
    if (args.draftIds !== undefined && args.draftIds !== null) {
      this.draftIds = Thrift.copyList(args.draftIds, [null]);
    }
  }
};
GlanceDraftService_getPublishedMetaForDrafts_args.prototype = {};
GlanceDraftService_getPublishedMetaForDrafts_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.SET) {
        var _size242 = 0;
        var _rtmp3246;
        this.draftIds = [];
        var _etype245 = 0;
        _rtmp3246 = input.readSetBegin();
        _etype245 = _rtmp3246.etype;
        _size242 = _rtmp3246.size;
        for (var _i247 = 0; _i247 < _size242; ++_i247)
        {
          var elem248 = null;
          elem248 = input.readString().value;
          this.draftIds.push(elem248);
        }
        input.readSetEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_getPublishedMetaForDrafts_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_getPublishedMetaForDrafts_args');
  if (this.draftIds !== null && this.draftIds !== undefined) {
    output.writeFieldBegin('draftIds', Thrift.Type.SET, 1);
    output.writeSetBegin(Thrift.Type.STRING, this.draftIds.length);
    for (var iter249 in this.draftIds)
    {
      if (this.draftIds.hasOwnProperty(iter249))
      {
        iter249 = this.draftIds[iter249];
        output.writeString(iter249);
      }
    }
    output.writeSetEnd();
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_getPublishedMetaForDrafts_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new PublishedMetaMapResponse(args.success);
    }
  }
};
GlanceDraftService_getPublishedMetaForDrafts_result.prototype = {};
GlanceDraftService_getPublishedMetaForDrafts_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new PublishedMetaMapResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_getPublishedMetaForDrafts_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_getPublishedMetaForDrafts_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_getMetasForDrafts_args = function(args) {
  this.query = null;
  if (args) {
    if (args.query !== undefined && args.query !== null) {
      this.query = new GlanceDraftMetaSearchQuery(args.query);
    }
  }
};
GlanceDraftService_getMetasForDrafts_args.prototype = {};
GlanceDraftService_getMetasForDrafts_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.query = new GlanceDraftMetaSearchQuery();
        this.query.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_getMetasForDrafts_args.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_getMetasForDrafts_args');
  if (this.query !== null && this.query !== undefined) {
    output.writeFieldBegin('query', Thrift.Type.STRUCT, 1);
    this.query.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftService_getMetasForDrafts_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceDraftMetaSearchResponse(args.success);
    }
  }
};
GlanceDraftService_getMetasForDrafts_result.prototype = {};
GlanceDraftService_getMetasForDrafts_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceDraftMetaSearchResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

GlanceDraftService_getMetasForDrafts_result.prototype.write = function(output) {
  output.writeStructBegin('GlanceDraftService_getMetasForDrafts_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

GlanceDraftServiceClient = function(input, output) {
    this.input = input;
    this.output = (!output) ? input : output;
    this.seqid = 0;
};
GlanceDraftServiceClient.prototype = {};
GlanceDraftServiceClient.prototype.create = function(draft, callback) {
  if (callback === undefined) {
    this.send_create(draft);
    return this.recv_create();
  } else {
    var postData = this.send_create(draft, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_create);
  }
};

GlanceDraftServiceClient.prototype.send_create = function(draft, callback) {
  this.output.writeMessageBegin('create', Thrift.MessageType.CALL, this.seqid);
  var params = {
    draft: draft
  };
  var args = new GlanceDraftService_create_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_create = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_create_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'create failed: unknown result';
};
GlanceDraftServiceClient.prototype.createFromUrl = function(url, targetDeviceProperties, testMode, callback) {
  if (callback === undefined) {
    this.send_createFromUrl(url, targetDeviceProperties, testMode);
    return this.recv_createFromUrl();
  } else {
    var postData = this.send_createFromUrl(url, targetDeviceProperties, testMode, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_createFromUrl);
  }
};

GlanceDraftServiceClient.prototype.send_createFromUrl = function(url, targetDeviceProperties, testMode, callback) {
  this.output.writeMessageBegin('createFromUrl', Thrift.MessageType.CALL, this.seqid);
  var params = {
    url: url,
    targetDeviceProperties: targetDeviceProperties,
    testMode: testMode
  };
  var args = new GlanceDraftService_createFromUrl_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_createFromUrl = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_createFromUrl_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'createFromUrl failed: unknown result';
};
GlanceDraftServiceClient.prototype.createFromImage = function(imageId, callback) {
  if (callback === undefined) {
    this.send_createFromImage(imageId);
    return this.recv_createFromImage();
  } else {
    var postData = this.send_createFromImage(imageId, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_createFromImage);
  }
};

GlanceDraftServiceClient.prototype.send_createFromImage = function(imageId, callback) {
  this.output.writeMessageBegin('createFromImage', Thrift.MessageType.CALL, this.seqid);
  var params = {
    imageId: imageId
  };
  var args = new GlanceDraftService_createFromImage_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_createFromImage = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_createFromImage_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'createFromImage failed: unknown result';
};
GlanceDraftServiceClient.prototype.update = function(draft, callback) {
  if (callback === undefined) {
    this.send_update(draft);
    return this.recv_update();
  } else {
    var postData = this.send_update(draft, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_update);
  }
};

GlanceDraftServiceClient.prototype.send_update = function(draft, callback) {
  this.output.writeMessageBegin('update', Thrift.MessageType.CALL, this.seqid);
  var params = {
    draft: draft
  };
  var args = new GlanceDraftService_update_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_update = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_update_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'update failed: unknown result';
};
GlanceDraftServiceClient.prototype.remove = function(draftId, callback) {
  if (callback === undefined) {
    this.send_remove(draftId);
    this.recv_remove();
  } else {
    var postData = this.send_remove(draftId, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_remove);
  }
};

GlanceDraftServiceClient.prototype.send_remove = function(draftId, callback) {
  this.output.writeMessageBegin('remove', Thrift.MessageType.CALL, this.seqid);
  var params = {
    draftId: draftId
  };
  var args = new GlanceDraftService_remove_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_remove = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_remove_result();
  result.read(this.input);
  this.input.readMessageEnd();

  return;
};
GlanceDraftServiceClient.prototype.get = function(draftId, callback) {
  if (callback === undefined) {
    this.send_get(draftId);
    return this.recv_get();
  } else {
    var postData = this.send_get(draftId, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_get);
  }
};

GlanceDraftServiceClient.prototype.send_get = function(draftId, callback) {
  this.output.writeMessageBegin('get', Thrift.MessageType.CALL, this.seqid);
  var params = {
    draftId: draftId
  };
  var args = new GlanceDraftService_get_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_get = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_get_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'get failed: unknown result';
};
GlanceDraftServiceClient.prototype.searchDrafts = function(query, callback) {
  if (callback === undefined) {
    this.send_searchDrafts(query);
    return this.recv_searchDrafts();
  } else {
    var postData = this.send_searchDrafts(query, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_searchDrafts);
  }
};

GlanceDraftServiceClient.prototype.send_searchDrafts = function(query, callback) {
  this.output.writeMessageBegin('searchDrafts', Thrift.MessageType.CALL, this.seqid);
  var params = {
    query: query
  };
  var args = new GlanceDraftService_searchDrafts_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_searchDrafts = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_searchDrafts_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'searchDrafts failed: unknown result';
};
GlanceDraftServiceClient.prototype.getPublishedMeta = function(draftId, callback) {
  if (callback === undefined) {
    this.send_getPublishedMeta(draftId);
    return this.recv_getPublishedMeta();
  } else {
    var postData = this.send_getPublishedMeta(draftId, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_getPublishedMeta);
  }
};

GlanceDraftServiceClient.prototype.send_getPublishedMeta = function(draftId, callback) {
  this.output.writeMessageBegin('getPublishedMeta', Thrift.MessageType.CALL, this.seqid);
  var params = {
    draftId: draftId
  };
  var args = new GlanceDraftService_getPublishedMeta_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_getPublishedMeta = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_getPublishedMeta_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'getPublishedMeta failed: unknown result';
};
GlanceDraftServiceClient.prototype.getPublishedMetaForDrafts = function(draftIds, callback) {
  if (callback === undefined) {
    this.send_getPublishedMetaForDrafts(draftIds);
    return this.recv_getPublishedMetaForDrafts();
  } else {
    var postData = this.send_getPublishedMetaForDrafts(draftIds, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_getPublishedMetaForDrafts);
  }
};

GlanceDraftServiceClient.prototype.send_getPublishedMetaForDrafts = function(draftIds, callback) {
  this.output.writeMessageBegin('getPublishedMetaForDrafts', Thrift.MessageType.CALL, this.seqid);
  var params = {
    draftIds: draftIds
  };
  var args = new GlanceDraftService_getPublishedMetaForDrafts_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_getPublishedMetaForDrafts = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_getPublishedMetaForDrafts_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'getPublishedMetaForDrafts failed: unknown result';
};
GlanceDraftServiceClient.prototype.getMetasForDrafts = function(query, callback) {
  if (callback === undefined) {
    this.send_getMetasForDrafts(query);
    return this.recv_getMetasForDrafts();
  } else {
    var postData = this.send_getMetasForDrafts(query, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_getMetasForDrafts);
  }
};

GlanceDraftServiceClient.prototype.send_getMetasForDrafts = function(query, callback) {
  this.output.writeMessageBegin('getMetasForDrafts', Thrift.MessageType.CALL, this.seqid);
  var params = {
    query: query
  };
  var args = new GlanceDraftService_getMetasForDrafts_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

GlanceDraftServiceClient.prototype.recv_getMetasForDrafts = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new GlanceDraftService_getMetasForDrafts_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'getMetasForDrafts failed: unknown result';
};
//
// Autogenerated by Thrift Compiler (0.11.0)
//
// DO NOT EDIT UNLESS YOU ARE SURE THAT YOU KNOW WHAT YOU ARE DOING
//


//HELPER FUNCTIONS AND STRUCTURES

PartnerService_removeMeta_args = function(args) {
  this.region = null;
  this.glanceId = null;
  this.partner = null;
  if (args) {
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
  }
};
PartnerService_removeMeta_args.prototype = {};
PartnerService_removeMeta_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_removeMeta_args.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_removeMeta_args');
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 1);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 2);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 3);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_removeMeta_result = function(args) {
};
PartnerService_removeMeta_result.prototype = {};
PartnerService_removeMeta_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_removeMeta_result.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_removeMeta_result');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_removeMetas_args = function(args) {
  this.region = null;
  this.glanceIds = null;
  this.partner = null;
  if (args) {
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.glanceIds !== undefined && args.glanceIds !== null) {
      this.glanceIds = Thrift.copyList(args.glanceIds, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceIds is unset!');
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
  }
};
PartnerService_removeMetas_args.prototype = {};
PartnerService_removeMetas_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size258 = 0;
        var _rtmp3262;
        this.glanceIds = [];
        var _etype261 = 0;
        _rtmp3262 = input.readListBegin();
        _etype261 = _rtmp3262.etype;
        _size258 = _rtmp3262.size;
        for (var _i263 = 0; _i263 < _size258; ++_i263)
        {
          var elem264 = null;
          elem264 = input.readString().value;
          this.glanceIds.push(elem264);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_removeMetas_args.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_removeMetas_args');
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 1);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.glanceIds !== null && this.glanceIds !== undefined) {
    output.writeFieldBegin('glanceIds', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.glanceIds.length);
    for (var iter265 in this.glanceIds)
    {
      if (this.glanceIds.hasOwnProperty(iter265))
      {
        iter265 = this.glanceIds[iter265];
        output.writeString(iter265);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 3);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_removeMetas_result = function(args) {
};
PartnerService_removeMetas_result.prototype = {};
PartnerService_removeMetas_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_removeMetas_result.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_removeMetas_result');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_approveGlance_args = function(args) {
  this.region = null;
  this.glanceId = null;
  this.partner = null;
  if (args) {
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
  }
};
PartnerService_approveGlance_args.prototype = {};
PartnerService_approveGlance_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_approveGlance_args.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_approveGlance_args');
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 1);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 2);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 3);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_approveGlance_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceApproveResponse(args.success);
    }
  }
};
PartnerService_approveGlance_result.prototype = {};
PartnerService_approveGlance_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceApproveResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_approveGlance_result.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_approveGlance_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_approveGlances_args = function(args) {
  this.region = null;
  this.glanceIds = null;
  this.partner = null;
  if (args) {
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.glanceIds !== undefined && args.glanceIds !== null) {
      this.glanceIds = Thrift.copyList(args.glanceIds, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceIds is unset!');
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
  }
};
PartnerService_approveGlances_args.prototype = {};
PartnerService_approveGlances_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size266 = 0;
        var _rtmp3270;
        this.glanceIds = [];
        var _etype269 = 0;
        _rtmp3270 = input.readListBegin();
        _etype269 = _rtmp3270.etype;
        _size266 = _rtmp3270.size;
        for (var _i271 = 0; _i271 < _size266; ++_i271)
        {
          var elem272 = null;
          elem272 = input.readString().value;
          this.glanceIds.push(elem272);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_approveGlances_args.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_approveGlances_args');
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 1);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.glanceIds !== null && this.glanceIds !== undefined) {
    output.writeFieldBegin('glanceIds', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.glanceIds.length);
    for (var iter273 in this.glanceIds)
    {
      if (this.glanceIds.hasOwnProperty(iter273))
      {
        iter273 = this.glanceIds[iter273];
        output.writeString(iter273);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 3);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_approveGlances_result = function(args) {
};
PartnerService_approveGlances_result.prototype = {};
PartnerService_approveGlances_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_approveGlances_result.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_approveGlances_result');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_rejectGlance_args = function(args) {
  this.region = null;
  this.glanceId = null;
  this.partner = null;
  if (args) {
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.glanceId !== undefined && args.glanceId !== null) {
      this.glanceId = args.glanceId;
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceId is unset!');
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
  }
};
PartnerService_rejectGlance_args.prototype = {};
PartnerService_rejectGlance_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.STRING) {
        this.glanceId = input.readString().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_rejectGlance_args.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_rejectGlance_args');
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 1);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.glanceId !== null && this.glanceId !== undefined) {
    output.writeFieldBegin('glanceId', Thrift.Type.STRING, 2);
    output.writeString(this.glanceId);
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 3);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_rejectGlance_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlanceApproveResponse(args.success);
    }
  }
};
PartnerService_rejectGlance_result.prototype = {};
PartnerService_rejectGlance_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlanceApproveResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_rejectGlance_result.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_rejectGlance_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_rejectGlances_args = function(args) {
  this.region = null;
  this.glanceIds = null;
  this.partner = null;
  if (args) {
    if (args.region !== undefined && args.region !== null) {
      this.region = args.region;
    }
    if (args.glanceIds !== undefined && args.glanceIds !== null) {
      this.glanceIds = Thrift.copyList(args.glanceIds, [null]);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field glanceIds is unset!');
    }
    if (args.partner !== undefined && args.partner !== null) {
      this.partner = new Partner(args.partner);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field partner is unset!');
    }
  }
};
PartnerService_rejectGlances_args.prototype = {};
PartnerService_rejectGlances_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.I32) {
        this.region = input.readI32().value;
      } else {
        input.skip(ftype);
      }
      break;
      case 2:
      if (ftype == Thrift.Type.LIST) {
        var _size274 = 0;
        var _rtmp3278;
        this.glanceIds = [];
        var _etype277 = 0;
        _rtmp3278 = input.readListBegin();
        _etype277 = _rtmp3278.etype;
        _size274 = _rtmp3278.size;
        for (var _i279 = 0; _i279 < _size274; ++_i279)
        {
          var elem280 = null;
          elem280 = input.readString().value;
          this.glanceIds.push(elem280);
        }
        input.readListEnd();
      } else {
        input.skip(ftype);
      }
      break;
      case 3:
      if (ftype == Thrift.Type.STRUCT) {
        this.partner = new Partner();
        this.partner.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_rejectGlances_args.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_rejectGlances_args');
  if (this.region !== null && this.region !== undefined) {
    output.writeFieldBegin('region', Thrift.Type.I32, 1);
    output.writeI32(this.region);
    output.writeFieldEnd();
  }
  if (this.glanceIds !== null && this.glanceIds !== undefined) {
    output.writeFieldBegin('glanceIds', Thrift.Type.LIST, 2);
    output.writeListBegin(Thrift.Type.STRING, this.glanceIds.length);
    for (var iter281 in this.glanceIds)
    {
      if (this.glanceIds.hasOwnProperty(iter281))
      {
        iter281 = this.glanceIds[iter281];
        output.writeString(iter281);
      }
    }
    output.writeListEnd();
    output.writeFieldEnd();
  }
  if (this.partner !== null && this.partner !== undefined) {
    output.writeFieldBegin('partner', Thrift.Type.STRUCT, 3);
    this.partner.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_rejectGlances_result = function(args) {
};
PartnerService_rejectGlances_result.prototype = {};
PartnerService_rejectGlances_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    input.skip(ftype);
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_rejectGlances_result.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_rejectGlances_result');
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_search_args = function(args) {
  this.query = null;
  if (args) {
    if (args.query !== undefined && args.query !== null) {
      this.query = new PartnerPairSearchQuery(args.query);
    } else {
      throw new Thrift.TProtocolException(Thrift.TProtocolExceptionType.UNKNOWN, 'Required field query is unset!');
    }
  }
};
PartnerService_search_args.prototype = {};
PartnerService_search_args.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 1:
      if (ftype == Thrift.Type.STRUCT) {
        this.query = new PartnerPairSearchQuery();
        this.query.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_search_args.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_search_args');
  if (this.query !== null && this.query !== undefined) {
    output.writeFieldBegin('query', Thrift.Type.STRUCT, 1);
    this.query.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerService_search_result = function(args) {
  this.success = null;
  if (args) {
    if (args.success !== undefined && args.success !== null) {
      this.success = new GlancePairSearchResponse(args.success);
    }
  }
};
PartnerService_search_result.prototype = {};
PartnerService_search_result.prototype.read = function(input) {
  input.readStructBegin();
  while (true)
  {
    var ret = input.readFieldBegin();
    var fname = ret.fname;
    var ftype = ret.ftype;
    var fid = ret.fid;
    if (ftype == Thrift.Type.STOP) {
      break;
    }
    switch (fid)
    {
      case 0:
      if (ftype == Thrift.Type.STRUCT) {
        this.success = new GlancePairSearchResponse();
        this.success.read(input);
      } else {
        input.skip(ftype);
      }
      break;
      case 0:
        input.skip(ftype);
        break;
      default:
        input.skip(ftype);
    }
    input.readFieldEnd();
  }
  input.readStructEnd();
  return;
};

PartnerService_search_result.prototype.write = function(output) {
  output.writeStructBegin('PartnerService_search_result');
  if (this.success !== null && this.success !== undefined) {
    output.writeFieldBegin('success', Thrift.Type.STRUCT, 0);
    this.success.write(output);
    output.writeFieldEnd();
  }
  output.writeFieldStop();
  output.writeStructEnd();
  return;
};

PartnerServiceClient = function(input, output) {
    this.input = input;
    this.output = (!output) ? input : output;
    this.seqid = 0;
};
PartnerServiceClient.prototype = {};
PartnerServiceClient.prototype.removeMeta = function(region, glanceId, partner, callback) {
  if (callback === undefined) {
    this.send_removeMeta(region, glanceId, partner);
    this.recv_removeMeta();
  } else {
    var postData = this.send_removeMeta(region, glanceId, partner, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_removeMeta);
  }
};

PartnerServiceClient.prototype.send_removeMeta = function(region, glanceId, partner, callback) {
  this.output.writeMessageBegin('removeMeta', Thrift.MessageType.CALL, this.seqid);
  var params = {
    region: region,
    glanceId: glanceId,
    partner: partner
  };
  var args = new PartnerService_removeMeta_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

PartnerServiceClient.prototype.recv_removeMeta = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new PartnerService_removeMeta_result();
  result.read(this.input);
  this.input.readMessageEnd();

  return;
};
PartnerServiceClient.prototype.removeMetas = function(region, glanceIds, partner, callback) {
  if (callback === undefined) {
    this.send_removeMetas(region, glanceIds, partner);
    this.recv_removeMetas();
  } else {
    var postData = this.send_removeMetas(region, glanceIds, partner, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_removeMetas);
  }
};

PartnerServiceClient.prototype.send_removeMetas = function(region, glanceIds, partner, callback) {
  this.output.writeMessageBegin('removeMetas', Thrift.MessageType.CALL, this.seqid);
  var params = {
    region: region,
    glanceIds: glanceIds,
    partner: partner
  };
  var args = new PartnerService_removeMetas_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

PartnerServiceClient.prototype.recv_removeMetas = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new PartnerService_removeMetas_result();
  result.read(this.input);
  this.input.readMessageEnd();

  return;
};
PartnerServiceClient.prototype.approveGlance = function(region, glanceId, partner, callback) {
  if (callback === undefined) {
    this.send_approveGlance(region, glanceId, partner);
    return this.recv_approveGlance();
  } else {
    var postData = this.send_approveGlance(region, glanceId, partner, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_approveGlance);
  }
};

PartnerServiceClient.prototype.send_approveGlance = function(region, glanceId, partner, callback) {
  this.output.writeMessageBegin('approveGlance', Thrift.MessageType.CALL, this.seqid);
  var params = {
    region: region,
    glanceId: glanceId,
    partner: partner
  };
  var args = new PartnerService_approveGlance_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

PartnerServiceClient.prototype.recv_approveGlance = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new PartnerService_approveGlance_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'approveGlance failed: unknown result';
};
PartnerServiceClient.prototype.approveGlances = function(region, glanceIds, partner, callback) {
  if (callback === undefined) {
    this.send_approveGlances(region, glanceIds, partner);
    this.recv_approveGlances();
  } else {
    var postData = this.send_approveGlances(region, glanceIds, partner, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_approveGlances);
  }
};

PartnerServiceClient.prototype.send_approveGlances = function(region, glanceIds, partner, callback) {
  this.output.writeMessageBegin('approveGlances', Thrift.MessageType.CALL, this.seqid);
  var params = {
    region: region,
    glanceIds: glanceIds,
    partner: partner
  };
  var args = new PartnerService_approveGlances_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

PartnerServiceClient.prototype.recv_approveGlances = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new PartnerService_approveGlances_result();
  result.read(this.input);
  this.input.readMessageEnd();

  return;
};
PartnerServiceClient.prototype.rejectGlance = function(region, glanceId, partner, callback) {
  if (callback === undefined) {
    this.send_rejectGlance(region, glanceId, partner);
    return this.recv_rejectGlance();
  } else {
    var postData = this.send_rejectGlance(region, glanceId, partner, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_rejectGlance);
  }
};

PartnerServiceClient.prototype.send_rejectGlance = function(region, glanceId, partner, callback) {
  this.output.writeMessageBegin('rejectGlance', Thrift.MessageType.CALL, this.seqid);
  var params = {
    region: region,
    glanceId: glanceId,
    partner: partner
  };
  var args = new PartnerService_rejectGlance_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

PartnerServiceClient.prototype.recv_rejectGlance = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new PartnerService_rejectGlance_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'rejectGlance failed: unknown result';
};
PartnerServiceClient.prototype.rejectGlances = function(region, glanceIds, partner, callback) {
  if (callback === undefined) {
    this.send_rejectGlances(region, glanceIds, partner);
    this.recv_rejectGlances();
  } else {
    var postData = this.send_rejectGlances(region, glanceIds, partner, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_rejectGlances);
  }
};

PartnerServiceClient.prototype.send_rejectGlances = function(region, glanceIds, partner, callback) {
  this.output.writeMessageBegin('rejectGlances', Thrift.MessageType.CALL, this.seqid);
  var params = {
    region: region,
    glanceIds: glanceIds,
    partner: partner
  };
  var args = new PartnerService_rejectGlances_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

PartnerServiceClient.prototype.recv_rejectGlances = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new PartnerService_rejectGlances_result();
  result.read(this.input);
  this.input.readMessageEnd();

  return;
};
PartnerServiceClient.prototype.search = function(query, callback) {
  if (callback === undefined) {
    this.send_search(query);
    return this.recv_search();
  } else {
    var postData = this.send_search(query, true);
    return this.output.getTransport()
      .jqRequest(this, postData, arguments, this.recv_search);
  }
};

PartnerServiceClient.prototype.send_search = function(query, callback) {
  this.output.writeMessageBegin('search', Thrift.MessageType.CALL, this.seqid);
  var params = {
    query: query
  };
  var args = new PartnerService_search_args(params);
  args.write(this.output);
  this.output.writeMessageEnd();
  return this.output.getTransport().flush(callback);
};

PartnerServiceClient.prototype.recv_search = function() {
  var ret = this.input.readMessageBegin();
  var fname = ret.fname;
  var mtype = ret.mtype;
  var rseqid = ret.rseqid;
  if (mtype == Thrift.MessageType.EXCEPTION) {
    var x = new Thrift.TApplicationException();
    x.read(this.input);
    this.input.readMessageEnd();
    throw x;
  }
  var result = new PartnerService_search_result();
  result.read(this.input);
  this.input.readMessageEnd();

  if (null !== result.success) {
    return result.success;
  }
  throw 'search failed: unknown result';
};



exports = module.exports = {
	ExpiredGlance: ExpiredGlance,
	AdGroupDeliverySetting: AdGroupDeliverySetting,
	AdInventoryImpressionCapping: AdInventoryImpressionCapping,
	AdInventorySurface: AdInventorySurface,
	AdInventoryFrequencyCapping: AdInventoryFrequencyCapping,
	AdInventoryGlanceAdPlacement: AdInventoryGlanceAdPlacement,
	AdInventoryCategory: AdInventoryCategory,
	AdInventoryCampaignObjective: AdInventoryCampaignObjective,
	Catalogue: Catalogue,
	Viewability: Viewability,
	AdInventoryAdvertiser: AdInventoryAdvertiser,
	AdInventoryPageName: AdInventoryPageName,
	AdInventoryProgrammaticType: AdInventoryProgrammaticType,
	AdInventoryTrackers: AdInventoryTrackers,
	AdInventoryAdGroupTrackers: AdInventoryAdGroupTrackers,
	AdInventoryProgrammaticSettings: AdInventoryProgrammaticSettings,
	AdInventoryAdUnitLineItem: AdInventoryAdUnitLineItem,
	AdInventoryAdUnit: AdInventoryAdUnit,
	AdInventoryAdForCampaign: AdInventoryAdForCampaign,
	AdInventoryAdGroup: AdInventoryAdGroup,
	AdInventoryCampaign: AdInventoryCampaign,
	AdInventoryCreativeItem: AdInventoryCreativeItem,
	AdInventoryCreative: AdInventoryCreative,
	AdInventoryCreativeGroup: AdInventoryCreativeGroup,
	AdInventoryAd: AdInventoryAd,
	AdInventoryGlanceAdPlacementMapping: AdInventoryGlanceAdPlacementMapping,
	AdImpression: AdImpression,
	MonetisationUserSummary: MonetisationUserSummary,
	IabCategoryId: IabCategoryId,
	Blocks: Blocks,
	BlockSetting: BlockSetting,
	GlanceApp: GlanceApp,
	GlasAdUnit: GlasAdUnit,
	ConfigObj: ConfigObj,
	Network: Network,
	NetworkAttribute: NetworkAttribute,
	NetworkAppAttribute: NetworkAppAttribute,
	AdLine: AdLine,
	StringField: StringField,
	IntField: IntField,
	BoolField: BoolField,
	Value: Value,
	UxTemplate: UxTemplate,
	UXTemplateMapping: UXTemplateMapping,
	IabCategory: IabCategory,
	CreativeAttribute: CreativeAttribute,
	ExperimentVariant: ExperimentVariant,
	RuleIds: RuleIds,
	Experiment: Experiment,
	ExperimentMeta: ExperimentMeta,
	Partner: Partner,
	AbConfig: AbConfig,
	PartnerMeta: PartnerMeta,
	ApiKeyMeta: ApiKeyMeta,
	AppOptionalDetails: AppOptionalDetails,
	AdjustPreload: AdjustPreload,
	AppsFlyerPreload: AppsFlyerPreload,
	PreloadAttirbution: PreloadAttirbution,
	Tag: Tag,
	CategoryInfo: CategoryInfo,
	InstallCtaMeta: InstallCtaMeta,
	AppInstallMeta: AppInstallMeta,
	CtaDisplayText: CtaDisplayText,
	CtaDisplayImage: CtaDisplayImage,
	CtaBgClr: CtaBgClr,
	CtaDisplay: CtaDisplay,
	Product: Product,
	ProductTiles: ProductTiles,
	WidgetTagDetails: WidgetTagDetails,
	ImageMap: ImageMap,
	SpaceServingCategory: SpaceServingCategory,
	City: City,
	State: State,
	Country: Country,
	CityInfo: CityInfo,
	StateInfo: StateInfo,
	Location: Location,
	LinearGradient: LinearGradient,
	TagLine: TagLine,
	TagLineInfo: TagLineInfo,
	SpaceData: SpaceData,
	LiveEventData: LiveEventData,
	CtaMeta: CtaMeta,
	AppCta: AppCta,
	Cta: Cta,
	ArticlePeek: ArticlePeek,
	WebPeek: WebPeek,
	NativeVideoPeek: NativeVideoPeek,
	NativeLiveVideoPeek: NativeLiveVideoPeek,
	FusionVideoPeek: FusionVideoPeek,
	TvVideoPeek: TvVideoPeek,
	LiveVideoPeek: LiveVideoPeek,
	FacebookVideoMeta: FacebookVideoMeta,
	YoutubeVideoMeta: YoutubeVideoMeta,
	InstagramVideoMeta: InstagramVideoMeta,
	YoukuVideoMeta: YoukuVideoMeta,
	VideoMeta: VideoMeta,
	AspectRatio: AspectRatio,
	VideoData: VideoData,
	VideoPeek: VideoPeek,
	AttributionDetails: AttributionDetails,
	TargetDeviceProperties: TargetDeviceProperties,
	ShareDetails: ShareDetails,
	GlanceSlots: GlanceSlots,
	GamPgConfig: GamPgConfig,
	GlanceConfig: GlanceConfig,
	GlanceContentConfig: GlanceContentConfig,
	GlanceLanguage: GlanceLanguage,
	GlanceAdConfig: GlanceAdConfig,
	GlanceGlobalConfig: GlanceGlobalConfig,
	ImageMeta: ImageMeta,
	RoposoGlance: RoposoGlance,
	Image: Image,
	InternalImage: InternalImage,
	UserLifeTimeFilter: UserLifeTimeFilter,
	StudioCategory: StudioCategory,
	GlanceCategory: GlanceCategory,
	RichText: RichText,
	GlanceContext: GlanceContext,
	PeekData: PeekData,
	Peek: Peek,
	Beacon: Beacon,
	GamingEuSpaceData: GamingEuSpaceData,
	GameMetaData: GameMetaData,
	SportEuSpaceData: SportEuSpaceData,
	Beacons: Beacons,
	BeaconsInfo: BeaconsInfo,
	SubscriptionMeta: SubscriptionMeta,
	Proposition: Proposition,
	Publisher: Publisher,
	PublisherData: PublisherData,
	ShoppableData: ShoppableData,
	Programmatic: Programmatic,
	TrelloCardInfo: TrelloCardInfo,
	FrequencyCaps: FrequencyCaps,
	AppTargeting: AppTargeting,
	Targeting: Targeting,
	GlanceProperty: GlanceProperty,
	GameCategory: GameCategory,
	Interaction: Interaction,
	RoposoInteraction: RoposoInteraction,
	Bubble: Bubble,
	GlanceBubbleDetails: GlanceBubbleDetails,
	GlanceCampaignMeta: GlanceCampaignMeta,
	OmSdkInfo: OmSdkInfo,
	CtaViewConfig: CtaViewConfig,
	OciConfigs: OciConfigs,
	FastCardData: FastCardData,
	GlamCampaignData: GlamCampaignData,
	ValuePropInfoThrift: ValuePropInfoThrift,
	EuInfoThrift: EuInfoThrift,
	DspCampaignData: DspCampaignData,
	GlanceData: GlanceData,
	AbsoluteTime: AbsoluteTime,
	RelativeTime: RelativeTime,
	Duration: Duration,
	StartTime: StartTime,
	GlanceTime: GlanceTime,
	PartingTime: PartingTime,
	ResyncDetails: ResyncDetails,
	GlanceMeta: GlanceMeta,
	DraftUsers: DraftUsers,
	DraftEditTimes: DraftEditTimes,
	DraftEditMeta: DraftEditMeta,
	PeekDraft: PeekDraft,
	ImpressionCappingData: ImpressionCappingData,
	ImpressionCapping: ImpressionCapping,
	GlanceDraft: GlanceDraft,
	GlancePair: GlancePair,
	GlancePairs: GlancePairs,
	RegionRequest: RegionRequest,
	PublishRequest: PublishRequest,
	SegmentTargetingRule: SegmentTargetingRule,
	App: App,
	SpaceEvent: SpaceEvent,
	GtvProvider: GtvProvider,
	StudioSessionToken: StudioSessionToken,
	SDKVersion: SDKVersion,
	DefaultRuleCity: DefaultRuleCity,
	DefaultTargetingRule: DefaultTargetingRule,
	StudioAppConfig: StudioAppConfig,
	StudioUser: StudioUser,
	ScreenInfo: ScreenInfo,
	Channel: Channel,
	NotificationInfo: NotificationInfo,
	AppInfo: AppInfo,
	InputInfo: InputInfo,
	DeviceInfo: DeviceInfo,
	SpaceMetaData: SpaceMetaData,
	SpaceCard: SpaceCard,
	SpacePreferences: SpacePreferences,
	Geo: Geo,
	GlanceOpportunities: GlanceOpportunities,
	UserExtras: UserExtras,
	SystemFlags: SystemFlags,
	Segment: Segment,
	LanguageSubscription: LanguageSubscription,
	UserEngagement: UserEngagement,
	HighlightsDetails: HighlightsDetails,
	SdkFeatures: SdkFeatures,
	AppLocale: AppLocale,
	UserStated: UserStated,
	LocationInfo: LocationInfo,
	User: User,
	GamingCoupon: GamingCoupon,
	StudioSegment: StudioSegment,
	SqlStudioSegment: SqlStudioSegment,
	Keyword: Keyword,
	Entity: Entity,
	Concept: Concept,
	Category: Category,
	NlpFeatures: NlpFeatures,
	GlanceNlpFeatures: GlanceNlpFeatures,
	ImageSuggestionMeta: ImageSuggestionMeta,
	ImageSuggestion: ImageSuggestion,
	ImageSuggestionsForSearchPhrase: ImageSuggestionsForSearchPhrase,
	ImageSuggestionResponse: ImageSuggestionResponse,
	GlanceImageSuggestions: GlanceImageSuggestions,
	SpacesCity: SpacesCity,
	CityState: CityState,
	SpacesCityState: SpacesCityState,
	CityGroup: CityGroup,
	Carrier: Carrier,
	CcidCarrier: CcidCarrier,
	GlanceServedMeta: GlanceServedMeta,
	GlancesServed: GlancesServed,
	OnlineGlancesServedWithSeen: OnlineGlancesServedWithSeen,
	ServedGlance: ServedGlance,
	ServedGlancesInSession: ServedGlancesInSession,
	OnlineGlancesServed: OnlineGlancesServed,
	HourlyGlanceImpressions: HourlyGlanceImpressions,
	PredictedImpressions: PredictedImpressions,
	UserSummary: UserSummary,
	RewardCredit: RewardCredit,
	RewardDebit: RewardDebit,
	RewardTransaction: RewardTransaction,
	Winning: Winning,
	RewardEvent: RewardEvent,
	UserReward: UserReward,
	OnlineRequestSummaryMessage: OnlineRequestSummaryMessage,
	NotificationCta: NotificationCta,
	UserEngageNotification: UserEngageNotification,
	GlancePublishData: GlancePublishData,
	GlanceTvTranscodedVideoMeta: GlanceTvTranscodedVideoMeta,
	GlanceTvVideo: GlanceTvVideo,
	GlanceTvCategoryMap: GlanceTvCategoryMap,
	SiloGlanceTvVideo: SiloGlanceTvVideo,
	GameTournament: GameTournament,
	IpBlacklistEntity: IpBlacklistEntity,
	UserAgentEntity: UserAgentEntity,
	CampaignObjective: CampaignObjective,
	GlanceImpressionCapping: GlanceImpressionCapping,
	CardpressImgMeta: CardpressImgMeta,
	OverlaySpec: OverlaySpec,
	PublishingOptions: PublishingOptions,
	UrlMeta: UrlMeta,
	GlancifiedVideoChunkInfo: GlancifiedVideoChunkInfo,
	GlancifyVideoData: GlancifyVideoData,
	OppoImageAssets: OppoImageAssets,
	FastCardAssets: FastCardAssets,
	TextAssets: TextAssets,
	TrendsData: TrendsData,
	VideoAssets: VideoAssets,
	Assets: Assets,
	CardMetaData: CardMetaData,
	CardInfo: CardInfo,
	JavelinUser: JavelinUser,
	SourceStatistics: SourceStatistics,
	Card: Card,
	ImageTag: ImageTag,
	Condition: Condition,
	RewardRuleItem: RewardRuleItem,
	RewardRuleGroup: RewardRuleGroup,
	Quiz: Quiz,
	RewardUserEvent: RewardUserEvent,
	CardsByBubbleResponse: CardsByBubbleResponse,
	PublishingQueue: PublishingQueue,
	CardBubble: CardBubble,
	Sweepstake: Sweepstake,
	DeviceAttestationData: DeviceAttestationData,
	AppOwnershipCampaignGlanceCreatedMeta: AppOwnershipCampaignGlanceCreatedMeta,
	AppOwnershipCampaign: AppOwnershipCampaign,
	NotificationTemplate: NotificationTemplate,
	DSGenerateCard: DSGenerateCard,
	BingeCardAssets: BingeCardAssets,
	DSGenerateResponse: DSGenerateResponse,
	SourceCreatorMeta: SourceCreatorMeta,
	Offer: Offer,
	VoucherMeta: VoucherMeta,
	PitaraMeta: PitaraMeta,
	RuleMeta: RuleMeta,
	Rule: Rule,
	JavelinSessionToken: JavelinSessionToken,
	GlanceDraftInfo: GlanceDraftInfo,
	UnpublishCard: UnpublishCard,
	CardBubbleInfo: CardBubbleInfo,
	JavelinPublishingPartner: JavelinPublishingPartner,
	GameBracket: GameBracket,
	TournamentBracket: TournamentBracket,
	FilteringConfig: FilteringConfig,
	UserBubbleDislikeSummary: UserBubbleDislikeSummary,
	DislikedBubble: DislikedBubble,
	OtdImage: OtdImage,
	ShortVideoAdConfig: ShortVideoAdConfig,
	Banner: Banner,
	Fallback: Fallback,
	MesonAdConfig: MesonAdConfig,
	InternalMesonAdConfig: InternalMesonAdConfig,
	GlanceIdEuIdentifierMapping: GlanceIdEuIdentifierMapping,
	RoundUpCardInfo: RoundUpCardInfo,
	RoundUpMeta: RoundUpMeta,
	RoundUp: RoundUp,
	RoundUpPair: RoundUpPair,
	TargetingRuleCity: TargetingRuleCity,
	TargetingRuleGender: TargetingRuleGender,
	TargetingRuleHandsetInfo: TargetingRuleHandsetInfo,
	TargetingRuleSdkVersion: TargetingRuleSdkVersion,
	TargetingRuleSegmentQuery: TargetingRuleSegmentQuery,
	TargetingRuleState: TargetingRuleState,
	TargetingRulePriceLimit: TargetingRulePriceLimit,
	TargetingRule: TargetingRule,
	MlibCohort: MlibCohort,
	MlibCohortResponse: MlibCohortResponse,
	HandsetInfo: HandsetInfo,
	AdPersonalization: AdPersonalization,
	AdUnits: AdUnits,
	AdMonConfig: AdMonConfig,
	CardRequest: CardRequest,
	AutoStackRankingRequest: AutoStackRankingRequest,
	AutoStackRankingEcpm: AutoStackRankingEcpm,
	AutoStackRankingResponse: AutoStackRankingResponse,
	BaseCategory: BaseCategory,
	TVLanguageSubscription: TVLanguageSubscription,
	TvGeo: TvGeo,
	OciBeacons: OciBeacons,
	AppOpenNudgeConfig: AppOpenNudgeConfig,
	AppOpenNotificationConfig: AppOpenNotificationConfig,
	OciAppConfig: OciAppConfig,
	CampaignTrackerInfo: CampaignTrackerInfo,
	OciCampaignMeta: OciCampaignMeta,
	MetricCacheModel: MetricCacheModel,
	GlanceDraftResponse: GlanceDraftResponse,
	Pagination: Pagination,
	GlanceDraftSearchQuery: GlanceDraftSearchQuery,
	GlanceDraftSearchResponse: GlanceDraftSearchResponse,
	GlancePublishResponse: GlancePublishResponse,
	PublishedMetaResponse: PublishedMetaResponse,
	PublishedMetaMapResponse: PublishedMetaMapResponse,
	GlanceDraftMetaSearchResponse: GlanceDraftMetaSearchResponse,
	GlanceDraftMetaSearchQuery: GlanceDraftMetaSearchQuery,
	GlancePairSearchQuery: GlancePairSearchQuery,
	GlancePauseRequest: GlancePauseRequest,
	GlancePauseResponse: GlancePauseResponse,
	PartnerPairSearchQuery: PartnerPairSearchQuery,
	GlancePairSearchResponse: GlancePairSearchResponse,
	GlanceApproveResponse: GlanceApproveResponse,
	AuthException: AuthException,
	TvUserDiff: TvUserDiff,
	TvUser: TvUser,
	TvUserV1: TvUserV1,
	UserDiff: UserDiff,
	AuthServiceClient: AuthServiceClient,
	GlanceConfigServiceClient: GlanceConfigServiceClient,
	GlanceDataServiceClient: GlanceDataServiceClient,
	GlanceDraftServiceClient: GlanceDraftServiceClient,
	PartnerServiceClient: PartnerServiceClient,
	Thrift: Thrift
};



//////////////////
// WEBPACK FOOTER
// ./~/glance-datatypes/index.js
// module id = 67
// module chunks = 0